// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { frModuleTips, frFrameworkRequirements } from './fr-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
export const fr = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Connectez-vous à votre compte',
        email: 'E-mail',
        password: 'Mot de passe',
        sign_in: 'Se connecter',
        signing_in: 'Connexion en cours...',
        mfa_code: 'Code MFA',
        mfa_placeholder: 'Entrez le code à 6 chiffres',
        mfa_instruction: "Entrez le code à 6 chiffres de votre application d'authentification.",
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Vérifier',
        verifying: 'Vérification en cours...',
        forgot_password: 'Mot de passe oublié ?',
        account_locked: 'Le compte est verrouillé. Veuillez réessayer plus tard.',
        account_deactivated: 'Le compte a été désactivé. Contactez votre administrateur.',
        invalid_mfa: 'Code MFA invalide. Veuillez réessayer.',
        invalid_credentials: 'E-mail ou mot de passe invalide.',
        login_failed: 'Échec de la connexion',
        session_limit_reached: 'Votre compte a atteint la limite de sessions actives. Déconnectez-vous sur un autre appareil ou demandez à votre administrateur de révoquer une session existante, puis réessayez.',
        session_expired: 'Votre session a expiré. Veuillez recharger la page et réessayer.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Mot de passe oublié',
            subtitle: 'Saisissez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
            send: 'Envoyer le lien de réinitialisation',
            sending: 'Envoi en cours...',
            success: 'Si cette adresse e-mail existe, un lien de réinitialisation du mot de passe a été envoyé.',
            back_to_login: 'Retour à la connexion',
        },
        reset_password: {
            title: 'Réinitialiser le mot de passe',
            subtitle: 'Saisissez votre nouveau mot de passe ci-dessous.',
            new_password: 'Nouveau mot de passe',
            confirm_password: 'Confirmer le mot de passe',
            reset: 'Réinitialiser le mot de passe',
            resetting: 'Réinitialisation en cours...',
            success: 'Votre mot de passe a été réinitialisé avec succès.',
            go_to_login: 'Aller à la connexion',
            password_mismatch: 'Les mots de passe ne correspondent pas.',
            invalid_token: 'Jeton de réinitialisation invalide ou manquant. Veuillez demander un nouveau lien de réinitialisation du mot de passe.',
            requirements_title: 'Exigences relatives au mot de passe :',
            password_requirements: 'Le mot de passe doit répondre aux exigences suivantes :',
            rules: {
                min_length: 'Au moins 12 caractères',
                uppercase: 'Au moins une lettre majuscule',
                lowercase: 'Au moins une lettre minuscule',
                digit: 'Au moins un chiffre',
                special: 'Au moins un caractère spécial',
            },
        },
        verify_email: {
            title: "Vérification de l'adresse e-mail",
            verifying: 'Vérification de votre adresse e-mail en cours...',
            success: 'Votre adresse e-mail a été vérifiée avec succès.',
            invalid_token: 'Jeton de vérification invalide ou manquant.',
            failed: "La vérification de l'adresse e-mail a échoué. Le jeton est peut-être invalide ou expiré.",
            go_to_login: 'Aller à la connexion',
        },
        offline: {
            title: 'Vous êtes hors ligne',
            message: 'Il semble que vous ayez perdu votre connexion Internet. Veuillez vérifier votre réseau et réessayer.',
        },
    },
    intake: {
        title: "Demande d'exercice des droits",
        subtitle: "Soumettre une demande d'exercice des droits à notre organisation",
        fields: {
            full_name: 'Nom complet',
            email: 'Adresse e-mail',
            phone: 'Numéro de téléphone (facultatif)',
            request_type: 'Type de demande',
            description: 'Décrivez votre demande',
            preferred_language: 'Langue préférée',
            consent: 'Je consens au traitement de cette demande',
        },
        submit: 'Soumettre la demande',
        validation: {
            name_required: 'Le nom complet est requis',
            email_required: 'Une adresse e-mail valide est requise',
            type_required: 'Veuillez sélectionner un type de demande',
            description_required: 'Veuillez décrire votre demande',
            consent_required: 'Vous devez consentir au traitement pour continuer',
        },
        error_title: 'Erreur',
        config_load_error: "Impossible de charger la configuration. Veuillez vérifier l'URL.",
        organization_label: 'Organisation',
        customer_reference: 'Numéro de référence client',
        country: 'Pays / Juridiction',
        preferred_channel: 'Canal de réponse préféré',
        channels: {
            email: 'E-mail',
            postal: 'Courrier',
            phone: 'Téléphone',
            portal: 'Portail',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Demande reçue',
            message: 'Votre demande a été enregistrée avec succès. Votre numéro de référence est :',
            next_steps: 'Nous traiterons votre demande dans les meilleurs délais conformément à la législation applicable. Le délai de traitement prévu est de 30 jours ouvrés.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: "Droit d'accès (DSAR)",
        deletion: "Droit à l'effacement",
        correction: 'Rectification / Mise à jour',
        objection: 'Opposition',
        portability: 'Portabilité des données',
        restriction: 'Limitation du traitement',
        consent_withdrawal: 'Retrait du consentement',
        protective_measures: 'Demander des mesures et solutions de protection des données personnelles',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Nouveau',
        in_review: "En cours d'examen",
        waiting_on_requester: 'En attente du demandeur',
        waiting_on_internal_data: 'En attente de données internes',
        approved: 'Approuvé',
        closed: 'Clôturé',
        rejected: 'Rejeté',
    },
    admin: {
        nav: {
            rights_requests: "Demandes d'exercice des droits",
            templates: 'Modèles',
            response_templates: 'Modèles de Réponse',
            data_mapping: 'Cartographie des données',
            governance: 'Gouvernance',
            consent: 'Consentement',
            assessments: 'Évaluations',
            classification: 'Classement',
            monitoring: 'Surveillance',
            incidents: 'Incidents',
            onboarding: 'Intégration',
            connectors: 'Connecteurs',
            vendor_risk: 'Risque prestataire',
            deletion_orchestration: 'Orchestration de suppression',
            discovery: 'Découverte de données',
            incident_ops: 'Opérations incidents',
            program_governance: 'Gouvernance de programme',
            access_accountability: 'Responsabilité des accès',
            consent_properties: 'Propriétés de consentement',
            localization: 'Localisation',
            regulatory: 'Réglementaire',
            partner_workspace: 'Espace partenaire',
            audit_trail: "Piste d'audit",
            forseti: 'Forseti',
            settings: 'Paramètres',
            help_center: "Centre d'aide",
            dashboard: 'Tableau de bord',
            platform: 'Plateforme',
            integrations: 'Intégrations',
            cmp_dsar_sync: 'Synchronisation CMP DSAR',
            cmp_integration: 'Intégration CMP',
            purpose_mapping: 'Correspondance des finalités',
            webhook_config: 'Configuration des webhooks',
            bulk_sync: 'Synchronisation en masse',
            privacy_scanner: 'Analyseur de confidentialité',
            privacy_monitoring: 'Surveillance de la confidentialité',
            real_time_scanner: 'Analyseur en temps réel',
            compliance_forms: 'Formulaires de conformité',
            forms_dashboard: 'Tableau de bord des formulaires',
            form_templates: 'Modèles',
            form_catalog: 'Catalogue de formulaires',
            pack_catalog: 'Catalogue de packs',
            org_groups: 'Groupes organisationnels',
            consolidation: 'Consolidation',
            organizations: 'Organisations',
            org_list: 'Toutes les organisations',
            intake_configs: "Configurations d'admission",
            task_management: 'Gestion des tâches',
            unified_audit: 'Audit Unifié',
            evidence_packs: 'Paquets de Preuves',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: "Périodes d'Audit",
            regulatory_updates: 'Mises à Jour Réglementaires',
            departments: 'Départements',
        },
        sidebar_category_data_privacy_rights: 'Confidentialité des Données & Droits',
        sidebar_category_vendor_third_party: 'Gestion des Fournisseurs & Tiers',
        sidebar_category_risk_assessment: 'Risque & Évaluation',
        sidebar_category_operations_automation: 'Opérations & Automatisation',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Gestion des Tâches & Flux de Travail',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP et webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'Registre des traitements',
            data_mapping_classification: 'Classement',
            data_mapping_governance: 'Gouvernance',
            access_live_monitoring: 'Surveillance en temps réel',
            access_reviews_investigations: 'Revues & Investigations',
            access_evidence_policies: 'Preuves & Politiques',
            classification_taxonomies: 'Taxonomies & Étiquettes',
            classification_workflow: 'Flux de travail',
            classification_insights: 'Analyses',
            connectors_runtime: 'Exécution',
            connectors_due_diligence: 'Diligence raisonnable',
            connectors_coverage: 'Couverture',
            cp_definitions: 'Définitions',
            cp_lifecycle: 'Cycle de vie',
            cp_quality: 'Qualité & Preuves',
            deletion_execution: 'Exécution',
            deletion_vendors: 'Fournisseurs',
            deletion_evidence: 'Preuves & Intégration',
            discovery_rules: 'Règles & Modèles',
            discovery_scanning: 'Analyse',
            discovery_review: 'Flux de revue',
            discovery_insights: 'Analyses',
            governance_oversight: 'Supervision',
            governance_reporting: 'Rapports',
            governance_vendors: 'Fournisseurs & Transferts hors UE',
            governance_lifecycle: 'Cycle de vie',
            incident_ops_dashboards: 'Tableaux de bord',
            incident_ops_sla: 'Configuration SLA',
            incident_ops_playbooks: 'Manuels de jeu',
            incident_ops_vendor_regulator: 'Fournisseur & Régulateur',
            incident_ops_evidence: 'Preuves & Revue',
            incidents_workload: 'Charge de travail',
            incidents_regulatory: 'Réglementaire',
            incidents_reporting: 'Rapports',
            localization_content: 'Contenu',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observabilité',
            monitoring_metrics: 'Métriques',
            monitoring_programs: 'Programmes',
            monitoring_tasks: 'Tâches',
            monitoring_controls: 'Contrôles',
            org_entities: 'Entités',
            org_config: 'Configuration',
            org_frameworks: 'Référentiels & Consolidation',
            partner_overview: 'Vue d’ensemble',
            partner_partners: 'Partenaires',
            partner_delivery: 'Livraison',
            partner_support: 'Assistance',
            platform_identity: 'Identité & Rôles',
            platform_access_control: 'Contrôle d’accès',
            platform_auth_session: 'Authentification & Session',
            platform_access_reviews: 'Revues d’accès',
            platform_files: 'Fichiers',
            platform_notifications: 'Notifications',
            platform_module_config: 'Configuration des modules',
            platform_ops: 'Opérations & Journaux',
            pg_health: 'État du programme',
            pg_maturity: 'Maturité',
            pg_audit: 'Audit & Preuves',
            pg_training: 'Formation',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Obligations',
            pg_documents: 'Documents',
            vr_overview: 'Vue d’ensemble',
            vr_onboarding: 'Intégration',
            vr_assessment: 'Évaluation',
            vr_oversight: 'Supervision',
        },
        tickets: {
            title: "Demandes d'exercice des droits",
            create: 'Créer une demande',
            filters: 'Filtres',
            search_placeholder: 'Search tickets...',
            export_csv: 'Exporter CSV',
            exporting: 'Exportation...',
            bulk_export_failed: "Échec de l'exportation groupée",
            bulk_export: {
                label: 'Exporter',
                format: {
                    pdf: 'PDF Résumé',
                    zip: 'ZIP (packs de preuves par cas)',
                    csv: 'CSV',
                },
                warning_over_50: 'Cela générera environ {count} dossiers - cela peut prendre plusieurs minutes. Continuer?',
                blocked_over_500: "Filtrez à 500 cas ou moins avant d'exporter le fichier ZIP (actuel : {count}).",
                success: 'Exportation groupée enregistrée dans le navigateur de fichiers (cas {count})',
                failed: "Échec de l'exportation groupée",
                continue: 'Continuer',
                cancel: 'Annuler',
            },
            no_results: 'Aucune demande trouvée',
            ticket_id: 'ID du ticket',
            request_type: 'Type de demande',
            requester: 'Demandeur',
            source: 'Origine',
            all_sources: 'Toutes les sources',
            assigned_to: 'Assigné à',
            created: 'Créé',
            load_error: 'Échec du chargement des dossiers. Veuillez réessayer.',
            case_creation_failed: 'Échec de la création du cas',
            business_unit: 'Unité commerciale',
            reference: 'Référence',
            sla: {
                title: 'Statut SLA',
                on_track: '{days} jours restants',
                approaching: 'Échéance proche',
                overdue: '{days} jours de retard',
                ack_overdue: "{hours}h au-delà du délai d'accusé de réception",
                ack_warning: 'Accusé de réception bientôt dû',
                closed: 'Fermé',
                completed_in: 'Terminé en {days} jours',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Modèles de réponse',
            new: 'Nouveau modèle',
            edit: 'Modifier le modèle',
            all_types: 'Tous les types',
            all_locales: 'Toutes les langues',
            type_access: 'Accès/DSAR',
            type_deletion: 'Suppression',
            type_correction: 'Rectificatif',
            type_objection: 'Opposition',
            type_portability: 'Portabilité',
            type_other: 'Autre',
            type_restriction: 'Restriction',
            type_all: 'Tous',
            locale_en: 'Anglais',
            locale_vi: 'Vietnamien',
            locale_all: 'Les deux',
            name: 'Nom',
            request_type: 'Type de demande',
            locale: 'Langue',
            subject: 'Objet',
            body: 'Corps du message',
            name_placeholder: 'Nom du modèle...',
            subject_placeholder: "Objet de l'e-mail/courrier...",
            body_placeholder: 'Corps du modèle. Utilisez {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} comme espaces réservés.',
            available_placeholders: 'Espaces réservés disponibles :',
            create_template: 'Créer un modèle',
            preview: 'Aperçu',
            close_preview: "Fermer l'aperçu",
            default_badge: 'Par défaut',
            no_templates: 'Aucun modèle trouvé',
            confirm_delete: 'Supprimer ce modèle ?',
            load_error: 'Échec du chargement des modèles. Veuillez réessayer.',
            create_error: 'Échec de la création du modèle',
            update_error: 'Échec de la mise à jour du modèle',
            delete_error: 'Échec de la suppression du modèle',
            preview_error: "Échec de l'aperçu du modèle",
            response_templates_badge: 'Modèles de Réponse',
            browse_form_templates: 'Parcourir les Modèles de Formulaires de Conformité',
            templates_loaded: 'Modèles chargés',
            sample_organization: 'Organisation modèle',
        },
        detail: {
            requester_info: 'Informations du demandeur',
            status: 'Statut',
            assignment: 'Attribution',
            notes: 'Remarques',
            audit_trail: "Piste d'audit",
            add_note: 'Ajouter une note',
            case_not_found: 'Dossier introuvable',
            load_error: 'Échec du chargement du dossier. Veuillez réessayer.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: "Échec de l'ajout de la note. Veuillez réessayer.",
            submitted_from: 'Soumis depuis',
            via_cmp: 'via AesirX CMP',
            cmp_ticket: 'Ticket CMP :',
            select_template: 'Sélectionner un modèle',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Détails',
                evidence: 'Preuves',
                communications: 'Échanges',
                escalations: 'Escalades',
                audit_trail: "Piste d'audit",
                fulfillment: 'Exécution',
                response: 'Réponse',
                extension: 'Prolongation',
            },
            requester: {
                name: 'Nom',
                email: 'E-mail',
                phone: 'Téléphone',
                language: 'Langue',
                source: 'Provenance',
                customer_ref: 'Réf. client',
                country: 'Pays',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Canal préféré',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Dans les délais',
                due_soon: 'Échéance proche',
                overdue: 'En retard',
                days_remaining: 'j restants',
                due_date: "Date d'échéance",
                breached: 'Dépassé',
                paused: 'En pause',
                total_paused_days: 'Total jours en pause',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Jours écoulés',
                progress: 'Progression',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: "Vérification d'identité",
                verified: 'Vérifié',
                not_verified: 'Non vérifié',
                method: 'Méthode',
                notes: 'Remarques',
                verified_by: 'Vérifié par',
                verified_at: 'Vérifié le',
                warning: "L'identité n'a pas encore été vérifiée.",
                verify_button: "Vérifier l'identité",
                select_method: '-- Sélectionner --',
                methods: {
                    email_confirmation: 'Confirmation par e-mail',
                    id_document: "Pièce d'identité",
                    phone_call: 'Appel téléphonique',
                    in_person: 'En personne',
                    other: 'Autre',
                },
            },
            closure: {
                reason: 'Motif de clôture',
                summary: 'Résumé de clôture',
                summary_placeholder: 'Résumé de la résolution de la demande...',
                reason_required: 'Le résumé et le motif de clôture sont requis',
                response_not_sent: 'Une réponse formelle doit être envoyée au sujet des données avant de clôturer ce dossier',
                reasons: {
                    fulfilled: 'Satisfait',
                    partially_fulfilled: 'Partiellement satisfait',
                    unfounded: 'Non fondé',
                    excessive: 'Excessif',
                    withdrawn: 'Retiré',
                    duplicate: 'Doublon',
                    identity_unverified: 'Identité non vérifiée',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exporter',
            export_pdf: 'Exporter en PDF',
            export_zip: 'Exporter en ZIP',
            case_file_pdf: 'Exporter PDF (dossier)',
            evidence_pack_zip: 'Exporter ZIP (Pack de preuves)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Signaler comme étant à l’origine d’une violation',
                modal: {
                    title: 'Signaler le cas comme étant à l’origine d’une violation',
                    type_label: 'Type de violation',
                    type: {
                        misdirected_response: 'Réponse mal adressée',
                        unauthorised_disclosure: 'Divulgation non autorisée',
                        identity_mismatch: "Inadéquation d'identité",
                        other: 'Autre',
                    },
                    description: 'Description (facultatif)',
                    description_placeholder: "Ce qui s'est passé? (maximum 1 000 caractères)",
                    confirm: 'Signaler et démarrer le chronomètre de notification',
                    cancel: 'Annuler',
                    flagging: 'Signalement...',
                },
                banner: {
                    title: 'Violation signalée : {type}',
                    detected_at: 'Détecté à {timestamp}',
                    filing_deadline: 'Délai de notification : dans {hours} heures',
                    overdue: 'Délai de notification : DÉPASSÉ',
                },
                toasts: {
                    flagged: "Affaire signalée comme étant à l'origine d'une violation - le chronomètre de 72 h a démarré",
                    flag_failed: "Échec de la signalisation du cas comme étant à l'origine d'une violation",
                },
            },
            export: {
                generating: 'Génération... veuillez patienter',
                success: 'Exportation enregistrée dans le navigateur de fichiers',
                failed: "Échec de l'exportation : vérifiez le journal d'audit",
            },
            no_notes: 'Aucune note pour le moment',
            assign_placeholder: 'ID utilisateur à assigner...',
            reassign: 'Réassigner',
            evidence: {
                checklist_title: 'Liste de contrôle des preuves',
                complete_pct: '% terminé',
                no_checklist: 'Aucun élément dans la liste de contrôle',
                attachments: 'Pièces jointes',
                upload_attachment: 'Télécharger une pièce jointe',
                upload_button: 'Télécharger',
                uploading: 'Téléchargement...',
                attachment_uploaded: 'Pièce jointe téléchargée avec succès',
                upload_error: 'Échec du téléchargement',
                category: 'Catégorie',
                categories: {
                    identity_proof: "Preuve d'identité",
                    correspondence: 'Correspondance',
                    internal_data: 'Données internes',
                    legal: 'Légal',
                    other: 'Autre',
                },
                no_attachments: 'Aucune pièce jointe',
                references_title: 'Références de preuves',
                no_references: 'Aucune référence',
                add_reference: 'Ajouter une référence',
                ref_type: 'Catégorie',
                ref_value: 'Valeur',
                ref_desc: 'Descriptif',
                ref_value_placeholder: 'URL, ID e-mail ou référence...',
                ref_desc_placeholder: 'Descriptif...',
                ref_types: {
                    url: 'URL',
                    email_id: 'ID e-mail',
                    crm_record: 'Enregistrement CRM',
                    ticket_ref: 'Référence du ticket',
                    other: 'Autre',
                },
            },
            communications: {
                title: 'Communications',
                add: 'Ajouter un message',
                no_records: 'Aucune communication enregistrée',
                direction: 'Sens',
                channel: 'Canal',
                visibility: 'Visibilité',
                subject: 'Objet',
                body: 'Corps',
                send: 'Envoyer',
                inbound: 'Entrant',
                outbound: 'Sortant',
                internal: 'Interne',
                external: 'Externe',
                tab_all: 'All',
                tab_internal: 'Interne',
                tab_external: 'Externe',
                subject_placeholder: 'Objet (facultatif)',
                body_placeholder: 'Corps du message...',
                channels: {
                    email: 'E-mail',
                    portal: 'Portail',
                    phone: 'Téléphone',
                    postal: 'Courrier postal',
                    in_person: 'En personne',
                },
                apply_template: 'Appliquer le Modèle',
                select_template: 'Sélectionner un modèle...',
                no_templates: 'Aucun modèle disponible',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Modèle appliqué à la réponse.',
                template_apply_error: "Impossible d'appliquer le modèle.",
            },
            escalations: {
                title: "Historique d'escalade",
                no_events: "Aucun événement d'escalade",
                tier: 'Niveau',
                threshold: 'Seuil',
                action: 'Mesure',
                target: 'Cible',
                triggered_at: 'Déclenché le',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'Synchronisation AesirX CMP',
                sync_status: 'Statut de synchronisation',
                last_synced: 'Dernière synchronisation',
                not_synced: 'Non synchronisé',
                resync: 'Resynchroniser maintenant',
                syncing: 'Synchronisation en cours...',
                sync_history: 'Historique de synchronisation (10 dernières)',
                direction: 'Sens',
                time: 'Heure',
                retries: 'Tentatives',
                error_col: 'Erreur',
            },
            audit: {
                filter_event_placeholder: "Filtrer par type d'événement...",
                filter_actor_placeholder: 'Filtrer par acteur...',
            },
            fulfillment: {
                title: 'Exécution par département',
                assign_departments: 'Attribuer les départements',
                department: 'Département',
                assignee: 'Responsable',
                deadline: 'Échéance',
                evidence_count: 'Preuves',
                signoff: 'Validation',
                signed_off: 'Validé',
                pending_signoff: 'En attente',
                start_button: 'Démarrer',
                complete_button: 'Marquer comme terminé',
                signoff_button: 'Se déconnecter',
                no_tasks: "Aucune tâche d'exécution attribuée.",
                tasks_table: "Tâches d'exécution",
                progress_label: "Progression de l'exécution",
                overdue: 'en retard',
                assigned_success: "Tâche d'exécution départementale attribuée avec succès.",
                assign_error: "Échec de l'attribution de la tâche d'exécution.",
                signoff_success: 'Validation départementale enregistrée.',
                signoff_error: "Échec de l'enregistrement de la validation.",
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Ajouter des preuves',
                evidence_uploaded: 'Preuve téléchargée et liée',
                evidence_upload_error: 'Échec du téléchargement',
                suggest_data_stores: 'Suggérer des entrepôts de données',
                suggest_error: 'Échec du chargement des suggestions de stockage.',
                suggest_stores_title: 'Magasins de données suggérés',
                suggest_stores_none: 'Aucun magasin de données ne correspond à ce cas. Configurez les systèmes dans Data Mapping pour améliorer les suggestions.',
                suggest_stores_add: 'Ajouter au cas',
                suggest_stores_added: 'Magasin de données ajouté au dossier',
                no_departments_title: 'Aucun département configuré',
                no_departments_body: "Les départements vous permettent d'acheminer les tâches d'exécution DSR vers une fonction (Confidentialité, Juridique, RH, Opérations informatiques). Installez des exemples de départements ou demandez à votre administrateur de configurer les départements dans les paramètres de l'organisation.",
                no_departments_cta: 'Installer des exemples de données',
                department_placeholder: 'Nom du département...',
                assignee_placeholder: 'ID utilisateur à attribuer...',
                assign_button: 'Attribuer',
                status_pending: 'En attente',
                status_in_progress: 'En cours',
                status_completed: 'Terminé',
                status_verified: 'Vérifié',
                confirm_approve: 'Êtes-vous sûr de vouloir approuver cette prolongation ?',
                confirm_deny: 'Êtes-vous sûr de vouloir refuser cette prolongation ?',
            },
            response: {
                title: 'Génération de réponse',
                select_template: 'Modèle de réponse',
                choose_template: 'Sélectionner un modèle...',
                generate: 'Générer la réponse',
                preview: 'Aperçu de la réponse',
                send: 'Envoyer la réponse',
                delivery_method: {
                    label: 'Méthode de livraison',
                    email: 'Courriel',
                    portal: 'Portail',
                    postal: 'Courrier postal',
                    in_person: 'En personne',
                },
                recipient: {
                    email: 'E-mail du destinataire',
                    portal: 'ID utilisateur du portail',
                    address: 'Adresse postale',
                },
                auto_generate_btn: 'Générer et envoyer une réponse',
                auto_generate_success: 'Réponse générée et délivrée',
                auto_generate_missing_recipient: "Veuillez indiquer un destinataire avant l'envoi.",
                auto_fields: 'Champs remplis automatiquement',
                case_id: 'ID du dossier',
                requester_name: 'Nom du demandeur',
                rights_type: 'Type de droit',
                history: 'Réponses envoyées',
                generated_at: 'Généré',
                confirmation: 'Confirmation',
                sent_label: 'Envoyé',
                draft_label: 'Brouillon',
                generated_success: 'Réponse générée avec succès.',
                generate_error: 'Échec de la génération de la réponse.',
                sent_success: 'Réponse envoyée avec succès.',
                send_error: "Échec de l'envoi de la réponse.",
                load_error: 'Échec du chargement des modèles de réponse.',
                pdf_title: 'Lettre de réponse à la personne concernée',
                download_pdf: 'Télécharger le PDF',
                download_all: 'Tout télécharger',
                download_error: 'Échec du téléchargement de la réponse',
            },
            extension: {
                title: 'Prolongations de délai',
                acknowledgment_deadline: "Délai d'accusé de réception",
                fulfillment_deadline: "Délai d'exécution",
                request_extension: 'Demander une prolongation',
                reason: 'Motif',
                reason_placeholder: 'Motif de la demande de temps supplémentaire...',
                additional_days: 'Jours supplémentaires',
                submit_request: 'Soumettre la demande de prolongation',
                history: 'Historique des prolongations',
                requested_at: 'Demandé',
                approve: 'Approuver',
                deny: 'Refuser',
                request_success: 'Demande de prolongation soumise.',
                request_error: 'Échec de la soumission de la demande de prolongation.',
                approved_success: 'Prolongation approuvée.',
                denied_success: 'Prolongation refusée.',
                decision_error: 'Échec du traitement de la décision de prolongation.',
                status_pending: 'En attente',
                status_approved: 'Approuvé',
                status_denied: 'Refusé',
                confirm_approve: 'Êtes-vous sûr de vouloir approuver cette demande de prolongation ?',
                confirm_deny: 'Êtes-vous sûr de vouloir refuser cette demande de prolongation ?',
                decided_by: 'Décidé par',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Exporter en CSV',
            exporting: 'Export en cours...',
            search_placeholder: "Rechercher dans les détails d'audit...",
            filter_by_actor: 'Filtrer par acteur...',
            all_event_types: "Tous les types d'événements",
            seq: '#',
            timestamp: 'Horodatage',
            event: 'Événement',
            case_col: 'Dossier',
            actor: 'Acteur',
            summary: 'Résumé',
            no_events: "Aucun événement d'audit trouvé",
            export_success: "Piste d'audit exportée avec succès.",
            export_error: "Échec de l'export CSV d'audit",
            load_error: "Échec du chargement de la piste d'audit. Veuillez réessayer.",
            announce_loaded: "Piste d'audit chargée",
            start_date: 'Date de début',
            end_date: 'Date de fin',
            per_page: '{count} / page',
            unknown: 'Inconnu',
            source_filter: {
                label: 'Origine',
                all: 'Tout',
                module: 'Module',
                forseti: 'Forseti',
                aria_label: "Filtrer les événements d'audit par source",
            },
            event_types: {
                case_created: 'Dossier créé',
                status_changed: 'Statut modifié',
                case_assigned: 'Dossier assigné',
                note_added: 'Note ajoutée',
                attachment_added: 'Pièce jointe ajoutée',
                attachment_deleted: 'Pièce jointe supprimée',
                reference_added: 'Référence ajoutée',
                checklist_updated: 'Liste de contrôle mise à jour',
                identity_verified: 'Identité vérifiée',
                closure_summary_added: 'Résumé de clôture',
                approval_granted: 'Approbation accordée',
                template_applied: 'Modèle appliqué',
                export_generated: 'Export généré',
                sla_warning: 'Avertissement SLA',
                sla_overdue: 'SLA dépassé',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: "Piste d'Audit Unifiée",
                module_filter: 'Module',
                framework_filter: 'Référentiel',
                select_framework: 'Sélectionner un référentiel...',
                entity_type_filter: "Type d'entité",
                action_filter: 'Action',
                actor_filter: 'Acteur',
                select_actor: 'Sélectionner un acteur...',
                search_filter: 'Recherche',
                search_placeholder: "Rechercher des événements d'audit...",
                filters_heading: 'Filtres',
                date_range: 'Plage de dates',
                no_events_found: "Aucun événement d'audit trouvé.",
                event_count: "Nombre d'événements",
                action: 'Action',
                actor: 'Acteur',
                date_range_start: 'Début de la plage de dates',
                date_range_end: 'Fin de la plage de dates',
                filtering_events: "Filtrage des événements d'audit",
                framework_codes: 'Codes de cadre',
                entity_type_col: "Type d'entité",
                aria_table: "Événements d'audit unifiés",
                page_of: 'Page {page} sur {total}',
                entity_types: {
                    user: 'Utilisateur',
                    case: 'Cas',
                    incident: 'Incident',
                    assessment: 'Évaluation',
                    vendor: 'Vendeur',
                    consent: 'Consentement',
                    policy: 'Politique',
                    data_map: 'Carte des données',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Créer',
                    update: 'Mise à jour',
                    delete: 'Supprimer',
                    view: 'Voir',
                    export: 'Exporter',
                    status_change: 'Changement de statut',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: "Paquets de Preuves d'Audit",
                assemble_pack: 'Assembler un Paquet de Preuves',
                framework: 'Référentiel',
                select_framework: 'Sélectionner un référentiel...',
                framework_code_label: 'Code du cadre',
                entity_types: "Types d'entités",
                select_entity_types: "Sélectionner les types d'entités...",
                period: 'Période',
                period_start: 'Début de la période',
                period_end: 'Fin de la période',
                completeness: 'Complétude',
                no_packs_found: 'Aucun paquet de preuves trouvé.',
                verify: 'Vérifier',
                export_pack: 'Exporter',
                exporting: 'Exportation en cours...',
                pack_assembled: 'Pack de preuves assemblé',
                pack_exported: 'Pack de preuves exporté',
                aria_table: "Packs de preuves d'audit",
                aria_export_pack: 'Exporter le pack',
                export_format: "Format d'exportation",
                export_in_progress: 'Exportation en cours...',
                status_draft: 'Brouillon',
                status_assembled: 'Assemblé',
                status_verified: 'Vérifié',
                status_submitted: 'Soumis',
                entity_type_labels: {
                    user: 'Utilisateur',
                    case: 'Cas',
                    incident: 'Incident',
                    assessment: 'Évaluation',
                    vendor: 'Vendeur',
                    consent: 'Consentement',
                    policy: 'Politique',
                    data_map: 'Carte des données',
                },
                template_id: 'ID de modèle',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Module',
                    action: 'Action',
                    actor_id: 'Acteur',
                    actor_role: 'Actor Role',
                    entity_type: "Type d'entité",
                    entity_id: "ID d'entité",
                    summary: 'Résumé',
                    timestamp: 'Horodatage',
                    record_type: 'Record Type',
                    framework_code: 'Cadre',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Pack de preuves — Cadre : {framework}',
                export_header_period: 'Période : {start} à {end}',
                export_header_completeness: 'Complétude : {score}%',
                export_header_status: 'Statut : {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: "Périodes d'Audit",
                create_period: "Créer une Période d'Audit",
                framework_code_label: 'Code du cadre',
                select_framework: 'Sélectionner un référentiel...',
                period_label: 'Libellé de période',
                period_start: 'Début de la période',
                period_end: 'Fin de la période',
                start: 'Commencer',
                end: 'Fin',
                framework: 'Cadre',
                retention_days: 'Jours de rétention',
                section_period_dates: 'Dates de la période',
                no_periods_found: "Aucune période d'audit trouvée.",
                period_created: "Période d'audit créée",
                aria_table: "Périodes d'audit",
                suggested_boundaries: 'Limites suggérées',
                suggested_boundaries_info: 'Les limites de période suggérées sont dérivées du cycle de reporting réglementaire de chaque cadre. Ajustez selon les besoins de votre organisation.',
                reporting_cycle: 'Cycle de reporting',
                suggested_start: 'Début suggéré',
                suggested_end: 'Fin suggérée',
                suggested_next: 'Période suivante suggérée',
                cycle_monthly: 'Mensuel',
                cycle_quarterly: 'Trimestriel',
                cycle_semi_annual: 'Semestriel',
                cycle_annual: 'Annuel',
                cycle_custom: 'Personnalisé',
                period_deleted: "Période d'audit supprimée",
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Référence client',
            country: 'Pays / Juridiction',
            preferred_channel: 'Canal préféré',
            channels: {
                email: 'E-mail',
                postal: 'Courrier postal',
                phone: 'Téléphone',
                portal: 'Portail',
            },
            source: 'Origine',
            sources: {
                admin_manual: "Manuel d'administration",
                public_form: 'Formulaire public',
                cmp_gateway: 'CMP Passerelle',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Téléphone reçu',
                postal_received: 'Poste reçu',
                email_received: 'E-mail reçu',
                in_person: 'En personne',
                legacy_import: 'Importation héritée',
            },
            required_fields: "Le type de demande, le nom et l'e-mail sont requis",
            create_error: 'Échec de la création du dossier',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Paramètres',
            request_types: 'Types de demande',
            request_types_desc: 'Configurez les types de demande activés pour le formulaire public.',
            notifications: 'Avis',
            ticket_numbering: 'Numérotation des tickets',
            ticket_format: 'Format actuel',
            ticket_prefix: 'Préfixe',
            ticket_prefix_note: 'non configurable',
            sla_deadlines: 'Délais SLA',
            sla_desc: 'Définissez le délai de réponse maximal (en jours calendaires) pour chaque type de demande. Les dossiers approchant du seuil afficheront des avertissements.',
            days: 'jours',
            warning_threshold: "Seuil d'avertissement",
            escalation_label: "Activer l'escalade automatique en cas de dépassement du SLA",
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Enregistrer la configuration SLA',
            saving: 'Enregistrement...',
            saved: 'Enregistré avec succès',
            announce_loaded: 'Paramètres chargés',
            email_on_creation: 'Notification par e-mail à la création du ticket',
            send_confirmation: 'Envoyer un e-mail de confirmation au demandeur',
            users: 'Utilisateurs',
            roles: 'Rôles',
            api_keys: 'API Clés',
            mfa: 'MFA',
            sessions: 'Séances',
            webhooks: 'Webhooks',
            modules: 'Modules',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: "Droit d'accès (DSAR)",
                deletion: 'Effacement',
                correction: 'Droit de rectification',
                portability: 'Portabilité',
                objection: 'Opposition',
                restriction: 'Limitation',
                consent_withdrawal: 'Retrait du consentement',
                protective_measures: 'Mesures de protection',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Attributions de sections',
            assign_section: 'Attribuer une section',
            section_ref: 'Référence de section',
            section_label: 'Libellé de section',
            department_id: 'Département',
            assignee_id: 'Responsable',
            mandatory: 'Obligatoire',
            assign: 'Attribuer',
            no_assignments: 'Aucune attribution de section',
            status: 'Statut',
            completion: 'Achèvement',
            mark_complete: 'Terminer',
        },
        consolidation: {
            title: 'Avancement de la consolidation',
            add_child: 'Ajouter une sous-tâche',
            child_task_id: 'Identifiant de sous-tâche',
            department_id: 'Département',
            link: 'Lien',
            overall_completion: 'Achèvement global',
            departments_complete: 'Départements terminés',
            overdue: 'En retard',
            ready: 'Prêt',
            not_ready: 'Pas prêt',
            master_status: 'Statut global',
            department_breakdown: 'Détail par département',
            department: 'Département',
            status: 'Statut',
            completion: 'Achèvement',
            due_date: "Date d'échéance",
            overdue_flag: 'En retard',
            child_tasks: 'Sous-tâches',
            task: 'Tâche',
        },
        conflict_detection: {
            title: 'Détection de conflits',
            unresolved: 'non résolu',
            no_conflicts: 'Aucun conflit détecté',
            section: 'Section',
            field: 'Champ',
            dept_a: 'Département A',
            value_a: 'Valeur A',
            dept_b: 'Département B',
            value_b: 'Valeur B',
            severity: 'Gravité',
            status: 'Statut',
            resolved: 'Résolu',
            open: 'Ouvert',
            resolve: 'Résoudre',
        },
        org_maturity: {
            title: "Maturité de l'Organisation",
            departments: 'Départements',
            department_hierarchy: 'Hiérarchie des Départements',
            create_department: 'Créer un Département',
            department_name: 'Nom du Département',
            department_code: 'Coder',
            level: 'Niveau',
            division: 'Division',
            department: 'Département',
            team: 'Équipe',
            parent_department: 'Département Parent',
            head_user: 'Chef de Département',
            no_departments: 'Aucun département configuré.',
            user_mappings: "Affectations d'Utilisateurs",
            map_user: 'Affecter un Utilisateur',
            user_id: 'ID Utilisateur',
            role: 'Rôle',
            no_users: 'Aucun utilisateur affecté à ce département.',
            remove: 'Supprimer',
            framework_assignments: 'Affectations de Référentiels',
            assign_framework: 'Affecter un Référentiel',
            entity_id: "ID de l'Entité",
            entity_type: "Type d'Entité",
            framework_id: 'ID du Référentiel',
            framework_code: 'Code du Référentiel',
            no_assignments: 'Aucune affectation de référentiel.',
            compliance_rollup: 'Synthèse de Conformité',
            entity_name: 'Entité',
            total_assessments: 'Évaluations Totales',
            completed: 'Terminé',
            completion_pct: 'Achèvement %',
            open_gaps: 'Écarts Ouverts',
            critical_gaps: 'Critiques',
            compliance_score: 'Score',
            no_rollup: 'Aucune donnée de synthèse de conformité disponible.',
            entity_summary: "Résumé de Conformité de l'Entité",
            frameworks_assigned: 'Référentiels',
            overall_score: 'Score Global',
            load_departments_error: 'Échec du chargement des départements',
            load_users_error: 'Échec du chargement des utilisateurs',
            department_created: 'Département créé',
            department_created_announce: 'Département créé',
            create_department_error: 'Échec de la création du département',
            user_mapped: 'Utilisateur associé',
            user_mapped_announce: 'Utilisateur associé au département',
            map_user_error: "Échec de l'association de l'utilisateur",
            user_removed: 'Utilisateur supprimé',
            load_rollup_error: 'Échec du chargement du résumé',
            load_assignments_error: 'Échec du chargement des attributions',
            framework_assigned: 'Cadre affecté',
            framework_assigned_announce: "Cadre affecté à l'entité",
            assign_framework_error: "Échec de l'attribution du cadre",
            assignment_removed: 'Affectation supprimée',
            enter_entity_id_placeholder: "Entrez l'ID de l'entité pour voir les affectations",
            entity_type_organization: 'Organisation',
            entity_type_department: 'Département',
        },
        onboarding_maturity: {
            title: "Maturité d'intégration",
            quick_starts: 'Démarrages rapides',
            progress: "Progression de l'intégration",
            personas: 'Chemins de personnalité',
            persona_onboarding: 'Intégration des personnages',
            quick_start_scenarios: 'Scénarios de démarrage rapide',
            start: 'Démarrer',
            restart: 'Redémarrer',
            estimated_minutes: 'Minutes estimées',
            steps: 'Étapes',
            scenario: 'Scénario',
            no_scenarios: 'Aucun scénario de démarrage rapide disponible.',
            persona_id: 'Identifiant personnel',
            persona_label: 'Personnalité',
            role_description: 'Description du rôle',
            primary_modules: 'Modules principaux',
            recommended_tours: 'Visites recommandées',
            sample_data_modules: 'Exemples de modules de données',
            quick_start_scenario: 'Scénario de démarrage rapide',
            no_personas: 'Aucun chemin personnel disponible.',
            framework_setup: 'Cadres réglementaires',
            framework_code: 'Code-cadre',
            framework_name: 'Nom du cadre',
            related_modules: 'Modules associés',
            persona_relevance: 'Personnalités pertinentes',
            no_frameworks: 'Aucune option de configuration du framework disponible.',
            tier_config: 'Configuration du module de niveau',
            tier: 'Niveau',
            available_modules: 'Modules disponibles',
            module_count: 'Nombre de modules',
            overall_completion: 'Achèvement global',
            wizard_completed: 'Assistant terminé',
            tours_completed: 'Visites terminées',
            quick_starts_completed: 'Démarrages rapides terminés',
            selected_frameworks: 'Cadres sélectionnés',
            no_progress: 'Aucune donnée de progression de l’intégration disponible.',
            detected_persona: 'Personnalité détectée',
            current_tier: 'Niveau actuel',
            completion_pct: 'Achèvement',
            progress_page: {
                loaded: 'Progression chargée',
                no_progress: "Aucune donnée de progression d'intégration disponible.",
                overall: 'Progression globale',
                detected_persona: 'Persona détecté',
                tier: 'Niveau',
                modules: 'modules',
                wizard: 'Assistant',
                completed: 'Terminé',
                pending: 'En attente',
                tours: 'Visites guidées',
                quick_starts: 'Démarrages rapides',
                frameworks: 'Cadres sélectionnés',
                no_frameworks: 'Aucun cadre sélectionné.',
                go_to_wizard: "Terminer l'assistant de configuration",
                framework_selection: 'Sélection du cadre',
                framework_toggle: 'Activer ou désactiver les cadres pour votre programme de conformité.',
                framework_updated: 'Sélection du cadre mise à jour.',
                view_tours: 'Voir les visites guidées',
                view_quick_starts: 'Voir les démarrages rapides',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Sélectionnez un persona pour voir les modules, visites guidées et scénarios de démarrage rapide recommandés.',
                no_personas: 'Aucun parcours de persona disponible.',
                modules: 'Modules',
                tours: 'Visites guidées',
                quick_start: 'Démarrage rapide',
                details: 'Détails',
                primary_modules: 'Modules principaux',
                recommended_tours: 'Visites guidées recommandées',
                framework_options: 'Cadres réglementaires',
                no_frameworks: 'Aucune option de configuration du cadre disponible.',
                no_frameworks_explain: "Aucun cadre réglementaire n'est encore installé pour votre organisation, il n'y a donc rien à configurer ici.",
                no_frameworks_action: 'Installez un pack de marché pour ajouter les cadres qui vous concernent',
                start_tour: 'Démarrer',
                no_tours: 'Aucune visite guidée disponible.',
                help_center: "Centre d'aide",
                no_articles: 'Aucun article trouvé.',
                primary: 'Principal',
                framework_code: 'Code du cadre',
                framework_label: 'Libellé',
                region: 'Région',
                category: 'Catégorie',
                enabled: 'Activé',
                disabled: 'Désactivé',
                tier_modules: 'Configuration des modules par niveau',
                tiers_loaded: 'Configurations de niveaux chargées',
                no_tier_config: 'Aucune configuration de niveau disponible.',
                view_tours: 'Voir les visites de modules',
                view_quick_start: 'Lancer le démarrage rapide',
                toggle_framework: 'Basculer le cadre',
                framework_updated: 'Sélection du cadre mise à jour.',
                tier_desc_light: 'Essentiels de confidentialité — bases de conformité PDPL',
                tier_desc_standard: 'Suite de conformité complète — tous les modules avec workflows',
                tier_desc_enterprise: 'Gouvernance avancée et automatisation — multi-entité, rapports de groupe',
                persona_highlight: 'Vos modules',
                aria_go_to_module: '{name} — aller au module',
                aria_start_tour: 'Démarrer {title}',
                aria_quick_start: 'Démarrage rapide : {title}',
                aria_help_article: "Article d'aide : {title}",
                aria_view_framework: '{label}{primary} — afficher les détails du framework',
                aria_primary_for_persona: '{name} (principal pour ce personnage)',
                primary_for_persona_suffix: ' (primaire pour ce personnage)',
                aria_view_in_persona_paths: '{name} — affichage dans les chemins personnels',
                progress_of_total: '{completed} sur {total} terminés',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Pratiquez les flux de travail courants avec des scénarios de démarrage rapide guidés.',
                no_scenarios: 'Aucun scénario de démarrage rapide disponible.',
                started: 'Démarrage rapide lancé',
                status_completed: 'Terminé',
                status_in_progress: 'En cours',
                status_not_started: 'Non commencé',
                minutes: 'min',
                steps: 'étapes',
                step: 'Étape',
                restart: 'Redémarrer',
                start: 'Démarrer',
                go_to_module: 'Aller au module',
                next_step: 'Étape suivante',
                complete: 'Terminer',
                reset: 'Réinitialiser',
                step_of: 'sur',
                action_navigate: 'Naviguer',
                action_form_fill: 'Remplir le formulaire',
                action_review: 'Vérifier',
                action_confirm: 'Confirmer',
                step_changed: 'Étape {current} sur {total} : {title}',
                completed_message: 'Démarrage rapide terminé avec succès !',
                reset_message: 'Le démarrage rapide a été réinitialisé.',
            },
            persona_p_vn_01_label: 'Entreprise DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Propriétaire du département',
            persona_p_vn_02_description: 'Chef de service responsable des rapports de conformité sectoriels',
            persona_p_vn_03_label: 'Conformité du commerce électronique',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Responsable de la gouvernance des données',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cybersécurité et opérations juridiques',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Responsable de la gouvernance',
            persona_p_vn_07_description: 'Responsable de la supervision de la gouvernance du système, des évaluations des risques et de la classification de AI',
            persona_p_vn_08_label: 'Gouvernement transfrontalier et fournisseur',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Responsable de la réponse aux violations',
            persona_p_vn_09_description: 'Responsable de la coordination des notifications de violations et de la réponse aux incidents',
            persona_p_vn_10_label: "Responsable de l'audit interne",
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'Responsable des données RH et employés',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Responsable conformité filiale multinationale',
            persona_p_vn_12_description: "DPO dans une filiale vietnamienne d'une entreprise étrangère nécessitant une couche de conformité locale",
            persona_p_vn_13_label: 'Responsable conformité éducation',
            persona_p_vn_13_description: 'Responsable conformité dans un établissement scolaire étranger gérant la protection des données des élèves et des parents',
            qs_hr_consent_title: 'Enregistrer les consentements des employés',
            qs_hr_consent_description: "Parcourir l'enregistrement du consentement des employés et générer un rapport de couverture.",
            qs_subsidiary_dpia_title: 'Créer une évaluation AIPD de filiale',
            qs_subsidiary_dpia_description: "Parcourir la création d'une AIPD pour une activité de traitement de filiale.",
            qs_student_consent_title: 'Configurer la collecte de consentement étudiant',
            qs_student_consent_description: 'Parcourir la création de politiques de consentement parental pour les données des élèves.',
            persona_p_vn_14_label: 'Conseiller juridique externe',
            persona_p_vn_14_description: "Associé principal d'un cabinet d'avocats, conseillant plusieurs organisations clientes sur la conformité de PDPL",
            persona_p_vn_15_label: "Responsable des opérations du cabinet d'avocats",
            persona_p_vn_15_description: "Coordonnateur de l'engagement client gérant le suivi des livrables et la coordination des délais",
            persona_p_vn_16_label: "Propriétaire d'entreprise / CEO",
            persona_p_vn_16_description: "CEO ou GM qui est légalement le responsable du traitement des données sous PDPL, responsable de l'investissement en matière de conformité",
            qs_dpia_filing_title: 'Complétez votre premier dépôt DPIA',
            qs_dept_compliance_title: 'Soumettez votre première section de conformité du service',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Configurer la gestion des données des abonnés télécoms',
            qs_data_flow_title: 'Cartographiez votre premier flux de données',
            qs_incident_response_title: 'Configurer le workflow de réponse aux incidents',
            qs_ai_risk_title: 'Exécuter une évaluation des risques système AI',
            qs_cross_border_title: 'Évaluer un transfert de données transfrontalier',
            qs_breach_notification_title: 'Simuler un workflow de notification de violation',
            qs_audit_trail_title: 'Examiner une piste d’audit de conformité',
            qs_legal_advisor_portfolio_title: 'Examiner le portefeuille de conformité multi-clients',
            qs_legal_advisor_portfolio_description: 'Examinez l’état de conformité de plusieurs organisations clientes.',
            qs_law_firm_ops_title: 'Suivre l’état des livrables du client',
            qs_law_firm_ops_description: 'Surveiller les progrès des livrables de conseil et l’engagement des clients.',
            qs_ceo_dashboard_title: 'Afficher le tableau de bord de conformité des dirigeants',
            qs_ceo_dashboard_description: 'Examinez l’état de conformité de haut niveau et les mesures d’investissement.',
        },
        rights_requests: {
            response_templates: {
                title: 'Modèles de réponse',
                subtitle: 'Gérez les modèles de lettre de réponse DSR que ResponseGenerator utilise pour rédiger les réponses aux personnes concernées.',
                create_btn: 'Nouveau modèle',
                edit_btn: 'Modifier le modèle',
                filter_right_type: 'Filtrer par type approprié',
                filter_locale: 'Filtrer par paramètres régionaux',
                filter_status: 'Filtrer par statut',
                all_right_types: 'Tous les bons types',
                any_right_type: "Tout (s'applique à tous les types)",
                all_locales: 'Tous les paramètres régionaux',
                all_statuses: 'Tout',
                status_active: 'Actif',
                status_inactive: 'Inactif',
                empty_state: 'Aucun modèle ne correspond aux filtres actuels.',
                columns: {
                    template_id: 'ID du modèle',
                    outcome: 'Résultat',
                    locale: 'Paramètres régionaux',
                    right_type: 'Type correct',
                    is_active: 'Statut',
                    last_updated: 'Dernière mise à jour',
                    actions: 'Actions',
                },
                actions: {
                    edit: 'Modifier',
                    clone: 'Cloner',
                    deactivate: 'Désactiver',
                    reactivate: 'Réactiver',
                    preview: 'Aperçu',
                },
                form: {
                    template_id: 'ID du modèle',
                    template_id_help: 'Identifiant minuscule, par ex. dsr_access_fulfilled_vi',
                    outcome: 'Résultat',
                    locale: 'Paramètres régionaux',
                    right_type: 'Type correct',
                    subject: 'Sujet',
                    body: 'Modèle de corps',
                    body_help: 'Utilisez des espaces réservés tels que {case_reference}, {data_subject_name}, {fulfilled_at}. Minimum 15 lignes pour les lettres à plusieurs sections.',
                    is_active: 'Actif',
                    official_form_id: 'ID du formulaire officiel (facultatif)',
                },
                outcomes: {
                    fulfilled: 'Réalisé',
                    partially_fulfilled: 'Partiellement réalisé',
                    denied: 'Refusé',
                },
                clone_modal_title: 'Modèle de clonage',
                clone_target_locale: 'Paramètres régionaux cibles',
                clone_help: 'Un nouveau modèle sera créé dans les paramètres régionaux cible en utilisant ce modèle comme point de départ.',
                preview_modal_title: 'Aperçu du modèle',
                preview_note: "L'aperçu utilise des espaces réservés DSR-DEMO-0020 du cas de démonstration. La livraison réelle remplace les données de cas réelles.",
                preview_unavailable: "L'aperçu nécessite un cas existant. Joignez ce modèle à un dossier et utilisez l'action Brouillon de réponse sur la page de détail du dossier.",
                deactivate_confirm: 'Désactiver ce modèle ? Il continuera de s’appliquer aux affaires existantes qui y font référence.',
                toasts: {
                    created: 'Modèle créé',
                    updated: 'Modèle mis à jour',
                    deactivated: 'Modèle désactivé',
                    reactivated: 'Modèle réactivé',
                    cloned: 'Modèle cloné',
                    load_error: 'Échec du chargement des modèles',
                    save_error: "Échec de l'enregistrement du modèle",
                    clone_error: 'Échec du clonage du modèle',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Départements',
                subtitle: 'Gérez les services qui acheminent les cas DSR, les tâches DPIA et les workflows de conformité.',
                create_btn: 'Nouveau département',
                edit_btn: 'Modifier le département',
                columns: {
                    name: 'Nom',
                    description: 'Descriptif',
                    active_cases: 'Cas actifs',
                    members: 'Membres',
                    status: 'Statut',
                    actions: 'Actions',
                },
                status_active: 'Actif',
                status_inactive: 'Inactif',
                form: {
                    name: 'Nom',
                    description: 'Descriptif',
                },
                actions: {
                    edit: 'Modifier',
                    deactivate: 'Désactiver',
                    reactivate: 'Réactiver',
                    delete: 'Supprimer',
                    manage_members: 'Gérer les membres',
                },
                delete_modal_title: 'Supprimer un département',
                delete_warning: 'Ce département a encore des dossiers actifs. Choisissez un département auquel les réaffecter avant la suppression.',
                delete_no_cases: "Ce service n'a aucun cas actif et peut être supprimé en toute sécurité.",
                reassign_prompt: 'Réaffecter les dossiers actifs à :',
                reassign_none_available: "Aucun autre département actif disponible. Créez d'abord un autre département.",
                member_section_title: 'Membres',
                add_member: 'Ajouter un membre',
                remove_member: 'Supprimer',
                member_role: 'Rôle',
                no_members: 'Aucun membre mappé à ce département.',
                toasts: {
                    created: 'Département créé',
                    updated: 'Département mis à jour',
                    deactivated: 'Département désactivé',
                    reactivated: 'Département réactivé',
                    deleted: 'Département supprimé',
                    reassigned_count: '{count} dossier(s) réaffecté(s)',
                    member_added: 'Membre ajouté',
                    member_removed: 'Membre supprimé',
                    load_error: 'Échec du chargement des départements',
                    save_error: "Échec de l'enregistrement du département",
                    delete_error: 'Échec de la suppression du département',
                },
            },
            retention_policies: {
                title: 'Politiques de rétention',
                subtitle: "Configurez les fenêtres de rétention par type de cible. Les documents de vérification d'identité l'utilisent pour tamponner retention_expires_at sur les futurs téléchargements.",
                create_btn: 'Nouvelle politique',
                edit_btn: 'Modifier la politique',
                columns: {
                    target_type: 'Type de cible',
                    retention_days: 'Rétention (jours)',
                    owner: 'Propriétaire de la police',
                    effective_from: 'En vigueur à partir de',
                    last_reviewed: 'Dernière révision',
                    status: 'Statut',
                    actions: 'Actions',
                },
                status_active: 'Actif',
                status_inactive: 'Inactif',
                form: {
                    target_type: 'Type de cible',
                    target_type_help: "À quelle catégorie de données la fenêtre de conservation s'applique. Actuellement, seuls les documents de vérification d'identité DSR sont pris en charge.",
                    retention_days: 'Fenêtre de rétention (jours)',
                    retention_days_help: 'Entre 1 et 3650 jours (10 ans max).',
                    owner: 'Propriétaire de la police',
                    owner_help: 'Généralement le DPO. Responsable de la décision de conservation sous DG-2.',
                    effective_from: 'En vigueur à partir de',
                },
                target_types: {
                    dsr_identity_verification_document: "Documents de vérification d'identité DSR",
                },
                deactivate_notice: "La désactivation n'affecte pas les pièces jointes déjà tamponnées : seuls les futurs téléchargements ne recevront plus de fenêtre de conservation.",
                deactivate_confirm: 'Désactiver cette politique de rétention ?',
                toasts: {
                    created: 'Politique de rétention créée',
                    updated: 'Politique de rétention mise à jour',
                    deactivated: 'Politique de rétention désactivée',
                    load_error: 'Échec du chargement des stratégies de rétention',
                    save_error: "Échec de l'enregistrement de la stratégie de rétention",
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Systèmes',
            categories: 'Catégories de données',
            registry: 'Registre de données',
            purposes: 'Finalités du traitement',
            retention: 'Conservation',
            flows: 'Flux de données',
            activities: 'Activités de traitement',
            evidence: 'Preuves',
            exports: 'Exportations',
            attestation: "Cycles d'attestation",
            governance: 'Gouvernance',
            review: 'Tâches de révision',
            inventory: 'Inventaire',
            exceptions: 'Exceptions',
            history: 'Historique',
            ropa_completeness: 'Complétude ROPA',
        },
        ropa_completeness_autofix: {
            ask_button: 'Demandez à Forseti',
            drafting: 'Rédaction…',
            accept: 'Accepter',
            reject: 'Rejeter',
            accepted: "Accepté — postulez via l'éditeur d'activité.",
            rejected: 'Rejeté.',
            needs_human_input: "Forseti a besoin de plus de contexte d'activité (par exemple un objectif documenté) avant de rédiger ce champ.",
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: "Rapport d'exhaustivité ROPA",
            subtitle: "Audit d'hygiène par domaine de vos enregistrements d'activités de traitement, limité par le cadre réglementaire.",
            framework_label: 'Cadre',
            kpi_total: 'Activités notées',
            kpi_critical: 'Critique',
            kpi_high: 'Élevé',
            kpi_conditional: 'Conditionnel',
            field_chart_heading: 'Champs manquants par fréquence',
            drilldown_heading: 'Activités avec des lacunes',
            col_activity: 'Activité',
            col_field: 'Champ',
            col_severity: 'Gravité',
            col_condition: 'État',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Critique',
            severity_high: 'Élevé',
            severity_conditional: 'Conditionnel',
            severity_informational: 'Informatif',
            include_informational: 'Inclure des informations',
            export_csv: 'Télécharger CSV',
            exporting: 'Exportation...',
            loading: 'Chargement…',
            empty_state: 'Toutes les activités actives ont des enregistrements ROPA complets pour ce cadre.',
            error_fetch: "Échec du chargement du rapport d'exhaustivité.",
            error_unsupported_framework: "Ce cadre n'est pas pris en charge.",
            error_framework_not_installed: "Ce framework n'est pas installé pour votre organisation.",
            no_misses_for_field: 'Aucune activité ne manque ce champ.',
            framework_PDPL: 'PDPL (Loi 91/2025 + Décret 356)',
            framework_GDPR: 'GDPR Article 30',
            framework_DECREE_165: 'Décret 165 (loi vietnamienne sur les données)',
            framework_ISO_27701: 'ISO 27701 (informatif)',
        },
        registry: {
            // Sprint 64.72.0 — en-têtes de colonnes de l’export du registre de données.
            export_col: {
                registry_item_id: 'ID de l’entrée de registre',
                name: 'Nom',
                description: 'Description',
                field_identifiers: 'Identifiants de champ',
                category_id: 'ID de catégorie',
                category_name: 'Nom de la catégorie',
                data_type: 'Type de données',
                effective_sensitivity: 'Sensibilité effective',
                sensitivity_override: 'Sensibilité dérogatoire',
                data_subjects: 'Personnes concernées',
                volume_band: 'Tranche de volume',
                source_system_ids: 'ID des systèmes sources',
                processing_activity_ids: 'ID des activités de traitement',
                retention_policy_id: 'ID de la politique de conservation',
                effective_retention_policy_id: 'ID de la politique de conservation effective',
                purposes: 'Finalités',
                collection_method: 'Mode de collecte',
                legal_basis: 'Base légale',
                data_tier: 'Niveau de données',
                data_tier_source: 'Origine du niveau de données',
                storage_locations: 'Lieux de stockage',
                cross_border: 'Transfrontalier',
                status: 'Statut',
                source_form_submission_id: 'ID de soumission du formulaire source',
                source_form_template_id: 'ID du modèle de formulaire source',
                last_reviewed_at: 'Dernier réexamen',
                next_review_at: 'Prochain réexamen',
                created_at: 'Créé le',
                updated_at: 'Mis à jour le',
                data_element: 'Élément de données',
                data_category: 'Catégorie de données',
                systems: 'Systèmes',
                retention_policy: 'Politique de conservation',
                name_of_processing: 'Nom du traitement',
                categories_of_personal_data: 'Catégories de données à caractère personnel',
                categories_of_data_subjects: 'Catégories de personnes concernées',
                recipients: 'Destinataires',
                third_country_transfers: 'Transferts vers des pays tiers',
                retention_period: 'Durée de conservation',
                doc_title: 'Registre de données',
                doc_generated: 'Export généré le {timestamp}',
                doc_item_count: '{count} entrée(s) de registre',
                doc_template_full: 'Complet',
            },
            export_vocab: {
                data_type: {
                    text: 'Texte',
                    number: 'Nombre',
                    date: 'Date',
                    datetime: 'Date et heure',
                    boolean: 'Oui/non',
                    binary: 'Binaire',
                    structured: 'Structuré',
                    file: 'Fichier',
                    identifier: 'Identifiant',
                    geolocation: 'Géolocalisation',
                },
                collection_method: {
                    direct_from_subject: 'Directement auprès de la personne concernée',
                    third_party: 'Auprès d’un tiers',
                    derived: 'Dérivé d’autres données',
                    observed: 'Observé',
                    inferred: 'Déduit',
                    public_source: 'Source publique',
                },
                data_subject_type: {
                    customer: 'Client',
                    employee: 'Salarié',
                    contractor: 'Prestataire',
                    prospect: 'Prospect',
                    minor: 'Mineur',
                    patient: 'Patient',
                    student: 'Étudiant',
                    beneficiary: 'Bénéficiaire',
                    vendor_contact: 'Contact du fournisseur',
                    website_visitor: 'Visiteur du site web',
                    other: 'Autre',
                },
                volume_band: {
                    lt_1k: 'Moins de 1 000 enregistrements',
                    '1k_to_10k': 'De 1 000 à 10 000 enregistrements',
                    '10k_to_100k': 'De 10 000 à 100 000 enregistrements',
                    '100k_to_1m': 'De 100 000 à 1 million d’enregistrements',
                    gt_1m: 'Plus de 1 million d’enregistrements',
                    unknown: 'Inconnu',
                },
                tier_source: {
                    assessed_and_approved: 'Évalué et approuvé',
                    manual_legacy: 'Repris d’une valeur héritée',
                    legacy_platform_field: 'Valeur héritée de la plateforme',
                    scheme_scoped_only: 'Évalué selon des niveaux propres à chaque référentiel — aucun code unique ne couvre plusieurs échelles de niveaux légales',
                },
            },
            page: {
                title: 'Registre de données',
                subtitle: 'Éléments de données personnelles détenus par votre organisation, organisés en catégories de données.',
                empty_state: "Aucun élément de registre pour l'instant. Ajoutez un élément manuellement ou importez depuis un registre réglementaire.",
                empty_filter_state: 'Aucun article ne correspond à vos filtres.',
            },
            filter: {
                category: 'Catégorie',
                sensitivity: 'Sensibilité',
                data_subject: 'Personne concernée',
                status: 'Statut',
                data_tier: 'Niveau de données',
                cross_border: 'Transfrontalier',
                has_owner: 'A le propriétaire',
                has_form_source: 'Importé depuis le formulaire',
                search_placeholder: 'Rechercher un nom, une description ou un identifiant de champ…',
                clear: 'Effacer les filtres',
                option_all: 'Tout',
            },
            col: {
                name: 'Nom',
                category: 'Catégorie',
                sensitivity: 'Sensibilité',
                data_subjects: 'Personnes concernées',
                systems: 'Systèmes',
                activities: 'Activités',
                owner: 'Propriétaire',
                retention: 'Rétention',
                data_tier: 'Niveau de données',
                status: 'Statut',
                last_reviewed: 'Dernière révision',
                actions: 'Actions',
            },
            action: {
                add_manual: 'Ajouter un article',
                add_from_form: 'Ajouter à partir du registre réglementaire',
                import_history: 'Importer des soumissions antérieures',
                export: 'Exporter',
                archive: 'Archiver',
                restore: 'Restaurer',
                edit: 'Modifier',
                assign_owner: 'Attribuer un propriétaire',
                attach_evidence: 'Joindre des preuves',
                open_lineage: 'Lignée ouverte',
            },
            modal_editor: {
                section_identity: 'Identité',
                section_classification: 'Classement',
                section_subjects: 'Sujets et volumes',
                section_location: 'Localisation et transfert',
                section_systems: 'Systèmes et activités',
                section_retention: 'Rétention et mentions légales',
                section_governance: 'Gouvernance',
                section_notes: 'Remarques',
                hint_inherited: '(hérité de la catégorie)',
                hint_overridden: '(remplacé)',
                placeholder_field_identifiers: 'par exemple, clients.primary_email, hris.employee_id',
                validation_name_required: 'Le nom est requis',
                validation_category_required: 'La catégorie est obligatoire',
                validation_data_subjects_required: 'Au moins un type de personne concernée est requis',
                label_data_type: 'Type de données',
                label_field_identifiers: 'Identifiants de champ (séparés par des virgules)',
                label_data_subject_types: 'Types de personnes concernées',
                label_volume_band: 'Bande de volume',
                label_storage_locations: 'Emplacements de stockage (séparés par des virgules)',
                placeholder_storage_locations: 'Codes ISO-3166 ou texte libre (par exemple, VN, Hetzner EU)',
                label_cross_border: 'Virement transfrontalier',
                label_source_systems: 'Systèmes sources',
                label_processing_activities: 'Activités de traitement',
                label_collection_method: 'Méthode de collecte',
                label_purposes: 'Objectifs',
                hint_multi_select: 'Maintenez Cmd/Ctrl pour en sélectionner plusieurs.',
                option_inherit_category: '— (hériter de la catégorie)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: "Aperçu de l'importation",
                label_to_create: 'Pour créer',
                label_to_update: 'Pour mettre à jour',
                label_skipped_archived: 'Sauté (archivé)',
                label_conflicts: 'Conflits',
                commit: 'Importer',
                cancel: 'Annuler',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Catégorie',
                section_systems: 'Systèmes sources',
                section_activities: 'Activités de traitement',
                section_flows: 'Flux de données',
                section_retention: 'Rétention efficace',
                section_governance: 'Gouvernance',
                section_evidence: 'Preuve',
                section_audit: 'Piste d’audit récente',
                section_source_form: 'Soumission du formulaire source',
                badge_inherited: 'Hérité',
                badge_overridden: 'Remplacé',
            },
            toast: {
                created: 'Élément de registre créé.',
                updated: 'Élément de registre mis à jour.',
                archived: 'Élément de registre archivé.',
                imported: '{count} éléments importés.',
                conflict_warning: 'Les {count} champ(s) ont conservé vos modifications — voir la liste des conflits.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Inventaire des systèmes',
            new: 'Nouveau système',
            edit: 'Modifier le système',
            name: 'Nom',
            owner: 'Responsable',
            criticality: 'Criticité',
            dpa_status: 'Statut DPA',
            location: 'Localisation',
            system_type: 'Type de système',
            metadata: 'Métadonnées (JSON)',
            department: 'Département',
            hosting_location: "Lieu d'hébergement",
            hosting_provider: "Fournisseur d'hébergement",
            data_residency_country: 'Pays de résidence des données',
            hosting: 'Hébergement',
            no_systems: 'Aucun système trouvé',
            confirm_archive: 'Archiver le système « {name} » ?',
            count: 'systèmes',
            count_one: 'système',
            type_internal: 'Interne',
            type_external: 'Externe',
            type_cloud_service: 'Service cloud',
            type_on_premise: 'Sur site',
            type_hybrid: 'Hybride',
            status_active: 'Actif',
            status_decommissioning: 'En cours de retrait',
            status_decommissioned: 'Retiré',
            status_planned: 'Planifié',
            dpa_not_required: 'Non requis',
            dpa_required_missing: 'Requis manquant',
            dpa_in_progress: 'En cours',
            dpa_signed: 'Signé',
            all_criticalities: 'Toutes les criticités',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Catégories de données',
            new: 'Nouvelle catégorie',
            edit: 'Modifier la catégorie',
            name: 'Nom',
            sensitivity: 'Sensibilité',
            tags: 'Étiquettes',
            used_in: 'Utilisée dans',
            examples: 'Exemples',
            examples_hint: 'Exemples (séparés par des virgules)',
            default: 'Par défaut',
            all_sensitivities: 'Toutes les sensibilités',
            all_statuses: 'Tous les statuts',
            no_categories: 'Aucune catégorie trouvée',
            confirm_delete: 'Supprimer la catégorie « {name} » ?',
            count: 'catégories',
            count_one: 'catégorie',
            status_draft: 'Brouillon',
            status_proposed: 'Proposé',
            status_under_review: 'En révision',
            status_approved: 'Approuvé',
            status_deprecated: 'Obsolète',
            status_updated: 'Statut de catégorie mis à jour',
            propose: 'Proposer',
            approve: 'Approuver',
            deprecate: 'Rendre obsolète',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Finalités de traitement',
            new: 'Nouvelle finalité',
            edit: 'Modifier la finalité',
            name: 'Nom',
            legal_basis: 'Base juridique',
            retention_period: 'Durée de conservation (jours)',
            justification: "Justification de l'intérêt légitime",
            legal_basis_detail: 'Détail de la base juridique',
            all_legal_bases: 'Toutes les bases juridiques',
            no_purposes: 'Aucune finalité trouvée',
            confirm_delete: 'Supprimer « {name} » ?',
            default: 'Par défaut',
            count: '{count} finalités',
            count_one: '{count} finalité',
        },
        retention: {
            title: 'Politiques de conservation',
            new: 'Nouvelle politique',
            edit: 'Modifier la politique',
            name: 'Nom',
            retention_days: 'Jours de conservation',
            disposal_method: "Méthode d'élimination",
            data_categories: 'Catégories de données',
            activities: 'Activités',
            review_frequency: 'Fréquence de révision',
            review_frequency_days: 'Fréquence de révision (jours)',
            no_policies: 'Aucune politique de conservation trouvée',
            confirm_delete: 'Supprimer « {name} » ?',
            count: '{count} politiques',
            count_one: '{count} politique',
            days: 'jours',
            disposal_deletion: 'Suppression',
            disposal_anonymization: 'Anonymisation',
            disposal_archival: 'Archivage',
            review_overdue: 'Révision en retard',
            review_status: 'Statut de révision',
            review_ok: 'En bonne voie',
            department: 'Département',
            all_departments: 'Tous les départements',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Vue des flux',
            view_list: 'Liste',
            view_map: 'Carte',
            title: 'Flux de données',
            new: 'Nouveau flux',
            edit: 'Modifier le flux',
            name: 'Nom',
            from_system: 'Système source',
            to_system: 'Système destinataire',
            flow_type: 'Type de flux',
            cross_border: 'Transfrontalier',
            risk_level: 'Niveau de risque',
            mechanism: 'Mécanisme',
            transfer_mechanism: 'Mécanisme de transfert',
            frequency: 'Fréquence',
            source_country: "Pays d'origine",
            destination_country: 'Pays de destination',
            transfer_safeguard: 'Garantie de transfert',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Tous les niveaux de risque',
            no_flows: 'Aucun flux de données trouvé',
            confirm_archive: 'Archiver « {name} » ?',
            count: '{count} flux',
            count_one: '{count} flux',
            status_active: 'Actif',
            status_inactive: 'Inactif',
            status_under_review: 'En cours de révision',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Transfert de fichiers',
            mechanism_database_replication: 'Réplication de base de données',
            mechanism_manual: 'Manuel',
            mechanism_email: 'E-mail',
            mechanism_other: 'Autre',
            frequency_real_time: 'Temps réel',
            frequency_hourly: 'Horaire',
            frequency_daily: 'Quotidien',
            frequency_weekly: 'Hebdomadaire',
            frequency_monthly: 'Mensuel',
            frequency_on_demand: 'À la demande',
            frequency_one_time: 'Ponctuel',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Juridiction',
        },
        activities: {
            title: 'Activités de traitement',
            new: 'Nouvelle activité',
            edit: "Modifier l'activité",
            activity_title: 'Titre',
            category: 'Catégorie',
            legal_basis: 'Base juridique',
            dpa_required: 'DPIA Required',
            ropa_generated: 'Registre généré',
            version: 'Révision',
            generate_ropa: 'Générer le registre',
            controller: 'Responsable du traitement',
            controller_name: 'Nom du responsable du traitement',
            controller_contact: 'Contact du responsable du traitement',
            legal_basis_detail: 'Détail de la base juridique',
            organization_info: 'Organization Information',
            org_address: "Adresse de l'organisation",
            tax_code: 'Code fiscal',
            contact_person: 'Personne de contact',
            contact_email: 'Email de contact',
            contact_phone: 'Téléphone de contact',
            select_entity: 'Sélectionnez…',
            no_entities: 'Aucun article trouvé',
            dpo_name: 'Nom du DPO',
            dpo_contact: 'Contact du DPO',
            processor_name: 'Nom du sous-traitant',
            processor_contact: 'Contact du sous-traitant',
            data_subjects: 'Personnes concernées',
            data_subjects_hint: 'Personnes concernées (séparées par des virgules)',
            recipients: 'Destinataires',
            recipients_hint: 'Destinataires (séparés par des virgules)',
            security_measures: 'Mesures de sécurité',
            dpia_reference: 'Référence AIPD',
            no_activities: 'Aucune activité de traitement trouvée',
            confirm_archive: 'Archiver « {name} » ?',
            count: '{count} activités',
            count_one: '{count} activité',
            ropa_success: 'Enregistrement RoPA généré. Accédez aux Exports pour le télécharger.',
            ropa_error: 'Échec de la génération du registre',
            required_label: 'Requis',
            status_draft: 'Brouillon',
            status_active: 'Actif',
            status_under_review: 'En cours de révision',
            status_archived: 'Archivé',
            decree_165_heading: 'Niveau de données du décret 165 (loi vietnamienne sur les données)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Niveau de données',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Niveau de données',
            data_tier_regular: 'Régulier',
            data_tier_important: 'Important',
            data_tier_core: 'Noyau',
            transfer_mechanism_label: 'Mécanisme de transfert transfrontalier',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: "Référence d'évaluation de sécurité",
            security_assessment_reference_placeholder: 'par ex. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Coffre-fort de preuves',
            new: 'Nouvelle preuve',
            edit: 'Modifier la preuve',
            name: 'Nom',
            evidence_type: 'Type de preuve',
            linked_entities: 'Entités liées',
            uploaded: 'Téléversé',
            expiry: 'Expiration',
            file_url: 'URL du fichier',
            source_mode_label: 'Source de la preuve',
            source_mode_file: 'Téléverser un fichier',
            source_mode_url: 'Lier une URL externe',
            attachment_label: 'Pièce jointe',
            link_to: 'Lier la preuve à',
            entity_type: "Type d'entité",
            entity_id: "Identifiant de l'entité",
            no_evidence: 'Aucun élément de preuve trouvé',
            manage_links: 'Gérer les liens',
            unlink: 'Dissocier',
            confirm_delete: 'Supprimer la preuve « {name} » ?',
            count: '{count} éléments',
            count_one: '{count} élément',
            type_document: 'Pièce jointe',
            type_screenshot: "Capture d'écran",
            type_policy: 'Politique',
            type_contract: 'Contrat',
            type_assessment: 'Évaluation',
            type_correspondence: 'Correspondance',
            type_other: 'Autre',
            linked_type_system: 'Système',
            linked_type_data_flow: 'Flux de données',
            linked_type_processing_activity: 'Activité de traitement',
            linked_type_data_category: 'Catégorie de données',
            linked_type_purpose: 'Objectif',
            linked_type_retention_policy: 'Politique de conservation',
            link_entity: "Lien vers l'entité",
            link_success: 'Entité liée avec succès',
            confirm_unlink: 'Êtes-vous sûr de vouloir dissocier cette entité ?',
            unlink_success: 'Entité dissociée avec succès',
            no_linked_entities: 'Aucune entité liée',
            select_entity: 'Sélectionnez…',
            no_entities: 'Aucun article',
        },
        exports: {
            title: 'Exports de données',
            recent: 'Exports récents',
            export_type: "Type d'export",
            format: 'Formater',
            requested_by: 'Demandé par',
            requested_at: 'Demandé le',
            download: 'Télécharger',
            csv_export: 'Exporter CSV',
            csv_description: 'Télécharger les données au format CSV pour analyse dans un tableur.',
            pdf_reports: 'Rapports PDF',
            pdf_description: 'Générer des rapports formatés pour la documentation de conformité.',
            csv_systems: 'Inventaire des systèmes',
            csv_categories: 'Catégories de données',
            csv_flows: 'Flux de données',
            csv_activities: 'Activités de traitement',
            csv_cross_border: 'Flux transfrontaliers',
            csv_retention_policies: 'Politiques de rétention',
            csv_evidence: 'Éléments de preuve',
            csv_governance_assignments: 'Missions de gouvernance',
            pdf_ropa: 'Registre des activités de traitement',
            pdf_systems: 'Rapport des systèmes',
            pdf_cross_border: 'Rapport transfrontalier',
            pdf_flow_map: 'Cartographie des flux',
            export_failed: "Échec de l'export",
            pdf_generated: 'PDF généré avec succès.',
            ropa: 'Registre RoPA',
            retention_policies: 'Politiques de rétention',
            governance_assignments: 'Missions de gouvernance',
            col_entity: "Entité d'exportation",
            col_description: 'Descriptif',
            desc_ropa: 'Registre ROPA complet de toutes les activités de traitement avec base juridique, statut DPIA et contrôles.',
            desc_systems: 'Inventaire des systèmes avec propriétaire, balises de catégorie de données et état de conformité.',
            desc_activities: 'Activités de traitement individuelles avec responsable du traitement, base juridique et classification DPIA',
            desc_categories: 'Catégories de données et niveaux de sensibilité utilisés dans la classification et les audits.',
            desc_purposes: 'Finalités de traitement avec la base juridique enregistrée pour chacune.',
            desc_flows: 'Flux de données entre les systèmes, y compris les itinéraires internes et transfrontaliers.',
            desc_cross_border: 'Flux transfrontaliers uniquement, avec mécanisme de transfert et évaluation des risques.',
            desc_retention_policies: 'Politiques de conservation, méthodes d’élimination et cadence de révision.',
            desc_evidence: 'Preuves attachées aux activités de traitement : certificats, contrats DPA, rapports DPIA',
            desc_governance_assignments: 'Missions de gouvernance : propriétaires, approbateurs et références ISO 27001.',
            unified_heading: 'Exporter des entités',
            unified_description: "Exportez n'importe quelle entité de mappage de données au format CSV, XLSX, PDF ou DOCX. Toutes les exportations sont enregistrées dans le navigateur de fichiers.",
            export_button_label: 'Exporter',
            csv_download_success: 'CSV téléchargé avec succès.',
            col_action: 'Export',
        },
        attestation: {
            title: "Cycles d'Attestation",
            create: 'Nouveau Cycle',
            name: 'Nom du Cycle',
            year: 'Année',
            deadline: 'Date Limite',
            scope: 'Portée',
            status: 'Statut',
            tasks: 'Tâches du Département',
            assign_tasks: 'Attribuer des Tâches',
            department: 'Département',
            assignee: 'Assigné',
            progress: 'Progression',
            stale_entries: 'Entrées Obsolètes',
            detect_stale: 'Détecter Obsolètes',
            export_bundle: 'Exporter le Paquet',
            no_cycles: "Pas encore de cycles d'attestation.",
            create_success: "Cycle d'attestation créé.",
            assign_success: 'Tâches attribuées avec succès.',
            complete_success: 'Tâche terminée.',
            stale_detected: 'Entrées obsolètes détectées.',
            stale_systems: 'Systèmes obsolètes',
            stale_flows: 'Flux obsolètes',
            no_stale: 'Aucune entrée obsolète détectée.',
            stale_name: 'Nom',
            stale_announcement: '{0} systèmes obsolètes et {1} flux obsolètes trouvés',
            last_updated: 'Dernière mise à jour',
            status_draft: 'Brouillon',
            status_active: 'Actif',
            status_completed: 'Terminé',
            task_status_pending: 'En attente',
            task_status_in_progress: 'En cours',
            task_status_completed: 'Terminé',
            scope_systems: 'Systèmes',
            scope_flows: 'Flux de données',
            scope_activities: 'Activités',
            scope_categories: 'Catégories',
            scope_purposes: 'Finalités',
            scope_retention: 'Conservation',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Attributions de Gouvernance',
            assign: 'Attribuer la Propriété',
            entity_type: "Type d'Entité",
            entity: 'Entité',
            role: 'Rôle',
            assigned_by: 'Attribué par',
            assigned_at: 'Attribué le',
            remove: 'Supprimer',
            no_assignments: 'Aucune attribution de gouvernance.',
            assign_success: 'Propriété attribuée.',
            remove_success: 'Attribution supprimée.',
            assignee: 'Attributaire',
            department: 'Département',
            all_departments: 'Tous les départements',
            roles: {
                data_owner: 'Propriétaire des Données',
                data_steward: 'Gestionnaire des Données',
                data_custodian: 'Gardien des Données',
                informed: 'Informé',
            },
            entity_types: {
                system: 'Système',
                flow: 'Flux de données',
                activity: 'Activité',
                category: 'Catégorie',
                purpose: 'Finalité',
                retention_policy: 'Politique de conservation',
            },
        },
        risk_assessment: {
            title: 'Évaluation des Risques',
            create: 'Nouvelle Évaluation',
            safeguard_type: 'Type de Garantie',
            adequacy_status: "Statut d'Adéquation",
            risk_factors: 'Facteurs de Risque',
            mitigations: "Mesures d'Atténuation",
            reviewer: 'Réviseur',
            decision: 'Décision',
            approve: 'Approuver',
            reject: 'Rejeter',
            no_assessments: 'Aucune évaluation des risques.',
            create_success: 'Évaluation des risques créée.',
            approve_success: 'Évaluation approuvée.',
            reject_success: 'Évaluation rejetée.',
            one_per_line: 'Un par ligne',
            adequacy_adequate: 'Adéquat',
            adequacy_inadequate: 'Inadéquat',
            adequacy_under_review: 'En cours de révision',
            adequacy_not_assessed: 'Non évalué',
            decision_approved: 'Approuvé',
            decision_rejected: 'Rejeté',
        },
        review: {
            title: 'Tâches de révision',
            description: "Les tâches de révision sont créées automatiquement lorsqu'une catégorie de données est proposée. Pour créer une nouvelle tâche, accédez aux Catégories de données et cliquez sur Proposer sur une catégorie brouillon.",
            count: 'tâches de révision',
            summary: 'Résumé des révisions',
            pending: 'En attente',
            resolved_today: "Résolues aujourd'hui",
            escalated: 'Escaladées',
            overdue: 'En retard (>5 jours)',
            all_statuses: 'Tous les statuts',
            status_pending: 'En attente',
            status_resolved: 'Résolu',
            status_escalated: 'Escaladé',
            category_name: 'Catégorie',
            system_name: 'Système',
            owner: 'Responsable',
            resolve: 'Résoudre',
            resolve_task: 'Résoudre la tâche de révision',
            resolution: 'Résolution',
            resolution_approve: 'Approuver',
            resolution_modify: 'Modifier et approuver',
            resolution_reject: 'Rejeter',
            resolution_escalate: 'Escalader',
            notes: 'Notes de résolution',
            notes_required: 'Notes obligatoires (minimum 10 caractères) pour Modifier, Rejeter ou Escalader',
            resolved_success: 'Tâche de révision résolue',
            bulk_approve: 'Approbation groupée',
            bulk_reject: 'Rejet groupé',
            bulk_success: '{count} tâches traitées',
            no_tasks: 'Aucune tâche de révision trouvée',
        },
        inventory: {
            title: 'Inventaire de classification',
            count: 'systèmes',
            summary: "Résumé de l'inventaire",
            total_systems: 'Total des systèmes',
            total_labels: 'Total des étiquettes',
            system_name: 'Système',
            label_count: 'Étiquettes',
            approved_count: 'Approuvés',
            pending_count: 'En attente',
            no_items: "Aucun élément d'inventaire trouvé",
        },
        exceptions: {
            title: 'Exceptions de classification',
            count: 'exceptions',
            all_severities: 'Toutes les sévérités',
            all_statuses: 'Tous les statuts',
            system_name: 'Système',
            severity_label: 'Sévérité',
            status_open: 'Ouverte',
            status_resolved: 'Résolue',
            status_accepted_risk: 'Risque accepté',
            resolve: 'Résoudre',
            resolve_exception: "Résoudre l'exception",
            resolution_type: 'Type de résolution',
            notes: 'Remarques',
            justification_required: 'Justification obligatoire (minimum 10 caractères) pour risque accepté',
            resolved_success: 'Exception résolue',
            no_exceptions: 'Aucune exception trouvée',
        },
        history: {
            title: 'Historique de classification',
            count: 'événements',
            actor: 'Acteur',
            action: 'Action',
            entity_type: "Type d'entité",
            changes: 'Modifications',
            view_lineage: 'Voir la lignée',
            lineage_title: 'Lignée de classification',
            no_events: "Aucun événement d'historique trouvé",
            no_lineage: 'Aucune donnée de lignée disponible',
            modified: 'Modifié',
            created: 'Créé',
            removed: 'Supprimé',
            action_created: 'Créé',
            action_updated: 'Mis à jour',
            action_archived: 'Archivé',
            action_approved: 'Approuvé',
            action_deprecated: 'Obsolète',
            entity_label: 'Étiquette',
            entity_taxonomy: 'Taxonomie',
            entity_review_task: 'Tâche de révision',
            entity_exception: 'Exception',
            before: 'Avant',
            after: 'Après',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: "Piste d'audit de la gouvernance",
        audit_trail_subtitle: "Flux d'audit inter-entités pour les actions de gouvernance : approbations, exécutions de playbook, liaisons de preuves, etc.",
        audit_trail_item_count: "événements d'audit",
        audit_trail_fetch_error: "Impossible de récupérer les événements d'audit.",
        audit_trail_no_events: "Aucun événement d'audit trouvé avec les filtres actuels.",
        audit_trail_entity_type: "Type d'entité",
        audit_trail_entity_id: "ID d'entité",
        audit_trail_action: 'Action',
        audit_trail_actor: 'Acteur',
        audit_trail_timestamp: 'Horodatage',
        audit_trail_date_from: 'Du',
        audit_trail_date_to: 'Au',
        audit_trail_no_rows_to_export: 'Aucune ligne à exporter.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Prestataires',
            subprocessors: 'Sous-traitants',
            cross_border: 'Transfrontalier',
            drift: 'Surveillance des écarts',
            scores: 'Score de conformité',
            playbooks: 'Guides opérationnels',
            approvals: 'Approbations',
            audit_trail: "Piste d'audit",
            board_report: "Rapport d'administration",
            evidence: 'Preuves',
            onboarding: "Pipeline d'intégration",
            dpa_reviews: 'Revues DPA',
            periodic_reviews: 'Revues périodiques',
            offboarding: 'Désinscription',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Registre des prestataires',
            new: 'Nouveau prestataire',
            edit: 'Modifier le prestataire',
            name: 'Nom',
            jurisdiction: 'Juridiction',
            dpa_status: 'Statut DPA',
            risk_level: 'Niveau de risque',
            onboarding_status: "Statut d'intégration",
            contact_email: 'E-mail de contact',
            contact_name: 'Nom du contact',
            contact_phone: 'Téléphone de contact',
            website: 'Site web',
            services_csv: 'Services (séparés par des virgules)',
            country: 'Pays',
            country_placeholder: 'ex. VN',
            last_reviewed: 'Dernière révision',
            offboard: 'Résilier',
            offboard_vendor: 'Résilier le prestataire',
            offboard_reason: 'Motif de résiliation',
            no_vendors: 'Aucun prestataire trouvé',
            vendor_count: '{count} prestataire(s)',
            all_dpa_status: 'Tous les statuts DPA',
            all_risk_levels: 'Tous les niveaux de risque',
            all_onboarding: "Tous les statuts d'intégration",
            fetch_error: 'Échec. Veuillez réessayer.',
            save_error: 'Échec de la sauvegarde du prestataire',
            offboard_error: 'Échec de la résiliation',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Sous-traitant',
            sub_processor: 'Sous-traitant Ultérieur',
            controller: 'Responsable du Traitement',
            joint_controller: 'Responsables Conjoints',
        },
        onboarding_statuses: {
            pending_review: 'En Attente de Révision',
            approved: 'Approuvé',
            active: 'Actif',
            suspended: 'Suspendu',
            offboarded: 'Résilié',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Non Requis',
            required_missing: 'Requis Manquant',
            draft: 'Brouillon',
            under_review: 'En Cours de Révision',
            signed: 'Signé',
            expired: 'Expiré',
        },
        risk_levels: {
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Faible',
        },
        approval_statuses: {
            pending: 'En Attente',
            approved: 'Approuvé',
            rejected: 'Rejeté',
            expired: 'Expiré',
        },
        approval_action_types: {
            vendor_onboarding: 'Intégration Fournisseur',
            vendor_offboarding: 'Résiliation Fournisseur',
            cross_border_acceptance: 'Approbation Transfrontalière',
            playbook_activation: 'Activation du Playbook',
            drift_accept_risk: 'Dérive Risque Accepté',
            activity_activation: "Activation d'Activité",
        },
        drift_check_types: {
            dpa_expiry: 'Expiration du DPA',
            dpa_missing: 'DPA Manquant',
            review_overdue: 'Révision en Retard',
            cross_border_gap: 'Écart Transfrontalier',
            sub_processor_chain: 'Chaîne de Sous-traitants',
            risk_assessment_overdue: 'Évaluation des Risques en Retard',
            retention_policy_missing: 'Politique de Conservation Manquante',
            evidence_expiring: 'Preuve Expirant',
        },
        drift_statuses: {
            open: 'Ouvert',
            acknowledged: 'Confirmé',
            in_progress: 'En Cours',
            resolved: 'Résolu',
            accepted_risk: 'Risque Accepté',
        },
        drift_entity_types: {
            vendor: 'Fournisseur',
            system: 'Système',
            data_flow: 'Flux de Données',
            processing_activity: 'Activité de Traitement',
            evidence_item: 'Élément de Preuve',
        },
        playbook_statuses: {
            draft: 'Brouillon',
            active: 'Actif',
            archived: 'Archivé',
        },
        playbook_action_types: {
            delete_data: 'Supprimer les Données',
            verify_deletion: 'Vérifier la Suppression',
            notify_vendor: 'Notifier le Fournisseur',
            confirm_receipt: 'Confirmer la Réception',
            document_evidence: 'Documenter',
            other: 'Autre',
        },
        playbook_target_types: {
            system: 'Système',
            vendor: 'Fournisseur',
        },
        dpa_chain_statuses: {
            complete: 'Complet',
            incomplete: 'Incomplet',
            unknown: 'Inconnu',
        },
        sub_processor_statuses: {
            active: 'Actif',
            terminated: 'Résilié',
        },
        evidence_types: {
            dpa_agreement: 'Accord DPA',
            risk_assessment: 'Évaluation des Risques',
            audit_report: "Rapport d'Audit",
            certification: 'Accréditation',
            deletion_confirmation: 'Confirmation de Suppression',
            training_record: 'Registre de Formation',
            incident_report: "Rapport d'Incident",
            document: 'Pièce jointe',
            screenshot: "Capture d'Écran",
            policy: 'Politique',
            contract: 'Contrat',
            assessment: 'Évaluation',
            correspondence: 'Correspondance',
            other: 'Autre',
        },
        linkable_entity_types: {
            vendor: 'Fournisseur',
            transfer_mechanism: 'Mécanisme de Transfert',
            playbook: 'Livre de procédures',
            execution: 'Exécution',
            system: 'Système',
            activity: 'Activité',
        },
        subprocessors: {
            title: 'Relations avec les sous-traitants',
            new: 'Nouveau lien',
            vendor: 'Prestataire',
            subprocessor: 'Sous-traitant',
            relationship_type: 'Type de relation',
            countries: 'Pays',
            countries_csv: 'Pays (séparés par des virgules)',
            dpa_chain: 'Chaîne DPA',
            dpa_chain_status: 'Statut de la chaîne DPA',
            no_processors: 'Aucun sous-traitant trouvé.',
            no_subprocessors: 'Aucun sous-traitant ultérieur trouvé.',
            processor_vendor: 'Prestataire sous-traitant',
            select_processor: 'Sélectionner le sous-traitant',
            select_vendor: 'Sélectionner le prestataire',
            services: 'Prestations',
            sub_processor_vendor_id: 'ID du sous-traitant ultérieur',
            terminate: 'Résilier',
            confirm_terminate: 'Êtes-vous sûr de vouloir résilier cette relation ?',
            create_error: 'Échec de la création du lien.',
            fetch_error: 'Échec de la récupération des sous-traitants.',
            processor: 'Sous-traitant',
            services_csv: 'Services (séparés par des virgules)',
            terminate_error: 'Échec de la résiliation.',
            version_history_heading: 'Gestion des versions modifiées par le sous-traitant',
            change_tracking_note: "Toutes les modifications apportées à la relation entre les sous-traitants sont versionnées. Chaque modification enregistre l'état avant/après pour la piste d'audit.",
            history_retention_note: "L'historique des modifications est conservé pendant toute la période de conservation des données. Consultez les journaux de modifications via la vue détaillée du fournisseur.",
            vendor_risk_cross_view_heading: 'Registre des sous-traitants (Vendor Risk)',
            vendor_risk_cross_view_source: 'Source : Vendor Risk Register',
            vendor_risk_cross_view_empty: 'Aucune entrée dans le registre Vendor Risk.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Rapport de gouvernance transfrontalière',
            flows: 'Flux transfrontaliers',
            transfers: 'Transferts transfrontaliers',
            detected_gaps: 'Écarts détectés',
            safeguards: 'Garanties',
            cross_border_vendors: 'Prestataires transfrontaliers',
            destination: 'Lieu de destination',
            no_cross_border_vendors: 'Aucun prestataire transfrontalier trouvé.',
            no_safeguard: 'Aucune garantie',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Lieux de traitement',
            export_error: "Échec de l'export du rapport.",
            export_pdf: 'Exporter en PDF',
            export_success: 'Rapport exporté avec succès.',
            exporting: 'Export en cours...',
            fetch_error: 'Échec de la récupération des données transfrontalières.',
            source: 'Provenance',
            pdpl_p2_heading: 'PDPL-P2 Conformité aux transferts transfrontaliers',
            dossier_prep_note: "Préparation du dossier : rassemblez l'analyse d'impact, la base juridique et la documentation de sauvegarde avant le dépôt.",
            filing_tracking_note: "Suivi du dépôt : surveillez l'état de la soumission auprès de l'Autorité (PDPC) depuis la demande initiale jusqu'à l'approbation.",
            supplement_loop_note: "Boucle de complément : répondre aux demandes d'informations de l'Autorité et soumettre à nouveau les dossiers modifiés si nécessaire.",
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Recherche de fournisseur',
            vendor_search_placeholder: 'Rechercher par nom de fournisseur…',
            jurisdiction_filter_label: 'Juridiction',
            filings_section_heading: 'Dépôts transfrontaliers',
            filings_empty: "Aucun dépôt pour l'instant. Créez-en un pour démarrer un dossier.",
            filings_create_button: 'Nouveau dépôt',
            filings_table_id: 'Identifiant de dépôt',
            filings_table_jurisdiction: 'Juridiction',
            filings_table_type: 'Type de formulaire',
            filings_table_status: 'Statut',
            filings_table_updated: 'Mis à jour',
            filings_table_open: 'Ouvert',
            filings_detail_title: 'Dépôt transfrontalier',
            filings_back_to_list: 'Retour à Transfrontalier',
            filings_status_heading: 'Cycle de vie',
            filings_evidence_heading: 'Preuve',
            filings_history_heading: "Piste d'audit",
            filings_field_filing_id: 'Identifiant de dépôt',
            filings_field_jurisdiction: 'Juridiction',
            filings_field_type: 'Type de formulaire',
            filings_field_status: 'Statut',
            filings_field_reference: "Référence d'autorité",
            filings_field_mps_receipt: 'MPS reçu',
            filings_field_submitted_at: 'Soumis à',
            filings_field_acknowledged_at: 'Reconnu à',
            filings_field_archived_at: 'Archivé à',
            filings_field_version: 'Version',
            filings_field_notes: 'Remarques',
            filings_action_advance: 'Statut avancé',
            filings_action_reject: 'Rejeter',
            filings_action_archive: 'Archiver',
            filings_action_submit: 'Marquer soumis',
            filings_action_acknowledge: 'Mark a reconnu',
            filings_action_resume_draft: 'Reprendre comme brouillon',
            filings_status_draft: 'Brouillon',
            filings_status_assessed: 'Évalué',
            filings_status_pending_review: "En attente d'examen",
            filings_status_ready_to_file: 'Prêt à déposer',
            filings_status_submitted: 'Soumis',
            filings_status_acknowledged: 'Reconnu',
            filings_status_rejected: 'Rejeté',
            filings_status_archived: 'Archivé',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Nouveau dépôt transfrontalier',
            filings_new_form_jurisdiction: 'Juridiction',
            filings_new_form_type: 'Type de formulaire',
            filings_new_form_notes: 'Remarques (facultatif)',
            filings_new_submit: 'Créer',
            filings_new_cancel: 'Annuler',
            filings_reason_label: 'Raison',
            filings_reference_label: "Numéro de référence de l'autorité",
            filings_mps_receipt_label: 'MPS référence du reçu',
            filings_create_error: 'Échec de la création du dossier.',
            filings_create_success: 'Dossier créé.',
            filings_update_error: 'Échec de la mise à jour du dossier.',
            filings_update_success: 'Dossier mis à jour.',
            filings_fetch_error: 'Échec de la récupération des dossiers.',
            filings_archive_confirm: 'Archiver ce dossier ? Cela ne peut pas être annulé.',
            filings_status_rejected_detour: 'Le dépôt a été rejeté après examen. Reprendre en tant que brouillon pour redémarrer le flux.',
            recent_exports_heading: 'Exportations récentes',
            recent_exports_empty: 'Aucune exportation pour l\'instant. Cliquez sur "Exporter PDF" pour en générer un.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Surveillance des écarts de configuration',
            drift_type: "Type d'écart",
            severity: 'Gravité',
            detected_at: 'Détecté le',
            resolve: 'Résoudre',
            all_check_types: 'Tous les types de vérification',
            all_entity_types: "Tous les types d'entité",
            event_count: "Nombre d'événements",
            accept_risk: 'Accepter le risque',
            acknowledge: 'Confirmer',
            assign_error: "Échec de l'attribution.",
            assign_event: 'Attribuer',
            assigned: 'Attribué',
            enter_resolution_notes: 'Saisir les notes de résolution...',
            enter_user_id: "Saisir l'identifiant utilisateur...",
            entity: 'Entité',
            fetch_error: 'Échec de la récupération des événements de dérive.',
            no_events: 'Aucun événement de dérive trouvé.',
            resolution_notes: 'Notes de résolution',
            update_status_error: 'Échec de la mise à jour du statut.',
        },
        scores: {
            title: 'Tableau de bord du score de conformité',
            overall: 'Score de conformité global',
            breakdown: 'Détail du score par module',
            trend: 'Tendance historique',
            module: 'Composant',
            score: 'Score',
            grade: 'Note',
            calculated_at: 'Calculé le',
            generate_board_report: 'Générer le rapport du conseil',
            lang_english: 'Anglais',
            lang_vietnamese: 'Vietnamien',
            fetch_error: 'Échec de la récupération des scores de conformité.',
            generate_report: 'Générer le rapport',
            generating: 'Génération en cours...',
            recommendations: 'Recommandations',
            report_error: 'Échec de la génération du rapport.',
            factor_owner_assigned: 'Propriétaire assigné',
            factor_data_categories_linked: 'Catégories de données liées',
            factor_purposes_linked: 'Finalités liées',
            factor_data_flows_documented: 'Flux de données documentés',
            factor_processing_activity_linked: 'Activité de traitement liée',
            factor_dpa_status: 'Statut DPA',
            factor_retention_policy_linked: 'Politique de conservation liée',
            factor_last_reviewed: 'Dernière révision dans le seuil',
            factor_risk_assessment_current: 'Évaluation des risques à jour',
            factor_sub_processors_documented: 'Sous-traitants documentés',
            factor_cross_border_safeguards: 'Garanties transfrontalières',
            factor_contact_info_complete: 'Coordonnées complètes',
            factor_onboarding_approved: 'Intégration approuvée',
            factor_avg_system_score: 'Score système moyen',
            factor_avg_vendor_score: 'Score fournisseur moyen',
            factor_processing_activity_coverage: 'Couverture des activités de traitement',
            factor_ropa_completeness: 'Complétude du RoPA',
            factor_open_drift_events: 'Événements de dérive ouverts',
        },
        playbooks: {
            title: 'Guides opérationnels de suppression',
            new: 'Nouveau guide',
            edit: 'Modifier le guide',
            steps: 'Étapes',
            add_step: 'Ajouter une étape',
            remove_step: 'Supprimer une étape',
            data_categories_csv: 'Catégories de données (séparées par des virgules)',
            estimated_duration: 'Durée estimée',
            estimated_duration_label: 'Durée estimée',
            evidence_description: 'Description de la preuve',
            no_playbooks: 'Aucun guide opérationnel trouvé.',
            playbook_count: 'Nombre de guides',
            requires_evidence: 'Preuve requise',
            step_label: "Libellé de l'étape",
            target_name: 'Nom de la cible',
            target_type: 'Type de cible',
            action_type: "Type d'action",
            archive_error: "Échec de l'archivage.",
            confirm_archive: 'Êtes-vous sûr de vouloir archiver ce guide opérationnel ?',
            data_categories: 'Catégories de données',
            days: 'jours',
            fetch_error: 'Échec de la récupération des guides opérationnels.',
            sla_days: 'SLA (jours)',
            target_id: 'ID de la cible',
            template_class_heading: 'Classification des modèles',
            official: 'Officiel',
            internal: 'Interne',
            classification: 'Classement',
            official_note: 'Officiel : modèles mandatés par la réglementation alignés sur PDPL, GDPR ou sur des exigences spécifiques au secteur.',
            internal_note: "Interne : procédures spécifiques à l'organisation non dérivées de modèles réglementaires.",
            active_executions_heading: 'Exécutions actives',
            no_active_executions: 'Aucune exécution active.',
            playbook_label: 'Manuel de jeu',
            ticket_id_label: 'Billet',
            progress_label: 'Progression',
            actions_label: 'Actions',
            view_steps_label: 'Voir les étapes',
            execution_detail_title: "Détail de l'exécution",
            playbook_missing: 'Playbook introuvable.',
            evidence_required_label: 'Preuve requise',
            upload_evidence_label: 'Téléverser la preuve',
            mark_complete_label: 'Marquer comme terminé',
            evidence_required_toast: 'Preuve requise avant de terminer cette étape.',
            evidence_blocking_message: 'Joignez un fichier de preuve pour continuer.',
            step_completed_toast: 'Étape terminée.',
            executions_fetch_error: 'Échec du chargement des exécutions.',
            step_evidence_default_name: "Preuve de l'étape d'exécution",
            attached_evidence_label: 'Preuves jointes',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: "Flux d'approbation",
            new: 'Nouvelle approbation',
            edit: "Modifier l'approbation",
            request_type: 'Type de demande',
            requester: 'Demandeur',
            approve: 'Approuver',
            reject: 'Rejeter',
            justification: 'Motif',
            all_action_types: "Tous les types d'action",
            decided_by: 'Décidé par',
            no_approvals: 'Aucune approbation trouvée.',
            approval_count: "Nombre d'approbations",
            approval_notes_optional: 'Notes (facultatif)',
            approve_error: "Échec de l'approbation.",
            approve_request: 'Approuver la demande',
            decision: 'Décision',
            entity: 'Entité',
            fetch_error: 'Échec de la récupération des approbations.',
            reject_error: 'Échec du rejet.',
            reject_request: 'Rejeter la demande',
            rejection_notes_required: 'Notes de rejet requises',
            rejection_notes_required_msg: 'Veuillez fournir un motif de rejet.',
            requested_at: 'Demandé le',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: "Preuve à l'appui (optionnelle)",
            decision_evidence_name: 'Preuve de décision',
        },
        evidence: {
            title: 'Preuves de gouvernance',
            new: 'Nouvelle preuve',
            link_to: 'Lier à',
            entity_id_placeholder: "Identifiant de l'entité",
            file_path_placeholder: '/chemin/vers/fichier',
            evidence_label: 'Libellé de la preuve',
            file_path: 'Chemin du fichier',
            source_mode_label: 'Source de la preuve',
            source_mode_file: 'Téléverser un fichier',
            source_mode_url: 'Lier une URL externe',
            attachment_label: 'Pièce jointe',
            item_count: "Nombre d'éléments",
            link_button: 'Lier la preuve',
            confirm_delete: 'Êtes-vous sûr de vouloir supprimer cette preuve ?',
            enter_entity_id: "Saisir l'identifiant de l'entité...",
            entity_id: "Identifiant de l'entité",
            entity_type: "Type d'entité",
            expires: 'Expirer',
            expires_at: 'Expire le',
            fetch_error: 'Échec de la récupération des preuves.',
            link_error: 'Échec de la liaison de la preuve.',
            no_evidence: 'Aucune preuve trouvée.',
            tags_csv: 'Étiquettes (séparées par des virgules)',
            uploaded: 'Téléversé',
            url: 'URL',
            framework_tag_heading: 'Preuves marquées par le cadre',
            framework_tag_note: "Chaque enregistrement de preuve capture le code-cadre, l'acteur et l'horodatage. Filtrez par framework pour isoler les enregistrements pertinents pour l’audit.",
            framework_code: 'Code-cadre',
            filter_by_framework: 'Filtrer par code-cadre...',
            actor: 'Acteur',
        },
        certification: {
            not_applicable: 'Sans objet',
            not_certified: 'Non certifié',
            pending: 'En attente',
            certified: 'Certifié',
            revoked: 'Révoqué',
        },
        onboarding_pipeline: {
            title: "Pipeline d'intégration",
            new_workflow: "Nouveau workflow d'intégration",
            vendor_id: 'ID fournisseur',
            stages: 'Étapes',
            current_stage: 'Étape actuelle',
            advance: "Avancer l'étape",
            advance_success: 'Étape avancée avec succès',
            workflow_status: 'Statut du workflow',
            initiated: 'Initié',
            in_progress: 'En cours',
            completed: 'Terminé',
            cancelled: 'Annulé',
            no_workflows: "Aucun workflow d'intégration trouvé",
            stages_placeholder: 'Étape 1, Étape 2, Étape 3',
            assigned_to: 'Assigné à',
            notes: 'Remarques',
            pdpl_lifecycle_heading: 'PDPL Cycle de vie du fournisseur – Intégration',
            due_diligence_note: "L'étape de diligence raisonnable est obligatoire avant d'intégrer un processeur sous PDPL. Vérifiez l’étendue du traitement des données, la base juridique et les mesures de sécurité.",
            contract_review_note: "L'examen du contrat doit confirmer les clauses DPA, les limites de conservation des données et les restrictions des sous-traitants ultérieurs.",
            create_dpa_review_cta: 'Créer un examen DPA',
            dpa_review_linked: 'Examen DPA lié',
        },
        dpa_review: {
            title: 'Revues DPA',
            new_review: 'Nouvelle revue DPA',
            review_type: 'Type de revue',
            findings: 'Constatations',
            outcome: 'Résultat',
            complete_review: 'Terminer la revue',
            complete_success: 'Revue terminée avec succès',
            link_assessment: 'Lier une évaluation de sécurité',
            link_success: 'Évaluation liée avec succès',
            assessment_id: "ID d'évaluation",
            combined_title: 'Évaluation combinée',
            no_reviews: 'Aucune revue DPA trouvée',
            telecom_disclosure_heading: 'Gouvernance de la divulgation des télécommunications',
            disclosure_trigger_note: 'Les examens déclenchés par la divulgation capturent les événements de partage de données des abonnés des télécommunications conformément à la loi VN Telecom.',
            telecom_t2_note: "L'approbation du niveau TELECOM-T2 est requise avant de divulguer les données des abonnés aux autorités.",
            quarterly_log_note: "Utilisez le journal trimestriel pour documenter tous les événements de divulgation et maintenir une piste d'audit continue.",
        },
        periodic_review: {
            title: 'Revues périodiques',
            new_review: 'Nouvelle revue périodique',
            review_type: 'Type de revue',
            due_date: "Date d'échéance",
            next_review: 'Prochaine date de revue',
            trigger_reassessment: 'Déclencher une réévaluation',
            reassessment_reason: 'Raison de la réévaluation',
            reassessment_success: 'Réévaluation déclenchée avec succès',
            complete_success: 'Revue terminée avec succès',
            scheduled: 'Planifiée',
            in_progress: 'En cours',
            completed: 'Terminée',
            overdue: 'En retard',
            material_change: 'Changement significatif',
            no_reviews: 'Aucune revue périodique trouvée',
            mark_complete: 'Marquer comme terminé',
            pdpl_periodic_heading: 'PDPL Cycle de vie du fournisseur – Examens périodiques',
            scheduled_review_note: 'Des examens périodiques doivent être programmés au moins une fois par an pour tous les processeurs actifs. Les examens en retard déclenchent des alertes de conformité.',
            material_change_note: 'Les changements importants dans le traitement des données nécessitent une réévaluation immédiate, quel que soit le calendrier.',
            overdue_badge: 'En retard',
            overdue_aria: 'Examen en retard',
            only_overdue_filter: 'Seulement en retard',
        },
        offboarding: {
            title: 'Désinscription fournisseur',
            initiate: 'Initier la désinscription',
            initiate_success: 'Désinscription initiée avec succès',
            reason: 'Raison de la désinscription',
            data_return_required: 'Retour des données requis',
            verify_data: 'Vérifier le retour des données',
            verify_success: 'Retour des données vérifié avec succès',
            sign_off: 'Approuver',
            sign_off_success: 'Désinscription approuvée avec succès',
            status_initiated: 'Initiée',
            status_data_pending: 'Retour des données en attente',
            status_data_verified: 'Retour des données vérifié',
            status_completed: 'Terminée',
            status_cancelled: 'Annulée',
            task_id: 'ID de tâche',
            no_tasks: 'Aucune tâche de désinscription trouvée',
            evidence_id: 'ID de preuve',
            pdpl_offboarding_heading: 'PDPL Cycle de vie du fournisseur – Délocalisation et suppression',
            deletion_capture_note: "L'offboarding doit capturer la confirmation de suppression des données du fournisseur. Vérifiez le retour des données avant la signature.",
            evidence_attachment_note: "Joignez des preuves de suppression (certificats, captures d'écran, confirmations du fournisseur) pour compléter le dossier de délocalisation.",
            verify_mode_label: 'Méthode de vérification',
            verify_mode_upload: 'Téléverser un nouveau fichier',
            verify_mode_link: 'Lier une preuve existante',
            verify_mode_none: 'Aucune donnée à restituer',
            upload_data_return_evidence: 'Téléverser la preuve de restitution des données',
            no_data_justification_label: 'Justification',
            no_data_justification_placeholder: "Expliquez pourquoi aucune donnée n'est à restituer",
            no_data_justification_hint: 'Minimum 20 caractères',
            upload_required_toast: 'Joindre un fichier pour continuer',
            evidence_link_required_toast: 'Sélectionner une preuve pour continuer',
            no_data_justification_required_toast: "Fournir une justification d'au moins 20 caractères",
            data_return_evidence_name: 'Preuve de restitution des données',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Initiale',
            annual: 'Annuel',
            renewal: 'Renouvellement',
            incident_triggered: 'Suite à un incident',
        },
        periodic_review_types: {
            initial: 'Initiale',
            annual: 'Annuelle',
            renewal: 'Renouvellement',
            incident_triggered: 'Déclenché par incident',
            material_change: 'Changement significatif',
            disclosure_triggered: 'Déclenché par divulgation',
            quarterly_log: 'Journal trimestriel',
        },
        dpa_review_statuses: {
            pending: 'En attente',
            in_progress: 'En cours',
            completed: 'Terminé',
            cancelled: 'Annulé',
        },
        onboarding_stage_names: {
            due_diligence: 'Due diligence',
            contract_review: 'Revue de contrat',
            security_assessment: 'Évaluation de sécurité',
            data_mapping: 'Cartographie des données',
            approval: 'Approbation',
            integration: 'Intégration',
        },
        access_denied_title: 'Accès refusé',
        access_denied: "Vous n'avez pas la permission de consulter cette page. Veuillez contacter votre administrateur.",
    },
    consent: {
        nav: {
            policies: 'Politiques',
            purposes: 'Catalogue des finalités',
            workflows: 'Flux de travail',
            snapshots: 'Instantanés CMP',
            change_awareness: 'Détection des modifications',
            audit: 'Audit du consentement',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Analyse des écarts',
            page_title: 'Analyse des écarts de consentement',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Cadres',
            org_flags_label: "Contexte de l'organisation",
            flag_withdrawal: 'Le mécanisme de retrait mondial est-il documenté',
            flag_parental: "Dispose d'un flux de consentement parental pour les sujets de moins de 16 ans",
            flag_cross_border: 'A un dépôt transfrontalier enregistré',
            run_label: "Analyse des écarts d'exécution",
            running_label: 'Courir…',
            requires_advisor_label: "Nécessite l'autorisation ai_advisor : chat.",
            results_heading: 'Lacunes apparues ({count})',
            tokens_label: '≈ {count} jetons',
            empty_label: 'Aucune lacune détectée pour les frameworks sélectionnés.',
            remediation_label: 'Correction suggérée :',
            dismiss_label: 'Rejeter comme sans objet',
            resolve_label: 'Résoudre',
            dismissed_label: 'Rejeté car sans objet',
            resolved_label: 'Résolu',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Importer les flux de consentement',
            nav_title: 'Import en masse',
            description: 'Téléversez un fichier CSV pour importer en masse les flux de consentement.',
            select_file: 'Sélectionner le fichier CSV',
            file_help: 'CSV avec des en-têtes correspondant aux champs de flux.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Colonnes requises : Type de flux, Personne concernée, Canal.',
            file_too_large: 'Fichier trop volumineux. La taille maximale est de 2 Mo.',
            preview: 'Aperçu',
            columns_detected: 'Colonnes détectées : {count}',
            column_mapping: 'Correspondance des colonnes',
            import_button: 'Importer',
            import_complete: 'Import terminé',
            import_error: "L'import a échoué",
            result: "Résultat de l'import",
            total: 'Total',
            imported: 'Importé',
            skipped: 'Ignoré',
            failed: 'Échoué',
            errors: 'Erreurs',
            error_row: 'Ligne',
            error_field: 'Champ',
            error_message: 'Message',
            history: "Historique d'import",
            no_history: "Aucun historique d'import.",
            history_file: 'Nom de fichier',
            history_date: 'Date',
            create_profiles_label: 'Créer des profils de Personne concernée pour les nouveaux sujets',
            create_profiles_help: "Les imports sans profil correspondant en créent un automatiquement. Décochez pour n'importer que les flux.",
            profiles_created: 'Profils créés',
            profiles_matched: 'Profils correspondants',
            subject_kind_help: 'Colonnes facultatives : subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Adresse e-mail non valide dans la colonne subject_email.',
            error_subject_unknown_kind: 'Valeur subject_kind inconnue. Attendu : email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key est requis lorsque subject_kind=external_ref',
        },
        policies: {
            title: 'Politiques de consentement',
            new: 'Nouvelle politique',
            edit: 'Modifier la politique',
            name: 'Nom',
            version: 'Révision',
            policy_text: 'Texte de la politique',
            publish: 'Publier',
            versions: 'Révisions',
            hide_versions: 'Masquer les versions',
            submit_for_review: 'Soumettre pour révision',
            suspend: 'Suspendre',
            retire: 'Retirer',
            jurisdictions: 'Juridictions',
            jurisdictions_csv: 'Juridictions (séparées par des virgules)',
            approval_mode: "Mode d'approbation",
            reviewer_ids: 'ID des réviseurs (séparés par des virgules)',
            tags_csv: 'Étiquettes (séparées par des virgules)',
            effective_date: "Date d'entrée en vigueur",
            review_date: 'Date de révision',
            consent_model_statement: 'Déclaration du modèle de consentement',
            consent_model_placeholder: 'Déclaration facultative du modèle de consentement...',
            default_settings: 'Paramètres par défaut (JSON)',
            changes: 'Modifications',
            fetch_error: 'Échec de la récupération des politiques de consentement. Veuillez réessayer.',
            save_error: "Échec de l'enregistrement de la politique de consentement. Veuillez réessayer.",
            submit_review_error: 'Échec de la soumission de la politique pour révision. Veuillez réessayer.',
            activate_error: "Échec de l'activation de la politique. Veuillez réessayer.",
            suspend_error: 'Échec de la suspension de la politique. Veuillez réessayer.',
            retire_error: 'Échec du retrait de la politique. Veuillez réessayer.',
            submit_review_success: 'Politique soumise pour examen.',
            activate_success: 'Politique activée.',
            suspend_success: 'Politique suspendue.',
            retire_success: 'Politique retirée.',
            versions_error: 'Échec de la récupération des versions de la politique. Veuillez réessayer.',
            invalid_json: 'JSON invalide dans le champ Paramètres par défaut. Veuillez réessayer.',
            page_title: 'Politiques de consentement',
            version_updated: 'Mis à jour',
            version_initial: 'Version initiale',
            json_format: 'Format JSON',
            sensitive: 'Sensible',
            flag_sensitive: 'Sensible au drapeau',
            flag_sensitive_title: 'Signaler la politique comme sensible',
            require_enhanced_review: 'Exiger un examen approfondi',
            enhanced_review_success: 'Exigence de révision améliorée activée.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Sélectionnez les catégories de données sensibles PDPL Article 2 applicables :',
            no_policies: 'Aucune politique de consentement trouvée. Créez votre première politique pour commencer.',
            approval_success: 'Politique approuvée.',
            reviewers_label: 'Réviseurs',
            enhanced_review_title: 'Révision approfondie requise',
            enhanced_review_badge: 'Révision approfondie requise',
            enhanced_review_status_pending: 'Révision en attente',
            enhanced_review_status_approved: 'Révision approuvée',
            enhanced_review_status_rejected: 'Révision rejetée',
            enhanced_review_reviewed_by: 'Révisé par',
            enhanced_review_notes: 'Notes de révision',
            enhanced_review_approve: 'Approuver la révision',
            enhanced_review_reject: 'Rejeter la révision',
            enhanced_review_notes_required: 'Les notes de révision sont obligatoires',
            enhanced_review_approved: 'Révision approfondie approuvée',
            enhanced_review_rejected: 'Révision approfondie rejetée — politique réinitialisée en brouillon',
            enhanced_review_reset_to_draft: 'Révision approfondie rejetée — politique réinitialisée en brouillon',
            enhanced_review_reject_warning: 'Le rejet réinitialisera cette politique au statut Brouillon. Le DPO doit la soumettre à nouveau après avoir résolu les problèmes.',
            submitted_modal_title: 'Soumis pour révision approfondie',
            submitted_modal_body: "Cette politique a été soumise. Un réviseur Juridique/Confidentialité doit l'approuver avant qu'elle puisse être activée.",
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Donnees de sante',
            biometric: 'Donnees biometriques',
            political: 'Opinions politiques',
            ethnic: 'Origine ethnique',
            genetic: 'Donnees genetiques',
            criminal: 'Casier judiciaire',
        },
        purposes: {
            title: 'Catalogue des finalités',
            new: 'Nouvelle finalité',
            edit: 'Modifier la finalité',
            name: 'Nom',
            legal_basis: 'Base juridique',
            retention_days: 'Durée de conservation (jours)',
            is_active: 'Actif',
            filter_by_category: 'Filtrer par catégorie',
            policy_ids: 'ID des politiques (séparés par des virgules)',
            ropa_activity_ids: 'ID des activités ROPA (séparés par des virgules)',
            policies: 'Politiques',
            deprecate: 'Déprécier',
            fetch_error: 'Échec de la récupération des finalités de consentement. Veuillez réessayer.',
            save_error: "Échec de l'enregistrement de la finalité de consentement. Veuillez réessayer.",
            deprecate_error: 'Échec de la dépréciation de la finalité de consentement. Veuillez réessayer.',
            page_title: 'Objectif Catalogue',
            no_purposes: 'Aucune finalité de consentement trouvée. Créez votre première finalité pour commencer.',
            category_personal_data: 'Données personnelles',
            category_sensitive_data: 'Données sensibles',
            category_health_data: 'Données de santé',
            category_financial_data: 'Données financières',
            category_biometric_data: 'Données biométriques',
            category_children_data: 'Données sur les enfants',
            category_location_data: 'Données de localisation',
            category_communications_data: 'Données de communication',
            category_other: 'Autre',
        },
        workflows: {
            title: 'Flux de travail du consentement',
            new: 'Nouveau flux de travail',
            edit: 'Modifier le flux de travail',
            workflow_type: 'Type de flux de travail',
            user_id: 'Identifiant utilisateur',
            purpose_id: 'Identifiant de finalité',
            purpose_ids_csv: 'ID de finalité (séparés par des virgules)',
            reason: 'Motif',
            process: 'Traiter',
            data_subject_ref: 'Référence de la personne concernée',
            data_subject: 'Personne concernée',
            channel: 'Canal',
            policy_id: 'ID de la politique',
            scope: 'Périmètre',
            legal_grounds: 'Fondements juridiques',
            assessment: 'Évaluation',
            select_outcome: 'Sélectionner le résultat',
            partially_approved: 'Partiellement approuvé',
            denied: 'Refusé',
            outcome_upheld: 'Confirmé',
            outcome_pending: 'En attente',
            outcome_denied: 'Refusé',
            status_count: 'Nombre par statut',
            channel_filter: 'Filtrer par canal',
            add_action: '+ Mesure',
            by_type: 'Par type',
            by_status: 'Par statut',
            downstream_actions: 'Actions en aval',
            downstream_actions_for: 'Actions en aval pour {subject} ({type})',
            add_downstream_action: 'Ajouter une action en aval',
            assigned_to: 'Assigné à',
            fetch_error: 'Échec de la récupération des flux de travail de consentement. Veuillez réessayer.',
            dashboard_error: 'Échec de la récupération du tableau de bord des flux de travail. Veuillez réessayer.',
            save_error: "Échec de l'enregistrement du flux de travail de consentement. Veuillez réessayer.",
            status_update_error: 'Échec de la mise à jour du statut du flux de travail. Veuillez réessayer.',
            assessment_error: "Échec de la mise à jour de l'évaluation du flux de travail. Veuillez réessayer.",
            add_action_error: "Échec de l'ajout de l'action du flux de travail. Veuillez réessayer.",
            update_action_error: "Échec de la mise à jour de l'action du flux de travail. Veuillez réessayer.",
            type_refusal: 'Refus',
            type_withdrawal: 'Retrait',
            type_restriction: 'Limitation',
            type_objection: 'Opposition',
            status_received: 'Reçu',
            status_processing: 'En cours de traitement',
            status_under_assessment: "En cours d'évaluation",
            status_implemented: 'Mis en œuvre',
            evidence_button: 'Preuve',
            evidence_modal_title: 'Preuve du flux de travail',
            no_evidence: 'Aucune preuve attachée à ce flux de travail.',
            upload_evidence_label: 'Télécharger des preuves de flux de travail',
            completed_at: 'Terminé à',
            effective_date: "Date d'entrée en vigueur",
            page_title: 'Flux de travail de consentement',
            no_workflows: 'Aucun flux de consentement trouvé. Les flux sont créés lorsque les personnes concernées exercent leurs droits.',
            type_grant: 'Octroi',
            propagation_none: 'Aucun',
            propagation_pending: 'En attente',
            propagation_pending_warning: 'Impossible de terminer — la propagation du retrait est toujours en attente',
            propagation_completed: 'Terminé',
            propagation_partial: 'Partiel',
            propagation_incomplete_error: 'Impossible de terminer le flux tant que la propagation du retrait est en attente',
            propagation_status: 'Statut de propagation',
        },
        snapshots: {
            title: 'Instantanés CMP',
            new: 'Nouvel instantané',
            label: 'Libellé',
            snapshot_date: "Date de l'instantané",
            purposes_count: 'Nombre de finalités',
            compare: 'Comparer',
            snapshot_data: "Données de l'instantané (JSON)",
            select_left: "Sélectionner l'instantané de gauche",
            select_right: "Sélectionner l'instantané de droite",
            comparison_results: 'Résultats de la comparaison',
            field: 'Champ',
            left: 'Gauche',
            right: 'Droite',
            close_comparison: 'Fermer la comparaison',
            trigger: 'Déclencheur',
            trigger_manual: 'Manuel',
            trigger_policy_approval: 'Approbation de politique',
            cmp_version: 'Version CMP',
            policy_version_id: 'ID de la version de politique',
            banner_config: 'Configuration de la bannière (JSON)',
            active_purposes: 'Finalités actives (JSON)',
            active_legal_bases: 'Bases juridiques actives (JSON)',
            domain_scope: 'Périmètre de domaine',
            domain_scope_csv: 'Périmètre de domaine (séparé par des virgules)',
            raw_config: 'Configuration brute (JSON)',
            checksum: 'Somme de contrôle',
            created: 'Créé',
            fetch_error: 'Échec de la récupération des instantanés CMP. Veuillez réessayer.',
            save_error: "Échec de l'enregistrement de l'instantané CMP. Veuillez réessayer.",
            compare_error: 'Échec de la comparaison des instantanés. Veuillez réessayer.',
            page_title: 'Instantanés de consentement',
            json_format: 'Format JSON',
            json_array_format: 'format de tableau JSON',
            manual_snapshot_note: "Pour administrateurs/développeurs uniquement. En fonctionnement normal, les instantanés sont créés automatiquement via le webhook CMP ou lors de l'approbation des politiques. Collez le JSON exporté depuis votre fournisseur CMP (ex. OneTrust, Cookiebot).",
            banner_config_hint: "Collez l'objet de configuration de bannière depuis votre export CMP.",
            active_purposes_hint: 'Tableau JSON des objets de finalités actives copiés depuis votre export CMP.',
            active_legal_bases_hint: 'Tableau JSON des objets de bases légales actives copiés depuis votre export CMP.',
            raw_config_hint: 'JSON de configuration brut complet depuis votre CMP. Les autres champs JSON en sont extraits. Une somme de contrôle SHA-256 est calculée automatiquement.',
            changed_field: 'Champ modifié : ',
            no_snapshots: 'Aucun instantané CMP trouvé. Les instantanés sont créés lorsque les politiques sont approuvées ou déclenchés manuellement.',
            detail_title: "Détail de l'instantané",
            view_snapshot_detail: "Voir le détail de l'instantané",
            policy_version: 'Version de la politique',
            removed: 'Avant',
            added: 'Après',
        },
        change_awareness: {
            title: 'Détection des modifications de consentement',
            change_type: 'Type de modification',
            entity_type: "Type d'entité",
            entity_id: "Identifiant de l'entité",
            detected_at: 'Détecté le',
            acknowledge: 'Prendre en compte',
            new_alert: 'Nouvelle alerte',
            new_alert_form: 'Nouvelle alerte de détection de modification',
            total_open: 'Total ouvert',
            by_severity: 'Par sévérité',
            snapshot_id: "ID de l'instantané",
            update_status: 'Mettre à jour le statut',
            resolution_notes: 'Notes de résolution',
            resolution_required: 'Notes de résolution (obligatoire)',
            resolution_required_msg: "Les notes de résolution sont requises lors de la résolution d'un élément.",
            status_open: 'Ouvert',
            status_under_review: 'En cours de révision',
            status_resolved: 'Résolu',
            status_accepted: 'Accepté',
            severity_informational: 'Informatif',
            severity_requires_review: 'Nécessite une révision',
            severity_policy_violation: 'Violation de politique',
            create_reconciliation: 'Créer une tâche de rapprochement',
            reconciliation_created: 'Tâche de réconciliation créée avec succès.',
            reconciliation_error: 'Échec de la création de la tâche de rapprochement. Veuillez réessayer.',
            fetch_error: 'Échec de la récupération des éléments de détection de modification. Veuillez réessayer.',
            dashboard_error: 'Échec de la récupération du tableau de bord de détection de modification. Veuillez réessayer.',
            create_error: "Échec de la création de l'élément de détection de modification. Veuillez réessayer.",
            status_update_error: 'Échec de la mise à jour du statut de détection de modification. Veuillez réessayer.',
            page_title: 'Sensibilisation au changement',
            no_items: 'Aucun élément de sensibilisation aux changements trouvé. Les éléments sont créés lorsque des changements de configuration du consentement sont détectés.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: "Piste d'audit du consentement",
            event_type: "Type d'événement",
            user_id: 'Identifiant utilisateur',
            timestamp: 'Horodatage',
            details: 'Détails',
            actor: 'Acteur',
            filter_by_entity_type: "Filtrer par type d'entité",
            filter_by_action: 'Filtrer par action',
            fetch_error: "Échec de la récupération de la piste d'audit du consentement. Veuillez réessayer.",
            page_title: 'Vérification du consentement',
            export_csv: 'Exporter CSV',
            exporting: 'Exporter…',
            export_success: "Piste d'audit exportée avec succès.",
            export_error: "Échec de l'exportation de la piste d'audit du consentement. Veuillez réessayer.",
            no_entries: 'Aucune entrée d’audit trouvée. Les événements d’audit sont enregistrés automatiquement lorsque les données de consentement changent.',
            entity_policy: 'Politique',
            entity_purpose: 'Finalité',
            entity_workflow: 'Flux de travail',
            entity_consent_record: 'Enregistrement de consentement',
            entity_change_awareness: 'Sensibilisation aux changements',
            entity_cmp_snapshot: 'Instantané CMP',
            action_created: 'Créé',
            action_updated: 'Mis à jour',
            action_status_changed: 'Statut modifié',
            action_drift_detected: 'Dérive détectée',
            action_snapshot_created: 'Instantané créé',
            action_approval_submitted: 'Approbation soumise',
            action_archived: 'Archivé',
            action_purpose_auto_mapped: 'Finalité attribuée automatiquement',
            action_purpose_mapping_updated: 'Attribution de finalité mise à jour',
            action_webhook_configured: 'Webhook configuré',
            entity_propagation: 'Propagation',
            entity_reconciliation: 'Réconciliation',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Propagation confirmée',
            action_propagation_failed: 'Échec de la propagation',
            action_consent_captured: 'Consentement capturé',
            action_reconciliation_created: 'Réconciliation créée',
            action_reconciliation_resolved: 'Réconciliation résolue',
            action_submitted_for_review: 'Soumis pour examen',
            action_activated: 'Activé',
            action_suspended: 'Suspendu',
            action_retired: 'Retiré',
            action_enhanced_review_required: 'Examen approfondi requis',
            action_enhanced_review_approved: 'Révision approfondie approuvée',
            action_enhanced_review_rejected: 'Révision approfondie rejetée — politique réinitialisée en brouillon',
            action_consented: 'Consentement',
            action_consented_customize: 'Consentement personnalisé',
            action_revoked: 'Révoqué',
            action_rejected: 'Rejeté',
        },
        web_records: {
            title: 'Enregistrements de consentement Web',
            page_title: 'Enregistrements de consentement Web',
            domain: 'Domaine',
            visitor_id: 'ID visiteur',
            consent_action: 'Action de consentement',
            received_at: 'Reçu à',
            admin_status: "Statut d'administrateur",
            revoke: 'Révoquer',
            reject: 'Rejeter',
            reason: 'Raison',
            reason_placeholder: 'Raison de cette action (facultatif)',
            revoke_confirm: 'Révoquer cet enregistrement de consentement ?',
            reject_confirm: 'Rejeter cet enregistrement de consentement ?',
            override_success: "Le statut de l'enregistrement a été mis à jour avec succès.",
            override_error: "Échec de la mise à jour du statut de l'enregistrement.",
            no_records: 'Aucun enregistrement de consentement Web trouvé.',
            fetch_error: 'Échec du chargement des enregistrements de consentement Web.',
            filter_by_domain: 'Filtrer par domaine',
            filter_by_action: 'Filtrer par action',
            active: 'Actif',
        },
        legal_bases: {
            all: 'Toutes les bases juridiques',
        },
        common_statuses: {
            suspended: 'Suspendu',
            retired: 'Retiré',
            deprecated: 'Déprécié',
        },
        approval_modes: {
            all: 'Tous',
            any: "N'importe lequel",
        },
        cmp_integration: {
            title: 'Intégration AesirX CMP',
            back_to_consent: 'Retour au consentement',
            back_to_integration: "Retour à l'intégration",
            webhook_status: 'Statut du webhook',
            events_received: 'Événements reçus',
            web_consent_records: 'Enregistrements de consentement web',
            unmapped_purposes: 'Finalités non mappées',
            integration_management: "Gestion de l'intégration",
            webhook_configuration: 'Configuration du webhook',
            webhook_config_desc: "Configurez le point d'accès du webhook CMP et gérez le secret HMAC.",
            purpose_mapping: 'Correspondance des finalités',
            purpose_mapping_desc: 'Associez les identifiants de finalité CMP aux finalités du catalogue de consentement GRC.',
            bulk_sync: 'Synchronisation en masse',
            bulk_sync_desc: "Importez les données de consentement historiques et consultez l'historique de synchronisation.",
            consent_analytics: 'Analytique du consentement web (30 derniers jours)',
            consent_grant_rate: "Taux d'octroi du consentement",
            top_domain: 'Domaine principal',
            records: 'enregistrements',
            banner_interactions: 'Interactions avec la bannière',
            consent_actions: 'Actions de consentement',
            opt_in_trend: "Tendance d'opt-in/opt-out",
            records_by_domain: 'Enregistrements par domaine',
            date: 'Date du',
            grants: 'Consentements',
            denials: 'Refus',
            not_configured: 'Non configuré',
            load_error: "Échec du chargement des données d'intégration",
            webhook_not_configured: 'Webhook non configuré.',
            webhook_not_configured_msg: 'Veuillez {link} au préalable.',
            configure_webhook_link: 'configurer le webhook',
            setup_wizard: "Assistant d'installation",
            step_configure_webhook: 'Configurer le webhook',
            step_map_purposes: 'Associer les finalités',
            step_run_sync: 'Lancer la synchronisation initiale en masse',
            step_verify_data: 'Vérifier les données dans le tableau de bord',
            configure: 'Configurer',
            view_dashboard: 'Voir le tableau de bord',
            syncing: 'Synchronisation en cours...',
            sync_now: 'Synchroniser maintenant',
            sync_completed: 'Synchronisation terminée : {imported} importé(s), {skipped} ignoré(s), {failed} échoué(s).',
            sync_failed: 'Échec de la synchronisation',
            last_sync_result: 'Dernier résultat de synchronisation',
            imported: 'Importé(s)',
            skipped: 'Ignoré(s)',
            errors: 'Erreurs',
            index: 'Répertoire',
            error: 'Erreur',
            sync_history: 'Historique de synchronisation',
            no_sync_jobs: 'Aucune tâche de synchronisation enregistrée.',
            load_sync_error: 'Échec du chargement des données de synchronisation',
            purpose_mapping_title: 'Correspondance des finalités CMP',
            auto_mapping: 'Association automatique en cours...',
            auto_map_purposes: 'Associer automatiquement les finalités',
            unmapped_warning: '{count} finalité(s) non associée(s) -- ces finalités CMP doivent être associées aux finalités GRC.',
            mapping_saved: 'Association enregistrée.',
            mapping_deleted: 'Association supprimée.',
            auto_map_complete: 'Association automatique terminée : {mapped} associée(s), {unmatched} non associée(s).',
            auto_map_failed: "Échec de l'association automatique",
            save_mapping_error: "Échec de l'enregistrement de l'association",
            delete_mapping_error: "Échec de la suppression de l'association",
            load_data_error: 'Échec du chargement des données',
            cmp_purpose: 'Finalité CMP',
            grc_purpose: 'Finalité GRC',
            select_grc_purpose: '-- Sélectionner une finalité GRC --',
            auto_mapped: 'Associé automatiquement',
            mapped: 'Associé',
            unmapped: 'Non associé',
            no_mappings: "Aucune correspondance de finalité trouvée. Les correspondances sont créées automatiquement à la réception d'événements de consentement.",
            total_mappings: 'Total : {count} correspondance(s)',
            webhook_config_title: 'Configuration du webhook',
            current_configuration: 'Configuration actuelle',
            setup_webhook: 'Configurer le webhook',
            setup_webhook_desc: 'Configurez le webhook AesirX CMP pour recevoir les événements de consentement.',
            allowed_domains: 'Domaines autorisés (un par ligne)',
            webhook_secret_warning: 'Secret du webhook (enregistrez-le maintenant -- il ne sera plus affiché) :',
            last_event: 'Dernier événement',
            none_received: 'Aucun',
            update_domains: 'Mettre à jour les domaines',
            pause_webhook: 'Suspendre le webhook',
            resume_webhook: 'Reprendre le webhook',
            regenerate_secret: 'Régénérer le secret',
            configuring: 'Configuration en cours...',
            create_webhook_config: 'Créer la configuration du webhook',
            webhook_endpoint: "Point d'accès du webhook",
            webhook_endpoint_desc: 'Configurez votre AesirX CMP pour envoyer les événements de consentement à :',
            webhook_signature_note: "Incluez la signature HMAC-SHA256 dans l'en-tête {header}.",
            configured_success: 'Webhook configuré avec succès.',
            config_updated: 'Configuration mise à jour.',
            config_error: 'Échec de la configuration du webhook',
            update_error: 'Échec de la mise à jour de la configuration',
            toggle_error: "Échec de l'activation/désactivation du webhook",
            deactivated: 'Webhook désactivé.',
            deactivate_error: 'Échec de la désactivation du webhook',
            page_title: 'CMP Intégration',
            webhook_config_page_title: 'CMP Configuration du webhook',
            bulk_sync_page_title: 'Remapper les enregistrements',
            purpose_mapping_page_title: 'Cartographie des objectifs',
            data_loaded: "CMP données d'intégration chargées",
            sync_all: 'Tout synchroniser',
            sync_selected: 'Synchroniser la sélection',
            sync_status: 'État de synchronisation',
            last_sync: 'Dernière synchronisation',
            no_records: 'Aucun enregistrement trouvé.',
            sync_in_progress: 'Synchronisation en cours...',
            sync_complete: 'Synchronisation terminée.',
            sync_error: 'La synchronisation a échoué. Veuillez réessayer.',
            map_purpose: 'Associer la finalité',
            unmap: 'Dissocier',
            mapping_status: 'État de l’association',
            save_mapping: 'Enregistrer l’association',
            remap_unmapped_records: 'Remapper les enregistrements non mappés',
            remap_records: 'Remapper les enregistrements',
            add_mappings_first: "Ajoutez d'abord des mappages d'objectifs (étape 2)",
            remap_result: 'Résultat du remappage',
            remap_result_desc: 'Nous avons traité tous les enregistrements de consentement avec des finalités non mappées ou partiellement mappées.',
            total_processed: 'Total traité',
            newly_mapped: 'Nouvellement cartographié',
            still_unmapped_label: 'Toujours non cartographié',
            still_unmapped_msg: '{count} les enregistrements ont toujours des objectifs non mappés. {link} puis cliquez à nouveau sur Re-mapper.',
            add_more_mappings: 'Ajouter plus de mappages',
            remap_complete: 'Remappage terminé : {remapped} nouvellement mappés, {still_unmapped} toujours non mappés.',
            add_mapping: 'Ajouter un mappage',
            cmp_purpose_id_label: "CMP ID de l'objectif *",
            cmp_purpose_id_required: "CMP L'ID de l'objectif est requis",
            cmp_purpose_name_label: "CMP Nom de l'objectif",
            grc_purpose_label: 'Objectif GRC',
        },
        propagation: {
            title: 'Propagation du retrait',
            systems: 'Systèmes liés',
            status: 'Statut',
            confirmed: 'Confirmé',
            pending: 'En attente',
            failed: 'Échoué',
            confirm: 'Confirmer le retrait',
            fail: 'Marquer comme échoué',
            failure_reason: "Raison de l'échec",
            can_close: 'Tous les systèmes confirmés — le flux peut être clôturé',
            cannot_close: 'En attente des confirmations système',
            no_tasks: 'Aucune tâche de propagation.',
            confirm_success: 'Retrait système confirmé.',
            fail_success: 'Système marqué comme échoué.',
            propagate_success: 'Retrait propagé aux systèmes liés.',
            propagate: 'Propager aux systèmes',
            add_system: 'Ajouter un système',
            remove_system: 'Supprimer',
            system_id: 'ID système',
            system_name: 'Nom du système',
            owner_id: 'ID du propriétaire',
            propagate_error: 'Échec de la propagation du retrait. Veuillez réessayer.',
            no_systems: 'Ajoutez au moins un système.',
            page_title: 'Propagation du retrait',
            workflow_id: 'Sélectionnez le flux de travail de retrait',
            system_id_hint: 'ex. crm-001, erp-prod',
            owner_id_hint: 'Propriétaire du système',
            elapsed: 'Écoulé',
        },
        sensitive: {
            title: 'Politiques de données sensibles',
            flag: 'Marquer comme sensible',
            categories: 'Catégories sensibles',
            enhanced_review: 'Exiger un examen approfondi',
            enhanced_review_success: 'Examen approfondi requis.',
            flag_success: 'Politique marquée comme sensible.',
            no_sensitive: 'Aucune politique de données sensibles.',
        },
        reconciliation: {
            title: 'Réconciliation des écarts',
            create: 'Nouvelle tâche de réconciliation',
            system: 'Système',
            owner: 'Propriétaire',
            description: 'Descriptif',
            resolution: 'Résolution',
            resolve: 'Résoudre',
            update_grc: 'Mettre à jour GRC',
            update_cmp: 'Mettre à jour CMP',
            accept_difference: 'Accepter la différence',
            escalate: 'Escalader',
            resolution_notes: 'Notes de résolution',
            no_tasks: 'Aucune tâche de réconciliation.',
            resolve_success: 'Tâche de réconciliation résolue.',
            create_success: 'Tâche de réconciliation créée.',
            open: 'Ouvert',
            resolved: 'Résolu',
            escalated: 'Escaladé',
            dashboard_open: 'Tâches ouvertes',
            dashboard_resolved: 'Tâches résolues',
            dashboard_escalated: 'Tâches escaladées',
            page_title: 'Rapprochement des discordances',
            my_tasks: 'Mes tâches',
            all_tasks: 'Toutes les tâches',
            severity: 'Gravité',
        },
        sync: {
            title: 'Synchronisation CMP',
            direction: 'Direction',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Conflit',
            resolve_conflict: 'Résoudre le conflit',
            no_events: 'Aucun événement de synchronisation.',
            resolve_success: 'Conflit de synchronisation résolu.',
            create_success: 'Événement de synchronisation enregistré.',
            page_title: 'CMP Synchronisation',
            detect_drift: 'Détecter les conflits',
            detecting: 'Détection...',
            drift_result: '{detected} conflit(s) détecté(s) parmi les {total} objectif(s) comparé(s).',
            no_drift: 'Aucun nouveau conflit détecté.',
            drift_error: "Échec de l'exécution de la détection des conflits.",
            resolve_modal_title: 'Résoudre le conflit',
            resolve_field: 'Champ',
            resolve_grc_value: 'Valeur GRC',
            resolve_cmp_value: 'CMP Valeur',
            accept_cmp: 'Accepter la valeur CMP',
            accept_cmp_desc: "Mettez à jour GRC pour qu'il corresponde à CMP — la valeur CMP devient la nouvelle valeur GRC.",
            keep_grc: 'Conserver la valeur GRC',
            keep_grc_desc: "Fermez ce conflit sans modifier GRC — informez le propriétaire CMP de l'aligner.",
            resolve_applied: 'Conflit résolu. Objectif GRC mis à jour pour correspondre à CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Ce rapport de préparation est consultatif. Il n’approuve, n’assemble, ne matérialise, ne dépose ni n’envoie le dossier.',
            run_label: 'Exécuter le pré-vol',
            running: 'Exécution du pré-vol…',
            assemble_label: 'Forfait à assembler automatiquement',
            assembling: 'Assemblage…',
            verdict_label: 'Verdict avant le vol',
            tokens_label: '≈ {count} jetons',
            remediation_label: 'Assainissement',
            no_gaps: 'Aucun écart avant vol détecté. Le dossier est prêt à monter.',
            override_label: "Annuler le verdict de blocage : l'assemblage sera estampillé par un audit.",
            accept_label: 'Accepter le rapport',
            reject_label: 'Rejeter le rapport',
            assembled_label: 'Dossier de soumission assemblé',
            package_id_label: 'Identifiant du paquet',
            package_filename_label: 'Nom du fichier',
            package_hash_label: 'Hachage du paquet',
            evidence_count_label: '{count} éléments de preuve regroupés',
            error_generic: "Impossible d'exécuter le pré-vol ou d'assembler le package. Réessayez ou vérifiez l'état du dépôt.",
        },
        errors: {
            fetch: 'Échec du chargement des évaluations.',
            save: "Échec de l'enregistrement de l'évaluation.",
            fetch_gap_report_failed: "Échec de la récupération du rapport d'écarts.",
            fetch_transfers_failed: 'Échec de la récupération des transferts.',
            save_transfer_failed: "Échec de l'enregistrement du transfert.",
            submit_review_failed: 'Échec de la soumission pour révision.',
            process_decision_failed: 'Échec du traitement de la décision.',
            deprecate_failed: 'Échec de la dépréciation.',
            evaluate_risk_failed: "Échec de l'évaluation du risque.",
            fetch_safeguards_failed: 'Échec de la récupération des garanties.',
            save_safeguard_failed: "Échec de l'enregistrement de la garantie.",
            update_safeguard_status_failed: 'Échec de la mise à jour du statut de la garantie.',
        },
        nav: {
            dpias: 'AIPD',
            transfers: 'Évaluations des transferts',
            dashboard: 'Tableau de bord',
            gap_report: "Rapport d'écarts",
        },
        risk_level_critical: 'Risque critique',
        risk_level_high: 'Risque élevé',
        risk_level_medium: 'Risque moyen',
        risk_level_low: 'Risque faible',
        severity_critical: 'Gravité critique',
        severity_high: 'Gravité élevée',
        severity_medium: 'Gravité moyenne',
        severity_low: 'Gravité faible',
        overdue: 'En retard',
        framework_filter_label: 'Cadre',
        aria_filter_by_framework: 'Filtrer par framework',
        dashboard: {
            title: 'Tableau de bord des évaluations',
            page_title: 'Tableau de bord des évaluations',
            announce_loaded: 'Tableau de bord des évaluations chargé',
            announce_chart_view: 'Affichage en graphique',
            announce_table_view: 'Affichage en tableau de données',
            aria_metrics_table: 'Métriques du tableau de bord',
            metric_col: 'Métrique',
            value_col: 'Valeur',
            dpia_prefix: 'AIPD',
            transfer_prefix: 'Transfert',
            risk_prefix: 'Risque',
            aria_dpias_by_status: 'AIPD par statut',
            aria_transfers_by_status: 'Transferts par statut',
            aria_top_risks: 'Principaux risques',
            aria_top_risks_table: 'Principaux risques des AIPD',
            overdue_dpias_attention: '{count} évaluations AIPD en retard nécessitent une attention',
            no_overdue_dpias: 'Aucune évaluation AIPD en retard',
            overdue_transfers_attention: '{count} évaluations de transfert en retard nécessitent une attention',
            no_overdue_transfers: 'Aucune évaluation de transfert en retard',
            dpias_overview: "Vue d'ensemble des AIPD",
            transfers_overview: "Vue d'ensemble des évaluations de transferts",
            overdue_items: 'Éléments en retard',
            overdue_dpias: 'AIPD en retard',
            overdue_transfers: 'Transferts en retard',
            count: 'Nombre',
            no_data: 'Aucune donnée disponible.',
            no_risks: 'Aucun risque trouvé.',
            no_dashboard_data: 'Aucune donnée de tableau de bord disponible.',
            fetch_error: 'Échec de la récupération des données du tableau de bord. Veuillez réessayer.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'AIPD',
            announce_submitted_for_review: 'AIPD soumise pour examen',
            announce_deprecated: 'AIPD obsolète',
            announce_risks_loaded: '{count} risque(s) chargé(s)',
            announce_mitigations_loaded: "{count} mesure(s) d'atténuation chargée(s)",
            announce_risk_deleted: 'Risque supprimé',
            announce_mitigation_status_updated: "Statut de la mesure d'atténuation mis à jour",
            aria_filter: 'Filtrer les AIPD',
            aria_filter_by_status: 'Filtrer par statut',
            aria_filter_by_lead: "Filtrer par responsable d'évaluation",
            aria_table: "Analyses d'impact sur la protection des données",
            announce_found: '{count} AIPD trouvée(s)',
            announce_decision: 'AIPD {decision}',
            mitigation_start: 'Démarrer',
            mitigation_mark_implemented: 'Marquer comme implémenté',
            mitigation_verify: 'Vérifier',
            title: "Analyses d'impact relatives à la protection des données",
            new: 'Nouvelle AIPD',
            edit: "Modifier l'AIPD",
            dpia_title: 'Titre',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Activité',
            risk_score: 'Score de risque',
            risks: 'Risques',
            mitigations: "Mesures d'atténuation",
            add_risk: 'Ajouter un risque',
            add_mitigation: "Ajouter une mesure d'atténuation",
            necessity: 'Nécessité / Proportionnalité',
            likelihood: 'Probabilité',
            impact: 'Impact',
            severity_label: 'Gravité',
            purpose: 'Finalité',
            consultation_notes: 'Notes de consultation',
            dpo_opinion: 'Avis du DPO',
            processing_activity_ids: 'ID des activités de traitement (séparés par des virgules)',
            system_ids: 'ID des systèmes (séparés par des virgules)',
            vendor_ids: 'ID des prestataires (séparés par des virgules)',
            data_types: 'Types de données (séparés par des virgules)',
            data_types_short: 'Types de données',
            data_subjects: 'Personnes concernées (séparées par des virgules)',
            assessment_lead_id: "ID du responsable de l'évaluation",
            assessment_lead: "Responsable de l'évaluation",
            dpo_id: 'ID du DPO',
            reviewer_ids: 'ID des réviseurs (séparés par des virgules)',
            approval_mode: "Mode d'approbation",
            approval_all: 'Tous',
            approval_any: "N'importe lequel",
            require_dpo_signoff: 'Signature du DPO requise',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Date de révision',
            submit_for_review: 'Soumettre pour révision',
            approve_reject: 'Approuver/Rejeter',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Déprécier',
            deprecated: 'Déprécié',
            hide_risks: 'Masquer les risques',
            hide_mitigations: "Masquer les mesures d'atténuation",
            comments: 'Commentaires',
            submit_decision: 'Soumettre la décision',
            category_label: 'Catégorie',
            category_data_breach: 'Violation de données',
            category_unauthorized_access: 'Accès non autorisé',
            category_function_creep: 'Détournement de finalité',
            category_re_identification: 'Ré-identification',
            category_discrimination: 'Traitement discriminatoire',
            category_surveillance: 'Mise sous surveillance',
            category_other: 'Autre',
            affected_rights: 'Droits affectés (séparés par des virgules)',
            no_risks: 'Aucun risque trouvé.',
            no_mitigations: "Aucune mesure d'atténuation trouvée.",
            no_dpias: 'Aucune AIPD trouvée.',
            mitigation_type: 'Catégorie',
            mitigation_technical: 'Technique',
            mitigation_organizational: 'Organisationnel',
            mitigation_contractual: 'Contractuel',
            risk_ids: 'ID des risques (séparés par des virgules)',
            responsible_party: 'Partie responsable',
            responsible_party_placeholder: 'Ou saisir le nom manuellement',
            affected_rights_placeholder: 'Les droits sélectionnés apparaissent ici',
            affected_rights_freetext_placeholder: 'Saisir les droits affectés (séparés par des virgules)',
            responsible: 'Responsable',
            target_date: 'Date cible',
            created: 'Créé',
            fetch_error: 'Échec de la récupération des AIPD. Veuillez réessayer.',
            save_error: "Échec de l'enregistrement de l'AIPD. Veuillez réessayer.",
            submit_review_error: "Échec de la soumission de l'AIPD pour révision. Veuillez réessayer.",
            decision_error: 'Échec du traitement de la décision. Veuillez réessayer.',
            deprecate_error: "Échec de la dépréciation de l'AIPD. Veuillez réessayer.",
            fetch_risks_error: 'Échec de la récupération des risques. Veuillez réessayer.',
            save_risk_error: "Échec de l'enregistrement du risque. Veuillez réessayer.",
            delete_risk_error: 'Échec de la suppression du risque. Veuillez réessayer.',
            fetch_mitigations_error: "Échec de la récupération des mesures d'atténuation. Veuillez réessayer.",
            save_mitigation_error: "Échec de l'enregistrement de la mesure d'atténuation. Veuillez réessayer.",
            update_mitigation_status_error: "Échec de la mise à jour du statut de la mesure d'atténuation. Veuillez réessayer.",
            link_form_template: 'Modèle de formulaire réglementaire de lien',
            link_form_template_placeholder: 'Sélectionnez un modèle de formulaire DPIA (facultatif)',
            link_form_template_none: 'Aucun',
            linked_form: 'Formulaire lié',
            linked_form_creating: "Création d'un formulaire lié...",
            linked_form_error: "Échec de la création de l'instance de formulaire liée. Le DPIA a été créé sans formulaire lié.",
            view_linked_form: 'Voir le formulaire',
            announce_created_with_form: 'DPIA créé avec une instance de formulaire liée',
            creation_mode: 'DPIA Mode de création',
            mode_standard: 'Norme DPIA',
            mode_template: 'Utiliser le modèle de formulaire réglementaire',
            select_form_template: 'Sélectionnez un modèle de formulaire',
            create_and_fill_form: 'Créer et remplir un formulaire',
            template_sections_title: 'Sections du modèle',
            field_singular: 'champ',
            field_plural: 'champs',
            no_sections: 'Aucune section trouvée dans ce modèle.',
            template_mode_hint: 'Après création, vous serez redirigé pour remplir le formulaire réglementaire complet.',
            linked_dossier_filings: 'Dépôts de dossier liés',
            no_linked_dossier_filings: 'Aucun dépôt de dossier lié à cette AIPD.',
            view_dossier_filing: 'Voir le dépôt',
            hide_dossier_filings: 'Masquer les dépôts de dossier',
            supporting_evidence: 'Preuve à l’appui',
            hide_supporting_evidence: 'Masquer les preuves',
            no_supporting_evidence: 'Aucune preuve à l’appui n’est jointe.',
            upload_supporting_evidence: "Télécharger des preuves à l'appui",
            view_form_responses: 'Voir les réponses du formulaire',
            regulatory_framework: 'Cadre réglementaire',
            all_frameworks: 'Tous les cadres',
            no_templates_for_market: 'Aucun modèle disponible pour le marché sélectionné. Veuillez sélectionner un autre marché ou créer un modèle.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Transferts de données',
            announce_found: '{count} évaluation(s) de transfert trouvée(s)',
            announce_submitted_for_review: 'Transfert soumis pour examen',
            announce_deprecated: 'Transfert obsolète',
            announce_decision: 'Transfert {decision}',
            announce_risk_eval_completed: 'Évaluation des risques terminée',
            announce_safeguard_status_updated: 'Statut de la mesure de protection mis à jour',
            announce_safeguards_loaded: '{count} mesure(s) de protection chargée(s)',
            aria_filter: 'Filtrer les évaluations de transfert',
            aria_filter_by_status: 'Filtrer par statut',
            aria_filter_by_mechanism: 'Filtrer par mécanisme de transfert',
            aria_table: 'Évaluations des transferts de données',
            title: 'Évaluations des transferts transfrontaliers',
            new: 'Nouveau transfert',
            edit: 'Modifier le transfert',
            name: 'Nom',
            from_country: "Pays d'origine",
            to_country: 'Pays de destination',
            safeguards: 'Garanties',
            risk_level: 'Niveau de risque',
            assessment_notes: "Notes d'évaluation",
            mechanism: 'Mécanisme',
            transfer_mechanism: 'Mécanisme de transfert',
            transfer_mechanism_detail: 'Détail du mécanisme de transfert',
            all_mechanisms: 'Tous les mécanismes',
            mechanism_other: 'Autre',
            data_types: 'Types de données (séparés par des virgules)',
            data_subjects: 'Personnes concernées (séparées par des virgules)',
            purpose: 'Finalité',
            recipient_name: 'Nom du destinataire',
            create_success: 'Évaluation de transfert créée.',
            update_success: 'Évaluation de transfert mise à jour.',
            recipient_details: 'Détails du destinataire',
            recipient_details_placeholder: 'Rôle, emplacement et systèmes du destinataire, et détails du transfert ultérieur',
            vendor_id: 'ID du prestataire',
            processing_activity_ids: 'ID des activités de traitement (séparés par des virgules)',
            reviewer_ids: 'ID des réviseurs (séparés par des virgules)',
            approval_mode: "Mode d'approbation",
            approval_all: 'Tous',
            approval_any: "N'importe lequel",
            require_dpo_signoff: 'Signature du DPO requise',
            review_date: 'Date de révision',
            created: 'Créé',
            deprecated: 'Déprécié',
            submit_for_review: 'Soumettre pour révision',
            approve_reject: 'Approuver/Rejeter',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Déprécier',
            risk_eval: 'Évaluation du risque',
            hide_safeguards: 'Masquer les garanties',
            mechanism_proof: 'Preuve de mécanisme',
            hide_mechanism_proof: 'Masquer la preuve du mécanisme',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Aucune preuve de mécanisme jointe.',
            upload_mechanism_proof: 'Télécharger la preuve du mécanisme',
            comments: 'Commentaires',
            submit_decision: 'Soumettre la décision',
            data_sensitivity: 'Sensibilité des données (1-5)',
            country_risk: 'Risque pays (1-5)',
            safeguard_adequacy: 'Adéquation des garanties (1-5)',
            volume_factor: 'Facteur de volume (1-5)',
            evaluate: 'Évaluer',
            add_safeguard: 'Ajouter une garantie',
            responsible_party: 'Partie responsable',
            responsible_party_placeholder: 'Ou saisir le nom manuellement',
            responsible: 'Responsable',
            implementation_date: 'Date de mise en œuvre',
            implementation: 'Mise en œuvre',
            expiry_date: "Date d'expiration",
            expiry: 'Expiration',
            expire: 'Expirer',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Aucune garantie trouvée.',
            no_transfers: 'Aucune évaluation de transfert trouvée.',
            fetch_error: 'Échec de la récupération des évaluations de transfert. Veuillez réessayer.',
            save_error: "Échec de l'enregistrement de l'évaluation de transfert. Veuillez réessayer.",
            submit_review_error: 'Échec de la soumission pour révision. Veuillez réessayer.',
            decision_error: 'Échec du traitement de la décision. Veuillez réessayer.',
            deprecate_error: 'Échec de la dépréciation du transfert. Veuillez réessayer.',
            risk_eval_error: "Échec de l'évaluation du risque. Veuillez réessayer.",
            fetch_safeguards_error: 'Échec de la récupération des garanties. Veuillez réessayer.',
            save_safeguard_error: "Échec de l'enregistrement de la garantie. Veuillez réessayer.",
            update_safeguard_status_error: 'Échec de la mise à jour du statut de la garantie. Veuillez réessayer.',
            safeguard_type_encryption_in_transit: 'Chiffrement en transit',
            safeguard_type_encryption_at_rest: 'Chiffrement au repos',
            safeguard_type_access_controls: "Contrôles d'accès",
            safeguard_type_contractual_clauses: 'Clauses contractuelles',
            safeguard_type_data_minimization: 'Minimisation des données',
            safeguard_type_pseudonymization: 'Pseudonymisation',
            safeguard_type_audit_rights: "Droits d'audit",
            safeguard_type_breach_notification: 'Notification de violation',
            safeguard_type_data_localization: 'Localisation des données',
            safeguard_type_other: 'Autre',
            activate: 'Activer',
            all_statuses: 'Tous les statuts',
            any: "N'importe lequel",
            mechanism_detail: 'Détail du mécanisme',
            safeguard_types: {
                encryption_in_transit: 'Chiffrement en transit',
                encryption_at_rest: 'Chiffrement au repos',
                access_controls: "Contrôles d'accès",
                contractual_clauses: 'Clauses contractuelles',
                data_minimization: 'Minimisation des données',
                pseudonymization: 'Pseudonymisation',
                audit_rights: "Droits d'audit",
                breach_notification: 'Notification de violation',
                data_localization: 'Localisation des données',
                other: 'Autre',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: "Rapport d'écarts des évaluations",
            announce_loaded: "Rapport d'écarts chargé. {count} écart(s) identifié(s)",
            aria_activities_without_dpia: 'Activités sans évaluation AIPD',
            aria_activities_unclassified: "Activités dont l'exigence d'AIPD n'est pas classifiée",
            activities_unclassified_section_title: "Activités dont l'exigence d'AIPD n'est pas classifiée",
            activities_unclassified_description: "Ces activités n'ont pas été examinées pour déterminer si une AIPD est légalement requise. Classifiez chacune pour lever le signalement.",
            activities_unclassified_empty: 'Toutes les activités actives ont été classifiées.',
            aria_unmitigated_risks: 'Risques élevés et critiques non atténués',
            aria_transfers_without_safeguards: 'Transferts sans mesures de protection adéquates',
            aria_expired_assessments: 'Évaluations expirées nécessitant un renouvellement',
            title: "Rapport d'écarts des évaluations",
            no_dpia: 'Activités sans AIPD',
            no_assessment: 'Transferts sans évaluation',
            unmitigated: 'AIPD avec risques non atténués',
            activity_id: "ID de l'activité",
            dpia_id: "ID de l'AIPD",
            assessment_id: "ID de l'évaluation",
            expired_assessments: 'Évaluations expirées',
            no_gaps_activities: 'Aucun écart trouvé. Toutes les activités ont des AIPD.',
            no_unmitigated_risks: 'Aucun risque élevé/critique non atténué trouvé.',
            no_gaps_safeguards: 'Aucun écart trouvé. Tous les transferts ont des garanties adéquates.',
            no_expired: 'Aucune évaluation expirée trouvée.',
            no_report_data: "Aucune donnée de rapport d'écarts disponible.",
            fetch_error: "Échec de la récupération du rapport d'écarts. Veuillez réessayer.",
            no_expired_assessments: 'Aucune évaluation expirée.',
            no_gaps_dpia: "Aucun écart d'AIPD trouvé.",
            no_data: 'Aucune donnée disponible.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Détail du dossier déposé',
            page_title_list: 'Dossiers déposés',
            announce_filings_found: '{count} dépôt(s) trouvé(s)',
            announce_results: '{count} résultats',
            aria_filter_by_status: 'Filtrer par statut',
            title: 'Dossiers de dépôt de dossiers',
            nav_label: 'Dépôts de dossiers',
            create: 'Créer un dépôt de dossier',
            form_type: 'Type de formulaire',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Référence',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Date de soumission',
            outcome: 'Résultat',
            parent_filing: 'Parent',
            no_filings: 'Aucun dépôt de dossier trouvé.',
            fetch_error: 'Échec de la récupération des dossiers. Veuillez réessayer.',
            save_error: 'Échec de la création du dossier de dossier. Veuillez réessayer.',
            created: 'Dépôt de dossier créé.',
            loaded: 'Dépôt de dossier chargé.',
            not_found: 'Dossier de dépôt introuvable.',
            back_to_list: 'Retour à la liste',
            section_assignments: 'Affectations des sections',
            assign_sections: 'Attribuer des sections',
            section_id: 'Section',
            department: 'Département',
            assignee: 'Cessionnaire',
            deadline: 'Échéance',
            no_assignments: "Aucune affectation de section pour l'instant.",
            sections_assigned_msg: 'Sections attribuées.',
            assign_error: "Échec de l'attribution des sections. Veuillez réessayer.",
            lifecycle: 'Cycle de vie du dépôt',
            submission: 'Détails de la soumission',
            submission_channel: 'Canal de soumission',
            confirmation_number: 'Numéro de confirmation',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Soumettre pour révision',
            submitted_for_review: 'Soumis pour examen interne.',
            submit_review_error: 'Échec de la soumission pour examen. Veuillez réessayer.',
            legal_signoff: 'Approbation légale',
            legal_signoff_done: 'Approbation légale terminée.',
            legal_signoff_error: "Échec de l'approbation légale. Veuillez réessayer.",
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: "Échec de l'approbation du dépôt. Veuillez réessayer.",
            materialisation_status_column: 'Artefact de soumission',
            materialisation_na: 'Sans objet',
            materialisation_pending: 'En attente',
            materialisation_success: 'Matérialisé',
            materialisation_failed: 'Échec',
            materialisation_banner_title: "L'artefact de soumission n'a pas pu être créé",
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Réessayer',
            materialisation_retry_count: 'Nouvelles tentatives précédentes :',
            materialisation_retry_success: "L'artefact de soumission s'est matérialisé avec succès.",
            materialisation_retry_announce: 'Nouvelle tentative réussie',
            filter_materialisation_failed: 'Afficher uniquement les échecs de matérialisation',
            column_title: 'Titre',
            column_form_type: 'Formulaire',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Demande de supplément',
            supplement_requests: 'Demandes de supplément',
            supplement_response: 'Réponse complémentaire',
            record_supplement: 'Demande de supplément au dossier',
            supplement_recorded: 'Demande de supplément enregistrée.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: "Échec de l'enregistrement de la demande de supplément. Veuillez réessayer.",
            submit_supplement: 'Soumettre une réponse supplémentaire',
            supplement_submitted_msg: 'Réponse supplémentaire soumise.',
            supplement_submit_error: "Échec de l'envoi de la réponse supplémentaire. Veuillez réessayer.",
            response_status: 'Statut de la réponse',
            response_notes: 'Notes de réponse',
            resubmit: 'Soumettre à nouveau',
            resubmitted_msg: 'Dossier soumis à nouveau.',
            resubmit_error: 'Échec de la soumission à nouveau du dossier. Veuillez réessayer.',
            outcome_error: "Échec de l'enregistrement du résultat. Veuillez réessayer.",
            accepted: 'Accepté',
            rejected: 'Rejeté',
            close: 'Fermer',
            closed_msg: 'Dossier clos.',
            close_error: 'Échec de la clôture du dossier. Veuillez réessayer.',
            change_filing: 'Créer un dépôt de modification',
            change_filing_created: 'Modification du dossier créé.',
            change_filing_error: 'Échec de la création du dossier de modification. Veuillez réessayer.',
            change_reason: 'Raison du changement',
            filing_lineage: 'Lignée de dépôt',
            timers: 'Minuteries actives',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Date limite du supplément',
            timer_resubmission_review: 'Date limite de nouvelle soumission',
            timer_department_input: 'Date limite du département',
            days_remaining: 'jours restants',
            days_overdue: 'jours de retard',
            cover_letter: 'Lettre de motivation',
            checklist: 'Liste de contrôle',
            evidence_index: 'Index des preuves',
            status_draft: 'Brouillon',
            status_internal_review: 'Revue interne',
            status_legal_signoff: 'Approbation légale',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Complément demandé',
            status_supplement_submitted: 'Supplément soumis',
            status_resubmitted: 'Resoumis',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Clôturé',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Brouillon',
            step_internal_review: 'Revue interne',
            step_legal_signoff: 'Approbation légale',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Complément demandé',
            step_supplement_submitted: 'Supplément soumis',
            step_resubmitted: 'Resoumis',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Clôturé',
            linked_dpia: 'AIPD liée',
            linked_dpia_none: 'Aucune AIPD liée à ce dépôt.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: "Voir l'AIPD",
            linked_dpia_label: "Ce dépôt de dossier est lié à l'AIPD suivante :",
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Santé des artefacts de soumission',
            page_title: 'Santé des artefacts de soumission',
            page_description: 'Détecter et résoudre les problèmes de matérialisation des dossiers. Les listes sont limitées à votre organisation.',
            failed_title: 'Échec des matérialisations',
            failed_empty: 'Aucune matérialisation ratée – bien.',
            pending_title: 'Approuvé mais non matérialisé',
            pending_empty: "Tous les dépôts approuvés sont accompagnés d'un dossier.",
            orphan_title: 'Dossiers orphelins',
            orphan_description: "Des dossiers créés par matérialisation qui ne sont plus référencés par aucun dossier – généralement le perdant d'une course concurrente. Répertorié pour enquête ; le nettoyage est manuel.",
            orphan_empty: 'Aucun dossier orphelin détecté.',
            materialise_button: 'Matérialisez-vous maintenant',
            column_failure_reason: "Raison de l'échec",
            column_failed_at: 'Échec à',
            column_retries: 'Nouvelles tentatives',
            column_approved_at: 'Approuvé à',
            column_dossier_id: 'ID du dossier',
            column_source_filing_id: 'Dépôt source',
            column_dossier_type: 'Tapez',
            column_created_at: 'Créé à',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Accès refusé',
        access_denied: 'Vous ne disposez pas des autorisations requises pour accéder au module Classification. Contactez votre administrateur.',
        nav: {
            taxonomies: 'Classifications',
            labeling: 'Étiquetage',
            lifecycle: 'Cycle de vie',
            gaps: "Rapport d'écarts",
            exports: 'Exportations',
        },
        exports: {
            title: 'Exportations de classification',
            csv_section: 'Exportations CSV',
            pdf_section: 'Rapports PDF',
            inventory: 'Inventaire',
            classification_register: 'Registre de classification',
            raci: 'Matrice RACI',
            exceptions: 'Exceptions',
            evidence_pack: 'Dossier de preuves',
            delta_report: 'Rapport delta',
            executive_summary: 'Synthèse exécutive',
            exporting: 'Exportation en cours…',
            export_success: 'Exportation terminée avec succès.',
            export_error: "L'exportation a échoué. Veuillez réessayer.",
            csv_download: 'Téléchargement CSV prêt.',
            pdf_download: 'Génération du PDF démarrée.',
            no_data: "Aucune donnée disponible pour l'exportation.",
        },
        delta: {
            title: 'Détail des modifications',
            added: 'Ajoutés',
            removed: 'Supprimés',
            changed: 'Modifiés',
            added_count: 'Ajoutés ({count})',
            removed_count: 'Supprimés ({count})',
            changed_count: 'Modifiés ({count})',
            object_id: 'ID objet',
            object_type: "Type d'objet",
            taxonomy: 'Taxonomie',
            level: 'Niveau',
            previous: 'Précédent',
            current: 'Actuel',
            no_records: 'Aucun enregistrement dans cette section.',
            empty_section: 'Cette section est vide.',
        },
        schedule: {
            title: 'Planifier le cycle de découverte',
            enabled: 'Activé',
            interval_days: 'Intervalle (jours)',
            interval_label: 'Exécuter tous les N jours',
            manual_only: 'Manuel uniquement',
            every_n_days: 'Tous les {n} jours',
            save: 'Enregistrer le planning',
            cancel: 'Annuler',
            schedule_updated: 'Planning mis à jour avec succès.',
        },
        taxonomies: {
            title: 'Taxonomies de classification',
            new: 'Nouvelle taxonomie',
            edit: 'Modifier la taxonomie',
            name: 'Nom',
            levels: 'Niveaux de classification',
            seed_defaults: 'Initialiser les valeurs par défaut',
            scope: 'Périmètre',
            scope_organization: 'Organisation',
            scope_module_specific: 'Spécifique au module',
            deprecate: 'Déprécier',
            hide_levels: 'Masquer les niveaux',
            versions: 'Révisions',
            hide_versions: 'Masquer les versions',
            add_level: 'Ajouter un niveau',
            new_level: 'Nouveau niveau',
            edit_level: 'Modifier le niveau',
            code: 'Identifiant',
            label: 'Libellé',
            color: 'Couleur',
            ordinal: "Numéro d'ordre",
            handling_requirements: 'Exigences de traitement',
            version_history: 'Historique des versions',
            changes: 'Modifications',
            failed_fetch: 'Échec de la récupération des taxonomies. Veuillez réessayer.',
            failed_save: "Échec de l'enregistrement de la taxonomie. Veuillez réessayer.",
            failed_activate: "Échec de l'activation de la taxonomie. Veuillez réessayer.",
            failed_deprecate: 'Échec de la dépréciation de la taxonomie. Veuillez réessayer.',
            failed_seed: "Échec de l'initialisation des taxonomies par défaut. Veuillez réessayer.",
            failed_fetch_levels: 'Échec de la récupération des niveaux de taxonomie. Veuillez réessayer.',
            failed_save_level: "Échec de l'enregistrement du niveau de taxonomie. Veuillez réessayer.",
            failed_delete_level: 'Échec de la suppression du niveau de taxonomie. Veuillez réessayer.',
            failed_fetch_versions: 'Échec de la récupération des versions de taxonomie. Veuillez réessayer.',
            loaded_count: '{count} taxonomies chargées',
            defaults_seeded: 'Taxonomies par défaut initialisées.',
            no_levels: 'Aucun niveau de classification défini — ajoutez des niveaux pour définir votre hiérarchie de classification.',
            no_versions: "Pas encore d'historique de versions — les versions sont créées lorsqu'une taxonomie active est mise à jour.",
        },
        labeling: {
            title: 'Étiquetage des données',
            new: 'Nouvelle étiquette',
            edit: "Modifier l'étiquette",
            entity_type: "Type d'entité",
            entity_id: "Identifiant de l'entité",
            taxonomy: 'Taxonomie',
            classification_level: 'Niveau de classification',
            applied_by: 'Appliqué par',
            applied_at: 'Appliqué le',
            bulk_apply: 'Application en masse',
            bulk_remove: 'Suppression en masse',
            all_taxonomies: 'Toutes les taxonomies',
            system: 'Système',
            dataset: 'Jeu de données',
            level_code_placeholder: 'Code du niveau...',
            select_taxonomy: 'Sélectionner une taxonomie...',
            select_level: 'Sélectionner un niveau...',
            rationale: 'Justification',
            review_date: 'Date de révision',
            review: 'Réviser',
            selected: 'sélectionné(s)',
            select_all_labels: 'Sélectionner tous les libellés',
            loaded_count: '{count} libellés chargés',
            hide_stale_labels: 'Masquer les étiquettes obsolètes',
            show_conflicts: 'Afficher les conflits',
            hide_conflicts: 'Masquer les conflits',
            label_conflicts: "Conflits d'étiquettes",
            conflict_reason: 'Raison du conflit',
            labels: 'Étiquettes',
            failed_fetch: 'Échec de la récupération des étiquettes. Veuillez réessayer.',
            failed_fetch_taxonomies: 'Échec de la récupération des taxonomies. Veuillez réessayer.',
            failed_fetch_levels: 'Échec de la récupération des niveaux de taxonomie. Veuillez réessayer.',
            failed_save: "Échec de l'enregistrement de l'étiquette. Veuillez réessayer.",
            failed_review: "Échec de la révision de l'étiquette. Veuillez réessayer.",
            failed_bulk: "Échec de l'étiquetage en masse. Veuillez réessayer.",
            failed_stale: 'Échec de la récupération des étiquettes obsolètes. Veuillez réessayer.',
            failed_conflicts: "Échec de la récupération des conflits d'étiquettes. Veuillez réessayer.",
            review_permission_denied: "Vous n'avez pas la permission de réviser cette étiquette.",
            review_not_found: 'Étiquette introuvable — elle a peut-être été supprimée.',
            approve_selected: 'Approuver la sélection',
            reject_selected: 'Rejeter la sélection',
            approved: 'Approuvée',
            rejected: 'Rejetée',
            labels_count: 'étiquettes',
            failed_count: 'échouées',
            confirm_approve: "Confirmer l'approbation",
            confirm_reject: 'Confirmer le rejet',
            approve_n_labels: 'Approuver {count} étiquettes ?',
            reject_n_labels: 'Rejeter {count} étiquettes ?',
            submit_for_review: 'Soumettre pour révision',
            approve: 'Approuver',
            reject: 'Rejeter',
            parent_system: 'Systeme parent',
            parent_system_placeholder: 'Selectionner le systeme parent...',
            parent_system_help: 'Utilise pour la detection des conflits.',
            dataset_level: 'Niveau du jeu de donnees',
            parent_level: 'Niveau parent',
            bulk_created: '{created} etiquette(s) creee(s)',
            bulk_skipped: '{skipped} ignoree(s)',
            bulk_all_skipped: 'Toutes les {skipped} ignorees',
            ids_hint: 'Saisissez des identifiants separes par des virgules',
            delete_confirm: 'Supprimer etiquette pour "{id}" ?',
        },
        lifecycle: {
            title: 'Suivi du cycle de vie des données',
            new: 'Nouvelle entrée',
            current_stage: 'Étape actuelle',
            last_transition: 'Dernière transition',
            transition: "Changement d'état",
            view_history: "Voir l'historique",
            stages: {
                creation: 'Création',
                collection: 'Collecte',
                processing: 'Traitement',
                storage: 'Stockage',
                retention: 'Conservation',
                disposal: 'Élimination',
                archived: 'Archivé',
                active: 'Actif',
                pending_deletion: 'Suppression en attente',
                deleted: 'Supprimé',
                on_hold: 'En attente',
            },
            all_stages: 'Toutes les étapes',
            object_id: "ID de l'objet",
            object_type: "Type d'objet",
            policy_reference: 'Référence de politique',
            retention_period_days: 'Période de conservation (jours)',
            retention_basis: 'Base de conservation',
            deletion_method: 'Méthode de suppression',
            deletion_secure_erase: 'Effacement sécurisé',
            deletion_anonymization: 'Anonymisation',
            deletion_physical_destruction: 'Destruction physique',
            deletion_other: 'Autre',
            responsible_party: 'Partie responsable',
            responsible_user_id: "ID de l'utilisateur responsable",
            retention_expiry: 'Expiration de la conservation',
            hold_reason: 'Motif de la mise en attente',
            hold: 'Mettre en attente',
            release: 'Libérer',
            hide_events: 'Masquer les événements',
            events_timeline: 'Chronologie des événements',
            add_event: 'Ajouter un événement',
            event_type: "Type d'événement",
            create_event: 'Créer un événement',
            from_stage: "Étape d'origine",
            to_stage: 'Étape de destination',
            actor: 'Acteur',
            reason_placeholder: 'Motif...',
            go: 'Exécuter',
            show_expired_retentions: 'Afficher les conservations expirées',
            hide_expired_retentions: 'Masquer les conservations expirées',
            expired_retentions_alert: 'Alerte de conservations expirées',
            failed_fetch: 'Échec de la récupération des enregistrements du cycle de vie. Veuillez réessayer.',
            failed_save: "Échec de l'enregistrement du cycle de vie. Veuillez réessayer.",
            failed_transition: "Échec de la transition de l'étape du cycle de vie. Veuillez réessayer.",
            failed_hold: 'Échec de la mise en attente. Veuillez réessayer.',
            failed_release: 'Échec de la libération. Veuillez réessayer.',
            failed_fetch_events: 'Échec de la récupération des événements du cycle de vie. Veuillez réessayer.',
            failed_create_event: "Échec de la création de l'événement du cycle de vie. Veuillez réessayer.",
            failed_expired: 'Échec de la récupération des conservations expirées. Veuillez réessayer.',
            hold_reason_prompt: 'Saisissez le motif de la mise en attente :',
            loaded_count: '{count} enregistrements de cycle de vie chargés',
            stage_transition_for: "Transition d'étape pour {id}",
            event_created: 'Créé',
            event_updated: 'Mis à jour',
            event_approved: 'Approuvé',
            event_deprecated: 'Obsolète',
            event_proposed: 'Proposé',
            event_reviewed: 'Révisé',
            event_archived: 'Archivé',
            event_restored: 'Restauré',
        },
        gap_report: {
            title: "Rapport d'écarts de classification",
            unlabeled: 'Données non étiquetées',
            missing_lifecycle: 'Cycle de vie manquant',
            stale_labels: 'Étiquettes obsolètes',
            run_detection: "Lancer la détection d'écarts",
            detecting: 'Détection en cours...',
            detection_complete: "Détection d'écarts terminée. {count} écart(s) détecté(s).",
            detection_failed: "Échec de la détection d'écarts.",
            by_severity: 'Par gravité',
            by_gap_type: "Par type d'écart",
            all_gap_types: "Tous les types d'écarts",
            conflicting_labels: 'Étiquettes en conflit',
            expired_retention: 'Conservation expirée',
            missing_handling: 'Traitement manquant',
            info: 'Informations',
            gap_type: "Type d'écart",
            object_id: "ID de l'objet",
            object_type: "Type d'objet",
            detected_at: 'Détecté le',
            open: 'Ouvert',
            acknowledged: 'Accusé de réception',
            remediated: 'Remédié',
            accepted: 'Accepté',
            acknowledge: 'Accuser réception',
            remediate: 'Remédier',
            accept_risk: 'Accepter le risque',
            accept_risk_prompt: "Saisissez la justification de l'acceptation du risque :",
            failed_fetch: 'Échec de la récupération des écarts. Veuillez réessayer.',
            failed_acknowledge: "Échec de l'accusé de réception de l'écart. Veuillez réessayer.",
            failed_remediate: "Échec de la remédiation de l'écart. Veuillez réessayer.",
            failed_accept_risk: "Échec de l'acceptation du risque de l'écart. Veuillez réessayer.",
            failed_detect: "Échec de l'exécution de la détection d'écarts. Veuillez réessayer.",
            loaded_count: '{count} lacunes de classification chargées',
            related_object_id: "ID de l'objet associé",
            related_object_type: "Type de l'objet associé",
            gap_types: {
                unlabeled_asset: 'Actif sans étiquette',
                missing_lifecycle: 'Cycle de vie manquant',
                expired_retention: 'Rétention expirée',
                extended_pending_deletion: 'Suppression en attente étendue',
            },
        },
        label_workflow: {
            title: "Flux d'étiquettes",
            proposed: 'Proposé',
            under_review: 'En cours de révision',
            approved: 'Approuvé',
            deprecated: 'Obsolète',
            submit_review: 'Soumettre pour révision',
            approve: "Approuver l'étiquette",
            deprecate: 'Rendre obsolète',
            bulk_approve: 'Approbation en masse',
            bulk_approve_success: 'Étiquettes approuvées avec succès.',
            submit_success: 'Étiquette soumise pour révision.',
            approve_success: 'Étiquette approuvée.',
            deprecate_success: 'Étiquette rendue obsolète.',
        },
        review_tasks: {
            title: 'Tâches de révision',
            create: 'Nouvelle tâche de révision',
            system: 'Système',
            owner: 'Propriétaire',
            description: 'Descriptif',
            resolution: 'Résolution',
            resolve: 'Résoudre',
            approve: 'Approuver',
            modify: 'Modifier',
            reject: 'Rejeter',
            escalate: 'Escalader',
            escalated: 'Tâches escaladées',
            no_tasks: 'Aucune tâche de révision.',
            resolve_success: 'Tâche de révision résolue.',
            status_pending: 'En attente',
            status_resolved: 'Résolue',
            status_escalated: 'Escaladée',
            notes: 'Remarques',
            create_success: 'Tâche de révision créée.',
            no_tasks_detail: "Pas encore de tâches de révision — créez une tâche pour assigner des révisions d'étiquettes aux propriétaires de systèmes.",
            label_id: "ID d'étiquette",
            system_id: 'ID système',
            system_name: 'Nom du système',
            owner_id: 'ID du propriétaire',
            resolution_col: 'Résolution',
            notes_col: 'Remarques',
        },
        exceptions: {
            title: "File d'attente des exceptions",
            create: 'Nouvelle exception',
            severity: 'Sévérité',
            system: 'Système',
            owner: 'Propriétaire',
            resolve: 'Résoudre',
            no_exceptions: 'Aucune exception trouvée.',
            resolve_success: 'Exception résolue.',
            dashboard_open: 'Exceptions ouvertes',
            dashboard_resolved: 'Exceptions résolues',
            dashboard_total: 'Total',
            dashboard_by_severity: 'Par sévérité',
            status_open: 'Ouverte',
            status_resolved: 'Résolue',
            status_accepted_risk: 'Risque accepté',
            create_success: 'Exception créée.',
            no_exceptions_detail: "Pas encore d'exceptions — les exceptions sont créées à partir de lacunes de haute sévérité nécessitant un traitement spécial.",
            gap_id: 'ID de lacune',
            system_id: 'ID système',
            system_name: 'Nom du système',
            owner_id: 'ID du propriétaire',
            resolution_notes: 'Notes de résolution',
            resolution_notes_placeholder: 'Décrivez comment cette exception a été résolue...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Carte des données entreprise',
            inventory: 'Inventaire des données',
            total_systems: 'Total des systèmes',
            total_labels: 'Total des étiquettes',
            approved_count: 'Étiquettes approuvées',
            proposed_count: 'Étiquettes proposées',
            no_data: 'Aucune donnée de classification disponible.',
            no_approved_labels: "Aucun objet étiqueté pour le moment — un objet apparaît sur la carte des données dès qu'il porte une étiquette de classification, qu'elle soit proposée, en cours de revue ou approuvée.",
            loaded_count: "Carte de données d'entreprise chargée",
        },
        discovery: {
            title: 'Cycles de découverte',
            create: 'Nouveau cycle',
            cycle_name: 'Nom du cycle',
            run_delta: 'Lancer la détection delta',
            delta_success: 'Détection delta terminée.',
            create_success: 'Cycle de découverte créé.',
            no_cycles: 'Aucun cycle de découverte.',
            status_created: 'Créé',
            status_scanning: 'Numérisation',
            status_review: 'Examen',
            status_approved: 'Approuvé',
            status_published: 'Publié',
            status_draft: 'Brouillon',
            running: 'En cours',
            completed: 'Terminé',
            cancelled: 'Annulé',
            delta_result_summary: '{added} ajoutées, {removed} supprimées, {changed} étiquettes modifiées trouvées.',
            delta_results: 'Résultats delta',
            view_results: 'Voir les résultats',
            scope_type: 'Type de portée',
            scope_type_all: 'Tout',
            scope_type_systems: 'Systèmes',
            scope_type_datasets: 'Ensembles de données',
            scope_ids: 'ID de portée',
            scope_ids_placeholder: 'Saisissez les identifiants séparés par des virgules',
            cycle_explanation: 'Un cycle de découverte analyse votre infrastructure de données pour identifier, classer et suivre les actifs de données. Configurez la portée pour cibler des systèmes ou des ensembles de données spécifiques.',
            empty_state_title: "Aucun cycle de découverte pour l'instant",
            empty_state_description: 'Créez votre premier cycle de découverte pour commencer à analyser et classer vos actifs de données.',
            empty_state_cta: 'Créer le premier cycle',
            validation_name_required: 'Le nom du cycle est obligatoire',
            validation_scope_type_required: 'Le type de portée est requis',
            validation_scope_ids_required: 'Au moins un ID de portée est requis',
        },
        regulatory: {
            sensitivity_importance_info: 'La loi sur les données (loi 60/2024/QH15, req. 18e4bfd1) nécessite une classification par sensibilité et par importance en tant que dimensions distinctes. Créez une taxonomie pour chaque dimension pour satisfaire à cette exigence.',
            handling_rules_info: 'Chaque niveau de classification doit définir des règles de traitement par demande. b89981ac ("Définir les règles de traitement par niveau de classification").',
            template_internal_ids_info: "Tous les modèles de classification utilisent des identifiants internes (par exemple vn_data_law_data_classification_record). Aucun modèle n'est présenté comme un formulaire officiel du ministère.",
            ai_risk_tiers_info: 'La loi AI (loi 134/2025/QH15, req. 3a87ad63) définit quatre niveaux de risque système AI : minimal, limité, élevé et inacceptable. Créez une taxonomie de risque AI avec ces niveaux.',
            ai_risk_tier_minimal: 'Minime',
            ai_risk_tier_limited: 'Limite',
            ai_risk_tier_high: 'Eleve',
            ai_risk_tier_unacceptable: 'Inacceptable',
            ai_register_info: "Les enregistrements de classification AI doivent être liés à une entrée du registre système AI (requête 0f4475d3) capturant : le nom du système, le propriétaire, l'objectif, le contexte de déploiement, le niveau de risque, l'implication du fournisseur.",
            ai_template_internal_info: "Les modèles de classification AI utilisent des identifiants internes (par exemple, AI-RISK-01). Aucun identifiant d'espace réservé officiel AI (AI-HC-01 à AI-HC-08) n'est utilisé.",
            ai_audit_trail_info: "Les modifications de classification AI produisent une piste d'audit versionnée par UC-VN-06 : acteur, horodatage, valeur précédente, nouvelle valeur et justification.",
            lifecycle_data_d1_info: 'Les transitions du cycle de vie correspondent aux étapes de la procédure Data Law DATA-D1 : cataloguer, classer, définir des règles de traitement, capturer des preuves, planifier des révisions.',
            lifecycle_deletion_link_info: 'Un actif de données classifiées arrivant en fin de vie doit déclencher un enregistrement de planification de suppression via le module Deletion Orchestration.',
            lifecycle_evidence_info: "Chaque changement d'état du cycle de vie (ébauche, actif, en cours de révision, archivé, supprimé) capture l'acteur, l'horodatage et la justification sous forme d'entrée de piste d'audit.",
            escalation_timers_info: "UC-VN-10 délais de remontée : 5 jours ouvrés pour l'examen par le propriétaire, 48 heures pour la remontée d'une exception, 72 heures pour les éléments à haut risque/sensibles.",
            escalation_owner_review: '5 jours ouvres',
            escalation_exception: '48 heures',
            escalation_high_risk: '72 heures',
            gap_risk_areas_info: "Loi sur les données requise. f6c3060a (« Évaluation des risques liés aux données ») nécessite une analyse des écarts dans 4 domaines de risque : confidentialité, cybersécurité, contrôle d'accès et risque opérationnel.",
            gap_risk_area_privacy: 'Vie privee',
            gap_risk_area_cybersecurity: 'Cybersecurite',
            gap_risk_area_access: "Controle d'acces",
            gap_risk_area_operational: 'Operationnel',
            gap_framework_filter_info: "Filtrez les lacunes par cadre réglementaire (VN_DATA_LAW ou VN_AI_LAW_2026) pour afficher les lacunes pertinentes pour chaque champ d'application réglementaire.",
            framework_vn_data_law: 'Loi sur les donnees VN',
            framework_vn_ai_law: "Loi sur l'IA VN 2026",
            enterprise_map_approved_info: "Cette carte de données affiche uniquement les enregistrements de classification approuvés selon l'étape 10 du flux de travail UC-VN-10 (« Les étiquettes approuvées deviennent la vérité actuelle de l'entreprise »). Les brouillons et les dossiers proposés sont exclus.",
            exports_bundle_info: "Les ensembles d'exportation comprennent : le registre de classification, les règles de traitement, la propriété/RACI et le pack de preuves – correspondant aux exigences d'exportation de UC-VN-10 et au format de feuille de preuves DATA-EVD-01.",
        },
    },
    monitoring: {
        no_permission_title: 'Autorisations insuffisantes',
        no_permission_message: 'Vous navez pas la permission dacceder a ce module de surveillance.',
        errors: {
            fetch: 'Échec du chargement.',
            save: "Échec de l'enregistrement.",
            fetch_programs_failed: 'Échec de la récupération des programmes.',
            fetch_kpi_failed: 'Échec de la récupération des données KPI.',
            create_snapshot_failed: "Échec de la création de l'instantané.",
            fetch_periods_failed: 'Échec de la récupération des périodes.',
            create_period_failed: 'Échec de la création de la période.',
            auto_generate_failed: 'Échec de la génération automatique des périodes.',
            start_period_failed: 'Échec du démarrage de la période.',
            close_period_failed: 'Échec de la clôture de la période.',
            save_program_failed: "Échec de l'enregistrement du programme.",
            activate_program_failed: "Échec de l'activation du programme.",
            pause_program_failed: 'Échec de la suspension du programme.',
            complete_program_failed: 'Échec de la finalisation du programme.',
            archive_program_failed: "Échec de l'archivage du programme.",
            fetch_ric_templates_failed: 'Échec de la récupération des modèles RIC.',
            fetch_ric_cases_failed: 'Échec de la récupération des dossiers RIC.',
            create_ric_case_failed: 'Échec de la création du dossier RIC.',
            transition_ric_failed: 'Échec de la transition du dossier RIC.',
            add_follow_up_failed: "Échec de l'ajout du suivi.",
            respond_follow_up_failed: 'Échec de la réponse au suivi.',
            link_evidence_failed: 'Échec de la liaison de la preuve.',
            fetch_task_templates_failed: 'Échec de la récupération des modèles de tâches.',
            save_task_template_failed: "Échec de l'enregistrement du modèle de tâche.",
            delete_task_template_failed: 'Échec de la suppression du modèle de tâche.',
            delete_framework_link_failed: 'Échec de la suppression du lien vers le cadre réglementaire.',
            seed_defaults_failed: "Échec de l'initialisation des valeurs par défaut.",
            fetch_tasks_failed: 'Échec de la récupération des tâches.',
            create_task_failed: 'Échec de la création de la tâche.',
            instantiate_tasks_failed: "Échec de l'instanciation des tâches.",
            start_task_failed: 'Échec du démarrage de la tâche.',
            complete_task_failed: 'Échec de la finalisation de la tâche.',
            defer_task_failed: 'Échec du report de la tâche.',
            block_task_failed: 'Échec du blocage de la tâche.',
            override_complete_failed: 'Échec du forçage de la clôture.',
            assign_task_failed: "Échec de l'attribution de la tâche.",
            update_checklist_failed: 'Échec de la mise à jour de la liste de contrôle.',
        },
        nav: {
            programs: 'Programmes',
            periods: 'Périodes',
            task_templates: 'Modèles de tâches',
            tasks: 'Tâches',
            kpi: 'Tableau de bord KPI',
            ric: 'Demandes des autorités',
            framework_link: 'Surveillance du cadre',
            authority_readiness: 'Préparation autorités',
            change_review: 'Revue des modifications',
            raci: 'Matrice RACI',
        },
        programs: {
            page_title: 'Programmes de surveillance',
            title: 'Programmes de surveillance',
            new: 'Nouveau programme',
            edit: 'Modifier le programme',
            name: 'Nom',
            scope: 'Périmètre',
            frequency: 'Fréquence',
            owner: 'Responsable',
            activate: 'Activer',
            pause: 'Suspendre',
            resume: 'Reprendre',
            complete: 'Terminer',
            archive: 'Archiver',
            custom_frequency_days: 'Fréquence personnalisée (jours)',
            select_user: 'Sélectionner un utilisateur…',
            assign_training: 'Attribuer une formation',
            linked_forms: 'Formulaires liés',
            linked_forms_none: 'Aucun formulaire lié pour le moment',
            loaded_count: '{0} programmes chargés',
            scope_labels: {
                full: 'Complet',
                partial: 'Partiel',
                targeted: 'Ciblé',
                consent: 'Consentement',
                dpia: 'DPIA',
                vendors: 'Fournisseurs',
                classification: 'Classement',
                rights_requests: 'Demandes de droits',
                data_mapping: 'Cartographie des données',
                transfers: 'Transferts',
                security_monitoring: 'Surveillance de la sécurité',
                authority_response: "Réponse de l'autorité",
                evidence_preservation: 'Préservation des preuves',
                localization_review: 'Examen de la localisation',
                ai_system_registration: 'AI Enregistrement du système',
                ai_pre_deployment: 'AI Évaluation préalable au déploiement',
                ai_transparency: 'AI Contrôle de transparence',
                ai_incident_review: 'AI Examen des incidents',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Périodes de surveillance',
            title: 'Périodes de revue',
            new: 'Nouvelle période',
            label: 'Libellé de la période',
            start_date: 'Date de début',
            end_date: 'Date de fin',
            reviewers: 'Réviseurs',
            auto_generate: 'Générer automatiquement les périodes',
            start: 'Démarrer',
            close: 'Clôturer',
            change_digest: 'Synthèse des modifications',
            no_digest: 'Aucun résumé disponible.',
            reviewers_count: 'Nombre de réviseurs',
            select_program: 'Sélectionnez programme',
            select_program_prompt: 'Sélectionnez program to view periods.',
            hide_digest: 'Masquer Résumer',
            loaded_count: '{0} périodes chargées',
            periods_auto_generated: 'Périodes générées automatiquement',
            label_placeholder: 'ex. 2026-Q1',
        },
        task_templates: {
            page_title: 'Modèles de tâches',
            title: 'Modèles de tâches',
            new: 'Nouveau modèle',
            edit: 'Modifier le modèle',
            category: 'Catégorie',
            priority: 'Priorité',
            checklist_items: 'Éléments de la liste de contrôle',
            effort: 'Effort estimé (heures)',
            pre_seeded: 'Pré-initialisé',
            seed_defaults: 'Initialiser les valeurs par défaut',
            add_checklist: 'Ajouter un élément à la liste de contrôle',
            guidance: "Notes d'orientation",
            all_categories: 'Toutes les catégories',
            loaded_count: 'Modèles de tâches {0} chargés',
            defaults_seeded: 'Modèles de tâches par défaut prédéfinis',
            checklist_item_label: 'Élément de la liste de contrôle {0}',
            remove_checklist_item: "Supprimer l'élément de la liste de contrôle {0}",
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Tâches de revue',
            title: 'Tâches de revue',
            new: 'Nouvelle tâche',
            assigned_to: 'Attribué à',
            due_date: "Date d'échéance",
            checklist: 'Liste de contrôle',
            start: 'Démarrer',
            complete: 'Terminer',
            defer: 'Reporter',
            block: 'Bloquer',
            override_complete: 'Forcer la clôture',
            link_evidence: 'Lier une preuve',
            direct_upload_label: 'Téléverser un fichier de preuve',
            instantiate: 'Instancier à partir des modèles',
            defer_reason: 'Motif du report',
            override_justification: 'Justification du forçage',
            filter_assignee: 'Filtrer par assigné',
            select_period: 'Sélectionnez période',
            select_program: 'Sélectionnez programme',
            select_program_period_prompt: 'Sélectionnez program and period to view tasks.',
            assign_placeholder: 'Assigner à...',
            loaded_count: '{0} tâches chargées',
            tasks_instantiated: 'Tâches créées à partir des modèles',
            overdue: 'En retard',
            checklist_for: 'liste de contrôle pour',
            manage_checklist: 'Gérer la liste de contrôle',
            add_checklist_item: 'Ajouter un élément',
            checklist_item_label: 'Élément',
            checklist_item_description: 'Description',
            remove_checklist_item: 'Supprimer l’élément',
            save_checklist: 'Enregistrer la liste de contrôle',
            no_checklist_items: 'Aucun élément pour le moment.',
            confirm_delete_attachment: 'Voulez-vous vraiment supprimer ce fichier de preuve ? Cette action est irréversible.',
        },
        kpi: {
            page_title: 'Tableau de bord KPI',
            title: 'Tableau de bord KPI',
            kpi_name: 'Nom du KPI',
            current_value: 'Valeur actuelle',
            target: 'Objectif',
            trends: 'Tendances',
            capture_snapshot: 'Capturer un instantané',
            select_program: 'Sélectionnez programme',
            select_program_prompt: 'Sélectionnez program to view KPIs.',
            snapshot: 'Instantané',
            kpi_labels: {
                on_track: 'Sur Suivre',
                at_risk: 'À Risque',
                behind: 'En retard',
                rights_request_sla_rate: 'Demande de droits SLA Taux',
                vendor_review_currency: "Devise d'évaluation du fournisseur",
                dpia_completion_rate: "DPIA Taux d'achèvement",
                classification_coverage: 'Couverture de la classification',
                consent_policy_currency: 'Devise de la politique de consentement',
                security_event_detection_rate: 'Taux de détection des événements de sécurité',
                evidence_preservation_compliance_rate: 'Taux de conformité en matière de préservation des preuves',
                authority_response_timeliness: 'Rapidité de réponse des autorités',
                localization_readiness_score: 'Score de préparation à la localisation',
                ai_register_completeness: 'AI Complétude du registre système',
                pre_deployment_gate_pass_rate: 'Taux de réussite au portail de pré-déploiement',
                transparency_disclosure_coverage: 'Couverture de la divulgation de transparence',
                ai_incident_readiness_score: 'AI Score de préparation aux incidents',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'KPI de la loi sur la cybersécurité : taux de détection des événements de sécurité, taux de conformité à la préservation des preuves, rapidité de réponse des autorités, score de préparation à la localisation.',
            ai_law_kpi_info: 'KPI de la loi AI : exhaustivité du registre des systèmes AI, taux de réussite de la porte de pré-déploiement, couverture de la divulgation de transparence, score de préparation aux incidents AI.',
            no_data: 'Aucune donnée disponible.',
            snapshot_date: "Date d'instantané",
            loaded_count: '{0} instantanés KPI chargés',
            snapshot_captured: 'Instantané KPI capturé',
            showing_chart: 'Affichage en graphique',
            showing_table: 'Affichage en tableau',
            aria_current_values: 'Valeurs actuelles KPI',
            kpi_label: 'KPI',
            actual: 'Réel',
            trend: 'Tendance',
            notes: 'Remarques',
            trend_up: 'En hausse',
            trend_down: 'En baisse',
            trend_stable: 'Stable',
        },
        ric: {
            page_title: 'Dossiers RIC',
            title: 'Dossiers de demandes des autorités de contrôle',
            new: 'Nouveau dossier',
            reference: 'Référence',
            regulator: 'Autorité de contrôle',
            inquiry_type: 'Type de demande',
            deadline: 'Date limite de réponse',
            lead: 'Responsable',
            follow_ups: 'Suivis',
            add_follow_up: 'Ajouter un suivi',
            respond: 'Répondre',
            question: 'Interrogation',
            response: 'Réponse',
            asked_at: 'Demandé le',
            no_follow_ups: 'Non follow-ups encore.',
            received_date: 'Reçu Date',
            responded_at: 'Répondu le',
            hide_follow_ups: 'Masquer Follow-ups',
            inquiry_types: {
                general: 'Général',
                investigation: 'Enquête',
                audit: 'Vérification',
                complaint: 'Plainte',
                general_audit: 'Audit général',
                rights_requests_review: 'Revue des demandes de droits',
                cross_border_transfer_review: 'Revue des transferts transfrontaliers',
                vendor_governance_review: 'Revue de la gouvernance fournisseurs',
                consent_management_review: 'Revue de la gestion du consentement',
                data_mapping_ropa_review: 'Revue de la cartographie des données / ROPA',
                incident_readiness_review: 'Revue de la préparation aux incidents',
                security_access_control_review: "Revue de la sécurité et du contrôle d'accès",
                targeted_processing_review: 'Revue du traitement ciblé',
                bank_readiness_pack: 'Dossier de préparation bancaire',
            },
            link_evidence: 'Lier Preuve',
            loaded_count: 'Cas RIC {0} chargés',
            to_follow_up: 'à la question complémentaire',
            overdue: 'En retard',
            overdue_indicator: 'Délai de réponse dépassé',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Reçu',
                in_triage: 'En triage',
                evidence_gathering: 'Collecte de preuves',
                response_drafting: 'Rédaction de la réponse',
                internal_review: 'Revue interne',
                submitted: 'Soumis',
                follow_up: 'Suivi',
                closed: 'Clôturé',
            },
        },
        framework_link: {
            title: 'Surveillance liée au cadre',
            link_program: 'Lier le programme au cadre',
            framework_code: 'Code du cadre',
            select_framework: 'Sélectionner un cadre...',
            select_program: 'Sélectionner un programme...',
            readiness_score: 'Score de préparation',
            no_links: 'Aucun programme lié au cadre trouvé',
            seed_defaults: 'Créer les modèles par défaut',
            program_id: 'ID du programme',
            program: 'Programme',
        },
        authority_readiness: {
            title: 'Préparation aux demandes des autorités',
            create_entry: 'Nouvelle entrée de préparation',
            select_framework: 'Sélectionner un cadre...',
            framework_code: 'Cadre',
            readiness_status: 'Statut de préparation',
            readiness_package: 'Dossier de préparation',
            run_drill: 'Lancer un exercice de préparation',
            drill_success: 'Exercice de préparation enregistré',
            last_drill: 'Dernier exercice',
            never_drilled: 'Jamais',
            no_readiness: 'Aucune donnée de préparation des autorités trouvée',
            entry_created: 'Entrée de préparation créée',
            ready: 'Prêt',
            partial: 'Partiel',
            not_ready: 'Non prêt',
            items: 'items',
        },
        change_review: {
            title: 'Revue des modifications réglementaires',
            new_review: 'Nouvelle revue de modification',
            change_id: 'ID de modification',
            select_change: 'Sélectionner une modification...',
            select_framework: 'Sélectionner un cadre...',
            impact_assessment: "Évaluation d'impact",
            decision: 'Décision',
            no_action: 'Aucune action',
            internal_update: 'Mise à jour interne',
            authority_filing: 'Dépôt auprès des autorités',
            no_reviews: 'Aucune revue de modification trouvée',
            auto_cycle: 'Cycle de revue automatique',
            cadence: 'Fréquence de revue',
            framework: 'Cadre',
        },
        export_csv: 'Exporter CSV',
        exporting: 'Exporter…',
        export_success: 'CSV exporté avec succès.',
        export_failed: "L'exportation de CSV a échoué.",
        export_attestation_pack: "Pack d'attestations d'exportation",
        staleness: {
            badge_label: '{0} éléments obsolètes',
            days_since: '{0} jours depuis la dernière mise à jour',
            detail_title: 'Articles périmés',
            accept_justification: 'Accepter avec justification',
            justification_label: 'Justification',
            no_stale: 'Aucun élément périmé trouvé.',
            threshold_label: "Seuil d'obsolescence (jours)",
            threshold_min: 'Minimum 30 jours',
            threshold_max: 'Maximum 730 jours',
            threshold_saved: "Seuil d'obsolescence mis à jour.",
        },
        csv_columns: {
            name: 'Nom',
            status: 'Statut',
            owner: 'Propriétaire',
            created_at: 'Créé à',
            updated_at: 'Mis à jour à',
            scope: 'Portée',
            frameworks: 'Cadres',
            period_label: 'Étiquette de période',
            start_date: 'Date de début',
            end_date: 'Date de fin',
            task_count: 'Nombre de tâches',
            completion_rate: "Taux d'achèvement",
            title: 'Titre',
            assigned_to: 'Attribué à',
            due_date: "Date d'échéance",
            priority: 'Priorité',
            completed_at: 'Terminé à',
            kpi_code: 'KPI Code',
            kpi_label: 'KPI Libellé',
            target: 'Cible',
            actual: 'Réel',
            trend: 'Tendance',
            reference: 'Référence',
            regulator: 'Régulateur',
            inquiry_type: 'Type de demande',
            lead: 'Diriger',
            received_date: 'Date de réception',
            deadline: 'Date limite',
            framework_code: 'Code-cadre',
            readiness_status: 'État de préparation',
            package_count: 'Nombre de colis',
            change_id: "Changer l'identifiant",
            impact_assessment: "Évaluation d'impact",
            decision: 'Décision',
            program: 'Programme',
            readiness_score: 'Score de préparation',
            category: 'Catégorie',
            checklist_count: 'Nombre de listes de contrôle',
            effort_hours: 'Effort (heures)',
            version: 'Version',
            department: 'Département',
            function_area: 'Zone de fonction',
            role: 'Rôle',
            assignee: 'Cessionnaire',
            frequency: 'Fréquence',
        },
        raci: {
            page_title: 'Matrice RACI de gouvernance',
            title: 'Matrice RACI de gouvernance',
            department: 'Département',
            function_area: 'Zone de fonction',
            responsible: 'Responsable',
            accountable: 'Responsable',
            consulted: 'Consulté',
            informed: 'Informé',
            assign_role: 'Attribuer un rôle RACI',
            select_program: 'Sélectionnez le programme',
            select_program_prompt: 'Sélectionnez un programme pour afficher la matrice RACI.',
            no_assignments: 'Aucune affectation RACI trouvée.',
            role_updated: 'Affectation RACI mise à jour.',
            role_labels: {
                R: 'Responsable',
                A: 'Responsable',
                C: 'Consulté',
                I: 'Informé',
            },
            loaded_count: '{0} affectations RACI chargées',
            single_accountability_warning: 'Un seul rôle de responsable (A) est autorisé par domaine de fonction. L’attribution d’un nouveau rôle de responsable remplacera celui existant.',
            cybersec_function_areas: 'Domaines de fonction du droit de la cybersécurité : réponse aux incidents, préservation des preuves, coopération entre autorités, examen de la localisation.',
            ai_law_function_areas: 'AI Domaines de fonctions juridiques : enregistrement du système, évaluation avant déploiement, transparence, gestion des incidents.',
            suggested_function_areas: 'Domaines de fonction suggérés',
        },
        prior_cycle: {
            link_label: 'Lien vers le cycle précédent',
            select_prior: 'Sélectionnez le cycle précédent…',
            linked: 'Année après année',
            no_prior: 'Aucun cycle antérieur disponible.',
            linked_success: 'Cycle antérieur lié.',
        },
        digest_table: {
            module: 'Module',
            entity_type: "Type d'entité",
            change_type: 'Changer le type',
            description: 'Descriptif',
            changed_at: 'Changé à',
        },
        view_related_ric: 'Voir les cas RIC connexes',
        linked_program: 'Programme lié',
        nav_raci: 'Matrice RACI',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Projet de réponse',
            drafting: 'Rédaction…',
            results_heading: 'Réponses rédigées ({count})',
            tokens_label: '≈ {count} jetons',
            counsel_label: 'Examen par un avocat requis :',
            counsel_default: "Au moins une réponse nécessite un examen par un avocat avant l'envoi.",
            requires_counsel_label: 'Nécessite un examen par un avocat.',
            accept_label: 'Accepter le brouillon',
            reject_label: 'Rejeter le brouillon',
            error_generic: 'Impossible de rédiger la réponse. Réessayez ou affinez le texte de la demande.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Chronologie des notifications de violation de calcul',
            computing: 'Informatique…',
            results_heading: 'Délais de notification des violations ({count})',
            tokens_label: '≈ {count} jetons',
            handoff_label: 'Forseti suggère :',
            empty_label: 'Aucun cadre installé ne déclare une obligation de notification pour cet incident.',
            mandatory_label: 'Champs obligatoires :',
            draft_label: 'Projet de notification',
            remaining_label: '{count}h restantes',
            overdue_label: 'EN RETARD par {count}h',
            acknowledge_label: 'Accuser réception de la chronologie',
            acknowledging: 'Reconnaissant…',
            error_generic: 'Impossible de calculer la chronologie de la violation. Essayer à nouveau.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmé Violation',
            no: 'Non un Violation',
            uncertain: 'En attente',
        },
        errors: {
            fetch: 'Échec de charge incidents. Veuillez réessayer.',
            save: 'Échec de save incident. Veuillez réessayer.',
            fetch_failed: 'Échec de fetch incidents. Veuillez réessayer.',
            save_failed: 'Échec de save incident. Veuillez réessayer.',
            transition_failed: 'Échec de transition incident. Veuillez réessayer.',
            close_failed: 'Échec de fermer incident. Veuillez réessayer.',
            fetch_timeline_failed: 'Échec de fetch timeline. Veuillez réessayer.',
            fetch_decisions_failed: 'Échec de fetch decisions. Veuillez réessayer.',
            triage_failed: 'Échec de update triage. Veuillez réessayer.',
            impact_update_failed: 'Échec de mise à jour impact évaluation. Veuillez réessayer.',
            breach_update_failed: 'Échec de mise à jour violation évaluation. Veuillez réessayer.',
            create_timeline_failed: 'Échec de create timeline entry. Veuillez réessayer.',
            create_decision_failed: 'Échec de create decision. Veuillez réessayer.',
            notification_update_failed: 'Échec de mise à jour notification. Veuillez réessayer.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Échec de fetch tasks. Veuillez réessayer.',
            create_task_failed: 'Échec de create task. Veuillez réessayer.',
            update_task_status_failed: 'Échec de la mise à jour du statut de la tâche. Veuillez réessayer.',
            update_checklist_failed: 'Échec de update checklist. Veuillez réessayer.',
            assign_task_failed: 'Échec de assign task. Veuillez réessayer.',
            escalate_task_failed: 'Échec de escalate task. Veuillez réessayer.',
            fetch_templates_failed: 'Échec de fetch templates. Veuillez réessayer.',
            permission_denied: 'Vous ne disposez pas des autorisations necessaires pour acceder a cette section. Contactez votre administrateur.',
        },
        harm_likelihood: {
            unlikely: 'Improbable',
            possible: 'Envisageable',
            likely: 'Probable',
            very_likely: 'Très probable',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-mail',
            phone: 'Téléphone',
            internal_system: 'Interne Système',
            third_party: 'Tiers',
            automated_detection: 'Détection automatisée',
        },
        task_transition_labels: {
            start: 'Début',
            complete: 'Complet',
            block: 'Bloquer',
            defer: 'Reporter',
            cancel: 'Annuler',
            unblock: 'Débloquer',
        },
        transition_labels: {
            start: 'Début',
            complete: 'Complet',
            block: 'Bloquer',
            defer: 'Reporter',
        },
        nav: {
            all: 'Tous les incidents',
            tasks: 'Tâches de réponse',
            reports: 'Rapports',
            breach_notifications: 'Notifications de violation',
            supplements: 'Suppléments',
            remediation: 'Remédiation',
            authority_requests: 'Demandes des autorités',
            disclosure_register: 'Registre de divulgation',
        },
        list: {
            page_title: 'Incidents',
            title: 'Incidents',
            new: 'Nouvel incident',
            edit: "Modifier l'incident",
            incident_title: 'Titre',
            severity: 'Gravité',
            reported_at: 'Signalé le',
            reported_by: 'Signalé par',
            assigned_to: 'Attribué à',
            view_detail: 'Voir le détail',
            reference: 'Référence',
            lead: 'Responsable',
            reported_via: 'Signalé via',
            discovery_date: 'Date de découverte',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Masquer Détails',
            close_incident: 'Fermer Incident',
            close_justification_prompt: 'Veuillez fournir une justification pour clôturer cet incident.',
            justification: 'Motif',
            lead_id: 'ID du responsable',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Personne-ressource',
            category: 'Catégorie',
            not_assessed: 'Non évalué',
        },
        detail: {
            page_title: "Détails de l'incident",
            title: "Détail de l'incident",
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: "Vue d'ensemble",
                triage: 'Évaluation initiale',
                impact: "Évaluation de l'impact",
                breach: 'Évaluation de la violation',
                timeline: 'Chronologie',
                decisions: 'Décisions',
                evidence: 'Preuves',
                audit_trail: "Piste d'audit",
            },
            evidence_upload_label: 'Joindre un fichier de preuve',
            evidence_uploaded: 'Preuve téléversée',
            evidence_deleted: 'Preuve supprimée',
            evidence_delete_error: 'Échec de la suppression de la preuve',
            evidence_uploaded_at: 'Téléversé',
            evidence_empty: 'Aucun fichier de preuve joint à cet incident.',
            evidence_size: 'Taille',
            triage_notes: 'Notes de triage',
            initial_classification: 'Classification initiale',
            individuals_affected: 'Nombre de personnes concernées',
            data_categories: 'Catégories de données impliquées',
            systems_compromised: 'Systèmes compromis',
            geographic_scope: 'Portée géographique',
            breach_determination: 'Détermination de la violation',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Notification requise',
            notification_deadline: 'Date limite de notification',
            pdpc_reportable: 'Signalement PDPC requis',
            add_timeline: 'Ajouter une entrée chronologique',
            add_decision: 'Ajouter une décision',
            not_found: 'Incident non found.',
            team_members: "Membres de l'équipe",
            checklist: 'Liste de contrôle',
            completed: 'Terminé',
            impact_severity_score: 'Calculated Impact Gravité Score',
            records_affected: 'Enregistrements affectés',
            potential_consequences: 'Conséquences potentielles',
            harm_likelihood: 'Probabilité de préjudice',
            legal_basis: 'Base juridique',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Juridictions',
            jurisdictions_comma: 'Juridictions (séparées par des virgules)',
            jurisdiction: 'Juridiction',
            authority_status: "Statut de l'autorité",
            subject_status: 'Sujet Statut',
            authority_deadline: "Délai de l'autorité",
            subject_deadline: 'Sujet Échéance',
            mark_authority_done: "Marquer l'autorité comme terminée",
            mark_subject_done: 'Marquer Sujet Done',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Non évalué',
            event_type: 'Événement Type',
            timestamp: 'Horodatage',
            actor: 'Acteur',
            no_timeline_events: 'Aucun événement de chronologie.',
            decision_type: 'Décision Type',
            decision: 'Décision',
            rationale: 'Justification',
            decision_made: 'Décision prise',
            no_decisions: 'Non décisions recorded encore.',
            no_audit_events: "Aucun événement d'audit enregistré pour l'instant.",
            export_pdf: 'Exporter PDF',
            pdf_title: "Rapport de Synthèse d'Incident",
            description: 'Descriptif',
            linked_records: 'Enregistrements Liés',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Tâches de réponse aux incidents',
            title: 'Tâches de réponse aux incidents',
            new: 'Nouvelle tâche',
            edit: 'Modifier la tâche',
            task_type: 'Type de tâche',
            assignee: 'Assigné à',
            due_date: "Date d'échéance",
            dependencies: 'Dépendances',
            mark_complete: 'Marquer comme terminé',
            templates: 'Modèles de tâches',
            select_incident: 'Sélectionner un incident',
            all_statuses: 'Tous les statuts',
            all_categories: 'Toutes les catégories',
            new_response_task: 'Nouvelle tâche de réponse',
            category: 'Catégorie',
            priority: 'Priorité',
            depends_on: 'Dépend de (identifiants de tâches séparés par des virgules)',
            checklist_items: 'Éléments de la liste de contrôle',
            remove: 'Supprimer',
            add: 'Ajouter',
            new_checklist_item: 'Nouvel élément de la liste de contrôle',
            no_templates: 'Aucun modèle disponible.',
            assign_task: 'Attribuer la tâche',
            assign_to: 'Attribuer à',
            assign: 'Attribuer',
            select_incident_prompt: 'Veuillez sélectionner un incident pour voir ses tâches de réponse.',
            checklist: 'Liste de contrôle',
            hide_checklist: 'Masquer la liste de contrôle',
            escalate: 'Escalader',
            escalations: 'Escalades',
            no_checklist_items: 'Aucun élément dans la liste de contrôle.',
        },
        reports: {
            page_title: "Rapports d'incidents",
            title: "Rapports d'incidents",
            summary: 'Statistiques récapitulatives',
            by_severity: 'Par gravité',
            by_status: 'Par statut',
            recent: 'Incidents récents',
            total_incidents: 'Total des incidents',
            open: 'Ouverts',
            closed: 'Clôturés',
            export_csv: 'Exporter CSV',
            export_pdf: 'Exporter PDF',
            export_success: 'Rapport exporté avec succès.',
            export_error: "Échec de l'exportation du rapport.",
            reference: 'Référence',
            view_report: 'Voir le rapport',
        },
        statuses: {
            reported: 'Signalé',
            triage: 'Évaluation initiale',
            contained: 'Contenu',
            investigation: 'Enquête',
            remediation: 'Remédiation',
            closed: 'Clôturé',
            false_positive: 'Faux positif',
        },
        task_statuses: {
            pending: 'En attente',
            in_progress: 'En cours',
            completed: 'Terminé',
            blocked: 'Bloqué',
            cancelled: 'Annulé',
        },
        severity: {
            low: 'Faible',
            medium: 'Moyen',
            high: 'Élevé',
            critical: 'Critique',
        },
        task_types: {
            containment: 'Confinement',
            investigation: 'Enquête',
            remediation: 'Remédiation',
            communication: 'Échange',
            documentation: 'Pièces justificatives',
            notification: 'Avertissement',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Données Violation',
            unauthorized_access: 'Accès non autorisé',
            data_loss: 'Perte de données',
            system_compromise: 'Compromission du système',
            malware: 'Logiciel malveillant',
            phishing: 'Hameçonnage',
            insider_threat: 'Menace interne',
            other: 'Autre',
        },
        reported_via_options: {
            email: 'E-mail',
            phone: 'Téléphone',
            internal_system: 'Interne Système',
            third_party: 'Tiers',
            automated_detection: 'Détection automatisée',
        },
        harm_likelihood_options: {
            unlikely: 'Improbable',
            possible: 'Envisageable',
            likely: 'Probable',
            very_likely: 'Très probable',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmé Violation',
            suspected_breach: 'Violation suspectée',
            not_a_breach: 'Non un Violation',
            pending: 'En attente',
        },
        timeline_event_types: {
            detection: 'Détection',
            escalation: 'Escalade',
            containment: 'Confinement',
            investigation_start: 'Enquête Début',
            evidence_collected: 'Preuves collectées',
            notification_sent: 'Notification Envoyé',
            remediation_action: 'Remédiation Action',
            status_change: 'Statut Changement',
            communication: 'Échange',
            other: 'Autre',
        },
        decision_types: {
            containment: 'Confinement',
            notification: 'Avertissement',
            remediation: 'Remédiation',
            escalation: 'Escalade',
            closure: 'Clôture',
            other: 'Autre',
        },
        task_transitions: {
            start: 'Début',
            complete: 'Complet',
            block: 'Bloquer',
            cancel: 'Annuler',
            unblock: 'Débloquer',
        },
        announce: {
            loaded: '{count} incidents chargés',
            reports_loaded: "{count} rapports d'incident chargés",
            tasks_loaded: '{count} tâches chargées',
            created: 'Incident créé avec succès',
            updated: 'Incident mis à jour avec succès',
            closed: 'Incident clôturé avec succès',
            status_changed: "Statut de l'incident modifié",
            triage_completed: 'Triage terminé avec succès',
            impact_updated: "Analyse d'impact mise à jour",
            breach_updated: 'Évaluation des violations mise à jour',
            timeline_added: 'Événement de chronologie ajouté',
            decision_recorded: 'Décision enregistrée',
            notification_updated: 'Statut de notification mis à jour',
            task_created: 'Tâche créée avec succès',
            task_status_changed: 'Statut de la tâche modifié',
            checklist_completed: 'Élément de la liste de contrôle terminé',
            checklist_unchecked: 'Élément de la liste de contrôle non coché',
            task_assigned: 'Tâche assignée avec succès',
            task_escalated: 'Tâche transférée',
            export_initiated: 'Exportation lancée',
        },
        breach_notifications: {
            page_title: 'Notifications de violation',
            title: 'Notifications de violation',
            new_notification: 'Nouvelle notification',
            no_notifications: 'Aucune notification de violation trouvée',
            status: 'Statut',
            submit_to_mps: 'Envoyer à MPS',
            decision: 'Décision',
            decision_maker: 'Décideur',
            notify: 'Notifier',
            do_not_notify: 'Ne pas avertir',
            rationale: 'Justification',
            filing_deadline: 'Date limite de dépôt',
            overdue: 'En retard',
            due_soon: 'Bientôt dû',
            on_track: 'Dans les temps',
            legal_review: 'Examen juridique',
            incident_id: "Identifiant de l'incident",
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Évaluation',
                decision_to_notify: 'Décision de notifier',
                notification_drafted: 'Notification rédigée',
                legal_review: 'Examen juridique',
                submitted_to_authority: "Soumis à l'autorité",
                supplement_requested: 'Supplément demandé',
                supplement_submitted: 'Supplément soumis',
                acknowledged: 'Reconnu',
                closed: 'Fermé',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Demandes de supplément',
            title: 'Demandes de supplément',
            new_supplement: 'Nouveau supplément',
            no_supplements: 'Aucune demande de supplément trouvée',
            request_details: 'Détails de la demande',
            response_package: 'Dossier de réponse',
            submit_response: 'Soumettre la réponse',
            deadline: 'Date limite',
            notification_id: 'ID de notification',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Entrez un ID de notification pour afficher les suppléments.',
            supplement_statuses: {
                requested: 'Demandé',
                drafting: 'Rédaction',
                submitted: 'Soumis',
                accepted: 'Accepté',
                rejected: 'Rejeté',
            },
        },
        remediation: {
            page_title: 'Actions correctives',
            title: 'Actions correctives',
            new_action: 'Nouvelle action',
            no_actions: 'Aucune action corrective trouvée',
            description: 'Descriptif',
            owner: 'Propriétaire',
            department: 'Département',
            deadline: 'Date limite',
            statuses: {
                planned: 'Prévu',
                in_progress: 'En cours',
                completed: 'Terminé',
                verified: 'Vérifié',
            },
            verify: 'Vérifier',
            incident_id: "Identifiant de l'incident",
            enter_incident_id: "Saisissez un ID d'incident pour afficher les actions correctives.",
            start: 'Commencer',
            complete_action: 'Terminer',
        },
        authority_requests: {
            page_title: "Demandes d'autorisation",
            title: "Demandes d'autorisation",
            new_request: 'Nouvelle demande',
            no_requests: "Aucune demande d'autorité trouvée",
            authority_name: "Nom de l'autorité",
            request_details: 'Détails de la demande',
            response_package: 'Dossier de réponse',
            submission_channel: 'Canal de soumission',
            submit_response: 'Soumettre la réponse',
            deadline: 'Date limite',
            confirmation: 'Confirmation',
            incident_id_optional: "ID de l'incident (facultatif)",
            response_context_label: 'Contexte de réponse (pour la matérialisation du document de soumission)',
            response_context_none: '(ignorer la matérialisation)',
            response_context_supplement: 'Réponse complémentaire',
            response_context_information: "Réponse à demande d'information",
            response_context_inspection: "Réponse à l'inspection",
            materialisation_status_column: 'Document de soumission',
            materialisation_pending: 'En attente',
            materialisation_success: 'Matérialisé',
            materialisation_failed: 'Échoué',
            materialisation_retry_button: 'Réessayer',
            ar_statuses: {
                received: 'Reçu',
                verified: 'Vérifié',
                assembling: 'Assemblage',
                submitted: 'Soumis',
                closed: 'Fermé',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Examen juridique',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Liste de contrôle',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Registre de divulgation',
            title: 'Registre de divulgation',
            new_entry: 'Nouvelle entrée',
            no_entries: 'Aucune entrée de registre de divulgation trouvée',
            disclosure_type: 'Type de divulgation',
            disclosure_date: 'Date de divulgation',
            recipient: 'Destinataire',
            authority_name: "Nom de l'autorité",
            channel: 'Chaîne',
            content_summary: 'Résumé du contenu',
            incident_id_optional: "ID de l'incident (facultatif)",
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Assistant de configuration',
            tours: 'Visites guidées',
            sample_data: "Données d'exemple",
        },
        wizard: {
            title: 'Assistant de configuration',
            welcome: 'Bienvenue sur AesirX ComplianceOne',
            welcome_message: 'Configurons votre organisation. Cet assistant vous guidera dans la configuration initiale.',
            setup_complete: 'Configuration terminée',
            skipped_message: "L'assistant de configuration a été ignoré. Vous pouvez le relancer à tout moment pour configurer votre organisation.",
            start_setup: 'Démarrer la configuration',
            skip_wizard: "Ignorer l'assistant",
            go_to_admin: 'Allez dans Administrateur',
            skip_for_now: 'Ignorer pour le moment',
            next_step: 'Étape suivante',
            previous_step: 'Étape précédente',
            finish_setup: 'Terminer la configuration',
            rerun: "Relancer l'assistant de configuration",
            step_label: 'Étape {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: "Le nom de l'organisation est requis.",
            validation_admin_email_required: "L'e-mail de l'administrateur est requis.",
            validation_admin_email_invalid: 'Veuillez saisir une adresse e-mail valide.',
            validation_cmp_endpoint_required: "Le point d'accès CMP est requis lorsque le CMP est activé.",
            steps: {
                license_activation: 'Activation de la licence',
                org_profile: "Profil de l'organisation",
                module_selection: 'Sélection des modules',
                cmp_integration: 'Intégration CMP',
                notification_preferences: 'Notifications',
                review: 'Vérification et confirmation',
            },
            step_descriptions: {
                license_activation: 'Entrez votre clé de licence pour activer votre package et déterminer quels modules sont disponibles.',
                org_profile: "Configurez le nom de l'organisation, la juridiction, le secteur, la taille et la langue préférée.",
                module_selection: 'Choisissez les modules de conformité à activer pour votre organisation.',
                cmp_integration: "Configurez l'intégration de la Consent Management Platform (optionnel).",
                notification_preferences: 'Configurez la réception des notifications pour les événements de conformité.',
                review: 'Vérifiez votre configuration et finalisez la mise en place.',
            },
            license_step: {
                validate_button: 'Valider la licence',
                skip_warning: "L'accès au module sera limité au niveau de package par défaut. Vous pouvez activer une licence ultérieurement dans Plateforme > Paramètres de licence.",
            },
            module_descriptions: {
                rights_requests: "Traitez les demandes d'accès et de suppression des données personnelles.",
                data_mapping: 'Cartographiez les systèmes, les flux de données et les activités de traitement.',
                governance: 'Supervisez les prestataires, les approbations, les dérives et les registres de preuves.',
                consent: 'Gérez les politiques de consentement et les catalogues de finalités.',
                consent_properties: 'Gérez le consentement sur plusieurs propriétés numériques.',
                assessments: "Réalisez des analyses d'impact et des transferts de données transfrontaliers.",
                classification: 'Classifiez et étiquetez les actifs de données avec suivi du cycle de vie.',
                monitoring: 'Revues de conformité programmées et suivi des KPI.',
                incidents: 'Signalez et gérez les incidents de sécurité et de confidentialité.',
                connectors: "Connectez-vous à des systèmes externes pour l'échange automatisé de données.",
                vendor_risk: 'Évaluez les fournisseurs, suivez les contrats et gérez la due diligence.',
                deletion_orchestration: 'Coordonnez la suppression des données entre les systèmes et les fournisseurs.',
                discovery: 'Découvrez et classifiez les données dans votre infrastructure.',
                incident_ops: "Playbooks d'incidents avancés, suivi SLA et escalades.",
                program_governance: "Évaluations de maturité, rapports de direction et préparation à l'audit.",
                access_accountability: "Surveillez l'accès utilisateur, les sessions et les flux de justification.",
                localization: 'Packs de conformité spécifiques au marché et cadres réglementaires.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Espace de travail partenaires et revendeurs.',
                privacy_scanner: 'Surveillance en temps réel de la confidentialité des propriétés numériques.',
                onboarding: "Assistant de configuration, visites guidées et gestion des données d'exemple.",
                compliance_forms: 'Modèles de conformité interactifs et génération de documents.',
                audit_trail: "Journalisation d'audit immuable complète et conservation.",
                organizations: 'Gestion multi-organisations et de groupes.',
                task_management: 'Planification unifiée des tâches, moteur de workflow et vues calendrier.',
            },
            module_categories: {
                data_privacy_rights: 'Confidentialité des données et droits',
                vendor_third_party: 'Gestion des fournisseurs et des tiers',
                risk_assessment: 'Risque et évaluation',
                operations_automation: 'Opérations et automatisation',
                platform_admin: 'Plateforme et administration',
            },
            package_tier_label: 'Votre Forfait',
            package_module_unavailable: 'Non inclus dans votre forfait',
            select_all: 'Tout Sélectionner',
            deselect_all: 'Tout Désélectionner',
            load_sample_data: "Charger les données d'exemple après la configuration",
            load_sample_data_description: "Installez des données de démonstration réalistes pour explorer la plateforme. Peut être supprimé ultérieurement depuis Onboarding > Données d'exemple.",
            language_label: 'Langue Préférée',
            language_description: 'Choisissez la langue par défaut pour votre organisation.',
            admin_account_title: 'Compte Administrateur',
            admin_account_created: 'Compte administrateur créé',
            admin_full_name: 'Nom complet',
            admin_email: 'Adresse e-mail',
            admin_password: 'Mot de passe',
            admin_confirm_password: 'Confirmer le mot de passe',
            admin_show_password: 'Afficher le mot de passe',
            admin_hide_password: 'Masquer le mot de passe',
            admin_name_placeholder: 'par ex. Jeanne Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minimum 8 caractères',
            admin_password_confirm_placeholder: 'Ressaisir le mot de passe',
            validation_admin_name_required: "Le nom de l'administrateur est requis",
            validation_admin_password_required: 'Le mot de passe est requis',
            validation_admin_password_min_length: 'Le mot de passe doit contenir au moins 8 caractères',
            validation_passwords_mismatch: 'Les mots de passe ne correspondent pas',
            notification_email_enabled: 'Activer les Notifications par E-mail',
            notification_email_description: 'Recevez des notifications par e-mail pour les affectations de tâches, les violations de SLA et les événements de conformité.',
            notification_digest_frequency: 'Fréquence du Résumé',
            notification_digest_daily: 'Quotidien',
            notification_digest_weekly: 'Hebdomadaire',
            notification_digest_none: 'Pas de résumé',
            notification_in_app_enabled: 'Activer les Notifications Plateforme',
            notification_in_app_description: 'Afficher le badge et le tiroir de notifications pour les alertes en temps réel.',
            jurisdictions: {
                vietnam: 'Viêt Nam',
                asean: 'ASEAN',
                eu_eea: 'UE/EEE',
                global: 'Mondial',
            },
            languages: {
                en: 'Anglais',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Suède',
                nb: 'Norsk bokmål',
                nn: 'Norsk-Nynorsk',
                de: 'Allemand',
                fi: 'Suomi',
                nl: 'Pays-Bas',
                fr: 'Français',
                lb: 'Letzebuergesch',
                ga: 'Gaeilge',
                es: 'espagnol',
                ca: 'Catalogne',
                eu: 'euskara',
                pt: 'Portugais',
            },
            industries: {
                financial_services: 'Services financiers',
                healthcare: 'Santé',
                technology: 'Technologie',
                telecommunications: 'Télécommunications',
                government: 'Secteur public',
                education: 'Éducation',
                manufacturing: 'Industrie manufacturière',
                retail: 'Commerce de détail',
                other: 'Autre',
            },
            placeholders: {
                org_name: 'par exemple, ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'utilisateur1@entreprise.vn, utilisateur2@entreprise.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: "Nom de l'organisation",
                jurisdiction: 'Juridiction',
                industry: "Secteur d'activité",
                org_size: "Taille de l'organisation",
                language: 'Langue Préférée',
                admin_email: "E-mail de l'administrateur",
                team_emails: "Adresses e-mail des membres de l'équipe (séparées par des virgules)",
                enable_cmp: "Activer l'intégration CMP",
                cmp_endpoint: "Point d'accès API CMP",
                email_notifications: 'Notifications par E-mail',
                in_app_notifications: 'Notifications Plateforme',
                digest_frequency: 'Fréquence du Résumé',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Visites guidées des modules',
            start: 'Démarrer la visite',
            continue_tour: 'Continuer la visite',
            replay: 'Rejouer la visite',
            exit: 'Quitter la visite',
            skip: 'Ignorer la visite',
            next: 'Suivant',
            previous: 'Précédent',
            finish: 'Terminer la visite',
            step_of: 'Étape {current} sur {total}',
            info: 'Suivez des visites guidées de chaque module pour découvrir les fonctionnalités et flux de travail clés. Les visites peuvent être rejouées à tout moment.',
            autoplay: 'Lecture automatique',
            interactive: 'Interactif',
            play_all: 'Tout jouer',
            search_tours: 'Rechercher des circuits...',
            duration: 'Durée',
            steps_label: '{count} étapes',
            mode: 'Mode',
            pause: 'Pause',
            resume: 'CV',
            speed: 'Vitesse',
            restart: 'Redémarrer',
            reset: 'Réinitialiser',
            reset_success: 'La progression de la tournée a été réinitialisée.',
            category_core_privacy: 'Confidentialité de base',
            category_governance: 'Gouvernance',
            category_operations: 'Opérations',
            category_platform: 'Plateforme',
            category_advanced: 'Avancé',
            category_data_privacy_rights: 'Confidentialité des données et droits',
            category_vendor_third_party: 'Gestion des fournisseurs et des tiers',
            category_risk_assessment: 'Risque et évaluation',
            category_operations_automation: 'Opérations et automatisation',
            category_platform_admin: 'Plateforme et administration',
            steps_count: '{count} étapes',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: "Gestion des données d'exemple",
            select_modules: 'Sélectionner les modules',
            current_status: 'Statut actuel',
            operation_history: 'Historique des opérations',
            load: "Charger les données d'exemple",
            clear: "Supprimer les données d'exemple",
            processing: 'Traitement en cours...',
            loaded: 'Chargé',
            not_loaded: 'Non chargé',
            records: "Enregistrements d'exemple",
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: "Êtes-vous sûr de vouloir supprimer toutes les données d'exemple des modules sélectionnés ?",
            confirm_clear_title: 'Supprimer les données exemples',
            confirm_load_title: "Charger les données d'exemple",
            confirm_load: "Êtes-vous sûr de vouloir charger les données d'exemple pour les modules sélectionnés ?",
            action_load: 'Charger',
            action_clear: 'Supprimer',
            info: "Chargez des données de démonstration réalistes pour la formation et les démonstrations. Les données d'exemple sont étiquetées et peuvent être supprimées en toute sécurité sans affecter les données réelles.",
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: "Centre d'aide",
            search: 'Rechercher',
            search_placeholder: "Rechercher des articles d'aide...",
            clear: 'Effacer',
            all_categories: 'Toutes les catégories',
            all_modules: 'Tous les Modules',
            back: "Retour au Centre d'aide",
            callout_important: 'Important',
            callout_tip: 'Astuce',
            no_results: 'Aucun article trouvé correspondant à votre recherche.',
            no_articles: "Aucun article d'aide disponible pour le moment. Les articles apparaîtront ici au fur et à mesure de leur ajout.",
            results_for: '{count} résultat(s) pour « {query} »',
            quick_links: {
                getting_started: 'Premiers pas',
                getting_started_desc: 'Installation, configuration et guide de démarrage rapide.',
                user_guide: "Guide de l'utilisateur",
                user_guide_desc: "Documentation d'utilisation module par module.",
                admin_guide: "Guide de l'administrateur",
                admin_guide_desc: 'Configuration, maintenance et dépannage.',
                faq: 'FAQ',
                faq_desc: 'Questions fréquemment posées et réponses.',
            },
            search_articles_aria: "Rechercher des articles d'aide",
            article_metadata_aria: "Métadonnées de l'article",
            faq_editor_title: "Éditeur de FAQ du centre d'aide",
            faq_editor_description: "Ajoutez, modifiez et supprimez des paires question-réponse de FAQ dans n'importe quel article du centre d'aide. L'éditeur n'est pas encore disponible ; cette adresse lui est réservée.",
            faq_editor_empty: "L'éditeur de FAQ n'est pas encore disponible. En attendant, demandez à votre administrateur d'ajouter pour vous des sections FAQ à un article du centre d'aide.",
            articles_list_aria: "Articles d'aide",
            category_nav_aria: 'Navigation par catégorie',
            quick_links_aria: "Liens rapides vers les catégories d'aide",
            returned_to_list: 'Retour à la liste des articles',
            opening_article: "Ouverture de l'article : {title}",
            read_article_aria: "Lire l'article : {title}",
            pagination_nav_aria: 'Pagination des articles',
            error_unavailable: "Le centre d'aide est actuellement indisponible. Veuillez réessayer plus tard.",
            retry: 'Réessayer',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'p. ex. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Enregistrer',
        cancel: 'Annuler',
        system_actor: 'Système',
        delete: 'Supprimer',
        edit: 'Modifier',
        create: 'Créer',
        update: 'Mettre à jour',
        loading: 'Chargement...',
        error: 'Une erreur est survenue',
        success: 'Succès',
        logout: 'Déconnexion',
        actions: 'Mesures',
        retrying: 'Nouvelle tentative…',
        permission_denied: "Vous n'avez pas accès à cette page.",
        status: 'Statut',
        description: 'Descriptif',
        name: 'Nom',
        created_at: 'Créé le',
        updated_at: 'Mis à jour le',
        yes: 'Oui',
        no: 'Non',
        confirm: 'Confirmer',
        confirm_dialog_title: 'Confirm action',
        back: 'Retour',
        next: 'Suivant',
        previous: 'Précédent',
        search: 'Rechercher',
        filter: 'Filtrer',
        page_x_of_y: 'Page {page} sur {total}',
        pagination_label: 'Pagination',
        view_mode_label: 'Mode d’affichage',
        export: 'Exporter',
        exporting: 'Exportation…',
        sidebar_navigation: 'Navigation latérale administration',
        items_count: '{count} éléments',
        items_count_one: '{count} élément',
        export_success: 'Document généré avec succès.',
        generate_pdf: 'Générer PDF',
        import: 'Importer',
        download: 'Télécharger',
        upload: 'Téléverser',
        view: 'Voir',
        close: 'Fermer',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Soumettre',
        saved: 'Enregistré avec succès',
        saving: 'Enregistrement...',
        submitting: 'Soumission...',
        created: 'Créé avec succès',
        all: 'Tous',
        none: 'Aucun',
        select: 'Sélectionner',
        select_user: 'Sélectionnez un utilisateur...',
        custom_input: 'Saisir manuellement',
        required: 'Requis',
        optional: 'Facultatif',
        notes: 'Remarques',
        tags: 'Étiquettes',
        archive: 'Archiver',
        clone: 'Cloner',
        refresh: 'Actualiser',
        retry: 'Réessayer',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Réinitialiser',
        add: 'Ajouter',
        remove: 'Supprimer',
        removing: 'Suppression…',
        attachment: 'Pièce jointe',
        upload_file: 'Téléverser un fichier',
        upload_file_required: 'Téléverser un fichier (requis)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Glissez-déposez ou cliquez pour parcourir',
        upload_max_size: 'Taille max. : {size} Mo',
        upload_accepted_types: 'Acceptés : {types}',
        upload_in_progress: 'Téléversement de {name}...',
        upload_size_exceeded: 'La taille du fichier dépasse la taille maximale autorisée de {size} Mo.',
        upload_type_not_accepted: 'Type de fichier non accepté. Types acceptés : {types}',
        upload_succeeded: 'Le fichier "{name}" a été téléversé.',
        upload_failed: 'Échec du téléversement du fichier.',
        select_entity_type_first: 'Sélectionnez d’abord un type d’entité',
        failed_to_remove_file: 'Impossible de supprimer le fichier.',
        entity_type_label: 'Type d’entité',
        entity_label: 'Entité',
        entity_reference: 'Référence d’entité',
        failed_to_load_entities: 'Impossible de charger les entités',
        enable: 'Activer',
        disable: 'Désactiver',
        no_data: 'Aucune donnée trouvée',
        confirm_delete: 'Êtes-vous sûr de vouloir supprimer cet élément ?',
        confirm_action: 'Êtes-vous sûr de vouloir continuer ?',
        save_success: 'Enregistré avec succès',
        save_error: "Échec de l'enregistrement",
        delete_success: 'Supprimé avec succès',
        delete_error: 'Échec de la suppression',
        hour: 'heure',
        hours: 'heures',
        load_error: 'Échec du chargement des données',
        operation_error: "Échec de l'opération",
        something_went_wrong: 'Une erreur est survenue',
        go_home: 'Aller au tableau de bord',
        access_denied: 'Accès refusé',
        no_permission: "Vous n'avez pas la permission d'accéder à cette page.",
        no_permission_title: 'Permissions insuffisantes',
        no_permission_message: "Vous n'avez pas la permission d'accéder à cette page.",
        unknown: 'Inconnu',
        back_to_dashboard: 'Retour à Tableau de bord',
        id: 'ID',
        type: 'Catégorie',
        date: 'Date du',
        created_by: 'Créé Par',
        updated_by: 'Mis à jour Par',
        page_of: 'Page {page} de {total}',
        showing_items: 'Affichage de {count} éléments',
        items_per_page: 'Éléments par page',
        no_results: 'Aucun enregistrement ne correspond à vos filtres',
        no_items: 'Aucun items trouvé',
        sort_by: 'Trier par',
        ascending: 'Croissant',
        descending: 'Décroissant',
        all_statuses: 'Tous les statuts',
        all_types: 'Tous les types',
        all_categories: 'Toutes les catégories',
        all_severities: 'Toutes les gravités',
        date_range: 'Plage de dates',
        from_date: 'De',
        to_date: 'To',
        clear_filters: 'Effacer les filtres',
        apply_filters: 'Appliquer les filtres',
        save_changes: 'Enregistrer Changements',
        discard_changes: 'Rejeter Changements',
        confirm_discard: 'Vous avez des modifications non enregistrées. Les abandonner ?',
        copy: 'Copier',
        duplicate: 'Dupliquer',
        move: 'Déplacer',
        assign: 'Assigner',
        unassign: 'Désassigner',
        approve: 'Approuver',
        reject: 'Rejeter',
        deleting: 'Suppression...',
        processing: 'Traitement...',
        loading_page: 'Chargement de page...',
        operation_success: 'Opération terminé avec succès',
        operation_failed: "L'opération a échoué. Veuillez réessayer.",
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Réseau erreur. Vérifier votre connection.',
        session_expired: 'Session expiré. Please journaliser dans again.',
        test: 'Tester',
        pause: 'Mettre en pause',
        resume: 'Reprendre',
        activate: 'Activer',
        last_sync: 'Dernier Synchroniser',
        health: 'Santé',
        email: 'E-mail',
        role: 'Rôle',
        title: 'Titre',
        owner: 'Propriétaire',
        priority: 'Priorité',
        category: 'Catégorie',
        details: 'Détails',
        overview: 'Aperçu',
        configuration: 'Paramétrage',
        history: 'Historique',
        new_item: 'New',
        edit_item: 'Modifier',
        select_option: 'Sélectionner...',
        total: 'Somme totale',
        active: 'Actif',
        inactive: 'Inactif',
        enabled: 'Activé',
        disabled: 'Désactivé',
        last_login: 'Dernière connexion',
        never: 'Jamais',
        password_changed: 'Mot de passe modifié. Vous allez être déconnecté.',
        password_change_failed: 'Échec de change password. Veuillez réessayer.',
        passwords_mismatch: 'Les mots de passe ne correspondent pas',
        install: 'Installer',
        install_named: 'Installer {name}',
        uninstall: 'Désinstaller',
        installed: 'Installé',
        available: 'Disponible',
        catalog: 'Catalogue',
        published: 'Publié',
        version: 'Révision',
        market: 'Marché',
        language: 'Langue',
        framework: 'Cadre',
        template: 'Modèle',
        connector_type: 'Connecteur Type',
        polling_interval: "Intervalle d'interrogation (minutes)",
        new_instance: 'Nouveau Instance',
        change_password: 'Changement Mot de passe',
        current_password: 'Actuel Mot de passe',
        new_password: 'Nouveau mot de passe',
        confirm_password: 'Confirmer Mot de passe',
        comments: 'Commentaires',
        completed: 'Terminé',
        contact: 'Personne-ressource',
        hide: 'Masquer',
        hide_details: 'Masquer Détails',
        label: 'Libellé',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Affichage',
        page: 'Feuillet',
        preview: 'Aperçu',
        print: 'Imprimer',
        show: 'Afficher',
        status_active: 'Actif',
        status_completed: 'Terminé',
        status_draft: 'Brouillon',
        status_in_progress: 'En cours',
        status_pending: 'En attente',
        view_details: 'Voir détails',
        all_priorities: 'Toutes les priorités',
        pipeline: 'Chaîne de traitement',
        all_requests: 'Tous Demandes',
        schedule: 'Planification',
        deploy: 'Déployer',
        rollback: 'Retour arrière',
        advance_stage: "Avancer l'étape",
        submit_for_approval: 'Soumettre pour Approbation',
        approval_log: 'Approbation Journal',
        approver: 'Approbateur',
        decision: 'Décision',
        decided_at: 'Décidé le',
        rollout_stages: 'Étapes de déploiement',
        approval_comment: 'Approbation Commentaire',
        optional_comment: 'Facultatif commentaire...',
        impact_summary: 'Impact Résumé',
        target_property_ids: 'Cible Propriété IDs (comma-separated)',
        policy_id: 'Politique ID',
        target_version: 'Cible Version',
        target_version_placeholder: 'ex. 2.0.0',
        scheduled_label: 'Planifié',
        scheduled_start: 'Planifié Début',
        scheduled_end: 'Planifié Fin',
        properties_count: '{count} propriétés',
        results: 'Résultats',
        comparison_results: 'Résultats de comparaison',
        past_comparison_reports: 'Rapports de comparaison passés',
        scanning: 'Analyse...',
        run_inconsistency_scan: 'Exécuter Inconsistency Scanner',
        property_ids_label: 'IDs de propriétés (séparés par des virgules, min 2)',
        comparison_type: 'Type de comparaison',
        golden_template_id: 'ID du modèle de référence (facultatif)',
        leave_blank_compare: 'Laissez vide pour comparer les propriétés entre elles',
        add_notes_placeholder: 'Ajouter votre analysis notes ici...',
        covered_of_active: '{covered} sur {active} propriétés actives couvertes',
        fetch_error: 'Échec de fetch data. Veuillez réessayer.',
        run_comparison_error: 'Échec de run comparison. Veuillez réessayer.',
        save_notes_error: 'Échec de save notes. Veuillez réessayer.',
        scan_complete: 'Inconsistency scan terminé avec succès',
        scan_error: 'Échec de run inconsistency scan. Veuillez réessayer.',
        min_two_properties: 'Veuillez fournir au moins 2 ID de propriétés pour la comparaison.',
        comma_separated: 'séparées par des virgules',
        language_english: 'Anglais',
        language_vietnamese: 'Vietnamien',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Auteur',
        dependencies: 'Dépendances',
        checksum: 'Somme de contrôle',
        changelog: 'Journal des modifications',
        tier: 'Niveau',
        phone: 'Téléphone',
        launch: 'Lancer',
        complete: 'Complet',
        back_to_list: 'Retour à Liste',
        clear_selection: 'Effacer la sélection',
        deactivate: 'Désactiver',
        scope: 'Portée',
        deadline: 'Échéance',
        progress: 'Progression',
        frequency: 'Fréquence',
        reviewer: 'Réviseur',
        user: 'Utilisateur',
        campaign: 'Campagne',
        items: 'Éléments',
        next_run: 'Suivant Exécuter',
        last_run: 'Dernier Exécuter',
        overdue: 'EN RETARD',
        creating: 'Création...',
        launching: 'Lancement...',
        loaded: 'loaded',
        page_size: 'Taille des pages',
        per_page: '/page',
        urgent: 'Urgent',
        pagination: 'Pagination',
        metric: 'Métrique',
        value: 'Valeur',
        country: 'Pays',
        showing_chart_view: 'Affichage de la vue graphique',
        showing_table_view: 'Affichage de la vue tableau de données',
        policies: 'Politiques',
        compliant: 'Conforme',
        non_compliant_status: 'Non conforme',
        all_data: 'Toutes les données',
        sr_urgent: 'Urgent',
        n_per_page: '{n} / page',
        select_user_placeholder: "-- Sélectionnez l'utilisateur --",
        field_required: 'Ce champ est obligatoire',
        results_found: 'résultats trouvés',
        select_placeholder: '-- Sélectionner --',
        unknown_user: 'Utilisateur inconnu',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Exporter CSV',
        not_applicable: 'S/O',
        aria_overdue_since: 'En retard depuis {date}',
        placeholders: {
            enter_justification: 'Saisissez justification',
            justification: 'Motif',
            campaign_name_example: 'Q1 2026 Accès Examiner',
            optional_description: 'Description facultative',
            enter_user_id: 'Saisissez user ID of the reviewer',
            enter_user_id_short: 'Saisissez user ID',
            schedule_name_example: 'Trimestriel Accès Attestation',
            enter_property_id: 'Saisissez propriété id',
            enter_policy_id: 'Saisissez politique id',
            property_or_group_id: 'ID de propriété ou de groupe',
            describe_template_purpose: "Décrivez l'objectif du modèle...",
            step_title: 'Étape titre...',
            detailed_instructions: 'Instructions détaillées...',
            filter_by_taxonomy_id: 'Filtrer par Taxonomie ID...',
            describe_classification_rule: 'Décrivez la règle de classification...',
            describe_dedup_rule: 'Décrivez la règle de dédoublonnage...',
            describe_import_template: "Décrivez le modèle d'importation...",
            reason_for_rejection: 'Reason pour rejet...',
            jurisdiction_codes_example: 'VN, SG, UE',
            filter_by_locale: 'Filtrer par langue...',
            filter_by_event_type: "Filtrer par type d'événement",
            filter_by_actor_id: "Filtrer par ID d'acteur",
            filter_by_market_code: 'Filtrer par code marché...',
            filter_by_framework_id: 'Filtrer par cadre ID...',
            add_comment: 'Ajouter commentaire...',
            provide_rejection_reason: 'Fournissez un motif de rejet de cette soumission...',
            filter_by_jurisdiction: 'Filtrer par code juridiction...',
            enter_report_title: 'Saisissez signaler titre',
            enter_name: 'Saisissez name',
            enter_description: 'Saisissez description',
            enter_org_id: 'Saisissez Org ID to add',
            describe_evidence_gap: 'Décrivez le manque de preuves...',
            expected_evidence: 'Quoi preuve est expected',
            entity_identifier: "Identifiant de l'entité",
            remediation_steps: 'Étapes pour remédier à cet écart',
            user_id_or_name: 'ID utilisateur ou nom',
            reporting_period_id: 'ID de la période de rapport',
            maturity_model_id: 'Maturité modéliser ID',
            describe_maturity_model: 'Describe ce maturité modéliser...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Fournissez un motif de refus de cette demande',
            describe_emergency: "Décrivez la situation d'urgence nécessitant un accès élevé (minimum 20 caractères)",
            enter_delegate_id: 'Saisissez user id de le déléguer',
            provide_delegation_reason: 'Fournissez un motif pour cette délégation',
            client_identifier: 'Identifiant client',
            client_secret: 'Secret client',
            time_format: 'HH:MM',
            describe_sod_rule: "Décrivez l'objectif de cette règle SoD...",
            enter_role_id: 'Saisissez role ID',
            explain_exception: 'Explain pourquoi ce exception est needed...',
            describe_compensating_controls: 'Décrivez les contrôles compensatoires en place...',
            full_name: 'Nom complet',
            min_chars: 'Min 12 caractères',
            enter_override_justification: 'Saisissez justification for overriding this gate evaluation',
            enter_evidence_id: "Saisissez l'ID de la preuve à lier :",
            date_format: 'JJ/MM/AAAA',
            describe_pack_contents: 'Décrivez le contenu et le but du paquet',
            select_reviewers: 'Sélectionner des réviseurs...',
        },
        aria_previous_page: 'Page précédente',
        aria_next_page: 'Page suivante',
        aria_results_per_page: 'Résultats par page',
        per_page_25: '25/page',
        per_page_50: '50/page',
        per_page_100: '100/page',
        select_language: 'Choisir la langue',
        today: "Aujourd'hui",
        tomorrow: 'Demain',
        dashboard_load_error: 'Impossible de charger le tableau de bord. Veuillez réessayer.',
        enum_grade: {
            red: 'Rouge',
            amber: 'Orange',
            yellow: 'Jaune',
            green: 'Vert',
        },
        enum_severity: {
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Faible',
        },
        enum_priority: {
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Faible',
        },
        enum_status: {
            pending: 'En attente',
            in_progress: 'En cours',
            completed: 'Terminé',
            blocked: 'Bloqué',
            escalated: 'Escaladé',
            cancelled: 'Annulé',
            overdue: 'En retard',
            not_started: 'Non démarré',
            new: 'Nouveau',
            open: 'Ouvert',
            closed: 'Fermé',
            acknowledged: 'Pris en compte',
            resolved: 'Résolu',
        },
        enum_department: {
            unassigned: 'Non attribué',
        },
        enum_form_category: {
            assessment: 'Évaluation',
            assessment_report: "Rapport d'évaluation",
            dpia: 'DPIA',
            authority_submission: "Soumission à l'autorité",
            cross_border_transfer: 'Transfert transfrontalier',
            vendor: 'Vendeur',
            breach: 'Violation',
            consent: 'Consentement',
            governance: 'Gouvernance',
            register: "S'inscrire",
            monitoring: 'Surveillance',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Confidentialité',
            incident: 'Incident',
            org: 'Organisation',
            sector: 'Secteur',
            classification: 'Classement',
            discovery: 'Découverte',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Demandes de droits',
            assessments: 'Évaluations',
            incidents: 'Incidents',
            incident_ops: 'Opérations sur les incidents',
            monitoring: 'Surveillance',
            vendor_risk: 'Risque du fournisseur',
            governance: 'Gouvernance',
            deletion_orchestration: 'Suppression',
            access_review: "Examen de l'accès",
            consent: 'Consentement',
            consent_properties: 'Propriétés du consentement',
            forms: 'Formulaires',
            localization: 'Localisation',
            program_governance: 'Gouvernance du programme',
            privacy_scanner: 'Scanner de confidentialité',
            classification: 'Classement',
            discovery: 'Découverte',
            manual: 'Manuel',
            data_mapping: 'Cartographie des données',
        },
        enum_risk_level: {
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Faible',
        },
        enum_request_type: {
            all: 'All',
            access: 'Accès (DSAR)',
            deletion: 'Suppression',
            correction: 'Correction',
            objection: 'Objection',
            portability: 'Portabilité',
            restriction: 'Restriction',
            consent_withdrawal: 'Retrait du consentement',
            protective_measures: 'Mesures de protection',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'Anglais',
            vi: 'Vietnamien',
            all: 'Les deux',
        },
        enum_event_type: {
            case_created: 'Cas créé',
            status_changed: 'Statut modifié',
            case_assigned: 'Cas attribué',
            note_added: 'Remarque ajoutée',
            attachment_added: 'Pièce jointe ajoutée',
            attachment_deleted: 'Pièce jointe supprimée',
            reference_added: 'Référence ajoutée',
            checklist_updated: 'Liste de contrôle mise à jour',
            identity_verified: 'Identité vérifiée',
            closure_summary_added: 'Résumé de la clôture',
            approval_granted: 'Approbation accordée',
            template_applied: 'Modèle appliqué',
            export_generated: 'Exportation générée',
            sla_warning: 'SLA Avertissement',
            sla_overdue: 'SLA En retard',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: "Échec de l'export",
        you: 'You',
        export_failed: "L'exportation a échoué. Veuillez réessayer.",
        filters: 'Filtres',
        failed: 'Échoué',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Supprimé',
        draft: 'Brouillon',
        active: 'Actif',
        inactive: 'Inactif',
        pending: 'En attente',
        in_progress: 'En cours',
        completed: 'Terminé',
        archived: 'Archivé',
        published: 'Publié',
        approved: 'Approuvé',
        rejected: 'Rejeté',
        cancelled: 'Annulé',
        blocked: 'Bloqué',
        deferred: 'Reporté',
        not_started: 'Non démarré',
        dismissed: 'Rejeté',
        failed: 'Échoué',
        in_review: 'Dans Examiner',
        deprecated: 'Obsolète',
        scheduled: 'Planifié',
        deploying: 'Déploiement',
        deployed: 'Déployé',
        paused: 'En pause',
        rolled_back: 'Annulé',
        received: 'Reçu',
        processing: 'Traitement',
        under_assessment: 'Sous Évaluation',
        implemented: 'Implémenté',
        pending_approval: 'En attente Approbation',
        pending_deletion: 'En attente Suppression',
        on_hold: 'En attente',
        deleted: 'Supprimé',
        expired: 'Expiré',
        outdated: 'Obsolète',
        drifted: 'Dévié',
        decommissioned: 'Décommissionné',
        match: 'Correspondance',
        different: 'Différent',
        missing: 'Manquant',
        confirmed: 'Confirmé',
        revoked: 'Révoqué',
        escalated: 'Escaladé',
        system: 'Système',
        custom: 'Personnalisé',
        warning: 'Avertissement',
        blocking: 'Bloquant',
        analyst: 'Analyste',
        viewer: 'Visualiseur',
        daily: 'Quotidien',
        weekly: 'Hebdomadaire',
        policies: 'Politiques',
        denied: 'Refusé',
        verified: 'Vérifié',
        detected: 'Détecté',
        ignored: 'Ignoré',
        open: 'Ouvert',
        resolved: 'Résolu',
        suspended: 'Suspendu',
        retired: 'Retraité',
        review: 'Examen',
        generated: 'Généré',
        exported: 'Exporté',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Faible',
        medium: 'Moyen',
        high: 'Élevé',
        critical: 'Critique',
    },
    sensitivity: {
        standard: 'Norme',
        sensitive: 'Sensible',
        special_category: 'Catégorie spéciale',
        badge_sensitive: 'Sensible',
    },
    change_types: {
        policy_update: 'Politique Mettre à jour',
        new_binding: 'Nouveau Binding',
        remove_binding: 'Supprimer la liaison',
        configuration_change: 'Configuration Changement',
        banner_update: 'Bannière Mettre à jour',
    },
    comparison_types: {
        full: 'Complet',
        banner_only: 'Bannière Seulement',
        purposes_only: 'Finalités Seulement',
        legal_bases_only: 'Bases juridiques uniquement',
        consent_settings: 'Consentement Paramètres',
    },
    lifecycle_stages: {
        active: 'Actif',
        archived: 'Archivé',
        pending_deletion: 'En attente Suppression',
        deleted: 'Supprimé',
        on_hold: 'En attente',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Données personnelles',
        consent_record: 'Consentement Enregistrement',
        user_account: 'Utilisateur Compte',
        audit_log: 'Audit Journal',
        document: 'Pièce jointe',
        backup: 'Sauvegarde',
    },
    risk_categories: {
        data_breach: 'Données Violation',
        unauthorized_access: 'Accès non autorisé',
        data_loss: 'Perte de données',
        regulatory_non_compliance: 'Non-conformité réglementaire',
        third_party_risk: 'Third-Party Risque',
        operational: 'Opérationnel',
        technical: 'Technique',
        legal: 'Juridique',
    },
    mitigation_types: {
        technical: 'Technique',
        organizational: 'Organisationnel',
        contractual: 'Contractuel',
        procedural: 'Procédural',
    },
    safeguard_types: {
        encryption_in_transit: 'Chiffrement en transit',
        encryption_at_rest: 'Chiffrement au repos',
        access_controls: "Contrôles d'accès",
        contractual_clauses: 'Clauses contractuelles',
        data_minimization: 'Minimisation des données',
        pseudonymization: 'Pseudonymisation',
        audit_rights: 'Droits de vérification',
        breach_notification: 'Notification de violation',
        data_localization: 'Localisation des données',
        other: 'Autre',
    },
    scoring_methods: {
        weighted_average: 'Moyenne pondérée',
        simple_average: 'Moyenne simple',
        maximum: 'Valeur maximale',
        manual: 'Manuel',
    },
    dpa_status: {
        not_required: 'Non requis',
        required: 'Requis',
        in_progress: 'En cours',
        completed: 'Terminé',
        signed: 'Signé',
    },
    disposal_methods: {
        anonymize: 'Anonymiser',
        pseudonymize: 'Pseudonymiser',
        aggregate: 'Agréger',
        delete: 'Supprimer',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Tableau de bord',
        no_data: 'Aucune donnée de tableau de bord disponible.',
        no_data_heading: 'Aucune donnée en temps réel pour le moment',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Couverture du consentement',
        ctia_filings: 'Déclarations CTIA',
        next_dpia_review: 'Prochain examen DPIA',
        next_review_none: 'Aucun prévu',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Déclarations MPS des incidents',
        compliance_score: 'Score de conformité',
        grade: 'Note',
        no_score: 'Aucun score de conformité calculé pour le moment.',
        rights_requests: "Demandes d'exercice des droits",
        open: 'Ouvertes',
        sla_breached: 'SLA dépassé',
        sla_approaching: 'SLA bientôt échu',
        closed_month: 'Clôturées (mois)',
        incidents: 'Incidents et violations',
        active: 'Actifs',
        breaches: 'Violations',
        overdue_tasks: 'Tâches en retard',
        breach_72h_notice: 'Un délai de notification réglementaire de 72 heures s’applique aux violations actives',
        recommendations: 'Recommandations',
        take_action: "Passez à l'action",
        drift_events: 'Écarts et approbations',
        open_events: 'Événements ouverts',
        pending_approvals: 'Approbations en attente',
        monitoring: 'Tâches de surveillance',
        programs: 'Programmes',
        overdue: 'En retard',
        pending: 'En attente',
        ric_cases: 'Dossiers des autorités de contrôle',
        coverage: 'Couverture des données',
        systems: 'Systèmes',
        activities: 'Activités',
        vendors: 'Prestataires',
        dpias: 'AIPD',
        open_gaps: 'écarts ouverts',
        view_as_table: 'Afficher en tableau',
        view_as_chart: 'Afficher en graphique',
        table_metric_header: 'Indicateur',
        table_value_header: 'Valeur',
        announce_chart_view: 'Affichage en graphique',
        announce_table_view: 'Affichage en tableau',
        section_core_metrics: 'Indicateurs cles',
        section_task_management: 'Gestion des taches',
        section_compliance_insights: 'Perspectives de conformite',
        configure_dashboard: 'Configurer le tableau de bord',
        config_col_active: 'Actif',
        config_col_widget: 'Widget',
        collapse_section: 'Reduire',
        expand_section: 'Developper',
        reset_to_defaults: 'Réinitialiser par défaut',
        preferences_saved: 'Préférences du tableau de bord enregistrées',
        preferences_reset: 'Tableau de bord réinitialisé aux paramètres du rôle',
        preferences_load_error: 'Échec du chargement des préférences du tableau de bord',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Score système moyen',
        factor_avg_vendor_score: 'Note moyenne du fournisseur',
        factor_processing_coverage: 'Couverture des activités de traitement',
        factor_ropa_completeness: 'Complétude du RoPA',
        factor_drift_penalty: 'Pénalité pour les événements de dérive',
        rec_improve_system_gov: 'Améliorer la documentation sur la gouvernance du système',
        rec_improve_vendor_gov: 'Améliorer la documentation sur la gouvernance des fournisseurs',
        rec_document_processing: 'Activités de traitement de documents pour les systèmes non couverts',
        rec_generate_ropa: 'Générer des enregistrements RoPA pour les activités de traitement actives',
        rec_resolve_drift: 'Résoudre les événements de dérive ouverts critiques et de haute gravité',
        score_good: 'Bon',
        score_fair: 'Acceptable',
        score_poor: 'Mauvais',
        score_critical: 'Critique',
        drag_to_reorder: 'Glisser pour réorganiser',
        widget_moved: 'Widget déplacé',
        press_space_to_grab: 'Appuyez sur Espace pour saisir, touches fléchées pour déplacer, Espace pour déposer',
        reorder_instructions: 'Utilisez le glisser-déposer ou les raccourcis clavier pour réorganiser les widgets',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Aperçu des tâches',
            pending: 'En attente',
            in_progress: 'En cours',
            overdue: 'En retard',
            done_7d: 'Terminé (7j)',
            by_priority: 'Par priorité',
            view_all_tasks: 'Afficher toutes les tâches',
            upcoming_tasks: 'Tâches à venir (7 prochains jours)',
            no_upcoming: 'Aucune tâche à accomplir dans les 7 prochains jours.',
            view_calendar: 'Voir le calendrier',
            overdue_alerts: 'En retard',
            no_overdue: 'Aucune tâche en retard. Tout est sur la bonne voie.',
            assigned_to: 'Attribué à',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Afficher tous les retards',
            notifications: 'Notifications récentes',
            unread: 'unread',
            no_notifications: 'Aucune notification. Vous êtes tous rattrapés !',
            view_all_notifications: 'Afficher toutes les notifications',
            load_error: 'Échec du chargement des données.',
            day_late: 'jour de retard',
            days_late_plural: 'jours de retard',
            due_today: "À rendre aujourd'hui",
            just_now: 'juste maintenant',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'ré',
            cross_org_stats: 'Statistiques inter-organisations',
            by_organization: 'Par organisation',
            overdue_across_orgs: 'tâches en retard dans les organisations',
            overdue_count_label: '{count} overdue tasks',
            total: 'Total',
            tasks: 'tasks',
            organization: 'Organisation',
            current_org: 'Organisation actuelle',
            all_orgs: 'Toutes les organisations',
            all_organizations: 'Toutes les organisations',
            filter_by_org: 'Filtrer par organisation',
            cross_org: 'Inter-organisation',
            empty_calendar: 'Aucune tâche pour cette période. Créez votre première tâche ou vérifiez vos filtres.',
            empty_my_tasks: 'Aucune tâche ne vous est assignée. Excellent travail !',
            empty_overdue: 'Aucune tâche en retard. Tout est sur la bonne voie.',
            empty_notifications: 'Aucune notification. Vous êtes tous rattrapés !',
            empty_workflows: 'Aucun flux de travail configuré. Parcourez les modèles pour commencer.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Mes Tâches',
            no_tasks: 'Aucune tâche ne vous est attribuée.',
            load_error: 'Impossible de charger les tâches.',
            overdue: 'en retard',
            remaining: 'restant',
            view_all: 'Voir Toutes les Tâches',
        },
        my_section_status_widget: {
            title: 'Statut de Mes Sections',
            overall_completion: 'Global',
            sections_count: 'Sections',
            no_sections: 'Aucune section attribuée à votre département.',
            load_error: 'Impossible de charger le statut des sections.',
            department_tasks_title: 'Tâches du département',
            no_department_tasks: "Votre département n'a aucune tâche.",
            department_tasks_error: 'Impossible de charger les tâches du département.',
            no_department: "Vous n'êtes rattaché à aucun département.",
        },
        revision_requests_widget: {
            title: 'Demandes de Révision',
            no_revisions: 'Aucune demande de révision. Toutes les sections sont en bonne voie.',
            load_error: 'Impossible de charger les demandes de révision.',
        },
        consent_summary: {
            title: 'Consentement',
            active: 'Actif',
            withdrawn_30d: 'Retiré (30j)',
            expiring_7d: 'Expirant (7j)',
        },
        transfer_assessments: {
            title: 'Évaluations de transfert',
            open: 'Ouvert',
            approved: 'Approuvé',
            gaps_flagged: 'Lacunes signalées',
        },
        breach_notifications: {
            title: 'Notifications de violation',
            deadline_72h: 'délai de 72h',
            submitted: 'Soumis',
            pending: 'En attente',
        },
        authority_requests: {
            title: "Demandes d'autorisation",
            pending: 'En attente',
            overdue: 'En retard',
            responded_30d: 'A répondu (30j)',
        },
        data_subjects_widget: {
            title: 'Personnes concernées',
            total: 'Total',
            high_risk: 'Risque élevé',
            active_30d: 'Actif (30j)',
        },
        audit_activity_summary: {
            title: 'Activité de vérification',
            events_today: "Événements aujourd'hui",
            events_7d: 'Événements (7j)',
            unique_actors_30d: 'Acteurs uniques (30j)',
        },
        onboarding_progress: {
            title: "Progression de l'intégration",
            completion_pct: 'Terminé',
            status_label: 'Statut',
            current_step: 'Étape actuelle',
            status: {
                in_progress: 'En cours',
                completed: 'Terminé',
                skipped: 'Sauté',
                not_started: 'Pas démarré',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sector Overlay Reports Due [FR]',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filtrer par référentiel',
            all_frameworks: 'Tous les référentiels',
            filing_lifecycle: 'Cycle de vie des déclarations',
            filing_pipeline: 'Pipeline de déclarations',
            draft: 'Brouillon',
            internal_review: 'Revue interne',
            approved: 'Approuvé',
            submitted: 'Soumis',
            supplement_requested: 'Supplément demandé',
            resubmitted: 'Soumis à nouveau',
            accepted: 'Accepté',
            rejected: 'Rejeté',
            closed: 'Fermé',
            deadline: 'Date limite',
            days_remaining: 'jours restants',
            days_overdue: 'jours de retard',
            no_filings: 'Aucune déclaration active.',
            department_consolidation: 'Consolidation des départements',
            departments_completed: 'Départements terminés',
            departments_pending: 'Départements en attente',
            departments_overdue: 'Départements en retard',
            no_consolidation_cases: 'Aucun cas multi-départements en cours.',
            vendor_risk: 'Risque fournisseurs et tiers',
            pending_assessment: 'Évaluation en attente',
            overdue_questionnaires: 'Questionnaires en retard',
            dpa_review_queue: 'File de revue DPA',
            risk_distribution: 'Répartition des risques',
            total_vendors: 'Total fournisseurs',
            by_risk_level: 'Par niveau de risque',
            no_vendor_data: 'Aucune donnée de risque fournisseur disponible.',
            discovery_summary: 'Découverte de données',
            discovery_classification: 'Découverte et classification des données',
            active_scans: 'Analyses actives',
            labels_pending: "Étiquettes en attente d'examen",
            exceptions_unresolved: 'Exceptions non résolues',
            pending_review: 'Revue en attente',
            exceptions: 'Exceptions',
            approved_labels: 'Étiquettes approuvées',
            total_classified: 'Total classifié',
            no_discovery_data: 'Aucune donnée de découverte disponible.',
            ai_governance: "Gouvernance des systèmes d'IA",
            systems_inventory: 'Systèmes en inventaire',
            systems_in_inventory: "Systèmes dans l'inventaire",
            classification_breakdown: 'Répartition des classifications',
            dossier_completion: 'Achèvement du dossier',
            by_classification: 'Par classification',
            labeling_status: "Statut d'étiquetage",
            not_started: 'Non démarré',
            no_ai_data: 'Aucune donnée de gouvernance IA disponible.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Réponse aux violations et incidents',
            active_breaches: 'Violations actives',
            notification_status: 'Statut de notification',
            remediation_progress: 'Progression de la remédiation',
            supplement_loops: 'Boucles de complément',
            no_breach_data: 'Aucun cas de violation actif.',
            audit_readiness: "Préparation à l'audit et aux évaluations",
            assessments: 'Évaluations',
            gap_findings: 'Constats de lacunes',
            evidence_completion: 'Achèvement des preuves',
            export_ready: 'Prêt à exporter',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Non prêt',
            no_audit_data: "Aucune donnée de préparation d'audit disponible.",
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cybersecurite 2018',
            framework_label_vn_ecommerce: 'VN E-commerce 2023',
            framework_label_vn_telecom: 'VN Telecommunications 2023',
            framework_label_vn_ai: 'VN Loi IA 2025',
            framework_label_gdpr: 'RGPD',
            framework_label_unspecified: 'Non spécifié',
            my_department: 'Mon département',
            my_tasks: 'Mes tâches',
            section_status: 'Statut de la section',
            revision_requests: 'Demandes de révision',
            completion: 'Achèvement',
            reason: 'Motif',
            requested_at: 'Demandé le',
            no_tasks_assigned: 'Aucune tâche assignée.',
            no_sections: 'Aucune section assignée à votre département.',
            no_revisions: 'Aucune demande de révision.',
            load_error: 'Échec du chargement des données.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Occurrences',
            credentials: "Coffre-fort d'identifiants",
            event_bus: "Bus d'événements",
            definitions: 'Définitions',
            brds: 'Capture BRD',
            security_reviews: 'Revues de sécurité',
            poc_scorecards: 'Fiches POC',
            automation_planning: 'Planification automatisation',
            system_inventory: 'Inventaire systèmes',
            traceability_matrix: 'Matrice traçabilité',
            gap_register: 'Registre des écarts',
        },
        instances: {
            title: 'Instances de connecteurs',
            page_title: 'Connecteurs',
            add: 'Ajouter une instance',
            test: 'Tester la connexion',
            sync_now: 'Synchroniser maintenant',
            new_instance: 'Nouveau connecteur instance',
            no_instances: 'Aucun connector instances trouvé',
            instance_count: '{count} occurrence(s)',
            confirm_delete: 'Supprimer connector instance "{name}"?',
            all_connector_types: 'Tous Connecteur Types',
            select_connector: 'Sélectionnez connector...',
            fetch_error: 'Échec de fetch connector instances. Veuillez réessayer.',
            fetch_definitions_error: 'Échec de fetch connector definitions. Veuillez réessayer.',
            create_error: 'Échec de create connector instance. Veuillez réessayer.',
            test_error: 'Échec de test connector. Veuillez réessayer.',
            status_error: 'Échec de la mise à jour du statut du connecteur. Veuillez réessayer.',
            delete_error: 'Échec de supprimer connector instance. Veuillez réessayer.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Statut mis à jour',
            reactivate: 'Réactiver',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: "Coffre-fort d'identifiants",
            page_title: 'Identifiants du connecteur',
            add: 'Ajouter un identifiant',
            rotate: 'Renouveler',
            expired: 'Expiré',
            expiring: 'Expiration prochaine',
            orphaned: 'Orphelin',
            new_credential: 'Nouveau identifiant',
            no_credentials: 'Aucun identifiants trouvé',
            no_expiry: "Pas d'expiration",
            linked_connectors: 'Connecteurs liés',
            expires_at: 'Expire À',
            value_label: 'Valeur * (write-only)',
            confirm_delete_title: 'Confirmer Supprimer',
            confirm_delete_message: "Supprimer cette accréditation ? Les connecteurs liés perdront l'accès.",
            rotate_title: 'Rotation des accréditations',
            new_credential_value: "Nouvelle valeur d'accréditation",
            fetch_error: 'Échec de charge identifiants. Veuillez réessayer.',
            health_error: 'Échec de load health. Veuillez réessayer.',
            create_error: 'Échec de create credential. Veuillez réessayer.',
            rotate_error: 'Échec de rotate credential. Veuillez réessayer.',
            delete_error: 'Échec de supprimer identifiant. Veuillez réessayer.',
        },
        event_bus: {
            title: "Bus d'événements",
            page_title: "Bus d'événements",
            events: 'Événements',
            subscriptions: 'Abonnements',
            dlq: 'File des messages en erreur',
            publish: 'Publier un événement',
            retry: 'Réessayer',
            dismiss: 'Ignorer',
            search_events: 'Rechercher des événements...',
            filter_event_type: "Filtrer par type d'événement...",
            event_type: 'Événement Type',
            source_module: 'Module source',
            timestamp: 'Horodatage',
            delivery_status: 'Statut de livraison',
            correlation_id: 'ID de corrélation',
            tracking_id: 'ID de suivi',
            delivery_status_hint: 'Le statut de livraison est mis à jour automatiquement au fur et à mesure que les événements sont traités.',
            no_events: 'Aucun events trouvé',
            total_with_count: '{count} au total',
            prev: 'Préc.',
            new_subscription: '+ Nouveau Abonnement',
            subscriber_type: 'Abonné Type',
            subscriber_id: 'Abonné ID',
            event_type_pattern: 'Événement Type Pattern',
            event_type_pattern_placeholder: 'p.ex. incident.*',
            filter_expression: 'Expression de filtre',
            filter_expression_placeholder: 'Facultatif filtrer',
            subscriber_type_hint: "Choisissez comment les événements seront livrés : webhook envoie des requêtes HTTP, e-mail envoie des notifications, file d'attente route vers une file de messages.",
            subscriber_id_hint: "Entrez la destination des événements. Pour les webhooks, utilisez une URL. Pour l'e-mail, utilisez une adresse. Pour les files, utilisez le nom de la file.",
            event_type_pattern_hint: "Faites correspondre les événements par type avec des caractères génériques. Utilisez * pour correspondre à n'importe quel segment (ex. incident.* correspond à incident.created, incident.resolved).",
            filter_expression_hint: 'Optionnel. Filtrez davantage les événements par contenu (ex. severity=high).',
            subscriber_id_placeholder_webhook: 'ex. https://votre-app.com/webhook',
            subscriber_id_placeholder_email: 'ex. alertes@votre-entreprise.com',
            subscriber_id_placeholder_queue: 'ex. file-evenements',
            event_pattern: "Modèle d'événement",
            filter: 'Filtrer',
            enabled: 'Activé',
            delivery_stats: 'Stats de livraison',
            no_subscriptions: 'Aucun abonnements trouvé',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} livrés / {failed} échoués',
            confirm_delete_subscription: 'Supprimer ce abonnement?',
            unresolved: 'Non résolu',
            resolved: 'Résolu',
            event_id: 'Événement ID',
            subscription_id: 'Abonnement ID',
            error_code: "Code d'erreur",
            error_message: "Message d'erreur",
            attempts: 'Tentatives',
            last_failed: 'Dernier Échoué',
            no_dlq_entries: 'Aucun dead letter entries trouvé',
            dlq_count: '{count} entrée(s) au total',
            fetch_events_error: 'Échec de fetch events. Veuillez réessayer.',
            fetch_subscriptions_error: 'Échec de fetch subscriptions. Veuillez réessayer.',
            fetch_dlq_error: 'Échec de fetch dlq entries. Veuillez réessayer.',
            create_subscription_error: 'Échec de create subscription. Veuillez réessayer.',
            toggle_subscription_error: 'Échec de basculer abonnement. Veuillez réessayer.',
            delete_subscription_error: 'Échec de supprimer abonnement. Veuillez réessayer.',
            retry_error: 'Nouvelle tentative échoué',
            dismiss_error: 'Rejet échoué',
            event_published: 'Événement publié',
            publish_event_error: "Échec de la publication de l'événement. Veuillez réessayer.",
            priority_label: 'Priorité',
            priority_low: 'Basse',
            priority_normal: 'Normale',
            priority_high: 'Haute',
            payload_helper_text: 'Saisissez les données de l\'événement au format JSON. Exemple : {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Abonnement créé',
            subscription_toggled: 'Abonnement basculé',
            subscription_deleted: 'Abonnement supprimé',
            dlq_retried: 'Message DLQ réessayé',
            dlq_dismissed: 'Message DLQ rejeté',
        },
        definitions: {
            title: 'Définitions des connecteurs',
            page_title: 'Définitions de connecteurs',
            add: 'Ajouter une définition',
            new_definition: 'Nouvelle définition de connecteur',
            edit_definition: 'Modifier la définition du connecteur',
            no_definitions: 'Aucune définition de connecteur trouvée',
            definition_count: '{count} définition(s)',
            all_categories: 'Toutes les catégories',
            select_category: 'Sélectionnez une catégorie...',
            capabilities: 'Capacités',
            capabilities_hint: 'Capacités (séparées par des virgules)',
            capabilities_placeholder: 'par ex. lire, écrire, synchroniser',
            created: 'Définition du connecteur créée',
            updated: 'Définition du connecteur mise à jour',
            fetch_error: 'Échec de la récupération des définitions de connecteur. Veuillez réessayer.',
            create_error: 'Échec de la création de la définition du connecteur',
            update_error: 'Échec de la mise à jour de la définition du connecteur',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'Capture BRD',
            page_title: 'Capture BRD',
            create_brd: 'Créer un BRD',
            brd_title: 'Titre du BRD',
            description: 'Descriptif',
            requirements: 'Exigences',
            status: 'Statut',
            stakeholders: 'Parties prenantes',
            connector_label: 'Connecteur',
            select_connector: 'Sélectionner un connecteur...',
            approval: 'Approbation',
            no_brds_found: 'Aucun BRD trouvé',
            decision_records: 'Registres de décisions',
            create_decision: 'Créer une décision',
            alternatives_considered: 'Alternatives envisagées',
            alternative_name: "Nom de l'alternative",
            reason_rejected: 'Motif de rejet',
            add_alternative: 'Ajouter une alternative',
            remove_alternative: "Supprimer l'alternative {name}",
            alternatives: 'Alternatives',
            rationale: 'Justification',
            selected_connector: 'Connecteur sélectionné',
            comma_separated_names: 'Noms séparés par des virgules',
            brd_created: 'BRD créé',
            decision_record_created: 'Enregistrement de décision créé',
            no_decision_records_found: 'Aucun enregistrement de décision trouvé',
            aria_brds_table: 'BRD',
            aria_decision_records_table: 'Enregistrements de décision',
            aria_approve: 'Approuver {name}',
            aria_archive: 'Archiver {name}',
            brd_title_help: 'Un nom court et lisible par l\'homme pour ce BRD - par ex. "Intégration de la billetterie Jira", "Déploiement Okta SSO".',
            brd_title_placeholder: 'par ex. Intégration de la billetterie Jira',
            description_help: 'Le problème commercial que ce connecteur résout. Quel résultat attendez-vous une fois qu’il sera en ligne ?',
            description_placeholder: "par ex. Automatisez la création de tickets d'incident à partir des alertes de surveillance afin que les appels soient signalés dans les 60 secondes.",
            connector_help: 'Facultatif. Choisissez un connecteur auquel ce BRD est destiné – laissez vide si le connecteur n’a pas encore été choisi.',
            requirements_help: 'Liste de fonctionnalités indispensables séparées par des virgules – par ex. "Connexion OAuth2, livraison de webhook, exportation d\'audit".',
            requirements_placeholder: "Connexion OAuth2, livraison de webhook, exportation d'audit",
            stakeholders_help: 'Noms ou adresses e-mail séparés par des virgules des personnes qui doivent se déconnecter.',
            selected_connector_help: "Choisissez le connecteur auquel cette décision s'applique.",
            rationale_help: 'Pourquoi ce connecteur a été choisi. Couvrez les facteurs décisifs afin qu’un futur lecteur comprenne l’appel.',
            rationale_placeholder: "par ex. Choisissez Jira car il contient déjà notre historique de tickets et l'équipe y est formée.",
            alternatives_considered_help: "Autres connecteurs évalués et raisons pour lesquelles ils n’ont pas été sélectionnés. Ajoute une capacité de défense pour l'audit.",
            alternative_name_placeholder: 'par ex. ServiceMaintenant',
            reason_rejected_placeholder: "par ex. Coût 3 fois plus élevé, aucun avantage pour notre cas d'utilisation",
        },
        security_reviews: {
            title: 'Revues de sécurité',
            page_title: 'Revues de sécurité',
            create_review: 'Créer une revue',
            connector_name: 'Nom du connecteur',
            connector_label: 'Connecteur',
            select_connector: 'Sélectionner un connecteur...',
            reviewer: 'Réviseur',
            select_reviewer: 'Sélectionner un réviseur...',
            findings: 'Constatations (une par ligne)',
            risk_level: 'Niveau de risque',
            risk_level_placeholder: 'p. ex. élevé, moyen, faible',
            notes: 'Remarques',
            review_status: 'Statut de la revue',
            checklist: 'Liste de contrôle',
            privacy_impact: 'Impact sur la vie privée',
            approval_gate: "Porte d'approbation",
            no_reviews_found: 'Aucune revue trouvée',
            review_created: 'Revue de sécurité créée',
            create_control_mapping: 'Créer un mappage de contrôle',
            control_mapping_created: 'Mappage de contrôle créé',
            connector_id_label: 'ID du connecteur',
            control_id_label: 'ID du contrôle',
            control_name_label: 'Nom du contrôle',
            aria_approve: 'Approuver {name}',
            aria_reject: 'Rejeter {name}',
            scan_imported: 'Scan importé avec succès',
            import_scan: 'Importer le scan',
            scan_format: 'Format du scan',
            scan_data: 'Données du scan',
            import_results: "Résultats d'importation",
            recommendations: 'Recommandations',
            auto_populated_from: 'Rempli automatiquement à partir de {tool}',
            manual_entry: 'Saisie manuelle',
            automation_type: "Type d'automatisation",
            select_automation_type: "Sélectionnez le type d'automatisation...",
            automation_full: 'Complet : le connecteur automatise entièrement ce contrôle',
            automation_partial: 'Partiel : le connecteur apporte des preuves ; les humains agissent toujours',
            automation_monitoring: "Surveillance uniquement : le connecteur observe, n'applique pas",
            control_id_help: 'Utilisez le code de votre système de conformité (par exemple ISO-27001-A.12.1, SOC2-CC6.1 ou votre ID de contrôle interne).',
            control_name_help: 'Nom court lisible par l\'homme (par exemple "Restriction d\'accès au réseau").',
            description_label: 'Descriptif',
            load_sarif_example: "Charger l'exemple SARIF",
            load_json_example: 'Charger un exemple personnalisé de JSON',
            scan_format_help: "SARIF est le standard OASIS émis par la plupart des scanners (Snyk, CodeQL, Semgrep). Utilisez un JSON personnalisé pour les outils qui n'émettent pas de SARIF.",
            scan_data_help: "Collez le résultat de l'analyse complète, téléchargez un fichier .sarif/.json ou cliquez sur un bouton Charger un exemple pour voir la forme.",
            upload_file_label: 'Téléchargez le fichier .sarif ou .json',
            invalid_scan_json: "Impossible d'analyser les données d'analyse en tant que JSON. Recherchez les erreurs de syntaxe à proximité du caractère en surbrillance.",
            save_as_review: "Enregistrer en tant qu'examen de sécurité",
            discard_preview: "Supprimer l'aperçu",
            preview_hint: "Ceci est un aperçu — rien n'est encore enregistré. Cliquez sur « Enregistrer en tant qu'examen de sécurité » pour créer un examen en attente pré-rempli avec ces valeurs.",
            reviewer_required_for_save: "Choisissez un réviseur pour enregistrer cette analyse en tant qu'examen de sécurité.",
        },
        poc_scorecards: {
            title: 'Fiches POC',
            page_title: 'Fiches POC',
            create_scorecard: 'Créer une fiche',
            connector_label: 'Connecteur',
            select_connector: 'Sélectionner un connecteur...',
            vendor_name: 'Nom du fournisseur',
            recommendation: 'Recommandation',
            recommendation_placeholder: 'p. ex. Continuer, Ne pas continuer, Nécessite une évaluation approfondie',
            criteria: "Critères d'évaluation",
            criterion_name: 'Nom du critère',
            score: 'Score',
            max_score: 'Score maximum',
            notes: 'Remarques',
            add_criterion: 'Ajouter un critère',
            remove_criterion: 'Supprimer le critère {name}',
            total_score: 'Score total',
            api_quality: 'Qualité API',
            reliability: 'Fiabilité',
            support_quality: 'Qualité du support',
            cost_score: 'Score de coût',
            overall_score: 'Score global',
            evidence: 'Preuves',
            no_scorecards_found: 'Aucune fiche trouvée',
            scorecard_created: 'Fiche POC créée',
            connector_help: 'Choisissez le connecteur auquel cette évaluation POC est destinée.',
            vendor_name_help: 'Le fournisseur ou le produit évalué – par ex. "Okta", "Datadog", "Flocon de neige".',
            vendor_name_placeholder: 'par ex. Okta, Datadog, Flocon de neige',
            recommendation_help: "Votre dernier appel après l'évaluation. Soyez bref et décisif.",
            criteria_help: 'Notez le fournisseur sur chaque critère d’évaluation. Ajoutez une ligne par critère (API qualité, fiabilité, support, coût, etc.).',
            criterion_name_help: 'Ce que vous notez – par ex. "API Qualité", "Fiabilité", "Support", "Coût".',
            criterion_name_placeholder: 'par ex. API Qualité',
            score_help: 'Note attribuée pour ce critère. Ne doit pas dépasser le score maximum.',
            max_score_help: 'Meilleur score disponible pour ce critère. Utilisez la même échelle sur toutes les lignes afin que les totaux soient comparables.',
            notes_help: 'Facultatif. Ajoutez des preuves, des mises en garde ou des liens – tout ce qui explique le score.',
        },
        common_labels: {
            aria_filter_instances: 'Filtrer les instances de connecteurs',
            aria_filter_definitions: 'Filtrer les définitions de connecteurs',
            aria_filter_credentials: 'Filtrer les identifiants',
            aria_filter_events: 'Filtrer les événements',
            aria_filter_dlq: 'Filtrer la file des lettres mortes',
            aria_instance_summary: "Résumé de l'instance du connecteur",
            aria_active_connectors: 'Connecteurs actifs',
            aria_error_connectors: 'Connecteurs en erreur',
            aria_credential_health: "Résumé de l'état des identifiants",
            aria_items_per_page: 'Éléments par page',
            show_password: 'Afficher le mot de passe',
            hide_password: 'Masquer le mot de passe',
            payload_json_label: 'Charge utile (JSON)',
            payload_invalid_json: 'La charge utile doit être du JSON valide',
            placeholder_event_type: 'ex. incident.created',
            placeholder_source_module: 'ex. monitoring',
            placeholder_version: 'ex. 1.0',
        },
        statuses: {
            configuring: 'Configuration',
            testing: 'Tests',
            active: 'Actif',
            paused: 'En pause',
            error: 'Erreur',
            disabled: 'Désactivé',
            draft: 'Brouillon',
            approved: 'Approuvé',
            archived: 'Archivé',
            pending: 'En attente',
            rejected: 'Rejeté',
        },
        system_types: {
            application: 'Demande',
            database: 'Base de données',
            service: 'Service',
            infrastructure: 'Infrastructures',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Sur site',
            cloud: 'Nuage',
            hybrid: 'Hybride',
            managed: 'Géré',
        },
        system_statuses: {
            active: 'Actif',
            decommissioned: 'Décommissionné',
            planned: 'Planifié',
            under_review: 'En révision',
        },
        automation_statuses: {
            planning: 'Planification',
            in_progress: 'En cours',
            completed: 'Terminé',
            on_hold: 'En attente',
        },
        gap_types: {
            functional: 'Fonctionnel',
            technical: 'Technique',
            security: 'Sécurité',
            compliance: 'Conformité',
            process: 'Processus',
        },
        severity_labels: {
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Faible',
        },
        gap_statuses: {
            open: 'Ouvert',
            in_progress: 'En cours',
            mitigated: 'Atténué',
            closed: 'Fermé',
            accepted: 'Accepté',
        },
        verification_statuses: {
            not_started: 'Non commencé',
            in_progress: 'En cours',
            verified: 'Vérifié',
            failed: 'Échoué',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Interne',
            email: 'E-mail',
            queue: 'File',
        },
        credential_types: {
            password: 'Mot de passe',
            api_key: 'Clé API',
            oauth2_client: 'Client OAuth2',
            certificate: 'Certificat',
            token: 'Jeton',
            ssh_key: 'Clé SSH',
            imap_credentials: 'Identifiants IMAP',
        },
        categories: {
            hr: 'RH',
            crm: 'GRC',
            erp: 'ERP',
            cloud: 'Nuage',
            security: 'Sécurité',
            itsm: 'ITSM',
            custom: 'Personnalisé',
        },
        delivery_statuses: {
            pending: 'En attente',
            delivered: 'Livré',
            partial: 'Partiel',
            failed: 'Échoué',
        },
        automation_planning: {
            title: 'Planification automatisation',
            page_title: 'Planification automatisation',
            program_name: 'Nom du programme',
            program_status: 'Statut',
            description: 'Descriptif',
            linked_artifacts: 'Artefacts liés',
            create_program: 'Créer un programme',
            edit_program: 'Modifier le programme',
            no_programs_found: 'Aucun programme trouvé',
            program_created: 'Programme créé',
            program_updated: 'Programme mis à jour',
            target_date: 'Date cible',
            owner: 'Propriétaire',
            select_status: 'Sélectionner un statut...',
            aria_programs_table: "Programmes d'automatisation",
            aria_edit: 'Modifier {name}',
            export_programs: 'Exporter les programmes',
            title_label: 'Titre du programme',
            title_help: 'Un nom court et lisible pour le programme d\'automatisation - par ex. "Déploiement des incidents vers Jira au troisième trimestre", "PDPL automatisation des demandes d\'accès".',
            title_placeholder: 'par ex. Déploiement des incidents vers Jira au troisième trimestre',
            description_help: "Ce que couvre ce programme et le résultat que vous attendez une fois qu'il sera en ligne. Facultatif mais recommandé.",
            description_placeholder: 'par ex. Transférez les alertes de surveillance dans Jira afin que les appels soient signalés dans les 60 secondes.',
            owner_label: 'Propriétaire du programme',
            owner_help: 'La personne responsable de ce programme. Ils recevront des notifications sur les progrès et les portes.',
            select_owner: 'Sélectionnez un propriétaire...',
            frameworks_in_scope: 'Cadres concernés',
            frameworks_in_scope_help: "Cadres de conformité pris en charge par ce programme. Aide la piste d'audit à montrer par quelles réglementations l'automatisation est régie.",
            select_frameworks: 'Sélectionnez les cadres...',
            status_draft: 'Brouillon',
            status_active: 'Actif',
            status_completed: 'Terminé',
            status_archived: 'Archivé',
            change_status: 'Changer le statut',
            status_updated: 'Statut mis à jour',
            aria_change_status: 'Modifier le statut de {name}',
        },
        system_inventory: {
            title: 'Inventaire des systèmes',
            page_title: 'Inventaire des systèmes',
            system_name: 'Nom du système',
            system_type: 'Type de système',
            hosting: 'Hébergement',
            status: 'Statut',
            description: 'Descriptif',
            create_system: 'Créer un système',
            edit_system: 'Modifier le système',
            no_systems_found: 'Aucun système trouvé',
            system_created: 'Système créé',
            system_updated: 'Système mis à jour',
            select_type: 'Sélectionner un type...',
            select_hosting: 'Sélectionner hébergement...',
            select_status: 'Sélectionner un statut...',
            owner: 'Propriétaire',
            department: 'Département',
            data_categories: 'Catégories de données',
            data_categories_placeholder: 'Séparées par des virgules, ex. PII, Financier, Santé',
            jurisdiction: 'Juridiction',
            jurisdiction_placeholder: 'ex. UE, US, Global',
            interfaces: 'Interfaces',
            interfaces_placeholder: 'Séparées par des virgules, ex. API REST, SFTP, JDBC',
            owner_placeholder: 'Sélectionner un propriétaire responsable...',
            department_placeholder: "Sélectionner l'unité organisationnelle responsable...",
            system_name_hint: "Utilisez le nom du système ou de l'application reconnu par l'entreprise.",
            system_type_hint: 'Classifiez le système afin que les rapports puissent regrouper des actifs similaires.',
            hosting_hint: "Identifiez où le système s'exécute pour la résidence, la sécurité et l'examen des fournisseurs.",
            status_hint: "Définissez l'état du cycle de vie utilisé par les révisions, les exportations et les preuves d'audit.",
            owner_hint: "Choisissez l'utilisateur actif responsable de la gouvernance du système et des tâches de suivi.",
            department_hint: "Utilisez l'unité organisationnelle responsable de l'exploitation ou de la gouvernance de ce système.",
            data_categories_hint: 'Listez les classes de données traitées par ce système, séparées par des virgules.',
            jurisdiction_hint: "Enregistrez la principale juridiction légale ou d'hébergement pour la cartographie de la conformité.",
            interfaces_hint: "Listez les chemins de connexion ou d'intégration utilisés pour échanger des données.",
            description_hint: 'Résumez ce que fait le système et pourquoi il est important pour la cartographie des données.',
            owner_department_required: 'Le propriétaire et le département sont obligatoires.',
            aria_systems_table: 'Inventaire des systèmes',
            aria_edit: 'Modifier {name}',
            export_systems: 'Exporter les systèmes',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Matrice de traçabilité',
            page_title: 'Matrice de traçabilité',
            brd_selector: 'Sélectionner le BRD',
            requirement: 'Exigence',
            linked_artifact: 'Artefact lié',
            verification_status: 'Statut de vérification',
            verified_by: 'Vérifié par',
            create_entry: 'Créer une entrée',
            edit_entry: 'Modifier une entrée',
            no_entries_found: 'Aucune entrée trouvée',
            entry_created: 'Entrée créée',
            entry_updated: 'Entrée mise à jour',
            select_brd: 'Sélectionner un BRD...',
            select_status: 'Sélectionner un statut...',
            aria_matrix_table: 'Matrice de traçabilité',
            aria_edit: 'Modifier entrée pour {name}',
            export_matrix: 'Exporter la matrice',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: "ID de l'exigence",
        },
        gap_register: {
            title: 'Registre des écarts',
            page_title: 'Registre des écarts',
            gap_title: "Titre de l'écart",
            gap_type: "Type d'écart",
            severity: 'Gravité',
            status: 'Statut',
            description: 'Descriptif',
            remediation_plan: 'Plan de remédiation',
            assigned_to: 'Assigné à',
            due_date: "Date d'échéance",
            create_gap: 'Créer un écart',
            edit_gap: "Modifier l'écart",
            no_gaps_found: 'Aucun écart trouvé',
            gap_created: 'Écart créé',
            gap_updated: 'Écart mis à jour',
            select_type: 'Sélectionner un type...',
            select_severity: 'Sélectionner la gravité...',
            select_status: 'Sélectionner un statut...',
            aria_gaps_table: 'Registre des écarts',
            aria_edit: 'Modifier {name}',
            export_gaps: 'Exporter les écarts',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Liaison au cadre',
            framework_linkage_placeholder: 'ex. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Portée du cadre',
            framework_scope_placeholder: 'ex. GDPR, PCI-DSS',
            procedure_linkage: 'Liaison de procédure',
            procedure_linkage_placeholder: 'ex. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Source réglementaire',
            regulatory_source_placeholder: 'ex. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Cartographie des contrôles',
            control_mappings_section: 'Mappages de contrôle',
            no_control_mappings: 'Aucun mappage de contrôle pour l\'instant. Cliquez sur "Créer un mappage de contrôle" pour en ajouter un.',
            automation_type_col: 'Automatisation',
        },
        gap_register_extra: {
            framework_filter: 'Cadre',
            framework_id: 'ID du cadre',
            framework_id_placeholder: 'ex. GDPR, ISO 27001',
            all_frameworks: 'Tous les cadres',
        },
        poc_scorecards_extra: {
            criteria_count: 'Critères',
        },
        export_labels: {
            export_brds: 'Exporter les BRD',
            export_decision_records: 'Exporter les registres de décisions',
            export_security_reviews: 'Exporter les revues de sécurité',
            export_poc_scorecards: 'Exporter les fiches POC',
            export_instances: 'Exporter des instances',
            export_definitions: 'Exporter des définitions',
            export_credentials: "Exporter les informations d'identification",
            export_events: 'Exporter des événements',
            exporting: 'Exportation...',
            export_success: 'Exportation terminée',
            export_error: "Échec de l'exportation",
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Extraire les obligations',
            extracting: 'Extraction…',
            results_heading: 'Obligations extraites ({count})',
            tokens_label: '≈ {count} jetons',
            no_obligations: "Aucun modèle d'obligation détecté dans ce texte de contrat.",
            col_class: 'Classe',
            col_summary: 'Résumé (modifiable)',
            col_excerpt: 'Extrait de clause',
            col_due_date: "Date d'échéance (modifiable)",
            col_priority: 'Priorité',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Délai de préavis',
            class_audit: 'Droit de vérification',
            class_insurance: 'Assurance',
            class_sla: 'Réponse SLA',
            class_breach: 'Notification de violation',
            class_renewal: 'Fenêtre de renouvellement',
            citations_label: 'Citations',
            accept_hint: "Accepter créera une tâche par obligation (cap 8) avec la priorité + la date d'échéance affichée.",
            accept_label: 'Accepter les obligations',
            reject_label: 'Rejeter le rapport',
            resolve_summary: 'Résolution : {status} · {tasks} tâche(s) créée(s)',
            error_generic: "Impossible d'extraire les obligations. Réessayez ou vérifiez le texte du contrat.",
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Projet de raisonnement',
            drafting: 'Rédaction du raisonnement…',
            results_heading: 'AI-raisonnement rédigé ({risk_level})',
            tokens_label: '≈ {count} jetons',
            narrative_label: 'Exposé des motifs',
            controls_heading: 'Contrôles compensatoires',
            no_controls: 'Aucun contrôle compensatoire suggéré.',
            citations_label: 'Citations',
            accept_label: 'Accepter le raisonnement',
            reject_label: 'Rejeter le brouillon',
            accept_hint: "Accepter enregistre le raisonnement sur le brouillon persistant pour la lignée IAT. Les contrôles narratif + ne mettent pas à jour automatiquement l'enregistrement d'acceptation : un collage par l'opérateur est requis.",
            resolve_summary: 'Résolution : {status}',
            error_generic: "Impossible de rédiger un raisonnement. Réessayez ou vérifiez l'état d'acceptation du risque.",
        },
        redline_assistant: {
            run_label: 'Redline avec bibliothèque',
            running: 'Exécution de la ligne rouge…',
            results_heading: 'Suggestions de ligne rouge ({count})',
            tokens_label: '≈ {count} jetons',
            no_suggestions: 'Aucune lacune dans la clause standard n’a été détectée.',
            jurisdiction_label: 'Juridiction',
            col_kind: 'Genre',
            col_severity: 'Gravité',
            col_title: 'Suggestions',
            col_excerpt: 'Extrait correspondant',
            col_apply: 'Postuler',
            kind_missing: 'Norme manquante',
            kind_weaker: 'Plus faible que la norme',
            kind_jurisdiction: 'Superposition de juridiction',
            proposed_label: 'Texte proposé',
            citations_label: 'Citations',
            accept_label: 'Accepter la sélection',
            reject_label: 'Rejeter le rapport',
            partial_label: 'Appliquer la sélection',
            accept_hint: "Appliquer la sélection enregistre les suggestions choisies sur le brouillon pour la lignée IAT. Aucune modification automatique n'est appliquée au texte du contrat : l'opérateur détermine la ligne rouge.",
            resolve_summary: 'Résolution : {status} · {count} suggestion(s) appliquée(s)',
            error_generic: "Impossible de produire une ligne rouge. Réessayez ou vérifiez l'état du contrat.",
            clause_library_link: 'Gérer la bibliothèque de clauses',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Bibliothèque de clauses',
            add_label: 'Ajouter une clause',
            edit_label: 'Modifier',
            delete_label: 'Supprimer',
            slug_label: 'Limace',
            title_label: 'Titre',
            body_label: 'Corps',
            jurisdiction_label: 'Juridiction',
            tags_label: 'Balises (séparées par des virgules)',
            must_contain_label: 'Marqueurs obligatoires (séparés par des virgules)',
            severity_label: "Gravité en cas d'absence",
            no_clauses: "Aucune clause pour l'instant. Ajoutez-en un pour amorcer l'assistant Redline.",
            save_label: 'Enregistrer',
            cancel_label: 'Annuler',
            load_error: 'Impossible de charger la bibliothèque de clauses.',
            save_error: "Impossible d'enregistrer la clause.",
            delete_error: 'Impossible de supprimer la clause.',
            delete_confirm: 'Supprimer la clause "{slug}" ? Cela ne peut pas être annulé.',
        },
        subprocessor_impact: {
            assess_label: "Évaluer l'impact",
            assessing: 'Évaluation de l’impact…',
            results_heading: 'Impact sur le sous-traitant secondaire ({severity})',
            tokens_label: '≈ {count} jetons',
            risk_delta_label: 'Delta du risque',
            notification_heading: 'Notification client',
            subject_label: 'Sujet',
            deadline_label: 'Date limite de notification',
            body_label: 'Corps de notification',
            required_fields_label: 'Champs de la notification (article 28(2))',
            mitigation_heading: "Mesures d'atténuation",
            citations_label: 'Citations',
            accept_label: "Accepter l'évaluation",
            reject_label: "Rejeter l'évaluation",
            accept_hint: "Accepter créera une tâche de notification client avec la date limite SLA comme date d'échéance.",
            resolve_summary: 'Résolution : {status}{task}',
            error_generic: "Impossible d'évaluer ce changement de sous-traitant. Réessayez ou vérifiez l'enregistrement des modifications.",
        },
        onboarding_evidence: {
            collect_label: 'Exécuter la collecte de preuves AI',
            collecting: 'Recueillir des preuves…',
            results_heading: 'Collecte de preuves ({tier})',
            tokens_label: '≈ {count} jetons',
            findings_heading: 'Preuve existante',
            missing_heading: 'Objets manquants',
            no_findings: 'Aucune preuve existante détectée.',
            no_missing: 'Aucun artefact requis ne manque pour ce niveau de risque.',
            next_step_label: 'Étape suivante',
            class_dpa: 'Accord de traitement des données',
            class_soc2: 'Rapport SOC2',
            class_iso27001: 'Certificat ISO 27001',
            class_insurance: 'Politique de cyberassurance',
            class_incident_history: 'Incident antérieur',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Accepter le rapport',
            reject_label: 'Rejeter le rapport',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: "Impossible d'exécuter la collecte de preuves AI Réessayez ou vérifiez l'état du dossier d'intégration.",
        },
        no_permission_title: 'Autorisations insuffisantes',
        no_permission_message: "Vous n'êtes pas autorisé à accéder à la gestion des risques liés aux fournisseurs.",
        unknown_vendor: 'Fournisseur inconnu',
        nav: {
            dashboard: 'Tableau de bord des risques',
            scoring: 'Notation des risques',
            questionnaires: "Formulaires d'enquête",
            contracts: 'Contrats',
            gates: "Points d'approbation",
            reviews: 'Évaluations fournisseurs',
            signals: 'Signaux de risque',
            reassessments: 'Réévaluations',
            portfolio: 'Portefeuille de risques',
            acceptances: 'Acceptation des risques',
            review_routing: 'Routage des examens',
            onboarding_cases: "Dossiers d'intégration",
            sub_processor_register: 'Registre des sous-traitants',
        },
        dashboard: {
            title: 'Tableau de bord risque fournisseur',
            page_title: 'Tableau de bord des risques liés aux fournisseurs',
            loading: 'Chargement de dashboard data...',
            risk_tier_header: 'Niveau de risque',
            count_header: 'Compter',
            percentage_header: 'Pourcentage',
            sections_nav: 'Sections sur les risques liés aux fournisseurs',
            urgent_expiry: 'Urgent : expire dans les 14 jours',
            no_previous_tier: 'Aucun niveau précédent',
            view_as_chart: 'Afficher sous forme de graphique',
            view_as_table: 'Afficher sous forme de tableau',
            showing_chart: 'Affichage de la vue graphique',
            showing_table: 'Affichage de la vue tableau',
            load_error: 'Échec de load vendor risk dashboard. Veuillez réessayer.',
            risk_distribution: 'Risque Distribution Aperçu',
            recent_tier_changes: 'Récent Niveau Changements',
            no_tier_changes: 'Non recent niveau changements.',
            review_kpis: 'KPI de révision',
            total_vendors_reviewed: 'Total Fournisseurs Reviewed',
            reviews_by_outcome: 'Révisions par résultat',
            contract_coverage: 'Couverture contractuelle',
            total_vendors: 'Total Fournisseurs',
            with_active_dpa: 'Avec Actif DPA',
            without_dpa: 'Sans DPA',
            dpa_coverage: 'Couverture DPA',
            active_contracts_by_type: 'Actif Contrats par Type',
            expiring_contracts: 'Contrats expirant (90 prochains jours)',
            no_expiring_contracts: 'Aucun contrat expirant dans les 90 prochains jours.',
            vendor: 'Fournisseur',
            previous_tier: 'Précédent Niveau',
            current_tier: 'Actuel Niveau',
            changed_at: 'Modifié le',
            expires_at: 'Expire À',
            days_remaining: 'Jours restants',
            days: 'jours',
            vendors_label: 'fournisseurs',
            no_data: 'Aucune donnée',
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Faible',
            expiring_acceptances_heading: 'Acceptations de risque arrivant à expiration',
            expiring_30d_label: 'Expire dans 30 jours',
            expiring_60d_label: 'Expire dans 60 jours',
            expiring_90d_label: 'Expire dans 90 jours',
        },
        scoring: {
            title: 'Notation des risques',
            page_title: 'Notation des risques liés aux fournisseurs',
            filter_scores: 'Filtrer les scores de risque',
            filter_by_tier: 'Filtrer par niveau de risque',
            scores_pagination: 'Pagination des scores de risque',
            config_dimensions: 'Configuration des dimensions de notation',
            scores_loaded: '{count} scores de risque chargés',
            weight_must_equal_100: 'Le poids total doit être égal à 100 (actuel : {current})',
            out_of_10: 'sur 10, noté {tier}',
            dimension_name: 'Nom de la dimension {index}',
            dimension_weight: '{name} poids (pourcentage)',
            must_equal_100: 'doit être égal à 100',
            config_title: 'Configuration de notation',
            scores_title: 'Fournisseur Risque Scores',
            new_score_title: 'Score un Fournisseur',
            new: 'Nouveau Score',
            vendor_id: 'Fournisseur ID',
            risk_tier: 'Risque Niveau',
            score: 'Score',
            scored_at: 'Noté le',
            no_scores: 'Aucun risk scores trouvé',
            all_tiers: 'Tous les niveaux',
            load_config_error: 'Échec de charge risque scoring configuration. Veuillez réessayer.',
            update_config: 'Mettre à jour la configuration de notation',
            update_config_error: 'Échec de mise à jour risque scoring configuration. Veuillez réessayer.',
            load_scores_error: 'Échec de load vendor risk scores. Veuillez réessayer.',
            calculate_error: 'Échec de calculer vendor risque noter. Veuillez réessayer.',
            weight: 'poids',
            score_range: 'Note (1-5)',
            method: 'Méthode',
        },
        questionnaires: {
            title: 'Questionnaires',
            page_title: 'Questionnaires des fournisseurs',
            filter_questionnaires: 'Filtres de questionnaire',
            questionnaire_views: 'Vues du questionnaire',
            templates_list: 'Liste des modèles de questionnaire',
            instances_list: 'Liste des instances de questionnaire',
            filter_by_status: 'Filtrer par statut du questionnaire',
            templates_per_page: 'Modèles par page',
            instances_per_page: 'Instances par page',
            select_template: 'Sélectionnez un modèle de questionnaire',
            template_category: 'Catégorie de modèle',
            templates_tab: 'Modèles',
            instances_tab: 'Occurrences',
            new: 'Nouveau Questionnaire',
            new_template: 'Nouveau modèle',
            new_instance: 'Nouvelle occurrence',
            vendor_id: 'Fournisseur ID',
            template_id: 'Modèle ID',
            assigned_to: 'Assigné À',
            due_date: "Date d'échéance",
            responses: 'Réponses',
            risk_score: 'Achèvement',
            no_questionnaires: 'Aucun questionnaires trouvé',
            load_templates_error: 'Échec de load questionnaire templates. Veuillez réessayer.',
            load_instances_error: 'Échec de load questionnaires. Veuillez réessayer.',
            create_template_error: 'Échec de create questionnaire template. Veuillez réessayer.',
            initiate_error: 'Échec de initiate questionnaire. Veuillez réessayer.',
            submit_error: 'Échec de submit questionnaire. Veuillez réessayer.',
            review_error: 'Échec de review questionnaire. Veuillez réessayer.',
            resubmit: 'Resoumettre',
            approve_error: "Impossible d'approuver le questionnaire",
            reject_error: 'Impossible de rejeter le questionnaire',
            resubmit_error: 'Impossible de resoumettre le questionnaire',
            templates_loaded: '{count} modèles de questionnaire chargés',
            instances_loaded: '{count} instances de questionnaire chargées',
            fill_responses_cta: 'Remplir les réponses',
            fill_responses_title: 'Remplir les réponses du questionnaire',
            template_missing: 'Modèle introuvable.',
            responses_saved: 'Réponses enregistrées.',
            load_template_error: 'Échec du chargement du modèle.',
            version_history: 'Historique des versions',
            download_previous_version: 'Télécharger la version précédente',
            trigger: 'Déclencheur',
            template_label: 'Modèle',
            reviewer: 'Réviseur',
            reviewer_required: 'Un réviseur doit être désigné pour les questionnaires {trigger} (séparation des tâches).',
            select_option: 'Sélectionnez une option',
            multi_choice_hint: "Sélectionnez tout ce qui s'applique",
            category_initial_onboarding: 'Intégration initiale',
            category_annual_review: 'Bilan annuel',
            category_incident_triggered: 'Déclenché par un incident',
            category_contract_renewal: 'Renouvellement du contrat',
            template_editor: {
                page_title: 'Modifier le modèle de questionnaire',
                preview_page_title: 'Aperçu du modèle de questionnaire',
                edit_structure: 'Modifier la structure',
                preview: 'Aperçu',
                actions: 'Actions',
                back_to_list: 'Retour aux modèles',
                load_error: 'Échec du chargement du modèle',
                save_error: "Échec de l'enregistrement de la structure du modèle",
                saved: 'Structure du modèle enregistrée.',
                sections_heading: 'Sections',
                questions_heading: 'Questions',
                add_section: 'Ajouter une rubrique',
                edit_section: 'Modifier la rubrique',
                delete_section: 'Supprimer la rubrique',
                move_section_up: 'Déplacer la section vers le haut',
                move_section_down: 'Déplacer la section vers le bas',
                confirm_delete_section: 'Supprimer la section "{title}" ? Toutes ses questions seront supprimées.',
                section_title: 'Titre de la section',
                section_description: 'Description de la rubrique',
                section_id: 'Identifiant de la section',
                section_id_hint: 'Généré automatiquement, sauf si vous en définissez un explicitement.',
                no_sections: "Aucune section pour l'instant. Ajoutez une section pour commencer.",
                no_questions: "Aucune question dans cette section pour l'instant.",
                add_question: 'Ajouter une question',
                edit_question: 'Modifier la question',
                delete_question: 'Supprimer la question',
                move_question_up: 'Déplacer la question vers le haut',
                move_question_down: 'Déplacer la question vers le bas',
                confirm_delete_question: 'Supprimer la question "{text}" ?',
                question_text: 'Texte de la question',
                question_type: 'Type de question',
                question_id: 'Numéro de question',
                evidence_required: 'Preuve requise',
                risk_weight: 'Pondération de risque',
                scoring_map: 'Carte de notation',
                scoring_map_json_hint: 'JSON objet mappant les valeurs de réponse aux scores numériques.',
                scoring_map_invalid_json: 'La carte de notation doit être valide JSON.',
                options_heading: 'Options de réponse',
                add_option: 'Ajouter une option',
                remove_option: "Supprimer l'option",
                option_label: 'Étiquette',
                option_value: 'Valeur',
                option_risk_weight: 'Pondération de risque',
                min_options_error: 'Les questions à choix doivent avoir au moins 2 options.',
                option_label_required: 'Chaque option nécessite une étiquette.',
                option_value_required: 'Chaque option nécessite une valeur.',
                option_value_unique: 'Les valeurs des options doivent être uniques.',
                branching_rules_heading: 'Règles de branchement',
                branching_rules_hint: 'Affichez ou masquez cette question en fonction de la réponse à une question précédente.',
                add_branching_rule: 'Ajouter une règle de branchement',
                remove_branching_rule: 'Supprimer la règle de branchement',
                branching_condition_question: 'Si question',
                branching_condition_value: 'a de la valeur',
                branching_action: 'alors',
                branching_action_show: 'montre cette question',
                branching_action_hide: 'cacher cette question',
                branching_select_condition_question: 'Sélectionnez la question précédente',
                branching_select_condition_value: 'Sélectionner une valeur',
                save: 'Enregistrer la structure',
                saving: 'Sauvegarde...',
                cancel: 'Annuler',
                close: 'Fermer',
                question_text_required: 'Le texte de la question est requis.',
                section_title_required: 'Le titre de la section est requis.',
                type_single_choice: 'Choix unique',
                type_multi_choice: 'Choix multiple',
                type_free_text: 'Texte libre',
                type_numeric: 'Numérique',
                type_date: 'Date',
                type_file_upload: 'Téléchargement de fichiers',
                type_yes_no: 'Oui / Non',
                preview_heading: 'Aperçu du modèle',
                preview_subtitle: 'Aperçu en lecture seule de la façon dont le questionnaire apparaît à un fournisseur.',
                preview_no_content: 'Ce modèle ne comporte pas encore de sections ni de questions.',
                preview_branching_notice: "Les règles de branchement sont évaluées lors du remplissage ; l'aperçu montre chaque question.",
            },
            review_modal: {
                title: 'Examiner les réponses au questionnaire',
                open_cta: 'Examen',
                loading: 'Chargement du questionnaire...',
                template_missing: 'Modèle introuvable.',
                load_error: 'Échec du chargement du questionnaire pour examen.',
                annotation_heading: 'Annotations',
                annotation_placeholder: 'Ajoutez une annotation de réviseur pour cette question...',
                annotation_status_label: 'Statut des annotations',
                annotation_status_acceptable: 'Acceptable',
                annotation_status_concern: 'Préoccupation',
                annotation_status_requires_action: 'Nécessite une action',
                review_comments_label: "Commentaires de l'évaluation globale",
                review_comments_placeholder: 'Résumez les résultats de votre examen...',
                justification_label: 'Justification de la décision',
                justification_placeholder: 'Expliquez la justification de votre décision...',
                reject_requires_comments: "Le rejet d'un questionnaire nécessite la révision des commentaires.",
                decision_heading: 'Décision',
                decision: {
                    approved: 'Approuver',
                    rejected: 'Rejeter',
                    additional_info_requested: 'Demander des informations complémentaires',
                },
                submit: "Envoyer l'avis",
                submitting: "Envoi de l'avis...",
                cancel: 'Annuler',
                submit_success: 'Avis soumis.',
                submit_error: "Échec de l'envoi de l'avis.",
                separation_of_duties_error: 'Vous ne pouvez pas consulter un questionnaire qui vous a été demandé de remplir. La séparation des tâches nécessite un réviseur différent.',
                decision_required: 'Veuillez sélectionner une décision de révision.',
                announce_opened: 'Examen modal ouvert.',
                announce_submitted: 'Avis soumis.',
            },
            instance_detail: {
                submitted_at: 'Soumis',
                reviewed_at: 'Révisé',
                reviewer: 'Réviseur',
                review_decision: 'Décision',
                review_comments: 'Examiner les commentaires',
                decision_approved: 'Approuvé',
                decision_rejected: 'Rejeté',
                decision_additional_info_requested: "Plus d'informations demandées",
                decision_pending: "En attente d'examen",
                expand_comments: 'Afficher les commentaires complets',
                collapse_comments: 'Masquer les commentaires complets',
                no_reviewer: 'Aucun réviseur attribué',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Brouillon',
                in_progress: 'En cours',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Approuvé',
                rejected: 'Rejeté',
                expired: 'Expiré',
            },
            export: {
                portfolio_label: "Portefeuille d'exportation",
                instance_label: 'Exporter',
                portfolio_success: "Prêt pour l'exportation du portefeuille.",
                instance_success: 'Exportation du questionnaire prête.',
                portfolio_error: "Échec de l'exportation du portefeuille de questionnaires.",
                instance_error: "Échec de l'exportation du questionnaire.",
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Certaines questions sont masquées par des règles de branchement.',
                cycle_warning: 'Un cycle a été détecté dans les règles de branchement. Les questions concernées sont visibles par défaut.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Contrats',
            page_title: 'Contrats des fournisseurs',
            new: 'Nouveau contrat',
            vendor_id: 'Fournisseur ID',
            filter_contracts: 'Filtrer les contrats',
            filter_by_document_type: 'Filtrer par type de document',
            filter_by_status: 'Filtrer par statut de contrat',
            per_page: 'Contrats par page',
            expired_notice: 'Ce contrat est expiré',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Type de document',
            expiry_date: "Date d'expiration",
            no_contracts: 'Aucun contracts trouvé',
            all_document_types: 'Tous Document Types',
            load_error: 'Échec de load contracts. Veuillez réessayer.',
            create_error: 'Échec de create contract. Veuillez réessayer.',
            delete_error: 'Échec de delete contract. Veuillez réessayer.',
            update_error: 'Échec de la mise à jour du statut. Veuillez réessayer.',
            confirm_delete: 'Supprimer le contrat « {title} » ?',
            renewal_date: 'Date de renouvellement',
            effective_date: "Date d'effet",
            parties: 'Intervenants',
            summary: 'Résumé',
            n_contracts: '{count} contrats',
            n_contract: '{count} contrat',
            contracts_loaded: '{count} contrats chargés',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Avenant de sécurité',
            doc_type_scc_annex: 'Annexe SCC',
            doc_type_sub_processor_list: 'Liste des sous-traitants',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Autre',
            status_draft: 'Brouillon',
            status_under_negotiation: 'En négociation',
            status_pending_signature: 'En attente de signature',
            status_active: 'Actif',
            status_expired: 'Expiré',
            status_terminated: 'Résilié',
            status_superseded: 'Remplacé',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: "Points d'approbation",
            page_title: 'Évaluations des portes',
            new: 'Nouveau Gate Evaluation',
            vendor_id: 'Fournisseur ID',
            gate_name: 'Nom de porte',
            outcome: 'Résultat',
            evaluator: 'Évaluateur',
            no_gates: 'Aucun gate evaluations trouvé',
            load_error: 'Échec de load gate evaluations. Veuillez réessayer.',
            evaluate_error: 'Échec de evaluate vendor gate. Veuillez réessayer.',
            detail_error: 'Échec de fetch evaluation detail. Veuillez réessayer.',
            override_error: 'Échec de override gate evaluation. Veuillez réessayer.',
            criteria_results: 'Critères Résultats',
            evaluate_form: 'Évaluer le portail des fournisseurs',
            per_page: 'Évaluations par page',
            passed: 'Réussi',
            failed: 'Échoué',
            override: 'Remplacement',
            override_justification: 'Justification de remplacement',
            override_placeholder: 'Saisissez justification for overriding this gate evaluation',
            approval_id: 'Approbation ID',
            overall_passed: 'Réussi',
            overall_failed: 'Échoué',
            overall_overridden: 'Contourné',
            evaluations_loaded: '{count} évaluations de porte chargées',
            create_approval_cta: 'Créer une approbation',
        },
        reviews: {
            title: 'Évaluations fournisseurs',
            page_title: 'Avis des fournisseurs',
            new: 'Nouveau examiner',
            vendor_id: 'Fournisseur ID',
            filter_by_outcome: "Filtrer par résultat de l'avis",
            per_page: 'Avis par page',
            kpi_summary: 'Consulter le résumé du KPI',
            review_outcome_label: "Résultat de l'examen",
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: "Modifier le résultat de l'examen",
            reviews_loaded: '{count} avis du fournisseur chargés',
            reviewer: 'Réviseur',
            review_type: 'Examiner Type',
            outcome: 'Résultat',
            findings: 'Constatations',
            no_reviews: 'Aucun vendor reviews trouvé',
            load_kpis_error: 'Échec de fetch vendor review kpis. Veuillez réessayer.',
            load_error: 'Échec de fetch vendor reviews. Veuillez réessayer.',
            update_error: "Impossible de mettre à jour l'avis fournisseur",
            create_error: 'Échec de create vendor review. Veuillez réessayer.',
            total_reviewed: 'Total Fournisseurs Reviewed',
            total_reviews: 'Total de révisions',
            satisfactory: 'Satisfaisant',
            concerns_identified: 'Préoccupations identifiées',
            remediation_required: 'Remédiation Obligatoire',
            escalated: 'Escaladé',
            program_id: 'Programme ID',
            period_id: 'Période ID',
            task_id: 'Tâche ID',
            recommendations: 'Recommandations',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Signaux de risque',
            page_title: 'Signaux de risque',
            new_signal: 'Nouveau signal de risque',
            signal_type: 'Type de signal',
            severity: 'Gravité',
            description: 'Descriptif',
            source: 'Origine',
            vendor_id: 'Identifiant du fournisseur',
            certification_expiry: 'Expiration de certification',
            breach_notification: 'Notification de violation',
            financial_instability: 'Instabilité financière',
            compliance_change: 'Changement de conformité',
            custom: 'Personnalisé',
            active: 'Actif',
            acknowledged: 'Accusé réception',
            resolved: 'Résolu',
            no_signals: 'Aucun signal de risque trouvé',
            source_external_feed: 'Flux externe',
            source_manual: 'Manuel',
            source_integration: 'Intégration',
            source_regulatory: 'Réglementaire',
            source_media: 'Médias',
            trigger_reassessment_prompt: 'Ce signal est de gravité élevée. Lancer une réévaluation du fournisseur maintenant ?',
            reassessment_triggered: 'Réévaluation déclenchée.',
            reassessment_trigger_error: 'Échec du déclenchement de la réévaluation.',
        },
        reassessments: {
            title: 'Tâches de réévaluation',
            page_title: 'Tâches de réévaluation',
            trigger: 'Déclencher une réévaluation',
            trigger_success: 'Réévaluation déclenchée avec succès',
            reason: 'Raison',
            deadline: 'Échéance',
            assigned_to: 'Assigné à',
            vendor_id: 'Identifiant du fournisseur',
            outcome: 'Résultat',
            new_risk_level: 'Nouveau niveau de risque',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Terminer la réévaluation',
            complete_success: 'Réévaluation terminée avec succès',
            pending: 'En attente',
            in_progress: 'En cours',
            completed: 'Terminé',
            cancelled: 'Annulé',
            no_tasks: 'Aucune tâche de réévaluation trouvée',
            risk_trend: 'Tendance du risque',
            notes: 'Remarques',
        },
        portfolio: {
            title: 'Portefeuille de risques',
            page_title: 'Portefeuille de risque',
            dashboard: 'Tableau de bord du portefeuille',
            vendor_id: 'Identifiant du fournisseur',
            total_vendors: 'Total des fournisseurs',
            average_score: 'Score de risque moyen',
            high_risk_count: 'Fournisseurs à haut risque',
            trend_direction: 'Direction de la tendance',
            improved: 'Amélioré',
            stable: 'Stable',
            degraded: 'Dégradé',
            vendor_trend: 'Tendance fournisseur',
            outstanding_questionnaires: 'Questionnaires en suspens',
            overdue_assessments: 'Évaluations en retard',
            no_outstanding: 'Aucun questionnaire en suspens',
            no_overdue: 'Aucune évaluation en retard',
            due_date: "Date d'échéance",
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: "Registre d'acceptation des risques",
            page_title: "Registre d'acceptation des risques",
            new_acceptance: 'Nouvelle acceptation de risque',
            vendor_id: 'Identifiant du fournisseur',
            risk_level: 'Niveau de risque',
            conditions: 'Conditions',
            justification: 'Justification',
            expiry_date: "Date d'expiration",
            create_success: 'Acceptation de risque créée avec succès',
            review_condition: 'Examiner la condition',
            condition_met: 'Condition remplie',
            condition_not_met: 'Condition non remplie',
            review_success: 'Condition examinée avec succès',
            export_register: 'Exporter le registre',
            active: 'Actif',
            expired: 'Expiré',
            revoked: 'Révoqué',
            no_acceptances: 'Aucune acceptation de risque trouvée',
            met_count: '{met}/{total} rencontré',
        },
        review_routing: {
            title: 'Routage des examens',
            page_title: 'Réviser le routage',
            route_to_reviewers: 'Envoyer aux examinateurs',
            route_success: 'Envoyé aux examinateurs avec succès',
            vendor_id: 'Identifiant du fournisseur',
            reviewer_id: "Identifiant de l'examinateur",
            reviewer_role: "Rôle de l'examinateur",
            questionnaire_id: 'Identifiant du questionnaire',
            review_status: "Statut de l'examen",
            reviewers_label: 'Réviseurs (user_id, rôle par ligne)',
            pending: 'En attente',
            in_review: 'En examen',
            completed: 'Terminé',
            no_routing: "Aucun routage d'examen trouvé",
            primary_reviewer: 'Examinateur principal',
            secondary_reviewer: 'Examinateur secondaire',
            approver: 'Approbateur',
        },
        onboarding: {
            title: "Dossiers d'intégration fournisseurs",
            page_title: "Dossiers d'intégration fournisseurs",
            new_case: "Nouveau dossier d'intégration",
            case_detail: "Dossier d'intégration",
            open_mps_dossier_filing: 'Ouvrir un dépôt MPS',
            dossier_filing_description: "Crée un nouveau dépôt MPS pré-rempli à partir de ce cas d'onboarding. Le dépôt sera lié au cas.",
            dossier_filing_dpia_id: 'ID DPIA',
            dossier_filing_dpia_id_placeholder: 'ex. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Titre du dépôt',
            dossier_filing_form_type: 'Type de formulaire',
            dossier_filing_submit: 'Créer le dépôt',
            dossier_filing_validation_error: 'ID DPIA et titre requis.',
            dossier_filing_created: 'Dépôt MPS créé',
            dossier_filing_error: 'Impossible de créer le dépôt MPS.',
            linked_questionnaire_due_date: 'Échéance',
            linked_questionnaire_assignee: 'Attribué à',
            linked_questionnaire_submitted_at: 'Soumis le',
            score_last_scored: 'Dernière notation',
            score_dimensions: 'Dimensions',
            linked_contract_document_type: 'Type de document',
            linked_contract_effective_date: "Date d'entrée en vigueur",
            linked_contract_expiry_date: "Date d'expiration",
            linked_sub_processor_countries: 'Pays de traitement',
            linked_sub_processor_last_assessment: 'Dernière évaluation',
            linked_sub_processor_next_assessment: 'Prochaine évaluation',
            case_id: 'ID du dossier',
            vendor: 'Fournisseur',
            case_type: 'Type de dossier',
            service_description: 'Description du service',
            data_categories: 'Catégories de données',
            systems_accessed: 'Systèmes accédés',
            jurisdiction: 'Juridiction',
            hosting_model: "Modèle d'hébergement",
            controller_role: 'Rôle responsable/sous-traitant',
            status_pipeline: 'Pipeline de statut',
            tabs_label: "Sections du dossier d'intégration",
            per_page: 'Dossiers par page',
            filter_cases: "Filtrer les dossiers d'intégration",
            cases_count: 'dossiers',
            cases_loaded: "dossiers d'intégration chargés",
            no_cases: "Aucun dossier d'intégration trouvé.",
            vendor_lookup_placeholder: 'Entrez ID du fournisseur',
            data_categories_placeholder: 'ex., données_personnelles, financières, santé',
            systems_placeholder: 'ex., CRM, ERP, Système RH',
            status_open: 'Ouvert',
            status_in_progress: 'En cours',
            status_awaiting_approval: "En attente d'approbation",
            status_approved: 'Approuvé',
            status_remediation_required: 'Remédiation requise',
            status_closed: 'Fermé',
            type_new_vendor: 'Nouveau fournisseur',
            type_service_change: 'Changement de service',
            type_renewal: 'Renouvellement',
            type_reassessment: 'Réévaluation',
            tab_overview: 'Aperçu',
            tab_due_diligence: 'Diligence raisonnable',
            tab_security_assessment: 'Évaluation de sécurité',
            tab_contractual_controls: 'Contrôles contractuels',
            tab_sub_processors: 'Sous-traitants',
            tab_approval_gates: "Portes d'approbation",
            tab_evidence: 'Preuves',
            tab_placeholder: "Le contenu de cette section sera complété lors du processus d'intégration.",
            completion_not_started: 'Non démarré',
            completion_in_progress: 'En cours',
            completion_completed: 'Terminé',
            completion_requiring_attention: 'Attention requise',
            gate_name: 'Nom de la porte',
            approver: 'Approbateur',
            decision: 'Décision',
            comments: 'Commentaires',
            decide: 'Prendre une décision',
            decision_approved: 'Approuvé',
            decision_rejected: 'Rejeté',
            decision_deferred: 'Différé',
            decision_pending: 'Pending',
            no_approvals: "Aucune porte d'approbation configurée pour ce dossier.",
            approval_success: "Décision d'approbation enregistrée avec succès",
            approval_error: "Impossible d'enregistrer la décision d'approbation",
            transition_status: 'Changer le statut',
            confirm_transition: 'Changer le dossier de "{from}" à "{to}" ?',
            transition_success: 'Statut du dossier changé avec succès',
            transition_error: 'Impossible de changer le statut du dossier',
            create_success: "Dossier d'intégration créé avec succès",
            create_error: "Impossible de créer le dossier d'intégration",
            load_error: "Impossible de charger les dossiers d'intégration",
            detail_error: 'Impossible de charger les détails du dossier',
            export: 'Exporter',
            export_error: "Impossible d'exporter le dossier d'intégration",
            evidence_tab_description: "Téléversez et exportez les preuves pour le cas d'onboarding.",
            upload_evidence_label: 'Téléverser une preuve',
            attached_evidence_heading: 'Preuves jointes',
            no_evidence_attached: 'Aucune preuve jointe pour le moment.',
            export_bundle_button: 'Exporter le dossier de preuves',
            load_attachments_error: 'Échec du chargement des preuves.',
            linked_questionnaires_heading: 'Questionnaires liés',
            no_linked_questionnaires: 'Aucun questionnaire lié.',
            linked_scores_heading: 'Scores liés',
            no_linked_scores: 'Aucun score lié.',
            latest_score_label: 'Dernier score',
            linked_contracts_heading: 'Contrats liés',
            no_linked_contracts: 'Aucun contrat lié.',
            linked_sub_processors_heading: 'Sous-traitants liés',
            no_linked_sub_processors: 'Aucun sous-traitant lié.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Marquer comme terminé',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Registre des sous-traitants',
            page_title: 'Registre des sous-traitants',
            new_record: 'Nouveau sous-traitant',
            edit_record: 'Modifier le sous-traitant',
            onboarding_case: "Dossier d'intégration",
            services: 'Services fournis',
            data_categories: 'Catégories de données',
            processing_countries: 'Pays de traitement',
            dpa_chain_status: 'Statut de la chaîne DPA',
            certifications: 'Certifications',
            last_assessment: 'Dernière évaluation',
            next_assessment: 'Prochaine évaluation',
            change_history: 'Historique des modifications',
            history: 'Historique',
            changed_by: 'Modifié par',
            comma_separated: 'Valeurs séparées par des virgules',
            filter_records: 'Filtrer les enregistrements',
            filter_by_dpa: 'Filtrer par statut de chaîne DPA',
            all_dpa_statuses: 'Tous les statuts DPA',
            per_page: 'Enregistrements par page',
            records_count: 'sous-traitants',
            records_loaded: 'enregistrements chargés',
            no_records: 'Aucun sous-traitant trouvé.',
            no_history: 'Aucun historique de modifications disponible.',
            dpa_active: 'Actif',
            dpa_pending: 'En attente',
            dpa_expired: 'Expiré',
            dpa_not_required: 'Non requis',
            create_success: 'Sous-traitant créé avec succès',
            create_error: 'Impossible de créer le sous-traitant',
            update_success: 'Sous-traitant mis à jour avec succès',
            update_error: 'Impossible de mettre à jour le sous-traitant',
            load_error: 'Impossible de charger le registre',
            history_error: "Impossible de charger l'historique des modifications",
            export_csv: 'Exporter CSV',
            export_json: 'Exporter JSON',
            export_error: "Impossible d'exporter les données",
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: "Tableau de bord d'orchestration",
            runs: "Exécutions d'orchestration",
            tasks: "Tâches d'exécution",
            templates: 'Modèles de liste de contrôle',
            vendor_requests: 'Demandes fournisseurs',
            proof_chain: 'Chaîne de preuves',
            dsr_integration: 'Intégration DSR',
            vendor_verification: 'Vérification fournisseur',
            completeness: 'Tableau de complétude',
        },
        dashboard: {
            title: "Tableau de bord d'orchestration",
            loading: 'Chargement de dashboard data...',
            active_runs: 'Exécutions actives',
            completion_rate: "Taux d'achèvement",
            avg_completion_time: "Temps moyen d'achèvement",
            overdue_tasks: 'En retard Tâches',
            vendor_compliance: 'Fournisseur Conformité',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Exécutions récentes',
            no_recent_runs: "Aucune exécution récente d'orchestration.",
            run_id: 'Exécuter ID',
            ticket_id: 'ID du ticket',
            progress: 'Progression',
            systems: 'Systèmes',
            vendors: 'Fournisseurs',
            load_error: "Échec du chargement du tableau de bord d'orchestration. Veuillez réessayer.",
            aria_table: "Exécutions d'orchestration récentes",
        },
        runs: {
            title: "Exécutions d'orchestration",
            new: 'Nouveau exécuter',
            run_id: 'Exécuter ID',
            ticket_id: 'ID du ticket',
            progress: 'Progression',
            systems: 'Systèmes',
            vendors: 'Fournisseurs',
            data_subject_ref: 'Données Sujet Ref',
            data_categories: 'Catégories de données',
            enable_proof_chain: 'Activer la chaîne de preuve',
            start: 'Démarrer',
            started: "L'exécution a commencé",
            mark_ready: 'Marquer prêt',
            marked_ready: 'Exécuter marqué comme prêt',
            complete: 'Terminé',
            complete_exceptions: 'Complet avec exceptions',
            pause: 'Pause',
            resume: 'CV',
            cancel: 'Annuler',
            no_runs: 'Aucun orchestration runs trouvé',
            load_error: "Échec du chargement des exécutions d'orchestration. Veuillez réessayer.",
            create_error: "Échec de la création de l'exécution d'orchestration",
            aria_filter_status: "Filtrer par statut d'exécution",
            aria_table: "Exécutions d'orchestration",
            placeholder_ticket_id: 'ex. TKT-000123',
            placeholder_data_subject_ref: 'ex. DS-REF-001',
            placeholder_data_categories: 'ex. personal_data, financial, health',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: "Tâches d'exécution",
            task_id: 'Tâche ID',
            run_id: 'Exécuter ID',
            system_name: 'Système Nom',
            assigned_to: 'Assigné À',
            sla_deadline: 'SLA Échéance',
            progress: 'Progression',
            enter_run_id: 'Saisissez exécuter id à filtrer...',
            select_run: 'Sélectionnez une exécution pour afficher les tâches',
            unassigned: 'Non assigné',
            overdue: 'En retard',
            assign_task: 'Assigner Tâche',
            assignee_id: 'Assigné ID',
            update_status: 'Mettre à jour Statut',
            new_status: 'Nouveau statut',
            no_transitions: 'Cette tâche est dans un état final et ne peut pas changer de statut.',
            select_status_placeholder: 'Sélectionnez un nouveau statut',
            skip_justification: 'Justification de l’omission',
            failure_reason: 'Motif de l’échec',
            no_tasks: 'Aucun execution tasks trouvé',
            new_task: 'Nouvelle tâche',
            system_id: 'ID système',
            data_classification: 'Classification des données',
            load_error: "Échec du chargement des tâches d'exécution. Veuillez réessayer.",
            assign_error: "Échec de l'attribution de la tâche",
            status_error: 'Échec de la mise à jour du statut de la tâche',
            no_tasks_for_run: 'Cette exécution de suppression n’a pas encore de tâches d’exécution.',
            aria_filter_run_id: "Filtrer par ID d'exécution",
            aria_filter_status: 'Filtrer par statut de tâche',
            aria_table: "Tâches d'exécution",
            view_steps: 'Voir les étapes',
            steps_modal_title: 'Étapes d’exécution',
            step_title: 'Étape',
            step_status: 'Statut de l’étape',
            step_notes: 'Remarques',
            step_evidence: 'Fichiers de preuve',
            step_evidence_required: 'Une preuve est requise pour cette étape',
            step_evidence_missing: 'Aucune preuve téléversée',
            step_save: 'Enregistrer l’étape',
            step_saved: 'Étape mise à jour',
            step_save_error: 'Échec de la mise à jour de l’étape',
            create_success: 'Tâche d’exécution créée',
            create_error: 'Échec de la création de la tâche d’exécution',
            step_upload_label: 'Joindre une preuve',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Modèles de liste de contrôle',
            new: 'Nouveau modèle',
            template_id: 'Modèle ID',
            system_type: 'Système Type',
            est_duration: 'Durée est.',
            steps: 'Étapes',
            action_type: "Type d'action",
            instructions: 'Consignes',
            mandatory: 'Obligatoire',
            add_step: 'Ajouter étape',
            step_n: 'Étape',
            no_templates: 'Aucun checklist templates trouvé',
            all_system_types: 'Tous Système Types',
            load_error: 'Échec du chargement des modèles de liste de contrôle. Veuillez réessayer.',
            create_error: 'Échec de la création du modèle de liste de contrôle',
            aria_filter_system_type: 'Filtrer par type de système',
            aria_filter_status: 'Filtrer par statut du modèle',
            aria_table: 'Modèles de liste de contrôle',
            placeholder_name: 'ex. Liste de contrôle de suppression de base de données',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Modèle mis à jour avec succès',
            create_success_msg: 'Modèle créé avec succès',
            update_error: 'Échec de la mise à jour du modèle',
            activate_success: 'Modèle activé avec succès',
            activate_error: 'Échec de l’activation du modèle',
            archive_success: 'Modèle archivé avec succès',
            archive_error: 'Échec de l’archivage du modèle',
            clone_success: 'Modèle cloné avec succès',
            clone_error: 'Échec du clonage du modèle',
        },
        vendor_requests: {
            title: 'Fournisseur Suppression Demandes',
            new: 'Nouveau demande',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Demande ID',
            vendor_name: 'Fournisseur Nom',
            vendor_id: 'Fournisseur ID',
            data_subject_ref: 'Données Sujet Ref',
            data_categories: 'Catégories de données',
            legal_basis: 'Base juridique',
            sla_days: 'Jours SLA',
            requested_completion: 'Achèvement demandé',
            communications: 'Échanges',
            total_requests: 'Total Demandes',
            no_requests: 'Aucun vendor deletion requests trouvé',
            filter_vendor_id: 'Filtrer par Fournisseur ID...',
            change_status: 'Changer le Statut',
            new_status: 'Nouveau Statut',
            actions: 'Actions',
            load_error: 'Échec du chargement des demandes de suppression fournisseur. Veuillez réessayer.',
            create_error: 'Échec de la création de la demande de suppression fournisseur',
            dashboard_error: 'Échec du chargement du tableau de bord de suppression fournisseur. Veuillez réessayer.',
            aria_filter_status: 'Filtrer par statut de la demande',
            aria_filter_vendor_id: 'Filtrer par ID fournisseur',
            aria_table: 'Demandes de suppression fournisseur',
            placeholder_vendor_id: 'ex. VND-001',
            placeholder_vendor_name: 'ex. Acme Analytics',
            placeholder_data_subject_ref: 'ex. DS-REF-001',
            placeholder_data_categories: 'ex. personal_data, analytics',
            placeholder_legal_basis: 'ex. PDPL Article 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Visualiseur de chaîne de preuve',
            enter_run_id: 'Saisissez exécuter id...',
            load_chain: 'Charger la chaîne',
            verify_chain: 'Vérifier la chaîne',
            verifying: 'Vérification...',
            chain_valid: 'La chaîne est valide',
            chain_invalid: 'La chaîne est cassée',
            chain_length: 'Longueur de chaîne',
            broken_at_index: "Cassé à l'index",
            no_proof_records: 'Aucun enregistrement de preuve trouvé pour cette exécution.',
            proof_id: 'ID de preuve',
            entity_type: "Type d'entité",
            entity_id: "ID de l'entité",
            event_hash: "Hash de l'événement",
            previous_hash: 'Hash précédent',
            attestation: "Déclaration sur l'honneur",
            attestation_actor: 'Réalisé par',
            attestation_signed_at: 'Signé le',
            attestation_signature: 'Signature',
            attestation_evidence_refs: 'Références de preuves',
            run_field_label: 'Exécution de suppression',
            select_run: 'Sélectionnez une exécution de suppression…',
            aria_select_run: 'Exécution de suppression pour la recherche de la chaîne de preuves',
            chain_broken_here: 'Chaîne cassée ici',
            load_error: 'Échec du chargement de la chaîne de preuves. Veuillez réessayer.',
            verify_error: 'Échec de la vérification de la chaîne de preuves. Veuillez réessayer.',
            aria_run_id: "ID d'exécution pour la recherche de chaîne de preuves",
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Changer le statut',
            request_type: 'Type de demande',
            data_subject_hint: 'Identifiant de la personne concernée dont les données seront effacées (p. ex. e-mail ou ID client). Correspond généralement au demandeur du dossier DSR sélectionné.',
            title: 'Intégration de suppression DSR',
            create_run: 'Créer une exécution de suppression DSR',
            dsr_case_id: 'ID du dossier DSR',
            data_subject_id: 'ID de la personne concernée',
            systems: 'Systèmes cibles',
            route_to_department: 'Transmettre au département',
            confirm_deletion: 'Confirmer la suppression',
            no_runs: 'Aucune exécution de suppression DSR trouvée',
            status: 'Statut de suppression',
            deadline: 'Date limite',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Vérification de suppression fournisseur',
            create_followup: 'Créer un suivi',
            deadline: 'Date limite de confirmation',
            escalation_contact: "Contact d'escalade",
            escalate: 'Escalader',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Aucun suivi fournisseur trouvé',
            vendor_request: 'Demande fournisseur',
            vendor: 'Fournisseur',
            unknown_vendor: 'Fournisseur inconnu',
            overdue_badge: 'En retard',
            mark_confirmed: 'Marquer comme confirmé',
            mark_confirmed_for: 'Marquer la suppression comme confirmée pour {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Suppression fournisseur confirmée',
            confirmation_failed: 'Échec de la confirmation de la suppression fournisseur',
            select_request: 'Sélectionner une demande fournisseur…',
            escalation_contact_placeholder: 'p. ex. dpo@vendor.example',
            create_success: 'Suivi fournisseur créé avec succès',
            create_failed: 'Échec de la création du suivi fournisseur',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Suppression fournisseur escaladée avec succès',
            escalation_failed: 'Échec de l’escalade de la suppression fournisseur',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Complétude de suppression',
            check_conflicts: 'Vérifier les conflits de conservation',
            no_conflicts: 'Aucun conflit de conservation trouvé',
            conflict_resolution: 'Résolution de conflit',
            extend_retention: 'Prolonger la conservation',
            proceed_deletion: 'Procéder à la suppression',
            escalate_conflict: 'Escalader',
            resolve: 'Résoudre le conflit',
            data_subject_id: 'ID de la personne concernée',
            placeholder_data_subject_id: "Saisir l'ID de la personne concernée",
            data_subject_hint: 'Identifiant de la personne dont vous vérifiez la suppression (p. ex. e-mail, ID client ou référence DSR). Choisissez un sujet connu ou saisissez-en un.',
            guidance: "Saisissez ou sélectionnez un ID de personne concernée, choisissez éventuellement des systèmes, puis lancez la vérification pour voir l'exhaustivité de la suppression et les conflits de conservation.",
            completion: 'Achèvement de la suppression',
            pending_items: 'Éléments en attente',
            system: 'Système',
            policy: 'Politique',
            retention_end: 'Fin de conservation',
            completed_items: 'Éléments Complétés',
            last_review_label: 'Dernier avis',
            days_ago: 'il y a quelques jours',
            review_overdue: 'Cycle de révision dépassé. Réexamen requis.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'En attente',
            planning: 'Planification',
            ready: 'Prêt',
            in_progress: 'En Cours',
            paused: 'En Pause',
            completed: 'Terminé',
            completed_with_exceptions: 'Terminé avec Exceptions',
            cancelled: 'Annulé',
        },
        task_status_labels: {
            pending: 'En Attente',
            assigned: 'Assigné',
            in_progress: 'En Cours',
            awaiting_verification: 'En Attente de Vérification',
            verified: 'Vérifié',
            failed: 'Échoué',
            skipped: 'Ignoré',
        },
        system_type_labels: {
            database: 'Base de Données',
            saas_application: 'Application SaaS',
            file_storage: 'Stockage de Fichiers',
            email_system: 'Système de Messagerie',
            analytics_platform: 'Plateforme Analytique',
            backup_system: 'Système de Sauvegarde',
            vendor_managed: 'Géré par le Fournisseur',
            custom: 'Personnalisé',
        },
        template_status_labels: {
            draft: 'Brouillon',
            active: 'Actif',
        },
        action_type_labels: {
            manual_check: 'Vérification Manuelle',
            api_call: 'Appel API',
            sql_query: 'Requête SQL',
            script_execution: 'Exécution de Script',
            file_deletion: 'Suppression de Fichier',
            verification: 'Vérification',
            notification: 'Notification',
            approval: 'Approbation',
        },
        request_status_labels: {
            draft: 'Brouillon',
            sent: 'Envoyé',
            acknowledged: 'Accusé de réception',
            in_progress: 'En Cours',
            completed: 'Terminé',
            overdue: 'En Retard',
            escalated: 'Escaladé',
            disputed: 'Contesté',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiation',
            data_deletion: 'Suppression de Données',
            completion: 'Achèvement',
            verification: 'Vérification',
            rollback: 'Annulation',
        },
        count_labels: {
            one_template: '1 modèle',
            n_templates: '{n} modèles',
            one_run: '1 exécution',
            n_runs: '{n} exécutions',
            one_task: '1 tâche',
            n_tasks: '{n} tâches',
            one_request: '1 demande',
            n_requests: '{n} demandes',
        },
        aria: {
            completion_rate: 'Taux achèvement: {pct}%',
            vendor_compliance: 'Conformité fournisseur: {pct}%',
            run_progress: 'Progression: {pct}%',
            run_progress_systems: 'Progression: {completed} sur {total} systèmes traités',
            chain_broken: 'Chaîne rompue à cet enregistrement',
            chain_valid: 'Chaîne valide à cet enregistrement',
            chain_unknown: 'Statut de chaîne inconnu',
            escalate_vendor: 'Escalader {id}',
            resolve_conflict: 'Résoudre le conflit pour {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'L’intégralité de la suppression doit être vérifiée au moins tous les 365 jours. Les enregistrements dépassant ce seuil sont signalés pour un nouvel examen obligatoire afin de maintenir la conformité réglementaire.',
            runs_data_d1_lifecycle_info: "Les états d'exécution de l'orchestration correspondent au cycle de vie DATA-D1 : planification = catalogue, prêt = classer, in_progress = appliquer les règles, terminé = preuves générées, completed_with_exceptions = révision requise. Chaque transition est enregistrée dans la chaîne de preuve.",
            vendor_record_linkage_info: "Chaque demande de suppression de fournisseur est liée à son enregistrement de fournisseur via vendor_id. La confirmation d'achèvement nécessite une preuve cryptographique de la chaîne de preuve avant que la demande puisse être marquée comme terminée.",
            templates_internal_classification_info: 'Tous les modèles de liste de contrôle de suppression sont classés comme documents organisationnels internes. Ils ne portent pas d’identifiant de formulaire réglementaire officiel et ne doivent pas être soumis en tant que dépôts réglementaires.',
            proof_chain_evidence_info: "Chaque enregistrement de preuve capture la personne concernée/catégorie, l'horodatage, l'acteur, la base juridique et un hachage SHA-256. Ensemble, ces champs produisent des preuves de suppression cryptographique qui satisfont aux exigences d'audit réglementaire.",
            retention_data_law_info: "Les calendriers de conservation sur ce tableau de bord sont dérivés de la classification Loi sur les données attribuée à chaque catégorie de données. Lorsqu'une classification change, les règles de conservation liées sont mises à jour automatiquement pour garantir l'alignement réglementaire.",
            dsr_erasure_route_info: "Les exécutions d’effacement DSR sont liées au dossier de demande de droits d’origine. Le délai réglementaire (généralement 30 jours en vertu de l'GDPR Art. 17) est suivi automatiquement. Les cas en retard sont signalés pour escalade.",
        },
    },
    discovery: {
        nav: {
            dashboard: 'Tableau de bord de découverte',
            scans: 'Scans de découverte',
            staging_area: 'Zone de transit',
            dedup_rules: 'Règles de déduplication',
            classification_rules: 'Règles de classification',
            suggestions: 'File de suggestions',
            analytics: 'Analytique des suggestions',
            import_templates: "Modèles d'importation",
            connectors: 'Gestion des connecteurs',
            discovery_cycles: 'Cycles de découverte',
            owner_review: 'Revue du propriétaire',
            classification_lineage: 'Historique de classification',
        },
        dashboard: {
            title: 'Tableau de bord de découverte',
            loading: 'Chargement de dashboard data...',
            total_scans: "Total d'analyses",
            active_scans: 'Analyses actives',
            completed_30d: 'Terminé (30d)',
            failed_30d: 'Échoué (30d)',
            systems_discovered: 'Systèmes découverts',
            pending_staged_records: 'En attente Staged Enregistrements',
            recent_scans: 'Analyses récentes',
            no_recent_scans: 'Aucune analyse récente.',
            scan_id: 'Scanner ID',
            discovered: 'Découvert',
            view_all_scans: 'Voir All Scans',
            staging_area: 'Zone de staging',
            suggestion_queue: 'File de suggestions',
            records_reviewed: 'Enregistrements examinés',
            records_approved: 'Enregistrements approuvés',
            exceptions_pending: 'Exceptions en attente',
            view_as_chart: 'Afficher en graphique',
            view_as_table: 'Afficher en tableau',
        },
        scans: {
            title: 'Scans de découverte',
            new: 'Nouveau scanner',
            scan_id: 'Scanner ID',
            scan_type: 'Scanner Type',
            progress: 'Progression',
            discovered: 'Découvert',
            no_scans: 'Aucun discovery scans trouvé',
            all_scan_types: 'Tous Scanner Types',
            source_config: 'Config. source (JSON)',
            invalid_json: 'JSON invalide dans la config. source',
            load_error: 'Échec de load discovery scans. Veuillez réessayer.',
            create_error: 'Échec de create discovery scan. Veuillez réessayer.',
            change_status: 'Changer le statut',
            new_status: 'Nouveau statut',
            actions: 'Actions',
            type_endpoint: 'Point de terminaison',
            type_spreadsheet_import: 'Importation de feuille de calcul',
            type_cmdb_sync: 'Synchronisation CMDB',
            type_cloud_sync: 'Synchronisation cloud',
            status_pending: 'En attente',
            status_running: 'En cours',
            status_completed: 'Terminé',
            status_failed: 'Échoué',
            status_cancelled: 'Annulé',
            source_config_placeholder: '{"hôte": "...", "port": 443}',
            adapter_not_implemented_toast: "Ce type d'analyse n'est pas encore disponible. Seules les analyses de points de terminaison peuvent être créées.",
            adapter_not_implemented_warning: "Pas encore disponible. Les analyses de ce type ne peuvent pas être créées tant que leur prise en charge n'est pas publiée.",
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Zone de transit',
            record_id: 'Enregistrement ID',
            source_scan: 'Analyse source',
            entity_type: "Type d'entité",
            suggested_name: 'Nom suggéré',
            confidence: 'Confiance',
            no_records: 'Aucun staged records trouvé',
            promote: 'Promouvoir',
            discard: 'Rejeter',
            review: 'Examiner',
            merge: 'Fusionner',
            merge_title: "Fusionner l'enregistrement",
            merge_target_id: 'Fusionner Cible ID',
            reject_reason_placeholder: 'Reason pour rejet...',
            promotion_status: 'Statut de promotion',
            dedup_status: 'Statut de dédup.',
            all_source_types: 'Tous les types de source',
            all_promotion_statuses: 'Tous les statuts de promotion',
            all_dedup_statuses: 'Tous les statuts de dédup.',
            load_error: 'Échec de load staged records. Veuillez réessayer.',
            promote_error: 'Échec de promote record. Veuillez réessayer.',
            reject_error: 'Échec de rejeter enregistrer. Veuillez réessayer.',
            merge_error: 'Échec de merge record. Veuillez réessayer.',
            review_error: 'Échec de marquer enregistrer pour examen. Veuillez réessayer.',
            bulk_promote_error: 'Échec de bulk promote records. Veuillez réessayer.',
            bulk_reject_error: 'Échec de bulk reject records. Veuillez réessayer.',
            service_type_web_server: 'Serveur web',
            service_type_database: 'Base de données',
            service_type_api_gateway: 'Passerelle API',
            service_type_storage: 'Stockage',
            service_type_authentication: 'Authentification',
            service_type_analytics: 'Analytique',
            service_type_unknown: 'Inconnu',
            source_type_endpoint_scan: 'Scan de point de terminaison',
            source_type_spreadsheet: 'Feuille de calcul',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Inventaire cloud',
            promotion_status_new: 'Nouveau',
            promotion_status_under_review: 'En cours de révision',
            promotion_status_promoted: 'Promu',
            promotion_status_rejected: 'Rejeté',
            promotion_status_merged: 'Fusionné',
            dedup_status_unmatched: 'Sans correspondance',
            dedup_status_potential_match: 'Correspondance potentielle',
            dedup_status_exact_match: 'Correspondance exacte',
            dedup_status_resolved: 'Résolu',
            confirm_bulk_reject: 'Êtes-vous sûr de vouloir rejeter les enregistrements sélectionnés ?',
            scan_batch_id: 'ID de lot',
            promote_to_registry_title: 'Promouvoir au registre de données',
            promote_modal_instructions: "Crée un élément du registre de données avec collection_method : lien d'observation et de provenance vers cet enregistrement de découverte. L'élément commence au statut de brouillon pour révision.",
            promote_label_category: 'Catégorie',
            promote_label_name_override: 'Nom (remplacement facultatif)',
            placeholder_name_override: "La valeur par défaut est le nom/nom d'hôte de l'enregistrement de découverte",
            promote_label_sensitivity: 'Sensibilité proposée',
            inherit_from_category: 'Hériter de la catégorie',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Règles de déduplication',
            new: 'Nouveau Rule',
            rule_name: 'Nom de la règle',
            match_fields: 'Correspondance Champs',
            threshold: 'Seuil',
            no_rules: 'Aucun dedup rules trouvé',
            seed_defaults: 'Valeurs initiales par défaut',
            match_type: 'Correspondance Type',
            similarity_threshold: 'Seuil de similarité',
            field_name: 'Champ Nom',
            load_error: 'Échec de load dedup rules. Veuillez réessayer.',
            create_error: 'Échec de create dedup rule. Veuillez réessayer.',
            toggle_error: 'Échec de toggle dedup rule. Veuillez réessayer.',
            seed_error: 'Échec de seed defaults. Veuillez réessayer.',
            priority: 'Priorité',
            enabled_status: 'Activé',
            disabled_status: 'Désactivé',
            default_badge: 'Par défaut',
            enable: 'Activer',
            disable: 'Désactiver',
            match_type_exact: 'Exact',
            match_type_fuzzy: 'Approximatif',
            match_type_regex: 'Expression régulière',
            match_type_ip_range: 'Plage IP',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Règles de classification',
            new: 'Nouveau Rule',
            rule_name: 'Nom de la règle',
            pattern: 'Modèle',
            classification: 'Classement',
            no_rules: 'Aucun classification rules trouvé',
            seed_defaults: 'Valeurs initiales par défaut',
            run_rules_now: 'Exécuter les règles maintenant',
            run_rules_success: 'Génération de {count} nouvelle(s) suggestion(s) de classification',
            run_rules_no_results: 'Aucune nouvelle suggestion générée : toutes les règles de correspondance ont déjà des suggestions en attente',
            run_rules_error: "Échec de l'exécution des règles de classification. Veuillez réessayer.",
            taxonomy_id: 'Taxonomie ID',
            level_id: 'Niveau ID',
            condition_logic: 'Logique de condition',
            weight: 'Poids',
            conditions: 'Prérequis',
            condition_field: 'Champ',
            condition_operator: 'Opérateur',
            condition_value: 'Valeur',
            effectiveness: 'Efficacité',
            match_count: 'Correspondances',
            approval_count: 'Approbations',
            rejection_count: 'Rejets',
            filter_taxonomy: 'Filtrer par Taxonomie ID...',
            load_error: 'Échec de charge classification rules. Veuillez réessayer.',
            create_error: 'Échec de create classification rule. Veuillez réessayer.',
            seed_error: 'Échec de seed defaults. Veuillez réessayer.',
            toggle_error: 'Échec de toggle rule. Veuillez réessayer.',
            priority: 'Priorité',
            enabled_status: 'Activé',
            disabled_status: 'Désactivé',
            enable: 'Activer',
            disable: 'Désactiver',
            logic_and: 'ET',
            logic_or: 'OU',
            operator_equals: 'Égal à',
            operator_contains: 'Contient',
            operator_matches_regex: 'Correspond au Regex',
            operator_in_list: 'Dans la liste',
            operator_not_equals: 'Différent de',
            operator_not_contains: 'Ne contient pas',
            enabled_label: 'Activé',
            disabled_label: 'Désactivé',
            taxonomy_label: 'Taxonomie',
            operator_label: 'Opérateur',
            value_label: 'Valeur',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'File de suggestions',
            entity_type: "Type d'entité",
            suggested_name: 'Nom suggéré',
            source: 'Provenance',
            confidence: 'Confiance',
            accept: 'Accepter',
            reject: 'Rejeter',
            no_suggestions: "Aucune suggestion en file d'attente.",
            modify_approve: 'Modifier et approuver',
            delegate: 'Déléguer',
            delegate_user_id: 'Déléguer Utilisateur ID',
            new_level_id: 'Nouveau niveau id',
            reject_reason_placeholder: 'Reason pour rejet...',
            current_level: 'Actuel Niveau',
            suggested_level: 'Niveau suggéré',
            filter_taxonomy: 'Filtrer par Taxonomie ID...',
            all_confidence: 'Toutes les bandes de confiance',
            load_error: 'Échec de load suggestions. Veuillez réessayer.',
            approve_error: 'Échec de approve suggestion. Veuillez réessayer.',
            reject_error: 'Échec de reject suggestion. Veuillez réessayer.',
            modify_error: 'Échec de modify & approve suggestion. Veuillez réessayer.',
            delegate_error: 'Échec de delegate suggestion. Veuillez réessayer.',
            bulk_approve_error: 'Échec de bulk approve suggestions. Veuillez réessayer.',
            bulk_reject_error: 'Échec de bulk reject suggestions. Veuillez réessayer.',
            suggestion_type_new_label: 'Nouvelle étiquette',
            suggestion_type_validation: 'Validation',
            suggestion_type_reclassification: 'Reclassement',
            status_pending: 'En attente',
            status_approved: 'Approuvé',
            status_rejected: 'Rejeté',
            status_superseded: 'Remplacé',
            confidence_high: 'Élevée',
            confidence_medium: 'Moyenne',
            confidence_low: 'Faible',
            confirm_bulk_reject: 'Êtes-vous sûr de vouloir rejeter les suggestions sélectionnées ?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Toutes les taxonomies',
        },
        analytics: {
            title: 'Analytique des suggestions',
            total_suggestions: 'Total de suggestions',
            accepted: 'Accepté',
            rejected: 'Rejeté',
            pending: 'En attente',
            acceptance_rate: "Taux d'acceptation",
            by_entity_type: "Par type d'entité",
            by_source: 'Par source',
            avg_decision_time: 'Avg Décision Heure',
            rule_effectiveness: 'Efficacité de la règle',
            by_status: 'Par Statut',
            by_confidence: 'Par bande de confiance',
            load_error: 'Échec de load suggestion analytics. Veuillez réessayer.',
            confidence_high: 'Élevée',
            confidence_medium: 'Moyenne',
            confidence_low: 'Faible',
            by_confidence_band: 'Par niveau de confiance',
            approval_rate: "Taux d'approbation",
            by_label_state: "Par état d'étiquette",
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: "Modèles d'importation",
            new: 'Nouveau modèle',
            template_name: 'Modèle Nom',
            file_format: 'Fichier Formater',
            column_mappings: 'Colonne Correspondances',
            no_templates: 'Aucun import templates trouvé',
            source_column: 'Colonne source',
            target_field: 'Cible Champ',
            transform: 'Transformer',
            source_column_label: 'Colonne source',
            target_field_label: 'Champ cible',
            transform_label: 'Transformer',
            confirm_delete: 'Êtes-vous sûr de vouloir supprimer this import template ?',
            load_error: 'Échec de load import templates. Veuillez réessayer.',
            save_error: 'Échec de save import template. Veuillez réessayer.',
            delete_error: 'Échec de delete import template. Veuillez réessayer.',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Gestion des connecteurs',
            new: 'Nouveau connecteur',
            connector_name: 'Connecteur Nom',
            connector_type: 'Catégorie',
            last_sync: 'Dernier Synchroniser',
            no_connectors: 'Aucun connectors trouvé',
            cmdb_syncs: 'Synchro. CMDB',
            cloud_syncs: 'Synchro. cloud',
            new_sync: 'Nouveau synchroniser',
            no_syncs: 'Aucun syncs trouvé',
            cmdb_error: 'Échec de load cmdb scans. Veuillez réessayer.',
            cloud_error: 'Échec de load cloud scans. Veuillez réessayer.',
            trigger_error: 'Échec de trigger sync. Veuillez réessayer.',
            trigger_sync_error: 'Échec de trigger {type}',
            cmdb_section_title: 'Synchronisations CMDB',
            cloud_section_title: 'Synchronisations cloud',
            column_connector: 'Connecteur',
            action_start: 'Démarrer',
            confirm_cancel_sync: 'Annuler cette synchronisation ?',
            start_error: 'Échec du démarrage de la synchronisation',
            cancel_error: 'Échec de l’annulation de la synchronisation',
            type_endpoint: 'Analyse de point de terminaison',
            type_spreadsheet_import: 'Import de feuille de calcul',
            type_cmdb_sync: 'Synchronisation CMDB',
            type_cloud_sync: 'Synchronisation cloud',
        },
        discovery_cycles: {
            title: 'Cycles de découverte',
            new_cycle: 'Nouveau cycle de découverte',
            cycle_name: 'Nom du cycle',
            scope_type: 'Type de portée',
            scope_ids: 'Identifiants de portée',
            cadence_days: 'Fréquence (jours)',
            status: 'Statut',
            no_cycles: 'Aucun cycle de découverte trouvé',
            compare: 'Comparer les cycles',
            delta_report: 'Rapport des modifications',
            new_records: 'Nouveaux enregistrements',
            changed_records: 'Enregistrements modifiés',
            removed_records: 'Enregistrements supprimés',
            schedule: 'Planifier une analyse',
            scope_organization: 'Organisation',
            scope_department: 'Département',
            scope_system: 'Système',
            scope_systems: 'Systèmes',
            bulk_actions: 'Actions en masse',
            selected: '{count} sélectionné(s)',
            bulk_create: 'Création en masse',
            bulk_assign_scope: 'Attribuer la portée en masse',
            bulk_advance: 'Avancer le statut en masse',
            confirm_bulk_action: "Confirmer l'action en masse",
            bulk_confirm_message: 'Êtes-vous sûr de vouloir effectuer cette action ?',
            cycles_affected: '{count} cycles seront affectés',
            select_all: 'Tout sélectionner',
            select_cycle: 'Sélectionner le cycle {name}',
            from_template: 'à partir du modèle',
            bulk_results: 'Résultats en masse',
            failed_label: 'échoué',
            template_id: 'ID du modèle',
            count: 'Nombre',
            advance_to: 'Statut cible',
            bulk_success: 'Opération en masse terminée',
        },
        owner_review: {
            title: 'Tâches de revue du propriétaire',
            assign_owner: 'Attribuer au propriétaire',
            owner_id: 'Propriétaire',
            decision: 'Décision',
            approve: 'Approuver',
            modify: 'Modifier',
            reject: 'Rejeter',
            escalate: 'Escalader',
            no_tasks: 'Aucune tâche de revue trouvée',
            bulk_resolve: 'Résolution en masse',
            exceptions: "Éléments d'exception",
            no_exceptions: "Aucun élément d'exception trouvé",
            record_id_label: "Identifiant de l'enregistrement",
            selected: 'sélectionné',
            notes: 'Remarques',
            select_all: 'Tout Sélectionner',
            select: 'Sélectionner',
            confirm_resolve: 'Confirmer',
        },
        classification_lineage: {
            title: 'Historique de classification',
            link_suggestion: "Lier à l'étiquette proposée",
            suggestion_id: 'Suggestions',
            proposed_label_id: 'Étiquette proposée',
            no_lineage: "Aucune donnée d'historique trouvée",
            full_lineage: 'Historique complet',
            record_id_label: "Identifiant de l'enregistrement",
            staged_record_label: 'Enregistrement mis en scène',
            enter_staged_record_id: "Saisissez l'ID de l'enregistrement intermédiaire",
            scan_id_label: "Numériser l'identifiant",
            select_record: 'Sélectionner un enregistrement',
            select_suggestion: 'Sélectionner une suggestion',
            select_label: 'Sélectionner une étiquette',
        },
        common_labels: {
            n_rules: '{n} règles',
            one_rule: '1 règle',
            n_scans: '{n} analyses',
            one_scan: '1 analyse',
            n_syncs: '{n} synchronisations',
            one_sync: '1 synchronisation',
            n_records: '{n} enregistrements',
            one_record: '1 enregistrement',
            n_templates: '{n} modèles',
            one_template: '1 modèle',
            n_suggestions: '{n} suggestions',
            one_suggestion: '1 suggestion',
            n_selected: '{n} sélectionné',
            dashboard_loaded: 'Tableau de bord de découverte chargé',
            n_scans_loaded: '{n} analyses chargées',
            n_staged_records_loaded: '{n} enregistrements intermédiaires chargés',
            confirm_delete_import_template: "Êtes-vous sûr de vouloir supprimer ce modèle d'importation ?",
            invalid_json_source_config: 'JSON non valide dans source_config',
            scope_organization: 'Organisation',
            scope_department: 'Département',
            scope_system: 'Système',
            aria_filter_scans: 'Filtrer les analyses',
            aria_filter_staged_records: 'Filtrer les enregistrements intermédiaires',
            aria_filter_suggestions: 'Filtres',
            aria_filter_by_taxonomy_id: 'Filtrer par ID de taxonomie',
            aria_filter_by_enabled_status: 'Filtrer par statut activé',
            aria_filter_by_source_type: 'Filtrer par type de source',
            aria_filter_by_promotion_status: 'Filtrer par statut de promotion',
            aria_filter_by_dedup_status: 'Filtrer par statut de déduplication',
            aria_filter_by_scan_type: "Filtrer par type d'analyse",
            aria_filter_by_status: 'Filtrer par statut',
            aria_filter_by_confidence: 'Filtrer par bande de confiance',
            aria_select_all_records: 'Sélectionnez tous les enregistrements',
            aria_select_record: "Sélectionner l'enregistrement {id}",
            aria_select_all_suggestions: 'Sélectionnez toutes les suggestions',
            aria_select_suggestion: 'Sélectionnez une suggestion {id}',
            aria_promote_record: "Promouvoir l'enregistrement {id}",
            aria_discard_record: "Supprimer l'enregistrement {id}",
            aria_merge_record: "Fusionner l'enregistrement {id}",
            aria_review_record: "Vérifier l'enregistrement {id}",
            aria_accept_suggestion: 'Accepter la suggestion {id}',
            aria_reject_record: "Rejeter l'enregistrement",
            aria_merge_record_dialog: "Fusionner l'enregistrement",
            aria_create_new_scan: 'Créer une nouvelle analyse',
            aria_sync_progress: 'Progression de la synchronisation : {processed} éléments traités sur {total}',
            aria_scan_progress: "Progression de l'analyse : {processed} éléments traités sur {total}",
            aria_confidence: 'Confiance : {pct} %',
            aria_effectiveness: 'Efficacité : {pct}',
            aria_promote_n_records: 'Promouvoir {n} enregistrements sélectionnés',
            aria_discard_n_records: 'Supprimer {n} enregistrements sélectionnés',
            placeholder_pii_detection_rule: 'par exemple, règle de détection des informations personnelles',
            placeholder_tax_id: 'par exemple, TAX-001',
            placeholder_lvl_id: 'par exemple, LVL-001',
            placeholder_service_type: 'par exemple, service_type',
            placeholder_database: 'par exemple, base de données',
            placeholder_hostname_match: "par exemple, correspondance exacte du nom d'hôte",
            placeholder_hostname: "par exemple, le nom d'hôte",
            placeholder_hr_systems_csv: 'par exemple, systèmes RH CSV Importer',
            placeholder_server_name: 'par exemple, le nom du serveur',
            placeholder_hostname_field: "par exemple, le nom d'hôte",
            placeholder_lowercase: 'par exemple, minuscule',
            placeholder_source_config: '{"hôte": "...", "port": 443}',
            placeholder_stg_id: 'par exemple, STG-000123',
            placeholder_lvl_002: 'par exemple, LVL-002',
            placeholder_usr_id: 'par exemple, USR-000123',
            decision_for: 'Décision pour {id}',
        },
        exports: {
            title: 'Exporter',
            inventory: 'Inventaire des données',
            classification_register: 'Registre de classification',
            raci: 'Matrice RACI',
            exceptions: "Rapport d'exceptions",
            evidence_pack: 'Dossier de preuves',
            delta_report: 'Rapport des modifications',
            executive_summary: 'Résumé exécutif',
            export_button: 'Exporter',
            downloading: 'Téléchargement en cours...',
            download_complete: 'Téléchargement terminé.',
            download_error: 'Échec du téléchargement. Veuillez réessayer.',
        },
        flags: {
            title: 'Marqueurs en aval',
            pending: 'Marqueurs en attente',
            cross_border_review_needed: 'Examen transfrontalier requis',
            sharing_disclosure_needed: 'Divulgation de partage requise',
            dpia_review_needed: 'Examen AIPD requis',
            acknowledge: 'Accuser réception',
            resolve: 'Résoudre',
            no_flags: 'Aucun marqueur en aval.',
        },
        delta: {
            title: 'Résumé des modifications',
            new_count: '{n} nouveaux',
            removed_count: '{n} supprimés',
            changed_count: '{n} modifiés',
            re_review_triggered: 'Réexamen déclenché',
            create_re_review: 'Créer un réexamen',
            recent_alerts: 'Alertes de modifications récentes',
            no_alerts: 'Aucune alerte de modification récente.',
            threshold_exceeded: 'Seuil de modification dépassé (>10 %)',
        },
        regulatory: {
            dashboard_governance_info: "Ce tableau de bord affiche l'état de gouvernance au niveau du cycle : systèmes analysés, enregistrements mis en scène, enregistrements examinés, enregistrements approuvés et exceptions en attente. Cela correspond aux critères de réussite des responsables de gouvernance : « tableau de bord en direct indiquant qui a mis à jour son inventaire, sa classification et ses journaux de partage » (P-VN-05).",
            export_artifacts_info: "Les exportations de découverte incluent tous les types d'artefacts requis par UC-VN-10 et DATA-EVD-01 : inventaire des données approuvées, registre de classification, mappage de propriété/RACI, file d'attente d'exceptions, résumé, ensemble de preuves et rapport delta.",
            scan_inventory_pipeline_info: "Chaque analyse terminée produit des enregistrements compatibles avec le modèle vn_data_law_data_inventory_register (exigence 88dd9877). Les actifs de données découverts remplissent automatiquement le registre d'inventaire des données pour satisfaire à l'obligation de la loi sur les données de maintenir un inventaire des données numériques.",
            connector_linkage_info: "Chaque configuration de connecteur doit inclure : le type de système (par exemple, CMDB, plate-forme cloud), la référence du magasin de données (base de données, magasin de fichiers, SaaS) et la liaison des processus métier (UC-VN-10 enregistrements de support requis). Ces champs garantissent que les actifs découverts sont traçables jusqu'à leur système source et leur contexte commercial.",
            owner_routing_info: "Les tâches de révision sont acheminées vers le bon service ou propriétaire de l'application en fonction de la propriété du système/des données (UC-VN-10 étape 5). Le routage utilise le registre de propriété du système, et non une affectation ad hoc.",
            owner_actions_audit_info: "Les propriétaires peuvent approuver, modifier, rejeter ou faire remonter chaque classification (UC-VN-10 étape 6). Chaque action capture l'acteur, l'horodatage et la raison, produisant ainsi des événements de preuve d'audit de gouvernance conformément à l'exigence f12fc090 (attestation annuelle de gouvernance). La piste d'audit montre qui a examiné, ce qui a été modifié et quand.",
            escalation_timers_info: "Minuteries de remontée : le délai d'examen par le propriétaire est de 5 jours ouvrables ; l'escalade des exceptions est de 48 heures (minuteurs UC-VN-10). Les éléments non résolus sont automatiquement signalés à l'expiration du délai. Les minuteries sont configurables par politique organisationnelle.",
            original_suggestion_preserved_info: "La suggestion originale de classification de machine/système est conservée à côté de chaque remplacement par le propriétaire (exigence de preuve de UC-VN-10). La suggestion originale n'est jamais écrasée et reste visible à des fins d'audit.",
            dedup_lineage_info: "Les règles de déduplication empêchent les entrées d'inventaire en double lorsque la même ressource de données est découverte via plusieurs analyses ou connecteurs. Les métadonnées d'analyse les plus récentes sont conservées en tant qu'enregistrement principal tout en conservant les références de traçabilité à toutes les découvertes antérieures pour la traçabilité de l'audit.",
            label_states_info: "Analytics distingue 4 états d'étiquette pour l'examen de la gouvernance : suggéré par la machine (en attente), approuvé par le propriétaire, modifié par le propriétaire et rejeté. Ces états permettent aux auditeurs d'évaluer la précision de l'automatisation et l'engagement des propriétaires.",
            import_pipeline_info: "Les modèles d'importation doivent produire des enregistrements compatibles avec le pipeline de découverte. Les enregistrements importés entrent en préparation avec les mêmes exigences en matière de métadonnées que les enregistrements analysés : identification du système source, ID de lot et horodatage. Les mappages de colonnes doivent cibler les champs définis dans vn_data_law_data_inventory_register.",
            lineage_full_path_info: "La lignée de classification retrace le chemin complet : analyse/importation -> préparation -> examen du propriétaire -> étiquette approuvée -> entrée de registre (UC-VN-10 étapes 1 à 10). Chaque étape affiche l'ID de l'enregistrement, l'acteur, l'horodatage et la transition d'état pour une traçabilité complète de l'audit.",
            staging_workflow_info: "Les enregistrements intermédiaires se distinguent visuellement des entrées d'inventaire approuvées par leur badge de statut de promotion (UC-VN-10 étape 3). Chaque enregistrement conserve les métadonnées de provenance : système source, ID du lot d'analyse et horodatage de découverte pour une traçabilité complète de l'audit.",
            suggestion_data_d1_info: "Les suggestions s'alignent sur la procédure de classification de la loi sur les données DATA-D1 : (1) cataloguer les actifs de données, (2) classer par niveau de sensibilité, (3) définir des règles de traitement. Chaque suggestion comprend des dimensions de sensibilité et d'importance selon l'exigence 18e4bfd1.",
            downstream_flags_info: 'Les éléments marqués pour le partage, le transfert transfrontalier ou le traitement à haut risque déclenchent des flux de travail en aval liés : évaluation du transfert transfrontalier, évaluation des risques de partage et examen de DPIA (UC-VN-10 étape 8).',
            delta_detection_info: "Les cycles de découverte prennent en charge la détection delta selon l'UC-VN-10 étape 11 : chaque cycle indique quels enregistrements sont nouveaux, modifiés ou supprimés depuis la dernière référence approuvée. Les modifications importantes (delta > 10 %) déclenchent automatiquement des workflows de réexamen.",
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Échec de charge. Veuillez réessayer.',
            save: 'Échec de save. Veuillez réessayer.',
            fetch_escalation_log_failed: 'Échec de fetch escalation log. Veuillez réessayer.',
            fetch_activations_failed: 'Échec de fetch activations. Veuillez réessayer.',
            deactivate_failed: 'Échec de désactiver. Veuillez réessayer.',
            complete_activation_failed: 'Échec de complete activation. Veuillez réessayer.',
            fetch_playbook_templates_failed: 'Échec de fetch playbook templates. Veuillez réessayer.',
            create_playbook_template_failed: 'Échec de create playbook template. Veuillez réessayer.',
            clone_template_failed: 'Échec de clone template. Veuillez réessayer.',
            update_status_failed: 'Échec de la mise à jour du statut. Veuillez réessayer.',
            deprecate_template_failed: 'Échec de deprecate template. Veuillez réessayer.',
            seed_defaults_failed: 'Échec de seed defaults. Veuillez réessayer.',
            fetch_regulator_exports_failed: 'Échec de fetch regulator exports. Veuillez réessayer.',
            create_regulator_export_failed: 'Échec de create regulator export. Veuillez réessayer.',
            update_submission_status_failed: 'Échec de update submission status. Veuillez réessayer.',
            fetch_response_metrics_failed: 'Échec de fetch réponse métriques. Veuillez réessayer.',
            fetch_sla_dashboard_failed: 'Échec de fetch sla dashboard. Veuillez réessayer.',
            fetch_sla_definitions_failed: 'Échec de fetch sla definitions. Veuillez réessayer.',
            create_sla_definition_failed: 'Échec de create sla definition. Veuillez réessayer.',
            seed_sla_defaults_failed: 'Échec de seed sla defaults. Veuillez réessayer.',
            fetch_vendor_coordination_failed: 'Échec de fetch vendor coordination. Veuillez réessayer.',
            fetch_vendor_detail_failed: 'Échec de fetch vendor detail. Veuillez réessayer.',
            create_vendor_coordination_failed: 'Échec de create vendor coordination. Veuillez réessayer.',
            add_communication_failed: 'Échec de add communication. Veuillez réessayer.',
            add_info_request_failed: 'Échec de add info request. Veuillez réessayer.',
            add_action_request_failed: 'Échec de add action request. Veuillez réessayer.',
        },
        nav: {
            playbooks: 'Modèles de guide opérationnel',
            sla_definitions: 'Définitions SLA',
            sla_dashboard: 'Tableau de bord SLA',
            escalation_log: "Journal d'escalade",
            regulator_exports: 'Exports réglementaires',
            deadlines: 'Notification Échéances',
            vendor_coordination: 'Coordination fournisseurs',
            metrics: 'Métriques de réponse',
            playbook_links: 'Liens Playbook',
            regulatory_sla: 'SLA réglementaire',
            coordination_dashboard: 'Coordination',
            evidence_chain: 'Chaîne de preuves',
            post_review: 'Revue post-incident',
        },
        playbooks: {
            page_title: 'Modèles de manuels de jeu',
            activations_page_title: 'Activations du manuel de jeu',
            title: 'Modèles de guide opérationnel',
            new: 'Nouveau modèle',
            seed_defaults: 'Valeurs initiales par défaut',
            scenario_type: 'Type de scénario',
            severity_applicability: 'Applicabilité de la gravité',
            all_scenario_types: 'Tous les types de scénario',
            new_playbook: 'Nouveau Playbook Template',
            clone: 'Cloner',
            clone_template: 'Cloner Modèle',
            template_name: 'Nom du modèle',
            deprecate: 'Rendre obsolète',
            confirm_deprecate: 'Rendre le modèle « {name} » obsolète ?',
            create_template: 'Ajouter modèle',
            update_status: 'Mettre à jour Statut',
            no_templates: 'Aucun playbook templates trouvé',
            deactivation_reason_placeholder: 'Motif de désactivation...',
            description_placeholder: 'Description du livre de procédures...',
            name_placeholder: 'Nom du livre de procédures...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'Définitions SLA',
            title: 'Définitions SLA',
            new: 'Nouveau SLA Definition',
            sla_name: 'SLA Nom',
            target_hours: 'Heures cibles',
            escalation_after: 'Escalade Après',
            applies_to: "S'applique à",
            no_definitions: 'Aucun sla definitions trouvé',
            target_value_placeholder: 'Cible valeur',
            name_placeholder: 'Nom du SLA...',
            threshold_hours_label: 'Heures seuil',
            severity_label: 'Gravité',
            all_types: 'Tous les types',
            all_severities: 'Toutes les gravités',
            default_label: 'Par défaut',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Conforme',
            page_title: 'Tableau de bord SLA',
            title: 'Tableau de bord SLA',
            loading: 'Chargement de sla data...',
            compliance_rate: 'Conformité Taux',
            breached: 'Violé',
            at_risk: 'À Risque',
            on_track: 'Sur Suivre',
            by_severity: 'Par Gravité',
            total_records: 'Total des enregistrements',
            met: 'Respecté',
            sla_records: 'Enregistrements SLA',
            sla_reference: 'Référence SLA',
            record_id: "ID d'enregistrement",
            metric_type: 'Type de métrique',
            severity: 'Gravité',
            elapsed: 'Écoulé',
            clock_start: 'Début du chrono',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: "Journal d'escalade",
            title: "Journal d'escalade",
            incident_id: "ID d'incident",
            escalated_by: 'Escaladé Par',
            escalated_to: 'Escaladé À',
            reason: 'Motif',
            escalated_at: 'Escaladé À',
            no_escalations: 'Aucun escalations trouvé',
            filter_incident_id: 'Filtrer par Incident ID',
            escalation_id: "ID d'escalade",
            level: 'Niveau',
            level_range: 'Niveau (L1–L4)',
            create_escalation: 'Créer une escalade',
            incident_id_placeholder: 'ex. INC-001',
            target_role_placeholder: 'ex. dpo, incident_manager',
            select_placeholder: '— sélectionner —',
        },
        regulator_exports: {
            page_title: 'Exports réglementaires',
            title: 'Exports réglementaires',
            new: 'Nouveau exporter',
            incident_id: "ID d'incident",
            regulator: 'Régulateur',
            export_format: 'Formater',
            exported_at: 'Exporté À',
            no_exports: 'Aucun regulator exports trouvé',
            enter_incident_id: 'Saisissez Incident ID',
            export_id: "ID d'export",
            watermark: 'Filigrane',
            submission_status: 'Statut de soumission',
            all_jurisdictions: 'Toutes les juridictions',
            no_transitions: 'Aucune autre transition disponible à partir de',
            select_new_status: '— sélectionner un nouveau statut —',
            sections_included: 'Sections incluses',
            form_classification: 'Classement',
            submission_channel: 'Chaîne',
        },
        deadlines: {
            title: 'Notification Échéances',
            incident_id: "ID d'incident",
            deadline: 'Échéance',
            regulator: 'Régulateur',
            remaining: 'Restant',
            no_deadlines: 'Aucun notification deadlines trouvé',
        },
        vendor_coordination: {
            page_title: 'Coordination fournisseurs',
            title: 'Coordination fournisseurs',
            new: 'Nouveau Coordination',
            incident_id: "ID d'incident",
            vendor_name: 'Fournisseur Nom',
            contact: 'Personne-ressource',
            coordination_status: 'Statut de coordination',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Aucun vendor coordination items trouvé',
            assessment_placeholder: 'Évaluation initiale du fournisseur...',
            coordination_id: 'ID de coordination',
            communications: 'Échanges',
            info_requests: "Demandes d'information",
            action_requests: "Demandes d'action",
            direction: 'Sens',
            channel_label: 'Canal',
            no_transitions: 'Aucune transition supplémentaire disponible.',
            vendor_ref_number: 'Numéro de référence',
            report_date: 'Date du rapport',
            contact_email: 'E-mail',
            contact_phone: 'Téléphone',
            channel_email: 'E-mail',
            channel_meeting: 'Réunion',
            channel_phone: 'Téléphone',
            channel_portal: 'Portail',
            incident_id_placeholder: 'Filtrer par Incident ID...',
            priority_critical: 'Critique',
            priority_high: 'Élevé',
            priority_low: 'Low',
            priority_medium: 'Moyen',
            vendor_id_placeholder: 'Filtrer par Fournisseur ID...',
            description_placeholder: 'Descriptif...',
            filter_vendor_id: 'Filtrer par Fournisseur ID',
            inbound: 'Entrant',
            outbound: 'Sortant',
            summary_placeholder: 'Résumé...',
            priority_label: 'Priorité',
            due_date_label: "Date d'échéance",
            info_request_desc: "Description de la demande d'informations",
            action_request_desc: "Description de la demande d'action",
            content_label: 'Contenu',
            select_incident: 'Sélectionner un incident',
            select_vendor: 'Sélectionner le prestataire',
            discovery_date: 'Date de découverte',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Acteur',
        },
        metrics: {
            page_title: 'Métriques de réponse',
            title: 'Métriques de réponse',
            loading: 'Chargement de metrics data...',
            avg_response_time: 'Avg Réponse Heure',
            avg_resolution_time: 'Avg Résolution Heure',
            total_incidents: "Total d'incidents",
            by_severity: 'Par Gravité',
            by_status: 'Par Statut',
            trend: 'Tendance',
            start_sla_clock: 'Démarrer le chrono SLA',
            sla_records: 'Enregistrements SLA',
            no_sla_records: "Aucun enregistrement SLA pour l'instant.",
            record_id: "ID d'enregistrement",
            severity: 'Gravité',
            progress: 'Progression',
            incident_id_required: "L'ID d'incident est obligatoire",
            sla_definition_required: 'La définition SLA est obligatoire',
            metric_type_required: 'Le type de métrique est obligatoire',
            threshold_required: 'Les heures seuil sont obligatoires',
            sla_clock_started: 'Chrono SLA démarré',
            sla_definition: 'Définition SLA',
            no_definitions: 'Aucune définition SLA trouvée.',
            select_definition: 'Sélectionner une définition SLA...',
            select_metric_type: 'Sélectionner le type de métrique...',
            select_severity: 'Sélectionner la gravité...',
            threshold_hours: 'Seuil (heures)',
            warning_threshold_pct: "Seuil d'avertissement (%)",
            start_clock: 'Démarrer le chrono',
            starting: 'Démarrage...',
            metric_label: 'Métrique',
            value_label: 'Valeur',
            incident_id: "ID d'incident",
            threshold_placeholder: 'ex. 24',
            metric_types: {
                triage_time: 'Temps de triage',
                containment_time: 'Temps de confinement',
                notification_time: 'Temps de notification',
                resolution_time: 'Temps de résolution',
            },
            severities: {
                critical: 'Critique',
                high: 'Élevé',
                medium: 'Moyen',
                low: 'Faible',
            },
            sla_reference: 'Référence SLA',
        },
        playbook_links: {
            page_title: 'Liens Playbook',
            title: 'Liens Playbook-Notification',
            new_link: 'Lier un playbook à une notification',
            activation_id: "ID d'activation",
            notification_id: 'ID de notification',
            select_activation: 'Sélectionner une activation...',
            select_notification: 'Sélectionner une notification...',
            no_links: 'Aucun lien playbook trouvé',
            seed_pdpl: 'Ajouter des liens de playbook modèles',
            created: 'Lien playbook créé',
            seeded: 'Liens de playbook modèles ajoutés',
            create_failed: 'Échec de la création du lien playbook',
            seed_failed: 'Échec de l’ajout des liens de playbook modèles',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: "Aucun cadre installé ne régit la réponse aux incidents ; il n'y a donc aucun délai à ajouter.",
                declared_but_none_active: "Un cadre régissant la réponse aux incidents est installé mais n'est pas en vigueur.",
                no_declared_deadlines: 'Les cadres de réponse aux incidents installés ne fixent aucun délai de notification.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Échéance',
            deadline_units: {
                hours: '{count} heures',
                working_days: '{count} jours ouvrables',
                calendar_days: '{count} jours calendaires',
                days: '{count} jours',
                weeks: '{count} semaines',
                months: '{count} mois',
                years: '{count} ans',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Correspondances SLA réglementaires',
            title: 'Correspondances SLA réglementaires',
            new_mapping: 'Nouvelle correspondance SLA',
            sla_definition: 'Définition SLA',
            select_sla_definition: 'Sélectionner une définition SLA...',
            framework_code: 'Code du cadre',
            deadline_type: "Type d'échéance",
            deadline_hours: 'Délai en heures',
            seed_pdpl: 'Ajouter des correspondances à partir des délais installés',
            no_mappings: 'Aucune correspondance SLA réglementaire trouvée',
            dashboard: 'Tableau de bord SLA réglementaire',
            deadline_types: {
                initial_notification: 'Notification initiale',
                supplement_response: 'Réponse complémentaire',
                authority_response: "Réponse de l'autorité",
                remediation_report: 'Rapport de remédiation',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Référence statutaire',
        },
        coordination_dashboard: {
            page_title: 'Tableau de bord de coordination',
            title: 'Tableau de bord de coordination',
            incident_id: "ID d'incident",
            select_incident: 'Sélectionner un incident...',
            team_role: "Rôle de l'équipe",
            members: 'Membres',
            tasks: 'Tâches',
            sla_status: 'Statut SLA',
            no_teams: 'Aucune équipe de coordination trouvée',
            new_team: 'Nouvelle équipe de coordination',
            enter_incident_id: "Saisissez un ID d'incident pour afficher les équipes de coordination.",
            member_ids_placeholder: 'utilisateur1, utilisateur2',
            edit_team: "Modifier l'équipe",
            task_count: 'Nombre de tâches',
            tasks_completed: 'Terminé',
            team_roles: {
                security: 'Sécurité',
                legal: 'Juridique',
                it: 'IT',
                management: 'Direction',
                department_owner: 'Responsable de département',
                external: 'Externe',
            },
        },
        evidence_chain: {
            page_title: 'Chaîne de preuves',
            title: 'Chaîne de garde des preuves',
            new_entry: 'Nouvelle entrée de preuve',
            select_incident: 'Sélectionner un incident...',
            actor: 'Acteur',
            select_actor: 'Sélectionner un acteur...',
            evidence_id: 'ID de preuve',
            action: 'Action',
            recipient: 'Destinataire',
            no_entries: 'Aucune entrée de chaîne de preuves trouvée',
            enter_incident_id: "Saisissez un ID d'incident pour afficher la chaîne de preuves.",
            evidence_id_placeholder: 'ex. EVD-2026-0001',
            evidence_id_help: 'Une référence unique pour l’élément de preuve : un fichier journal, une image disque ou une capture d’écran. Une suggestion est préremplie ; modifiez-la pour qu’elle corresponde à votre catalogue de preuves.',
            select_incident_first: 'Sélectionnez un incident ci-dessus avant d’ajouter une entrée de preuve.',
            for_incident: 'Pour l’incident',
            actions: {
                collected: 'Collecté',
                shared: 'Partagé',
                exported: 'Exporté',
                archived: 'Archivé',
            },
        },
        post_review: {
            page_title: 'Revue post-incident',
            title: 'Revue post-incident',
            new_review: 'Nouvelle revue',
            select_incident: 'Sélectionner un incident...',
            lessons_learned: 'Leçons apprises',
            recommendations: 'Recommandations',
            improvement_actions: "Actions d'amélioration",
            submit_for_review: 'Soumettre pour revue',
            aria_submit_review: 'Soumettre la revue {name} pour revue',
            aria_sign_off: 'Approuver la revue {name}',
            sign_off: 'Approbation',
            no_reviews: 'Aucune revue post-incident trouvée',
            enter_incident_id: "Saisissez un ID d'incident pour afficher la revue post-incident.",
            one_per_line: 'Un par ligne',
            statuses: {
                draft: 'Brouillon',
                in_review: 'En revue',
                signed_off: 'Approuvé',
                closed: 'Clôturé',
            },
        },
        activations: {
            title: 'Activations du manuel de jeu',
            activation_id: "Identifiant d'activation",
            phases: 'Étapes',
            activated_at: 'Activé à',
            activated_by: 'Activé par',
            deactivate: 'Désactiver',
            complete: 'Terminer',
            deactivation_reason: 'Raison de la désactivation',
            evidence_button: 'Preuve',
            evidence_modal_title: "Preuve d'activation",
            no_evidence: 'Aucune preuve attachée à cette activation.',
            upload_evidence_label: "Télécharger la preuve d'activation",
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Guide-moi à travers les lacunes',
            running: 'Des écarts de marche…',
            results_heading: "Feuille de route d'amélioration ({count} écart{plural})",
            tokens_label: '≈ {count} jetons',
            no_gaps: 'Aucune lacune détectée : tous les domaines atteignent ou dépassent la cible.',
            col_priority: 'Priorité',
            col_domain: 'Domaine',
            col_levels: 'Actuel → Cible',
            col_severity: 'Gravité',
            col_actions: 'Actions correctives',
            citations_label: 'Citations',
            accept_label: 'Accepter la feuille de route',
            reject_label: 'Rejeter le brouillon',
            export_label: 'Exporter DOCX',
            exported_label: 'Feuille de route exportée',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Résolution : {status}',
            resolve_status_accepted: 'Accepté',
            resolve_status_rejected: 'Rejeté',
            error_generic: "Impossible d'exécuter la procédure pas à pas. Réessayez ou vérifiez l'état de l'évaluation.",
        },
        policy_gap: {
            run_label: 'Détecter les lacunes politiques',
            running: 'Détecter les lacunes…',
            results_heading: 'Lacunes politiques ({count})',
            tokens_label: '≈ {count} jetons',
            no_gaps: 'Aucune lacune de couverture détectée.',
            col_severity: 'Gravité',
            col_kind: 'Genre',
            col_clause: 'Clause/exigence',
            kind_missing: 'Clause manquante',
            kind_weak: 'Faible couverture',
            kind_stale: 'Obsolète (examen en retard)',
            remediation_label: 'Correction suggérée',
            citations_label: 'Citations',
            accept_label: "Accepter le rapport d'écart",
            reject_label: 'Rejeter le rapport',
            export_label: 'Exporter DOCX',
            accept_hint: "Accepter enregistre le rapport d'écart sur le brouillon persistant. Utilisez Exporter DOCX pour générer un rapport d'audit de stratégie ; c'est l'opérateur qui pilote la modification réelle de la politique.",
            resolve_summary: 'Résolution : {status}',
            error_generic: "Impossible de détecter les lacunes politiques. Réessayez ou vérifiez l'état du document.",
        },
        remediation_plan_generator: {
            run_label: 'Générer un plan à partir des lacunes',
            running: 'Plan de génération…',
            results_heading: 'Plan de remédiation ({phase_count} phase{plural})',
            tokens_label: '≈ {count} jetons',
            phase_label: 'Phase {index}',
            window_label: 'Fenêtre',
            owner_label: 'Rôle de propriétaire',
            evidence_label: 'Types de preuves requis',
            tasks_label: 'Tâches',
            no_phases: "Aucune phase produite – l'ensemble d'intervalles d'entrée peut être vide.",
            citations_label: 'Citations',
            accept_label: 'Accepter le forfait',
            reject_label: 'Rejeter le plan',
            export_label: 'Exporter DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Résolution : {status} · {tasks} tâche(s) créée(s)',
            error_generic: "Impossible de générer un plan de correction. Réessayez ou vérifiez l'entrée définie par l'écart.",
            source_picker_label: 'Origine',
            source_all: 'Tout',
            source_consent: 'Analyse des écarts de consentement',
            source_policy: 'Détection des lacunes politiques',
            source_vendor: 'Intégration des fournisseurs',
            load_upstream_label: "Charger depuis l'amont",
            loading_upstream: 'Chargement…',
            upstream_count: '{count} espace(s) chargé(s)',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Modèles de maturité',
            maturity_assessments: 'Évaluations de maturité',
            trend_dashboard: 'Tableau de bord des tendances',
            reporting_periods: 'Périodes de rapport',
            executive_summaries: 'Résumés exécutifs',
            audit_readiness: "Préparation à l'audit",
            evidence_gaps: 'Lacunes de preuves',
            program_health: 'Santé du programme',
            obligation_mappings: 'Correspondances des obligations',
            governance_documents: 'Documents de gouvernance',
            remediation_plans: 'Plans de remédiation',
            governance_model: 'Modèle de gouvernance',
            document_drafting: 'Rédaction de documents',
            document_issuance: 'Émission de documents',
            acknowledgment_tracking: 'Suivi des accusés de réception',
            change_log: 'Journal des modifications',
            review_cadence: 'Cadence de révision',
        },
        maturity_models: {
            title: 'Modèles de maturité',
            new: 'Nouveau modéliser',
            seed_defaults: 'Valeurs initiales par défaut',
            domains: 'Domaines',
            activate: 'Activer',
            no_models: 'Aucun maturity models trouvé',
            new_maturity_model: 'Nouveau maturité modéliser',
            create_model: 'Ajouter modéliser',
            load_error: 'Impossible de charger les modèles de maturité. Veuillez réessayer.',
            create_error: 'Impossible de créer le modèle de maturité',
            status_error: 'Impossible de mettre à jour le statut du modèle',
            seed_error: 'Impossible de générer les valeurs par défaut',
            aria_seed_defaults: 'Générer les modèles de maturité par défaut',
            aria_create_new: 'Créer un nouveau modèle de maturité',
            loading_sr: 'Chargement des modèles de maturité',
            model_name_placeholder: 'p. ex., Modèle de maturité vie privée',
            status_changed: 'Statut du modèle changé en {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Évaluations de maturité',
            new: 'Nouveau évaluation',
            model: 'Modéliser',
            assessor: 'Évaluateur',
            overall_score: 'Score global',
            overall_score_help: 'Une note de maturité pondérée de 0 à 5, calculée automatiquement lorsqu’une évaluation est finalisée. Augmentez-la en attribuant une note plus élevée à chaque domaine lors de l’évaluation, puis en faisant passer l’évaluation par Soumettre pour révision → Finaliser.',
            assessed_at: 'Évalué le',
            no_assessments: 'Aucun maturité évaluations trouvé',
            load_error: 'Impossible de charger les évaluations. Veuillez réessayer.',
            create_error: "Impossible de créer l'évaluation",
            start_error: "Impossible de démarrer l'évaluation",
            archive_error: "Impossible d'archiver l'évaluation",
            eval_error: "Impossible de charger l'évaluation",
            eval_min_score: 'Veuillez noter au moins un domaine',
            eval_save_error: 'Impossible de sauvegarder les évaluations de domaine',
            review_error: 'Impossible de soumettre pour révision',
            finalize_error: "Impossible de finaliser l'évaluation",
            start: 'Démarrer',
            start_confirm_title: "Démarrer l'évaluation",
            start_confirm_message: 'Êtes-vous sûr de vouloir démarrer cette évaluation ?',
            archive_confirm_title: "Archiver l'évaluation",
            archive_confirm_message: 'Êtes-vous sûr de vouloir archiver cette évaluation ?',
            evaluate_domains: 'Évaluer',
            evaluate_domains_title: 'Évaluer les domaines',
            submit_for_review: 'Soumettre pour révision',
            finalize: 'Finaliser',
            archive: 'Archiver',
            all_reviewers: 'Tous les réviseurs doivent approuver',
            any_reviewer: "N'importe quel réviseur peut approuver",
            select_level: 'Sélectionner le niveau de maturité...',
            findings_placeholder: 'Conclusions, notes, références de preuves...',
            no_domains: 'Aucun domaine trouvé pour ce modèle.',
        },
        trend_dashboard: {
            title: 'Tableau de bord des tendances',
            loading: 'Chargement de trend data...',
            score_trend: 'Tendance du score',
            domain_comparison: 'Comparaison de domaines',
            period: 'Période',
            load_error: 'Impossible de charger le résumé des tendances. Veuillez réessayer.',
            metrics_load_error: 'Impossible de charger les métriques. Veuillez réessayer.',
            module: 'Module',
            metric: 'Métrique',
            value: 'Valeur',
            trend: 'Tendance',
            aria_filters: 'Filtres des métriques de tendance',
            showing_chart: 'Affichage de la vue graphique',
            showing_table: 'Affichage de la vue tableau',
            create_metric: 'Créer un indicateur',
            data_points: 'Points de données',
            trend_direction: 'Direction de la tendance',
            unit: 'Unité',
        },
        reporting_periods: {
            title: 'Périodes de rapport',
            new: 'Nouveau période',
            period_name: 'Période Nom',
            start_date: 'Début Date',
            end_date: 'Fin Date',
            no_periods: 'Aucun reporting periods trouvé',
            load_error: 'Impossible de charger les périodes. Veuillez réessayer.',
            create_error: 'Impossible de créer la période',
            auto_generate_error: 'Impossible de générer automatiquement les périodes',
            status_error: 'Impossible de mettre à jour le statut de la période',
            start_collecting: 'Commencer la collecte',
            mark_as_draft: 'Marquer comme brouillon',
            submit_for_review: 'Soumettre pour révision',
            publish: 'Publier',
            auto_generate: 'Générer automatiquement les périodes',
            period_label_placeholder: 'p. ex., T1 2026',
            status_updated: 'Statut de la période mis à jour',
        },
        executive_summaries: {
            title: 'Résumés exécutifs',
            new: 'Nouveau résumé',
            period: 'Période',
            generated_at: 'Généré le',
            generated_by: 'Généré par',
            no_summaries: 'Aucun executive summaries trouvé',
            generate: 'Générer',
            load_error: 'Impossible de charger les résumés exécutifs. Veuillez réessayer.',
            generate_error: 'Impossible de générer le résumé exécutif',
            save_error: 'Impossible de sauvegarder le résumé exécutif',
        },
        audit_readiness: {
            title: "Préparation à l'audit",
            loading: 'Chargement de audit readiness data...',
            overall_readiness: 'Préparation globale',
            by_domain: 'Par Domaine',
            evidence_coverage: 'Couverture des preuves',
            gaps: 'Écarts',
            load_error: 'Impossible de charger la dernière préparation. Veuillez réessayer.',
            history_error: "Impossible de charger l'historique. Veuillez réessayer.",
            calculate_error: "Impossible de calculer la préparation à l'audit",
            aria_recalculate: 'Recalculer le score de préparation',
            aria_current_readiness: "Préparation actuelle à l'audit",
            aria_history: 'Historique des scores de préparation',
            recalculated: "Préparation à l'audit recalculée",
            loading_sr: 'Chargement des données de préparation',
        },
        evidence_gaps: {
            title: 'Lacunes de preuves',
            domain: 'Domaine',
            control: 'Contrôle',
            gap_type: "Type d'écart",
            severity: 'Gravité',
            no_gaps: 'Aucun evidence gaps trouvé',
            load_error: 'Impossible de charger les lacunes. Veuillez réessayer.',
            stats_error: 'Impossible de charger les statistiques. Veuillez réessayer.',
            create_error: 'Impossible de créer la lacune',
            update_error: 'Impossible de mettre à jour la lacune',
            status_error: 'Impossible de mettre à jour le statut',
            aria_create_new: 'Créer une nouvelle lacune',
            aria_statistics: 'Statistiques des lacunes',
            loading_sr: 'Chargement des lacunes',
            status_updated: 'Statut de la lacune mis à jour',
            entity_type_placeholder: 'p. ex., fournisseur, système',
            all_modules: 'Tous les domaines',
            all_severities: 'Toutes les gravités',
        },
        program_health: {
            title: 'Santé du programme',
            loading: 'Chargement de program health data...',
            overall_health: 'Santé globale',
            by_module: 'Par Module',
            risk_areas: 'Domaines de risque',
            recommendations: 'Recommandations',
            load_error: 'Impossible de charger les données. Veuillez réessayer.',
            aria_key_metrics: 'Métriques clés de santé du programme',
            aria_module_scorecard: 'Détail de la fiche de score par module',
            loading_sr: 'Chargement des données de santé du programme',
            trending_up: 'Tendance à la hausse',
            trending_down: 'Tendance à la baisse',
            trending_stable: 'Stable',
            data_loaded: 'Données de santé du programme chargées',
        },
        obligation_mappings: {
            title: 'Correspondances des obligations',
            create_mapping: 'Créer une correspondance des obligations',
            framework: 'Cadre réglementaire',
            select_framework: 'Sélectionner un cadre...',
            obligation: 'Obligation',
            obligation_ref: "Référence de l'obligation",
            obligation_description: "Description de l'obligation",
            role: 'Rôle',
            assigned_role: 'Rôle assigné',
            select_role: 'Sélectionner un rôle...',
            assigned_department: 'Département assigné',
            select_department: 'Sélectionner un département...',
            control_ids: 'ID de contrôle',
            control_ids_placeholder: 'p. ex. A.5.1, CC6.1',
            control_ids_hint: 'Codes de référence de contrôle séparés par des virgules.',
            raci_assignments: 'Attributions RACI',
            create_raci: 'Créer une attribution RACI',
            activity: 'Activité',
            activity_name: "Nom de l'activité",
            activity_description: "Description de l'activité",
            responsible: 'Responsable',
            select_users: 'Sélectionner des utilisateurs...',
            accountable: 'Redevable',
            select_user: 'Sélectionner un utilisateur...',
            consulted: 'Consulté',
            informed: 'Informé',
            review_frequency: 'Fréquence de révision',
            frequency_monthly: 'Mensuel',
            frequency_quarterly: 'Trimestriel',
            frequency_semi_annual: 'Semestriel',
            frequency_annual: 'Annuel',
            no_mappings_found: 'Aucune correspondance des obligations trouvée.',
            no_raci_found: 'Aucune attribution RACI trouvée.',
            mapping_created: "Mappage d'obligation créé",
            mapping_updated: "Correspondance d'obligation mise à jour",
            mapping_deleted: "Correspondance d'obligation supprimée",
            raci_created: 'Attribution RACI créée',
            raci_updated: 'Attribution RACI mise à jour',
            raci_deleted: 'Attribution RACI supprimée',
            aria_edit_mapping: 'Modifier la correspondance {name}',
            aria_delete_mapping: 'Supprimer la correspondance {name}',
            aria_edit_raci: 'Modifier RACI {name}',
            aria_delete_raci: 'Supprimer RACI {name}',
            confirm_delete_mapping: "Supprimer cette correspondance d'obligation ?",
            confirm_delete_raci: 'Supprimer cette attribution RACI ?',
            table_view: 'Vue tableau',
            matrix_view: 'Vue matricielle',
            print_export: 'Imprimer / Exporter',
            filter_framework: 'Filtrer par cadre',
            no_matrix_data: 'Aucune attribution RACI à afficher en vue matricielle.',
            aria_print_export: 'Imprimer ou exporter la matrice RACI',
        },
        governance_documents: {
            title: 'Documents de gouvernance',
            create_document: 'Créer un document de gouvernance',
            document_title: 'Titre du document',
            document_type: 'Type de document',
            content_url: 'URL du contenu',
            content_url_placeholder: 'https://...',
            framework_codes: 'Codes cadre',
            select_frameworks: 'Sélectionner des cadres...',
            effective_date: "Date d'entrée en vigueur",
            review_date: 'Date de révision',
            status: 'Statut',
            version: 'Version',
            acknowledgments: 'Accusés de réception',
            acknowledge: 'Accuser réception',
            acknowledged_by: 'Accusé de réception par',
            department: 'Département',
            no_documents_found: 'Aucun document de gouvernance trouvé.',
            draft: 'Brouillon',
            review: 'Révision',
            approved: 'Approuvé',
            issued: 'Émis',
            superseded: 'Remplacé',
            document_created: 'Document créé',
            document_acknowledged: 'Document accusé de réception',
            policy: 'Politique',
            procedure: 'Procédure',
            standard: 'Norme',
            guideline: 'Ligne directrice',
        },
        governance_model: {
            title: 'Modèle de gouvernance',
            create_model: 'Créer un modèle de gouvernance',
            edit_model: 'Modifier le modèle de gouvernance',
            model_name: 'Nom du modèle',
            description: 'Descriptif',
            governance_type: 'Type de gouvernance',
            type_centralized: 'Centralisé',
            type_federated: 'Fédéré',
            type_hybrid: 'Hybride',
            framework_codes: 'Codes de cadre',
            select_frameworks: 'Sélectionner les cadres...',
            review_frequency: 'Fréquence de révision',
            freq_monthly: 'Mensuelle',
            freq_quarterly: 'Trimestrielle',
            freq_semi_annual: 'Semestrielle',
            freq_annual: 'Annuelle',
            committees: 'Comités',
            committee_name: 'Nom du comité',
            committee_members: 'Membres (séparés par des virgules)',
            committee_mandate: 'Mandat',
            add_committee: 'Ajouter un comité',
            no_committees: 'Aucun comité défini.',
            delegation_rules: 'Règles de délégation',
            from_role: 'Du rôle',
            to_role: 'Au rôle',
            scope: 'Portée',
            add_delegation_rule: 'Ajouter une règle de délégation',
            control_owner_summary: 'Résumé des propriétaires de contrôle',
            no_raci_data: 'Aucune donnée RACI liée à ce modèle.',
            activity: 'Activité',
            responsible: 'Responsable',
            accountable: 'Redevable',
            consulted: 'Consulté',
            informed: 'Informé',
            no_models_found: 'Aucun modèle de gouvernance trouvé.',
            model_created: 'Modèle de gouvernance créé',
            model_updated: 'Modèle de gouvernance mis à jour',
            model_deleted: 'Modèle de gouvernance supprimé',
            confirm_delete: 'Supprimer ce modèle de gouvernance ?',
            expand: 'Développer',
            collapse: 'Réduire',
            aria_expand: 'Développer les détails de {name}',
            aria_edit: 'Modifier le modèle {name}',
            aria_delete: 'Supprimer le modèle {name}',
            aria_export_csv: 'Exporter {name} en CSV',
            aria_export_json: 'Exporter {name} en JSON',
            framework_read_permission_warning: "Vous avez besoin de l'autorisation regulatory_framework:read pour sélectionner les frameworks. Demandez à un administrateur de l'accorder.",
            select_members: 'Sélectionner les membres du comité',
            select_from_role: 'Sélectionnez le rôle de délégation',
            select_to_role: 'Sélectionnez le rôle de réception',
            mandate_markdown_hint: 'Markdown pris en charge : **gras**, listes, liens.',
            scope_tab_obligation: 'Obligation',
            scope_tab_framework: 'Cadre',
            scope_tab_free_text: 'Texte libre',
            select_obligation: 'Sélectionnez une obligation',
            select_framework: 'Sélectionnez un cadre',
            aria_remove_committee: 'Supprimer le comité {n}',
            aria_remove_delegation_rule: 'Supprimer la règle de délégation {n}',
            no_frameworks_available: "Aucun cadre disponible. Ajoutez d'abord des cadres réglementaires.",
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Rédaction et approbation',
            document_title: 'Titre du document',
            document_type: 'Type de document',
            status: 'Statut',
            drafter: 'Rédacteur',
            deadline: 'Date limite',
            assign_drafter: 'Assigner un rédacteur',
            select_drafter: 'Sélectionner un rédacteur...',
            assign: 'Assigner',
            submit_for_review: 'Soumettre pour révision',
            legal_review: 'Révision juridique',
            reviewer: 'Réviseur',
            select_reviewer: 'Sélectionner un réviseur...',
            comments: 'Commentaires',
            review_action: 'Action de révision',
            approve: 'Approuver',
            request_changes: 'Demander des modifications',
            submit_review: 'Soumettre la révision',
            executive_approval: 'Approbation de la direction',
            no_drafts_found: 'Aucun document en rédaction, révision ou approbation.',
            drafter_assigned: 'Rédacteur assigné',
            status_updated: 'Statut du document mis à jour',
            review_approved: 'Révision approuvée',
            changes_requested: 'Modifications demandées',
            document_approved: 'Document approuvé',
            aria_assign: 'Assigner un rédacteur à {name}',
            aria_submit_review: 'Soumettre {name} pour révision',
            aria_review: 'Réviser {name}',
            aria_approve: 'Approuver {name}',
        },
        document_issuance: {
            title: 'Émission et distribution',
            document_title: 'Titre du document',
            document_type: 'Type de document',
            status: 'Statut',
            version: 'Version',
            effective_date: "Date d'entrée en vigueur",
            distribution_scope: 'Portée de distribution',
            distribution_placeholder: 'Ex. : Tous les départements',
            issuance_number: "Numéro d'émission",
            issuance_number_placeholder: 'Ex. : ISS-2026-001',
            supersedes: 'Remplace le document',
            none: 'Aucun',
            issue_document: 'Émettre le document',
            ready_for_issuance: 'Prêt pour émission',
            issued_documents: 'Documents émis',
            no_issued_documents: 'Aucun document émis trouvé.',
            view_lineage: 'Voir la lignée',
            supersession_lineage: 'Lignée de remplacement',
            document_issued: 'Document émis',
            document_superseded: 'Document remplacé',
            aria_issue: 'Émettre {name}',
            aria_view_lineage: 'Voir la lignée de {name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Suivi des accusés de réception',
            document_title: 'Documenter',
            acknowledged_count: 'Reçus',
            pending_count: 'En attente',
            percentage: 'Achèvement',
            details: 'Détails',
            send_reminder: 'Envoyer un rappel',
            acknowledgment_details: 'Détails des accusés',
            user: 'Utilisateur',
            department: 'Département',
            acknowledged_at: 'Reçu le',
            status: 'Statut',
            acknowledged: 'Reçu',
            no_issued_documents: 'Aucun document émis à suivre.',
            no_acknowledgments: 'Aucun accusé enregistré.',
            reminder_sent: 'Rappel envoyé',
            aria_expand: 'Détails des accusés pour {name}',
            aria_send_reminder: 'Envoyer un rappel pour {name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Journal des modifications',
            date: 'Date',
            document: 'Documenter',
            change_type: 'Type de modification',
            actor: 'Acteur',
            summary: 'Résumé',
            export_csv: 'Exporter CSV',
            filters: 'Filtres du journal',
            filter_document: 'Filtrer par document',
            all_documents: 'Tous les documents',
            filter_change_type: 'Filtrer par type',
            all_types: 'Tous les types',
            date_from: 'Date début',
            date_to: 'Date fin',
            version_history: 'Historique des versions',
            version_archive: 'Archive des versions',
            no_entries: 'Aucune entrée trouvée.',
            no_history: 'Aucun historique disponible.',
            exported: 'Journal exporté',
            aria_export: 'Exporter le journal en CSV',
            aria_view_history: "Voir l'historique de {name}",
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Cadence de révision',
            configure_cadence: 'Configurer la cadence',
            edit_cadence: 'Modifier la cadence',
            document: 'Documenter',
            select_document: 'Sélectionner un document...',
            review_frequency: 'Fréquence de révision',
            freq_30: '30 jours',
            freq_60: '60 jours',
            freq_90: '90 jours',
            freq_180: '180 jours',
            freq_365: '365 jours',
            last_reviewed: 'Dernière révision',
            next_review: 'Prochaine révision',
            status: 'Statut',
            overdue: 'En retard',
            on_track: 'À jour',
            days: 'jours',
            horizon: 'Horizon de révision',
            overdue_warning: 'Révisions en retard',
            overdue_count: '{count} document(s) en retard.',
            no_cadences: 'Aucune cadence configurée.',
            cadence_created: 'Cadence créée',
            cadence_updated: 'Cadence mise à jour',
            aria_edit: 'Modifier la cadence de {name}',
        },
        remediation_plans: {
            title: 'Plans de remédiation',
            create_plan: 'Créer un plan de remédiation',
            gap_id: 'Référence de lacune',
            select_gap: 'Sélectionner une lacune...',
            plan_title: 'Titre',
            description: 'Descriptif',
            priority: 'Priorité',
            priority_critical: 'Critique',
            priority_high: 'Élevée',
            priority_medium: 'Moyenne',
            priority_low: 'Faible',
            assigned_to: 'Assigné à',
            select_user: 'Sélectionner un utilisateur...',
            target_date: 'Date cible',
            framework_code: 'Cadre',
            select_framework: 'Sélectionner un cadre...',
            gap_finding: 'Constat de lacune',
            action_items: "Éléments d'action",
            owner: 'Propriétaire',
            deadline: 'Échéance',
            planned: 'Planifié',
            in_progress: 'En cours',
            completed: 'Terminé',
            verified: 'Vérifié',
            no_plans_found: 'Aucun plan de remédiation trouvé.',
            evidence: 'Preuve',
            plan_created: 'Plan de remédiation créé',
            action_items_hint: "Éléments d'action (un par ligne)",
        },
        statuses: {
            identified: 'Identifié',
            assigned: 'Attribué',
            in_remediation: 'En réhabilitation',
            resolved: 'Résolu',
            accepted_risk: 'Risque accepté',
            planned: 'Prévu',
            in_progress: 'En cours',
            under_review: 'En cours de révision',
            finalized: 'Finalisé',
            archived: 'Archivé',
            draft: 'Brouillon',
            active: 'Actif',
            upcoming: 'À venir',
            collecting: 'Collecte',
            published: 'Publié',
            review: 'Examen',
            approved: 'Approuvé',
            issued: 'Publié',
            superseded: 'Remplacé',
            completed: 'Terminé',
            verified: 'Vérifié',
        },
        severities: {
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Low',
        },
        priorities: {
            critical: 'Critique',
            high: 'Élevé',
            medium: 'Moyen',
            low: 'Low',
        },
        gap_types: {
            missing: 'Manquant',
            expired: 'Expiré',
            insufficient: 'Insuffisant',
        },
        document_types: {
            policy: 'Politique',
            procedure: 'Procédure',
            standard: 'Norme',
            guideline: 'Ligne directrice',
            template: 'Modèle (interne)',
        },
        maturity_levels: {
            level_1: 'Niveau 1 — Initiale',
            level_2: 'Niveau 2 — Développement',
            level_3: 'Niveau 3 — Défini',
            level_4: 'Niveau 4 — Géré',
            level_5: 'Niveau 5 — Optimisation',
        },
        grades: {
            excellent: 'Excellent',
            good: 'Bon',
            fair: 'Foire',
            poor: 'Pauvre',
            critical: 'Critique',
            needs_improvement: "Besoin d'amélioration",
            not_assessed: 'Non évalué',
        },
        modules: {
            data_mapping: 'Cartographie des données',
            consent: 'Consentement',
            governance: 'Gouvernance',
            incidents: 'Incidents',
            monitoring: 'Surveillance',
            classification: 'Classement',
            vendor_risk: 'Risque fournisseurs',
            discovery: 'Découverte',
            deletion: 'Suppression',
        },
        cadences: {
            monthly: 'Mensuel',
            quarterly: 'Trimestriel',
            semi_annual: 'Semestriel',
            annual: 'Annuel',
        },
        section_labels: {
            opening: 'Ouverture',
            key_metrics: 'Indicateurs clés',
            highlights: 'Points forts',
            concerns: 'Préoccupations',
            recommendations: 'Recommandations',
            outlook: 'Perspectives',
        },
        out_of_100: '/100',
        count_gaps: '{count} écart',
        count_gaps_plural: '{count} lacunes',
        count_summaries: '{count} résumé',
        count_summaries_plural: '{count} résumés',
        count_assessments: '{count} évaluation',
        count_assessments_plural: '{count} évaluations',
        count_models: 'Modèle {count}',
        count_models_plural: '{count} modèles',
        count_periods: '{count} période',
        count_periods_plural: '{count} périodes',
        count_metrics: '{count} métrique',
        count_metrics_plural: '{count} métriques',
        unknown_user: 'Utilisateur inconnu',
        unknown_gap: 'Écart inconnu',
        no_permission_title: 'Autorisations insuffisantes',
        no_permission_message: "Vous n'avez pas la permission d'accéder à cette page.",
        ac_vn_frameworks_title: 'Couverture du cadre VN',
        ac_vn_frameworks_body: "Les évaluations de maturité couvrent les 6 cadres réglementaires vietnamiens : PDPL, loi sur les données, loi sur les télécommunications, loi sur la cybersécurité, loi AI et loi sur le commerce électronique, chacun avec des critères d'évaluation spécifiques au cadre.",
        ac_controller_processor_title: 'Contrôleur vs processeur',
        ac_controller_processor_body: "Les mappages d’obligations font la distinction entre les responsabilités du responsable du traitement et celles du sous-traitant. Attribuez des rôles à l'aide de la matrice RACI pour garantir que chaque obligation a un propriétaire clair, que l'organisation agisse en tant que contrôleur ou sous-traitant.",
        ac_versioned_evidence_title: 'Piste de preuves versionnées',
        ac_versioned_evidence_body: 'Chaque transition de statut (Brouillon, Révision, Approuvé, Publié) crée un enregistrement horodaté et versionné qui sert de preuve vérifiable. Le journal des modifications capture l’historique complet du cycle de vie du document.',
        ac_remediation_gap_link_title: 'Lié aux lacunes d’évaluation',
        ac_remediation_gap_link_body: 'Chaque plan de remédiation est directement lié à une lacune d’évaluation identifiée. Attribuez un propriétaire et fixez un délai cible pour suivre les progrès de la résolution, de l’écart à la fermeture.',
        ac_annual_attestation_title: "Cycle d'attestation annuel",
        ac_annual_attestation_body: "Les périodes de déclaration sont conformes aux exigences d'attestation annuelle de la loi vietnamienne sur les données. Utilisez la cadence « Annuelle » (cycle de 365 jours) pour générer automatiquement des périodes alignées sur l'attestation.",
        ac_evidence_per_obligation_title: 'Preuve par obligation-cadre',
        ac_evidence_per_obligation_body: 'Les lacunes en matière de preuves identifient les preuves manquantes, expirées ou insuffisantes pour chaque obligation-cadre. Filtrez par domaine pour voir quelles exigences réglementaires manquent de preuves à l’appui.',
        ac_cross_framework_title: 'Complétude multi-framework',
        ac_cross_framework_body: 'La préparation à l’audit fournit une vue d’exhaustivité inter-framework. Les scores des composants reflètent la couverture de tous les cadres cartographiés, mettant en évidence les domaines qui nécessitent une attention avant un audit.',
        ac_cadence_default_title: 'Cadence de révision par défaut',
        ac_cadence_default_body: 'La cadence de révision par défaut est de 180 jours. Les avis en retard sont automatiquement signalés. Ajustez la fréquence par document selon vos besoins – les options vont de 30 à 365 jours.',
        ac_templates_internal_title: 'Classification des modèles',
        ac_templates_internal_body: 'Les modèles sont classés comme documents internes. Utilisez le type de document « Modèle » pour marquer les artefacts de gouvernance réutilisables avec une portée de distribution interne uniquement.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Écritures en attente',
        subtitle: 'Les clients Claude externes proposent ici des écritures. Approuvez ou refusez chaque entrée — Forseti n’exécute qu’après validation par un administrateur.',
        no_pending: 'Aucune écriture en attente.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Outil',
        col_preview: 'Aperçu',
        col_key: 'Touche API',
        col_requested: 'Demandé',
        col_expires: 'Expire',
        col_status: 'Statut',
        status_pending: 'En attente',
        status_approved: 'Approuvé',
        status_declined: 'Refusé',
        status_expired: 'Expiré',
        status_executed: 'Exécuté',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Approuver',
        decline_label: 'Refuser',
        refresh_label: 'Actualiser',
        error_generic: 'Impossible de charger les écritures en attente.',
        confirm_approve: 'L’approbation exécutera l’outil sur votre organisation avec les permissions du détenteur de l’API key. Continuer ?',
        confirm_decline: 'Refuser cette écriture en attente ? Cette action est irréversible.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Les écritures en attente expirent 24 heures après leur création. Le balayage GC horaire refuse automatiquement les entrées expirées.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Mesures',
    },
    audit_walkthrough: {
        title: 'Forseti — Générateur de visite d’audit',
        subtitle: 'Générez une visite structurée pour le lancement d’un audit : question d’ouverture par contrôle, preuves attendues, questions complémentaires, lacunes potentielles.',
        audit_picker_label: 'Vérification',
        audit_picker_hint: 'Choisissez un audit planifié pour générer une visite.',
        run_label: 'Générer la visite',
        running: 'Génération…',
        results_heading: 'Visite — {count} contrôle(s)',
        tokens_label: '≈ {count} jetons',
        no_controls: 'Aucun contrôle dans le périmètre. Ajoutez des contrôles à la fiche d’audit.',
        col_control: 'Contrôle',
        col_question: 'Question d’ouverture',
        col_evidence: 'Preuve',
        col_gaps: 'Lacunes potentielles',
        expected_evidence_label: 'Preuves attendues',
        followup_probes_label: 'Questions complémentaires',
        accept_label: 'Accepter la visite',
        reject_label: 'Rejeter',
        export_label: 'Exporter en DOCX',
        resolve_summary: 'Résolution : {status}',
        resolve_status_accepted: 'Accepté',
        resolve_status_rejected: 'Rejeté',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Impossible de générer la visite. Réessayez ou vérifiez le statut de l’audit.',
    },
    privilege_creep: {
        title: 'Forseti — Détection de dérive de privilèges',
        subtitle: 'Analyse périodique des affectations de rôles. Les conclusions sont consultatives — Forseti ne révoque jamais automatiquement.',
        run_label: 'Analyser maintenant',
        running: 'Analyse…',
        findings_heading: '{count} conclusion(s)',
        no_findings: 'Aucune dérive de privilèges détectée.',
        col_user: 'Utilisateur',
        col_job_function: 'Fonction',
        col_severity: 'Gravité',
        col_unexpected: 'Permissions inattendues',
        col_remediation: 'Remédiation suggérée',
        col_status: 'Statut',
        severity_critical: 'Critique',
        severity_high: 'Élevée',
        severity_medium: 'Moyenne',
        severity_low: 'Faible',
        status_open: 'Ouverte',
        status_dismissed: 'Rejetée',
        status_remediated: 'Corrigée',
        dismiss_label: 'Rejeter',
        remediate_label: 'Marquer corrigée',
        note_label: 'Note (facultatif)',
        error_generic: 'Impossible d’analyser la dérive de privilèges. Réessayez plus tard.',
        advisory_banner: 'Ces conclusions sont consultatives. Forseti ne révoque jamais automatiquement les permissions — examinez avec le propriétaire du rôle avant d’agir.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Mappage automatique des règles de conformité',
            subtitle: 'Forseti parcourt un pack réglementaire installé et suggère des règles de conformité nouvelles ou actualisées fondées sur les articles sources.',
            pack_picker_label: 'Dossier réglementaire',
            pack_picker_hint: 'Sélectionnez un pack installé à mapper.',
            run_label: 'Carte du pack',
            running: 'Cartographie…',
            results_heading: '{count} suggestion(s)',
            tokens_label: '≈ {count} jetons de votre bucket mensuel',
            no_suggestions: 'Aucune suggestion : le contenu du pack est peut-être déjà entièrement couvert par les règles existantes.',
            kind_new: 'Nouvelle règle',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Aucun changement requis',
            col_kind: 'Genre',
            col_title: 'Titre proposé',
            col_existing: 'Règle existante',
            col_source: 'Origine',
            proposed_body_label: 'Corps proposé',
            source_quote_label: 'Citation source',
            accept_selected: 'Accepter la sélection',
            reject_all: 'Rejeter tout',
            accept_all: 'Acceptez tout',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: "Impossible d'exécuter le mappage de règles. Réessayez ou vérifiez le code du pack.",
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Tableau de bord de session',
            access_log: "Journal d'accès",
            justification_review: 'Examen des justifications',
            investigations: 'Enquêtes',
            pattern_analysis: 'Analyse de modèles',
            anchoring_status: "Statut d'ancrage",
            configuration: 'Paramétrage',
            access_policies: "Politiques d'accès",
            review_campaigns: 'Campagnes de révision',
            evidence_packages: 'Paquets de preuves',
        },
        session_dashboard: {
            title: 'Tableau de bord de session',
            loading: 'Chargement des données du tableau de bord...',
            total_sessions: 'Total de sessions',
            active_sessions: 'Sessions actives',
            avg_duration: 'Durée moyenne',
            peak_day_sessions: 'Jour le plus actif',
            auth_method_breakdown: "Répartition par méthode d'authentification",
            geo_country_breakdown: 'Répartition par pays',
            recent_session_events: 'Événements de session récents',
            user_id: 'ID utilisateur',
            session_id: 'ID de session',
            ip_address: 'Adresse IP',
            auth_method: "Méthode d'authentification",
            started_at: 'Démarré le',
            event_type: "Type d'événement",
            all_event_types: "Tous les types d'événements",
            filter_by_user: 'Filtrer par utilisateur',
            no_active_sessions: 'Aucune session active',
            no_session_events: 'Aucun événement de session trouvé',
            revoke: 'Révoquer',
            revoke_session: 'Révoquer la session',
            aria_revoke_session: 'Révoquer la session {user}',
            revoke_reason: 'Motif',
            revoke_placeholder: 'Fournissez un motif de révocation de cette session...',
            session_label: 'Session :',
            load_failed: 'Échec du chargement du tableau de bord de session. Veuillez réessayer.',
            events_load_failed: 'Échec du chargement des événements de session. Veuillez réessayer.',
            revoke_failed: 'Échec de la révocation de la session. Veuillez réessayer.',
            session_events_loaded: '{n} événements de session chargés',
            event_types: {
                session_started: 'Session démarrée',
                session_refreshed: 'Session actualisée',
                session_expired: 'Session expirée',
                session_revoked: 'Session révoquée',
                session_terminated: 'Session terminée',
            },
        },
        access_log: {
            title: "Journal d'accès",
            user_id: 'Utilisateur ID',
            resource: 'Ressource',
            action: 'Mesure',
            ip_address: 'IP Adresse',
            timestamp: 'Horodatage',
            no_logs: 'Aucun access log entries trouvé',
            filter_by_user: 'Filtrer par Utilisateur ID...',
            load_failed: 'Échec de charge access journaliser. Veuillez réessayer.',
            export_success: 'Exporter initiated avec succès',
            export_failed: 'Échec de export access log. Veuillez réessayer.',
            record_count: 'enregistrer',
            record_count_plural: 'enregistrements',
            unclassified: 'Non classifié',
            classification_missing: 'Classification manquante',
            classification_level: 'Niveau de classification',
            response_code: 'Code de reponse',
            access_surface: "Canal d'accès",
            api_key: 'Clé API',
            classification_levels: {
                public: 'Publique',
                internal: 'Interne',
                confidential: 'Confidentiel',
                restricted: 'Restreint',
                unclassified: 'Non classifie',
            },
        },
        justification_review: {
            title: 'Examen des justifications',
            user_id: 'Utilisateur ID',
            resource: 'Ressource',
            justification: 'Motif',
            submitted_at: 'Soumis le',
            reviewer: 'Réviseur',
            no_reviews: 'Aucun justification reviews trouvé',
            load_failed: 'Échec de load justifications. Veuillez réessayer.',
            review_failed: 'Échec de review justification. Veuillez réessayer.',
            decline_failed: 'Échec de decline access. Veuillez réessayer.',
            justification_count: 'motif',
            justification_count_plural: 'motifs',
            reviewed: 'Révisé',
            concerning: 'Préoccupant',
            escalated: 'Escaladé',
            link_investigation_placeholder: 'Lier à un enquête (optional)',
            review_queue: "Examiner File d'attente",
            access_id_label: "ID d'accès",
            access_id_placeholder: "ID d'entrée du journal d'accès",
            resource_type_label: 'Type de ressource',
            resource_type_placeholder: 'ex. document, database',
            resource_id_label: 'ID de la ressource',
            resource_id_placeholder: 'Identifiant de la ressource',
            reason_placeholder: "Raison d'accès à cette ressource...",
            create_failed: 'Échec de create justification',
            unclassified: 'Non classifié',
            review_statuses: {
                pending: 'En attente',
                reviewed: 'Révisé',
                concerning: 'Concernant',
                escalated: 'Escaladé',
            },
            reason_codes: {
                business_need: "Besoin de l'entreprise",
                legal_obligation: 'Obligation légale',
                data_subject_request: 'Demande de personne concernée',
                audit_requirement: 'Exigence de vérification',
                incident_response: 'Réponse aux incidents',
                research: 'Recherche',
                administrative: 'Administratif',
                other: 'Autre',
            },
            classification_level: 'Niveau de classification',
            review_decision: 'Décision de révision',
        },
        investigations: {
            title: "Espace d'enquête",
            new: 'Nouveau enquête',
            subject: 'Sujet',
            investigator: 'Enquêteur',
            opened_at: 'Ouvert À',
            findings: 'Constatations',
            no_investigations: 'Aucun enquêtes trouvé',
            load_failed: 'Échec de charge enquêtes. Veuillez réessayer.',
            create_failed: 'Échec de create investigation. Veuillez réessayer.',
            timeline_add_failed: 'Échec de add timeline entry. Veuillez réessayer.',
            link_access_failed: 'Échec de link access ids. Veuillez réessayer.',
            link_justification_failed: 'Échec de link justification ids. Veuillez réessayer.',
            close_failed: 'Échec de fermer enquête. Veuillez réessayer.',
            export_success: 'Exporter initiated avec succès',
            export_failed: 'Échec de export investigation. Veuillez réessayer.',
            refresh_failed: 'Échec de refresh investigation detail. Veuillez réessayer.',
            update_failed: "Échec de la mise à jour de l'enquête",
            investigation_count: 'enquête',
            investigation_count_plural: 'enquêtes',
            title_placeholder: 'Enquête titre',
            description_placeholder: 'Détaillé description de le enquête...',
            assignee_placeholder: "ID utilisateur de l'assigné (facultatif)",
            timeline_action_placeholder: "p.ex., Journaux d'accès révisés",
            timeline_note_placeholder: 'Facultatif note',
            access_ids_placeholder: "identifiant d'accès-1, identifiant d'accès-2",
            justification_ids_placeholder: 'justification-id-1, justification-id-2',
            findings_placeholder: 'Résumer le enquête constatations...',
            by_actor: 'by',
            close_investigation: 'Fermer Enquête',
            add_timeline_failed: 'Échec de add timeline entry. Veuillez réessayer.',
            fetch_failed: 'Échec de fetch investigations. Veuillez réessayer.',
            change_status: "Changer le statut de l'enquête",
            details: "Détails de l'enquête",
            link_evidence: 'Lier une preuve',
            link_justifications: 'Lier des justifications',
            access_ids_label: "IDs d'accès",
            justification_ids_label: 'IDs de justification',
            access_ids_link_placeholder: "identifiant d'accès-1, identifiant d'accès-2",
            justification_ids_link_placeholder: 'justification-id-1, justification-id-2',
            investigations_loaded: '{n} investigations chargés',
            status_changed_to: 'Statut changé en {status}',
            add_timeline_entry: 'Ajouter une entrée chronologique',
            add_access_ids: "Ajouter des IDs d'accès",
            add_justification_ids: 'Ajouter des IDs de justification',
            investigation_types: {
                anomaly_review: 'Examen des anomalies',
                complaint_driven: 'Suite à une plainte',
                scheduled_review: 'Examen programmé',
                whistleblower_report: 'Rapport de dénonciation',
            },
            severity_labels: {
                low: 'Faible',
                medium: 'Moyen',
                high: 'Élevé',
                critical: 'Critique',
            },
            status_labels: {
                open: 'Ouvert',
                in_progress: 'En cours',
                pending_review: "En attente d'examen",
                closed_substantiated: 'Clôturé (justifié)',
                closed_unsubstantiated: 'Clôturé (non fondé)',
                closed_inconclusive: 'Fermé (non concluant)',
            },
            disposition_labels: {
                substantiated: 'Justifié',
                unsubstantiated: 'Non fondé',
                inconclusive: 'Non concluant',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Analyse de modèles',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Chargement de pattern data...',
            anomalies_detected: 'Anomalies détectées',
            patterns: 'Modèles',
            by_user: 'Par Utilisateur',
            by_resource: 'Par ressource',
            time_range: 'Plage horaire',
            load_failed: 'Échec de analyze patterns. Veuillez réessayer.',
            top_resources: 'Top 10',
            access_by_hour: 'Accès par heure du jour',
            access_by_day: 'Accès par Day de Semaine',
            fetch_failed: 'Échec de fetch pattern analysis. Veuillez réessayer.',
            analysis_loaded: 'Analyse de motifs chargée pour {user}, {n} sessions trouvées',
            date_format_placeholder: 'JJ/MM/AAAA',
            access_by_hour_heading: 'Accès par heure de la journée',
            access_by_day_heading: 'Accès par jour de la semaine',
            by_classification: 'Par niveau de classification',
            top_accessed_resources: 'Ressources les plus consultées',
            user_access_summary: "Résumé d'accès utilisateur",
            days: {
                sunday: 'Dimanche',
                monday: 'Lundi',
                tuesday: 'Mardi',
                wednesday: 'Mercredi',
                thursday: 'Jeudi',
                friday: 'Vendredi',
                saturday: 'Samedi',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: "Statut d'ancrage",
            loading: 'Chargement de anchoring data...',
            anchored: 'Ancré',
            pending: 'En attente',
            failed: 'Échoué',
            last_anchor: 'Dernier ancrage',
            no_records: 'Aucun anchoring records trouvé',
            status_load_failed: 'Échec de load anchoring status. Veuillez réessayer.',
            proofs_load_failed: 'Échec de load access proofs. Veuillez réessayer.',
            verify_failed: 'Échec de verify proof chain. Veuillez réessayer.',
            anchor_failed: 'Échec de anchor access proof. Veuillez réessayer.',
            batch_anchor_failed: 'Échec de batch anchor access proofs. Veuillez réessayer.',
            proof_count: 'preuve',
            proof_count_plural: 'preuves',
            anchor_single: 'Ancrage individuel',
            anchor_batch: 'Ancrage par lot',
            access_id_placeholder: 'Saisissez access ID to anchor',
            batch_ids_placeholder: "identifiant d'accès-1, identifiant d'accès-2, identifiant d'accès-3",
            verify_chain: 'Vérifier la chaîne',
            chain_breaks: 'Ruptures de chaîne',
            total_checked: 'Total vérifié',
            anchor_placeholder: 'Saisissez access ID to anchor',
            fetch_failed: 'Échec de fetch anchoring status. Veuillez réessayer.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Échec de fetch proofs. Veuillez réessayer.',
            access_id: "ID d'accès",
            chain_sequence: 'Séq. chaîne',
            proof_hash_label: 'Hash de preuve',
            batch_id: 'ID de lot',
            proofs_loaded: '{n} access proofs chargés',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'En attente',
                confirmed: 'Confirmé',
                failed: 'Échec',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Chaîne',
                blockchain_anchor: 'Ancre Blockchain',
                zkp_stub: 'Talon ZKP',
            },
        },
        configuration: {
            title: 'Configuration',
            session_settings: 'Session Paramètres',
            log_retention: 'Journal Conservation (days)',
            anomaly_threshold: "Seuil d'anomalie",
            anchoring_enabled: 'Ancrage activé',
            save_config: 'Enregistrer Configuration',
            load_failed: 'Échec de charge configuration. Veuillez réessayer.',
            save_success: 'Configuration enregistré avec succès',
            save_failed: 'Échec de save configuration. Veuillez réessayer.',
            justification_rules: 'Règles de justification',
            reason_codes: 'Codes de motif',
            anchoring_settings: "Paramètres d'ancrage",
            anchoring_method: "Méthode d'ancrage",
            anchoring_frequency: "Fréquence d'ancrage",
            batch_interval: 'Lot Intervalle (minutes)',
            eligibility_criteria: "Critères d'éligibilité",
            add_criterion_placeholder: 'Ajouter eligibility criterion...',
            retention_settings: 'Conservation Paramètres',
            standard_retention: 'Norme Conservation (days)',
            sensitive_retention: 'Sensible Conservation (days)',
            working_hours: 'Heures de travail',
            work_start: 'Début Heure',
            work_end: 'Fin Heure',
            timezone: 'Fuseau horaire',
            timezone_placeholder: 'p.ex. Asia/Ho_Chi_Minh',
            working_days: 'Jours ouvrés',
            concurrent_sessions: 'Limites de sessions simultanées',
            concurrent_threshold: 'Concurrent Session Seuil',
            classification_level: 'Classification Niveau',
            prompt_mode: "Mode d'invite",
            grace_period: 'Période de grâce (s)',
            code_placeholder: 'identifiant',
            is_custom: 'Personnalisé',
            label_placeholder: 'Libellé',
            description_placeholder: 'Descriptif',
            custom: 'Personnalisé',
            fetch_failed: 'Échec de fetch configuration. Veuillez réessayer.',
            classification_levels: {
                public: 'Publique',
                internal: 'Interne',
                confidential: 'Confidentiel',
                restricted: 'Restreint',
                unclassified: 'Non classé',
            },
            prompt_modes: {
                reason_only: 'Raison seulement',
                reason_text: 'Motif + Texte',
                reason_text_supervisor: 'Raison + Texte + Superviseur',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Chaîne',
                blockchain_anchor: 'Ancre Blockchain',
                zkp_stub: 'Talon ZKP',
            },
            anchoring_frequencies: {
                realtime: 'En temps réel',
                batched: 'Par lots',
                on_demand: 'Sur demande',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: "Politiques d'accès",
            create_policy: "Créer une politique d'accès",
            policy_name: 'Nom de la stratégie',
            framework_code: 'Cadre réglementaire',
            select_framework: 'Sélectionner un cadre réglementaire...',
            review_frequency: 'Fréquence de révision',
            scope: 'Périmètre',
            no_policies_found: "Aucune politique d'accès trouvée.",
            policy_created: "Politique d'accès créée",
            frequency_labels: {
                monthly: 'Mensuel',
                quarterly: 'Trimestriel',
                semi_annual: 'Semestriel',
                annual: 'Annuel',
            },
        },
        review_campaigns: {
            title: "Campagnes de révision d'accès",
            create_campaign: 'Créer une campagne de révision',
            reviewer: 'Réviseur',
            due_date: "Date d'échéance",
            certify: 'Certifier',
            no_campaigns_found: "Aucune campagne de révision d'accès trouvée.",
            status_open: 'Ouvert',
            status_in_review: 'En révision',
            status_completed: 'Terminé',
            campaign_created: 'Campagne de revue créée',
            review_certified: "Revue d'accès certifiée",
            certify_campaign: 'Certifier la campagne {id}',
            policy_id: 'ID de la politique',
            select_policy: 'Sélectionner une politique...',
            reviewer_ids_label: 'Réviseurs',
            select_reviewers: 'Sélectionner des réviseurs...',
            status_labels: {
                open: 'Ouvert',
                in_review: 'En cours de revue',
                in_progress: 'En cours',
                cancelled: 'Annulée',
                completed: 'Terminé',
            },
        },
        evidence_packages: {
            title: 'Paquets de preuves',
            generate_package: 'Générer un paquet de preuves',
            export_type: "Type d'exportation",
            package_type: 'Type de paquet',
            framework_scope: 'Périmètre du cadre',
            period: 'Période',
            generated_by: 'Généré par',
            generated_at: 'Généré le',
            no_packages_found: 'Aucun paquet de preuves trouvé.',
            package_generated: 'Pack de preuves généré',
            export_type_full: 'Exportation complète',
            export_type_summary: 'Résumé',
            export_type_certification_only: 'Certification uniquement',
            package_type_access_review: "Revue d'accès",
            package_type_investigation: 'Enquête',
            package_type_certification: 'Attestation',
            package_type_authority_request: "Demande d'autorité",
            period_start: 'Début de la période',
            period_end: 'Fin de la période',
            select_frameworks: 'Sélectionner les cadres...',
            completeness_col: 'Complétude',
            status_col: 'Statut',
            generated_by_col: 'Généré par',
        },
        evidence_package_detail: {
            title: 'Détail du pack de preuves',
            not_found: 'Pack de preuves introuvable.',
            package_metadata: 'Métadonnées du pack',
            status: 'Statut',
            item_count: "Nombre d'éléments",
            disposition: 'Disposition',
            closed_at: 'Fermé le',
            completeness_dashboard: 'Tableau de complétude',
            no_completeness_data: 'Aucune donnée de complétude disponible.',
            controls_covered: 'contrôles couverts',
            missing: 'Manquants',
            sign_off_tracker: 'Suivi des approbations',
            stage_framework_owner: 'Propriétaire du cadre',
            stage_legal_compliance: 'Juridique et Conformité',
            stage_internal_audit: 'Audit interne',
            approved: 'Approuvé',
            returned: 'Retourné',
            pending: 'En attente',
            approve: 'Approuver',
            return_btn: 'Retourner',
            sign_off_submitted: 'Approbation soumise',
            artifacts: 'Artefacts',
            no_artifacts: 'Aucun artefact trouvé.',
            template_id: 'ID de modèle',
            description: 'Descriptif',
            classification: 'Classement',
            evidence_type: 'Type de preuve',
            framework_section: 'Section du cadre',
            history: "Historique d'approbation",
            expand: 'Développer',
            collapse: 'Réduire',
            action: 'Action',
            actor: 'Acteur',
            timestamp: 'Horodatage',
            comment: 'Commentaire',
            close_case: 'Fermer le dossier',
            confirm_close: 'Confirmer la fermeture',
            closure_notes: 'Notes de fermeture',
            disposition_compliant: 'Conforme',
            disposition_non_compliant: 'Non conforme',
            disposition_partial: 'Partiel',
            case_closed: 'Pack de preuves fermé',
            export_package: 'Exporter le pack',
            export_complete: 'Pack de preuves exporté',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Tableau de bord',
            properties: 'Propriétés',
            groups: 'Groupes',
            bindings: 'Liaisons',
            change_requests: 'Demandes de modification',
            comparison: 'Comparaison',
            inconsistencies: 'Incohérences',
            snapshot_schedules: "Planifications d'instantanés",
            golden_templates: 'Modèles de référence',
            evidence_packs: 'Paquets de preuves',
            audit: 'Vérification',
        },
        select_property: 'Sélectionner la propriété…',
        select_policy: 'Sélectionner la politique…',
        select_properties: 'Sélectionner les propriétés…',
        select_target_properties: 'Sélectionner les propriétés cibles…',
        select_owner: 'Sélectionner le propriétaire…',
        select_technical_contact: 'Sélectionner le contact technique…',
        property_statuses: {
            active: 'Actif',
            inactive: 'Inactif',
            pending: 'En attente',
            decommissioned: 'Déclassé',
        },
        dashboard: {
            title: 'Tableau de bord inter-propriétés',
            loading: 'Chargement de dashboard data...',
            total_properties: 'Total Propriétés',
            active_properties: 'Actif Propriétés',
            snapshot_coverage: 'Couverture des instantanés',
            properties_covered: 'propriétés couvertes',
            total_bindings: 'Total des liaisons',
            deployed_bindings: 'Déployé',
            pending_bindings: 'Déploiement en attente',
            deployment_progress: 'Progression du déploiement',
            on_target: "Sur l'objectif",
            below_target: "Sous l'objectif",
            target_label: 'Objectif',
            total_groups: 'Total Groupes',
            pending_changes: 'En attente Changements',
            inconsistencies: 'Incohérences',
            recent_activity: 'Récent Activité',
            no_recent_activity: 'Aucune activité récente.',
            fetch_error: 'Échec de load dashboard. Veuillez réessayer.',
        },
        properties: {
            title: 'Registre de propriétés',
            new: 'Nouveau propriété',
            edit: 'Modifier Propriété',
            property_name: 'Nom',
            property_type: 'Tapez',
            identifier: 'Identifiant',
            environment: 'Environnement',
            owner_id: 'Propriétaire ID',
            technical_contact_id: 'ID du contact technique',
            decommission: 'Décommissionner',
            confirm_decommission: 'Êtes-vous sûr de vouloir décommissionner cette propriété ?',
            no_properties: 'Aucun propriétés trouvé',
            all_environments: 'Tous les environnements',
            all_types: 'Tous les types',
            cmp_instance_id: "CMP ID d'instance",
            deployment_date: 'Date de déploiement',
            identifier_placeholder: 'par exemple, www.example.com ou com.example.app',
            owner_name: 'Propriétaire',
            technical_contact_name: 'Contact technique',
            sensitive_data_categories: 'Catégories de données sensibles',
            sensitive_data_categories_placeholder: 'Sélectionnez les catégories de données sensibles',
            requires_enhanced_review: 'Nécessite un examen approfondi',
            sensitive_review_status: 'Examen des données sensibles',
            sensitive_review_pending: "En attente d'examen",
            sensitive_review_approved: 'Approuvé',
            sensitive_review_rejected: 'Rejeté',
            sensitive_review_submit: "Soumettre l'avis",
            sensitive_review_approve: 'Approuver',
            sensitive_review_reject: 'Rejeter',
            sensitive_review_notes: 'Notes de révision',
            sensitive_review_notes_placeholder: 'Saisir des notes de révision...',
            record_withdrawal: 'Retrait du dossier',
            withdrawal_subject_id: 'ID du sujet',
            withdrawal_consent_id: 'Identifiant de consentement',
            withdrawal_reason: 'Raison',
            withdrawal_downstream_systems: 'Systèmes en aval',
            withdrawal_downstream_placeholder: 'système-1, système-2',
            withdrawal_success: 'Le retrait s’est propagé avec succès.',
            withdrawal_error: 'Échec de la propagation du retrait.',
            withdrawal_data_subject_id: 'ID de la personne concernée',
            withdrawal_consent_record_id: "ID de l'enregistrement de consentement",
            withdrawal_data_subject_id_placeholder: 'ex. usr_abc123',
            withdrawal_consent_record_id_placeholder: 'ex. consent_xyz789',
            withdrawal_purposes: 'Objectifs retirés',
            withdrawal_purposes_placeholder: 'Sélectionnez les raisons pour lesquelles le consentement est retiré pour...',
            withdrawal_withdrawn_at: 'Date de retrait',
            withdrawal_tasks_created: 'Tâches créées',
            withdrawal_affected_bindings: 'Liaisons concernées',
            withdrawal_affected_activities: 'Activités de traitement concernées',
            withdrawal_history: 'Historique des retraits',
        },
        groups: {
            title: 'Groupes de propriétés',
            new: 'Nouveau groupe',
            group_name: 'Groupe Nom',
            member_count: 'Membres',
            no_groups: 'Aucun property groups trouvé',
            policies: 'Politiques',
            confirm_delete: 'Êtes-vous sûr de vouloir supprimer ce groupe ?',
        },
        bindings: {
            title: 'Matrice de liaisons',
            property: 'Propriété',
            purpose: 'Finalité',
            legal_basis: 'Base juridique',
            bound: 'Lié',
            no_bindings: 'Aucun bindings trouvé',
            bind_policy: 'Lier la stratégie',
            bulk_bind: 'Liaison en masse',
            bind_policy_to_property: 'Lier la stratégie à la propriété',
            bulk_bind_policy: 'Politique de liaison groupée',
            unbound: 'Non lié',
            policy_id: 'ID de la politique',
            version_placeholder: 'par exemple, 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Liaison',
            property_name: 'Nom de propriété',
            policy_name: 'Nom de politique',
            bindings_list: 'Liste des liaisons',
            binding_actions: {
                start_deploy: 'Démarrer le déploiement',
                mark_deployed: 'Marquer comme déployé',
                rollback: 'Annuler',
            },
        },
        change_requests: {
            title: 'Pipeline de demandes de modification',
            new: 'Nouveau changement demande',
            requester: 'Demandeur',
            change_type: 'Changement Type',
            property: 'Propriété',
            submitted_at: 'Soumis le',
            no_requests: 'Aucun change requests trouvé',
            fetch_pipeline_error: 'Échec de fetch pipeline. Veuillez réessayer.',
            fetch_list_error: 'Échec de fetch change requests. Veuillez réessayer.',
            create_error: 'Échec de create change request. Veuillez réessayer.',
            action_error: 'Échec de perform action. Veuillez réessayer.',
            approver_name: 'Approbateur',
            placeholder_target_props: 'identifiant-prop-1, identifiant-prop-2',
            view_all: 'Tout',
            view_my_properties: 'Mes Propriétés',
            stage_requests: 'Demandes {stage}',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Espace de comparaison',
            select_properties: 'Sélectionnez propriétés à comparer',
            compare: 'Comparer',
            no_comparison: 'Sélectionnez à le moins two propriétés à comparer.',
            fetch_error: 'Échec de fetch comparison reports. Veuillez réessayer.',
            run_error: 'Échec de run comparison. Veuillez réessayer.',
            save_notes_error: 'Échec de save notes. Veuillez réessayer.',
            scan_complete: 'Inconsistency scan terminé avec succès',
            scan_error: 'Échec de run inconsistency scan. Veuillez réessayer.',
            min_two_properties: 'Veuillez fournir au moins 2 ID de propriétés pour la comparaison.',
            view_inconsistencies: 'Voir les incohérences',
            click_to_select: 'Cliquez pour sélectionner deux propriétés ou plus',
            no_properties_available: 'Aucune propriété disponible',
            no_matching_properties: 'Aucune propriété correspondante',
            select_at_least_two_props: 'Sélectionnez au moins deux propriétés dans le menu déroulant.',
            no_differences_found: 'Aucune différence trouvée entre les propriétés sélectionnées.',
            found_inconsistencies: '{count} incohérences trouvées dans {properties} propriétés et {bindings} liaisons.',
        },
        inconsistencies: {
            title: "Liste d'incohérences",
            property: 'Propriété',
            field: 'Champ',
            expected: 'Attendu',
            actual: 'Réel',
            severity: 'Gravité',
            no_inconsistencies: 'Aucun inconsistencies trouvé',
            trend: "Tendance d'incohérence",
            trend_chart_label: 'Graphique de tendance des incohérences montrant les décomptes au fil du temps',
            acknowledge: 'Accuser réception',
            resolve: 'Résoudre',
            suppress: 'Supprimer',
            cr_id_placeholder: 'ID CR',
            view_all: 'Tout',
            view_my_properties: 'Mes Proprietes',
            view_dpo_review: "DPO File d'attente de révision",
            change_request_id_required: 'L’ID de demande de modification est requis pour résoudre cette incohérence.',
        },
        snapshot_schedules: {
            title: "Planifications d'instantanés",
            new: 'Nouveau planification',
            schedule_name: 'Planification Nom',
            frequency: 'Fréquence',
            next_run: 'Suivant Exécuter',
            last_run: 'Dernier Exécuter',
            no_schedules: 'Aucun snapshot schedules trouvé',
            target_type: 'Type de cible',
            target_id: 'ID cible',
            custom_frequency_days: 'Fréquence personnalisée (jours)',
            time_of_day: 'Heure de la journée',
            time: 'Temps',
            retention_days: 'Rétention (jours)',
            execute_now: 'Exécuter maintenant',
            placeholder_days: 'par exemple, 10',
            target_name: 'Nom de la cible',
        },
        golden_templates: {
            title: 'Modèles de référence',
            new: 'Nouveau modèle',
            template_name: 'Modèle Nom',
            property_type: 'Propriété Type',
            no_templates: 'Aucun golden templates trouvé',
            applicable_group_ids: 'ID de groupe applicables (séparés par des virgules)',
            applicable_groups: 'Groupes applicables',
            purposes: 'Objectifs',
            legal_bases: 'Bases juridiques',
            banner_config: 'Configuration de la bannière',
            consent_settings: 'Paramètres de consentement',
            group_ids_placeholder: 'identifiant-groupe-1, identifiant-groupe-2',
            apply_to_property: 'Appliquer à la Propriété',
            apply_to_group: 'Appliquer au Groupe',
            apply_success: 'Modèle appliqué avec succès.',
            applications: 'Historique des applications',
            no_applications: 'Aucune application de modèle pour le moment.',
            no_configuration_details: "Le modèle n'a pas de détails de configuration. Modifiez le modèle pour ajouter des finalités, des bases légales, des paramètres de bannière ou des paramètres de consentement.",
        },
        rollouts: {
            title: 'Déploiements de modèles',
            rollout_status: 'Statut du déploiement',
            confirmed: 'Confirmé',
            pending: 'En attente',
            failed: 'Échoué',
            confirm: 'Confirmer la Propriété',
            confirm_success: 'Propriété confirmée dans le déploiement.',
            can_close: 'Toutes les propriétés confirmées — déploiement terminé',
            cannot_close: 'En attente des confirmations de propriétés',
            no_rollouts: 'Aucun déploiement trouvé.',
            in_progress: 'En cours',
            completed: 'Terminé',
            cancelled: 'Annulé',
            reject: 'Rejeter la Propriété',
            reject_success: 'Propriété rejetée du déploiement.',
            confirm_all: 'Tout Confirmer',
            reject_all: 'Tout Rejeter',
            confirm_label: 'Confirmer le déploiement pour',
            reject_label: 'Rejeter le déploiement pour',
            confirm_all_label: 'Confirmer Tous les Déploiements en Attente',
            reject_all_label: 'Rejeter Tous les Déploiements en Attente',
            no_properties_in_rollout: "Ce déploiement n'a aucune propriété.",
        },
        analytics: {
            title: 'Analyse des propriétés',
            opt_in_rate: "Taux d'adhésion",
            opt_out_rate: 'Taux de refus',
            withdrawal_rate: 'Taux de retrait',
            total_consents: 'Total des consentements',
            total_withdrawals: 'Total des retraits',
            compliance_score: 'Score de conformité',
            non_compliant: 'Propriétés non conformes',
            no_non_compliant: 'Toutes les propriétés sont conformes.',
            threshold: 'Seuil de conformité',
            property_id: 'ID de propriété',
            compliant: 'Conforme',
            non_compliant_status: 'Non conforme',
            compliance_help_summary: 'Comment le score de conformité est-il calculé et comment y remédier ?',
            compliance_help_intro: 'Une propriété est signalée comme non conforme lorsque son score est inférieur à 80 %. Le score est calculé comme suit :',
            compliance_help_with_template_label: 'Si un Golden Template est appliqué :',
            compliance_help_with_template_text: '% de clés de configuration (objectifs, legal_bases, banner_config, consent_settings) qui correspondent exactement au modèle.',
            compliance_help_no_template_label: 'Si aucun Golden Template n’est appliqué :',
            compliance_help_no_template_text: '% de liaisons (types de consentement attribués à la propriété) avec deployment_status = déployé. Une propriété sans liaison est définie par défaut sur 100 %.',
            compliance_help_fix_intro: "Pour supprimer une propriété de cette liste, effectuez l'une des opérations suivantes :",
            compliance_help_fix_deploy_label: 'Déployer ses liaisons',
            compliance_help_fix_deploy_text: "ouvrez la page Binding Matrix et définissez l'état de déploiement de chaque liaison sur déployé.",
            compliance_help_fix_template_label: 'Appliquer un modèle doré',
            compliance_help_fix_template_text: 'utilisez Appliquer au groupe sur le modèle, puis confirmez le déploiement de cette propriété. Assurez-vous que la configuration de la propriété correspond au modèle.',
            compliance_help_fix_decommission_label: 'Mettre la propriété hors service',
            compliance_help_fix_decommission_text: "utilisez le bouton Mettre hors service dans le modal de détail de la propriété s'il n'est plus utilisé.",
            compliance_help_fix_no_bindings_label: 'Supprimer toutes les liaisons',
            compliance_help_fix_no_bindings_text: "si une propriété n'a ni modèle ni liaisons, elle obtient un score de 100 % par défaut.",
        },
        evidence_packs: {
            title: 'Paquets de preuves',
            new: 'Nouveau Pack',
            pack_name: 'Nom du pack',
            properties: 'Propriétés',
            generated_at: 'Généré le',
            no_packs: 'Aucun evidence packs trouvé',
            generate: 'Générer',
            content_type_consent_records: 'Consentement Enregistrements',
            content_type_policy_versions: 'Versions de la politique',
            content_type_binding_history: 'Historique de liaison',
            content_type_change_requests: 'Changement Demandes',
            content_type_inconsistency_reports: "Rapports d'incohérence",
            content_type_snapshot_data: "Données d'instantané",
            content_type_audit_trail: "Piste d'audit",
        },
        audit: {
            title: 'Audit des propriétés de consentement',
            event_type: 'Événement Type',
            entity_type: "Type d'entité",
            entity_id: "ID de l'entité",
            actor: 'Acteur',
            timestamp: 'Horodatage',
            no_events: 'Aucun audit events trouvé',
            entity_type_property: 'Propriété',
            entity_type_group: 'Groupe',
            entity_type_binding: 'Liaison',
            entity_type_change_request: 'Demande de modification',
            entity_type_golden_template: 'Modèle de référence',
            entity_type_snapshot_schedule: 'Planification instantané',
            entity_type_inconsistency: 'Incohérence',
            entity_type_evidence_pack: 'Dossier de preuves',
            action_created: 'Créé',
            action_updated: 'Mis à jour',
            action_deleted: 'Supprimé',
            action_decommissioned: 'Mis hors service',
            action_status_changed: 'Statut modifié',
            action_submitted: 'Soumis',
            action_approved: 'Approuvé',
            action_rejected: 'Rejeté',
            action_deployed: 'Déployé',
            action_rolled_back: 'Annulé',
            action_paused: 'En pause',
            action_resumed: 'Repris',
            action_cancelled: 'Annulé',
            action_acknowledged: 'Acquitté',
            action_resolved: 'Résolu',
            action_suppressed: 'Supprimé',
            action_executed: 'Exécuté',
        },
        property_types: {
            website: 'Site web',
            mobile_app: 'Application mobile',
            ott: 'OTT',
            iot_device: 'Appareil IoT',
            kiosk: 'Kiosque',
            api_service: 'Service API',
            sdk: 'SDK',
        },
        environments: {
            production: 'Fabrication',
            staging: 'Préproduction',
            development: 'Développement',
            testing: 'Test',
        },
        group_types: {
            brand: 'Marque',
            region: 'Région',
            product_line: 'Ligne de produits',
            business_unit: 'Unité commerciale',
            custom: 'Personnalisé',
        },
        target_types: {
            property: 'Propriété',
            group: 'Groupe',
            all: 'Tous',
        },
        frequencies: {
            hourly: 'Toutes les heures',
            daily: 'Quotidien',
            weekly: 'Hebdomadaire',
            monthly: 'Mensuel',
            custom: 'Personnalisé',
        },
        binding_statuses: {
            current: 'Actuel',
            outdated: 'Obsolète',
            pending: 'En attente',
            deploying: 'En déploiement',
            rolled_back: 'Annulé',
        },
        sensitive_categories: {
            health: 'Données de santé',
            biometric: 'Données biométriques',
            genetic: 'Données génétiques',
            racial_ethnic: 'Origine raciale/ethnique',
            political_opinions: 'Opinions politiques',
            religious_beliefs: 'Croyances religieuses',
            trade_union: 'Adhésion syndicale',
            sexual_orientation: 'Orientation sexuelle',
            criminal_records: 'Casier judiciaire',
            financial: 'Données financières',
            children: 'Données sur les enfants',
            geolocation: 'Géolocalisation précise',
        },
        export_csv: 'Exporter CSV',
        exporting: 'Exportation...',
        page_x_of_y: 'Page {page} sur {total}',
        regulatory: {
            capture_method_guidance: "PDPL nécessite une preuve de consentement pour correspondre au canal de collecte. Les types de propriétés correspondent aux méthodes de capture du consentement : site Web/OTT = consentement basé sur une bannière, application mobile/SDK = flux de consentement dans l'application, kiosque = capture du consentement physique/à l'écran, service API = consentement programmatique, appareil IoT = mécanisme de consentement au niveau de l'appareil.",
            dashboard_metrics_info: "Ce tableau de bord fournit au DPO une vue unique de l'état de consentement sur toutes les propriétés numériques. Les mesures clés incluent : le pourcentage de couverture de liaison (liaisons déployées par rapport au total), l'adhésion au modèle via le widget d'incohérences, les demandes de modification en attente nécessitant un examen et la répartition de l'état des propriétés montrant les propriétés avec des états actifs, inactifs ou mis hors service. Les propriétés comportant des catégories de données sensibles sont suivies via le registre immobilier.",
            change_request_impact_info: "Les demandes de modification affectant les propriétés du consentement doivent documenter l'impact réglementaire : quelles exigences PDPL sont concernées, si la modification affecte le traitement des données sensibles (Art. 2.4) et si la modification nécessite un nouveau consentement des personnes concernées. Le résumé d'impact dans chaque demande de modification détaille les références réglementaires PDPL pour les éléments probants.",
            outdated_binding_warning: "Les liaisons avec le statut « obsolète » indiquent qu'une propriété exécute une version de stratégie qui n'est pas à jour. Ces propriétés peuvent ne pas refléter les dernières exigences PDPL ou les mises à jour des règles de l'organisation. Examinez et mettez à jour rapidement les liaisons obsolètes pour maintenir la conformité PDPL dans toutes les propriétés de consentement.",
        },
    },
    localization: {
        nav: {
            dashboard: 'Tableau de bord',
            market_packs: 'Packs de marché',
            language_packs: 'Packs linguistiques',
            frameworks: 'Cadres réglementaires',
            mappings: 'Correspondances de modules',
            submissions: 'Soumissions de packs',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Aperçu de la traduction',
            audit: "Piste d'audit",
        },
        dashboard: {
            title: 'Tableau de bord de localisation',
            loading: 'Chargement de dashboard data...',
            active_market_packs: 'Actif Marché Packs',
            published_packs_available: 'Packs marché publiés disponibles',
            language_packs: 'Packs linguistiques',
            total_language_packs: 'Total de packs linguistiques enregistrés',
            active_regulatory_frameworks: 'Actif Regulatory Cadres',
            frameworks_active: 'Cadres avec statut actif',
            pending_pack_submissions: 'Soumissions de packs en attente',
            submissions_awaiting_review: 'Soumissions en attente de révision',
            recent_audit_events: 'Récent Audit Événements',
            no_recent_audit: "Aucun événement d'audit récent.",
            event_type: 'Événement Type',
            entity_type: "Type d'entité",
            entity_id: "ID de l'entité",
            actor: 'Acteur',
            timestamp: 'Horodatage',
        },
        market_packs: {
            title: 'Catalogue de packs de marché',
            new: 'Nouveau Market Pack',
            pack_name: 'Nom du pack',
            market: 'Marché',
            locale: 'Langue',
            no_packs: 'Aucun market packs trouvé',
        },
        language_packs: {
            title: 'Packs linguistiques',
            new: 'Nouveau Language Pack',
            language: 'Langue',
            locale_code: 'Code de langue',
            completeness: 'Complétude',
            completeness_detail: "Détail de l'exhaustivité",
            overall_completeness: 'exhaustivité globale',
            total_keys: 'Clés totales',
            translated_keys: 'Clés traduites',
            missing_keys: 'Clés manquantes',
            namespace_breakdown: "Répartition de l'espace de noms",
            namespace: 'Espace de noms',
            translated: 'Traduit',
            no_packs: 'Aucun language packs trouvé',
            install_language_pack: 'Installer le pack de langue',
            available_packs: 'Packs de langue disponibles',
            install: 'Installer',
            all_installed: 'Tous les packs de langue sont déjà installés.',
            install_success: 'Pack de langue installé avec succès.',
            market: 'Marché',
            featured: 'En vedette',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Cadres réglementaires',
            new: 'Nouveau cadre',
            framework_name: 'Cadre Nom',
            jurisdiction: 'Juridiction',
            effective_date: "Date d'effet",
            no_frameworks: 'Aucun regulatory frameworks trouvé',
            view_requirements: 'Voir les exigences',
            hide_requirements: 'Masquer les exigences',
            show_legacy: 'Afficher les cadres réglementaires anciens',
            effective_until: "En vigueur jusqu'au",
            effective_on: 'En vigueur à partir du',
            replaced_by: 'Remplacé par',
            replaces_label: 'Remplace',
            status_active: 'Actif',
            status_draft: 'Brouillon',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Ancien',
            status_upcoming: 'À venir',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Liste de surveillance',
            watchlist_empty: 'Aucun projet ou cadre à venir',
            watchlist_empty_desc: 'Les frameworks avec le statut « projet » ou « à venir » apparaîtront ici pour être surveillés.',
            expected_effective: 'Efficacité attendue',
            effective_date_tbd: "Date d'entrée en vigueur : à déterminer",
            activate: 'Activer',
            ai_law_title: 'Loi vietnamienne AI',
            ai_risk_classification: 'AI Classification des risques',
            ai_system_dossier: 'AI Dossier système',
            ai_labeling_checklist: 'AI Étiquetage et divulgation',
            ai_incident_report: "AI Rapport d'incident",
            ai_impact_assessment: "AI Évaluation d'impact",
            ai_sandbox_application: 'Application bac à sable',
            ai_sandbox_monitoring: 'Surveillance du bac à sable',
            ai_sandbox_completion: 'Achèvement du bac à sable',
            ai_submission_tracker: 'Suivi des soumissions',
            ai_evidence_capture: 'Capture de preuves',
            risk_level_minimal: 'Risque minimal',
            risk_level_limited: 'Risque limité',
            risk_level_high: 'Risque élevé',
            risk_level_unacceptable: 'Risque inacceptable',
            submission_status_draft: 'Brouillon',
            submission_status_submitted: 'Soumis',
            submission_status_returned: 'Renvoyé pour modification',
            submission_status_accepted: 'Accepté',
            submission_status_rejected: 'Rejeté',
            submission_status_closed: 'Fermé',
            ecommerce_law_title: 'Loi vietnamienne sur le commerce électronique',
            ecommerce_platform_classification: 'Classification des plateformes',
            ecommerce_seller_register: 'Registre des vendeurs',
            ecommerce_complaint_register: 'Registre des plaintes',
            ecommerce_takedown_register: 'Registre de retrait',
            ecommerce_submission_tracking: 'Suivi des soumissions',
            ecommerce_dossier_checklist: 'Liste de contrôle des dossiers',
            ecommerce_cross_border: 'Conformité transfrontalière',
            ecommerce_trustmark: 'Programme de marque de confiance',
            ecommerce_notification: 'Notification',
            ecommerce_registration: 'Inscription',
            ecommerce_termination: 'Résiliation',
            ecommerce_reporting_readiness: 'Préparation aux rapports',
            ecommerce_evidence_capture: 'Capture de preuves',
            submission_status_approved: 'Approuvé',
            cybersec_law_2025_title: 'Loi vietnamienne sur la cybersécurité (2025)',
            cybersec_system_classification: 'Classification du système',
            cybersec_risk_assessment: 'Évaluation des risques',
            cybersec_incident_report: "Rapport d'incident",
            cybersec_authority_cooperation: 'Coopération entre autorités',
            cybersec_data_localization: 'Localisation des données',
            cybersec_vendor_security: 'Sécurité des fournisseurs',
            cybersec_submission_tracker: 'Suivi des soumissions',
            cybersec_evidence_capture: 'Capture de preuves',
            cybersec_dossier_checklist: 'Liste de contrôle des dossiers',
            cybersec_classification_general: 'Général',
            cybersec_classification_important: 'Important',
            cybersec_classification_critical: 'Critique',
            data_law_title: 'Loi vietnamienne sur les données',
            data_inventory: 'Inventaire des données',
            data_classification: 'Classification des données',
            data_sharing: 'Partage de données',
            data_governance: 'Gouvernance des données',
            data_submission_tracker: 'Suivi des soumissions',
            data_evidence_capture: 'Capture de preuves',
            data_dossier_checklist: 'Liste de contrôle des dossiers',
            telecom_law_title: 'Loi vietnamienne sur les télécommunications',
            telecom_authority_request: "Demande d'autorisation",
            telecom_disclosure: 'Divulgation',
            telecom_confidentiality: 'Confidentialité',
            telecom_submission_tracker: 'Suivi des soumissions',
            telecom_evidence_capture: 'Capture de preuves',
            telecom_dossier_checklist: 'Liste de contrôle des dossiers',
            pdpl_law_title: 'Loi vietnamienne sur la protection des données personnelles',
            pdpl_breach_notification: 'Notification de violation',
            pdpl_dpo_notification: 'DPO Notification de rendez-vous',
            pdpl_data_subject_notification: 'Notification de la personne concernée',
            pdpl_submission_tracker: 'Suivi des soumissions',
            pdpl_evidence_capture: 'Capture de preuves',
            pdpl_dossier_checklist: 'Liste de contrôle des dossiers',
            pdpl_authority_filing: "Dépôt d'autorité",
            pdpl_mops_filing: 'Dépôt MoPS',
        },
        mappings: {
            title: 'Correspondances de modules',
            new: 'Nouveau correspondance',
            module: 'Composant',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Consentement',
            module_dpia: 'DPIA',
            module_incident: 'Incident',
            module_rights_request: 'Demande de droits',
            module_data_mapping: 'Cartographie des données',
            module_classification: 'Classement',
            module_monitoring: 'Surveillance',
            module_governance: 'Gouvernance',
            module_vendor: 'Vendeur',
            module_risk: 'Risque',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Pack marché',
            mapping_status: 'Correspondance Statut',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Aucun module correspondances trouvé',
            incomplete_entries_warning: "{count} correspondance(s) masquée(s) en raison d'un ID de cadre, d'un ID d'exigence ou d'un module manquant.",
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Soumissions de packs',
            new: 'Nouveau Submission',
            submitter: 'Soumetteur',
            pack: 'Lot',
            submitted_at: 'Soumis le',
            no_submissions: 'Aucun pack submissions trouvé',
            installed_sector_overlays_title: 'Sector Overlays ({count}) [FR]',
            installed_sector_overlays_aria: 'Installed Sector Overlays [FR]',
            installed_col_name: 'Name [FR]',
            installed_col_code: 'Code [FR]',
            installed_col_sector: 'Sector [FR]',
            installed_col_mode: 'Mode [FR]',
            installed_col_status: 'Status [FR]',
            installed_refresh: 'Refresh [FR]',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Aperçu de la traduction',
            select_locale: 'Sélectionnez Locale',
            select_module: 'Sélectionnez Module',
            key: 'Key',
            value: 'Valeur',
            no_translations: 'Aucun translations trouvé',
        },
        audit: {
            title: "Piste d'audit de localisation",
            event_type: 'Événement Type',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: "Type d'entité",
            entity_id: "ID de l'entité",
            entity_name: "Nom de l'entité",
            actor: 'Acteur',
            timestamp: 'Horodatage',
            payload: 'Charge utile',
            collapse_payload: 'Réduire les détails de la charge',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Développer les détails de la charge',
            collapse: 'Réduire',
            expand: 'Développer',
            all_entity_types: "Tous les types d'entité",
            no_events: 'Aucun audit events trouvé',
        },
        market_packs_detail: {
            installed_at: 'Installé le',
            pack_details_label: 'Détails du pack',
            market_code: 'Code marché',
            supported_locales: 'Langues prises en charge',
            min_platform_version: 'Version minimale de la plateforme',
            pack_information: 'Information du pack',
            pack_contents: 'Contenu du pack',
            included_frameworks: 'Cadres inclus',
            pack_artifacts: 'Artefacts du pack',
            no_artifacts: 'Aucun artefact trouvé pour ce pack',
            pack_not_found: 'Pack introuvable',
        },
        mappings_form: {
            framework_id: 'ID du cadre',
            requirement_id: "ID de l'exigence",
            module_label: 'Module',
            mapping_type: 'Type de mappage',
            configuration_json: 'Configuration (JSON)',
            conflict_resolution: 'Résolution de conflit',
            is_override: 'Est une surcharge',
            all_modules: 'Tous les modules',
            all_mapping_types: 'Tous les types de mappage',
        },
        submissions_detail: {
            submission_id: 'ID de soumission',
            validation: 'Validation',
            reviewers: 'Réviseurs',
            start_review: 'Démarrer la revue',
            hide_comments: 'Masquer les commentaires',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Commentaires de revue',
            loading_comments: 'Chargement des commentaires...',
            no_comments: 'Pas encore de commentaires.',
            add_comment: 'Ajouter un commentaire',
            reject_submission: 'Rejeter la soumission',
            reason_for_rejection: 'Motif du rejet',
            reviewer_ids: 'IDs des réviseurs (séparés par des virgules)',
            submit_new_pack: 'Soumettre un nouveau pack',
            pack_type: 'Type de pack',
            market_code: 'Code marché',
            target_locales: 'Langues cibles',
            pack_file: 'Fichier du pack',
            pack_format_info: "Format du pack : les packs de marché doivent inclure des fichiers JSON spécifiques aux paramètres régionaux avec des traductions d'espace de noms/clés. Les modules linguistiques doivent fournir des traductions pour les paramètres régionaux cibles spécifiés. Voir la documentation pour le schéma détaillé.",
            accepted_formats: 'Formats acceptés : .json ou .zip. Les fichiers JSON doivent contenir un objet plat ou avec un espace de noms de paires clé-valeur de traduction. Les fichiers ZIP doivent contenir un ou plusieurs fichiers de paramètres régionaux JSON.',
            comma_separated_locales: 'Codes de langue séparés par des virgules pour ce pack linguistique',
            confirm_approval: "Confirmer l'approbation",
            approve_message: 'Approuver cette soumission de pack ?',
            submission_label: 'Soumission',
            all_pack_types: 'Tous les types de pack',
            passed: 'réussi',
            failed_label: 'échoué',
            market_code_required: 'Le code marché est requis',
            version_required: 'La version est requise',
            pack_type_regulatory: 'Réglementaire',
            framework_code: 'Code du cadre',
            jurisdiction_code: 'Code de juridiction',
            pack_source: 'Origine',
            pack_source_manual: 'Manuel',
            pack_version: 'Version du paquet',
            install_regulatory_pack: 'Installer le paquet réglementaire',
            regulatory_pack_desc: 'Téléverser un paquet de cadre réglementaire (.zip)',
            framework_required: 'Le code du cadre réglementaire est requis',
            jurisdiction_required: 'Le code de juridiction est requis',
        },
        catalog_detail: {
            packs_installed_of: 'sur {total} packs installés',
            install_all: 'Tout installer',
            installing: 'Installation en cours...',
            removing: 'Suppression...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Nécessite',
            tab_catalog: 'Catalogue',
            tab_installed: 'Installé',
            details: 'Détails',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Langue source',
            target_locale: 'Langue cible',
            locales_must_differ: 'Les langues source et cible doivent être différentes.',
            all_keys_translated: 'Toutes les clés traduites',
            keys_missing: '{count} clé(s) manquante(s)',
            show_missing_only: 'Afficher uniquement les manquants',
            namespace: 'Espace de noms',
            source: 'Origine',
            target: 'Cible',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Cadres',
            tab_watchlist: 'Liste de surveillance',
            authority: 'Autorité',
            requirements_count: 'Exigences',
            all_types: 'Tous les types',
            collapse_all: 'Tout réduire',
            expand_all: 'Tout développer',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtré à',
            clear_filter: 'Effacer le filtre',
            no_requirements: 'Aucune exigence trouvée pour ce cadre.',
            requirements_label: 'Exigences',
            status_updated: 'Statut mis à jour avec succès',
            overview_tab: 'Aperçu',
            forms_tab: 'Formulaires',
            procedures_tab: 'Procédures',
            fines_risks_tab: 'Amendes et risques',
            deadlines_tab: 'Délais',
            guidance_tab: 'Conseils',
            guidance_empty: "Aucun contenu d'orientation pour ce cadre.",
            guidance_loading: 'Guide de chargement…',
            guidance_section: "Section d'orientation",
            guidance_view: 'Afficher les conseils',
            guidance_map_to_workflow: 'Mapper au flux de travail',
            guidance_link_evidence: 'Lien vers les preuves',
            guidance_filter_type: 'Tapez',
            guidance_filter_category: 'Catégorie',
            guidance_filter_module: 'Module',
            guidance_filter_status: 'Statut',
            guidance_filter_search: 'Guide de recherche',
            guidance_filter_all: 'Tout',
            guidance_table_reference: 'Référence',
            guidance_table_name: 'Nom du guide',
            guidance_table_type: 'Tapez',
            guidance_table_category: 'Catégorie',
            guidance_table_effective_date: "Date d'entrée en vigueur",
            guidance_table_modules: 'Modules associés',
            guidance_table_actions: 'Actions',
            guidance_reference_only_banner: "Référence uniquement : s'applique au secteur {{sector}} mais pas à votre organisation.",
            guidance_open_official_source: 'Ouvrir la source officielle',
            guidance_no_detail: 'Aucun détail supplémentaire disponible pour cette directive.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Conseils connexes',
            deadlines_empty: 'Aucun délai réglementaire trouvé pour ce cadre.',
            deadlines_sla_type: 'Type de SLA',
            deadlines_value: 'Délai',
            deadlines_unit: 'Unité',
            deadlines_extension: 'Rallonge',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Référence juridique',
            framework_stack: 'Structure du cadre',
            layer_parent_law: 'Loi principale',
            layer_implementing_decree: "Décret d'application",
            layer_enforcement_decree: 'Décret de sanctions',
            layer_admin_procedures: 'Procédures administratives',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Instruments subordonnés',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Actif',
            status_draft: 'Projet',
            draft_enforcement_badge: 'Projet — pas encore promulgué',
            effective_date_draft: 'Projet — pas encore en vigueur',
            effective_date: "Date d'entrée en vigueur",
            forms_table_id: 'ID formulaire',
            forms_table_name: 'Nom',
            forms_table_source: 'Source',
            forms_table_category: 'Catégorie',
            forms_table_type: 'Tapez',
            fill_form: 'Remplir le formulaire',
            view_reference: 'Voir la référence',
            forms_empty: 'Aucun formulaire trouvé pour ce cadre.',
            forms_loading: 'Chargement des formulaires...',
            filter_by_source_layer: 'Filtrer par couche source',
            all_layers: 'Tous les calques',
            procedures_id: 'ID procédure',
            procedures_name: 'Procédure',
            procedures_trigger: 'Déclencheur',
            procedures_steps: 'Étapes',
            procedures_outputs: 'Résultats',
            procedures_related_forms: 'Formulaires associés',
            procedures_submission_channels: 'Canaux de soumission',
            procedures_empty: 'Aucune procédure trouvée pour ce cadre.',
            fines_area: "Zone d'infraction",
            fines_articles: 'Articles',
            fines_individual: 'Amende (individuel)',
            fines_organization: 'Amende (organisation)',
            fines_revenue: "Escalade sur le chiffre d'affaires",
            fines_deadlines: 'Délais',
            fines_remediation: 'Mesures correctives',
            fines_related: 'Procédures associées',
            fines_empty: 'Aucune donnée de sanctions disponible pour ce cadre.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Type de risque',
            fines_risk_triggers: 'Élément déclencheur',
            fines_risk_only_note: 'Ces entrées décrivent un risque de conformité et opérationnel, et non des sanctions pécuniaires. Le cas échéant, les amendes sont définies dans le cadre parent.',
            fines_enforcement_pending: "Cadre de sanctions non encore publié. Les données de sanctions seront ajoutées lors de la promulgation de l'instrument d'application.",
            view_details: 'Voir les détails',
            back_to_list: 'Retour à la liste',
            description: 'Descriptif',
            jurisdiction: 'Juridiction',
            lineage: 'Filiation',
            replaces: 'Remplace',
            replaced_by: 'Remplacé par',
            requirements: 'Exigences',
            tab_navigation: 'Onglets de détail du cadre',
            forms_table_actions: 'Actions',
            create_form: 'Créer un formulaire',
            mark_promulgated: 'Marquer comme promulgué',
            status_promulgated: 'Promulgué',
            promulgated_success: 'Statut de la couche mis à jour en promulgué',
            transition_review: 'Revue de transition',
            transition_review_desc: "Ce cadre a une revue de transition en attente pour l'adoption de la loi successeur.",
            transition_review_by: 'Date limite de revue',
            transition_review_status: 'Statut de la revue',
            transition_review_notes: 'Remarques',
            transition_review_pending: 'En attente',
            transition_review_in_progress: 'En cours',
            transition_review_completed: 'Terminée',
            transition_review_successor: 'Cadre successeur',
            transition_review_lead_time: 'Délai de préparation',
            transition_review_lead_time_days: "{days} jours avant la date d'entrée en vigueur",
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Revues de transition des formulaires',
            transition_review_forms_desc: 'Vérifiez la pertinence de chaque modèle opérationnel dans le cadre successeur.',
            transition_review_outcome: 'Résultat',
            transition_review_outcome_carry_forward: 'Conserver',
            transition_review_outcome_carry_forward_revised: 'Conserver (révisé)',
            transition_review_outcome_retire: 'Retirer',
            transition_review_outcome_split: 'Scinder',
            transition_review_outcome_awaiting_decree: 'En attente de décret',
            transition_review_no_forms: 'Aucune revue de transition de formulaire configurée.',
            transition_review_update_success: 'Revue de transition mise à jour avec succès',
            transition_review_operational_note: "Ce sont des modèles opérationnels de soutien aux processus, pas des décomptes de formulaires réglementaires. L'attribution juridique reste au cadre ancien jusqu'à l'entrée en vigueur de la loi successeur.",
            rights_tab: 'Droits',
            rights_empty: 'Aucun droit des personnes concernées défini pour ce cadre.',
            rights_code: 'Coder',
            rights_name: 'Droit',
            rights_legal_basis: 'Base juridique',
            rights_elaborating_articles: 'Articles détaillés',
            rights_description: 'Descriptif',
            requirements_reference: 'Référence',
            requirements_mandatory: 'Obligatoire',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Origine',
            category_legal_basis: 'Base juridique',
            category_data_subject_rights: 'Droits des personnes concernées',
            category_notification: 'Notification',
            category_assessment: 'Évaluation',
            category_governance: 'Gouvernance',
            category_record_keeping: 'Tenue de registres',
            category_cross_border: 'Transfrontalier',
            category_technical_measures: 'Mesures techniques',
            category_incident_reporting: "Rapport d'incident",
            category_risk_management: 'Gestion des risques',
            category_supply_chain: "Chaîne d'approvisionnement",
            category_certification: 'Attestation',
            child_frameworks: '{count} framework enfant',
            child_frameworks_plural: '{count} frameworks enfants',
            replaces_prefix: 'Remplace :',
            replaced_by_prefix: 'Remplacé par :',
            responsible_prefix: 'Responsable :',
            watchlist_empty_hint: 'Les frameworks avec le statut « projet » ou « à venir » apparaîtront ici pour être surveillés.',
            framework_count: 'Cadre {count}',
            framework_count_plural: '{count} cadres',
            hiding_legacy: "(cachant l'héritage de {count})",
            label_VN_PDPD_DECREE_13: 'Décret PDPD 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Loi 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Décret 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: "PDPL Projet d'application 2026",
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Loi sur les données',
            label_VN_DATA_LAW_DECREE_165_2025: 'Décret-loi sur les données 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Bourses de données Draft 2026',
            label_VN_TELECOM_LAW_2023: 'Loi sur les télécommunications 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Décret Télécom 163/2024',
            label_VN_CYBERSEC_LAW: 'Loi Cybersécurité (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Décret Cybersec 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Loi sur la cybersécurité 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'Loi sur le commerce électronique 122/2025',
            label_VN_AI_LAW_2026: 'AI Loi 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Entreprise',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Autorité',
            sla_type_rights_request_ack: 'Accusé de réception de la demande de droits',
            'sla_type_Access control review': "Révision du contrôle d'accès",
            'sla_type_Annual governance attestation': 'Attestation annuelle de gouvernance',
            'sla_type_Audit review cycle': "Cycle d'examen des audits",
            'sla_type_Authority request response': "Réponse à la demande d'autorité",
            'sla_type_Change notification processing': 'Traitement des notifications de modification',
            'sla_type_Complaint acknowledgment': 'Accusé de réception de la plainte',
            'sla_type_Complaint resolution': 'Résolution des plaintes',
            'sla_type_Confidentiality policy review': 'Examen de la politique de confidentialité',
            'sla_type_Cybersecurity incident full report': 'Rapport complet sur les incidents de cybersécurité',
            'sla_type_Cybersecurity incident initial report': "Rapport initial d'incident de cybersécurité",
            'sla_type_Data inventory review': "Examen de l'inventaire des données",
            'sla_type_Disclosure log review cycle': 'Cycle d’examen du journal de divulgation',
            'sla_type_Incident classification decision': "Décision de classification de l'incident",
            'sla_type_Information system classification review': "Revue de la classification des systèmes d'information",
            'sla_type_Notification processing (sales website/app)': 'Traitement des notifications (site/application de vente)',
            'sla_type_Pen test cycle': 'Cycle de test du stylet',
            'sla_type_Registration processing (service platform)': 'Traitement des inscriptions (plateforme de services)',
            'sla_type_Risk assessment cycle': "Cycle d'évaluation des risques",
            'sla_type_Takedown decision (after review)': 'Décision de retrait (après examen)',
            'sla_type_Termination notice (public)': 'Avis de résiliation (public)',
            sla_type_ai_impact_assessment_completion: "AI achèvement de l'évaluation d'impact",
            sla_type_ai_sandbox_application_review: "AI examen de l'application sandbox",
            sla_type_ai_serious_incident_report: "AI rapport d'incident grave",
            sla_type_ai_system_registration: 'Enregistrement du système AI',
            sla_type_authority_request_response: "Réponse à la demande d'autorité",
            sla_type_certificate_issuance_review: 'Examen de la délivrance du certificat',
            sla_type_complaint_handling_approach_notice: 'Avis d’approche de traitement des plaintes',
            sla_type_complaint_incomplete_info_request: "Réclamation demande d'informations incomplète",
            sla_type_data_governance_attestation: 'Attestation de gouvernance des données',
            sla_type_dsr_acknowledgment: 'Accusé de réception DSR',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Retirer/restreindre/objecter',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Tiers',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Accéder / corriger / fournir',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Tiers',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Suppression',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Suppression, tiers',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Mesures de protection',
            sla_type_telecom_authority_request_response: "Réponse à la demande de l'autorité des télécommunications",
            sla_type_rights_request_completion: 'Finalisation de la demande de droits',
            sla_type_incident_authority_notification: "Notification à l'autorité d'incident",
            sla_type_incident_subject_notification: "Notification du sujet de l'incident",
            sla_type_dpia_completion: 'DPIA achèvement',
            sla_type_data_retention_review: 'Examen de la conservation des données',
            sla_type_authority_filing_review: "Examen du dépôt d'autorisation",
            sla_type_authority_supplement_response: 'Réponse au supplément d’autorité',
            sla_type_authority_resubmission: "Nouvelle soumission à l'autorité",
            sla_type_authority_filing_supplement_response: "Réponse supplémentaire au dépôt d'autorité",
            sla_type_authority_filing_resubmission: "Nouvelle soumission de la demande d'autorisation",
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'jours ouvrables',
            unit_calendar_days: 'jours calendaires',
            unit_hours: 'heures',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Consentement',
            form_category_privacy: 'Confidentialité',
            form_category_breach: 'Violation',
            form_category_dpia: 'DPIA',
            form_category_register: "S'inscrire",
            form_category_monitoring: 'Surveillance',
            form_category_vendor: 'Vendeur',
            form_category_org: 'Organisationnel',
            form_category_sector: 'Secteur',
            form_category_data_mapping: 'Cartographie des données',
            form_category_governance: 'Gouvernance',
            form_category_incident: 'Incident',
            form_category_authority_submission: "Soumission à l'autorité",
            form_category_notice: 'Avis',
            form_category_assessment_report: "Rapport d'évaluation",
            form_category_request: 'Demande',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Dépôt',
            module_consent: 'Consentement',
            module_rights_request: 'Demande de droits',
            module_classification: 'Classement',
            module_dpia: 'DPIA',
            module_governance: 'Gouvernance',
            module_monitoring: 'Surveillance',
            module_register: "S'inscrire",
            module_risk: 'Risque',
            module_data_mapping: 'Cartographie des données',
            module_incident: 'Incident',
            module_assessment: 'Évaluation',
            module_vendor: 'Vendeur',
            module_policy: 'Politique',
            module_audit: 'Vérification',
            module_training: 'Formation',
            module_discovery: 'Découverte',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Guide de calcul',
            enum_guidance_type_implementation_guidance: 'Conseils de mise en œuvre',
            enum_guidance_type_interpretive_note: 'Note interprétative',
            enum_guidance_type_legal_guidance: 'Conseils juridiques',
            enum_guidance_type_procedural_guidance: 'Conseils procéduraux',
            enum_guidance_type_sector_guidance: 'Orientation sectorielle',
            enum_guidance_type_technical_methodology: 'Méthodologie technique',
            enum_guidance_category_annual_reporting: 'Rapport annuel',
            enum_guidance_category_authority_cooperation: 'Coopération entre autorités',
            enum_guidance_category_authority_coordination: 'Coordination des autorités',
            enum_guidance_category_business_continuity: 'Continuité des activités',
            enum_guidance_category_capital_adequacy: 'Adéquation du capital',
            enum_guidance_category_complaint_handling: 'Traitement des plaintes',
            enum_guidance_category_consent: 'Consentement',
            enum_guidance_category_consent_management: 'Gestion du consentement',
            enum_guidance_category_consumer_protection: 'Protection des consommateurs',
            enum_guidance_category_control_environment: 'Environnement de contrôle',
            enum_guidance_category_cross_border: 'Transfrontalier',
            enum_guidance_category_cross_border_transfers: 'Virements transfrontaliers',
            enum_guidance_category_data_classification: 'Classification des données',
            enum_guidance_category_data_localization: 'Localisation des données',
            enum_guidance_category_data_protection_impact_assessment: "Analyse d'impact sur la protection des données",
            enum_guidance_category_direct_marketing: 'Marketing direct',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Gouvernance',
            enum_guidance_category_ict_risk_management: 'Gestion des risques TIC',
            enum_guidance_category_incident_management: 'Gestion des incidents',
            enum_guidance_category_internal_audit: 'Audit interne',
            enum_guidance_category_isms_documentation: 'Documentation SMSI',
            enum_guidance_category_lawful_basis: 'Base légale',
            enum_guidance_category_licensing: 'Licence',
            enum_guidance_category_local_presence: 'Présence locale',
            enum_guidance_category_logging_and_retention: 'Journalisation et conservation',
            enum_guidance_category_logical_and_physical_access: 'Accès logique et physique',
            enum_guidance_category_operational_resilience_testing: 'Tests de résilience opérationnelle',
            enum_guidance_category_outsourcing: 'Externalisation',
            enum_guidance_category_platform_registration: 'Inscription à la plateforme',
            enum_guidance_category_quality_of_service: 'Qualité de service',
            enum_guidance_category_risk_classification: 'Classification des risques',
            enum_guidance_category_risk_management: 'Gestion des risques',
            enum_guidance_category_scope_definition: 'Définition de la portée',
            enum_guidance_category_sectoral_localisation: 'Localisation sectorielle',
            enum_guidance_category_subscriber_protection: 'Protection des abonnés',
            enum_guidance_category_supply_chain: "Chaîne d'approvisionnement",
            enum_guidance_category_system_classification: 'Classification du système',
            enum_guidance_category_system_operations: 'Opérations du système',
            enum_guidance_category_telecommunications_data: 'Données de télécommunications',
            enum_guidance_category_third_party_risk: 'Risque de tiers',
            enum_guidance_category_transitional_provisions: 'Dispositions transitoires',
            enum_guidance_category_vulnerability_management: 'Gestion des vulnérabilités',
            enum_guidance_status_active: 'Actif',
            enum_guidance_status_draft: 'Brouillon',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Gestion des accès',
            enum_module_ai_governance: 'AI Gouvernance',
            enum_module_asset_management: 'Gestion des actifs',
            enum_module_audit_trail: "Piste d'audit",
            enum_module_breach_notification: 'Notification de violation',
            enum_module_capital_adequacy: 'Adéquation du capital',
            enum_module_classification: 'Classement',
            enum_module_consent: 'Consentement',
            enum_module_consent_management: 'Gestion du consentement',
            enum_module_cross_border_transfers: 'Virements transfrontaliers',
            enum_module_data_localization: 'Localisation des données',
            enum_module_data_mapping: 'Cartographie des données',
            enum_module_data_protection: 'Protection des données',
            enum_module_data_subject_rights: 'Droits des personnes concernées',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Cryptage',
            enum_module_governance: 'Gouvernance',
            enum_module_incident: 'Incident',
            enum_module_incident_management: 'Gestion des incidents',
            enum_module_internal_audit: 'Audit interne',
            enum_module_isms_documentation: 'Documentation SMSI',
            enum_module_isms_governance: 'Gouvernance du SMSI',
            enum_module_logging_and_monitoring: 'Journalisation et surveillance',
            enum_module_monitoring: 'Surveillance',
            enum_module_policy_management: 'Gestion des politiques',
            enum_module_risk: 'Risque',
            enum_module_risk_management: 'Gestion des risques',
            enum_module_training: 'Formation',
            enum_module_vendor: 'Vendeur',
            enum_module_vendor_management: 'Gestion des fournisseurs',
            enum_module_vendor_risk: 'Risque du fournisseur',
            enum_module_vulnerability_management: 'Gestion des vulnérabilités',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Accès',
            category_availability: 'Disponibilité',
            category_change_management: 'Gestion du changement',
            category_classification: 'Classement',
            category_communication_and_information: 'Communication et informations',
            category_compliance: 'Conformité',
            category_confidentiality: 'Confidentialité',
            category_control_activities: 'Activités de contrôle',
            category_control_environment: 'Environnement de contrôle',
            category_incident: 'Incident',
            category_internal_audit: 'Audit interne',
            category_internal_control: 'Contrôle interne',
            category_logical_and_physical_access: 'Accès logique et physique',
            category_mis_data: 'Données du système d’information de gestion',
            category_monitoring: 'Surveillance',
            category_monitoring_activities: 'Activités de surveillance',
            category_outsourcing: 'Externalisation',
            category_privacy: 'Confidentialité',
            category_processing_integrity: 'Intégrité du traitement',
            category_reporting: 'Rapports',
            category_risk_assessment: 'Évaluation des risques',
            category_risk_mitigation: 'Atténuation des risques',
            category_sector_applicability: 'Applicabilité sectorielle',
            category_system_operations: 'Opérations du système',
            category_training: 'Formation',
            category_vendor: 'Vendeur',
            guidance_related_forms_advisory: 'Certains formulaires comportent des conseils connexes – voir dans le',
            guidance_related_procedures_advisory: "Certaines procédures sont accompagnées d'instructions connexes — voir dans le",
            guidance_related_requirements_advisory: "Certaines exigences sont accompagnées d'orientations associées – voir dans le",
        },
        channels: {
            title: 'Canaux de soumission et échéances',
            channels: 'Canaux de soumission',
            add_channel: 'Ajouter un canal',
            channel_type: 'Tapez',
            name: 'Nom',
            instructions: 'Instructions',
            active: 'Actif',
            no_channels: 'Aucun canal de soumission configuré',
            portal: 'Portail',
            in_person: 'En personne',
            postal: 'Poste',
            email: 'E-mail',
            add: 'Ajouter',
            deadlines: 'Échéances réglementaires',
            add_deadline: 'Ajouter une échéance',
            label: 'Libellé',
            days: 'Jours',
            day_type: 'Type de jour',
            calendar_days: 'Jours calendaires',
            working_days: 'Jours ouvrables',
            no_deadlines: 'Aucune échéance configurée',
            description: 'Descriptif',
            failed_load: 'Échec de load channel data',
            channel_added: 'Canal ajouté',
            channel_added_announce: 'Canal de soumission ajouté',
            failed_add_channel: 'Échec de add channel',
            deadline_added: 'Échéance ajoutée',
            deadline_added_announce: 'Échéance réglementaire ajoutée',
            failed_add_deadline: 'Échec de add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Liaison du cadre',
            modules: 'Modules liés',
            add_module: 'Ajouter un module',
            module_code: 'Code du module',
            required: 'Requis',
            link: 'Lier',
            no_modules: 'Aucun module lié',
            notes: 'Remarques',
            personas: 'Personas liés',
            add_persona: 'Ajouter un persona',
            persona_id: 'ID du persona',
            role_description: 'Description du rôle',
            no_personas: 'Aucun persona lié',
            failed_load: 'Échec de load framework links',
            module_linked: 'Module lié',
            module_linked_announce: 'Module lié avec succès',
            failed_link_module: 'Échec de link module',
            persona_linked: 'Persona lié',
            persona_linked_announce: 'Persona lié avec succès',
            failed_link_persona: 'Échec de link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'En attente',
                assessed: 'Évalué',
                applied: 'Appliqué',
                dismissed: 'Rejeté',
            },
            title: 'Mises à jour réglementaires et tableau de bord du cadre',
            page_description: 'Coverage at a glance for every installed regulatory framework, plus the in-product change log of regulatory updates: amendments, effective dates, and impact assessments. [FR]',
            coverage_dashboard: 'Tableau de bord de couverture du cadre',
            no_frameworks: 'Aucun cadre trouvé',
            framework: 'Cadre',
            modules: 'Modules',
            templates: 'Modèles',
            forms: 'Formulaires',
            channels: 'Canaux',
            deadlines: 'Échéances',
            pending: 'Mises à jour en attente',
            updates: 'Mises à jour réglementaires',
            create_update: 'Créer une mise à jour',
            framework_id: 'ID du cadre',
            update_title: 'Titre',
            description: 'Descriptif',
            effective_date: "Date d'entrée en vigueur",
            amendment_ref: 'Référence de modification',
            create: 'Créer',
            no_updates: 'Aucune mise à jour réglementaire',
            status: 'Statut',
            impact: 'Impact',
            change_summary: 'Résumé des modifications',
            assess: 'Évaluer',
            failed_load: 'Échec de load data',
            update_created: 'Mise à jour créée',
            update_created_announce: 'Mise à jour réglementaire créée',
            failed_create: 'Échec de create update',
            update_assessed: 'Mise à jour évaluée',
            failed_assess: 'Échec de assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Cadres associés',
            panel_intro: "Comment ce cadre se rattache aux autres. Une surcouche peut s'appliquer sous plusieurs cadres parents à la fois ; chacun apparaît ici exactement une fois.",
            outgoing_heading: "Ce cadre s'applique à",
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Rattaché à ce cadre',
            outgoing_caption: 'Cadres vers lesquels ce cadre pointe',
            incoming_caption: 'Cadres rattachés à ce cadre',
            col_framework: 'Cadre',
            col_relationship: 'Relation',
            col_scope: 'Source',
            scope_global: 'Issu du contenu du pack',
            scope_org: "Spécifique à l'organisation",
            load_failed: 'Échec du chargement des cadres associés',
            relationship_kinds: {
                parent: 'Cadre parent',
                applies_to: "S'applique à",
                cross_reference: 'Renvoi',
                supersedes: 'Remplace',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Banking [FR]',
                insurance: 'Insurance [FR]',
                healthcare: 'Healthcare [FR]',
                education: 'Education',
                ict_critical: 'Critical ICT [FR]',
                telecom: 'Telecommunications [FR]',
                energy: 'Energy [FR]',
                transport: 'Transport [FR]',
                digital_services: 'Digital services [FR]',
                digital_technology_industry: 'Industrie des technologies numériques',
                manufacturing: 'Manufacturing',
                public_administration: 'Public administration [FR]',
                payments: 'Payments [FR]',
                publishing: 'Édition',
                crypto_assets: 'Crypto-actifs',
                other: 'Other [FR]',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sector [FR]',
            direct_scope_label: 'Direct legal scope [FR]',
            chip_direct: 'Directly applicable [FR]',
            chip_reference: 'Reference only [FR]',
            banner_direct_copy: 'Directly applicable to your organization. Sector reports and deadlines are active. [FR]',
            banner_reference_copy: 'Reference only — your organization is not classified in this sector. [FR]',
            edit_mode_button: 'Edit mode [FR]',
            edit_form_title: 'Change install mode [FR]',
            mode_direct_label: 'Direct — legally applicable [FR]',
            mode_direct_help: 'Schedules sector deadlines and dashboard widgets. [FR]',
            mode_reference_label: 'Reference only [FR]',
            mode_reference_help: 'Browseable but no deadlines or widgets. [FR]',
            reason_note_label: 'Reason (optional) [FR]',
            reason_note_placeholder: 'e.g. Bank subsidiary acquired [FR]',
            cancel: 'Cancel [FR]',
            confirm: 'Confirm [FR]',
            confirming: 'Saving… [FR]',
            save: 'Save [FR]',
            saving: 'Saving… [FR]',
            toast_mode_changed: 'Sector overlay mode updated. [FR]',
            toast_installed: 'Sector overlay installed. [FR]',
            toast_sectors_saved: 'Sector self-identification saved. [FR]',
            install_modal_title: 'Install sector overlay [FR]',
            install_modal_intro: 'Sector overlays are opt-in regulatory packs scoped to a single industry. [FR]',
            install_modal_footer: 'You can change install mode later from the framework detail page. [FR]',
            install_button: 'Install [FR]',
            installed_label: 'Installed [FR]',
            uninstall_button: 'Désinstaller',
            uninstall_confirm: 'Confirmer la désinstallation',
            uninstall_cancel: 'Annuler',
            suggested_label: 'Suggested [FR]',
            catalog_page_title: 'Sector overlays [FR]',
            catalog_page_intro: 'Optional regulatory packs scoped to a single industry sector. [FR]',
            catalog_empty_title: 'No sector overlays available [FR]',
            catalog_empty_description: 'Sector overlay packs published in your platform will appear here. [FR]',
            authority_label: 'Authority [FR]',
            org_sectors_title: 'Industry sectors [FR]',
            org_sectors_intro: 'Select the industry sectors your organization operates in. [FR]',
            widget_title: 'Sector overlay reports due [FR]',
            widget_aria_label: 'Sector overlay reports due [FR]',
            widget_empty: 'No upcoming sector overlay deadlines. [FR]',
            audit_event_mode_changed: 'Sector overlay install mode changed [FR]',
            audit_event_installed: 'Sector overlay installed [FR]',
            view_details_button: 'View details → [FR]',
            banner_aria_label: 'Sector overlay status [FR]',
            toast_load_failed: 'Failed to load sector overlays [FR]',
            toast_install_failed: 'Failed to install sector overlay [FR]',
            toast_uninstall_confirm_required: 'Type "{packCode}" to confirm uninstall. [FR]',
            toast_uninstalled: 'Uninstalled {packCode}. [FR]',
            toast_uninstall_failed: 'Failed to uninstall sector overlay [FR]',
            uninstall_warning_destructive_label: 'Destructive action. [FR]',
            uninstall_warning_body: 'Uninstalling will delete the framework record and any persisted regulatory deadlines tied to {frameworkCode} from this organisation. [FR]',
            uninstall_warning_type_to_confirm: 'Type {packCode} to confirm. [FR]',
            uninstall_in_progress: 'Uninstalling… [FR]',
            widget_view_all: 'View all sector overlays → [FR]',
            widget_due_in_days: 'Due in {days}d [FR]',
            widget_overdue_days: 'Overdue by {days}d [FR]',
            widget_due_today: 'Due today [FR]',
            widget_due_unknown: '— [FR]',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Phased obligations [FR]',
            panel_aria_label: 'Phased obligations [FR]',
            panel_intro: 'Some clauses in this framework activate later than the framework itself. [FR]',
            phased_effective_date_label: 'Phased effective: [FR]',
            status_pending: 'Pending [FR]',
            status_active: 'Active [FR]',
            status_early: 'Active (early-implemented) [FR]',
            notice_window_label: 'Authority notice window: [FR]',
            days_label: 'days [FR]',
            recipient_label: 'Recipient: [FR]',
            early_implement_button: 'Mark early-implemented [FR]',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Mark phased obligation as early-implemented [FR]',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent. [FR]',
            effective_at_label: 'Effective from [FR]',
            reason_label: 'Reason (optional) [FR]',
            reason_placeholder: 'e.g. Board approved early adoption 2026-09-01 [FR]',
            confirm: 'Confirm [FR]',
            toast_early_implemented: 'Early implementation recorded. [FR]',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Phased obligation marked early-implemented [FR]',
            widget_rolling_window_label: 'Phased activations within 90 days [FR]',
            notice_due_label: 'Notice due by the end of [FR]',
            notice_due_time_suffix: '(organisation time) [FR]',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day. [FR]',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent. [FR]',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday. [FR]',
                day_after_is_day_one: 'Computed: the day after implementation is day 1. [FR]',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period. [FR]',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies. [FR]',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states. [FR]',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read. [FR]',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due. [FR]',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run. [FR]',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read. [FR]',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Tableau de bord',
            partner_orgs: 'Organisations partenaires',
            service_tiers: 'Niveaux de service',
            deliverable_templates: 'Modèles de livrables',
            deliverables: 'Livrables',
            brand_profiles: 'Profils de marque',
            support_tickets: 'Tickets de support',
            workflow_templates: 'Modèles de flux de travail',
            workflows: 'Flux de travail',
            escalation: 'Escalade',
            reports: 'Rapports',
            audit: "Piste d'audit",
        },
        dashboard: {
            title: 'Tableau de bord partenaire',
            loading: 'Chargement de dashboard data...',
            total_managed_tenants: 'Total de locataires gérés',
            avg_health_score: 'Avg Santé Score',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Tickets ouverts',
            upcoming_deliverables: 'Livrables à venir',
            escalated_tickets: 'Tickets escaladés',
            managed_tenants: 'Locataires gérés',
            partner_tier: 'Partenaire Niveau',
            managed_tenants_count: 'Nombre de locataires gérés',
            onboarding_date: "Date d'intégration",
            view_details: 'Voir détails',
            no_partner_orgs: 'Aucun partner organizations trouvé',
        },
        partner_orgs: {
            title: 'Organisations partenaires',
            new: 'Nouveau Partner Org',
            contact_name: 'Contact Nom',
            contact_email: 'Contact E-mail',
            partner_tier: 'Partenaire Niveau',
            managed_orgs: 'Organisations gérées',
            onboarding_date: "Date d'intégration",
            no_orgs: 'Aucun partner organizations trouvé',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contact Téléphone',
            metadata_json: 'Métadonnées (JSON)',
            change_status: 'Changement Statut',
            new_status: 'Nouveau statut',
            invalid_json_metadata: 'JSON invalide dans le champ métadonnées',
            suspended: 'Suspendu',
            offboarded: 'Désintégré',
            all_tiers: 'Tous les niveaux',
            silver: 'Argent',
            gold: 'Or',
            platinum: 'Platine',
            status_offboarded: 'Désintégré',
            status_suspended: 'Suspendu',
            tier_gold: 'Or',
            tier_platinum: 'Platine',
            tier_silver: 'Argent',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Niveaux de service',
            new: 'Nouveau niveau',
            tier_name: 'Niveau Nom',
            monthly_fee: 'Mensuel Frais',
            features: 'Fonctionnalités',
            no_tiers: 'Aucun service tiers trouvé',
            tier_code: 'Code de niveau',
            response_time_hrs: 'Réponse Heure (hrs)',
            resolution_time_hrs: 'Résolution Heure (hrs)',
            review_frequency: 'Examiner Fréquence',
            sla_response_time: 'SLA Réponse Heure (hours)',
            sla_resolution_time: 'SLA Résolution Heure (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Comparaison...',
            compare: 'Comparer',
            comparison: 'Service Niveau Comparison',
            attribute: 'Attribut',
            deprecate: 'Rendre obsolète',
            deprecated: 'Obsolète',
            select_compare: 'Sélectionnez à le moins 2 tiers à comparer',
            comparison_title: 'Service Niveau Comparison',
            status_deprecated: 'Obsolète',
            activate: 'Activer',
            resolution_time: 'Résolution Heure',
            response_time: 'Réponse Heure',
            select_at_least_two: 'Sélectionnez at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Modèles de livrables',
            new: 'Nouveau modèle',
            template_name: 'Modèle Nom',
            deliverable_type: 'Type de livrable',
            no_templates: 'Aucun deliverable templates trouvé',
            frequency: 'Fréquence',
            est_effort_hrs: 'Effort est. (h)',
            estimated_effort: 'Effort estimé (heures)',
            associated_module: 'Module associé',
            linked_tiers_count: 'Nombre de niveaux liés',
            evidence_requirements: 'Preuve Exigences (comma-separated)',
            tier_ids: 'IDs de niveaux (séparés par des virgules)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Ponctuel',
            frequency_monthly: 'Mensuel',
            frequency_quarterly: 'Trimestriel',
            frequency_annually: 'Annuellement',
            type_report: 'Rapport',
            type_review: 'Examiner',
            type_assessment: 'Évaluation',
            type_meeting: 'Réunion',
            type_configuration: 'Paramétrage',
            freq_one_time: 'Ponctuel',
            freq_monthly: 'Mensuel',
            freq_quarterly: 'Trimestriel',
            freq_annually: 'Annuellement',
            status_deprecated: 'Obsolète',
        },
        deliverables: {
            title: 'Livrables',
            new: 'Nouveau Deliverable',
            partner: 'Partenaire',
            deliverable_type: 'Type de livrable',
            due_date: "Date d'échéance",
            no_deliverables: 'Aucun deliverables trouvé',
            assigned_to: 'Assigné À',
            completion_date: "Date d'achèvement",
            update_status: 'Mettre à jour Statut',
            hide_details: 'Masquer Détails',
            view_details: 'Voir détails',
            evidence_ids: 'IDs de preuves',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Client Approbation',
            bulk_update: 'Mise à jour en masse',
            bulk_update_status: 'Bulk Mettre à jour Statut',
            generate_schedule: 'Générer Planification',
            generate_deliverable_schedule: 'Générer Deliverable Planification',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisation ID',
            service_tier_id: 'Service Niveau ID',
            filter_assigned_to: 'Filtrer par Assigné À',
            select_status: 'Sélectionnez status...',
            new_status: 'Nouveau statut',
            upcoming: 'À venir',
            overdue: 'En retard',
            status_deferred: 'Reporté',
            status_overdue: 'En retard',
            status_upcoming: 'À venir',
        },
        brand_profiles: {
            title: 'Profils de marque',
            new: 'Nouveau Brand Profile',
            partner: 'Partenaire',
            brand_name: 'Nom de marque',
            primary_color: 'Principal Couleur',
            logo_url: 'URL du logo',
            no_profiles: 'Aucun brand profiles trouvé',
            secondary_color: 'Secondaire Couleur',
            header_text: 'En-tête Texte',
            footer_text: 'Pied de page Texte',
            contact_email: 'Contact E-mail',
            contact_phone: 'Contact Téléphone',
            contact_website: 'Site web de contact',
            contact_address: 'Contact Adresse',
            is_default: 'Est Par défaut',
            set_default: 'Définir par défaut',
            preview: 'Aperçu',
            report_header_preview: 'Rapport En-tête Preview',
            report_footer_preview: 'Rapport Pied de page Preview',
            preview_description: 'Ce est un preview de le signaler en-tête avec le selected brand colors.',
            contact_website_short: 'Site web',
            logo_preview: 'Aperçu du logo',
        },
        support_tickets: {
            title: 'Tickets de support',
            new: 'Nouveau Ticket',
            partner: 'Partenaire',
            subject: 'Sujet',
            assigned_to: 'Assigné À',
            no_tickets: 'Aucun support tickets trouvé',
            category: 'Catégorie',
            priority: 'Priorité',
            escalation_tier: 'Escalade Niveau',
            sla_status: 'SLA Statut',
            update_status: 'Mettre à jour Statut',
            escalate: 'Escalader',
            add_note: 'Ajouter Note',
            escalate_ticket: 'Escalader le ticket',
            escalate_to_tier: 'Escalader À Niveau',
            notes_timeline: 'Notes Chronologie',
            no_notes: 'Non notes encore.',
            attachments: 'Pièces jointes',
            no_attachments: 'Aucune pièce jointe.',
            client_org_id: 'ID Org. client',
            content: 'Contenu',
            note_type: 'Catégorie',
            view_detail: 'Voir Detail',
            hide: 'Masquer',
            new_status: 'Nouveau statut',
            all_categories: 'Toutes les catégories',
            all_priorities: 'Toutes les priorités',
            all_tiers: 'Tous les niveaux',
            ticket_not_found: 'Ticket non trouvé',
            ticket_id: 'ID du ticket',
            client_org: 'Org. client',
            deescalate: 'Désescalader',
            deescalate_ticket: 'Désescalader le ticket',
            deescalate_to_tier: 'De-escalate À Niveau',
            add_attachment: 'Ajouter pièce jointe',
            file: 'Fichier',
            linked_entity: 'Entité liée',
            sla_target: 'Cible',
            breached: 'Violé',
            remaining_hours: 'restants',
            na: 'N/A',
            tickets_found: 'tickets trouvés',
            reason: 'Motif',
            target: 'Cible',
            upload: 'Téléverser',
        },
        workflow_templates: {
            title: 'Modèles de flux de travail',
            new: 'Nouveau modèle',
            template_name: 'Modèle Nom',
            steps: 'Étapes',
            no_templates: 'Aucun workflow templates trouvé',
            category: 'Catégorie',
            view_steps: 'Voir étapes',
            hide_steps: 'Masquer Étapes',
            step: 'Étape',
            step_title: 'Titre',
            step_description: 'Descriptif',
            assignee_role: 'Assigné Rôle',
            sla_hours: 'Heures SLA',
            evidence_required: 'Preuve Obligatoire',
            guidance_notes: "Notes d'orientation",
            add_step: 'Ajouter étape',
            no_steps: 'Aucune étape définie.',
            no_steps_defined: 'Aucune étape définie.',
            templates_found: 'modèles trouvés',
            all_categories: 'Toutes les catégories',
        },
        workflows: {
            client_org: 'Org. client',
            title: 'Instances de flux de travail',
            new: 'Nouveau flux de travail',
            template: 'Modèle',
            partner: 'Partenaire',
            current_step: 'Actuel Étape',
            progress: 'Progression',
            no_workflows: 'Aucun workflow instances trouvé',
            instances_found: 'instances trouvées',
        },
        escalation: {
            title: 'Escalade Tableau de bord',
            ticket: 'Demande',
            partner: 'Partenaire',
            escalated_at: 'Escaladé À',
            reason: 'Motif',
            no_escalations: 'Aucun escalations trouvé',
            time_in_tier: 'Heure dans Niveau',
            auto_evaluate: 'Évaluation auto.',
            priority_critical: 'Critique',
            priority_high: 'Élevé',
            priority_medium: 'Moyen',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Désescaladé depuis le tableau de bord',
            sla_breached: 'SLA violé',
            sla_remaining: 'SLA restant: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Org. client',
            title: 'Rapports',
            partner: 'Partenaire',
            report_type: 'Rapport Type',
            generated_at: 'Généré le',
            no_reports: 'Aucun reports trouvé',
            generate: 'Générer Rapport',
        },
        audit: {
            title: 'Partenaire Audit Trail',
            event_type: 'Événement Type',
            entity_type: "Type d'entité",
            entity: 'Entité',
            entity_id: "ID de l'entité",
            actor: 'Acteur',
            timestamp: 'Horodatage',
            no_events: 'Aucun audit events trouvé',
            events_found: 'événements trouvés',
            client_org: 'Organisation cliente',
            all_actors: 'Tous les acteurs',
            event_type_search: "Rechercher un type d'événement",
            event_type_created: 'Créé',
            event_type_updated: 'Mis à jour',
            event_type_deleted: 'Supprimé',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Organisation partenaire',
            entity_type_service_tier: 'Niveau de service',
            entity_type_deliverable_template: 'Modèle de livrable',
            entity_type_deliverable_instance: 'Instance livrable',
            entity_type_brand_profile: 'Profil de marque',
            entity_type_support_ticket: "Billet d'assistance",
            entity_type_workflow_template: 'Modèle de flux de travail',
            entity_type_workflow_instance: 'Instance de flux de travail',
            entity_type_generated_report: 'Rapport généré',
            entity_type_audit_export: "Export d'audit",
        },
        no_permission_title: 'Autorisations insuffisantes',
        no_permission_message: "Vous n'êtes pas autorisé à accéder à l'espace de travail partenaire.",
        status_active: 'Actif',
        status_suspended: 'Suspendu',
        status_offboarded: 'Hors-bord',
        status_completed: 'Terminé',
        status_cancelled: 'Annulé',
        status_deprecated: 'Obsolète',
        status_new: 'Nouveau',
        status_triaged: 'Trié',
        status_in_progress: 'En cours',
        status_waiting_on_client: 'En attente du client',
        status_waiting_on_vendor: 'En attente du vendeur',
        status_escalated: 'Escaladé',
        status_resolved: 'Résolu',
        status_closed: 'Fermé',
        status_pending: 'En attente',
        status_skipped: 'Sauté',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Intégration',
        category_incident_escalation: 'Escalade des incidents',
        category_config_change: 'Changement de configuration',
        category_periodic_review: 'Examen périodique',
        category_offboarding: 'Débarquement',
        category_custom: 'Personnalisé',
        category_configuration: 'Configuration',
        category_incident: 'Incident',
        category_compliance_question: 'Question de conformité',
        category_feature_request: 'Demande de fonctionnalité',
        category_bug_report: 'Rapport de bug',
        priority_critical: 'Critique',
        priority_high: 'Élevé',
        priority_medium: 'Moyen',
        priority_low: 'Faible',
        tier_silver: 'Argent',
        tier_gold: 'Or',
        tier_platinum: 'Platine',
        report_type_compliance_summary: 'Résumé de conformité',
        report_type_risk_overview: 'Aperçu des risques',
        report_type_incident_report: "Rapport d'incident",
        report_type_consent_status: 'Statut de consentement',
        report_type_program_maturity: 'Maturité du programme',
        report_type_executive_pack: 'Pack Exécutif',
        note_type_internal: 'Interne',
        note_type_client_visible: 'Client visible',
        status_updated: 'Statut mis à jour avec succès',
        deliverable_status_updated: 'Statut du livrable mis à jour avec succès',
        ticket_status_updated: 'Statut du ticket mis à jour avec succès',
        ticket_escalated: 'Le ticket a été transféré avec succès',
        note_added: 'Note ajoutée avec succès',
        attachment_uploaded: 'Pièce jointe téléchargée avec succès',
        ticket_deescalated: 'Le ticket a été résolu avec succès',
        auto_evaluation_completed: 'Auto-évaluation terminée',
        template_created: 'Modèle de workflow créé avec succès',
        template_updated: 'Modèle de workflow mis à jour avec succès',
        brand_set_default: 'Profil de marque défini par défaut avec succès',
        brand_created: 'Profil de marque créé avec succès',
        brand_updated: 'Profil de marque mis à jour avec succès',
        tier_created: 'Le niveau de service a été créé avec succès',
        tier_updated: 'Le niveau de service a été mis à jour avec succès',
        tier_deprecated: 'Le niveau de service a été obsolète avec succès',
        tier_activated: 'Le niveau de service a été activé avec succès',
        period: 'Période',
        format: 'Formater',
        period_start: 'Début de la période',
        period_end: 'Fin de période',
        reports_count: '{count} rapports',
        reports_count_one: 'Rapport {count}',
        metric: 'Métrique',
        value: 'Valeur',
        showing_chart_view: 'Affichage de la vue graphique',
        showing_table_view: 'Affichage de la vue du tableau de données',
        evidence_ids_placeholder: 'par ex. ev-001, ev-002',
        confirm_cancel_workflow: 'Êtes-vous sûr de vouloir annuler ce flux de travail ? Cette action ne peut pas être annulée.',
        export_csv: 'Exporter CSV',
        compliance_qualifications_title: 'Qualifications du service de conformité',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Alignement des résultats réglementaires',
        regulatory_output_desc: "Chaque type de modèle correspond à une catégorie de sortie réglementaire : les rapports produisent une documentation de conformité, les examens valident l'efficacité du contrôle, les évaluations couvrent les évaluations des risques (DPIA, analyse des écarts), les réunions documentent les sessions de gouvernance. Les exigences en matière de preuves définissent les artefacts que chaque livrable doit référencer pour être prêt à l'audit.",
        sla_compliance_title: 'SLA Suivi pour la livraison conforme',
        sla_compliance_desc: "Les SLA sur les délais de réponse et de résolution sont contractuellement contraignants pour la fourniture de services de conformité. La fréquence de révision détermine la fréquence à laquelle les livrables de conformité des partenaires sont évalués. Les violations de SLA sur les tickets critiques en matière de conformité déclenchent une escalade automatique et sont enregistrées dans la piste d'audit des partenaires à des fins de preuve réglementaire.",
        regulatory_engagement_title: 'Modèles d’engagement réglementaire',
        regulatory_engagement_desc: "Les catégories de flux de travail correspondent aux types d'engagement réglementaire : l'intégration couvre la configuration initiale de la conformité et l'analyse des écarts, l'examen périodique gère les évaluations de conformité récurrentes, l'escalade des incidents gère la réponse aux violations réglementaires, l'offboarding garantit le respect des obligations de conservation des données. Chaque étape peut nécessiter la collecte de preuves pour garantir l’exhaustivité de la piste d’audit.",
        compliance_escalation_title: "Procédures d'escalade critiques en matière de conformité",
        compliance_escalation_desc: "L1 gère les problèmes opérationnels standard. L2 couvre les défaillances ayant un impact sur la conformité (non-respect des délais réglementaires, lacunes en matière de contrôle). L3 est réservé aux scénarios de violation de la réglementation nécessitant une attention immédiate de la direction. La violation de SLA sur les tickets L2/L3 génère automatiquement des entrées de piste d'audit. Toutes les actions d’escalade et de désescalade sont enregistrées en permanence.",
        audit_regulatory_title: 'Sentier des preuves réglementaires',
        audit_regulatory_desc: "Cette piste d'audit fournit des enregistrements inviolables pour la conformité réglementaire. Chaque action du partenaire (création, mise à jour, suppression, escalade, changement de statut) est enregistrée avec l'identité de l'acteur, l'horodatage et la différence complète de charge utile. Utilisez les filtres de type d'entité pour isoler les preuves par domaine de conformité. Exportez les résultats filtrés pour produire une documentation prête à être auditée pour les inspections réglementaires.",
        evidence_trail_title: 'Parcours de preuves inter-modules',
        evidence_trail_desc: "Chaque étape du flux de travail peut collecter des ID de preuves liés aux livrables, aux événements d'audit et aux artefacts de conformité dans les modules. Avancer d'une étape avec les identifiants de preuves crée une chaîne vérifiable reliant les flux de travail des partenaires à la documentation réglementaire. Les flux de travail terminés avec des chaînes de preuves complètes servent de preuve de la réalisation de l'engagement de conformité.",
        evidence_ids_label: 'ID de preuve',
    },
    org_groups: {
        title: "Groupes d'organisations",
        new: 'Ajouter groupe',
        group_name: 'Groupe Nom',
        member_count: 'Nombre de membres',
        root_org: 'Organisation racine',
        no_groups: 'Aucun org groups trouvé',
        add_organization: 'Ajouter une Organisation',
        select_organization: 'Sélectionnez une organisation...',
        no_available_orgs: 'Aucune organisation disponible à ajouter.',
        no_members: 'Aucune organisation membre dans ce groupe.',
        member_added: 'Organisation ajoutée au groupe.',
        member_removed: 'Organisation retirée du groupe.',
        already_in_group: 'Cette organisation appartient déjà à un groupe.',
        org_not_found: 'Organisation introuvable.',
        add_member_error: "Impossible d'ajouter l'organisation au groupe.",
        remove_member_error: "Impossible de retirer l'organisation du groupe.",
        confirm_remove_member: 'Êtes-vous sûr de vouloir retirer "{name}" de ce groupe ?',
        load_error: "Impossible de charger les détails du groupe d'organisations.",
        page_title_list: 'Groupes organisationnels',
        page_title_detail: 'Détails du groupe organisationnel',
        page_title_create: 'Créer un groupe organisationnel',
        page_title_settings: 'Paramètres du groupe organisationnel',
        page_title_users: "Groupe d'utilisateurs",
        page_title_reports: 'Rapports de groupe',
        page_title_dashboard: 'Tableau de bord du groupe',
        page_title_audit: 'Vérification du groupe',
        page_title_org_audit: 'Audit de groupe organisationnel',
        page_title_shared_vendors: 'Fournisseurs partagés',
        page_title_shared_frameworks: 'Cadres partagés',
        page_title_shared_forms: 'Modèles de formulaires partagés',
        page_title_merged_vendors: 'Fournisseurs fusionnés',
        page_title_settings_inheritance: 'Héritage des paramètres',
        announce_loaded_groups: '{count} groupes organisationnels chargés',
        announce_loaded_group: 'Groupe chargé {name}',
        announce_loaded_users: 'Utilisateurs du groupe {count} chargés',
        announce_loaded_reports: 'Rapports {count} chargés',
        announce_loaded_vendors: 'Fournisseurs partagés {count} chargés',
        announce_loaded_frameworks: 'Chargé des frameworks partagés {count}',
        announce_loaded_templates: '{count} modèles de formulaires partagés chargés',
        announce_loaded_audit: "Événements d'audit {count} chargés",
        announce_loaded_merged: 'Fournisseurs {shared} partagés et {local} locaux chargés',
        announce_dashboard_loaded: 'Tableau de bord chargé.',
        announce_dashboard_refreshed: 'Tableau de bord rafraîchi.',
        announce_dashboard_refresh_failed: "Échec de l'actualisation du tableau de bord.",
        announce_refreshing: 'Tableau de bord actualisé...',
        announce_settings_loaded: 'Héritage des paramètres chargé.',
        announce_settings_saved: 'Paramètres enregistrés avec succès.',
        announce_saving_settings: 'Enregistrement des paramètres...',
        announce_error_saving: "Erreur lors de l'enregistrement des paramètres.",
        announce_group_dissolved: 'Groupe dissous.',
        announce_creating_group: "Création d'un groupe organisationnel...",
        announce_group_created: 'Groupe organisationnel créé avec succès.',
        announce_error_creating: 'Erreur lors de la création du groupe organisationnel.',
        announce_vendor_published: 'Fournisseur publié avec succès.',
        announce_vendor_retired: 'Vendeur à la retraite.',
        announce_framework_published: 'Cadre publié avec succès.',
        announce_framework_retired: 'Cadre retiré.',
        announce_template_published: 'Modèle de formulaire publié avec succès.',
        announce_template_retired: 'Modèle de formulaire retiré.',
        announce_role_assigned: 'Rôle attribué avec succès.',
        announce_error_assign_role: "Erreur lors de l'attribution du rôle.",
        announce_role_revoked: 'Rôle révoqué avec succès.',
        announce_report_generating: 'Génération du rapport...',
        announce_report_requested: 'Génération de rapport demandée.',
        announce_error_report: 'Erreur lors de la demande de rapport.',
        announce_report_deleted: 'Rapport supprimé.',
        announce_search_applied: 'Recherche appliquée.',
        announce_search_cleared: 'Recherche effacée.',
        announce_tab_selected: 'Onglet {tab} sélectionné',
        announce_error_add_member: "Erreur lors de l'ajout d'un membre au groupe.",
        announce_category_reverted: '{category} est redevenu hérité du parent.',
        announce_category_overridden: '{category} défini sur remplacement local.',
        announce_override_saved: 'Remplacement {category} enregistré.',
        announce_export_started: "Exportation de l'audit en tant que {format}...",
        announce_export_completed: 'Exportation en tant que {format} terminée.',
        announce_export_failed: "L'exportation a échoué.",
        announce_filter_org: 'Filtré par organisation {name}',
        announce_filter_org_cleared: "Filtre d'organisation effacé",
        announce_filter_event: "Filtré par type d'événement {type}",
        announce_filter_cleared: 'Filtre effacé',
        announce_download: 'Téléchargement de {name} en tant que {format}',
        error_load_groups: 'Échec du chargement des groupes organisationnels. Veuillez réessayer.',
        error_load_users: 'Échec du chargement des utilisateurs du groupe.',
        error_load_reports: 'Échec du chargement des rapports de groupe.',
        error_load_dashboard: 'Échec du chargement du tableau de bord du groupe.',
        error_load_settings: 'Échec du chargement des paramètres du groupe.',
        error_load_vendors: 'Échec du chargement des fournisseurs fusionnés.',
        error_save_settings: "Échec de l'enregistrement des paramètres.",
        error_dissolve_group: 'Échec de la dissolution du groupe.',
        error_assign_role: "Échec de l'attribution du rôle.",
        error_create_group: 'Échec de la création du groupe organisationnel. Veuillez réessayer.',
        error_request_report: 'Échec de la demande de rapport.',
        error_group_name_required: 'Le nom du groupe est requis.',
        success_settings_saved: 'Paramètres enregistrés avec succès.',
        confirm_retire_vendor: 'Êtes-vous sûr de vouloir retirer le fournisseur « {name} » ?',
        confirm_retire_framework: 'Êtes-vous sûr de vouloir retirer le framework « {name} » ?',
        confirm_retire_template: 'Êtes-vous sûr de vouloir supprimer le modèle de formulaire « {name} » ?',
        confirm_dissolve_group: 'Êtes-vous sûr de vouloir dissoudre « {name} » ? Cette action ne peut pas être annulée.',
        confirm_revoke_role: 'Êtes-vous sûr de vouloir révoquer le rôle « {role} » de « {name} » ?',
        confirm_delete_report: 'Êtes-vous sûr de vouloir supprimer le rapport « {name} » ?',
        dissolve_group: 'Dissoudre le groupe',
        dissolve_warning: 'Cela dissoudra définitivement le groupe d’organisation. Toutes les organisations membres seront supprimées.',
        count_groups: 'Groupe {count}',
        count_groups_plural: '{count} groupes',
        count_users: '{count} utilisateur',
        count_users_plural: '{count} utilisateurs avec des rôles de groupe',
        count_events: 'Événement {count}',
        count_events_plural: '{count} événements',
        count_reports: 'Rapport {count}',
        count_reports_plural: '{count} rapports',
        count_vendors: '{count} fournisseur partagé au sein du groupe',
        count_vendors_plural: '{count} fournisseurs partagés au sein du groupe',
        count_frameworks: 'Cadre {count} partagé au sein du groupe',
        count_frameworks_plural: '{count} frameworks partagés au sein du groupe',
        count_templates: 'Modèle {count} partagé au sein du groupe',
        count_templates_plural: '{count} modèles partagés au sein du groupe',
        count_shared_vendors: '{count} fournisseurs partagés',
        count_local_vendors: '{count} fournisseurs locaux',
        merged_combined_view: 'Vue combinée des {shared} fournisseurs partagés et des {local} fournisseurs locaux',
        dashboard: {
            title: 'Tableau de bord du groupe',
            loading: 'Chargement de dashboard data...',
            total_groups: 'Total Groupes',
            total_members: 'Total de membres',
            active_groups: 'Actif Groupes',
            overall_compliance_score: 'Score de conformité global',
            group_risk_level: 'Niveau de risque du groupe',
            sla_compliance: 'SLA Conformité',
            compliance_posture: 'Posture de conformité',
            risk_heatmap: 'Carte thermique des risques',
            sla_performance: 'SLA Performances',
            recent_incidents: 'Incidents récents',
            recent_activity: 'Activité récente',
            refresh_label: 'Actualiser les données du tableau de bord',
            score_good: 'Bon',
            score_needs_attention: "A besoin d'attention",
            score_critical: 'Critique',
            trend_up: 'Tendance à la hausse',
            trend_down: 'Tendance à la baisse',
            trend_stable: 'Stable',
            risk_low: 'Low',
            risk_medium: 'Moyen',
            risk_high: 'Élevé',
            risk_critical: 'Critique',
            risk_unknown: 'Inconnu',
            col_organization: 'Organisation',
            col_score: 'Score',
            col_trend: 'Tendance',
            col_open_issues: 'Problèmes ouverts',
            col_critical_issues: 'Problèmes critiques',
            col_health: 'Santé',
            col_vendor_risk: 'Risque du fournisseur',
            col_data_risk: 'Risque lié aux données',
            col_compliance_risk: 'Risque de conformité',
            col_total_cases: 'Total des cas',
            col_on_time: "À l'heure",
            col_breached: 'Violé',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Jours moyens',
            sr_vendor_risk: 'Score de risque du fournisseur : {score}',
            sr_data_risk: 'Score de risque lié aux données : {score}',
            sr_compliance_risk: 'Score de risque de non-conformité : {score}',
            sr_sla_breaches: 'Avertissement : {count} SLA violations',
            sr_severity: 'Gravité : {severity}',
        },
        detail: {
            title: 'Détails du groupe',
            members: 'Membres',
            settings: 'Paramètres',
            audit: 'Vérification',
            sr_loading: 'Chargement des détails du groupe organisationnel',
            sr_loading_hierarchy: 'Chargement de la hiérarchie',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'organisation membre',
            root_org_label: 'organisation racine',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Utilisateurs du groupe',
            add_user: 'Ajouter utilisateur',
            no_users: 'Aucun utilisateur dans ce groupe.',
            role_group_admin: 'Administrateur de groupe',
            role_group_reader: 'Lecteur de groupe',
            sr_loading: 'Chargement des utilisateurs du groupe',
        },
        reports: {
            title: 'Rapports du groupe',
            no_reports: 'Aucun reports trouvé',
            generate_report: 'Générer un nouveau rapport',
            report_type_compliance: 'Résumé de conformité',
            report_type_risk: 'Aperçu des risques',
            report_type_sla: 'SLA Performances',
            report_type_incident: "Rapport d'incident",
            report_type_executive: 'Note de synthèse',
            report_type_board: "Rapport du conseil d'administration",
            status_queued: "En file d'attente",
            status_generating: 'Génération',
            status_completed: 'Terminé',
            status_failed: 'Échec',
            period_start: 'Début de la période',
            period_end: 'Fin de période',
            sr_loading: 'Chargement des rapports de groupe',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Fournisseurs partagés',
            no_vendors: 'Aucun shared vendors trouvé',
            merged_vendors: 'Fournisseurs fusionnés',
            publish_form_label: 'Publier un fournisseur partagé',
            sr_loading: 'Chargement des fournisseurs partagés',
            sr_loading_merged: 'Chargement des fournisseurs fusionnés',
            local_vendors: 'Fournisseurs locaux',
            source_shared: 'Partagé du groupe',
            source_local: 'Vendeur local',
            category_technology: 'Technologie',
            category_consulting: 'Conseil',
            category_outsourcing: 'Externalisation',
            category_cloud: 'Nuage',
            category_data_processing: 'Traitement des données',
            category_other: 'Autre',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Moyen',
            risk_tier_high: 'Élevé',
            risk_tier_critical: 'Critique',
            compliance_compliant: 'Conforme',
            compliance_non_compliant: 'Non conforme',
            compliance_pending: 'En attente',
            compliance_under_review: 'En cours de révision',
            status_active: 'Actif',
            status_retired: 'Retraité',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Code du cadre',
            title: 'Cadres partagés',
            no_frameworks: 'Aucun shared frameworks trouvé',
            publish_form_label: 'Publier un cadre partagé',
            sr_loading: 'Chargement de frameworks partagés',
            status_active: 'Actif',
            status_retired: 'Retraité',
        },
        shared_forms: {
            title: 'Modèles de formulaires partagés',
            no_forms: 'Aucun shared form templates trouvé',
            publish_form_label: 'Publier un modèle de formulaire partagé',
            sr_loading: 'Chargement de modèles de formulaires partagés',
            status_active: 'Actif',
            status_retired: 'Retraité',
        },
        audit: {
            title: 'Audit du groupe',
            consolidated_title: "Piste d'audit consolidée",
            consolidated_subtitle: 'Cross-organization auditer events pour ce group',
            audit_log_title: 'Groupe Audit Journal',
            event_type: 'Événement Type',
            actor: 'Acteur',
            timestamp: 'Horodatage',
            source_org: 'Org. source',
            org: 'Org',
            summary: 'Résumé',
            details: 'Détails',
            no_events: 'Aucun audit events trouvé',
            all_events: 'Tous Événements',
            all_orgs: 'Toutes les org.',
            organization: 'Organisation',
            from: 'De',
            to: 'To',
            search_events: 'Rechercher des événements...',
            clear: 'Effacer',
            events_24h: 'Événements (24h)',
            events_7d: 'Événements (7d)',
            active_orgs: 'Org. actives',
            exporting: 'Exportation...',
            export_csv: 'Exporter CSV',
            export_json: 'Exporter JSON',
            back_to_group: 'Retour à Groupe',
            back_to_detail: 'Retour au détail',
            sr_loading: "Chargement des événements d'audit",
            sr_loading_summary: "Chargement du résumé de l'audit",
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Groupe Paramètres',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Héritage des paramètres',
            inheritance_subtitle: 'Configurer les catégories de paramètres qui héritent du groupe ou utiliser des remplacements locaux',
            sla_settings: 'SLA Paramètres',
            calendar_settings: 'Calendrier Paramètres',
            notification_settings: 'Notification Paramètres',
            intake_settings: "Paramètres d'entrée",
            cmp_integration: 'Intégration CMP',
            overridden: 'Remplacé',
            inheriting: 'Héritage',
            hide_defaults: 'Masquer les valeurs par défaut',
            show_group_defaults: 'Afficher Groupe Defaults',
            group_defaults: 'Valeurs par défaut du groupe',
            inherit_from_group: 'Inherit de Groupe',
            use_local_override: 'Utiliser le remplacement local',
            local_override_json: 'Remplacement local (JSON)',
            save_override: 'Enregistrer le remplacement',
            saving: 'Enregistrement...',
            sr_loading: "Chargement de l'héritage des paramètres",
            sr_loading_settings: 'Chargement des paramètres du groupe',
            sr_enabled: 'Activé',
            sr_disabled: 'Désactivé',
            sr_override_active: 'Remplacement local actif',
            sr_inherited: 'Hérité du parent',
            danger_zone: 'Zone dangereuse',
            sharing_options: 'Options de partage',
            advanced_options: 'Options avancées',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Actif',
        status_dissolved: 'Dissous',
        status_pending: 'En attente',
        status_suspended: 'Suspendu',
    },
    integrations: {
        nav: {
            webhooks: 'Points de rappel',
            domains: 'Domaines',
            export_schedules: 'Planification des exports',
            email_log: 'Journal des e-mails',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Utilisateurs',
            roles: 'Rôles',
            profile: 'Mon profil',
        },
        security: {
            mfa: 'Paramètres MFA',
            sso: 'Configuration SSO',
            api_keys: 'Clés API',
            service_accounts: 'Comptes de service',
            ip_allowlist: "Liste blanche d'adresses IP",
            delegations: 'Délégations',
            break_glass: "Accès d'urgence",
            session_settings: 'Paramètres de session',
            active_sessions: 'Sessions actives',
            access_reviews: "Revues d'accès",
            my_reviews: 'Mes revues',
            role_analytics: 'Analytique des rôles',
            attestation_schedules: 'Planification des attestations',
            sod_rules: 'Règles SoD',
            sod_violations: 'Violations SoD',
        },
        module_config: {
            title: 'Configuration des modules',
            subtitle: 'Activez ou désactivez les modules fonctionnels de votre organisation. Les modules désactivés sont masqués dans la navigation.',
            enabled: 'Activé',
            disabled: 'Désactivé',
            save: 'Enregistrer la configuration',
            saving: 'Enregistrement...',
            saved: 'Configuration enregistrée avec succès',
            nav_label: 'Configuration des modules',
            settings: 'Paramètres',
            webhooks: 'Points de rappel',
            domains: 'Domaines',
            export_schedules: 'Planification des exports',
            email_log: 'Journal des e-mails',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Fichiers',
            configuration: 'Configuration',
            browser: 'Navigateur de fichiers',
            dashboard: 'Tableau de bord des fichiers',
        },
        immutable_audit_trails: {
            nav_group_label: 'Audit et preuve',
            nav_settings: 'Paramètres',
            nav_status: 'Statut de preuve',
            nav_integrity_center: "Centre d'intégrité",
            nav_usage: 'Utilisation et crédits',
            settings_title: "Pistes d'audit immuables - Paramètres",
            settings_subtitle: 'Paramètres par organisation pour le module complémentaire Immutable Audit Trails.',
            settings_banner: "Le module complémentaire Immutable Audit Trails n’est pas activé pour votre organisation. Activez-le ici pour commencer à ancrer la preuve pour les événements d'audit de niveau A et B.",
            status_title: "Pistes d'audit immuables - Statut de preuve",
            status_subtitle: 'Relations de preuve par événement entre les modules.',
            status_banner: 'Le module complémentaire Immutable Audit Trails n’est pas activé pour votre organisation. Visitez Paramètres pour l’activer.',
            integrity_center_title: "Pistes d'audit immuables — Centre d'intégrité",
            integrity_center_subtitle: 'Vérifiez les preuves et exportez les packs d’intégrité.',
            integrity_center_banner: 'Le module complémentaire Immutable Audit Trails n’est pas activé pour votre organisation. Visitez Paramètres pour l’activer avant d’exécuter la vérification ou d’exporter des packs d’intégrité.',
            usage_title: "Pistes d'audit immuables - Utilisation et crédits",
            usage_subtitle: 'Solde créditeur, achats de forfaits et historique d’utilisation.',
            usage_banner: "Activez les pistes d'audit immuables dans les paramètres pour commencer à consommer des crédits de preuve.",
            module_tips_placeholder: "Des conseils et des conseils pour cette surface sont disponibles dans le centre d'aide.",
            column_label: 'Preuve',
            no_proof_aria: 'Aucune preuve enregistrée',
            proof_status_receipt_label: 'reçu {id}',
            in_progress_hint: 'En cours – mises à jour automatiquement',
            status_pills: {
                pending: 'En attente',
                submitted: 'Soumis',
                anchored: 'Ancré',
                confirmed: 'Confirmé',
                retrying: 'Réessayer',
                paused_cap: 'En pause (limite)',
                failed: 'Échec',
                failed_auth: "Échec de l'authentification",
            },
            status_tooltips: {
                pending: "En attente : en file d'attente pour le service de preuve. Passera sous peu à Soumis ; aucune action n'est nécessaire.",
                submitted: "Soumis : votre événement a été envoyé au service d'épreuves. Pour le mode batch, il attend d’être scellé et ancré sur la chaîne. Mises à jour automatiquement ; aucune action n'est nécessaire.",
                anchored: "Ancré — le lot a été écrit en chaîne et attend la confirmation finale. Mises à jour automatiquement ; aucune action n'est nécessaire.",
                confirmed: 'Confirmé — la preuve est ancrée sur la chaîne et vérifiable.',
                retrying: "Nouvelle tentative : une erreur passagère s'est produite. Le réconciliateur réessaye automatiquement ; aucune action n'est nécessaire.",
                paused_cap: 'En pause : votre plafond mensuel de crédits de preuve a été atteint. Échangez plus de crédits ou attendez le mois de facturation suivant.',
                failed: "Échec : l'ancrage n'a pas réussi. Le réconciliateur réessayera automatiquement ; contactez le support si le problème persiste.",
                failed_auth: "L'authentification a échoué : le service de preuve a rejeté nos informations d'identification. Consultez la page Paramètres des pistes d'audit immuables.",
            },
            mode_pills: {
                direct: 'Direct',
                batch: 'Lot',
            },
            drawer: {
                title: 'Détails de la preuve',
                close: 'Fermer',
                section_proof: 'Preuve',
                section_batch: 'Adhésion par lots',
                status: 'Statut',
                proof_reference: 'Référence de preuve',
                receipt_id: 'ID du reçu',
                taxonomy_class: 'Cours de taxonomie',
                units_consumed: 'Unités consommées',
                created_at: 'Créé',
                verification_ref: 'Référence de vérification de chaîne',
                chain: 'Chaîne',
                tx_hash: "Hachage d'émission",
                block_ref: 'Réf bloc',
                batch_reference: 'Référence du lot',
                leaf_index: 'Index feuille',
                local_event_hash: "Hachage d'événement local",
                show_inclusion: "Afficher le chemin d'inclusion",
                hide_inclusion: "Masquer le chemin d'inclusion",
                privacy_note: "La charge utile est hachée ; Aucune donnée personnelle n'a été laissée à ComplianceOne pour cet événement.",
                verify_now: 'Vérifiez maintenant',
                verify_pending: 'Vérification en cours…',
                verify_verified: 'Vérifié – les contrôles locaux + côté chaîne ont été réussis.',
                verify_issue_prefix: 'Problème de vérification :',
                verify_failed: "L'appel de vérification a échoué.",
                verify_result_prefix: 'Verdict de vérification : ',
                verify_pending_note: "Vérification en attente : cette preuve est toujours en attente d'ancrage en chaîne. Réessayez une fois le lot scellé.",
                awaiting_anchor_note: "Cette épreuve fait partie d'un lot ouvert. La vérification devient disponible une fois que le lot est scellé et ancré dans la chaîne, généralement dans la fenêtre de lot configurée par l'organisation. Le badge de statut sera mis à jour automatiquement.",
                terminal_error_note: "Cette preuve n’a pas réussi à s’ancrer. Le réconciliateur de statut de preuve réessayera automatiquement ; la vérification manuelle n'a encore rien à vérifier.",
            },
            integrity_center: {
                section_run: 'Exécuter la vérification',
                section_progress: 'Exécuter la progression',
                section_issues: 'Problèmes',
                period_start: 'Début des règles',
                period_end: 'Fin de période',
                module_filter: 'Modules (séparés par des virgules, facultatif)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Exécuter la vérification',
                export_button: "Pack d'intégrité à l'exportation",
                export_success: "Pack d'intégrité exporté.",
                export_failed: "L'exportation a échoué.",
                empty_disabled: "Activez les pistes d'audit immuables dans les paramètres pour exécuter des passes de vérification.",
                error_period_required: 'Choisissez une date de début et de fin.',
                error_start_run: "Échec du démarrage de l'exécution.",
                processed: 'Traité : {processed} sur {total}',
                col_verdict: 'Verdict',
                col_count: 'Compter',
                col_proof_link: 'Lien de preuve',
                col_module: 'Module',
                col_event_type: "Type d'événement",
                col_expected: 'Hachage attendu',
                col_recomputed: 'Hachage recalculé',
                col_raised_at: 'Élevé à',
                any_verdict: 'Tout verdict',
                no_issues: 'Aucun problème trouvé.',
            },
            settings_page: {
                load_error: 'Échec du chargement des paramètres.',
                save: 'Enregistrer',
                save_success: 'Paramètres enregistrés.',
                save_error: "L'enregistrement a échoué.",
                discard: 'Ignorer les modifications',
                read_only_banner: "Vous disposez d'un accès en lecture seule. Contactez un administrateur de plateforme pour apporter des modifications.",
                section_status: 'Statut et activation',
                enabled_label: 'Module complémentaire activé',
                enabled_help: "Lorsqu'il est activé, chaque événement d'audit de niveau A et B est haché et ancré.",
                section_mode: 'Mode de preuve par défaut',
                mode_direct: 'Direct (un présentateur par événement)',
                mode_batch: 'Lot (ancre Merkle-root par groupe)',
                mode_hybrid: 'Hybride (registre par classe par défaut)',
                batch_window_label: 'Fenêtre de lot (minutes, 1 à 60)',
                section_taxonomy: 'Taxonomie des événements protégés',
                taxonomy_help: 'Remplacements de classe par événement. Niveau A → mode direct, Niveau B → mode batch, Désactivé → non vérifié.',
                taxonomy_search: 'Remplacements de recherche',
                taxonomy_reset: 'Réinitialiser aux valeurs par défaut',
                taxonomy_event_class: "Classe d'événement",
                taxonomy_override: 'Remplacer',
                taxonomy_actions: 'Actions',
                taxonomy_clear: 'Effacer',
                taxonomy_no_overrides: 'Aucun remplacement : utilisation des valeurs par défaut du registre.',
                taxonomy_add_class: 'event_class.code',
                taxonomy_add: 'Ajouter un remplacement',
                section_caps: 'Plafonds et limites',
                cap_label: 'Plafond mensuel (unités ; vide = illimité)',
                warn_label: "Seuil d'avertissement (unités ; doit être inférieur au plafond)",
                auto_pause_label: 'Mettre automatiquement en pause les nouvelles épreuves une fois le plafond atteint',
                section_credentials: "Informations d'identification",
                credential_id: "Identifiant d'identification",
                credential_rotated_at: 'Dernière rotation',
                rotate_button: "Rotation des informations d'identification",
                rotate_success: "Les informations d'identification ont fait l'objet d'une rotation. Ancien identifiant valide pour {n} minutes supplémentaires.",
                rotate_failed: "La rotation des informations d'identification a échoué.",
                rotate_modal_title: "Changer l'identifiant AesirX ?",
                rotate_modal_body: "La rotation délivre un nouveau titre. L'ancien identifiant reste accepté par AesirX pendant une période de grâce de 60 minutes afin que les travailleurs en vol puissent terminer leur travail actuel. Après 60 minutes, l'ancien identifiant est révoqué.",
                rotate_modal_confirm: 'Faites pivoter maintenant',
                tier_a_off_modal_title: "Désactiver la vérification linguistique pour une classe d'événement ?",
                tier_a_off_modal_body: "La définition d'une classe d'événements sur « Désactivé » empêche le test d'ancrer ces événements. L'architecture §6 explique l'implication réglementaire : les événements de niveau A sont pertinents pour le régulateur ; les désactiver peut briser la défendabilité de votre piste d'audit.",
                tier_a_off_modal_confirm: 'Je comprends - appliquer le remplacement',
                error_warn_lt_cap: "Le seuil d'avertissement doit être inférieur au plafond mensuel.",
                error_window_range: 'La fenêtre de lot doit être comprise entre 1 et 60 minutes.',
                error_run_in_progress: 'Une vérification est en cours ; Je ne peux pas désactiver le module complémentaire pour le moment.',
            },
            status_page: {
                filter_status: 'Statut',
                filter_mode: 'Mode preuve',
                filter_module: 'Module',
                filter_source_collection: 'Collection de sources',
                filter_from_date: 'De',
                filter_to_date: 'À',
                filter_search: 'Rechercher',
                any_status: "N'importe quel statut",
                any_mode: "N'importe quel mode",
                col_module: 'Module',
                col_event_type: "Type d'événement",
                col_proof_status: 'Statut',
                col_proof_mode: 'Mode',
                col_created_at: 'Créé',
                col_units: 'Unités',
                empty_disabled: 'Le module complémentaire Immutable Audit Trails n’est pas activé pour votre organisation. Visitez Paramètres pour l’activer.',
                empty_enabled_no_rows: 'Aucun lien de preuve ne correspond aux filtres actuels.',
                export_csv: 'Exporter CSV',
                export_xlsx: 'Exporter XLSX',
                export_success: 'Exportation terminée',
                export_failed: "Échec de l'exportation",
            },
            usage_page: {
                section_alerts: 'Alertes',
                alert_dismiss: 'Rejeter',
                alert_dismiss_aria: "Ignorer l'alerte {id}",
                alert_dismissed: 'Alerte rejetée',
                alert_dismiss_failed: "Échec de la suppression de l'alerte",
                section_balance: 'Solde créditeur',
                balance_label: 'Crédits disponibles',
                balance_hint: "Unités restantes (1 unité = 1 preuve d'événement protégé).",
                section_current_month: 'Mois en cours',
                consumed_label: 'Consommé',
                granted_label: 'Accordé',
                refunded_label: 'Remboursé',
                net_label: 'Filet',
                section_history: "Historique d'utilisation",
                history_kind: 'Genre',
                history_units: 'Unités',
                history_balance_after: 'Solde après',
                history_notes: 'Remarques',
                history_at: 'Quand',
                cap_warning_banner: "Vous avez dépassé votre seuil d'avertissement pour ce mois de facturation. Pensez à utiliser des crédits supplémentaires.",
                cap_paused_banner: 'Plafond mensuel atteint. Les soumissions de preuves sont suspendues jusqu’au mois prochain ou jusqu’à ce que le plafond soit augmenté.',
                section_redeem: 'Échanger un forfait de crédits',
                redeem_purchase_id: 'Numéro de facture',
                redeem_package: 'Forfait',
                redeem_button: 'Échanger',
                redeem_pending: 'Rachat…',
                redeem_success: 'Crédits échangés',
                redeem_failed: "Échec de l'échange",
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'unités',
                alert_dismissing: 'Rejeter…',
                alert_kind: {
                    cap_reached: 'Plafond mensuel atteint',
                    low_balance: 'Solde faible',
                    auth_failure: "Échec d'authentification",
                    merkle_mismatch: 'Inadéquation de Merkle',
                    batch_terminal_failure: 'Panne du terminal batch',
                    direct_terminal_failure: 'Échec du terminal de soumission directe',
                },
                usage_kind: {
                    consume: 'Consommer',
                    refund: 'Remboursement',
                    grant: 'Subvention',
                    adjustment: 'Ajustement',
                    reservation: 'Réservation',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Démarreur',
                package_growth: 'Croissance',
                package_enterprise: 'Entreprise',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Acheter plus de crédits',
                    purchase_title: 'Acheter plus de crédits',
                    purchase_subtitle: 'Choisissez un forfait. Vous serez redirigé vers le paiement sécurisé Stripe, et votre facture vous parviendra par e-mail.',
                    checkout_button: 'Continuer vers le paiement',
                    checkout_pending: 'Ouverture du paiement…',
                    cancel_button: 'Annuler',
                    purchase_error: 'Impossible de démarrer le paiement. Veuillez réessayer dans un instant.',
                    redeem_subtitle: "Déjà payé ? Sélectionnez le forfait que vous avez acheté et saisissez l'identifiant de facture reçu par e-mail pour ajouter les crédits.",
                    error_not_found: "Aucune facture n'a été trouvée avec ce numéro. Vérifiez le numéro de facture et réessayez.",
                    error_not_paid: "Cette facture n'est pas encore marquée comme payée.",
                    error_org_mismatch: "Cette facture n'a pas été émise à votre organisation.",
                    error_package_mismatch: 'Cette facture ne correspond pas au forfait sélectionné.',
                    error_billing_unavailable: 'Impossible de joindre le service de facturation. Veuillez réessayer dans un instant.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Configuration des fichiers',
            subtitle: 'Configurez les limites de téléchargement de fichiers, les politiques de rétention et les remplacements par type de propriétaire pour la surface des pièces jointes de la plateforme.',
            section_global: 'Valeurs par défaut globales',
            section_global_help: "Ces limites s'appliquent à chaque fichier téléchargé par les utilisateurs de votre organisation, à moins qu'un remplacement par type de propriétaire ci-dessous ne les modifie.",
            section_overrides: 'Remplacements par type de propriétaire',
            section_overrides_help: "Remplacez les limites globales pour un type de propriétaire spécifique. Les valeurs de type par propriétaire l'emportent toujours sur le paramètre global.",
            max_file_size_label: 'Taille maximale du fichier',
            max_file_size_hint: 'Par téléchargement individuel (MiB)',
            max_total_attachments_label: 'Nombre total maximum de pièces jointes',
            max_total_attachments_hint: 'Dans toute l’organisation',
            max_total_size_label: 'Stockage total maximum',
            max_total_size_hint: 'Dans l’ensemble de l’organisation (MiB)',
            allowed_mime_types_label: 'Types de fichiers autorisés',
            allowed_mime_types_placeholder: 'Sélectionnez les types MIME',
            retention_days_label: 'Période de conservation (jours)',
            retention_days_hint: 'Combien de temps les fichiers doivent être conservés avant de pouvoir être supprimés. Laissez vide pour aucune politique de rétention.',
            legal_hold_label: 'Mise en attente légale',
            legal_hold_hint: "Lorsqu'elle est activée, la suppression définitive d'un fichier de ce type de propriétaire nécessite l'autorisation légale de remplacement et l'adhésion explicite de l'opérateur.",
            save: 'Enregistrer les modifications',
            saving: 'Sauvegarde…',
            reset: 'Réinitialiser',
            save_success: 'Paramètres enregistrés',
            empty_state_title: 'Utilisation des paramètres par défaut de la plateforme',
            empty_state_body: "Votre organisation s'exécute actuellement sur la stratégie de fichiers par défaut de la plateforme. Modifiez n'importe quelle valeur ci-dessous pour créer un remplacement au niveau de l'organisation.",
            read_only_banner: "Vous disposez d'un accès en lecture seule à la configuration des fichiers. Contactez un administrateur de plateforme pour apporter des modifications.",
            owner_type_default_badge: 'Par défaut',
            owner_type_overridden_badge: 'Remplacé',
            owner_type_label: 'Type de propriétaire',
            owner_type_collapse: 'Réduire',
            owner_type_expand: 'Développer',
            override_save: 'Enregistrer le remplacement',
            override_remove: 'Supprimer le remplacement',
            mime_labels: {
                'application/pdf': 'PDF document',
                'image/png': 'Image PNG',
                'image/jpeg': 'Image JPEG',
                'image/jpg': 'Image JPG',
                'application/msword': 'Document Word (ancien)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Document Word',
                'application/vnd.ms-excel': 'Feuille de calcul Excel (ancienne version)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Feuille de calcul Excel',
                'text/csv': 'Fichier CSV',
                'text/plain': 'Texte brut',
                'message/rfc822': 'Message électronique',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Navigateur de fichiers',
            subtitle: "Recherchez, filtrez et gérez chaque pièce jointe téléchargée dans l'ensemble de l'organisation.",
            filter_owner_type: 'Type de propriétaire',
            filter_uploader: 'Téléchargé par',
            filter_from: 'Téléchargé après',
            filter_to: 'Téléversé avant',
            filter_size_min: 'Taille minimale (Mio)',
            filter_size_max: 'Taille maximale (Mio)',
            filter_mime: 'Type de fichier',
            filter_deleted_state: 'État de suppression',
            filter_deleted_any: 'Tous les fichiers',
            filter_deleted_only: 'Seulement supprimé',
            filter_deleted_exclude: 'Exclure supprimé',
            filter_orphaned: 'Orphelin seulement',
            filter_search_placeholder: 'Rechercher le nom du fichier…',
            filter_apply: 'Appliquer des filtres',
            filter_clear: 'Effacer les filtres',
            col_filename: 'Nom du fichier',
            col_owner_type: 'Type de propriétaire',
            col_owner_entity: 'Propriétaire',
            col_uploader: 'Téléchargé par',
            col_uploaded_at: 'Téléchargé',
            col_size: 'Taille',
            col_mime: 'Tapez',
            col_state: 'État',
            col_actions: 'Actions',
            state_active: 'Actif',
            state_deleted: 'Supprimé',
            state_orphaned: 'Orphelin',
            action_view_detail: 'Afficher les détails',
            action_download: 'Télécharger',
            action_ai_edits: 'AI Modifications',
            action_soft_delete: 'Suppression logicielle',
            action_restore: 'Restaurer',
            action_hard_delete: 'Suppression définitive',
            bulk_soft_delete: 'Suppression logicielle sélectionnée',
            bulk_export_csv: 'Exporter CSV',
            bulk_select_all: 'Tout sélectionner',
            reason_label: 'Raison',
            reason_min_10_chars: 'Le motif est requis (au moins 10 caractères)',
            reason_min_20_chars: 'Le motif est requis (au moins 20 caractères)',
            legal_hold_override_label: 'Remplacer la conservation légale',
            legal_hold_override_warning: "Ce fichier est sous conservation légale. La suppression définitive sera enregistrée dans la piste d'audit avec votre motif et votre indicateur de remplacement.",
            empty_state_title: 'Aucun fichier ne correspond à vos filtres',
            empty_state_body: 'Ajustez vos filtres pour voir les résultats ou effacez-les pour afficher tous les fichiers.',
            pagination_prev: 'Précédent',
            pagination_next: 'Suivant',
            pagination_page: 'Page {page} sur {total}',
            pagination_size_label: 'Par page',
            pagination_total: 'Total : {count}',
            toast_soft_deleted: 'Fichier supprimé de manière logicielle',
            toast_restored: 'Fichier restauré',
            toast_hard_deleted: 'Fichier définitivement supprimé',
            toast_bulk_deleted: '{count} fichier(s) supprimé(s)',
            toast_error: "L'opération a échoué",
            confirm_soft_delete_title: 'Fichier de suppression logicielle',
            confirm_restore_title: 'Restaurer le fichier',
            confirm_hard_delete_title: 'Supprimer définitivement le fichier',
            confirm_bulk_soft_delete_title: 'Supprimer les fichiers sélectionnés',
            confirm_submit: 'Confirmer',
            confirm_cancel: 'Annuler',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Détails du fichier',
            close: 'Fermer',
            meta_filename: 'Nom du fichier',
            meta_size: 'Taille',
            meta_mime: 'Tapez',
            meta_owner_type: 'Type de propriétaire',
            meta_owner_entity: 'Entité propriétaire',
            meta_uploader: 'Téléchargé par',
            meta_uploaded_at: 'Téléchargé',
            meta_storage_path: 'Chemin de stockage',
            meta_deleted_state: 'État de suppression',
            audit_section_title: "Piste d'audit",
            audit_load_more: "Charger plus d'événements",
            audit_empty: "Aucun événement d'audit enregistré pour ce fichier.",
            audit_config_changed: 'Configuration modifiée',
            audit_soft_deleted: 'Suppression logicielle',
            audit_restored: 'Restauré',
            audit_hard_deleted: 'Supprimé définitivement',
            audit_downloaded: 'Téléchargé',
            audit_quota_breached: 'Tentative de violation du quota',
            audit_owner_type_download_denied: 'Récupération refusée',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} par {actor} le {at}',
            action_download: 'Télécharger',
            action_soft_delete: 'Suppression logicielle',
            action_restore: 'Restaurer',
            action_hard_delete: 'Suppression définitive',
            effective_settings_title: 'Politique efficace',
            effective_max_file_size: 'Taille maximale du fichier',
            effective_retention_days: 'Rétention',
            effective_legal_hold: 'Mise en attente légale',
            effective_legal_hold_yes: 'Actif',
            effective_legal_hold_no: 'Aucun',
        },
        files_dashboard: {
            title: 'Tableau de bord des fichiers',
            subtitle: 'Utilisation du stockage, croissance et fichiers orphelins dans toute l’organisation.',
            kpi_total_files: 'Total des fichiers',
            kpi_total_size: 'Stockage total',
            kpi_near_quota: 'Utilisation des quotas',
            kpi_orphaned: 'Fichiers orphelins',
            chart_by_owner_type: 'Fichiers par type de propriétaire',
            chart_growth_30d: 'Croissance sur 30 jours',
            toggle_count: 'Par comptage',
            toggle_size: 'Par taille',
            orphaned_panel_title: 'Fichiers orphelins (top 20)',
            orphaned_view_all: 'Afficher tous les fichiers orphelins',
            orphaned_empty: 'Aucun fichier orphelin trouvé.',
            growth_empty: 'Aucune activité de téléchargement au cours des 30 derniers jours.',
            retention_n_days: '{days} jour(s)',
            retention_none: 'Aucune politique',
        },
    },
    category_labels: {
        'user-guide': "Guide de l'utilisateur",
        'admin-guide': "Guide d'administration",
        'getting-started': 'Commencer',
        faq: 'FAQ',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Clés',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Administrateur',
        aesirx: 'AesirX',
        purposes: 'Objectifs',
        preferences: 'Préférences',
        onboarding: 'Intégration',
        'first-steps': 'Premiers pas',
        login: 'Connexion',
        navigation: 'Navigation',
        users: 'Utilisateurs',
        user: 'Utilisateur',
        roles: 'Rôles',
        role: 'Rôle',
        'access-control': "Contrôle d'accès",
        'access-management': 'Gestion des accès',
        permissions: 'Autorisations',
        permission: 'Autorisation',
        consent: 'Consentement',
        'consent-management': 'Gestion du consentement',
        privacy: 'Confidentialité',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Vérification',
        auditing: 'Audit',
        breach: 'Violation',
        incident: 'Incident',
        incidents: 'Incidents',
        notification: 'Notification',
        notifications: 'Notifications',
        reporting: 'Rapports',
        risk: 'Risque',
        vendor: 'Vendeur',
        vendors: 'Vendeurs',
        'vendor-risk': 'Risque du fournisseur',
        assessment: 'Évaluation',
        assessments: 'Évaluations',
        dpia: 'DPIA',
        'cross-border': 'Transfrontalier',
        transfer: 'Transfert',
        transfers: 'Transferts',
        deletion: 'Suppression',
        retention: 'Rétention',
        'data-mapping': 'Cartographie des données',
        discovery: 'Découverte',
        classification: 'Classement',
        workflow: 'Flux de travail',
        workflows: 'Flux de travail',
        template: 'Modèle',
        templates: 'Modèles',
        catalog: 'Catalogue',
        monitoring: 'Surveillance',
        tasks: 'Tâches',
        task: 'Tâche',
        'task-management': 'Gestion des tâches',
        organizations: 'Organisations',
        org: 'Organisation',
        governance: 'Gouvernance',
        compliance: 'Conformité',
        forms: 'Formulaires',
        form: 'Formulaire',
        connectors: 'Connecteurs',
        connector: 'Connecteur',
        integration: 'Intégration',
        integrations: 'Intégrations',
        security: 'Sécurité',
        encryption: 'Cryptage',
        backup: 'Sauvegarde',
        recovery: 'Récupération',
        maintenance: 'Entretien',
        settings: 'Paramètres',
        configuration: 'Configuration',
        sla: 'SLA',
        escalation: 'Escalade',
        remediation: 'Assainissement',
        evidence: 'Preuve',
        'case-management': 'Gestion de cas',
        profile: 'Profil',
        delegation: 'Délégation',
        'service-accounts': 'Comptes de services',
        license: 'Licence',
        'license-management': 'Gestion des licences',
        plan: 'Planifier',
        plans: 'Forfaits',
        dashboard: 'Tableau de bord',
        'web-consent': 'Consentement Web',
        'partner-marketplace': 'Marché des partenaires',
        partner: 'Partenaire',
        partners: 'Partenaires',
        deliverables: 'Livrables',
        'service-tiers': 'Niveaux de service',
        'support-tickets': "Billets d'assistance",
        'audit-trail': "Piste d'audit",
        'first-time-setup': 'Configuration initiale',
        setup: 'Configuration',
        quickstart: 'Démarrage rapide',
        tour: 'Visite',
        tours: 'Visites',
        'sample-data': 'Exemples de données',
        'data-subject': 'Personne concernée',
        'data-subjects': 'Personnes concernées',
        pseudonymous: 'Pseudonyme',
        identified: 'Identifié',
        'help-center': "Centre d'aide",
        article: 'Article',
        articles: 'Articles',
        search: 'Rechercher',
        'platform-settings': 'Paramètres de la plateforme',
        'platform-config': 'Configuration de la plateforme',
        'module-config': 'Configuration des modules',
        'module-configuration': 'Configuration des modules',
        'rights-requests': 'Demandes de droits',
        'access-accountability': 'Responsabilité d’accès',
        'data-discovery': 'Découverte de données',
        'incident-ops': 'Opérations sur les incidents',
        'deletion-orchestration': 'Orchestration de la suppression',
        'compliance-forms': 'Formulaires de conformité',
        'program-governance': 'Gouvernance du programme',
        localization: 'Localisation',
        'privacy-scanner': 'Scanner de confidentialité',
        'consent-properties': 'Propriétés du consentement',
        acceptances: 'Acceptations',
        access: 'Accès',
        'access-review': "Examen de l'accès",
        'access-reviews': 'Accès aux avis',
        accountability: 'Responsabilité',
        acknowledgment: 'Remerciement',
        'action-plans': "Plans d'action",
        activation: 'Activation',
        activations: 'Activations',
        'active-response': 'Réponse active',
        activity: 'Activité',
        adequacy: 'Adéquation',
        'admin-guide': "Guide d'administration",
        aggregation: 'Agrégation',
        agreements: 'Accords',
        ai: 'AI',
        alerts: 'Alertes',
        analytics: 'Analyse',
        anchoring: 'Ancrage',
        approval: 'Approbation',
        approvals: 'Approbations',
        apps: 'Applications',
        assets: 'Actifs',
        assignments: 'Missions',
        attestation: 'Attestation',
        'audit-log': "Journal d'audit",
        'audit-pack': "Pack d'audit",
        authentication: 'Authentification',
        authority: 'Autorité',
        'auto-play': 'Lecture automatique',
        automated: 'Automatisé',
        automation: 'Automatisation',
        baseline: 'Référence',
        batches: 'Lots',
        bidirectional: 'Bidirectionnel',
        bilingual: 'Bilingue',
        bindings: 'Reliures',
        blockchain: 'Chaîne de blocs',
        board: 'Conseil',
        brand: 'Marque',
        brds: 'BRD',
        'break-glass': 'Briser la vitre',
        builder: 'Constructeur',
        bulk: 'En vrac',
        'bulk-sync': 'Synchronisation groupée',
        bundles: 'Forfaits',
        calendar: 'Calendrier',
        campaigns: 'Campagnes',
        cases: 'Cas',
        categories: 'Catégories',
        category: 'Catégorie',
        certification: 'Attestation',
        chain: 'Chaîne',
        change: 'Changement',
        'change-awareness': 'Sensibilisation au changement',
        'change-log': 'Journal des modifications',
        'change-management': 'Gestion du changement',
        'change-requests': 'Demandes de changement',
        'cmp-validation': 'CMP Validation',
        collection: 'Collecte',
        comparison: 'Comparaison',
        completeness: 'exhaustivité',
        completion: 'Achèvement',
        'compliance-posture': 'Posture de conformité',
        'compliance-proof': 'Preuve de conformité',
        'compliance-reviews': 'Examens de conformité',
        config: 'Configuration',
        confirmation: 'Confirmation',
        conflicts: 'Conflits',
        consolidation: 'Consolidation',
        continuous: 'Continu',
        contracts: 'Contrats',
        controls: 'Contrôles',
        cookies: 'Biscuits',
        cooperation: 'Coopération',
        coordination: 'Coordination',
        'corporate-governance': "Gouvernance d'entreprise",
        'corrective-actions': 'Actions correctives',
        coverage: 'Couverture',
        'credential-vault': "Coffre-fort d'informations d'identification",
        credentials: "Informations d'identification",
        cycles: 'Cycles',
        dashboards: 'Tableaux de bord',
        'data-assets': 'Actifs de données',
        'data-flows': 'Flux de données',
        'data-sensitivity': 'Sensibilité des données',
        'data-steward': 'Gestionnaire de données',
        deadlines: 'Délais',
        'decree-356': 'Décret 356',
        dedup: 'Déduplication',
        definitions: 'Définitions',
        deliverable: 'Livrable',
        departments: 'Départements',
        'dependency-order': 'Ordre de dépendance',
        deployment: 'Déploiement',
        detection: 'Détection',
        diff: 'Différence',
        'digital-properties': 'Propriétés numériques',
        dimensions: 'Dimensions',
        disable: 'Désactiver',
        'disaster-recovery': 'Reprise après sinistre',
        disclosure: 'Divulgation',
        documentation: 'Documentation',
        documents: 'Documents',
        domains: 'Domaines',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Dérive',
        'drift-detection': 'Détection de dérive',
        'due-diligence': 'Diligence raisonnable',
        email: 'Email',
        emergency: 'Urgence',
        enable: 'Activer',
        'enterprise-map': "Carte d'entreprise",
        erasure: 'Effacement',
        evaluation: 'Évaluation',
        'event-bus': 'Bus événementiel',
        exceptions: 'Exceptions',
        execution: 'Exécution',
        executive: 'Exécutif',
        exercises: 'Exercices',
        export: 'Exporter',
        exports: 'Exportations',
        filing: 'Dépôt',
        filter: 'Filtrer',
        filtering: 'Filtrage',
        findings: 'Résultats',
        flows: 'Flux',
        'follow-up': 'Suivi',
        forensic: 'Forensique',
        forensics: 'Médecine légale',
        'form-filler': 'Remplisseur de formulaire',
        framework: 'Cadre',
        frameworks: 'Cadres',
        'gap-analysis': 'Analyse des écarts',
        'gap-report': "Rapport d'écart",
        gaps: 'Lacunes',
        gates: 'Portes',
        'getting-started': 'Commencer',
        'golden-templates': 'Modèles dorés',
        grc: 'GRC',
        'group-management': 'Gestion de groupe',
        groups: 'Groupes',
        'guided-tours': 'Visites guidées',
        guides: 'Guides',
        handling: 'Manipulation',
        'hands-on': 'Pratique',
        health: 'Santé',
        help: 'Aide',
        hierarchy: 'Hiérarchie',
        history: 'Histoire',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': "Fournisseur d'identité",
        implementation: 'Mise en œuvre',
        import: 'Importer',
        improvement: 'Amélioration',
        'incident-response': 'Réponse aux incidents',
        inconsistencies: 'Incohérences',
        instances: 'Instances',
        intake: 'Admission',
        'intake-key': "Clé d'admission",
        interactive: 'Interactif',
        international: 'Internationale',
        inventory: 'Inventaire',
        investigation: 'Enquête',
        investigations: 'Enquêtes',
        'ip-allowlist': "Liste d'adresses IP autorisées",
        issues: 'Problèmes',
        justification: 'Justification',
        justifications: 'Justifications',
        kanban: 'Kanban',
        keyboard: 'Clavier',
        keys: 'Clés',
        'knowledge-base': 'Base de connaissances',
        koncernstyring: "Gouvernance d'entreprise",
        kpi: 'KPI',
        labeling: 'Étiquetage',
        language: 'Langue',
        'language-packs': 'Packs de langue',
        languages: 'Langues',
        lawfulness: 'Légalité',
        layout: 'Disposition',
        'legal-basis': 'Base juridique',
        'lessons-learned': 'Leçons apprises',
        levels: 'Niveaux',
        library: 'Bibliothèque',
        lifecycle: 'Cycle de vie',
        lineage: 'Lignée',
        links: 'Liens',
        listings: 'Annonces',
        live: 'En direct',
        locale: 'Paramètres régionaux',
        log: 'Log',
        logging: 'Journalisation',
        'managed-service': 'Service géré',
        mapping: 'Cartographie',
        mappings: 'Mappages',
        'market-packs': 'Packs de marché',
        marketplace: 'Marché',
        maturity: 'Maturité',
        metrics: 'Métriques',
        models: 'Modèles',
        module: 'Module',
        modules: 'Modules',
        mps: 'MPS',
        'multi-factor': 'Multi-Facteur',
        'multi-market': 'Multi-marchés',
        'multi-org': 'Multi-organisation',
        'multi-property': 'Multi-propriété',
        'my-tasks': 'Mes tâches',
        obligations: 'Obligations',
        offboarding: 'Débarquement',
        oidc: 'OIDC',
        orchestration: 'Orchestration',
        'org-groups': 'Groupes organisationnels',
        organization: 'Organisation',
        overdue: 'En retard',
        overview: 'Aperçu',
        'owner-review': 'Examen du propriétaire',
        package: 'Forfait',
        packages: 'Forfaits',
        packs: 'Packs',
        pagination: 'Pagination',
        'parent-child': 'Parent-Enfant',
        patterns: 'Modèles',
        performance: 'Performances',
        periodic: 'Périodique',
        periods: 'Périodes',
        personal: 'Personnel',
        'personal-data': 'Données personnelles',
        personas: 'Personnages',
        planning: 'Planification',
        platform: 'Plateforme',
        playbooks: 'Manuels de jeu',
        poc: 'PoC',
        'point-in-time': 'Point dans le temps',
        policies: 'Politiques',
        portfolio: 'Portefeuille',
        'post-incident': "Après l'incident",
        'power-user': 'Utilisateur expérimenté',
        practice: 'Pratique',
        pricing: 'Tarifs',
        'privacy-impact': 'Impact sur la confidentialité',
        procedures: 'Procédures',
        processes: 'Processus',
        processing: 'Traitement',
        'processing-activities': 'Activités de traitement',
        procurement: 'Approvisionnement',
        productivity: 'Productivité',
        profiles: 'Profils',
        programs: 'Programmes',
        progress: 'Progrès',
        proof: 'Preuve',
        propagation: 'Propagation',
        properties: 'Propriétés',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Formulaire public',
        quality: 'Qualité',
        questionnaires: 'Questionnaires',
        'quick-links': 'Liens rapides',
        'quick-start': 'Démarrage rapide',
        'quick-starts': 'Démarrages rapides',
        raci: 'RACI',
        readiness: 'Préparation',
        'real-time': 'En temps réel',
        reassessments: 'Réévaluations',
        reconciliation: 'Réconciliation',
        record: 'Enregistrer',
        records: 'Enregistrements',
        recurring: 'Récurrent',
        register: "S'inscrire",
        registry: 'Registre',
        regulations: 'Règlements',
        regulator: 'Régulateur',
        regulatory: 'Réglementaire',
        'regulatory-frameworks': 'Cadres réglementaires',
        remap: 'Remapper',
        reminders: 'Rappels',
        reports: 'Rapports',
        requests: 'Demandes',
        requirements: 'Exigences',
        response: 'Réponse',
        'response-plans': 'Plans de réponse',
        'response-team': "Équipe d'intervention",
        'response-templates': 'Modèles de réponse',
        'response-time': 'Temps de réponse',
        responses: 'Réponses',
        responsibility: 'Responsabilité',
        results: 'Résultats',
        review: 'Examen',
        reviews: 'Avis',
        'right-to-delete': 'Droit de suppression',
        rights: 'Droits',
        'risk-tiers': 'Niveaux de risque',
        rollouts: 'Déploiements',
        rollup: 'Cumul',
        ropa: 'ROPA',
        routing: 'Routage',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Règles',
        runs: 'Fonctionne',
        saml: 'SAML',
        scan: 'Numériser',
        scanning: 'Numérisation',
        scc: 'SCC',
        schedule: 'Calendrier',
        scheduled: 'Programmé',
        schedules: 'Horaires',
        scheduling: 'Planification',
        scores: 'Partitions',
        scoring: 'Notation',
        scripts: 'Scripts',
        'security-reviews': 'Examens de sécurité',
        'sensitive-data': 'Données sensibles',
        sensitivity: 'Sensibilité',
        'separation-of-duties': 'Séparation des tâches',
        service: 'Service',
        sessions: 'Séances',
        'setup-wizard': 'Assistant de configuration',
        severity: 'Gravité',
        'shared-governance': 'Gouvernance partagée',
        'shared-settings': 'Paramètres partagés',
        shortcuts: 'Raccourcis',
        sidebar: 'Barre latérale',
        signals: 'Signaux',
        'single-sign-on': 'Authentification unique',
        snapshots: 'Instantanés',
        sod: 'SoD',
        sorting: 'Tri',
        sources: 'Sources',
        staging: 'Mise en scène',
        standards: 'Normes',
        status: 'Statut',
        structure: 'Structure',
        'sub-processor': 'Sous-traitant',
        'sub-processors': 'Sous-traitants',
        submissions: 'Soumissions',
        subprocessors: 'Sous-traitants',
        subscription: 'Abonnement',
        suggestions: 'Suggestions',
        summaries: 'Résumés',
        supplements: 'Suppléments',
        support: 'Assistance',
        sync: 'Synchroniser',
        system: 'Système',
        systems: 'Systèmes',
        tables: 'Tableaux',
        tagging: 'Marquage',
        'tamper-proof': 'Inviolable',
        taxonomies: 'Taxonomies',
        taxonomy: 'Taxonomie',
        tenants: 'Locataires',
        termination: 'Résiliation',
        'third-party': 'Tiers',
        'ticket-sync': 'Synchronisation des billets',
        tickets: 'Billets',
        tiers: 'Niveaux',
        tokens: 'Jetons',
        totp: 'TOTP',
        traceability: 'Traçabilité',
        tracking: 'Suivi',
        trail: 'Sentier',
        training: 'Formation',
        translation: 'Traduction',
        translations: 'Traductions',
        transparency: 'Transparence',
        trends: 'Tendances',
        unified: 'Unifié',
        updates: 'Mises à jour',
        validation: 'Validation',
        vault: 'Coffre-fort',
        'vendor-requests': 'Demandes des fournisseurs',
        verification: 'Vérification',
        versioning: 'Gestion des versions',
        versions: 'Versions',
        vietnam: 'Viêt Nam',
        violations: 'Violations',
        visualization: 'Visualisation',
        waivers: 'Renonciations',
        'web-records': 'Enregistrements Web',
        website: 'Site Web',
        websites: 'Sites Web',
        weights: 'Poids',
        'white-label': 'Marque blanche',
        withdrawal: 'Retrait',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: "Demandes d'exercice des droits",
        data_mapping: 'Cartographie des données',
        governance: 'Gouvernance',
        consent: 'Gouvernance du consentement',
        consent_properties: 'Propriétés du consentement',
        assessments: 'AIPD et évaluations',
        classification: 'Classification des données',
        monitoring: 'Programmes de surveillance',
        program: 'Programme de surveillance',
        incidents: 'Réponse aux incidents',
        connectors: 'Connecteurs',
        vendor_risk: 'Risque du fournisseur',
        deletion_orchestration: 'Orchestration de suppression',
        discovery: 'Découverte de données',
        incident_ops: "Opérations d'incidents",
        program_governance: 'Gouvernance de programme',
        access_accountability: 'Responsabilité des accès',
        localization: 'Localisation',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Espace de travail partenaire',
        privacy_scanner: 'Scanner de confidentialité',
        onboarding: 'Intégration',
        compliance_forms: 'Formulaires de Conformité',
        audit_trail: "Piste d'Audit",
        data_subjects: 'Personnes concernées',
        organizations: 'Organisations',
        task_management: 'Gestion des tâches',
        deletion_orch: 'Orchestration de suppression',
        forms: 'Formulaires de conformité',
        partner_marketplace: 'Place de marché partenaires',
        dashboard: 'Tableau de bord',
        platform_settings: 'Paramètres de la plateforme',
        help_center: "Centre d'aide",
    },
    module_descriptions: {
        rights_requests: "Gérez les demandes d'exercice des droits des personnes concernées, y compris l'accès, la suppression et la portabilité. Suivez le cycle de vie des demandes, attribuez des responsables et respectez les délais réglementaires.",
        data_mapping: 'Cartographiez les flux de données dans votre organisation. Suivez les activités de traitement, les catégories de données, les finalités, les politiques de conservation et les intégrations système.',
        governance: "La supervision et les preuves qui la fondent. Gérez le registre des prestataires et son cycle de vie, approuvez les décisions que ce module encadre, surveillez les dérives de conformité et tenez les registres de confiance et d'identité, d'artefacts réglementaires, de paiements et eKYC, et de preuves de la plateforme.",
        consent: "Gérez la collecte, le stockage et le retrait du consentement. Suivez les enregistrements de consentement, les finalités et l'intégration avec les plateformes de gestion du consentement.",
        consent_properties: 'Configurez les propriétés de consentement, les groupes et les modèles de référence. Définissez des configurations de consentement réutilisables pour un déploiement cohérent.',
        assessments: "Réalisez des Analyses d'Impact relatives à la Protection des Données (AIPD) et des évaluations des risques de confidentialité. Évaluez les activités de traitement pour identifier les risques de conformité.",
        classification: 'Classifiez les actifs de données par niveau de sensibilité. Appliquez des étiquettes taxonomiques, suivez les enregistrements du cycle de vie et identifiez les lacunes de classification.',
        monitoring: "Mettez en place des programmes de surveillance pour suivre les indicateurs de conformité, les indicateurs clés de risque et l'efficacité des contrôles dans le temps.",
        incidents: "Signalez et gérez les incidents de confidentialité et les violations de données. Suivez les processus d'enquête, de notification et de remédiation.",
        connectors: 'Configurez les connecteurs de sources de données et les intégrations. Connectez des systèmes externes pour la découverte automatisée des données et la surveillance.',
        vendor_risk: "Évaluez et surveillez les risques des prestataires tiers. Notez les prestataires, suivez l'atténuation des risques et gérez les configurations de risque des prestataires.",
        deletion_orchestration: 'Orchestrez la suppression des données dans tous les systèmes. Suivez les demandes de suppression, vérifiez les chaînes de preuves et assurez la suppression complète des données.',
        discovery: 'Découvrez les données personnelles dans les systèmes connectés. Analysez les sources de données pour constituer un inventaire complet des données.',
        incident_ops: "Gestion opérationnelle des incidents avec des manuels de procédures, des processus d'escalade et un suivi des SLA pour la résolution des incidents.",
        program_governance: "Suivez la maturité globale du programme de confidentialité, les scores de conformité, les lacunes en matière de preuves et les rapports à la direction dans l'ensemble de votre organisation.",
        access_accountability: "Enquêtez et auditez les modèles d'accès des utilisateurs. Suivez les configurations d'accès, surveillez les activités suspectes et appliquez la responsabilisation.",
        localization: 'Installez et gérez les packs de localisation de marché. Ajoutez des cadres réglementaires, des packs linguistiques et du contenu de conformité spécifique à chaque région.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Collaborez avec des partenaires externes. Partagez des données de conformité, suivez les obligations des partenaires et gérez les relations partenaires.',
        privacy_scanner: "Analysez les sites web pour détecter les problèmes de conformité en matière de confidentialité. Surveillez l'utilisation des cookies, les scripts de suivi et la présence de politiques de confidentialité en temps réel.",
        onboarding: "Guidez les nouvelles organisations dans la configuration de la plateforme. Assistant d'intégration étape par étape pour la configuration initiale et l'importation des données.",
        compliance_forms: "Créez et gérez des formulaires de conformité à partir d'un catalogue de modèles. Remplissez des formulaires réglementaires, suivez les soumissions et générez des rapports.",
        audit_trail: "Consultez une piste d'audit complète de toutes les actions du système. Suivez qui a fait quoi, quand et où dans tous les modules.",
        organizations: "Gérez les organisations et les paramètres multi-locataires. Configurez les profils d'organisation, les hiérarchies et les relations inter-organisations.",
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Expliquer les anomalies',
            explaining: 'Expliquer…',
            results_heading: 'Explications des anomalies ({severity})',
            tokens_label: '≈ {count} jetons',
            items_label: '{count} éléments',
            remediation_label: 'Étapes de remédiation',
            items_detail_label: 'Articles concernés',
            accept_label: "Accepter l'explication",
            reject_label: "Rejeter l'explication",
            error_generic: "Impossible d'expliquer les anomalies. Réessayez ou vérifiez que l'analyse s'est terminée avec succès.",
        },
        cookie_insight: {
            insight_label: 'Informations sur les cookies et le tracker AI',
            producing: 'Produire des insights…',
            results_heading: 'Informations sur les cookies et les trackers ({severity})',
            tokens_label: '≈ {count} jetons',
            items_label: '{count} éléments',
            classification_label: 'Classement',
            severity_label: 'Gravité',
            banner_copy_label: 'Copie de bannière suggérée',
            retention_label: 'Conseils de rétention',
            citations_label: 'Citations',
            kind_cookie: 'Biscuit',
            kind_tracker: 'Traqueur',
            class_necessary: 'Nécessaire',
            class_analytics: 'Analyse',
            class_marketing: 'Commercialisation',
            class_social: 'Social',
            accept_label: 'Accepter les informations',
            reject_label: 'Rejeter les insights',
            no_items: "Aucun cookie ou traceur n'a été détecté lors de cette analyse.",
            error_generic: "Impossible de produire les informations sur les cookies. Réessayez ou vérifiez que l'analyse s'est terminée avec succès.",
        },
        monitoring: {
            title: 'Confidentialité Surveillance',
            new_scan: 'Nouveau scanner',
            scan_url: 'URL à Scanner',
            scan_frequency: 'Scanner Fréquence',
            last_scan: 'Dernier Scanner',
            score: 'Confidentialité Score',
            issues: 'Problèmes Trouvé',
            history: 'Scanner Historique',
            settings: 'Surveillance Paramètres',
        },
        real_time: {
            title: 'Analyseur en temps réel',
            scan_now: 'Scanner Maintenant',
            scanning: 'Analyse...',
            results: 'Scanner Résultats',
            no_results: "Aucun résultat d'analyse.",
            cookies: 'Témoins de connexion',
            trackers: 'Traceurs',
            consent_banners: 'Bannières de consentement',
            third_party: 'Scripts tiers',
            archive: 'Résultats Archiver',
            settings: 'Paramètres du scanneur',
        },
        dashboard: {
            page_title: 'Tableau de bord de surveillance de la confidentialité',
            privacy_monitoring: 'Surveillance de la confidentialité',
            plan_label: '{package} forfait',
            expires_label: 'expire le {date}',
            settings: 'Paramètres',
            refresh: 'Actualiser',
            refreshing_dashboard: 'Actualisation du tableau de bord de surveillance',
            refresh_aria: 'Actualiser le tableau de bord de surveillance',
            not_configured_title: 'Surveillance de la confidentialité non configurée',
            not_configured_desc: 'Connectez votre AesirX service de surveillance de la confidentialité pour commencer à suivre la conformité du site Web.',
            configure_monitoring: 'Configurer la surveillance',
            license_expired_title: 'Licence expirée',
            license_expired_desc: 'Votre licence AesirX Privacy Monitoring a expiré. Veuillez le renouveler pour éviter une interruption du service.',
            compliance_score: 'Score de conformité',
            cookies_detected: 'Cookies détectés',
            trackers_label: 'Traqueurs',
            open_issues: 'Problèmes ouverts',
            score_good: 'Bon',
            score_needs_improvement: "Besoin d'amélioration",
            score_critical: 'Critique',
            action_required: 'Action requise',
            cookies_sr: 'cookies détectés',
            trackers_sr: 'trackers trouvés',
            open_issues_sr: 'problèmes ouverts',
            action_required_sr: 'action requise',
            metric_label: 'Métrique',
            value_label: 'Valeur',
            status_label: 'Statut',
            aria_metrics: 'Mesures de surveillance de la confidentialité',
            plan_limit_warning: 'Numérisation de URL : {current}/{max} — vous avez atteint la limite de votre forfait.',
            manage_domains: 'Gérez les domaines ou mettez à niveau votre forfait.',
            monitored_domains: 'Domaines surveillés',
            domain_added: 'Domaine {domain} ajouté',
            domain_removed: 'Domaine {domain} supprimé',
            failed_add_domain: "Échec de l'ajout du domaine",
            failed_remove_domain: 'Échec de la suppression du domaine',
            failed_load: 'Échec du chargement du tableau de bord de surveillance',
            new_domain_aria: 'Nouveau domaine URL',
            add_domain: 'Ajouter un domaine',
            adding_domain: 'Ajout…',
            no_domains: "Aucun domaine surveillé n'est encore configuré.",
            aria_domains_table: 'Domaines surveillés',
            col_domain: 'Domaine',
            col_compliance_score: 'Score de conformité',
            col_status: 'Statut',
            col_cookies: 'Biscuits',
            col_trackers: 'Traqueurs',
            col_issues: 'Problèmes',
            col_last_scan: 'Dernière analyse',
            col_action: 'Action',
            compliance_status_sr: 'état de conformité',
            showing_chart: 'Affichage de la vue graphique',
            showing_table: 'Affichage de la vue du tableau de données',
            loading_sr: 'Tableau de bord de surveillance du chargement',
            col_framework_tags: 'Balises du cadre',
            col_last_monitored: 'Dernière surveillance',
            framework_tags_sr: 'cadres réglementaires applicables',
            last_monitored_sr: 'horodatage de surveillance continue',
            audit_trail_heading: "Piste d'audit",
            audit_trail_info: "Chaque analyse de surveillance est horodatée et enregistrée. Les modifications de conformité au niveau du domaine produisent un enregistrement d'audit immuable qui peut être exporté pour examen réglementaire.",
            cybersecurity_law_info_heading: 'Détection des lois sur la cybersécurité',
            cybersecurity_law_info: 'Les domaines surveillés sont automatiquement vérifiés par rapport aux exigences de la loi sur la cybersécurité (par exemple, la loi vietnamienne sur la cybersécurité, la directive NIS2). Les balises framework indiquent quelles réglementations s’appliquent. Les problèmes sont classés par cadre réglementaire pour des mesures correctives ciblées.',
        },
        scan: {
            page_title: 'Analyse en temps réel',
            title: 'Scanner de confidentialité en temps réel',
            settings: 'Paramètres',
            results_archive: 'Archives des résultats',
            launch_scan: "Lancer l'analyse",
            target_url: 'Cible URL',
            protocol_aria: 'Protocole',
            url_required: 'URL est requis',
            url_whitespace: 'Veuillez supprimer les espaces du URL',
            url_invalid: 'Veuillez saisir un domaine valide (par exemple, exemple.com)',
            add_subpages: 'Ajouter des sous-pages',
            add_subpages_aria: 'Ajouter des sous-pages, {count} ajouté',
            start_scan: "Démarrer l'analyse",
            launching: 'Lancement…',
            scan_launched: "L'analyse a été lancée avec succès",
            scan_failed: "Échec du lancement de l'analyse",
            scan_progress: "Progression de l'analyse",
            scanning_in_progress: 'Numérisation en cours…',
            scan_id: "Numériser l'identifiant",
            status_label: 'Statut',
            status_unknown: 'unknown',
            progress_label: 'Progrès',
            pages_label: 'Pages',
            refresh_status: 'Actualiser le statut',
            new_scan: 'Nouvelle analyse',
            scan_completed: 'Scan terminé, résultats disponibles',
            failed_check_status: "Échec de la vérification de l'état de l'analyse",
            scan_results: "Résultats de l'analyse",
            main_page: 'Page principale',
            page_n: 'Page {n}',
            result_pages_nav: 'Pages de résultats',
            page_url: 'Page URL',
            risk_score: 'Score de risque',
            risk_score_sr: 'score de risque',
            cookies_label: 'Biscuits',
            cookies_sr: 'cookies détectés',
            scripts_label: 'Scripts',
            scripts_sr: 'scripts détectés',
            issues_label: 'Problèmes',
            issues_sr: 'problèmes trouvés',
            issues_found: 'Problèmes trouvés',
            aria_issues_table: "Problèmes détectés lors de l'analyse",
            col_severity: 'Gravité',
            col_category: 'Catégorie',
            col_title: 'Titre',
            col_remediation: 'Assainissement',
            severity_sr: 'severity',
            scan_result_metrics: "Métriques des résultats de l'analyse",
            view_full_detail: 'Voir tous les détails',
            col_consent_category: 'Catégorie de consentement',
            consent_category_sr: 'catégorisation du consentement',
            compliance_evidence_heading: 'Preuve de conformité',
            compliance_evidence_info: 'Les résultats de l’analyse sont automatiquement liés aux cas de conformité. Chaque problème peut être attribué à une catégorie de consentement (essentiel, fonctionnel, analytique, marketing) et produit des preuves prêtes à être auditées pour un examen réglementaire.',
            rule_config_heading: 'Configuration des règles',
            rule_config_info: "Les règles d'analyse sont configurables selon le cadre réglementaire. Les vérifications des cookies, la détection des scripts et l'analyse des fournisseurs peuvent être activées ou désactivées via les paramètres. Des règles personnalisées peuvent être définies pour répondre aux exigences de conformité de votre organisation.",
            case_linkage_heading: 'Lien avec les dossiers de conformité',
            case_linkage_info: "Les problèmes détectés lors des analyses en temps réel peuvent être liés à des cas de conformité à des fins de suivi et de correction. Accédez à la page Détails des résultats de l'analyse pour afficher les associations de cas.",
            subpages_modal_title: 'Ajouter des sous-pages',
            subpages_modal_desc: "Ajoutez jusqu'à 5 URL de sous-pages à analyser à côté du domaine principal.",
            subpage_n: 'Sous-page {n}',
        },
        result_detail: {
            page_title: "Détails des résultats de l'analyse",
            title: "Résultat de l'analyse",
            back_to_archive: 'Retour aux archives',
            failed_load: "Échec du chargement du résultat de l'analyse",
            loading_sr: "Chargement du résultat de l'analyse…",
            scan_id: "Numériser l'identifiant",
            target_url: 'Cible URL',
            status_label: 'Statut',
            status_sr: 'status',
            started: 'Commencé',
            completed: 'Terminé',
            pages_scanned: 'Pages numérisées',
            https_label: 'HTTPS',
            highest_risk_score: 'Score de risque le plus élevé',
            most_cookies: 'La plupart des cookies',
            most_vendors: 'La plupart des fournisseurs tiers',
            most_issues: 'La plupart des problèmes',
            across_pages: 'à travers les pages',
            highest_values_aria: 'Valeurs les plus élevées sur toutes les pages',
            scanned_pages_nav: 'Pages numérisées',
            main_page: 'Page principale',
            page_n: 'Page {n}',
            page_url: 'Page URL',
            risk_score: 'Score de risque',
            overall_risk_sr: 'score de risque global',
            cookies_label: 'Biscuits',
            cookies_sr: 'cookies détectés',
            vendors_label: 'Fournisseurs tiers',
            vendors_sr: 'fournisseurs tiers détectés',
            issues_label: 'Problèmes',
            issues_sr: 'problèmes trouvés',
            risk_breakdown_aria: 'Répartition du score de risque',
            issues_found: 'Problèmes trouvés',
            aria_issues_table: "Problèmes détectés lors de l'analyse",
            col_severity: 'Gravité',
            col_category: 'Catégorie',
            col_title: 'Titre',
            col_remediation: 'Assainissement',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies détectés',
            col_name: 'Nom',
            col_domain: 'Domaine',
            col_path: 'Chemin',
            col_secure: 'Sécurisé',
            col_httponly: 'Http uniquement',
            col_samesite: 'Même site',
            vendors_heading: 'Fournisseurs tiers ({count})',
            aria_vendors_table: 'Fournisseurs tiers détectés',
            col_hostname: "Nom d'hôte",
            scripts_heading: 'Scripts / Balises ({count})',
            aria_scripts_table: 'Scripts et balises détectés',
            col_url: 'URL',
            col_type: 'Tapez',
            links_heading: 'Liens',
            first_party: 'Première partie',
            third_party: 'Tiers',
            col_risk_category: 'Catégorie de risque',
            col_regulatory_framework: 'Cadre réglementaire',
            col_actionable_detail: 'Détails exploitables',
            risk_category_sr: 'catégorie de risque réglementaire',
            risk_category_mapping_heading: 'Cartographie des catégories de risques',
            risk_category_mapping_info: "Chaque résultat d'analyse est mappé à une catégorie de risque réglementaire (collecte de données, transfert transfrontalier, gestion du consentement, conservation des données, contrôles de sécurité). Cette cartographie permet des remédiations ciblées priorisées par l’impact réglementaire.",
            compliance_case_heading: 'Association de cas de conformité',
            compliance_case_info: "Les résultats de l'analyse sont liés aux cas de conformité pour un suivi de bout en bout. Les problèmes peuvent être transmis directement des résultats de l’analyse au flux de travail des dossiers de conformité.",
        },
        results_archive: {
            page_title: "Archives des résultats de l'analyse",
            title: "Archives des résultats de l'analyse",
            back_to_scanner: 'Retour au scanner',
            failed_load: "Échec du chargement des résultats de l'analyse",
            results_loaded: "Résultats de l'analyse chargés, enregistrements {count}",
            total_label: 'Total : {count}',
            aria_table: "Archives des résultats de l'analyse",
            col_scan_date: 'Date de numérisation',
            col_target_url: 'Cible URL',
            col_duration: 'Durée',
            col_issues_found: 'Problèmes trouvés',
            col_risk_score: 'Score de risque',
            col_status: 'Statut',
            col_actions: 'Actions',
            scan_status_sr: "état de l'analyse",
            view_result: 'Voir',
            no_results: "Aucun résultat d'analyse trouvé.",
            loading_sr: "Chargement des résultats de l'analyse",
            pagination_aria: "Pagination des résultats de l'analyse",
            col_risk_category: 'Catégorie de risque',
            risk_category_sr: 'catégorie de risque réglementaire',
            archive_retention_heading: 'Conservation des archives',
            archive_retention_info: "Les résultats de l'analyse sont conservés pendant la durée spécifiée dans la politique de conservation des données de votre organisation. Les résultats archivés incluent le contexte d'analyse complet (balises de framework, catégories de risque, conseils de remédiation) et peuvent être exportés au format CSV pour les preuves réglementaires.",
            export_context_info: 'Les fichiers CSV exportés incluent le contexte réglementaire : catégorie de risque, cadres applicables et détails de mesures correctives exploitables pour chaque résultat.',
        },
        scanner_settings: {
            page_title: 'Paramètres du scanner',
            title: 'Paramètres du scanner',
            back_to_scanner: 'Retour au scanner',
            failed_load: 'Échec du chargement de la configuration du scanner',
            endpoint_url: 'Point de terminaison URL',
            save_success: 'La configuration du scanner a été enregistrée avec succès.',
            save_failed: "Échec de l'enregistrement de la configuration du scanner",
            test_connection: 'Tester la connexion',
            testing: 'Test...',
            saving: 'Sauvegarde...',
            loading_sr: 'Chargement des paramètres du scanner',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Paramètres de surveillance de la confidentialité',
            title: 'Paramètres de surveillance',
            back_to_dashboard: 'Retour au tableau de bord',
            failed_load: 'Échec du chargement de la configuration de surveillance',
            credentials_heading: 'AesirX Identifiants',
            connected_as: 'Connecté en tant que {username}',
            credentials_desc: 'Saisissez les identifiants de votre compte AesirX pour connecter Privacy Monitoring. Recherchez votre identifiant de membre sur la page de profil de votre compte AesirX.',
            client_id: 'Identifiant client',
            client_secret: 'Secret client',
            username: "AesirX Nom d'utilisateur (e-mail)",
            password: 'AesirX Mot de passe',
            placeholder_keep: "(laisser vide pour conserver l'existant)",
            credential_form_aria: "AesirX formulaire d'identification",
            test_connection: 'Tester la connexion',
            testing: 'Test…',
            test_aria: 'Tester la connexion AesirX',
            testing_aria: 'Test de connexion',
            save_configuration: 'Enregistrer la configuration',
            saving: 'Sauvegarde…',
            save_aria: "Enregistrer les informations d'identification",
            saving_aria: "Enregistrer les informations d'identification",
            credentials_saved: 'AesirX identifiants enregistrés avec succès.',
            credentials_saving: 'AesirX identifiants enregistrés. Rechargement de la configuration…',
            credentials_save_failed: "Échec de l'enregistrement des informations d'identification",
            client_id_required: 'L’ID client et le nom d’utilisateur sont requis.',
            all_fields_required: 'Tous les champs d’informations d’identification sont obligatoires.',
            email_heading: 'Notifications par courrier électronique',
            receive_emails: "Recevoir des e-mails de rapport d'analyse",
            email_toggle_aria: "Toggle les e-mails de rapport d'analyse",
            only_on_change: "Envoyez un e-mail uniquement lorsque l'état du risque change",
            only_on_change_aria: "Activer l'envoi d'e-mails uniquement en cas de changement de statut de risque",
            emails_enabled: "E-mails de rapport d'analyse activés",
            emails_disabled: "E-mails de rapport d'analyse désactivés",
            only_change_on: 'Envoi par e-mail uniquement sur le changement de risque',
            only_change_off: 'Envoi par e-mail à chaque analyse',
            email_failed: 'Échec de la mise à jour des paramètres de messagerie',
            email_settings_aria: 'Paramètres de notification par e-mail',
            loading_sr: 'Chargement des paramètres de surveillance',
        },
        entity_types: {
            user: 'Utilisateur',
            case: 'Cas',
            incident: 'Incident',
            assessment: 'Évaluation',
            vendor: 'Vendeur',
            consent: 'Consentement',
            policy: 'Politique',
            data_map: 'Carte des données',
        },
        actions: {
            create: 'Créer',
            update: 'Mise à jour',
            delete: 'Supprimer',
            view: 'Voir',
            export: 'Exporter',
            status_change: 'Changement de statut',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Tableau de bord des formulaires',
            total_templates: 'Total Modèles',
            installed: 'Installé',
            available: 'Disponible',
            by_market: 'Par Marché',
            back_dashboard: 'Retour à Tableau de bord',
            browse_templates: 'Parcourir les modèles',
            catalog_coverage: 'Couverture du catalogue',
            quick_start: 'Démarrage rapide',
            recent_instances: 'Instances récentes',
            total_instances: "Total d'instances",
            fetch_failed: 'Échec de fetch dashboard data. Veuillez réessayer.',
            no_instances: 'Aucun instances trouvé',
            form_metrics: 'Aperçu des métriques de formulaires',
            templates_total: 'modèles au total',
            instances_total: 'instances au total',
            installed_of_available: '{installed} sur {available} installés',
            templates_in_category: 'modèles dans {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Modèles de formulaires',
            new: 'Nouveau modèle',
            edit: 'Modifier Modèle',
            name: 'Modèle Nom',
            form_type: 'Formulaire Type',
            market: 'Marché',
            language: 'Langue',
            schema: 'Schéma du formulaire',
            preview: 'Aperçu',
            duplicate: 'Dupliquer Modèle',
            grid_view: 'Grille Voir',
            list_view: 'Liste Voir',
            fetch_failed: 'Échec de fetch templates. Veuillez réessayer.',
            filter_templates: 'Filtrer les modèles',
            version_history: 'Historique des versions',
            no_version_history: 'Aucun historique de versions — ceci est la version initiale.',
            form_id: 'ID du formulaire',
            source_instrument: 'Instrument source',
            issuer: 'Émetteur',
            all_frameworks: 'Tous les cadres',
            view_mode: "Mode d'affichage",
            classification: 'Classement',
        },
        catalog: {
            title: 'Catalogue de formulaires',
            install: 'Installer',
            uninstall: 'Désinstaller',
            open_form: 'Open form',
            install_all: 'Installer Tous',
            installed_tab: 'Installé',
            catalog_tab: 'Catalogue',
            search_placeholder: 'Rechercher des modèles...',
            no_templates: 'Aucun templates trouvé',
            template_count: '{count} modèles',
            all_markets: 'Tous les marchés',
            sections: 'Rubriques',
            all_categories: 'Toutes les catégories',
            category: 'Catégorie',
            category_install_failed: 'Échec de install category templates. Veuillez réessayer.',
            category_install_success: 'Modèles de catégorie installés.',
            fetch_failed: 'Échec de fetch templates. Veuillez réessayer.',
            fetch_installed_failed: 'Échec de fetch installed templates. Veuillez réessayer.',
            fields: 'Champs',
            install_failed: 'Échec de install template. Veuillez réessayer.',
            install_success: 'Modèle installed avec succès.',
            installing: 'Installation...',
            removing: 'Suppression...',
            uninstall_confirm: 'Êtes-vous sûr de vouloir désinstaller ce modèle ?',
            uninstall_failed: 'Échec de uninstall template. Veuillez réessayer.',
            uninstall_success: 'Modèle uninstalled avec succès.',
            installation_summary: "Résumé de l'installation",
            filter_catalog: 'Filtrer le catalogue',
            all_frameworks: 'Tous les cadres',
            templates_installed_count: '{count} modèle(s) installés avec succès',
            all_already_installed: 'Tous les modèles de cette catégorie sont déjà installés',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'ID du formulaire',
            issuer: 'Émetteur',
            catalog_tabs: 'Onglets du catalogue de formulaires',
        },
        filler: {
            back_templates: 'Retour à Modèles',
            no_sections: 'Aucun sections trouvé',
            draft_created: 'Brouillon créé.',
            draft_saved: 'Brouillon enregistré.',
            fetch_template_failed: 'Échec de fetch template. Veuillez réessayer.',
            save_draft: 'Enregistrer Brouillon',
            save_draft_failed: 'Échec de save draft. Veuillez réessayer.',
            saving: 'Enregistrement...',
            select_option: 'Sélectionnez option',
            submit: 'Soumettre',
            submit_failed: 'Échec de submit. Veuillez réessayer.',
            submitting: 'Soumission...',
            title: 'Assistant de formulaire',
            form_id_label: 'ID du formulaire',
            schema_label: 'Schéma',
            form_number_label: 'Form',
            framework_label: 'Cadre',
            issuer_label: 'Émetteur',
            source_label: 'Origine',
            market_label: 'Marché',
            section_of: 'Section {current} sur {total}',
            form_navigation: 'Navigation du formulaire',
            form_progress: 'Progression du formulaire : Section {current} sur {total}',
            removed_item: 'Élément {index} supprimé de {field}',
            added_item: 'Nouvel élément ajouté à {field}',
            go_to_previous: 'Aller à la section précédente, section {current} sur {total}',
            go_to_next: 'Aller à la section suivante, section {current} sur {total}',
            risk_matrix_placeholder: '1 à 5',
        },
        filing: {
            title: 'Statut de dépôt',
            advance: 'Mettre à jour le statut de dépôt...',
            not_started: 'Non commencé',
            drafting: 'Rédaction',
            internal_review: 'Revue interne',
            approved_for_submission: 'Approuvé pour soumission',
            submitted_to_authority: "Soumis à l'autorité",
            supplement_requested: 'Complément demandé',
            supplement_in_progress: 'Complément en cours',
            resubmitted: 'Resoumis',
            accepted: 'Accepté',
            rejected: 'Rejeté',
            closed: 'Clôturé',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Approuvé Par',
            form_answers: 'Réponses du formulaire',
            column_header: 'Form',
            no_answers: 'Aucun answers trouvé',
            view_outputs: 'Voir Outputs',
            approve: 'Approuver',
            approve_failed: 'Échec de approuver. Veuillez réessayer.',
            approving: 'Approbation...',
            detail_title: "Détail de l'instance",
            fetch_failed: 'Échec de fetch instances. Veuillez réessayer.',
            generate_failed: 'Échec de generate output. Veuillez réessayer.',
            generate_output: 'Générer Sortie',
            generating: 'Génération...',
            submitted_at: 'Soumis le',
            instance_metadata: "Métadonnées de l'instance du formulaire",
            filing_status_steps: 'Étapes du statut de classement',
            current_status: 'statut actuel',
            filing_status_update_failed: 'Échec de la mise à jour du statut du dépôt',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Retour à Instance',
            no_outputs_msg: 'Non outputs avoir été generated encore.',
            no_outputs_title: 'Aucun résultat',
            output_name: 'Sortie Nom',
            download: 'Télécharger',
            download_failed: 'Échec de télécharger. Veuillez réessayer.',
            fetch_failed: 'Échec de fetch outputs. Veuillez réessayer.',
            format: 'Formater',
            generated_at: 'Généré le',
            generated_by: 'Généré par',
            size: 'Taille',
            title: 'Résultats du formulaire',
            print: 'Imprimer',
            print_outputs: 'Imprimer les sorties du formulaire',
            printing: 'Impression des sorties du formulaire',
            pdf_preview: 'Aperçu PDF',
            downloading: 'Téléchargement de {name}',
            preview_in_new_window: 'Aperçu de {name} ({format}) dans une nouvelle fenêtre',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Portée et applicabilité',
            risk: 'Évaluation des risques et cartographie des contrôles',
            policy: 'Politiques de sécurité organisationnelle',
            localization: 'Localisation des données et présence locale',
            vendor: "Tiers et chaîne d'approvisionnement",
            authority: 'Traitement des demandes des autorités',
            incident: 'Réponse aux incidents et preuves',
            training: 'Formation et préparation',
        },
        market_finalization_domains: {
            data_law: 'Loi sur les Données — Gouvernance et Contrôles Opérationnels',
            pdpl_2025: 'PDPL 2025 — Gouvernance de la Vie Privée et Droits des Personnes Concernées',
            decree_356: 'Décret 356/2025 — Contrôles des Fournisseurs et Transfrontaliers',
            telecom: 'Loi Télécommunications — Confidentialité et Demandes des Autorités',
        },
        nav: {
            filing_case_links: 'Liens de dossier de dépôt',
            section_routing: 'Routage de section',
            submission_packages: 'Paquets de soumission',
        },
        filing_case_links: {
            title: 'Liens de dossier de dépôt',
            link_form: 'Lier le formulaire au dossier de dépôt',
            filing_case_type: 'Type de dossier de dépôt',
            form_instance: 'Instance de formulaire',
            select_form_instance: 'Sélectionner une instance de formulaire...',
            select_filing_case: 'Sélectionner un dossier de dépôt...',
            no_links_found: 'Aucun lien de dossier de dépôt trouvé.',
            linked: 'Lié',
            filing_case_id: 'ID du dossier de classement',
            link_created: 'Lien de dossier de classement créé',
            link_deleted: 'Lien de dossier de dépôt supprimé',
            aria_delete_link: 'Supprimer le lien {name}',
            case_type_dpia_dossier: 'Dossier AIPD',
            case_type_cross_border_filing: 'Dépôt transfrontalier',
            case_type_breach_notification: 'Notification de violation',
            no_instances_guidance: "Pas encore d'instances de formulaire. Créez-en une depuis les Modèles de formulaire.",
            no_filings_guidance: 'Pas encore de dossiers de dépôt. Créez-en un depuis les Évaluations.',
        },
        section_routing: {
            title: 'Routage de section',
            assign_section: 'Attribuer une section',
            department: 'Département',
            select_department: 'Sélectionner un département...',
            assignee: 'Assigné à',
            select_assignee: 'Sélectionner un assigné...',
            section: 'Section',
            select_section: 'Sélectionner une section...',
            select_form_instance: 'Sélectionner une instance de formulaire...',
            no_assignments_found: 'Aucune attribution de section trouvée.',
            select_instance_prompt: 'Sélectionnez une instance de formulaire pour voir les attributions de section.',
            complete_section: 'Terminer la section',
            status_pending: 'En attente',
            status_in_progress: 'En cours',
            status_completed: 'Terminé',
            status_reviewed: 'Révisé',
            form_instance_id: "ID d'instance du formulaire",
            assignment_created: 'Attribution de section créée',
            assignment_deleted: 'Attribution de section supprimée',
            status_updated: 'Statut de la section mis à jour',
            aria_delete_assignment: "Supprimer l'attribution {name}",
            deadline: 'Date limite',
            deadline_help: 'Facultatif. Les attributions dépassant cette date sont affichées comme en retard.',
            overdue: 'En retard',
            days_remaining: 'jours restants',
            approval_requested: 'Soumis pour examen',
            form_approved: 'Formulaire approuvé',
            form_rejected: 'Formulaire rejeté',
            no_departments_guidance: "Aucun département configuré. Configurez-les dans les Paramètres de l'organisation.",
            no_sections_guidance: 'Aucune section définie dans le modèle. Assurez-vous que le modèle de formulaire comporte des sections.',
            select_instance_first: "Sélectionnez d'abord une instance de formulaire pour charger les sections disponibles.",
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Paquets de soumission',
            assemble_package: 'Assembler le paquet de soumission',
            dossier: 'Dossier',
            select_dossier: 'Sélectionner un dossier...',
            package_label: 'Étiquette du paquet',
            cover_letter: 'Lettre de couverture',
            checklist: 'Liste de contrôle',
            evidence_index: 'Index des preuves',
            completeness: 'Complétude',
            incomplete_warning: 'Assemblage impossible : le formulaire comporte des sections incomplètes.',
            no_packages_found: 'Aucun paquet de soumission trouvé.',
            version: 'Version',
            package_assembled: 'Paquet de soumission assemblé',
            package_deleted: 'Paquet de soumission supprimé',
            aria_delete_package: 'Supprimer le paquet {name}',
            status_draft: 'Brouillon',
            status_complete: 'Terminé',
            status_submitted: 'Soumis',
            status_accepted: 'Accepté',
            status_rejected: 'Rejeté',
            package_updated: 'Paquet de soumission mis à jour',
            document_generated: 'Document généré',
            documents: 'Documents',
            generate: 'Générer',
            preview: 'Aperçu',
            download: 'Télécharger',
            download_all: 'Tout télécharger',
            no_dossiers_guidance: 'Pas encore de dossiers. Créez-en un depuis les Évaluations.',
            package_plan_help: 'Inclure dans le plan du paquet (génération automatique de documents disponible) :',
            cover_letter_help: "Lettre d'accompagnement officielle adressée à l'autorité destinataire",
            checklist_help: 'Liste de vérification de conformité vérifiant que toutes les exigences de soumission sont remplies',
            evidence_index_help: 'Index des documents justificatifs référencés dans la soumission',
            completeness_skipped: 'Vérification de complétude ignorée — aucune instance de formulaire liée à ce dossier.',
            completeness_unavailable: 'Vérification de complétude indisponible — assemblage du paquet en cours.',
            duplicate_package_error: 'Un paquet de soumission existe déjà pour ce dossier.',
            doc_cover_letter: 'Lettre de couverture',
            doc_checklist: 'Liste de vérification',
            doc_evidence_index: 'Index des preuves',
            export_csv: 'Exporter CSV',
            export_success: 'Liens des dossiers de dépôt exportés avec succès',
            no_assignments_guidance: "Aucune attribution de section pour le moment. Attribuez des sections aux départements et aux membres de l'équipe pour commencer le routage.",
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Sélectionner un modèle',
            hint: 'Choisissez un modèle de formulaire réglementaire ou commencez avec un formulaire vierge.',
            market_filter: 'Filtrer par marché',
            framework_filter: 'Filtrer par cadre',
            all_markets: 'Tous les marchés',
            all_frameworks: 'Tous les cadres',
            search_placeholder: 'Rechercher des modèles...',
            blank_form: 'Formulaire vierge / générique',
            blank_form_desc: 'Créer sans modèle — remplir les champs manuellement.',
            no_templates: 'Aucun modèle trouvé pour ce filtre.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Sélectionner',
            link_error: 'Échec de la liaison du modèle — vous pouvez lier un modèle ultérieurement depuis la vue Édition.',
            internal_badge: 'Modèle interne',
            legacy_badge: 'Obsolète',
            draft_badge: 'Brouillon',
            upcoming_badge: 'À venir',
            hidden_badge: 'Masqué',
            show_hidden_toggle: 'Afficher les modèles masqués / obsolètes',
            show_hidden_toggle_hint: 'Vue administrateur : inclut les modèles marqués internal_only, hidden, legacy ou draft.',
        },
        cover_letter_modal: {
            title: "Lettre d'accompagnement : aperçu et édition",
            iframe_title: "Aperçu de la lettre d'accompagnement",
            stage_label: 'Étape',
            stage_initial: 'Soumission initiale',
            stage_supplement: 'Réponse complémentaire',
            stage_resubmission: 'Nouvelle soumission',
            subject_label: 'Objet',
            body_label: 'Paragraphe principal',
            notes_label: 'Notes supplémentaires',
            refresh_preview: "Actualiser l'aperçu",
            confirm: 'Confirmer et enregistrer',
            regenerate: 'Régénérer',
            snapshot_saved: "Lettre d'accompagnement enregistrée",
        },
        related_panel: {
            title: 'Formulaires réglementaires associés',
            open_form: 'Remplir le formulaire',
            no_forms: 'Aucun formulaire associé trouvé.',
            hint_vn: "Formulaires réglementaires spécifiques au VN disponibles — sélectionnez un formulaire pour ouvrir l'assistant de conformité.",
            internal_badge: 'Interne',
            official_badge: 'Officiel',
            loading: 'Chargement des formulaires associés...',
            error: 'Impossible de charger les formulaires associés.',
            form_id: 'ID du formulaire',
            form_name: 'Nom du formulaire',
            framework: 'Cadre réglementaire',
        },
        view_form: 'Voir le formulaire',
        edit_form: 'Modifier le formulaire',
        save_draft: 'Enregistrer le brouillon',
        module_settings: 'Paramètres du module',
        validation_errors: 'Veuillez corriger les erreurs de validation',
        custom_name_help: 'Optionnel — par défaut, le nom du modèle si laissé vide',
        select_placeholder: 'Sélectionner...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Précédent',
        next_section: 'Suivant',
        loading: 'Chargement...',
        array_add: 'Ajouter',
        array_remove: 'Supprimer',
        array_item_label: 'Élément {index} de {label}',
        array_add_item: 'Ajouter un élément à {label}',
        array_remove_item: 'Supprimer l’élément {index} de {label}',
        legacy_banner: 'Cet enregistrement a été créé avant les formulaires unifiés. Certaines données peuvent différer entre l’entité et l’instance de formulaire.',
        field_required: '{label} est requis',
        section_announcement: 'Section {current} de {total} : {title}',
        sections_nav_label: 'Sections du formulaire',
        no_sections: 'Aucune section de modèle disponible.',
        section_label_tpl: 'Section {index} : {title}',
        field_shown_announcement: '{field} est maintenant affiché.',
        field_required_announcement: '{field} est maintenant obligatoire.',
        submit_blocked_hint_one: '1 champ obligatoire nécessite encore une réponse.',
        submit_blocked_hint_many: '{count} champs obligatoires nécessitent encore une réponse.',
        module_settings_auto_applied: 'Les paramètres spécifiques au module sont appliqués automatiquement.',
        operation_failed: "L'opération a échoué",
        failed_to_load_form: 'Impossible de charger le formulaire',
        failed_to_save_form: "Impossible d'enregistrer le formulaire",
        failed_to_submit_form: "Impossible d'envoyer le formulaire",
        failed_to_load_template: 'Impossible de charger le modèle',
        failed_to_save_draft: "Impossible d'enregistrer le brouillon",
        failed_to_create: 'Impossible de créer',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Catalogue de packs',
        install: 'Installer le pack',
        uninstall: 'Désinstaller le pack',
        install_batch: 'Installer Cluster',
        installed_tab: 'Installé',
        catalog_tab: 'Catalogue',
        pack_details: 'Détails du pack',
        frameworks: 'Cadres',
        requirements: 'Exigences',
        artifacts: 'Artefacts',
        language_pack: 'Pack linguistique',
        dependencies: 'Dépendances',
        no_packs: 'Aucun packs trouvé',
    },
    platform_pages: {
        users: {
            title: 'Utilisateurs',
            new: 'Nouveau utilisateur',
            invite: 'Inviter Utilisateur',
            edit: 'Modifier Utilisateur',
            name: 'Nom',
            email: 'E-mail',
            role: 'Rôle',
            status: 'Statut',
            identity_type: 'Identité Type',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Dernière connexion',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Désactiver',
            reactivate: 'Réactiver',
            unlock: 'Déverrouiller',
            reset_password: 'Réinitialiser Mot de passe',
            remove: 'Supprimer',
            remove_confirm: "Supprimer \"{name}\" ? Cette action est définitive et l'utilisateur perdra tout accès. L'enregistrement est conservé à des fins d'audit.",
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Rôles',
            new: 'Nouveau rôle',
            edit: 'Modifier Rôle',
            name: 'Rôle Nom',
            description: 'Descriptif',
            permissions: 'Autorisations',
            users_count: 'Utilisateurs',
            detail: 'Détail du rôle',
            all_tiers: 'Tous les niveaux',
            tier: 'Niveau',
            type_system: 'Système',
            type_custom: 'Personnalisé',
            role_name_placeholder: 'ex. Responsable conformité régional',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Mon Profil',
            name: 'Nom',
            display_name: "Nom d'affichage",
            email: 'E-mail',
            phone: 'Téléphone',
            department: 'Département',
            job_title: 'Intitulé du poste',
            language: 'Langue',
            role: 'Rôle',
            persona: 'Persona détectée',
            last_login: 'Dernière connexion',
            save: 'Enregistrer les modifications',
            save_success: 'Profil mis à jour avec succès',
            module_tips_section: 'Conseils de module',
            tips_toggle: 'Afficher les conseils réglementaires dans les modules',
            dismissed_tips: 'Conseils ignorés',
            reset_dismissed: 'Réinitialiser tous les conseils ignorés',
            notification_prefs: 'Préférences de notification',
            notification_prefs_link: 'Gérer les préférences de notification →',
            change_password: 'Changement Mot de passe',
            current_password: 'Actuel Mot de passe',
            new_password: 'Nouveau mot de passe',
            confirm_password: 'Confirmer Mot de passe',
            accessibility: 'Accessibilite',
            high_contrast: 'Mode contraste eleve',
            high_contrast_desc: 'Augmenter le contraste pour une meilleure lisibilite. Conforme WCAG 2.1 AAA.',
            passwords_no_match: 'Les mots de passe ne correspondent pas',
            password_changed_logout: 'Mot de passe modifié. Vous serez déconnecté.',
            failed_update: 'Échec de la mise à jour du profil',
            failed_change_password: 'Échec du changement de mot de passe',
            none: 'Aucun',
            never: 'Jamais',
            password_requirements: 'Min. 12 car., majuscule, minuscule, chiffre, caractère spécial',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'Paramètres MFA',
            mfa_enable: 'Activer la MFA',
            mfa_disable: 'Désactiver la MFA',
            mfa_setup: "Scannez ce code QR avec votre appli d'authentification",
            sso_title: 'Configuration SSO',
            sso_provider: 'Fournisseur',
            sso_client_id: 'ID client',
            sso_redirect: 'URI de redirection',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API Clés',
            api_keys_new: 'Nouveau api clé',
            api_keys_name: 'Clé Nom',
            api_keys_last_used: 'Dernière utilisation',
            api_keys_expires: 'Expirer',
            api_keys_revoke: 'Révoquer',
            service_accounts_title: 'Comptes de service',
            ip_allowlist_title: "Liste blanche d'IP",
            ip_add: 'Ajouter ip adresse',
            ip_address: 'IP Adresse',
            ip_description: 'Descriptif',
            delegations_title: 'Délégations',
            delegations_new: 'Nouveau Delegation',
            delegations_from: 'De Utilisateur',
            delegations_to: 'À Utilisateur',
            delegations_expires: 'Expirer',
            break_glass_title: 'Break-Glass Accès',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Demande Accès',
            break_glass_reason: 'Reason pour Accès',
            break_glass_duration: 'Durée',
            session_settings_title: 'Session Paramètres',
            eviction_policy_section: "Politique d'éviction",
            session_timeout: "Session Délai d'attente (minutes)",
            session_max_concurrent: 'Sessions simultanées max.',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Sessions actives',
            active_sessions_device: 'Appareil',
            active_sessions_ip: 'IP Adresse',
            active_sessions_started: 'Démarré',
            active_sessions_revoke: 'Révoquer Session',
            active_sessions_confirm_terminate: 'Êtes-vous sûr de vouloir mettre fin à cette session ?',
            active_sessions_confirm_terminate_current: 'Ceci est votre session en cours. En y mettant fin, vous vous déconnecterez immédiatement. Continuer?',
            active_sessions_confirm_terminate_all: 'Êtes-vous sûr de vouloir mettre fin à toutes les autres sessions ? Seule votre session en cours restera active.',
            active_sessions_terminated: "La session s'est terminée avec succès.",
            active_sessions_all_terminated: 'Toutes les autres sessions ont été interrompues.',
            active_sessions_terminate_all: 'Terminer toutes les autres sessions',
            active_sessions_last_activity: 'Dernière activité',
            active_sessions_current: 'Session en cours',
            active_sessions_revoke_selected: 'Révoquer la sélection',
            active_sessions_confirm_terminate_selected: 'Êtes-vous sûr de vouloir terminer les sessions sélectionnées ?',
            active_sessions_selected_terminated: 'Sessions sélectionnées terminées avec succès.',
            ip_allowlist_desc: "Gérez les adresses IP autorisées pour l'accès API.",
            ip_no_entries: 'Aucune adresse IP configurée.',
            session_settings_desc: "Configurez le délai d'expiration de la session et les limites des sessions simultanées.",
            session_settings_save_policy: 'Enregistrer la politique de session',
            session_settings_saved: 'Paramètres de session mis à jour avec succès.',
            session_settings_idle_timeout: "Délai d'inactivité (minutes)",
            session_settings_absolute_timeout: "Délai d'attente absolu (minutes)",
            session_settings_help_idle: "La session expirera après cette période d'inactivité.",
            session_settings_help_absolute: "La session expirera après cette durée totale, quelle que soit l'activité.",
            session_settings_help_max: 'Nombre maximum de sessions simultanées par utilisateur.',
            sso_note: 'Les modifications apportées à la configuration SSO peuvent nécessiter une nouvelle authentification des utilisateurs.',
            service_accounts_warning: "Cette clé API ne sera affichée qu'une seule fois. Copiez-le maintenant et conservez-le en toute sécurité.",
            service_accounts_new: 'Nouveau compte de service',
            service_accounts_name: 'Nom du compte',
            service_accounts_role: 'Rôle',
            service_accounts_created: 'Créé',
            service_accounts_desc: 'Gérez les comptes de service machine à machine pour les intégrations API.',
            break_glass_warning: "L’accès aux bris de glace est audité et enregistré. À utiliser uniquement en cas d'urgence réelle.",
            break_glass_min_chars: '{count} / {min} caractères minimum',
            break_glass_permission_count: '{count} autorisation(s) sélectionnée(s)',
            break_glass_submitted: "Demande de bris de glace soumise. En attente d'approbation.",
            break_glass_approved: 'Demande de bris de glace approuvée.',
            break_glass_denied: 'Demande de bris de vitre refusée.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: "Confirmer l'activation",
            break_glass_pending: 'Demandes en attente',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Temps restant',
            break_glass_expired: 'Expiré',
            break_glass_approved_denied_by: 'Approuvé/Refusé par',
            delegations_desc: "Gérez les délégations d'autorisations temporaires entre les utilisateurs.",
            delegations_my: 'Mes délégations',
            delegations_to_me: "M'a délégué",
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Toutes les autorisations',
            delegations_scope_specific: 'Autorisations spécifiques',
            delegations_created: 'Délégation créée avec succès.',
            delegations_revoked: 'Délégation révoquée.',
            delegations_confirm_revoke: 'Etes-vous sûr de vouloir révoquer cette délégation ?',
            delegations_confirm_revoke_title: 'Confirmer la révocation',
            delegations_permission_count: '{count} autorisation(s)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Statut',
            api_keys_prefix: 'Préfixe de clé',
            api_keys_permissions_count: 'Autorisations',
            api_keys_ip_count: 'Restrictions IP',
            api_keys_created: 'Créé',
            api_keys_confirm_revoke_title: 'Confirmer la révocation',
            api_keys_confirm_revoke_message: 'Êtes-vous sûr de vouloir révoquer la clé API "{name}" ? Cette action ne peut pas être annulée.',
            api_keys_confirm_rotate_title: 'Confirmer la rotation',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'Clé API créée avec succès.',
            api_keys_revoked_success: 'La clé API "{name}" a été révoquée.',
            api_keys_rotated_success: 'La clé API "{name}" a été pivotée. Copiez la nouvelle clé ci-dessous.',
            api_keys_load_error: 'Échec du chargement des clés API.',
            api_keys_create_error: 'Échec de la création de la clé API.',
            api_keys_revoke_error: 'Échec de la révocation de la clé API.',
            api_keys_rotate_error: 'Échec de la rotation de la clé API.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minutes',
            break_glass_duration_30: '30 minutes',
            break_glass_duration_60: '60 minutes (1h)',
            break_glass_duration_120: '120 minutes (2h)',
            break_glass_duration_240: '240 minutes (4h)',
            sod_rules_title: 'Règles de séparation des tâches',
            sod_violations_report: "Rapport d'infractions",
            sod_create_rule: 'Créer une règle',
            sod_col_name: 'Nom',
            sod_col_description: 'Descriptif',
            sod_col_conflict_count: 'Nombre de conflits',
            sod_col_severity: 'Gravité',
            sod_col_status: 'Statut',
            sod_col_actions: 'Actions',
            sod_no_rules: 'Aucune règle SoD trouvée',
            sod_severity_warning: 'Avertissement',
            sod_severity_blocking: 'Blocage',
            sod_active: 'Actif',
            sod_inactive: 'Inactif',
            sod_edit: 'Modifier',
            sod_exceptions: 'Exceptions',
            sod_deactivate: 'Désactiver',
            sod_confirm_deactivate_title: 'Confirmer la désactivation',
            sod_confirm_deactivate_message: 'Désactiver la règle "{name}" ? Il ne signalera plus les violations.',
            sod_deactivate_error: 'Échec de la désactivation de la règle',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: "Revues d'accès",
            campaigns: 'Campagnes de révision',
            new_campaign: 'Nouveau Campaign',
            campaign_name: 'Nom de campagne',
            scope: 'Portée',
            reviewers: 'Réviseurs',
            due_date: "Date d'échéance",
            progress: 'Progression',
            my_reviews: 'Mes révisions',
            approve: 'Approuver',
            revoke: 'Révoquer',
            analytics: 'Rôle Analyses',
            analytics_title: 'Rôle Analyses',
            permission_usage: 'Permission Utilisation',
            role_coverage: 'Couverture des rôles',
            campaign_detail: 'Détails de la campagne',
            campaign_description: 'Descriptif',
            campaign_status: 'Statut',
            campaign_deadline: 'Date limite',
            campaign_scope_type: 'Type de portée',
            campaign_completion: 'Achèvement',
            campaign_total_items: 'Total des articles',
            campaign_pending_items: 'En attente',
            campaign_confirmed_items: 'Confirmé',
            campaign_revoked_items: 'Révoqué',
            campaign_escalated_items: 'Escaladé',
            campaign_launch: 'Campagne de lancement',
            campaign_cancel: 'Annuler la campagne',
            campaign_confirm_launch: 'Êtes-vous sûr de vouloir lancer cette campagne ?',
            campaign_confirm_cancel: 'Êtes-vous sûr de vouloir annuler cette campagne ?',
            campaign_launched: 'Campagne lancée avec succès.',
            campaign_cancelled: 'Campagne annulée.',
            review_items: 'Réviser les éléments',
            review_item_user: 'Utilisateur',
            review_item_role: 'Rôle',
            review_item_reviewer: 'Réviseur',
            review_item_decision: 'Décision',
            review_item_justify: 'Justification',
            review_item_confirm: 'Confirmer',
            review_item_revoke: 'Révoquer',
            review_item_escalate: 'Escalader',
            all_decisions: 'Toutes les décisions',
            bulk_action: 'Action groupée',
            bulk_confirm: 'Confirmation groupée',
            bulk_revoke: 'Révocation groupée',
            selected_count: '{count} sélectionné',
            justify_required: 'Une justification est requise',
            scope_all_users: 'Tous les utilisateurs',
            scope_role_owner: 'Propriétaire du rôle',
            scope_department: 'Département',
            back_to_campaigns: 'Retour aux campagnes',
            recommendations: 'Recommandations',
            refreshing: 'Rafraîchissant...',
            decide: 'Décider',
            decision_by: 'Décision par',
            loading_items: 'Chargement des éléments...',
            loading_review_items: 'Chargement des éléments de révision...',
            campaign_not_found: 'Campagne introuvable',
            loading_campaign: 'Chargement des détails de la campagne...',
            campaign_completed: 'Campagne terminée avec succès.',
            confirm_cancel_title: 'Confirmer Annuler',
            justify_bulk_confirm: 'Justification de la confirmation groupée :',
            justify_bulk_revoke: 'Justification de la révocation groupée :',
            items_selected: '{count} élément(s) sélectionné(s)',
            justify_bulk_required: 'Une justification est requise pour les actions groupées',
            create_campaign_title: 'Créer une campagne de révision des accès',
            name_required: 'Le nom est requis',
            scope_type_required: 'Le type de portée est requis',
            reviewer_strategy_required: 'Une stratégie de réviseur est requise',
            deadline_required: 'Un délai est requis',
            reviewer_id_required: 'L’ID du réviseur est requis pour une stratégie utilisateur spécifique',
            invalid_json: 'JSON non valide',
            scope_filter_label: 'Filtre de portée (JSON)',
            reviewer_strategy_label: 'Stratégie des évaluateurs',
            reviewer_id_label: 'ID du réviseur',
            deadline_label: 'Date limite',
            create_campaign_btn: 'Créer une campagne',
            scope_type_label: 'Type de portée',
            scope_by_role: 'Par rôle',
            scope_by_department: 'Par département',
            scope_custom: 'Personnalisé',
            reviewer_manager: 'Gestionnaire',
            reviewer_specific_user: 'Utilisateur spécifique',
            enter_json_filter: 'Entrez le filtre JSON',
            hint_label: 'Indice :',
            role_usage: 'Utilisation des rôles',
            over_privileged_users: 'Utilisateurs trop privilégiés',
            role_name_col: 'Nom du rôle',
            assigned_users: 'Utilisateurs attribués',
            active_users_30d: 'Utilisateurs actifs (30j)',
            total_perms: 'Autorisations totales',
            used_perms: 'Perms utilisées',
            unused_perms: 'Permissions inutilisées',
            no_data_available: 'Aucune donnée disponible',
            roles_col: 'Rôles',
            total_col: 'Total',
            used_col: 'Utilisé',
            unused_pct: '% inutilisé',
            current_count: 'Nombre actuel',
            recommended_count: 'Nombre recommandé',
            rationale: 'Justification',
            loading_analytics: 'Chargement des analyses...',
            loading_role_analytics: "Chargement des données d'analyse des rôles...",
            computed_at: 'Calculé à :',
            form_errors: 'Le formulaire contient des {count} erreur(s). Merci de les corriger.',
            scope_filter_invalid_json: 'Le filtre de portée contient des JSON non valides.',
            scope_hint_all_users: "Aucun filtre n'est nécessaire : tous les utilisateurs seront inclus.",
            review_type_label: 'Type de révision',
            review_type_required: 'Le type de révision est requis',
            reviewer_ids_label: 'IDs des réviseurs',
            reviewer_ids_hint: "Saisissez des IDs d'utilisateur séparés par des virgules",
            reviewer_ids_required: 'Au moins un ID de réviseur est requis',
            start_date_label: 'Date de début',
            start_date_required: 'La date de début est requise',
            end_date_label: 'Date de fin',
            end_date_required: 'La date de fin est requise',
            review_type_user_access: 'Accès utilisateur',
            review_type_role_permissions: 'Permissions de rôle',
            review_type_service_accounts: 'Comptes de service',
            review_type_privileged_access: 'Accès privilégié',
            review_type_application_access: 'Accès application',
            auto_revoke_label: 'Révocation automatique en cas de refus',
            campaigns_title: "Campagnes d'examen d'accès",
            create_campaign_label: '+ Créer une campagne',
            no_campaigns_found: 'Aucune campagne trouvée',
            loading_campaigns: 'Chargement des campagnes...',
            no_reviews_assigned: 'Aucun avis ne vous a été attribué',
            loading_reviews: 'Chargement des avis...',
            review_decision_title: 'Décision de révision',
            justification_label: 'Justification',
            announce_loaded_review_items: "Éléments d'évaluation {count} chargés",
            announce_loaded_campaigns: '{count} campagnes chargées',
            announce_decision_submitted: 'Décision "{decision}" soumise pour examen',
            announce_decision_submitted_for: 'Décision "{decision}" soumise pour {name}',
            announce_bulk_decision: '{decision} en bloc appliqué aux éléments {count}',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: "Calendrier d'attestation",
            new: 'Nouveau planification',
            edit: 'Modifier Planification',
            create_title: "Créer un calendrier d'attestation",
            edit_title: "Modifier le calendrier d'attestation",
            schedule_history: 'Historique du calendrier',
            next_run_date: 'Date de la prochaine exécution',
            schedule_name: 'Planification Nom',
            frequency: 'Fréquence',
            next_run: 'Suivant Exécuter',
            last_run: 'Dernier Exécuter',
            history: "Historique d'attestation",
            description: 'Descriptif',
            scope: 'Portée',
            status: 'Statut',
            created_at: 'Créé',
            updated_at: 'Mis à jour',
            trigger_now: 'Déclenchez maintenant',
            back_to_schedules: 'Retour aux horaires',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Planification déclenchée avec succès.',
            deleted: 'Calendrier supprimé.',
            saved: 'Planification enregistrée avec succès.',
            campaigns_generated: 'Campagnes générées',
            run_date: "Date d'exécution",
            run_status: "Statut d'exécution",
            create_schedule: 'Créer un calendrier',
            view_history: "Afficher l'historique",
            deactivate: 'Désactiver',
            overdue: 'OVERDUE',
            overdue_sr: 'Le calendrier est en retard',
            confirm_trigger_title: "Confirmer l'action",
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Confirmer la désactivation',
            confirm_deactivate_message: 'Etes-vous sûr de vouloir désactiver ce planning ?',
            triggered_success: 'Planification déclenchée avec succès.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Calendrier désactivé.',
            name_label: 'Nom *',
            frequency_label: 'Fréquence *',
            scope_type_label: 'Type de portée *',
            scope_filter_label: 'Filtre de portée (JSON)',
            reviewer_strategy_label: 'Stratégie des évaluateurs *',
            reviewer_id_label: 'ID du réviseur *',
            frequency_quarterly: 'Trimestriel',
            frequency_semi_annual: 'Semestriel',
            frequency_annual: 'Annuel',
            scope_all_users: 'Tous les utilisateurs',
            scope_by_role: 'Par rôle',
            scope_by_department: 'Par département',
            scope_custom: 'Personnalisé',
            reviewer_manager: 'Gestionnaire',
            reviewer_role_owner: 'Propriétaire du rôle',
            reviewer_specific_user: 'Utilisateur spécifique',
            computed_next_run_date: 'Date de prochaine exécution calculée',
            current_scheduled_date: 'Date prévue actuelle',
            recomputed_frequency_changed: '(recalculé -- fréquence modifiée)',
            estimated_days_hint: "Estimation basée sur {days} jours à partir d'aujourd'hui",
            validation_name_required: 'Le nom est requis',
            validation_frequency_required: 'La fréquence est requise',
            validation_scope_type_required: 'Le type de portée est requis',
            validation_reviewer_strategy_required: 'Une stratégie de réviseur est requise',
            validation_reviewer_id_required: 'L’ID du réviseur est requis pour une stratégie utilisateur spécifique',
            validation_invalid_json: 'JSON non valide',
            validation_scope_filter_invalid_json: 'Le filtre de portée contient des JSON non valides.',
            form_errors: 'Le formulaire contient des {count} erreur(s). Merci de les corriger.',
            create_schedule_btn: 'Créer un calendrier',
            schedule_history_title: 'Historique des programmes',
            campaign_name_col: 'Nom de la campagne',
            items_col: 'Articles',
            no_campaigns_yet: "Aucune campagne générée pour l'instant",
            loading_history: "Chargement de l'historique...",
            loading_schedule: 'Calendrier de chargement...',
            total_campaigns: 'Nombre total de campagnes : {count}',
            results_per_page: 'Résultats par page',
            page_size_option: '{size} / page',
        },
        sod: {
            title: 'Règles de séparation des tâches',
            new: 'Nouveau Rule',
            edit: 'Modifier la règle',
            create_title: 'Créer une règle SoD',
            edit_title: 'Modifier la règle SoD',
            rule_name: 'Nom de la règle',
            conflicting_permissions: 'Permissions en conflit',
            permission_a: 'Permission Un',
            permission_b: 'Autorisation B',
            violations: 'Violations SoD',
            violation_user: 'Utilisateur',
            violation_rule: 'Règle violée',
            violation_date: 'Détecté le',
            exceptions: 'Exceptions SoD',
            exception_reason: "Motif d'exception",
            exception_approver: 'Approuvé Par',
            exception_status: "Statut d'exception",
            has_exception: 'A une exception',
            no_exception: 'Aucune exception',
            grant_exception: 'Accorder une exception',
            granting: 'Accorder...',
            grant_new_exception: 'Accorder une nouvelle exception',
            exceptions_for: 'Exceptions pour : {name}',
            back_to_rules: 'Retour aux règles',
            no_violations: 'Aucune violation trouvée',
            no_exceptions_found: 'Aucune exception trouvée pour cette règle',
            all_exception_statuses: "Tous les statuts d'exception",
            user_id_label: 'Identifiant utilisateur',
            justification_label: 'Justification',
            compensating_controls_label: 'Contrôles compensatoires',
            expires_at_label: 'Expire à',
            granting_exception_for: 'Accorder une exception pour la règle : {name}',
            severity: 'Gravité',
            severity_warning: 'Avertissement',
            severity_blocking: 'Blocage',
            violation_count: '{count} violation(s)',
            exception_count: '{count} exception(s)',
            rule_description: 'Descriptif',
            confirm_delete: 'Êtes-vous sûr de vouloir supprimer cette règle ?',
            deleted: 'Règle supprimée.',
            framework_code: 'Cadre reglementaire',
            name_label: 'Nom *',
            severity_label: 'Gravité *',
            conflict_pairs_legend: "Paires d'autorisations en conflit",
            conflict_pairs_hint: 'Chaque paire définit deux autorisations qui ne doivent pas être détenues par le même utilisateur.',
            placeholder_permission_a: '-- Autorisation A --',
            placeholder_permission_b: '-- Autorisation B --',
            permission_a_for_pair: 'Autorisation A pour la paire {index}',
            permission_b_for_pair: 'Autorisation B pour la paire {index}',
            conflict_pair_label: 'Paire de conflits {index}',
            remove_conflict_pair: 'Supprimer la paire en conflit {index}',
            add_pair: '+ Ajouter une paire',
            placeholder_name: "par ex. Séparation de l'approbation du paiement",
            create_rule: 'Créer une règle',
            loading_rule: 'Chargement de la règle SoD...',
            failed_load_rule: 'Échec du chargement de la règle.',
            failed_create_rule: 'Échec de la création de la règle SoD.',
            failed_update_rule: 'Échec de la mise à jour de la règle SoD.',
            validation_name_required: 'Le nom est requis.',
            validation_pair_required: "Au moins une paire d'autorisations de conflit est requise.",
            validation_pair_both_required: 'Paire {index} : les deux autorisations doivent être sélectionnées.',
            validation_pair_must_differ: "Paire {index} : l'autorisation A et l'autorisation B doivent être différentes.",
            added_conflict_pair: "Ajout d'une paire de conflits {index}",
            removed_conflict_pair: 'Paire de conflits supprimée {index}',
            announce_loaded_violations: 'Violations de {count} chargées',
            announce_loaded_exceptions: '{count} exceptions chargées',
            announce_loaded_rules: '{count} règles SoD chargées',
            announce_loading_exceptions: 'Chargement des exceptions…',
            regulatory_info: {
                sod_context: 'Les regles SoD imposent la separation reglementaire des roles exigee par la PDPL. Les violations liees a un framework_code peuvent etre incluses dans les paquets de preuves (UC-VN-09).',
                violation_export_context: 'Les exports de violations SoD incluent framework_code, roles en conflit, severite, createur et date de detection pour les paquets de preuves (P-VN-10).',
                exception_context: 'Les exceptions SoD produisent des preuves: approbateur, justification, controles compensatoires et date d\\u0027expiration. Les exceptions expirees restent visibles (UC-VN-09 Etape 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'Intégration CMP',
            status: 'Connexion Statut',
            endpoint: 'Point de terminaison CMP',
            last_sync: 'Dernier Synchroniser',
            configure: 'Configurer',
        },
        purpose_mapping: {
            title: 'Finalité Correspondance',
            cmp_purpose: 'Finalité CMP',
            grc_purpose: 'Finalité GRC',
            mapping_status: 'Correspondance Statut',
            auto_map: 'Correspondance auto.',
            new_mapping: 'Nouveau correspondance',
        },
        webhook: {
            title: 'Configuration du webhook',
            webhook_url: 'URL du webhook',
            secret: 'Secret du webhook',
            events: "Types d'événements",
            test_webhook: 'Tester Webhook',
        },
        bulk_sync: {
            title: 'Synchro. en masse',
            sync_now: 'Synchroniser Maintenant',
            last_run: 'Dernier Exécuter',
            records_synced: 'Enregistrements Synchronisé',
            sync_history: 'Synchroniser Historique',
            syncing: 'Synchronisation...',
        },
        sync_settings: {
            sync_failures: 'Échecs de synchronisation',
            sync_secret_warning: 'Synchroniser Secret (save ce maintenant — il va non être shown again):',
            push_description: 'Configurez GRC Suite pour envoyer les mises à jour de statut des dossiers à AesirX CMP.',
            technical_description: "Cette configuration contrôle la manière dont GRC Suite transmet les mises à jour de l'état des dossiers à AesirX CMP.",
            direction_label: 'Sens',
            direction_value: 'GRC Suite → AesirX CMP (sortant)',
            auth_label: 'Authentification',
            auth_value: "Signature HMAC-SHA256 dans l'en-tête X-GRC-Signature",
            retry_label: 'Nouvelle tentative Politique',
            retry_value: '3 tentatives avec recul exponentiel (30s, 2min, 10min)',
            inbound_label: 'Config. entrante',
            inbound_value: 'Le webhook entrant (CMP → GRC) est configuré dans',
            cmp_webhook_settings: 'CMP Webhook Paramètres',
            page_title: 'Paramètres de Synchronisation CMP',
            outbound_sync_configured: 'Synchronisation sortante configurée.',
            configure_sync_failed: 'Échec de la configuration de la synchronisation',
            configuration_updated: 'Configuration mise à jour.',
            update_configuration_failed: 'Échec de la mise à jour de la configuration',
            connection_test_failed: 'Échec du test de connexion',
            test_result_message: 'Test envoyé à {endpoint}. Statut : {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Nouveau → sous examiner (assigned)',
                under_review_to_identity_verification: 'Sous Examiner → Identité Vérification',
                under_review_to_in_progress: 'Sous Examiner → En cours',
                in_progress_to_completed: 'En cours → Terminé',
                in_progress_to_denied: 'En cours → Refusé',
                any_to_closed: 'Tout → Fermé',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'Nouveau Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Événements',
            secret: 'Clé secrète',
            active: 'Actif',
            test: 'Tester',
            create_failed: 'Échec de create webhook. Veuillez réessayer.',
            create_success: 'Webhook créé avec succès',
            deactivate_failed: 'Échec de deactivate webhook. Veuillez réessayer.',
            deactivate_success: 'Webhook désactivé.',
            fetch_failed: 'Échec de fetch webhooks. Veuillez réessayer.',
            send_test_failed: 'Échec de send test. Veuillez réessayer.',
            test_failed: 'Webhook test échoué.',
            test_success: 'Test du webhook réussi.',
            edit_title: 'Modifier le webhook',
            announce_loaded: 'Webhooks chargés',
            update_success: 'Webhook mis à jour avec succès.',
            update_failed: 'Échec de la mise à jour du webhook.',
            activate_success: 'Webhook activé.',
            activate_failed: "Échec de l'activation du webhook.",
        },
        domains: {
            title: 'Domaines',
            new: 'Nouveau domaine',
            domain_name: 'Domaine Nom',
            verified: 'Vérifié',
            verify: 'Vérifier',
            add_failed: 'Échec de add domain. Veuillez réessayer.',
            add_success: 'Domaine added avec succès',
            domain_placeholder: 'p.ex. grc.example.com',
            fetch_failed: 'Échec de fetch domains. Veuillez réessayer.',
            remove_failed: 'Échec de remove domain. Veuillez réessayer.',
            remove_success: 'Domaine removed avec succès',
            txt_record: 'Enregistrement TXT',
            verify_dns_failed: 'La vérification DNS a échoué.',
            verify_failed: 'Échec de verify domain. Veuillez réessayer.',
            verify_success: 'Domaine vérifié avec succès',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: "Planifications d'export",
            new: 'Nouveau planification',
            schedule_name: 'Nom',
            frequency: 'Fréquence',
            format: 'Formater',
            last_run: 'Dernier Exécuter',
            next_run: 'Suivant Exécuter',
            create_failed: 'Échec de create schedule. Veuillez réessayer.',
            create_success: 'Planification créé avec succès',
            day_of_month: 'Jour du mois',
            day_of_week: 'Jour de la semaine',
            deactivate_failed: 'Échec de désactiver planifier. Veuillez réessayer.',
            deactivate_success: 'Planification désactivée.',
            fetch_failed: 'Échec de fetch schedules. Veuillez réessayer.',
            freq_daily: 'Quotidien',
            freq_monthly: 'Mensuel',
            freq_weekly: 'Hebdomadaire',
            recipients: 'Destinataires',
            time_utc: 'Heure (UTC)',
            trigger_failed: 'Échec de trigger export. Veuillez réessayer.',
            trigger_success: 'Exporter triggered avec succès.',
            type_audit_trail: "Piste d'audit",
            type_compliance_report: 'Conformité Rapport',
            type_custom: 'Personnalisé',
            type_incident_report: 'Incident Rapport',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: "Journal d'e-mails",
            recipient: 'Destinataire',
            subject: 'Sujet',
            sent_at: 'Envoyé À',
            status: 'Statut',
            resend: 'Renvoyer',
            fetch_failed: 'Échec de fetch email logs. Veuillez réessayer.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisations',
        create: 'Ajouter organisation',
        edit: 'Modifier Organisation',
        name: 'Nom',
        slug: 'Identifiant URL',
        timezone: 'Fuseau horaire',
        locale: 'Par défaut Locale',
        parent: 'Organisation parente',
        group: 'Groupe',
        org_group: "Groupe d'Organisations",
        no_group: "(Aucun - n'appartient à aucun groupe)",
        created: 'Créé',
        no_parent: '(Aucun - autonome)',
        no_orgs: 'Aucun organizations trouvé',
        load_error: 'Échec de load organizations. Veuillez réessayer.',
        save_error: 'Échec de save organization. Veuillez réessayer.',
        created_msg: 'Organisation créé',
        updated: 'Organisation mis à jour',
        deactivate: 'Désactiver',
        deactivated: 'Organisation désactivée',
        confirm_deactivate: 'Êtes-vous sûr de vouloir désactiver cette organisation ?',
        loaded_results: 'Organizations chargés, {count} résultats',
        form_loaded: 'Formulaire Organization chargé',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Anglais (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Tous les cadres',
        assigned_at: 'Attribué le',
    },
    intake_configs: {
        title: "Configs d'entrée publiques",
        create: 'Ajouter Config',
        edit: "Modifier la config. d'entrée",
        label_col: 'Libellé',
        key: "Clé d'entrée",
        key_hint: 'Public URL va être: /rights-request?key=',
        mapped_org: 'Cible Organisation',
        status: 'Statut',
        enabled: 'Activé',
        disabled: 'Désactivé',
        request_types: 'Activé Demande Types',
        language: 'Par défaut Langue',
        public_url: 'URL publique',
        no_configs: "Aucune config. d'entrée trouvée. Créez-en une pour activer les formulaires de demande de droits.",
        load_error: 'Échec de load intake configs. Veuillez réessayer.',
        save_error: 'Échec de save. Veuillez réessayer.',
        delete_error: 'Échec de supprimer. Veuillez réessayer.',
        created_msg: 'Configuration créée',
        updated: 'Configuration mise à jour',
        deleted: "Config. d'entrée supprimée",
        confirm_delete: "Supprimer cette configuration d'entrée ?",
        select_types: 'Sélectionnez at least one request type',
        select_org: 'Sélectionnez target organization',
        routing_mode: 'Mode de Routage',
        single_org: 'Organisation Unique',
        org_group_mode: "Groupe d'Organisations",
        org_group: "Groupe d'Organisations",
        select_group: "Sélectionnez un groupe d'organisations...",
        group_root_hint: "Défini automatiquement sur l'organisation racine du groupe lorsque le groupe est sélectionné.",
        loaded_results: 'Intake configurations chargés, {count} résultats',
        form_loaded: 'Formulaire Intake configuration chargé',
    },
    frequency: {
        weekly: 'Hebdomadaire',
        monthly: 'Mensuel',
        quarterly: 'Trimestriel',
        semi_annually: 'Semestriel',
        annually: 'Annuel',
        custom: 'Personnalisé',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Calendrier',
            all_tasks: 'Toutes les tâches',
            my_tasks: 'Mes tâches',
            overdue: 'En retard',
            board: 'Tableau',
            workflows: 'Flux de travail',
        },
        list: {
            title: 'Toutes les tâches',
            my_title: 'Mes tâches',
            overdue_title: 'Tâches en retard',
            new_task: 'Créer une tâche',
            no_tasks: 'Aucune tâche trouvée.',
            empty_state: 'Aucune tâche trouvée. Les tâches sont générées à partir des flux de travail des modules comme les évaluations, les incidents et les campagnes de révision.',
            total: 'Total des tâches',
            search_placeholder: 'Rechercher des tâches...',
            bulk_actions: 'Actions groupées',
            change_status: 'Changer le statut',
            change_priority: 'Changer la priorité',
            assign: 'Assigner',
            sr_my_tasks_count: '{count} tâches assignées à vous',
            sr_days_overdue: '{count} jours de retard',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Détail de la tâche',
            description: 'Descriptif',
            workflow_progress: 'Progression du flux de travail',
            checklist: 'Liste de contrôle',
            dependencies: 'Dépendances',
            comments: 'Commentaires',
            activity_timeline: "Chronologie d'activité",
            assignees: 'Assignés',
            add_assignee: 'Ajouter un assigné',
            remove_assignee: 'Retirer un assigné',
            due_date: "Date d'échéance",
            start_date: 'Date de début',
            sla_status: 'Statut SLA',
            sla_on_track: 'Dans les délais',
            sla_at_risk: 'À risque',
            sla_breached: 'Dépassé',
            tags: 'Balises',
            source_entity: 'Entité source',
            view_source: 'Voir la source',
            add_dependency: 'Ajouter une dépendance',
            remove_dependency: 'Retirer une dépendance',
            write_comment: 'Écrire un commentaire...',
            post_comment: 'Publier',
            edit_comment: 'Modifier',
            delete_comment: 'Supprimer',
            add_checklist_item: 'Ajouter un élément',
            no_dependencies: 'Aucune dépendance',
            no_comments: 'Aucun commentaire',
            blocks: 'Bloque',
            blocked_by: 'Bloqué par',
            related: 'Lié',
            working_days_remain: 'jours ouvrés restants',
            created_by: 'Créé par',
            completed_at: 'Terminé le',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Aucun assigné',
            add_assignee_placeholder: "Saisir l'ID utilisateur...",
            edited: 'modifié',
            timeline: "Chronologie d'activité",
            no_timeline: 'Aucune activité',
            timeline_reason: 'Reason',
            no_workflow: 'Aucun flux de travail assigné',
            add_dependency_placeholder: "Saisir l'ID de tâche...",
            sr_status_updated: 'Statut mis à jour : {status}',
            sr_priority_updated: 'Priorité mise à jour sur {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Exécuter en tant que workflow',
            convert_modal_title: 'Convertir une tâche en exécution de flux de travail',
            convert_workflow_label: 'Flux de travail',
            convert_workflow_placeholder: 'Choisissez un flux de travail…',
            convert_attachments_label: 'Pièces jointes',
            convert_attachments_empty: 'Aucune pièce jointe sur cette tâche.',
            convert_persona_label: 'Remplacement de la personnalité (facultatif)',
            convert_submit: "Démarrer l'exécution du flux de travail",
            convert_starting: "Démarrage de l'exécution du flux de travail…",
            convert_started: 'L’exécution du workflow a démarré – ouverture de l’exécution…',
            convert_disabled_in_flight: 'Un workflow exécuté à partir de cette tâche est déjà en cours.',
            linked_workflow_chip: 'De {workflow_title}',
            linked_chat_chip: 'Depuis le chat AI',
            linked_more: '+{count} plus',
        },
        calendar: {
            title: 'Calendrier des tâches',
            page_title: 'Calendrier',
            create_task: 'Créer une tâche',
            month: 'Mois',
            week: 'Semaine',
            day: 'Jour',
            today: "Aujourd'hui",
            more_tasks: '+{count} plus',
            no_tasks_day: 'Aucune tâche ce jour',
            all_day: 'Toute la journée',
            quick_create_tooltip: 'Créer une tâche pour ce jour',
            view_changed: 'Vue du calendrier changée en {view}',
            tasks_in_view: '{count} tâches dans la vue',
            task_in_view: '{count} tâche en vue',
            filter_my_tasks: 'Mes tâches',
            filter_overdue: 'En retard',
            filter_high_priority: 'Priorité haute',
            filter_all: 'Toutes',
        },
        filters: {
            assignee: 'Assigné',
            me: 'Moi',
            role: 'Rôle',
            status: 'Statut',
            priority: 'Priorité',
            task_type: 'Type de tâche',
            source_module: 'Module source',
            organization: 'Organisation',
            sla_breached_only: 'Afficher uniquement SLA dépassé',
            clear_filters: 'Effacer les filtres',
            apply_filters: 'Appliquer',
            source_label: 'Origine',
            source_manual: 'Manuel',
            source_module_kind: 'Généré par le module',
            source_chat: 'Depuis le chat AI',
            source_workflow_run: 'À partir de AI Exécution du workflow',
        },
        status: {
            pending: 'En attente',
            in_progress: 'En cours',
            blocked: 'Bloqué',
            completed: 'Terminé',
            overdue: 'En retard',
            escalated: 'Escaladé',
            cancelled: 'Annulé',
        },
        priority: {
            critical: 'Critique',
            high: 'Élevée',
            medium: 'Moyenne',
            low: 'Basse',
        },
        task_types: {
            manual_task: 'Tâche manuelle',
            dsr_case: 'Cas DSR',
            dsr_review: 'Revue DSR',
            dsr_escalation: 'Escalade DSR',
            dpia_review: 'Revue AIPD',
            dpia_approval: 'Approbation AIPD',
            transfer_review: 'Revue de transfert',
            incident_task: "Tâche d'incident",
            incident_notification: "Notification d'incident",
            playbook_step: 'Étape du guide',
            regulator_deadline: 'Échéance réglementaire',
            monitoring_task: 'Tâche de surveillance',
            kpi_alert: 'Alerte KPI',
            vendor_review: 'Revue fournisseur',
            vendor_questionnaire: 'Questionnaire fournisseur',
            governance_review: 'Revue de gouvernance',
            drift_alert: 'Alerte de dérive',
            governance_approval: 'Approbation de gouvernance',
            deletion_task: 'Tâche de suppression',
            deletion_verification: 'Vérification de suppression',
            access_review: 'Revue des accès',
            sod_alert: 'Alerte SoD',
            consent_workflow: 'Flux de consentement',
            consent_alert: 'Alerte de consentement',
            property_review: 'Revue de propriété',
            inconsistency_alert: "Alerte d'incohérence",
            form_review: 'Revue de formulaire',
            dossier_deadline: 'Échéance de dossier',
            submission_task: 'Tâche de soumission',
            translation_review: 'Revue de traduction',
            governance_task: 'Tâche de gouvernance',
            reporting_deadline: 'Échéance de rapport',
            scanner_alert: 'Alerte du scanner',
            classification_review: 'Revue de classification',
            lifecycle_task: 'Tâche de cycle de vie',
            discovery_review: 'Revue de découverte',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Affectation de formation',
        task_type_groups: {
            training: 'Formation',
            manual: 'Manuel',
            dsr: 'DSR / Demandes de droits',
            assessments: 'Évaluations',
            incidents: 'Incidents',
            incident_ops: "Opérations d'incidents",
            monitoring: 'Surveillance',
            vendor_risk: 'Risque fournisseurs',
            governance: 'Gouvernance',
            deletion: 'Suppression',
            access_review: 'Revue des accès',
            consent: 'Consentement',
            consent_properties: 'Propriétés du consentement',
            forms: 'Formulaires',
            localization: 'Localisation',
            program_governance: 'Gouvernance de programme',
            privacy_scanner: 'Scanner de confidentialité',
            classification: 'Classement',
            discovery: 'Découverte',
            forseti: 'Forseti',
        },
        create: {
            title: 'Créer une tâche',
            task_title: 'Titre',
            task_description: 'Descriptif',
            task_type: 'Type de tâche',
            task_type_label: 'Type de tâche',
            task_type_placeholder: 'Sélectionner un type de tâche',
            priority: 'Priorité',
            assignees: 'Assignés',
            assignee_search_placeholder: 'Rechercher des utilisateurs...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Retirer {name}',
            due_date: "Date d'échéance",
            start_date: 'Date de début',
            checklist: 'Liste de contrôle',
            tags: 'Balises',
            add_checklist_item: 'Ajouter un élément de liste',
            validation: {
                title_required: 'Le titre est obligatoire',
                title_max_length: 'Le titre doit comporter 500 caractères ou moins',
                description_max_length: 'La description doit comporter 5000 caractères ou moins',
                due_date_before_start: "La date d'échéance doit être postérieure à la date de début",
                max_reminders: 'Maximum 5 rappels par tâche',
            },
            reminders: {
                title: 'Rappels',
                add: 'Ajouter un rappel',
                at_due_date: "À la date d'échéance",
                minutes_before: '{count} minutes avant',
                hours_before: '{count} heures avant',
                days_before: '{count} jours avant',
                weeks_before: '{count} semaines avant',
                custom: 'Personnalisé',
                channel_platform: 'Plateforme',
                channel_email: 'E-mail',
                channel_both: 'Les deux',
                offset_label: 'Rappel {n} : moment',
                channel_label: 'Rappel {n} : canal',
                remove_label: 'Supprimer le rappel {n}',
            },
            workflow: {
                label: 'Flux de travail',
                none: 'Aucun (gestion manuelle des statuts)',
                steps_count: '{count} étapes',
                preview_label: 'Étapes du flux',
            },
        },
        errors: {
            fetch_failed: 'Échec du chargement des tâches.',
            save_failed: "Échec de l'enregistrement de la tâche.",
            delete_failed: 'Échec de la suppression de la tâche.',
            status_change_failed: 'Échec du changement de statut.',
            assign_failed: "Échec de l'assignation.",
            comment_failed: "Échec de l'enregistrement du commentaire.",
            checklist_failed: 'Échec de la mise à jour de la liste de contrôle.',
            dependency_failed: 'Échec de la mise à jour de la dépendance.',
            workflow_failed: "Échec de l'enregistrement du flux.",
            workflow_activate_failed: "Échec de l'activation du flux.",
            workflow_delete_failed: 'Échec de la suppression du flux.',
            template_install_failed: "Échec de l'installation du modèle.",
            search_failed: 'Échec de la recherche.',
            convert_failed: "Impossible de démarrer l'exécution du flux de travail.",
            convert_quota_exhausted: "Le seau de jetons est bas. Faites l'appoint avant de réexécuter.",
        },
        dependency_types: {
            blocks: 'Bloque',
            blocked_by: 'Bloqué par',
            related: 'Lié',
        },
        workflow: {
            current_step: 'Étape actuelle',
            transition: 'Transition',
            history: 'Historique des étapes',
            no_workflow: 'Aucun flux de travail assigné',
            detail_title: 'Détails du workflow',
            list_title: 'Définitions des flux de travail',
            create_workflow: 'Créer un flux de travail',
            browse_templates: 'Parcourir les modèles',
            template_catalog_title: 'Catalogue de modèles',
            name: 'Nom',
            description: 'Descriptif',
            task_types: 'Types de tâche',
            steps: 'Étapes',
            version: 'Version',
            created: 'Créé',
            actions: 'Actions',
            status_draft: 'Brouillon',
            status_active: 'Actif',
            status_archived: 'Archivé',
            activate: 'Activer',
            archive: 'Archiver',
            save: 'Enregistrer',
            edit: 'Modifier',
            delete: 'Supprimer',
            clone: 'Cloner',
            install: 'Installer',
            installed: 'Installé',
            system: 'Système',
            general_settings: 'Paramètres généraux',
            default_assignment: 'Assignation par défaut',
            default_sla: 'SLA par défaut',
            workflow_steps: 'Étapes du flux de travail',
            add_step: 'Ajouter une étape',
            step_name: "Nom de l'étape",
            step_status: 'Valeur du statut',
            assignment_strategy: "Stratégie d'assignation",
            assignment_role: "Rôle d'assignation",
            sla_hours: 'Heures SLA',
            sla_calendar_type: 'Type de calendrier',
            working_days: 'Jours ouvrés',
            calendar_days: 'Jours calendaires',
            is_terminal: 'Étape terminale',
            required_fields: 'Champs obligatoires',
            checklist_template: 'Modèle de liste de contrôle',
            transitions: 'Transitions',
            add_transition: 'Ajouter une transition',
            from_step: 'De',
            to_step: 'Vers',
            permission: 'Autorisation',
            sla_action: 'Action SLA',
            notify: 'Notifier',
            escalation_rules: "Règles d'escalade",
            add_rule: 'Ajouter une règle',
            trigger: 'Déclencheur',
            threshold: 'Seuil',
            action: 'Action',
            target_role: 'Rôle cible',
            trigger_sla_threshold: 'Seuil SLA',
            trigger_sla_breach: 'Violation SLA',
            trigger_blocked_duration: 'Durée bloquée',
            trigger_manual: 'Manuel',
            action_notify: 'Notifier',
            action_escalate: 'Escalader le niveau',
            action_reassign: 'Réassigner',
            sla_start: 'Démarrer',
            sla_pause: 'Pause',
            sla_resume: 'Reprendre',
            sla_stop: 'Arrêter',
            strategy_manual: 'Manuel',
            strategy_role_based: 'Basé sur le rôle',
            strategy_round_robin: 'Tournoi à la ronde',
            strategy_least_loaded: 'Moins chargé',
            strategy_creator: 'Créateur',
            confirm_delete: 'Êtes-vous sûr de vouloir supprimer ce flux de travail ?',
            confirm_archive: 'Êtes-vous sûr de vouloir archiver ce flux de travail ?',
            cannot_delete_active: "Les flux actifs ne peuvent pas être supprimés. Archivez d'abord.",
            validation_errors: 'Erreurs de validation du flux',
            step_count: '{count} étapes',
            no_workflows: 'Aucun flux trouvé.',
            no_templates: 'Aucun modèle disponible.',
            sr_templates_count: '{count} modèles disponibles',
            template_installed: 'Modèle installé avec succès.',
            activate_success: 'Flux de travail activé avec succès.',
            archive_success: 'Flux de travail archivé avec succès.',
            save_success: 'Flux de travail enregistré avec succès.',
            delete_success: 'Flux de travail supprimé avec succès.',
            applicable_task_types: 'Types de tâches applicables',
            steps_column: 'Étapes',
            status_value_hint: "Clé d'état du flux utilisée par les transitions. Lettres minuscules, chiffres et traits de soulignement.",
            from_status: "De l'état",
            to_status: "Vers l'état",
            required_permissions: 'Autorisations requises',
            transition_required_fields: 'Champs obligatoires (transition)',
            auto_assign: 'Attribution automatique',
            auto_assign_role: "Rôle d'attribution automatique",
            threshold_percent: 'Seuil (%)',
            blocked_hours: 'Heures bloquées',
            notify_roles: 'Rôles à notifier',
            escalate_to_role: 'Escalader vers le rôle',
            notification_template: 'Modèle de notification',
            action_escalate_tier: "Niveau d'escalade",
            create_modal_title: 'Créer un flux de travail',
            create_modal_description: "Définissez les étapes, les transitions et les règles d'escalade. Le flux de travail est créé en tant que brouillon.",
            create_modal_footer_note: 'Créé en tant que brouillon. Activez-le une fois le graphe validé.',
            validation_name_required: 'Le nom du flux de travail est requis.',
            validation_task_types_required: 'Sélectionnez au moins un type de tâche applicable.',
            validation_min_one_step: 'Ajoutez au moins une étape.',
            validation_min_one_transition: 'Ajoutez au moins une transition.',
            validation_step_name_required: "Le nom de l'étape est requis.",
            validation_status_value_required: "La valeur d'état est requise.",
            validation_status_value_duplicate: "La valeur d'état doit être unique parmi les étapes.",
            validation_transition_endpoints_required: "Sélectionnez à la fois un état de départ et un état d'arrivée.",
            validation_transition_unknown_status: "Cette transition référence un état qu'aucune étape ne définit.",
            validation_sla_positive: 'Les heures SLA doivent être supérieures à zéro. Laissez vide pour aucun SLA.',
            validation_threshold_percent_range: 'Le seuil doit être compris entre 0 et 100.',
            validation_blocked_hours_positive: 'Les heures bloquées doivent être supérieures à zéro.',
            warning_no_terminal_step: "Aucune étape terminale. L'activation échouera tant qu'une étape n'est pas marquée comme terminale.",
            warning_step_no_outgoing: 'Étapes non terminales sans transitions sortantes : {statuses}',
            warning_unreachable_steps: 'Étapes inaccessibles depuis la première étape : {statuses}',
            orphaned_transitions_warning: '{count} transitions référencent une étape supprimée.',
            remove_orphaned_transitions: 'Supprimer les transitions orphelines',
            transitions_renamed_announcement: "{count} transitions mises à jour vers la nouvelle valeur d'état.",
            add_checklist_item: 'Ajouter un élément de liste de contrôle',
            checklist_item_label: 'Élément de liste de contrôle',
        },
        columns: {
            priority: 'Priorité',
            title: 'Titre',
            status: 'Statut',
            type: 'Tapez',
            assignees: 'Assigné(s)',
            due_date: "Date d'échéance",
            source: 'Origine',
            actions: 'Actions',
        },
        groups: {
            overdue: 'En retard',
            due_today: "Échéance aujourd'hui",
            due_this_week: 'Échéance cette semaine',
            due_later: 'Échéance ultérieure',
            no_due_date: "Sans date d'échéance",
            closed: 'Terminées ou annulées',
        },
        board: {
            title: 'Tableau des tâches',
            drag_hint: 'Glissez les tâches entre les colonnes pour changer le statut',
            empty_column: 'Aucune tâche',
            keyboard_hint: 'Clavier : flèches pour naviguer, espace pour saisir une tâche, gauche/droite pour sélectionner la colonne, Entrée pour déposer, Échap pour annuler.',
            sr_moving: 'Déplacement de {title}. Au-dessus de {column}. Appuyez sur Entrée pour déposer, Échap pour annuler.',
            sr_tasks_shown: '{count} tâches affichées',
            sr_picked_up: '{title} saisi',
            sr_returned: '{title} est revenu à {column}',
            sr_dropped: '{title} déposé dans {column}, position {position} sur {total}',
            sr_move_cancelled: 'Déplacement annulé',
            sr_over_column: 'Au-dessus de {column}',
            sr_moved_to: '{title} deplace vers {column}',
            invalid_transition: 'Impossible de deplacer la tache de {from} vers {to}.',
            terminal_status: 'Cette tache a un statut terminal et ne peut pas etre deplacee.',
            invalid_drop_target: 'Cible de depot invalide',
        },
        team: {
            title: "Charge de travail de l'équipe",
            assignee: 'Assigné',
            active: 'Actif',
            overdue: 'En retard',
            blocked: 'Bloqué',
            completed_7d: 'Terminé (7j)',
            load: 'Charge',
            load_high: 'Élevée',
            load_medium: 'Moyenne',
            load_normal: 'Normale',
        },
        graph: {
            title: 'Graphique de dépendance',
            list_view: 'Vue en liste',
            graph_view: 'Vue graphique',
            zoom_in: 'Zoomer',
            zoom_out: 'Zoom arrière',
            reset_zoom: 'Réinitialiser',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Aucune dépendance à visualiser.',
        },
        add_dependency_modal: {
            title: 'Ajouter une dépendance',
            search_placeholder: 'Rechercher des tâches par titre ou ID...',
            no_results: 'Aucune tâche trouvée.',
            this_blocks: 'Cette tâche bloque les blocs sélectionnés',
            this_blocked_by: 'Cette tâche est bloquée par les sélectionnés',
            related: 'Lié',
            max_dependencies: 'Maximum 10 dépendances par tâche.',
            circular_error: "Impossible d'ajouter : cela créerait une dépendance circulaire.",
        },
        conflict_detection: {
            title: 'Détection de conflits',
            unresolved: 'non résolu',
            no_conflicts: 'Aucun conflit détecté',
            section: 'Section',
            field: 'Champ',
            dept_a: 'Département A',
            value_a: 'Valeur A',
            dept_b: 'Département B',
            value_b: 'Valeur B',
            severity: 'Gravité',
            status: 'Statut',
            resolved: 'Résolu',
            open: 'Ouvert',
            resolve: 'Résoudre',
            load_failed: 'Échec de load conflicts',
            resolve_success: 'Conflit résolu avec succès',
            resolve_failed: 'Échec de resolve conflict',
            severity_critical: 'Critique',
            severity_warning: 'Avertissement',
            severity_info: 'Informations',
        },
        consolidation: {
            title: 'Progrès de consolidation',
            add_child: 'Ajouter une sous-tâche',
            child_task_id: 'ID de la sous-tâche',
            department_id: 'ID du département',
            link: 'Lier',
            overall_completion: 'Achèvement global',
            departments_complete: 'Départements terminés',
            overdue: 'En retard',
            ready: 'Prêt',
            not_ready: 'Pas prêt',
            master_status: 'Statut principal',
            department_breakdown: 'Répartition par département',
            department: 'Département',
            status: 'Statut',
            completion: 'Achèvement',
            due_date: "Date d'échéance",
            overdue_flag: 'En retard',
            child_tasks: 'Sous-tâches',
            task: 'Tâche',
            load_failed: 'Échec de load consolidation data',
            child_linked: 'Sous-tâche liée avec succès',
            link_failed: 'Échec de link child task',
            confirm_unlink: 'Voulez-vous vraiment dissocier cette sous-tâche ?',
            child_unlinked: 'Sous-tâche dissociée',
            unlink_failed: 'Échec de la dissociation de la sous-tâche',
            unlink: 'Dissocier',
            status_completed: 'Terminé',
            status_in_progress: 'En cours',
            status_pending: 'En attente',
            select_child_task: 'Sélectionner la sous-tâche',
            select_department: 'Sélectionner le département',
        },
        section_assignments: {
            title: 'Attributions de section',
            assign_section: 'Attribuer une section',
            section_ref: 'Réf. de section',
            section_label: 'Libellé de section',
            department_id: 'ID du département',
            department: 'Département',
            assignee_id: "ID de l'assigné",
            assignee: 'Cessionnaire',
            mandatory: 'Obligatoire',
            assign: 'Attribuer',
            no_assignments: 'Aucune attribution de section',
            status: 'Statut',
            completion: 'Achèvement',
            mark_complete: 'Terminer',
            load_failed: 'Échec de load section assignments',
            assign_success: 'Section attribuée avec succès',
            assign_failed: 'Échec de assign section',
            status_updated: 'Statut mis à jour',
            status_update_failed: 'Échec de la mise à jour du statut',
            section_ref_placeholder: 'par ex. 3.2',
            status_completed: 'Terminé',
            status_in_progress: 'En cours',
            status_pending: 'En attente',
            select_department: 'Sélectionnez le département',
            select_assignee: 'Sélectionnez le destinataire',
        },
        overdue_columns: {
            days_overdue: 'Jours de retard',
        },
        regulatory_info: {
            deadline_context: 'Lors de la creation de taches DPIA, les dates d echeance doivent correspondre aux delais de depot PDPL : contribution departementale = 10 jours ouvrables. Les delais de cybersecurite et de telecommunications s appliquent egalement.',
            overdue_deadline_context: 'Delais reglementaires des donnees de pack : contribution departementale PDPL = 10 jours ouvrables, reponse supplementaire = 15 jours ouvrables, resoumission = 10 jours ouvrables. Les taches en violation de SLA sont distinguees visuellement.',
            section_assignment_context: 'Processus DPIA multi-departements (PDPL-P1) : le DPO attribue les sections du dossier aux departements. Delai de contribution departementale : 10 jours ouvrables.',
            template_catalog_context: 'Les modeles systeme couvrent les processus specifiques aux cadres : DPIA PDPL, attestation de la loi sur les donnees, reponse aux incidents de cybersecurite, demandes d autorite telecom et enregistrement de systeme IA. Les modeles systeme ne sont pas modifiables.',
            workflow_instantiation_context: 'Les modeles installes peuvent etre instancies en flux de travail actifs. Les taches sont creees automatiquement avec des dates d echeance calculees.',
        },
        overdue: {
            days_overdue: 'jours de retard',
        },
    },
    notifications: {
        bell: {
            title: 'Notifications',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Notifications',
            mark_all_read: 'Marquer tout comme lu',
            no_notifications: 'Aucune notification',
            filter: {
                all: 'All',
                unread: 'Non lu',
                tasks: 'Tâches',
                approvals: 'Approbations',
                alerts: 'Alertes',
            },
        },
        preferences: {
            title: 'Préférences de notifications',
            description: 'Configurer la façon dont vous recevez les notifications',
            category: {
                label: 'Catégorie',
                task_assigned: 'Tâche assignée',
                task_sla_approaching: 'SLA Approche',
                task_overdue: 'Tâche en retard',
                task_completed: 'Tâche terminée',
                task_escalated: 'Tâche escaladée',
                workflow_transition: 'Transition du flux de travail',
                approval_requested: 'Approbation demandée',
                comment_added: 'Commentaire ajouté',
                weekly_digest: 'Résumé hebdomadaire',
                sla_breach_report: 'SLA Rapport de violation',
                system_alert: 'Alerte système',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plateforme',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Heures calmes',
                start: 'Démarrer',
                end: 'End',
                timezone: 'Fuseau horaire',
            },
            digest: {
                title: 'Fréquence de résumé',
                daily: 'Quotidiennement',
                weekly: 'Hebdomadaire',
                none: 'Aucun',
            },
            watched: {
                title: 'Entités surveillées',
                empty: 'Aucune entité surveillée',
            },
            save: 'Enregistrer les préférences',
            saving: 'Sauvegarde...',
            saved_success: 'Préférences enregistrées avec succès',
            load_error: 'Impossible de charger les préférences enregistrées. Affichage des valeurs par défaut : les modifications seront enregistrées lorsque vous cliquerez sur Enregistrer.',
            enabled: 'Activé',
            disabled: 'Désactivé',
            unwatch: 'Ne plus regarder',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: "File d'administration des notifications",
            description: "Surveiller l'état de livraison des e-mails et réessayer les messages échoués",
            load_error: "Impossible de charger les données de la file d'attente des e-mails",
            retry_error: "Impossible de réessayer l'e-mail",
            filter_status: 'Filtrer par statut',
            all_statuses: 'Tous',
            refresh: 'Actualiser',
            status_queued: "En file d'attente",
            status_sent: 'Envoyé',
            status_failed: 'Échoué',
            status_bounced: 'Rejeté',
            status_sending: 'Sending',
            col_recipient: 'Destinataire',
            col_category: 'Catégorie',
            col_template: 'Modèle',
            col_status: 'Statut',
            col_retries: 'Tentatives',
            col_created: 'Créé',
            col_sent: 'Envoyé le',
            col_actions: 'Actions',
            no_items: "Aucun élément dans la file d'attente des e-mails",
            retry: 'Réessayer',
            retrying: 'Nouvelle tentative...',
            prev: 'Précédent',
            next: 'Suivant',
            access_denied_title: 'Accès administrateur requis',
            access_denied: "Cette page est réservée aux administrateurs. Veuillez contacter votre administrateur si vous avez besoin d'un accès.",
            announce_queue_loaded: "File d'attente de courrier électronique chargée, {count} éléments",
            announce_processed: '{count} e-mail(s) traité(s)',
            process_now: 'Traiter maintenant',
            processing_ellipsis: 'Traitement…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'Assistance UI',
            activate: 'Activer',
            deactivate: 'Désactiver',
            connected: 'Connecté',
            disconnected: 'Déconnecté',
            disconnected_hint: 'Déconnecté - exécutez `npm run relay` pour vous connecter à Claude CLI',
            open_panel: 'Ouvrir le panneau UI Assist',
            history: 'Histoire',
            settings: 'Paramètres',
        },
        prompt: {
            placeholder: 'Décrivez le changement que vous souhaitez apporter…',
            send: 'Envoyer',
            cancel: 'Annuler',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Traitement…',
            complete: 'Terminé',
            error: 'Erreur',
            abort: 'Abandonner',
            abort_confirm: 'Êtes-vous sûr de vouloir abandonner ?',
            aborted_hint: "La session a été interrompue — vous pouvez continuer d'ici.",
            undo: 'Annuler',
            redo: 'Refaire',
            retry: 'Réessayer',
            dismiss: 'Rejeter',
        },
        label: {
            component: 'Composant',
            element: 'Élément',
            file: 'Fichier',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: "Aucun message pour l'instant. Sélectionnez des éléments et envoyez une invite pour commencer.",
        },
        panel: {
            tab_conversation: 'Discuter',
            tab_tasks: 'Tâches',
            tab_sprint: 'Sprint',
            tab_sessions: 'Séances',
            tab_lobby: "Hall d'entrée",
            follow_up: 'Suivi',
            followup_placeholder: 'Posez une question complémentaire…',
            interrupt_placeholder: 'Tapez pour interrompre et rediriger…',
            send_interrupt: 'Envoyer & Interrompre',
            will_interrupt: 'Interrompt la réponse en cours et envoie ceci à la place',
            expand: 'Agrandir en plein écran',
            collapse: 'Réduire au panneau latéral',
            no_sessions: 'Aucune session précédente. Sélectionnez des éléments et envoyez une instruction.',
            session_active: 'Actif',
            session_ended: 'Terminée',
            session_messages: '{count} messages',
            switch_session: 'Basculer vers cette session',
            load_session: "Charger l'historique des sessions",
            session_restored: "Session restaurée à partir de l'historique",
            archive_session: "Séance d'archivage",
            archive_confirm: 'Archiver cette session ? Le fichier de session sera supprimé.',
            new_session: 'Nouvelle séance',
            new_session_desc: "Utilisez Activer pour sélectionner un élément de l'interface utilisateur et démarrer une nouvelle session",
            start_new_session: 'Démarrer une nouvelle session',
            no_active_session_chat: "Démarrez une nouvelle session ou utilisez Activer pour sélectionner un élément d'interface utilisateur sur la page pour commencer.",
            no_active_session_tasks: 'Démarrez une nouvelle session ou sélectionnez une session existante pour créer des tâches.',
            no_active_session_sprint: 'Démarrez une nouvelle session ou sélectionnez une session existante pour planifier un sprint.',
            previous_sessions: 'Séances précédentes',
        },
        tasks: {
            none_found: 'Aucune tâche trouvée dans la réponse. Demandez à Claude de suggérer des tâches.',
            selected: 'selected',
            create_selected: 'Créer des tâches',
            execute_tasks: 'Exécuter les tâches',
            creating: 'Création…',
            created: 'tâches créées',
            create_error: 'Échec de la création de la tâche',
            plan_sprint: 'Planifier le sprint',
            priority_critical_desc: 'Bloquant — corriger immédiatement',
            priority_high_desc: 'Important — traiter ce sprint',
            priority_medium_desc: 'Normal — planifier pour le prochain sprint',
            priority_low_desc: 'Souhaitable — candidat pour le backlog',
            add_custom: 'Ajouter une tâche',
            add_title_placeholder: 'Titre de la tâche…',
            add_desc_placeholder: 'Description (facultatif)',
            add_save: 'Ajouter',
            add_cancel: 'Annuler',
        },
        sprint: {
            title: 'Planificateur de sprints',
            assigned: 'assigned',
            new_placeholder: 'Nom du sprint (par exemple, Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Carnet de commandes',
            move_to: 'Déplacer vers…',
            empty: 'Créez un groupe de sprint et attribuez-lui des tâches.',
            create_all: 'Créer toutes les tâches',
            remove: 'Supprimer',
            drop_here: 'Attribuez des tâches à partir du backlog ci-dessous',
            add_task: 'Ajouter une tâche du backlog…',
        },
        feature_disabled: "UI Assist n'est pas activé",
        permission_denied: "Vous n'êtes pas autorisé à utiliser UI Assist",
    },
    license: {
        title: 'Licence et forfait',
        current_license: 'Licence actuelle',
        license_key: 'Clé de licence',
        activate: 'Activer la licence',
        deactivate: 'Désactiver la licence',
        validate_now: 'Valider maintenant',
        status: 'Statut de la licence',
        active: 'Actif',
        expired: 'Expiré',
        suspended: 'Suspendu',
        invalid: 'Invalide',
        pending: 'En attente',
        no_license: 'Pas de licence',
        expires_at: 'Expire',
        last_validated: 'Dernière validation',
        domain: 'Domaine sous licence',
        activation_success: 'Licence activée avec succès.',
        activation_failed: "L'activation de la licence a échoué.",
        domain_mismatch: 'Le domaine de licence ne correspond pas au domaine actuel.',
        deactivation_success: 'Licence désactivée.',
        validation_success: 'Licence validée avec succès.',
        validation_failed: 'La validation de la licence a échoué.',
        enter_key: 'Entrez votre clé de licence',
        confirm_deactivate: "Êtes-vous sûr de vouloir désactiver cette licence ? Les restrictions de module s'appliqueront en fonction du niveau par défaut.",
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Forfait',
        module_column: 'Module',
        current_tier: 'Forfait actuel',
        light: 'Lumière',
        standard: 'Norme',
        enterprise_unlimited: 'Entreprise illimitée',
        change_package: 'Changer de forfait',
        upgrade: 'Mise à niveau',
        downgrade: 'Rétrograder',
        target_tier: 'Forfait cible',
        new_license_key: 'Clé de licence pour le package cible',
        confirm_change: 'Confirmer le changement de package',
        change_success: 'Le package a été modifié avec succès.',
        change_failed: 'La modification du package a échoué.',
        modules_included: 'Modules inclus',
        modules_restricted: 'Non disponible dans le forfait actuel',
        upgrade_to_enable: 'Mettez à niveau pour activer ce module.',
        module_availability: 'Disponibilité des modules',
        comparison: 'Comparaison des forfaits',
        power_users: 'Utilisateurs expérimentés',
        unlimited: 'Illimité',
        impact_warning: 'La rétrogradation désactivera les modules suivants :',
        invalid_license: "La clé de licence fournie n'est pas valide pour ce package.",
        tier_mismatch: 'La clé de licence concerne un niveau de package différent.',
    },
    board_report: {
        title: 'Rapport de Conformité au Conseil',
        subtitle: "Synthèse de la conformité sur l'ensemble des modules",
        date_from: 'Date de début',
        date_to: 'Date de fin',
        language: 'Langue du rapport',
        generate: 'Générer le rapport',
        generating: 'Génération du rapport...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Télécharger le PDF',
        close: 'Fermer le rapport',
        open_board_report: "Générer le rapport d'administration",
        close_board_report: "Fermer le rapport d'administration",
        report_period: 'Période du rapport',
        generated_at: 'Généré le',
        generated_by: 'Généré par',
        organization: 'Organisation',
        section_rights_requests: 'Demandes de droits',
        section_vendors: 'Fournisseurs',
        section_dpias: 'AIPD',
        section_consent: 'Politiques de consentement',
        section_data_mapping: 'Cartographie des données',
        section_incidents: 'Incidents',
        section_classification: 'Classification des données',
        section_governance: 'Gouvernance',
        open: 'Ouvert',
        closed: 'Fermé',
        sla_compliance: 'Conformité SLA',
        total: 'Total',
        expired_dpa: 'DPA expiré',
        high_risk: 'Risque élevé',
        completed: 'Terminé',
        overdue: 'En retard',
        policies_active: 'Politiques actives',
        coverage: 'Couverture',
        systems: 'Systèmes',
        completeness: 'Complétude',
        avg_resolution_days: 'Durée moy. de résolution (jours)',
        labeled: 'Étiqueté',
        unlabeled_systems: 'Systèmes non étiquetés',
        compliance_score: 'Score de conformité',
        drift_events_open: 'Écarts ouverts',
        no_data: 'Aucune donnée disponible pour la période sélectionnée.',
        error_date_range: 'Veuillez sélectionner une date de début et de fin.',
        table_category: 'Catégorie',
        table_metric: 'Métrique',
        table_value: 'Valeur',
        showing_chart_view: 'Affichage de la vue graphique',
        showing_table_view: 'Affichage de la vue tableau de données',
        error_generate: "Le rapport d'administration n'a pas pu être généré.",
        playbook_evidence_title: "Preuves d'exécution des playbooks",
        playbook_evidence_description: "Activité d'exécution par playbook et fichiers de preuve joints pour la période du rapport.",
        playbook_name: 'Manuel de jeu',
        playbook_execution_count: 'Exécutions',
        playbook_steps_total: 'Étapes',
        playbook_steps_required: 'Preuve requise',
        playbook_steps_attached: 'Preuve jointe',
        playbook_coverage_pct: 'Couverture',
        playbook_evidence_files: 'Fichiers de preuve',
        playbook_no_evidence: 'Aucune exécution de playbook sur cette période.',
        playbook_download_file: 'Télécharger le fichier',
    },
    framework_module_links: {
        title: 'Liaison de cadre réglementaire',
        modules: 'Modules liés',
        add_module: 'Ajouter un module',
        module_code: 'Code de module',
        required: 'Obligatoire',
        notes: 'Remarques',
        link: 'Lier',
        no_modules: 'Aucun module lié',
        personas: 'Personas liés',
        add_persona: 'Ajouter un persona',
        persona_id: 'ID de persona',
        role_description: 'Description du rôle',
        no_personas: 'Aucun persona lié',
    },
    framework_channels: {
        title: 'Canaux de soumission & Délais',
        channels: 'Canaux de soumission',
        add_channel: 'Ajouter un canal',
        channel_type: 'Tapez',
        name: 'Nom',
        instructions: 'Instructions',
        active: 'Actif',
        no_channels: 'Aucun canal de soumission configuré',
        portal: 'Portail',
        in_person: 'En personne',
        postal: 'Poste',
        email: 'E-mail',
        add: 'Ajouter',
        deadlines: 'Délais réglementaires',
        add_deadline: 'Ajouter un délai',
        label: 'Libellé',
        days: 'Jours',
        day_type: 'Type de jour',
        calendar_days: 'Jours calendaires',
        working_days: 'Jours ouvrables',
        description: 'Descriptif',
        no_deadlines: 'Aucun délai configuré',
    },
    regulatory_updates: {
        title: 'Mises à jour réglementaires & Tableau de bord des cadres',
        coverage_dashboard: 'Tableau de bord de couverture des cadres',
        no_frameworks: 'Aucun cadre trouvé',
        framework: 'Cadre réglementaire',
        status: 'Statut',
        modules: 'Modules',
        templates: 'Modèles',
        forms: 'Formulaires',
        channels: 'Canaux',
        deadlines: 'Délais',
        pending: 'Mises à jour en attente',
        updates: 'Mises à jour réglementaires',
        create_update: 'Créer une mise à jour',
        framework_id: 'ID du cadre',
        update_title: 'Titre',
        description: 'Descriptif',
        effective_date: "Date d'entrée en vigueur",
        create: 'Créer',
        no_updates: 'Aucune mise à jour réglementaire',
        impact: 'Impact',
        assess: 'Évaluer',
    },
    module_tips: frModuleTips,
    framework_rights: {
        be_informed: "Droit d'être informé",
        consent: 'Droit au consentement',
        refuse_consent: 'Droit de refuser le consentement',
        withdraw_consent: 'Droit de retirer le consentement',
        access: "Droit d'accès",
        rectification: 'Droit de rectification',
        data_provision: 'Droit à la fourniture des données',
        deletion: 'Droit à la suppression',
        restriction_of_processing: 'Droit à la limitation du traitement',
        object_to_processing: "Droit d'opposition au traitement",
        complaint: 'Droit de déposer une plainte',
        denunciation: 'Droit de dénonciation',
        lawsuit: 'Droit de poursuivre en justice',
        claim_damages: 'Droit à réparation du préjudice',
        request_protection_measures: 'Droit de demander des mesures de protection',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Directive 2002/58/CE (directive ePrivacy)',
            description: 'Directive parentale pour les transpositions nationales ePrivacy. Couvre les cookies, la confidentialité des communications électroniques et le marketing direct. Chaque État membre doit transposer dans son droit national.',
        },
        EU_GDPR: {
            display_name: 'Règlement général sur la protection des données (UE) 2016/679',
            description: "Le règlement général sur la protection des données (GDPR) est un règlement de l'UE sur la protection des données et la confidentialité. Elle s’applique directement dans tous les États membres de l’UE/EEE.",
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Directive (UE) 2022/2555 (NIS2)',
            description: "Directive parent pour les transpositions nationales NIS2 Couvre la gestion des risques de cybersécurité, le reporting des incidents et la sécurité de la chaîne d'approvisionnement pour les entités essentielles et importantes.",
        },
        VN_AI_LAW_2026: {
            display_name: 'Loi vietnamienne AI (134/2025/QH15) — En vigueur le 1er mars 2026',
            description: "Vietnam AI Loi (Luật Trí tuệ nhân tạo 134/2025/QH15) régissant le développement, le déploiement et l'utilisation de systèmes d'intelligence artificielle. Établit la classification des risques, les obligations de transparence et d'étiquetage, les exigences de déclaration d'incidents, les obligations d'évaluation d'impact et un mécanisme de test contrôlé (bac à sable) pour les activités AI au Vietnam.",
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Loi vietnamienne sur la cybersécurité (ère 24/2018)',
            description: "Loi vietnamienne sur la cybersécurité (loi n° 24/2018/QH14) établissant des exigences en matière de cybersécurité, de localisation des données et de signalement des incidents. Actif jusqu'à son remplacement par la loi 116/2025/QH15 en vigueur le 01/07/2026.",
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Décret 53 — Mise en œuvre de la loi sur la cybersécurité',
            description: "Décret 53/2022/ND-CP mettant en œuvre la loi sur la cybersécurité, couvrant la détermination du champ d'application, les déclencheurs de localisation des données, les exigences de présence locale et les flux de travail de coopération des autorités.",
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Loi sur la cybersécurité (116/2025/QH15)',
            description: 'Loi consolidée du Vietnam sur la cybersécurité (loi 116/2025/QH15), remplaçant la loi 24/2018/QH14, entrée en vigueur le 1er juillet 2026. Consolide la portée du décret 53/2022/NĐ-CP (voir VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Loi vietnamienne sur les données',
            description: "Loi vietnamienne sur les données (loi 60/2024/QH15) établissant des exigences complètes en matière de gouvernance des données, notamment l'inventaire des données, la classification, la gouvernance du partage et la gouvernance des transferts de données transfrontaliers. Chevauche avec VN_PDPL_LAW_2025 (aspects des données personnelles) et VN_CYBERSEC_LAW_2025 (localisation/classification des données).",
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Décret 165/2025/NĐ-CPĐ-CP (Mise en œuvre de la loi sur les données)',
            description: 'Décret 165/2025/NĐ-CP mettant en œuvre la loi vietnamienne sur les données (loi 60/2024/QH15). Fournit des formulaires officiels pour l’évaluation de l’impact du transfert de données transfrontalier, le rapport annuel d’évaluation des risques et l’accès à la base de données nationale. Définit 11 formulaires officiels Mẫu số.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Projet de décret sur les opérations des bourses de données 2026',
            description: "Projet de décret relatif au fonctionnement des plateformes d'échange de données (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Régule la classification des plateformes, l'éligibilité des participants, les contrôles de liste de produits et de services de données, les tests sandbox, le flux de travail des transactions, les contrats électroniques, les certificats de droits de propriété sur les données et le traitement des plaintes/litiges. Actuellement en consultation publique; pas encore promulgué. Cite la loi sur les données 60/2024/QH15, la loi sur la protection des données personnelles 91/2025/QH15, la loi sur la cybersécurité 116/2025/QH15 et la loi sur les transactions électroniques 20/2023/QH15.",
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Loi sur le commerce électronique (122/2025/QH15)',
            description: "Loi vietnamienne sur le commerce électronique (Luật Thương mại điện tử 122/2025/QH15) établissant un régime national unifié pour le commerce électronique, couvrant les opérations de plateforme nationales et transfrontalières, la gouvernance et la responsabilité de la plateforme, la protection des consommateurs, les contrôles des vendeurs et des affiliés, le traitement des plaintes, la modération et l'application du retrait du contenu, ainsi que la préparation aux procédures administratives pour le système national de gestion du commerce électronique.",
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Décret 13/2023/NĐ-CP',
            description: "Décret 13/2023/ND-CP, l'instrument opérationnel pour les tâches PDPL, y compris le dépôt de dossiers, les artefacts de gouvernance, les mécanismes transfrontaliers et les procédures administratives MoPS. A cessé de produire ses effets le 1er janvier 2026 en application de l'article 42, paragraphe 2, du décret 356/2025/NĐ-CP ; la loi 91/2025/QH15 régit désormais la protection des données personnelles.",
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Loi sur la protection des données personnelles (91/2025/QH15)',
            description: "La loi vietnamienne sur la protection des données personnelles (loi 91/2025/QH15) établissant un cadre complet de protection des données personnelles, en vigueur le 1er janvier 2026. Couvre les thèmes de conformité A à J : bases de traitement licites, consentement aux données sensibles, droits des personnes concernées (accès/rectification/effacement/restriction/objection/portabilité), notification des violations aux autorités et aux personnes concernées, DPIA pour les traitements à haut risque, gouvernance des transferts transfrontaliers, Désignation DPO, enregistrements des activités de traitement, obligations contractuelles du sous-traitant. Obligations de dépôt d'autorité via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Renvois croisés : VN_PDPL_DECREE_356_2025 (décret d'application), VN_CYBERSEC_LAW_2025 (recoupements en matière de cybersécurité), VN_DATA_LAW (gouvernance des données).",
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Décret 356/2025/ND-CP (PDPL Mise en œuvre)',
            description: "Décret 356/2025/ND-CP portant application de la loi sur la protection des données personnelles (loi 91/2025/QH15). Couvre le dépôt de dossiers, les artefacts de gouvernance, les mécanismes transfrontaliers et la certification des prestataires de services. Inventaire du formulaire annexe : Phụ lục I–VIII (analyse d'impact, transfert transfrontalier, certification). Procédures administratives MoPS via VN_PDPL_ADMIN_PROCEDURES_778_2026 (Décision 778/QĐ-BCA-A05). Workflow de certification : demande → délivrance → demande de modification → réémission → révocation. Renvois croisés : VN_PDPL_LAW_2025 (loi mère), VN_CYBERSEC_LAW_2025 (localisation des données), VN_DATA_LAW (chevauchements de gouvernance des données).",
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Procédures administratives (Décision 778/QĐ-BCA-A05)',
            description: "Décision 778/QĐ-BCA-A05 (février 2026) — Procédures administratives pour la protection des données personnelles en vertu de l'A05 (MPS). Définit les procédures de soumission, de traitement et d'émission des résultats des dossiers, y compris les formulaires DLCN-HC et les formulaires statutaires Mẫu số.",
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: "Projet de décret d'application 2026",
            description: "Projet de décret d'application et de sanctions en cas de violation de la loi sur la protection des données personnelles (loi 91/2025/QH15). Définit les fourchettes de pénalités, les facteurs aggravants/atténuants et les procédures d’application. Actuellement à l’état de projet en attente de promulgation.",
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Loi vietnamienne sur les télécommunications 2023',
            description: "Loi vietnamienne sur les télécommunications de 2023 (Luật Viễn thông), une superposition sectorielle pour les sociétés de télécommunications/FAI/plateformes avec des obligations de confidentialité, de contrôle de la divulgation et de traitement des demandes d'autorité. Renvois : VN_CYBERSEC_LAW_2025 (sécurité des systèmes d'information), VN_PDPL_LAW_2025 (traitement des données utilisateurs), VN_DATA_LAW (gouvernance des données).",
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Décret 163/2024/NĐ-CPĐ-CP (Mise en œuvre de la loi sur les télécommunications)',
            description: "Décret 163/2024/NĐ-CP mettant en œuvre la loi sur les télécommunications (loi 24/2023/QH15). Fournit des formulaires officiels pour la notification des services de télécommunications, les licences, l'enregistrement et les plans de développement des centres de données. Définit 7 formulaires officiels Mẫu số.",
        },
    },
    framework_requirements: frFrameworkRequirements,
    framework_rights_descriptions: {
        be_informed: "La personne concernée a le droit d'être informée des activités de traitement des données personnelles.",
        consent: 'La personne concernée a le droit de consentir au traitement de ses données personnelles.',
        refuse_consent: 'La personne concernée a le droit de ne pas consentir au traitement de ses données personnelles.',
        withdraw_consent: 'La personne concernée a le droit de demander le retrait du consentement précédemment donné.',
        access: "La personne concernée a le droit de consulter et d'accéder à ses données personnelles détenues par le responsable du traitement.",
        rectification: 'La personne concernée a le droit de corriger ou de demander la rectification de données personnelles inexactes.',
        data_provision: 'La personne concernée a le droit de demander la fourniture de ses données personnelles (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'La personne concernée a le droit de demander la suppression ou la destruction de ses données personnelles, sous réserve des exceptions prévues par la loi.',
        restriction_of_processing: 'La personne concernée a le droit de demander la limitation du traitement de ses données personnelles.',
        object_to_processing: "La personne concernée a le droit d'adresser une demande d'opposition au traitement de ses données personnelles.",
        complaint: 'La personne concernée a le droit de déposer une plainte (khiếu nại) concernant le traitement des données personnelles.',
        denunciation: 'La personne concernée a le droit de déposer une dénonciation (tố cáo) concernant les violations de la protection des données personnelles.',
        lawsuit: "La personne concernée a le droit d'intenter une action en justice (khởi kiện) en cas de violation de ses droits en matière de protection des données.",
        claim_damages: 'La personne concernée a le droit de réclamer une indemnisation pour les dommages causés par des violations de la protection des données personnelles.',
        request_protection_measures: 'La personne concernée a le droit de demander aux autorités compétentes ou aux organisations/individus concernés de mettre en œuvre des mesures pour protéger ses données personnelles.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Gestion et surveillance des risques (projet)',
            steps: {
                '1': 'Identifier les catégories de risques',
                '2': 'Maintenir des contrôles d’alerte précoce',
                '3': 'Examiner les risques liés à la collecte, au stockage, au traitement, au partage et aux transactions',
                '4': 'Enregistrer les risques liés à la confidentialité, à la cybersécurité et à la gestion des accès',
                '5': 'Déclencher des actions correctives',
                '6': "Conserver l'historique des avis",
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Plaintes et litiges (projet)',
            steps: {
                '1': 'Recevoir les plaintes / litiges',
                '2': "Vérifier l'exhaustivité des informations requises ; demander un supplément sous 24 heures si incomplet",
                '3': "Dans les 3 jours ouvrables suivant la réception d'une demande complète, informez les parties de l'approche de traitement et du calendrier prévu.",
                '4': 'Demander des réponses et des pièces justificatives',
                '5': 'Évaluer les faits du litige et la responsabilité de la plateforme',
                '6': 'Enregistrer le résultat de la résolution',
                '7': 'Préserver les preuves et clôturer le dossier',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Remise et certificat de propriété des données (projet)',
            steps: {
                '1': 'Livrer le produit/service selon la méthode contractuelle',
                '2': "Enregistrer l'événement et l'heure de livraison",
                '3': 'Générer un certificat de droits de propriété sur les données',
                '4': 'Enregistrer la fin de la transaction',
                '5': 'Activer la recherche/vérification ultérieure si nécessaire',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Initiation et passation de transactions (projet)',
            steps: {
                '1': "Examiner la liste et le résultat de l'évaluation",
                '2': "Lancer un workflow d'achat, de négociation ou d'enchères",
                '3': 'Générer un contrat électronique',
                '4': 'Assurez l’acceptation des deux parties',
                '5': "Enregistrer l'achèvement du paiement",
                '6': 'Autoriser la livraison',
            },
        },
        VN_DP_DRAFT_P3: {
            name: "Accès au bac à sable/à l'environnement de test (projet)",
            steps: {
                '1': 'Soumettre une demande de bac à sable',
                '2': 'Définir la portée, les données autorisées, la période et les autorisations de traitement',
                '3': "Mise à disposition d'un environnement technique isolé",
                '4': 'Surveiller la sécurité et l’accès pendant les tests',
                '5': "Vérifier la sortie avant l'extraction",
                '6': 'Approuver l’extraction uniquement lorsque cela est autorisé',
                '7': "Fermer la session de test et nettoyer l'environnement",
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Liste des produits/services de données (ébauche)',
            steps: {
                '1': "Créer une demande d'inscription",
                '2': "Capturer la source et les preuves de propriété/droit d'utilisation",
                '3': 'Enregistrer les caractéristiques techniques, le format, les API détails et les restrictions',
                '4': 'Enregistrer les informations sur la qualité',
                '5': "Soumettre le dossier d'inscription à l'examen de la plateforme",
                '6': "La plateforme effectue des contrôles d'exhaustivité, de légalité et techniques",
                '7': 'Approuver ou rejeter la liste',
                '8': "Si approuvé, publier la liste avec le résultat de l'évaluation en pièce jointe",
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Intégration des participants (ébauche)',
            steps: {
                '1': 'Déterminer le type de participant : vendeur ou acheteur',
                '2': 'Vérifier le statut juridique et la capacité juridique',
                '3': "Vérifier les conditions supplémentaires si des secteurs d'activité conditionnels s'appliquent",
                '4': 'Enregistrer un compte de transaction',
                '5': 'Associer un compte de paiement',
                '6': 'Approuver la participation à la plateforme',
                '7': "Stocker le dossier des participants et la piste d'audit",
            },
        },
        AI_A1: {
            name: 'AI Enregistrement/Notification du système',
            steps: {
                '1': "Vérification de l'applicabilité et classification du système AI",
                '2': "Construire le dossier d'enregistrement/notification",
                '3': 'Approbation interne',
                '4': 'Soumettre via le canal',
                '5': "Boucle d'amendement - si renvoyé, mettez à jour et soumettez à nouveau",
                '6': 'Fermer et confirmer le stockage',
            },
        },
        AI_A2: {
            name: 'AI Flux de travail de classification des risques',
            steps: {
                '1': 'Créer une entrée de registre système AI',
                '2': "Exécuter l'assistant de classification",
                '3': 'Joindre des preuves',
                '4': 'Approuver le classement',
                '5': 'Lier les sorties au dossier système',
            },
        },
        AI_A3: {
            name: 'Conformité à l’étiquetage et à la divulgation',
            steps: {
                '1': "Identifier les obligations d'étiquetage par système et cas d'utilisation",
                '2': "Mettre en œuvre des avis d'étiquetage",
                '3': "Capturer des preuves : captures d'écran, copie de l'interface utilisateur, métadonnées techniques",
                '4': 'Gérer les exemptions et enregistrer la justification',
                '5': 'Approuver et version',
            },
        },
        AI_A4: {
            name: "Rapport d'incident grave",
            steps: {
                '1': "Créer un AI enregistrement d'incident",
                '2': 'Classer la gravité',
                '3': 'Exécuter des actions de confinement et capturer des preuves',
                '4': "Générer un export de modèle de rapport d'incident grave",
                '5': "Soumettre à l'autorité via le canal",
                '6': 'Suivre les réponses des autorités et la clôture',
            },
        },
        AI_A5: {
            name: "AI Évaluation d'impact",
            steps: {
                '1': "Créer un dossier d'évaluation",
                '2': 'Remplir les sections requises',
                '3': 'Joindre des preuves : flux de données, documentation du modèle, contrôles',
                '4': 'Approbation interne',
                '5': 'Exporter le rapport et le stocker dans un dossier',
            },
        },
        AI_A6: {
            name: 'Tests contrôlés / Participation au bac à sable',
            steps: {
                '1': 'Dossier complet d’éligibilité et de préparation',
                '2': "Préparer l'application et l'export",
                '3': 'Soumettre via le canal',
                '4': "Suivre l'approbation",
                '5': 'Exécuter des points de contrôle et des journaux de surveillance',
                '6': 'Rapport d’achèvement complet et dossier de clôture',
            },
        },
        CYBERSEC_C1: {
            name: 'Évaluation de la sécurité des systèmes d’information critiques',
            steps: {
                '1': "Classer les systèmes d'information selon les exigences nationales en matière de cybersécurité",
                '2': 'Effectuer une évaluation de la sécurité et une évaluation des risques pour les systèmes classifiés',
                '3': 'Mettre en œuvre les mesures de sécurité requises et les contrôles de base',
                '4': 'Documenter les preuves de conformité et planifier des examens périodiques',
            },
        },
        CYBERSEC_C2: {
            name: "Notification d'incident de cybersécurité",
            steps: {
                '1': 'Détecter, classer et évaluer l’incident de cybersécurité',
                '2': 'Exécuter des actions de réponse immédiate et préserver les preuves',
                '3': 'Faire rapport aux autorités et participer à la coordination nationale de la réponse aux incidents',
                '4': 'Effectuer un examen post-incident et documenter les leçons apprises',
            },
        },
        CYBERSEC_C3: {
            name: 'Vérification de la conformité de la localisation des données',
            steps: {
                '1': "Évaluer si les déclencheurs de localisation des données s'appliquent en fonction du type de service et des catégories de données",
                '2': "Vérifier que l'architecture de stockage locale et les contrôles de flux transfrontaliers sont en place",
                '3': 'Évaluer les exigences de présence locale (succursale/bureau de représentation), le cas échéant',
                '4': "Documenter les preuves de conformité et les conserver pour examen par l'autorité",
            },
        },
        CYBERSEC_C4: {
            name: 'Réponse à la demande de coopération des autorités',
            steps: {
                '1': "Recevoir et vérifier la légitimité et la portée de la demande d'autorité",
                '2': 'Recueillir et préparer les informations demandées ou exécuter les actions requises',
                '3': "Soumettre la réponse à l'autorité dans les délais requis",
                '4': 'Enregistrez la divulgation et fermez le dossier',
            },
        },
        CYBERSEC_C5: {
            name: 'Vérification des qualifications du personnel en cybersécurité',
            steps: {
                '1': "Évaluer la posture de sécurité des fournisseurs à l'aide du cadre d'évaluation de la cybersécurité",
                '2': "Vérifier la portée de l'accès aux données du fournisseur et s'inscrire dans le journal d'accès aux données du fournisseur",
                '3': "Examiner la clause de notification d'incident du fournisseur et les exigences contractuelles en matière de cybersécurité",
                '4': 'Planifiez des examens périodiques de la sécurité des fournisseurs et surveillez la conformité',
            },
        },
        DATA_D1: {
            name: "Configuration de l'inventaire et de la classification des données",
            steps: {
                '1': "Cataloguer tous les actifs de données avec types et sources dans le registre d'inventaire des données",
                '2': 'Classer chaque actif par niveau de sensibilité',
                '3': 'Définir des règles de traitement par niveau de classification',
                '4': "Capturer des preuves : captures d'écran, diagrammes d'architecture, contrôles d'accès",
                '5': 'Planifier des examens périodiques',
            },
        },
        DATA_D2: {
            name: 'Gouvernance du partage de données',
            steps: {
                '1': 'Évaluer les nouveaux accords de partage via une évaluation des risques liés au partage de données',
                '2': 'Workflow d’approbation interne complet',
                '3': "Enregistrer l'événement de partage dans le journal de partage/divulgation de données",
                '4': "Capturer des preuves : dossiers d'approbation, documents contractuels",
                '5': 'Examen périodique des accords de partage',
            },
        },
        DATA_D3: {
            name: 'Évaluation du transfert de données transfrontalier',
            steps: {
                '1': "Identifier les transferts transfrontaliers dans l'inventaire des données",
                '2': 'Effectuer une évaluation des risques de transfert',
                '3': 'Appliquer des mesures de sauvegarde et de gouvernance',
                '4': "Document dans la section transfrontalière de l'attestation de gouvernance",
                '5': "Dossier d'évaluation des exportations pour audit",
            },
        },
        DATA_D4: {
            name: 'Attestation annuelle de gouvernance',
            steps: {
                '1': 'Examiner la matrice RACI de gouvernance',
                '2': "Vérifier l'état de la politique (gouvernance, classification, partage, rétention)",
                '3': "Dossier d'attestation complet",
                '4': "Obtenir l'approbation interne",
                '5': "Attestation d'exportation et d'archivage",
            },
        },
        DATA_D5: {
            name: 'Gestion des incidents de données',
            steps: {
                '1': "Classer l'incident à l'aide du dossier de décision",
                '2': 'Exécuter la réponse aux incidents selon le playbook',
                '3': "Capturer des preuves : journaux, captures d'écran, communications",
                '4': 'Effectuer un examen post-incident',
                '5': 'Si une notification aux autorités est requise : créez un dossier de soumission et soumettez-le à MIC',
                '6': 'Fermer et archiver',
            },
        },
        ECOM_P1: {
            name: 'Notification (site Web/application de vente)',
            steps: {
                '1': 'Déterminer la classification : site/application de vente vs place de marché/plateforme de services',
                '2': "Créez un dossier de dossier interne avec l'identité de l'entreprise, les informations sur le site Web/l'application et les divulgations requises",
                '3': "Générer un pack de preuves : captures d'écran des divulgations, URL des politiques, historique des versions",
                '4': "Soumettre via Online.gov.vn - télécharger des documents, enregistrer l'horodatage et la confirmation",
                '5': 'Suivre le traitement – en cas de retour pour modification, saisir les raisons et soumettre à nouveau',
                '6': "Fermer : stocker la confirmation, lier les preuves et la piste d'audit",
            },
        },
        ECOM_P2: {
            name: 'Inscription (Plateformes de services / Marketplaces)',
            steps: {
                '1': 'Déterminer le modèle et les obligations de la plateforme : marché, commerce mixte et commerce social',
                '2': "Créer un dossier d'enregistrement - formulaire de candidature, programme de prestation de services, conditions générales, règles de la plateforme",
                '3': 'Approbations internes : attribuer un propriétaire juridique/de conformité, approuver la soumission',
                '4': "Soumettre via Online.gov.vn - télécharger le dossier, enregistrer l'horodatage",
                '5': 'Boucle de traitement – si renvoyé pour modification, mettre à jour et soumettre à nouveau',
                '6': 'Approbation et opérationnalisation – stocker la confirmation, maintenir des registres de conformité continus',
            },
        },
        ECOM_P3: {
            name: 'Notification de mise à jour/modification',
            steps: {
                '1': 'Identifier le déclencheur de changement — changement important apporté aux pièces de dossier',
                '2': 'Générer un package de modifications : résumé des modifications, documents mis à jour, mises à jour des preuves',
                '3': 'Soumettre la mise à jour et enregistrer le statut de soumission',
                '4': 'Suivre le traitement et clôturer',
            },
        },
        ECOM_P4: {
            name: 'Résiliation / Désactivation',
            steps: {
                '1': "Préparer le dossier de résiliation - date de cessation, motif, plan de gestion de la protection des consommateurs, preuve d'avis public",
                '2': "Soumettre la résiliation - enregistrer l'horodatage et la confirmation",
                '3': 'Fermer – stocker le statut final et les preuves',
            },
        },
        ECOM_P5: {
            name: 'Programme de marque de confiance/de notation de crédibilité',
            steps: {
                '1': 'Examen d’éligibilité et de préparation',
                '2': 'Préparer le dossier en utilisant le formulaire officiel',
                '3': 'Soumettre et suivre',
                '4': 'Maintenir des preuves de conformité continues',
            },
        },
        PDPL_P1: {
            name: 'DPIA Dépôt de la soumission à MPS',
            steps: {
                '1': 'Complétez DPIA en utilisant le cheminement du formulaire du décret 356 (Mẫu số 02a/02b)',
                '2': 'Rassembler les documents de couverture et les preuves à l’appui',
                '5': "Conserver le dossier et les preuves de soumission pour la piste d'audit",
            },
        },
        PDPL_P2: {
            name: 'Demande de transfert transfrontalier',
            steps: {
                '1': "Remplir le dossier de transfert transfrontalier à l'aide des formulaires du décret 356 (Mẫu số 01a/01b, Mẫu số 09)",
            },
        },
        PDPL_P3: {
            name: 'Demande de certificat de service de traitement de données',
            steps: {
                '1': 'Préparer le dossier de demande de certification conformément aux exigences du décret 356',
                '2': 'Soumettre la candidature à MPS avec les pièces justificatives requises',
                '3': 'Répondre aux commentaires de révision et aux demandes de supplément MPS',
            },
        },
        PDPL_P4: {
            name: 'Traitement des demandes de droits des personnes concernées',
            steps: {
                '1': "Recevoir et enregistrer la demande ; vérifier l'identité de la personne concernée dans les 2 jours ouvrables",
                '2': 'Évaluer le type de demande et déterminer le délai applicable (10/15/20 jours par type)',
                '3': "Exécuter des actions d'exécution dans les systèmes et départements concernés",
                '4': 'Préparer et envoyer la réponse à la personne concernée avec le résultat et la justification',
                '5': "Clôturer le dossier et conserver les preuves pour l'audit ; documenter toute justification du refus",
            },
        },
        PDPL_P5: {
            name: 'Notification de violation à MPS',
            steps: {
                '1': "Détecter et évaluer la violation ; déterminer si l'obligation de notification s'applique et établir le délai de notification applicable à partir des enregistrements de délais installés",
                '2': 'Préparer le package de notification conformément aux exigences de PDPL',
                '3': 'Envoyer la notification à MPS dans le délai de notification établi à partir des enregistrements de délais installés, décompté depuis la découverte',
                '4': 'Répondre aux MPS demandes de suivi ; soumettre des suppléments et des preuves de coordination',
            },
        },
        TELECOM_T1: {
            name: "Traitement des demandes d'autorité",
            steps: {
                '1': "Recevoir et enregistrer la demande d'autorisation via le dossier d'admission",
                '2': "Vérifier l'identité de l'autorité, la base juridique et la portée de la demande",
                '3': 'Effectuer un examen juridique interne et obtenir l’approbation de la direction',
                '4': 'Préparer le dossier de réponse avec les preuves rassemblées',
                '5': 'Rédiger et approuver la lettre de réponse',
                '6': 'Créer un dossier de soumission et soumettre la réponse via le canal',
                '7': "Enregistrer la divulgation et suivre la reconnaissance de l'autorité",
                '8': "Clôturer le dossier et conserver les preuves pour la piste d'audit",
            },
        },
        TELECOM_T2: {
            name: 'Gestion de la divulgation',
            steps: {
                '1': "Recevoir une demande de divulgation (consentement de l'utilisateur, obligation légale ou demande d'autorité)",
                '2': 'Flux de travail complet d’approbation de la divulgation',
                '3': 'Exécuter la divulgation et enregistrer dans le registre des journaux',
                '4': "Capturer des preuves - dossiers d'approbation, dossiers de communication",
                '5': 'Examen périodique du journal de divulgation',
            },
        },
        TELECOM_T3: {
            name: "Politique de confidentialité et configuration du contrôle d'accès",
            steps: {
                '1': 'Rédiger et approuver la politique de confidentialité',
                '2': 'Rédiger et approuver la politique de contrôle d’accès et de journalisation',
                '3': 'Activités de traitement de documents dans le registre de traitement des informations sur les utilisateurs',
                '4': 'Capturer des preuves – documents politiques, preuves de mise en œuvre',
                '5': 'Planifier des examens périodiques',
            },
        },
        TELECOM_T4: {
            name: 'Évaluation des incidents de télécommunications',
            steps: {
                '1': "Classer et évaluer l'incident",
                '2': 'Déterminer les exigences de notification',
                '3': 'Si une notification aux autorités est requise : créer un dossier de soumission',
                '4': 'Capturer et conserver les preuves',
                '5': 'Résolution et clôture',
            },
        },
        TELECOM_T5: {
            name: 'Gestion de la conservation et de la suppression',
            steps: {
                '1': 'Définir le calendrier de conservation et de suppression par catégorie de données',
                '2': 'Mettre en œuvre les procédures de suppression',
                '3': 'Capturer des preuves – supprimer des enregistrements',
                '4': 'Révision périodique des plannings',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Inscription / Notification / Préparation administrative',
            'ECOM-FINE-B': 'Plaintes et protection des consommateurs',
            'ECOM-FINE-C': 'Surveillance et application du retrait',
            'ECOM-FINE-D': 'Opérations transfrontalières',
            'ECOM-FINE-E': 'Règles de la plateforme et transparence',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Échecs de traitement des demandes d’autorité',
            'TELECOM-FINE-B': 'Confidentialité et divulgation non autorisée',
            'TELECOM-FINE-C': 'Échecs du contrôle d’accès et de la journalisation',
            'TELECOM-FINE-D': 'Violations de conservation et de suppression',
            'TELECOM-FINE-E': 'Échecs de l’évaluation des incidents de télécommunications',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: "Jusqu'à 100 millions de VND",
                fine_org: "Jusqu'à 200 millions de VND",
                deadlines: 'Immédiatement après la détection',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: "Jusqu'à 100 millions de VND",
                fine_org: "Jusqu'à 200 millions de VND",
                deadlines: 'Obligation continue',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: "Jusqu'à 100 millions de VND",
                fine_org: "Jusqu'à 200 millions de VND",
                deadlines: 'Obligation continue',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: "Jusqu'à 100 millions de VND",
                fine_org: "Jusqu'à 200 millions de VND",
                deadlines: 'Obligation continue',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: "Jusqu'à 100 millions de VND",
                fine_org: "Jusqu'à 200 millions de VND",
                deadlines: "Comme spécifié dans la demande d'autorisation",
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: "Jusqu'à 80 millions de VND",
                fine_org: "Jusqu'à 160 millions de VND",
                deadlines: 'Obligation continue',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: "Jusqu'à 80 millions de VND",
                fine_org: "Jusqu'à 160 millions de VND",
                deadlines: 'Obligation continue',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: "Jusqu'à 80 millions de VND",
                fine_org: "Jusqu'à 160 millions de VND",
                deadlines: 'Obligation continue',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: "Jusqu'à 80 millions de VND",
                fine_org: "Jusqu'à 160 millions de VND",
                deadlines: 'Comme spécifié par les obligations de traitement des réclamations',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: "Jusqu'à 80 millions de VND",
                fine_org: "Jusqu'à 160 millions de VND",
                deadlines: 'Avant de commencer les opérations de commerce électronique',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Fonctionnement continu de la plateforme',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Opérateur de plateforme / Risque',
                '2': 'Opérateur de plateforme / Sécurité',
                '3': 'Opérateur de plateforme / Risque',
                '4': 'Opérateur de plateforme / Sécurité',
                '5': 'Opérateur de plateforme',
                '6': 'Conformité',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Un participant dépose une réclamation ou une demande de contestation',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Opérateur de plateforme',
                '2': 'Opérateur de plateforme',
                '3': 'Opérateur de plateforme',
                '4': 'Opérateur de plateforme',
                '5': 'Opérateur de plateforme / Juridique',
                '6': 'Opérateur de plateforme',
                '7': 'Conformité',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'La livraison est terminée une fois le contrat et les conditions de paiement remplies',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Vendeur / Opérateur de plateforme',
                '2': 'Opérateur de plateforme',
                '3': 'Opérateur de plateforme',
                '4': 'Opérateur de plateforme',
                '5': 'Opérateur de plateforme',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Un acheteur initie une transaction pour des données, un produit ou un service',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Acheteur',
                '2': 'Acheteur / Opérateur de plateforme',
                '3': 'Opérateur de plateforme',
                '4': 'Acheteur / Vendeur',
                '5': 'Opérateur de plateforme / Paiement',
                '6': 'Opérateur de plateforme',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Un participant souhaite évaluer des produits/services de données dans un environnement isolé',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Participant',
                '2': 'Opérateur de plateforme / Participant',
                '3': 'Opérateur de plateforme / informatique',
                '4': 'Opérateur de plateforme / Sécurité',
                '5': 'Opérateur de plateforme',
                '6': 'Opérateur de plateforme',
                '7': 'Opérateur de plateforme / informatique',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Un vendeur cherche à répertorier des données, des produits ou des services de données à des fins de trading.',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Vendeur',
                '2': 'Vendeur / Mentions légales',
                '3': 'Vendeur / informatique',
                '4': 'Vendeur',
                '5': 'Vendeur',
                '6': 'Opérateur de plateforme',
                '7': 'Opérateur de plateforme',
                '8': 'Opérateur de plateforme',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Un vendeur ou un acheteur rejoint la plateforme de données',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Opérateur de plateforme',
                '2': 'Opérateur de plateforme / Juridique',
                '3': 'Opérateur de plateforme / Juridique',
                '4': 'Opérateur de plateforme',
                '5': 'Opérateur de plateforme',
                '6': 'Opérateur de plateforme',
                '7': 'Conformité',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Début d’un traitement de données personnelles nécessitant une analyse d’impact',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Équipe de confidentialité',
                '2': 'Équipe de confidentialité',
                '3': 'Juridique / Conformité',
                '4': 'DPO / Équipe de confidentialité',
                '5': 'Équipe de conformité',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Intention de transférer des données personnelles en dehors du Vietnam',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Équipe de confidentialité',
                '2': 'Juridique / Conformité',
                '3': 'DPO / Équipe de confidentialité',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organisme fournissant des services de traitement de données nécessitant une certification',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Juridique / Conformité',
                '2': 'Juridique / Conformité',
                '3': 'DPO / Équipe de confidentialité',
                '4': 'Équipe de conformité',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: "Réception d'une demande de droits de la personne concernée",
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Équipe de confidentialité / Service client',
                '2': 'DPO / Équipe de confidentialité',
                '3': 'Chefs de département / TI',
                '4': 'DPO / Équipe de confidentialité',
                '5': 'Équipe de conformité',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Découverte d’une violation ou d’un manquement à la protection des données personnelles',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Équipe de réponse aux incidents / DPO',
                '2': 'DPO / Équipe de confidentialité',
                '3': 'Juridique / Conformité',
                '4': 'DPO / Équipe de réponse aux incidents',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Décret 13 Art. 24',
        'MoPS Admin Procedures': "Procédures d'administration MoPS",
        'Annual cycle': 'Cycle annuel',
        'Decree 13/2023/NĐ-CP Art. 24': 'Décret 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Décret 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Décret 356/2025/NĐ-CPĐ-CP Art. 5 cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Décret 356/2025/NĐ-CPĐ-CP Art. 5 cl. 2 à 5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Décret 356/2025/NĐ-CPĐ-CP Art. 5 cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Décret 356/2025/NĐ-CPĐ-CP Art. 5 cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Décret 356/2025/NĐ-CPĐ-CP Art. 5 cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Décret 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Projet de décret sur les plateformes de données 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Loi 122/2025/QH15 Ch. III ; NĐ 98/2020 ; 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Loi 122/2025/QH15 Ch. IV (Protection des consommateurs)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Loi 122/2025/QH15 — Procédure opérationnelle Online.gov.vn',
        'Law 134/2025/QH15 Article 12': 'Loi 134/2025/QH15 Article 12',
        'Law 134/2025/QH15 Article 21': 'Loi 134/2025/QH15 Article 21',
        'Law 134/2025/QH15 Article 27': 'Loi 134/2025/QH15 Article 27',
        'Law 134/2025/QH15 Articles 9–10': 'Loi 134/2025/QH15 Articles 9 à 10',
        'Law 24/2023/QH15 — article mapping pending': "Loi 24/2023/QH15 — mappage d'article en attente",
        'Law 60/2024/QH15 (internal control target)': 'Loi 60/2024/QH15 (cible du contrôle interne)',
        'Law 91/2025/QH15 Art. 21': 'Loi 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Loi 91/2025/QH15 Art. 23',
        'Policy-defined': 'Défini par la stratégie',
        Quarterly: 'Trimestriel',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'Procédure administrative QĐ 778/QĐ-BCA-A05',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': "objectif de contrôle interne (Loi 116/2025/QH15 Art. 24(2)(b), 41(5) — calendrier exact en attendant l'instrument de mise en œuvre)",
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': "objectif du contrôle interne (Loi 116/2025/QH15 Art. 41(3) — calendrier exact en attendant l'instrument de mise en œuvre)",
        'source verification pending': 'vérification de la source en attente',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C) [FR]',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A) [FR]",
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A) [FR]',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A) [FR]',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C) [FR]',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D) [FR]',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D) [FR]',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D) [FR]',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C) [FR]',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B) [FR]',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C) [FR]',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A) [FR]',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Internal Audit Governance [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C) [FR]',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D) [FR]',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management Information System (Layer 4) [FR]',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3) [FR]',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G) [FR]',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G) [FR]',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Outsourcing Controls [FR]',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G) [FR]',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G) [FR]',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G) [FR]',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G) [FR]',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C) [FR]',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01 [FR]',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5) [FR]',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1) [FR]',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C) [FR]',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A) [FR]',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E) [FR]',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01 [FR]',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A) [FR]',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F) [FR]',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B) [FR]',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2) [FR]',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D) [FR]',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Assemblée nationale)',
        'Ministry of Public Security / A05 Department': 'Ministère de la Sécurité Publique / Département A05',
        'Ministry of Public Security': 'Ministère de la Sécurité Publique',
        'Ministry of Information and Communications': "Ministère de l'Information et des Communications",
        'Government of Vietnam': 'Gouvernement du Vietnam',
        'European Data Protection Board (EDPB)': 'Comité européen de la protection des données (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / CSIRT nationaux',
        'State Bank of Vietnam': "Banque d'État du Vietnam",
    },
    tour_titles: {
        access_accountability: 'Visite guidée de la responsabilité d’accès',
        assessments: 'Visite des évaluations',
        audit_trail: "Visite de la piste d'audit",
        classification: 'Tournée de classement',
        compliance_forms: 'Visite guidée des formulaires de conformité',
        connectors: 'Visite des connecteurs',
        consent_properties: 'Visite guidée des propriétés du consentement',
        consent: 'Visite guidée du consentement',
        dashboard: 'Visite du tableau de bord',
        data_mapping: 'Visite de cartographie des données',
        data_subjects: 'Visite des personnes concernées',
        deletion_orchestration: "Visite guidée de l'orchestration des suppressions",
        discovery: 'Circuit découverte',
        governance: 'Visite de la gouvernance',
        help_center: "Visite guidée du centre d'aide",
        incident_ops: "Visite des opérations en cas d'incident",
        incidents: 'Visite des incidents',
        localization: 'Visite de localisation',
        monitoring: 'Visite de surveillance',
        onboarding: "Visite d'intégration",
        organizations: 'Visite des organisations',
        partner_workspace: "Visite de l'espace de travail des partenaires",
        platform_settings: 'Visite guidée des paramètres de la plateforme',
        privacy_scanner: 'Visite guidée du scanner de confidentialité',
        program_governance: 'Visite de la gouvernance du programme',
        rights_requests: 'Visite des demandes de droits',
        task_management: 'Visite guidée de la gestion des tâches',
        vendor_risk: 'Visite guidée des risques liés aux fournisseurs',
    },
    tour_descriptions: {
        access_accountability: "En tant que responsable de l'audit interne (P-VN-10), examiner les preuves d'accès, auditer l'utilisation des autorisations et enquêter sur les modèles anormaux pour démontrer l'intégrité et la confidentialité de l'GDPR Article 5(1)(f).",
        assessments: "En tant qu'entreprise DPO (P-VN-01), mener des DPIA conformément au décret 356 Mau so 02a, identifier les lacunes en matière de conformité et évaluer les risques de transfert transfrontalier en vertu de PDPL.",
        audit_trail: "En tant que responsable de l'audit interne (P-VN-10), interrogez les preuves inter-modules, assemblez des packs d'audit et exportez des journaux inviolables pour les soumissions réglementaires.",
        classification: 'En tant que Data Steward (P-VN-05), définissez les taxonomies de classification des données, attribuez des étiquettes de sensibilité, appliquez les politiques de conservation et résolvez les écarts de couverture requis pour la conformité de PDPL.',
        compliance_forms: "En tant qu'Entreprise DPO (P-VN-01), parcourez, complétez et soumettez les formulaires réglementaires vietnamiens officiels, notamment les modèles Phu luc I, Phu luc II et Mau so pour les dépôts auprès du Comité PDP.",
        connectors: "En tant que responsable des systèmes informatiques (P-VN-05), capturez les exigences commerciales pour les intégrations, mappez les connecteurs aux flux de données de conformité et vérifiez l'état de préparation du POC avant la mise en ligne.",
        consent_properties: "En tant qu'entreprise DPO (P-VN-01), configurez le déploiement de CMP : enregistrement de la propriété numérique, modèles dorés pour des bannières de consentement PDPL cohérentes et matrices de liaison qui lient les propriétés aux activités de traitement.",
        consent: "En tant qu'entreprise DPO (P-VN-01), gérez le cycle de vie de la politique de consentement : définissez les objectifs de collecte, appliquez les droits de retrait de PDPL et intégrez-les à votre plateforme de gestion du consentement.",
        dashboard: "En tant qu'entreprise DPO (P-VN-01), orientez-vous autour du centre de commande de posture de conformité qui fait apparaître les délais PDPL, les violations des SLA et les KPI de préparation à l'audit.",
        data_mapping: 'En tant que Data Steward (P-VN-05), construisez votre inventaire ROPA, cartographiez les flux de données transfrontaliers et maintenez le registre des activités de traitement requis par PDPL.',
        data_subjects: "En tant que responsable de la conformité de l'entreprise DPO ou de l'éducation, gérez les profils unifiés des personnes concernées qui relient les enregistrements de consentement, les demandes de droits et l'activité chronologique sur l'ensemble de la plateforme.",
        deletion_orchestration: "En tant que DPO (P-VN-01), vérifiez la suppression des données de bout en bout dans les systèmes internes et les fournisseurs tiers, avec des chaînes de preuve cryptographiques pour la conformité à l'article 17 du droit à l'effacement de GDPR.",
        discovery: "En tant que Data Steward (P-VN-05), exécutez des analyses automatisées pour découvrir des données personnelles sur les systèmes connectés, examinez les résultats par étapes et alimentez les résultats dans le module de classification pour l'étiquetage PDPL.",
        governance: 'En tant que gestionnaire des risques des fournisseurs (P-VN-08), maintenir le registre des fournisseurs, suivre les chaînes de sous-traitants, évaluer les risques de transfert transfrontalier et appliquer les manuels de gouvernance sous PDPL.',
        help_center: 'Pour tous les acteurs (DPD, auditeurs, responsables informatiques et responsables de la conformité), trouvez des réponses, des guides pratiques et une documentation spécifique aux modules pour prendre en charge vos flux de travail de conformité quotidiens.',
        incident_ops: "En tant que commandant de l'incident (P-VN-09), activez les playbooks de réponse, suivez la conformité de SLA par rapport à la fenêtre de notification de 72 heures PDPL, gérez les escalades et coordonnez la réponse des fournisseurs.",
        incidents: "En tant que commandant des incidents (P-VN-09), détecter et signaler les violations de données avec des obligations de notification PDPL dans les 72 heures au comité PDP, trier par gravité et constituer des dossiers d'incidents de niveau audit.",
        localization: "En tant qu'entreprise DPO (P-VN-01), gérer le cycle de vie du cadre réglementaire : installer les packs du marché vietnamien, activer les cadres PDPL et le décret 13, mapper les exigences aux modules de la plateforme et gérer les traductions.",
        monitoring: "En tant qu'entreprise DPO (P-VN-01), établissez des programmes continus de surveillance de la conformité, suivez les modifications réglementaires, mesurez les KPI et gérez les cas d'interaction réglementaire sous PDPL.",
        onboarding: "En tant que formateur en conformité (P-VN-02), dispensez une formation via des visites guidées, chargez des exemples de données réalistes pour des exercices pratiques et exécutez l'assistant de configuration pour des scénarios de démarrage rapide.",
        organizations: "En tant que DPO (P-VN-01), configurez des structures de conformité multi-organisations, établissez des hiérarchies de services et acheminez les demandes des personnes concernées au sein de votre groupe d'entreprise.",
        partner_workspace: 'En tant que responsable de compte partenaire, intégrez les organisations partenaires, configurez les niveaux de service, suivez les livrables de conformité et gérez le marché des partenaires.',
        platform_settings: "En tant qu'administrateur de plateforme, gérez toute la configuration opérationnelle : identité, sécurité, intégrations et accès aux modules qui sous-tendent votre programme de conformité.",
        privacy_scanner: "En tant qu'analyste de la confidentialité (P-VN-06), exécutez des analyses de surveillance continue pour détecter les violations du consentement aux cookies, les trackers non autorisés et les lacunes des politiques, générant ainsi des preuves de la conformité de l'ePrivacy et de GDPR.",
        program_governance: 'Naviguez entre les modèles de maturité, les évaluations, les lacunes en matière de données probantes, les résumés exécutifs, et bien plus encore. Chaque sous-module contribue à une vue holistique de la santé interne de votre programme.',
        rights_requests: "En tant qu'entreprise DPO (P-VN-01), gérez les demandes de droits des personnes concernées sous PDPL avec des délais légaux de 15 jours, une vérification d'identité et des pistes de résolution de niveau audit.",
        task_management: 'Acheminez les tâches de conformité entre les personnages, suivez les délais SLA et automatisez les flux de travail qui responsabilisent votre équipe.',
        vendor_risk: 'En tant que gestionnaire des risques des fournisseurs (P-VN-08), évaluez les risques liés aux tiers, appliquez les examens de contrôle et déclenchez des réévaluations - distincts de la gouvernance du programme, qui suit votre maturité interne.',
    },
    consolidation: {
        title: 'Dossiers de consolidation',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Créer un dossier de consolidation',
        case_count: 'case',
        created: 'Dossier de consolidation créé',
        create: 'Créer un cas',
        creating: 'Création...',
        cancel: 'Annuler',
        no_cases: 'Aucun cas de consolidation trouvé.',
        all_statuses: 'Tous les statuts',
        all_purposes: 'Tous usages',
        filter_status: 'Filtrer par statut',
        filter_purpose: 'Filtrer par objectif',
        col_title: 'Titre',
        col_purpose: 'Objectif',
        col_status: 'Statut',
        col_progress: 'Progrès',
        col_deadline: 'Date limite',
        col_department: 'Département',
        col_version: 'Version',
        col_evidence: 'Preuve',
        sections_accepted: 'accepted',
        prev: 'Précédent',
        next: 'Suivant',
        page: 'Pages',
        prev_page: 'Page précédente',
        next_page: 'Page suivante',
        field_title: 'Titre',
        field_purpose: 'Objectif',
        field_framework: 'Code-cadre',
        field_deadline: 'Date limite',
        field_reviewer: 'ID utilisateur du réviseur',
        title_placeholder: 'Entrez le titre du cas',
        framework_placeholder: 'par exemple, PDPD-2023',
        reviewer_placeholder: "Saisir l'ID utilisateur du réviseur",
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Dépôt',
            cross_border_filing: 'Dépôt transfrontalier',
            annual_governance_attestation: 'Attestation annuelle de gouvernance',
            breach_package: 'Forfait infraction',
            audit_inspection_pack: "Pack d'inspection d'audit",
        },
        statuses: {
            draft: 'Brouillon',
            collecting: 'Collecte',
            consolidating: 'Consolidation',
            review: 'Examen',
            corrections: 'Corrections',
            pending_approval: "En attente d'approbation",
            approved: 'Approuvé',
            completed: 'Terminé',
            closed: 'Fermé',
            rejected: 'Rejeté',
        },
        section_statuses: {
            pending: 'En attente',
            in_progress: 'En cours',
            submitted: 'Soumis',
            accepted: 'Accepté',
            needs_correction: "Besoin d'une correction",
            corrected: 'Corrigé',
        },
        flag_types: {
            missing_section: 'Section manquante',
            conflicting_entry: 'Entrée conflictuelle',
            stale_entry: 'Entrée périmée',
            missing_evidence: 'Preuve manquante',
            incomplete_data: 'Données incomplètes',
            duplicate_entry: 'Entrée en double',
        },
        flag_severities: {
            critical: 'Critique',
            warning: 'Avertissement',
            info: 'Informations',
        },
        sections_title: 'Sections',
        no_sections: "Aucune section attribuée pour l'instant.",
        flags_title: 'Drapeaux',
        no_flags: 'Aucun indicateur détecté.',
        open: 'open',
        approval_chain: "Chaîne d'approbation",
        pending: 'En attente',
        audit_trail: "Piste d'audit",
        no_audit: "Aucun événement d'audit.",
        auto_assign: 'Attribution automatique des sections',
        start_collection: 'Commencer la collecte',
        review_case: 'Cas de révision',
        generate_export: 'Générer une exportation',
        review_title: 'Revue de consolidation',
        advance_approval: "Avancer jusqu'à l'approbation",
        back_to_case: 'Retour au cas',
        accept: 'Accepter',
        return_section: 'Retour',
        return_comment: 'Retourner le commentaire...',
        resolve: 'Résoudre',
        dismiss: 'Rejeter',
        resolution_notes: 'Notes de résolution...',
        notes: 'Remarques',
        section_accepted: 'Section acceptée',
        section_returned: 'Section renvoyée pour correction',
        advanced_to_approval: "Dossier avancé en attente d'approbation",
        section_data: 'Données de section',
        save_progress: 'Enregistrer la progression',
        submit_section: 'Soumettre la section',
        submitting: 'Envoi...',
        resubmit: 'Soumettre à nouveau',
        progress_saved: 'Progrès enregistré',
        section_submitted: 'Section soumise',
        correction_required: 'Correction requise',
        evidence_title: 'Preuve',
        no_evidence: 'Aucune preuve jointe.',
        add_evidence: 'Ajouter des preuves',
        evidence_added: 'Preuve ajoutée',
        evidence_files: 'dossiers de preuves',
        uploaded_by: 'téléchargé par',
        comments_title: 'Commentaires',
        no_comments: 'Pas encore de commentaires.',
        comment_placeholder: 'Ajouter un commentaire...',
        add_comment: 'Ajouter un commentaire',
        comment_added: 'Commentaire ajouté',
        view_data: 'Afficher les données soumises',
        export_title: 'Exportation de dossiers',
        export_generated: "Package d'exportation généré",
        regenerate: 'Régénérer',
        regenerating: 'Régénérant...',
        generated_at: 'Généré à',
        artifacts_title: 'Exporter des artefacts',
        artifact_type: 'Artefact',
        file_name: 'Nom du fichier',
        file_size: 'Taille',
        generated: 'Généré',
        download: 'Télécharger',
        artifacts: {
            master_report: 'Rapport principal',
            evidence_bundle: 'Ensemble de preuves (ZIP)',
            contributor_lineage: 'Lignée des contributeurs',
            conflict_resolution: 'Résolution des conflits',
            approval_history: 'Historique des approbations',
        },
        official_forms: 'Formulaires officiels',
        forms_description: 'Les formulaires officiels suivants peuvent être générés à partir des données consolidées. Les formulaires officiels sont distincts des documents de travail internes.',
        announce_loaded_cases: 'Cas de consolidation {count} chargés',
        announce_case_detail_loaded: 'Détails du dossier chargés',
        announce_case_transitioned: 'Requête transférée vers {status}',
        announce_flag_resolved: 'Indicateur résolu',
        announce_flag_dismissed: 'Drapeau rejeté',
        case_detail_loaded: 'Détails du dossier chargés',
        sections_auto_assigned: 'Sections attribuées automatiquement à partir de la hiérarchie des départements',
        errors: {
            load_failed: 'Échec du chargement des données. Veuillez réessayer.',
            create_failed: 'Échec de la création du dossier. Veuillez réessayer.',
            required_fields: 'Veuillez remplir tous les champs obligatoires.',
            not_found: 'Pas trouvé.',
            comment_required: 'Un commentaire est requis lors du retour d’une section.',
            notes_required: 'Des notes sont requises lors de la résolution ou du rejet d’un signalement.',
        },
    },
    rights_requests: {
        import: {
            title: 'Importer des dossiers',
            nav_title: 'Import en masse',
            description: 'Téléversez un fichier CSV pour importer en masse les dossiers de demande de droits.',
            select_file: 'Sélectionner le fichier CSV',
            file_help: 'CSV avec des en-têtes correspondant aux champs de dossier.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Fichier trop volumineux. La taille maximale est de 2 Mo.',
            preview: 'Aperçu',
            columns_detected: 'Colonnes détectées : {count}',
            column_mapping: 'Correspondance des colonnes',
            import_button: 'Importer',
            import_complete: 'Import terminé',
            import_error: "L'import a échoué",
            result: "Résultat de l'import",
            total: 'Total',
            imported: 'Importé',
            skipped: 'Ignoré',
            failed: 'Échoué',
            errors: 'Erreurs',
            error_row: 'Ligne',
            error_field: 'Champ',
            error_message: 'Message',
            history: "Historique d'import",
            no_history: "Aucun historique d'import.",
            history_file: 'Nom de fichier',
            history_date: 'Date',
        },
        batch: {
            nav_title: 'Processus par lots',
            page_title: 'Processus par lots DSR',
            page_subtitle: "Téléchargez un CSV de DSR en attente. Forseti rédige une réponse d'accusé de réception par demande et met en file d'attente une tâche de vérification par ligne. Le DPO examine + accepte chacun avant l'envoi.",
            upload_heading: 'Télécharger',
            csv_hint: 'Colonnes obligatoires : request_id, requester_email, request_type. Facultatif : date limite (ISO-8601), notes.',
            rows_heading: '{count} lignes analysées',
            submit_label: 'Exécuter un lot',
            submitting: 'Rédaction…',
            col_request_id: 'Identifiant de la demande',
            col_email: 'E-mail du demandeur',
            col_type: 'Tapez',
            col_status: 'Statut',
            col_actions: 'Actions',
            status_queued: "En file d'attente",
            status_drafted: 'Rédigé',
            status_failed: 'Échec',
            status_accepted: 'Accepté',
            status_rejected: 'Rejeté',
            status_parsing_error: "Erreur d'analyse",
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Accepter',
            reject: 'Rejeter',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Déclencheurs d’évaluation',
        subtitle: "Configurez la création automatisée d'évaluations et les notifications DPO pour les workflows de confidentialité.",
        auto_create_dpia_label: "Créer automatiquement DPIA lors du traitement d'une activité marquée comme DPIA requise",
        auto_notify_new_purpose_label: "Notifier automatiquement DPO lorsqu'un nouvel objectif est créé avec des catégories de données sensibles",
        auto_notify_cross_border_label: "Notifier automatiquement DPO lorsqu'un transfert transfrontalier est ajouté à l'activité de traitement",
        default_dpo_label: 'Par défaut DPO',
        default_dpo_placeholder: 'Sélectionnez DPO par défaut...',
        notification_channels_label: 'Canaux de notification',
        channel_in_app: "Dans l'application",
        channel_email: 'Email',
        save_success: 'Paramètres de déclenchement d’évaluation enregistrés.',
        save_error: "Échec de l'enregistrement des paramètres du déclencheur d'évaluation.",
        fetch_error: "Échec du chargement des paramètres du déclencheur d'évaluation.",
        fetch_users_error: 'Échec du chargement des utilisateurs.',
        announce_loaded: "Paramètres de déclenchement d'évaluation chargés",
        dpia_status: 'DPIA Statut',
        dpia_linked: 'DPIA Lié',
        dpia_required_no_dpia: 'DPIA Obligatoire — Non créé',
        create_dpia: 'Créer DPIA',
        create_dpia_success: "DPIA créé et lié à l'activité.",
        create_dpia_error: "Échec de la création de DPIA pour l'activité.",
        view_dpia: 'Afficher DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Profils des personnes concernées',
        nav_title: 'Personnes concernées',
        nav: {
            profiles: 'Profils',
        },
        create: 'Créer un profil',
        edit: 'Modifier le profil',
        delete: 'Supprimer le profil',
        delete_confirm: 'Êtes-vous sûr de vouloir supprimer ce profil de personne concernée ?',
        display_name: "Nom d'affichage",
        email: 'Email',
        phone: 'Téléphone',
        employee_id: "ID d'employé",
        student_id: "Carte d'étudiant",
        notes: 'Remarques',
        external_refs: 'Références externes',
        cases_count: 'Cas',
        workflows_count: 'Dossiers de consentement',
        last_activity: 'Dernière activité',
        timeline: 'Chronologie',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Aucun profil de personne concernée trouvé.',
        search_placeholder: 'Recherchez par nom, email, téléphone ou identifiant...',
        create_success: 'Profil de la personne concernée créé.',
        create_error: 'Échec de la création du profil de la personne concernée.',
        update_success: 'Profil de la personne concernée mis à jour.',
        update_error: 'Échec de la mise à jour du profil de la personne concernée.',
        delete_success: 'Profil de la personne concernée supprimé.',
        delete_error: 'Échec de la suppression du profil de la personne concernée.',
        link_case_success: 'Cas lié au profil.',
        link_case_error: 'Échec de la liaison du dossier au profil.',
        link_workflow_success: 'Workflow lié au profil.',
        link_workflow_error: 'Échec de la liaison du flux de travail au profil.',
        load_error: 'Échec du chargement des profils des personnes concernées.',
        detail_load_error: 'Échec du chargement du profil de la personne concernée.',
        timeline_load_error: 'Échec du chargement de la chronologie.',
        tab_cases: 'Cas DSR',
        tab_consent: 'Dossiers de consentement',
        tab_timeline: 'Chronologie',
        tab_consent_workflows: 'Flux de travail de consentement',
        tab_web_consent: 'CMP Consentement',
        web_consent_empty: 'Aucun enregistrement de consentement CMP trouvé pour ce sujet.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Échec du chargement de CMP enregistrements de consentement.',
        col_policy_version: 'Version de la politique',
        col_versions: 'Versions',
        col_consent_version: 'Consentement',
        col_cookie_declaration_version: 'Déclaration relative aux cookies',
        col_purposes: 'Objectifs',
        purpose_granted: 'Accordé',
        purpose_denied: 'Refusé',
        granted_domains: 'Accordé',
        denied_domains: 'Refusé',
        col_action: 'Action',
        col_timestamp: 'Horodatage',
        col_domain: 'Domaine',
        no_cases: 'Aucun cas lié.',
        no_workflows: 'Aucun workflow de consentement lié.',
        no_timeline: 'Aucun événement chronologique.',
        case_id: 'Numéro de cas',
        case_status: 'Statut',
        case_type: 'Type de demande',
        case_created: 'Créé',
        workflow_id: 'ID de flux de travail',
        workflow_type: 'Tapez',
        workflow_status: 'Statut',
        workflow_created: 'Créé',
        timeline_date: 'Date',
        timeline_event: 'Événement',
        timeline_details: 'Détails',
        timeline_time: 'Temps',
        timeline_type: 'Tapez',
        timeline_summary: 'Résumé',
        type_case: 'Cas DSR',
        type_consent_workflow: 'Flux de travail de consentement',
        profile_info: 'Informations sur le profil',
        announce_loaded: 'Profils de personnes concernées chargés',
        announce_detail_loaded: 'Profil de la personne concernée chargé',
        select_case: '— Sélectionner un dossier —',
        select_workflow: '— Sélectionner un flux de travail —',
        no_cases_hint: 'Liez des cas pour les voir ici.',
        no_workflows_hint: 'Liez des flux de consentement pour les voir ici.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} systèmes · {flows} flux · {crossBorder} transferts hors UE · {highRisk} risque élevé/critique',
            switch_to_list: 'Passer à la vue liste',
            switch_to_list_aria: 'Passer à la vue tableau',
            svg_aria: 'Carte des flux de données : {systems} systèmes, {flows} flux, {crossBorder} transferts hors UE',
            selected_system: 'Système sélectionné',
            drill_hint: 'Ouvrez l’onglet « Liste » pour modifier les flux, AIPD et affectations de fournisseurs liés à ce système.',
        },
    },
    packages: {
        light: {
            name: 'Lumière',
            description: 'Outils essentiels de conformité à la confidentialité pour les petites équipes',
        },
        standard: {
            name: 'Norme',
            description: 'Plateforme de conformité complète pour les organisations en croissance',
        },
        enterprise_unlimited: {
            name: 'Entreprise illimitée',
            description: 'Conformité de niveau entreprise avec prise en charge multi-organisations et services dédiés',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Paramètres du fournisseur',
            subtitle: 'Configurez le fournisseur Claude pour le conseiller juridique Forseti AI.',
            healthHeading: 'Santé du prestataire',
            statusLabel: 'Statut',
            statusOk: "D'accord",
            statusFailed: 'Indisponible',
            providerLabel: 'Fournisseur',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Relais Claude CLI',
            providerAesirManaged: 'Jetons gérés par Aesir',
            lastCheckedLabel: 'Dernière vérification',
            reasonLabel: 'Raison',
            refreshButton: 'Actualiser',
            apiKeyHeading: 'Claude API clé',
            apiKeyHint: "La définition d'une touche API active le mode Claude API. Laissez non défini pour utiliser le relais CLI local (mode développeur/auto-hébergé).",
            apiKeyLabel: 'Touche API',
            apiKeyMinLength: 'Minimum 32 caractères. Chiffré au repos ; jamais montré à nouveau.',
            apiKeyTooShort: 'La clé API doit comporter au moins 32 caractères.',
            apiKeySaved: 'Clé API enregistrée. Une santé rafraîchissante…',
            apiKeyCleared: 'Touche API effacée. Forseti utilise désormais le relais CLI local.',
            setKeyButton: 'Enregistrer la clé API',
            clearKeyButton: 'Effacer la touche API (utiliser le relais CLI)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Utilisation',
            recentHeading: 'Activité récente',
            historyHeading: '12 dernières périodes',
            noActivity: "Aucune activité pour l'instant cette période.",
            featureLabel: 'Caractéristique',
            whenLabel: 'Quand',
            periodLabel: 'Période',
            byFeatureHeading: 'Ce mois-ci par fonctionnalité',
            byFeatureEmpty: "Aucune activité de fonctionnalité pour l'instant cette période.",
            featureColumn: 'Caractéristique',
            featureUnitsColumn: 'Unités',
            featureRunsColumn: 'Fonctionne',
            byUserHeading: 'Ce mois-ci par utilisateur (vue administrateur)',
            byUserEmpty: "Aucune activité par utilisateur pour l'instant cette période.",
            userColumn: 'Utilisateur',
            reconciliationLinkLabel: 'Afficher le rapport de dérive de rapprochement',
            reconciliationLinkHint: 'Administrateur interne à Aesir uniquement.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Discuter',
                workflow: 'Exécution du flux de travail',
                tabular: 'Extraction tabulaire',
                redline: 'Examen de la ligne rouge',
                title: 'Titre de la conversation',
                unknown: 'Autre',
            },
        },
        reconciliation: {
            title: 'Rapprochement des quotas Forseti',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: "Retour à l'utilisation de Forseti",
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: "Cette page est réservée aux administrateurs internes d'Aesir. Si vous pensez que vous devriez avoir accès, contactez votre représentant AesirX.",
        },
        projects: {
            title: 'Projets',
            subtitle: 'Espaces de travail pour les documents, les conversations, les flux de travail et les révisions.',
            modeLabel: 'Voir',
            tabMine: 'Mes projets',
            tabShared: 'Partagé avec moi',
            searchLabel: 'Rechercher des projets',
            searchPlaceholder: 'Rechercher…',
            newButton: 'Nouveau projet',
            colName: 'Nom',
            colVisibility: 'Visibilité',
            colDocs: 'Documents',
            colMembers: 'Membres',
            colUpdated: 'Mis à jour',
            visibilityOwn: 'Posséder uniquement',
            visibilityShared: 'Partagé',
            emptyMine: "Aucun projet pour l'instant. Créez-en un pour commencer.",
            emptyShared: "Aucun projet n'a été partagé avec vous.",
            pageHint: 'Affichage de {shown} sur {total}.',
            createTitle: 'Nouveau projet',
            createSubmit: 'Créer un projet',
            fieldName: 'Nom',
            fieldDescription: 'Description (facultatif)',
            fieldCmNumber: 'Numéro de dossier/affaire (facultatif)',
            fieldVisibility: 'Visibilité',
            errorNameRequired: 'Le nom est requis.',
            errorSelectorRequired: 'Sélectionnez un utilisateur ou un groupe organisationnel à ajouter.',
            renameButton: 'Renommer',
            renamePrompt: 'Renommer le projet',
            deleteButton: 'Supprimer',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: "Supprimer ce dossier ? Les documents à l'intérieur resteront dans le projet, mais non classés.",
            removeMemberConfirm: 'Supprimer ce membre ?',
            foldersHeading: 'Dossiers',
            foldersEmpty: "Aucun dossier pour l'instant.",
            addFolderButton: 'Nouveau dossier',
            addFolderTitle: 'Nouveau dossier',
            documentsHeading: 'Documents',
            documentsEmpty: "Aucun document joint pour l'instant.",
            colFilename: 'Nom du fichier',
            colAdded: 'Ajouté',
            unattachButton: 'Détacher',
            membersHeading: 'Membres',
            membersEmpty: 'Propriétaire uniquement.',
            addMemberButton: 'Ajouter un membre',
            addMemberTitle: 'Ajouter un membre',
            addMemberSubmit: 'Ajouter un membre',
            colMember: 'Membre',
            colRole: 'Rôle',
            selectorTypeLabel: 'Ajouter par',
            selectorUser: 'Identifiant utilisateur',
            selectorGroup: 'ID du groupe organisationnel',
            fieldUserId: 'Utilisateur',
            fieldGroupId: 'Groupe organisationnel',
            fieldRole: 'Rôle',
            roleAdmin: 'Administrateur',
            roleEditor: 'Éditeur',
            roleViewer: 'Visionneuse',
            notFound: 'Projet introuvable.',
            selectUserPlaceholder: 'Sélectionnez un utilisateur…',
            selectGroupPlaceholder: 'Sélectionnez un groupe organisationnel…',
            noUsersAvailable: 'Aucun utilisateur disponible.',
            noGroupsAvailable: 'Aucun groupe organisationnel disponible.',
            loadFailed: 'Échec du chargement des options.',
            deleteProjectTitle: 'Supprimer le projet ?',
            deleteFolderTitle: 'Supprimer le dossier ?',
            removeMemberTitle: 'Supprimer un membre ?',
            removeMemberConfirmGeneric: 'Supprimer ce membre du projet ?',
            addDocumentButton: 'Ajouter un document',
            addDocumentTitle: 'Ajouter un document au projet',
            fieldDocument: 'Sélectionner un document',
            selectDocPlaceholder: 'Rechercher un document...',
            ownerLabel: 'Propriétaire du système',
        },
        chat: {
            sidebarLabel: 'Discussions',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Votre AI Conseiller juridique et conformité — fondé sur vos données de locataire et vos packs réglementaires installés.',
            personaSelectorLabel: 'Personnalité',
            personaHeading: 'Choisissez un personnage',
            startChatButton: 'Démarrer une nouvelle discussion',
            startingChat: 'Démarrage du chat…',
            recentHeading: 'Discussions récentes',
            recentEmpty: 'Pas encore de chat – démarrez-en un ci-dessus.',
            untitledChat: 'Nouvelle discussion',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Conversation',
            detailSubtitle: 'Personnage : {persona}',
            composeHeading: 'Envoyer un message',
            composeLabel: 'Message Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Personnalité',
            presetPersona: 'Personnalité prédéfinie',
            changePersona: 'Changer de personnalité',
            hidePersonaOptions: 'Masquer les options de personnalité',
            starterHeading: 'Commencez par l’une de ces actions',
            renameButton: 'Renommer',
            renamePrompt: 'Renommer la discussion',
            deleteButton: 'Supprimer',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat introuvable.',
            emptyChat: "Aucun message pour l'instant : dites bonjour pour démarrer une conversation.",
            roleUser: 'Vous',
            roleAssistant: 'Forseti',
            roleSystem: 'Système',
            thinkingLabel: 'Penser',
            toolCallPlaceholder: "Appel d'outil : ",
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Modifications du document proposées (V{version}).',
            disclaimer: 'Forseti soutient votre jugement – il ne remplace pas un avocat agréé.',
            sendButton: 'Envoyer',
            sending: 'Envoi…',
            composePlaceholder: 'Demandez à Forseti…',
            composingPlaceholder: 'Forseti répond…',
            streamingPlaceholder: 'Forseti tape…',
            promptInjectionTitle: 'Forseti a laissé tomber quelques jetons de contrôle.',
            promptInjectionBody: 'Motifs dépouillés : {patterns}. Forseti ne verra que le texte de votre message.',
            rateLimitTitle: 'Vous envoyez des messages trop vite.',
            rateLimitRetryAfter: 'Réessayez dans {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti est incertain ici.',
            counselReviewBody: 'Confirmez auprès d’un avocat agréé avant de donner suite à cette réponse.',
            legalConclusionRefused: "Forseti a refusé de formuler une conclusion juridique sans fondement. Essayez d'abord search_regulatory ou contactez un conseiller.",
            toolCallDone: 'Terminé.',
            toolCallArgsLabel: 'Arguments',
            toolCallResultLabel: 'Résultat',
            toolResultLogged: "Résultat de l'outil capturé dans le journal d'audit.",
            toolResultAria: "résultat de l'outil",
            documentEditAria: 'modification de documents',
            createTaskFromMessage: 'Créer une tâche à partir de ceci',
            createTaskHeading: 'Créer une tâche à partir de ce message',
            createTaskTitleLabel: 'Titre',
            createTaskTitleRequired: 'Le titre est requis.',
            createTaskDescriptionLabel: 'Descriptif',
            createTaskAssigneeLabel: 'Cessionnaire',
            createTaskAssigneePlaceholder: 'Sélectionnez le destinataire…',
            createTaskAssigneeNone: '- non attribué -',
            createTaskDueDateLabel: "Date d'échéance",
            createTaskWorkflowLabel: 'Workflow lié (facultatif)',
            createTaskWorkflowPlaceholder: 'Sélectionnez un flux de travail…',
            createTaskWorkflowNone: '— aucun —',
            renameModalSubmit: 'Renommer',
            renameModalSaving: 'Sauvegarde…',
            deleteConfirmTitle: 'Supprimer le chat ?',
            createTaskSubmit: 'Créer une tâche',
            createTaskSubmitting: 'Création…',
            cancelButton: 'Annuler',
            autoRouteHintMessage: 'Cette question est davantage dans la timonerie de {persona}.',
            autoRouteSwitchButton: 'Passer à {persona}',
            autoRouteSwitching: 'Commutation…',
            handoffSuggestionMessage: 'Ou ouvrez une nouvelle discussion avec {persona} pour faire avancer cette question.',
            handoffSuggestionButton: 'Démarrez une nouvelle conversation avec {persona}',
            handoffSuggestionWorking: 'Ouverture…',
            handoffSuggestionDismiss: 'Pas maintenant',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Spécialiste de la conformité',
                'forseti-legal': 'Conseiller juridique',
                'forseti-dpo': 'Conseiller DPO',
                'forseti-risk': 'Analyste des risques',
                'forseti-auditor': "Œil de l'auditeur",
            },
            exportButton: 'Exporter en PDF',
            exportRunning: 'Exportation…',
            exportSuccess: 'Exporté : {filename} (ouvrir le navigateur de fichiers).',
            exportOpenFile: 'Ouvrir dans le navigateur de fichiers',
            exportDownload: 'Télécharger le PDF',
            citation: {
                verifyBadge: '[VÉRIFIER]',
                verifyAria: "la citation n'a pas pu être vérifiée – examiner avant de s'appuyer",
                popoverAria: 'Source pour {label}',
                openSource: 'Page open source',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: "Événement généré par AI à partir de la piste d'audit Forseti.",
        },
        rowAction: {
            ask: 'Demandez à Forseti',
            tooltipOpen: 'Demandez à Forseti à ce sujet {recordType}',
            tooltipLocked: 'Ce {recordType} est verrouillé — Forseti peut répondre aux questions mais ne peut pas apporter de modifications.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — à propos de ce disque',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Résumé',
            suggestedPersona: 'Personnalité suggérée',
            askForseti: 'Demandez à Forseti',
            runWorkflow: 'Exécuter un flux de travail',
            viewLineage: "Afficher le lignage d'audit",
            createPdplEvidencePack: 'Créer un dossier de preuves PDPL',
            prepareBreachDraft: 'Préparer le projet de notification',
            primaryActions: {
                projectSummary: 'Résumer le document de projet',
                supplementSummary: 'Résumer la demande de complément',
                authoritySummary: "Résumer la demande de l'autorité",
                rightsRequestResponse: 'Rédiger une réponse DSR',
                dpiaQuickReview: 'Lancer la revue rapide DPIA',
                taskControlWalkthrough: 'Parcourir les preuves de contrôle',
                vendorRiskNarrative: 'Générer le narratif de risque fournisseur',
                incidentLetter: 'Rédiger la lettre de violation PDPL',
                ropaSufficiency: 'Lancer le contrôle de suffisance RoPA',
                vendorDpaReview: 'Lancer la revue DPA fournisseur',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti prépare uniquement le contenu du projet pour le Mẫu số 08 lié. Il ne décide pas si une notification est requise et ne dépose ni envoie le formulaire.',
            breachDraftReady: 'Projet Mẫu số 08 prêt à être examiné',
            breachDraftReviewBeforeSelecting: 'Examiner avant de sélectionner',
            workflowHeading: 'Flux de travail pertinents',
            workflowLoading: 'Chargement des flux de travail pertinents…',
            workflowEmpty: "Aucun flux de travail pertinent n'est disponible pour cet enregistrement.",
            workflowError: 'Impossible de charger les flux de travail pertinents.',
            selectWorkflow: 'Sélectionner {title}',
            workflowSelected: "Flux de travail sélectionné. L'exécuteur intégré s'ouvre à l'étape suivante.",
            workflowRunSelected: 'Exécuter le flux de travail sélectionné',
            workflowStarting: 'Démarrage du flux de travail…',
            workflowRunError: 'Impossible de démarrer ce flux de travail.',
            workflowCreateGuided: 'Créer un flux de travail guidé pour cet enregistrement',
            lineageHeading: 'Traçabilité d’audit de cet enregistrement',
            lineageLoading: 'Chargement de la traçabilité d’audit…',
            lineageEmpty: "Aucun événement de traçabilité Forseti n'est enregistré pour cet enregistrement.",
            lineageError: "Impossible de charger la traçabilité d'audit.",
            retry: 'Réessayer',
            chatContextLoading: 'Chargement du contexte de l’enregistrement…',
            chatGuidance: {
                general: 'Forseti peut résumer cet enregistrement, repérer les informations manquantes et suggérer des étapes de vérification.',
                privacy: 'Forseti peut examiner les faits relatifs à la vie privée, vérifier les obligations des référentiels installés et aider à préparer un projet soumis à une validation humaine.',
                compliance: 'Forseti peut repérer les lacunes de conformité, étayer les exigences par les sources installées et suggérer des prochaines étapes attribuables.',
                legal: 'Forseti peut résumer l’enregistrement, trouver des sources étayées et aider à préparer un projet à soumettre au conseil juridique.',
                risk: 'Forseti peut évaluer les risques consignés, signaler les faits manquants et suggérer des étapes de vérification proportionnées.',
                audit: 'Forseti peut examiner les lacunes de preuve, relier l’enregistrement aux contrôles installés et suggérer un suivi prêt pour l’audit.',
            },
            starterPrompts: {
                identifyGaps: 'Quelles informations ou preuves manquent dans cet enregistrement ?',
                reviewFrameworks: 'Quelles obligations de nos référentiels installés dois-je examiner pour cet enregistrement ?',
                prepareDraft: 'Aidez-moi à préparer un projet fondé uniquement sur cet enregistrement et sur des sources installées dûment étayées.',
                nextSteps: 'Quelles étapes de vérification dois-je entreprendre ensuite ?',
                summarizeRecord: 'Résumez cet enregistrement et signalez les points qui nécessitent une validation humaine.',
                assessRisk: 'Évaluez les risques consignés, repérez les faits manquants et suggérez des étapes de vérification.',
            },
            disclaimer: 'Forseti soutient votre jugement – il ne remplace pas un avocat agréé.',
            recordType: {
                project: 'Projet',
                dpia: 'DPIA',
                dossier_filing: 'Dépôt de dossiers',
                transfer_assessment: 'Transfert transfrontalier',
                vendor: 'Vendeur',
                vendor_contract: 'Contrat du fournisseur',
                vendor_acceptance: 'Acceptation des risques du fournisseur',
                incident: 'Incident',
                incident_op: 'Opération sur incident',
                breach_notification: 'Notification de violation',
                authority_request: "Demande d'autorisation",
                supplement_request: 'Demande de supplément',
                rights_request: 'Demande de droits',
                contract: 'Contrat',
                task: 'Tâche',
                data_mapping_activity: 'Activité RoPA',
                compliance_rule: 'Règle de conformité',
                program: 'Programme',
                governance_document: 'Document de gouvernance',
                remediation_plan: 'Plan de remédiation',
                cross_border_filing: 'Dépôt transfrontalier',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Demandez à Forseti de rédiger ceci',
            drafting: 'Rédaction…',
            hint_pick_activity: 'Choisissez une activité de traitement liée pour activer la rédaction Forseti.',
            draft_origin_label: 'Projet Forseti — examen par champ',
            tokens_label: '≈ {count} jetons de votre bucket mensuel',
            accept_selected: 'Accepter la sélection',
            reject_all: 'Rejeter le brouillon',
            field_checkbox_label: 'Appliquer le brouillon Forseti pour {field}',
            field_labels: {
                description: 'Descriptif',
                purpose: 'Objectif',
                data_subjects: 'Personnes concernées',
                data_types: 'Types de données',
                necessity_justification: 'Nécessité et proportionnalité',
                consultation_notes: 'Notes de consultation',
                dpo_opinion: 'DPO avis',
                system_ids: 'Systèmes liés',
                vendor_ids: 'Fournisseurs liés',
            },
            error_generic: "Forseti n'a pas pu rédiger ce DPIA. Réessayez ou affinez l'enregistrement d'activité.",
            variant_legend: 'Cadre cadre',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Aucun cadre installé disponible',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'OIN 27001',
            variant_label_soc2: 'SOC2',
            conflict_banner: "Attention : des {count} conflits entre frameworks s'appliquent à cette variante. Révisez avant d’accepter.",
        },
        formDraft: {
            button_label: 'Demander à Forseti de rédiger ce formulaire',
            drafting: 'Rédaction…',
            draft_origin_label: 'Brouillon Forseti — vérifiez chaque champ',
            tokens_label: '≈ {count} jetons de votre quota mensuel',
            accept_selected: 'Accepter la sélection',
            reject_all: 'Rejeter le brouillon',
            field_checkbox_label: 'Appliquer le brouillon Forseti pour {field}',
            excluded_note: '{count} champ(s) ne peuvent pas être rédigés (références, pièces jointes ou lecture seule).',
            error_generic: 'Forseti n’a pas pu rédiger ce formulaire. Réessayez.',
            empty_draft: 'Forseti n’a produit aucun brouillon de champ pour ce formulaire.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Demandez à Forseti de rédiger ce TIA',
            drafting: 'Rédaction de TIA…',
            hint_pick_destination: 'Définissez le pays de destination sur le dossier de transfert avant que Forseti puisse rédiger.',
            draft_origin_label: 'Projet Forseti TIA — examen par domaine',
            tokens_label: '≈ {count} jetons de votre bucket mensuel',
            accept_selected: 'Accepter la sélection',
            reject_all: 'Rejeter le brouillon',
            field_checkbox_label: 'Appliquer le brouillon Forseti pour {field}',
            error_generic: "Forseti n'a pas pu rédiger ce TIA. Réessayez ou affinez l'enregistrement du transfert.",
            usage_details: "Détails d'utilisation de l'IA",
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: "Mises à jour d'enregistrement recommandées",
            mechanism_recommendation: 'Mécanisme de transfert : {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Définissez une échéance de revue avant de mettre à jour ce transfert sensible.',
            processing_activity_warning: 'Associez au moins une activité de traitement / un enregistrement ROPA avant de finaliser la TIA.',
            go_to_field: 'Aller au champ',
            adequacy_label: 'Régime de destination',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Raccourci transitionnel/EEE',
            adequacy_status_unknown: 'Inconnu — définir la destination',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: "Attention : {count} des conflits entre frameworks s'appliquent à ce régime de transfert. Révisez avant d’accepter.",
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Faits requis manquants',
            missing_facts_hint: 'Forseti a signalé des faits absents de l’enregistrement. Les champs qui en dépendent ne sont pas présélectionnés — fournissez les faits ou acceptez le champ délibérément.',
            risk_label: 'Risque calibré',
            risk_low: 'Faible',
            risk_medium: 'Moyen',
            risk_high: 'Élevé',
            jurisdiction_label: 'Déclencheurs de juridiction',
            jurisdiction_none_installed: "Aucun cadre installé ne couvre cette évaluation — aucun régime juridique n'a été évalué. Installez un module réglementaire adapté avant de vous fier aux conclusions juridiques de ce brouillon.",
            jurisdiction_none_assessed: "Forseti n'a renvoyé aucune évaluation de juridiction. Rien n'a été évalué — ce n'est pas une conclusion selon laquelle aucun régime ne s'applique.",
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Chargement des noms des régimes évalués…',
            trigger_applicable: 'S’applique',
            trigger_not_applicable: 'Non déclenché',
            trigger_unknown: 'Inconnu — fait manquant',
            recipient_role_label: 'Rôle du destinataire',
            recipient_role_controller: 'Responsable du traitement',
            recipient_role_processor: 'Sous-traitant',
            recipient_role_joint_controller: 'Responsable conjoint',
            recipient_role_unknown: 'Inconnu — non précisé',
            opt_in_hint: 'Conclusion juridique — ne l’activez que si vous l’acceptez avec les faits disponibles.',
        },
        mcp: {
            pageTitle: 'Clés du connecteur MCP',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Émettre une nouvelle clé',
            issueModalTitle: 'Émettre la clé MCP API',
            labelField: 'Étiquette',
            labelHint: 'Une brève description (par exemple, "AI laboratoire de capacités – recherche réglementaire »).',
            permittedToolsField: 'Outils autorisés',
            expiresAtField: 'Expire (facultatif)',
            expiresAtHint: "Laissez vide pour n'expire jamais.",
            submit: 'Clé du problème',
            cancel: 'Annuler',
            keyDisplayedOnce: 'Copiez cette clé maintenant – vous ne pourrez plus la revoir. Stockez-le dans votre gestionnaire de secrets (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: "Je l'ai copié — fermer",
            colLabel: 'Étiquette',
            colPrefix: 'Préfixe',
            colStatus: 'Statut',
            colTools: 'Outils',
            colCreatedAt: 'Créé',
            colLastUsed: 'Dernière utilisation',
            colCalls: 'Appels',
            colActions: 'Actions',
            statusActive: 'Actif',
            statusRevoked: 'Révoqué',
            statusExpired: 'Expiré',
            revokeAction: 'Révoquer',
            rotateAction: 'Rotation',
            confirmRevokeTitle: 'Révoquer cette clé MCP ?',
            confirmRevokeBody: 'Les clients Claude externes utilisant cette clé perdront immédiatement leur accès. Cela ne peut pas être annulé.',
            confirmRevoke: 'Révoquer la clé',
            rotatedTitle: 'Clé tournée',
            rotatedBody: "La nouvelle clé en texte brut se trouve ci-dessous. L'ancienne clé continue de fonctionner pendant 5 minutes afin que tous les appels en vol soient terminés.",
            emptyState: 'Aucune clé MCP émise pour le moment.',
            errorIssue: "Impossible d'émettre la clé MCP.",
            errorList: 'Impossible de charger les clés MCP.',
            errorRevoke: 'Impossible de révoquer la clé.',
            errorRotate: 'Impossible de faire tourner la clé.',
            neverUsed: '—',
            neverExpires: 'Jamais',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Mémoire Forseti',
            pageSubtitle: 'Parcourez et organisez le contexte spécifique à l’organisation que Forseti a accumulé au fil des conversations et des décisions. Les entrées organisées se verrouillent contre la rétention nocturne et restent disponibles pour chaque personne.',
            filterCategory: 'Catégorie',
            filterAllCategories: 'Toutes les catégories',
            totalCount: '{count} entrées',
            colAnchor: 'Ancre',
            colCategory: 'Catégorie',
            colSource: 'Origine',
            colContent: 'Contenu',
            colCreatedAt: 'Créé',
            colActions: 'Actions',
            editButton: 'Modifier',
            deleteButton: 'Supprimer',
            promoteButton: 'Promouvoir comme organisé',
            emptyState: "Pas de souvenirs pour l'instant : Forseti accumulera du contexte au fur et à mesure que vous discuterez.",
            categories: {
                framework_interpretation: 'Interprétation du cadre',
                process_preference: 'Préférence de processus',
                escalation_threshold: "Seuil d'escalade",
                decision_context: 'Contexte décisionnel',
                glossary_term: 'Terme du glossaire',
                auto_inferred: 'Auto-déduit',
            },
            sources: {
                admin_curated: 'Organisé',
                user_explicit: 'Explicite',
                auto: 'Inféré',
            },
            deleteConfirmTitle: 'Supprimer cette mémoire ?',
            deleteConfirmBody: 'La suppression supprimera cette ancre de chaque futur démarrage de chat Forseti. Ancre : {anchor}',
            deleteConfirmLabel: 'Supprimer',
            deleteCancelLabel: 'Annuler',
            editModalTitle: 'Modifier la mémoire',
            editAnchorLabel: 'Ancre',
            editCategoryLabel: 'Catégorie',
            editContentLabel: 'Contenu',
            editContentCount: '{n}/{max} caractères',
            editEmptyError: 'Le contenu ne peut pas être vide.',
            editTooLongError: 'Le contenu doit contenir ≤ 1 000 caractères.',
            editAutoPromoteHint: 'L’enregistrement de cette mémoire auto-déduite la marquera comme confirmée par l’utilisateur et la supprimera du balayage de rétention.',
            editCancelButton: 'Annuler',
            editSaveButton: 'Enregistrer',
            editSaving: 'Sauvegarde…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: "Lignée d'audit",
            subtitle: "Tracez chaque artefact généré par AI attaché à un enregistrement. Discussions, appels d'outils, tâches, brouillons de preuves et versions de documents.",
            filterForm: 'Filtres',
            recordType: "Type d'enregistrement",
            recordId: "Identifiant de l'enregistrement",
            dateStart: 'De',
            dateEnd: 'À',
            search: 'Rechercher',
            loading: 'Chargement…',
            exportPdf: 'Exporter PDF',
            exportCsv: 'Exporter CSV',
            nodesHeading: 'Nœuds de lignée',
            colType: 'Tapez',
            colLabel: 'Étiquette',
            colOccurredAt: 'Survenu à',
            colActor: 'Acteur',
            colId: "pièce d'identité",
            noResults: 'Aucune activité de conseiller pour ce dossier.',
            draftBadge: 'Brouillon généré par AI',
            prev: 'Précédent',
            next: 'Suivant',
            viewToggleTable: 'Afficher sous forme de tableau',
            viewToggleGraph: 'Afficher sous forme de graphique',
            graphAriaLabel: 'Graphique de lignée (visuel). Utilisez la vue tableau pour la technologie d’assistance.',
            graphLegendDraft: 'Projet de preuve AI',
            graphEmpty: 'Aucun nœud à représenter graphiquement.',
            paginationAriaLabel: 'Pagination',
            exportSuccessTemplate: '{format} : {filename}',
            exportErrorTemplate: "Échec de l'exportation : {message}",
            truncatedNotice: "Cette traçabilité n'est pas complète. La plateforme lit au plus un nombre fixe de lignes par source, et la limite a été atteinte pour : {sources}. Les lignes au-delà d'une limite, s'il y en a, ne sont pas affichées. Réduisez la plage de dates pour les inclure.",
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Projet',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Vendeur',
                incident: 'Incident',
                incident_op: 'Opération sur incident',
                rights_request: 'Demande de droits',
                contract: 'Contrat',
                task: 'Tâche',
                data_mapping_activity: 'Activité de cartographie des données',
                compliance_rule: 'Règle de conformité',
                program: 'Programme',
            },
            nodeTypes: {
                chat: 'Discuter',
                tool_call: "Appel d'outil",
                workflow_run: 'Exécution du flux de travail',
                task: 'Tâche',
                evidence: 'Preuve',
                document_version: 'Version du document',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: "D'accord",
                drifted: 'Dérivé',
                hash_mismatch: 'Incompatibilité de hachage',
                merkle_mismatch: 'Inadéquation de Merkle',
                source_missing: 'Source manquante',
                not_found: 'Pas trouvé',
                chain_unreachable: 'Chaîne inaccessible',
                service_unreachable: 'Service inaccessible',
                pending: 'Ancrage en attente',
            },
        },
        evidenceDraft: {
            draftBadge: 'DRAFT généré par AI',
            pendingReviewBadge: "En attente d'examen",
            approvedBadge: 'Approuvé',
            rejectedBadge: 'Rejeté',
            moveToReviewButton: 'Marquer comme prêt à être examiné',
            approveButton: 'Approuver',
            rejectButton: 'Rejeter',
            rejectReasonLabel: 'Motif du refus',
            humanSignoffBanner: "Forseti soutient votre jugement – il ne remplace pas un avocat agréé. Les preuves générées par AI nécessitent l'approbation humaine avant d'être considérées comme des preuves formelles.",
            pageTitle: 'AI brouillons de preuves',
            pageSubtitle: 'Examinez, approuvez ou rejetez les preuves générées par AI. Les brouillons doivent être approuvés par un humain avant de pouvoir être considérés comme des preuves formelles.',
            filterAll: 'Tout est exploitable',
            filterDrafts: 'Brouillons uniquement',
            filterPending: "En attente d'examen",
            filterApproved: 'Récemment approuvé',
            filterRejected: 'Récemment rejeté',
            colId: 'Identifiant des preuves',
            colStatus: 'Statut',
            colSource: 'Origine',
            colCreatedAt: 'Créé',
            colActions: 'Actions',
            empty: 'Aucun brouillon de preuve AI dans cet état.',
            confirmApprove: 'Approuver cette preuve générée par AI comme formelle ?',
            confirmReject: 'Rejetez cette ébauche de preuves. Cela ne peut pas être annulé.',
            reasonRequired: 'Un motif de refus est requis.',
            rejectModalTitle: 'Rejeter le projet de preuve',
            approveModalTitle: 'Approuver le projet de preuve',
            operationFailed: "L'opération a échoué. Veuillez réessayer.",
            moveToReviewSuccess: 'Marqué prêt pour examen.',
            approveSuccess: 'Approuvé.',
            rejectSuccess: 'Rejeté.',
            sourceLabelWorkflowRunTitle: 'Flux de travail : {title}',
            sourceLabelWorkflowRunId: 'Exécution du workflow {id}',
            sourceLabelChatTitle: 'Chat : {title}',
            sourceLabelChatId: 'Chat Forseti {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Statut du filtre',
        },
        workflows: {
            pageTitle: 'Flux de travail IA',
            pageSubtitle: 'Modèles de prompts IA enregistrés produisant des tâches et des brouillons de preuves.',
            newWorkflow: 'Nouveau flux de travail',
            newWorkflowHeading: 'Nouveau flux de travail IA',
            tabAll: 'Tous',
            tabBuiltin: 'Pack intégré',
            tabMine: 'Mes flux de travail',
            tabDetail: 'Détails',
            tabLinkedTasks: 'Tâches liées',
            builtinBadge: 'Intégré',
            builtinSubtitle: 'Flux de travail intégré.',
            userSubtitle: 'Flux de travail personnalisé.',
            emptyHint: 'Aucun flux de travail pour le moment.',
            loadTimeoutError: "Impossible de charger les flux de travail après 10 secondes. Réessayez ou vérifiez l'onglet Réseau du navigateur pour une requête bloquée.",
            loadError: 'Échec du chargement des flux de travail.',
            hideError: 'Échec du masquage.',
            unhideError: "Échec de l'affichage.",
            retry: 'Réessayer',
            producesTasks: 'Produit des tâches',
            producesEvidence: 'Produit des brouillons de preuves',
            hideButton: 'Masquer de ma liste',
            unhideButton: 'Afficher',
            runButton: 'Exécuter',
            editButton: 'Modifier',
            shareButton: 'Partager',
            backToList: 'Retour à la liste',
            requireTitleAndPrompt: 'Le titre et le prompt sont requis.',
            fieldTitle: 'Titre',
            fieldDescription: 'Descriptif',
            fieldType: 'Tapez',
            fieldPersona: 'Affinité de persona',
            fieldPractice: 'Pratique',
            fieldPrompt: 'Invite (markdown)',
            fieldProducesTasks: 'Produit des tâches',
            fieldProducesEvidence: 'Produit des brouillons de preuves',
            fieldProjectId: 'Projet (facultatif)',
            fieldAttachmentIds: 'Pièces jointes',
            fieldParentRecord: 'Enregistrement parent (facultatif)',
            fieldPersonaOverride: 'Substitution de persona (facultatif)',
            typeAssistant: 'Assistante',
            typeTabular: 'Tabulaire',
            personaNone: '— aucun —',
            parentNone: '— aucun —',
            parentIdPlaceholder: "ID de l'enregistrement parent",
            selectProjectPlaceholder: 'Sélectionner un projet…',
            selectProjectFirst: 'Sélectionnez un projet pour choisir les pièces jointes.',
            selectAttachmentsPlaceholder: 'Sélectionner des documents…',
            noAttachmentsInProject: 'Ce projet ne comporte aucun document joint.',
            selectParentRecordPlaceholder: 'Sélectionner un enregistrement…',
            selectParentRecordFirst: "Sélectionnez d'abord un type d'enregistrement.",
            noRecordsForType: 'Aucun enregistrement de ce type disponible.',
            shareUserSelectorLabel: 'Destinataires (utilisateurs)',
            shareGroupSelectorLabel: "Destinataires (groupes d'organisation)",
            selectUsersPlaceholder: 'Sélectionner des utilisateurs…',
            selectOrgGroupsPlaceholder: "Sélectionner des groupes d'organisation…",
            loadingOptions: 'Chargement…',
            loadFailed: 'Échec du chargement des options.',
            parentRecordTypes: {
                dpia: 'AIPD',
                vendor: 'Fournisseur',
                incident: 'Incident',
                rights_request: 'Demande de droits',
                contract: 'Contrat',
                data_mapping_activity: 'Activité RoPA',
                compliance_rule: 'Règle de conformité',
                program: 'Programme',
            },
            cancelButton: 'Annuler',
            closeButton: 'Fermer',
            createButton: 'Créer',
            creating: 'Création…',
            saveButton: 'Enregistrer',
            saving: 'Enregistrement…',
            shareWorkflowHeading: 'Partager le flux de travail',
            shareIntro: 'Partage : {title}',
            shareUserIds: "ID d'utilisateur (séparés par espace ou virgule)",
            shareGroupIds: "ID de groupe d'organisation (séparés par espace ou virgule)",
            shareAllowEdit: 'Autoriser les destinataires à modifier',
            shareRequireRecipient: "Fournissez au moins un id d'utilisateur ou de groupe.",
            shareSuccess: 'Partagé.',
            sharing: 'Partage…',
            runWorkflowHeading: 'Exécuter : {title}',
            runStarting: 'Démarrage…',
            runStatusLabel: 'Statut : {status}',
            runProducedCounts: 'Tâches : {tasks} · Brouillons de preuves : {evidence}',
            runPanelHeading: 'Exécution du flux de travail',
            runPanelHeadingWithTitle: 'Exécution du flux de travail : {title}',
            runViewTasks: 'Afficher les tâches générées',
            runViewEvidence: 'Afficher les preuves brouillon',
            runResultHeading: 'Résultat du conseiller',
            runNoResult: "L'exécution s'est terminée mais n'a pas renvoyé de résultat exploitable. Aucune tâche, preuve ni pièce jointe n'a été créée. Réessayez avec l'enregistrement ou les documents requis sélectionnés.",
            runCitationWarning: "Certaines citations n'ont pas pu être vérifiées automatiquement. Examinez-les avant de vous fier à ce résultat.",
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Impossible de charger l’exécution du flux de travail.',
            runStreamFailed: 'La connexion au suivi du flux de travail a échoué.',
            runStatusValues: {
                pending: 'En attente',
                running: 'En cours',
                completed: 'Terminé',
                succeeded: 'Réussi',
                failed: 'Échoué',
                cancelled: 'Annulé',
            },
            runProgressStatus: 'Statut : {status}',
            runProgressTaskCreated: 'Tâche créée.',
            runProgressEvidenceDrafted: 'Brouillon de preuve créé.',
            runProgressAttachmentGenerated: 'Pièce jointe générée.',
            guidedWorkflowTitle: 'Flux de travail guidé pour {recordType}',
            guidedWorkflowDescription: 'Créé pour les enregistrements de type {recordType}. Vérifiez et personnalisez ce flux de travail avant de l’enregistrer.',
            guidedWorkflowPrompt: 'Examinez l’enregistrement de type {recordType} fourni, repérez les faits manquants et proposez des prochaines étapes étayées pour validation humaine.',
            promptHeading: 'Invite',
            howItWorksHeading: 'Ce qui se passe lorsque vous exécutez ce workflow',
            builtinGuidance: "Forseti examine les enregistrements et documents que vous sélectionnez, renvoie un résultat vérifiable par un opérateur et ne crée des tâches ou preuves brouillon que lorsque le résultat le justifie. Rien n'est approuvé ni soumis automatiquement.",
            editWorkflowHeading: 'Modifier le flux de travail',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Flux de travail introuvable.',
            schedules: {
                tabTitle: 'Plannings',
                sectionHeading: 'Exécutions planifiées',
                empty: 'Aucun planning pour le moment. Créez-en un pour exécuter ce flux de travail automatiquement.',
                addButton: 'Ajouter un planning',
                addHeading: 'Planifier le flux de travail',
                editHeading: 'Modifier le planning',
                fieldFrequency: 'Fréquence',
                fieldDayOfWeek: 'Jour de la semaine',
                fieldDayOfMonth: 'Jour du mois',
                fieldTime: 'Heure (UTC)',
                fieldTimezone: 'Fuseau horaire',
                fieldProject: 'Projet (facultatif)',
                fieldAttachments: 'Pièces jointes par défaut',
                fieldPersonaOverride: 'Substitution de persona (facultatif)',
                frequencyDaily: 'Quotidien',
                frequencyWeekly: 'Hebdomadaire',
                frequencyMonthly: 'Mensuel',
                weekdaySun: 'Dimanche',
                weekdayMon: 'Lundi',
                weekdayTue: 'Mardi',
                weekdayWed: 'Mercredi',
                weekdayThu: 'Jeudi',
                weekdayFri: 'Vendredi',
                weekdaySat: 'Samedi',
                statusActive: 'Actif',
                statusPaused: 'En pause',
                nextRunLabel: 'Prochaine exécution',
                lastRunLabel: 'Dernière exécution',
                pauseButton: 'Pause',
                resumeButton: 'Reprendre',
                deleteButton: 'Supprimer',
                triggerButton: 'Exécuter maintenant',
                savingButton: 'Enregistrement…',
                saveButton: 'Enregistrer',
                triggered: "Exécution démarrée — vérifiez l'historique des exécutions.",
            },
            branching: {
                helpHeading: 'Syntaxe de prompt conditionnelle',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Conflits entre frameworks',
            pageSubtitle: 'Catalogue organisé des endroits où vos packs réglementaires installés interagissent de manière conflictuelle. Surfaces Forseti; les humains décident.',
            noInstalledPacks: "Aucun pack réglementaire n'est installé pour cette organisation : installez au moins deux packs pour détecter les conflits entre frameworks.",
            noConflicts: 'Aucun conflit ne correspond aux filtres actuels.',
            allAcknowledged: 'Chaque conflit visible a été reconnu. Beau travail.',
            columnSeverity: 'Gravité',
            columnTitle: 'Titre',
            columnFrameworks: 'Cadres',
            columnTopic: 'Sujet',
            columnStatus: 'Statut',
            filterFrameworkPlaceholder: 'Tous les cadres',
            filterTopicPlaceholder: 'Tous les sujets',
            filterSeverityPlaceholder: 'Toutes les sévérités',
            filterStatusPlaceholder: 'Tous les statuts',
            filterStatusAcknowledged: 'Reconnu',
            filterStatusUnacknowledged: 'Non reconnu',
            severityCritical: 'Critique',
            severityHigh: 'Élevé',
            severityMedium: 'Moyen',
            severityLow: 'Faible',
            statusAcknowledged: 'Reconnu',
            statusInReview: 'En revue',
            statusResolved: 'Résolu',
            statusWontFix: 'Ne résoudra pas',
            statusUnacknowledged: 'Non reconnu',
            drawerSummaryHeading: 'Résumé',
            drawerSideAHeading: 'Face A',
            drawerSideBHeading: 'Face B',
            drawerRequirementsLabel: 'Exigences citées',
            drawerProvisionsLabel: 'Dispositions citées',
            drawerSuggestedResolutionHeading: 'Résolution suggérée',
            drawerLegalCounselBadge: 'Examen par un conseiller juridique requis',
            drawerAckHeading: 'Remerciement',
            drawerAckStatusLabel: 'Statut',
            drawerAckAssigneeLabel: 'Attribué à',
            drawerAckDueDateLabel: "Date d'échéance",
            drawerAckNotesLabel: 'Remarques',
            drawerAckButtonAcknowledge: 'Reconnaître',
            drawerAckButtonUpdate: 'Mise à jour',
            drawerAckButtonUnacknowledge: 'Annuler la réception',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Rechercher un titre ou un résumé…',
            searchButton: 'Rechercher',
            searchButtonAriaLabel: 'Exécuter une recherche de conflit',
            exportSuccessTemplate: 'CSV : {filename}',
            exportSuccessTemplateXlsx: 'XLSX : {filename}',
            exportErrorTemplate: "Échec de l'exportation : {message}",
            exportAriaLabel: "Conflits d'exportation",
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Revues tabulaires',
            pageSubtitle: 'Comparez N documents sur M attributs avec des citations fondées.',
            newReview: 'Nouvelle revue',
            newReviewHeading: 'Nouvelle revue tabulaire',
            emptyHint: 'Aucune revue tabulaire pour le moment.',
            docsLabel: '{n} documents',
            colsLabel: '{n} colonnes',
            detailSubtitle: '{docs} documents · {cols} colonnes',
            addColumn: 'Ajouter une colonne',
            addColumnHeading: 'Ajouter une colonne',
            columnInfo: 'Voir l’invite et le format',
            columnInfoAria: 'Afficher l’invite et le format de la colonne {name}',
            columnInfoPrompt: 'Invite',
            columnInfoFormat: 'Format de retour',
            columnInfoPresets: 'Valeurs autorisées',
            columnInfoCitations: 'Citations',
            columnInfoCitationsRequired: 'Obligatoire',
            removeColumn: 'Supprimer la colonne',
            removeColumnAria: 'Supprimer la colonne {name}',
            removeColumnConfirm: 'Supprimer la colonne « {name} » ? Ses cellules extraites seront supprimées.',
            deleteReview: 'Supprimer',
            deleteReviewAria: 'Supprimer la revue {title}',
            deleteReviewConfirm: 'Supprimer la revue « {title} » ? Cela supprime définitivement ses colonnes, cellules et discussions.',
            runAll: 'Tout exécuter',
            running: 'Exécution…',
            cancel: 'Annuler',
            exportXlsx: 'Exporter Excel',
            exportCsv: 'Exporter CSV',
            exporting: 'Exportation…',
            backToList: 'Retour à la liste',
            notFound: 'Revue tabulaire introuvable.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Joignez des documents pour démarrer la revue.',
            noColumns: 'Ajoutez des colonnes pour définir ce qui doit être extrait.',
            tableAriaLabel: "Grille d'extraction",
            threadsCountAria: '{n} message(s) de discussion',
            startDiscussionAria: 'Démarrer la discussion',
            docColumn: 'Documenter',
            statusPending: 'En attente',
            statusRunning: 'Exécution…',
            statusFailed: 'Échec : {msg}',
            statusCancelled: 'Annulé',
            citationsCount: '{n} citation(s)',
            citationsView: 'Voir les passages sources',
            citationPage: 'Page {n}',
            titleRequired: 'Le titre est requis.',
            fieldTitle: 'Titre',
            fieldProjectId: 'Projet (facultatif)',
            fieldAttachmentIds: 'Pièces jointes',
            fieldColumnName: 'Nom de la colonne',
            fieldColumnNameHelp: "Une étiquette courte et descriptive pour cette colonne dans la grille d'extraction.",
            columnNamePlaceholder: 'p. ex. Date de début du contrat',
            fieldExtractionPrompt: "Prompt d'extraction",
            fieldExtractionPromptHelp: 'Décrivez les informations que vous souhaitez extraire. Soyez précis pour obtenir de meilleurs résultats.',
            columnPromptPlaceholder: 'Que doit rechercher Forseti dans les documents ?',
            fieldReturnFormat: 'Format de retour',
            fieldReturnFormatHelp: "Contrôle la manière dont l'IA formate sa réponse (par exemple sous forme de date ou de nombre).",
            fieldPresets: 'Valeurs autorisées (une par ligne)',
            fieldPresetsPlaceholder: 'oui\nnon\npartiel',
            fieldCitationsRequired: 'Citation requise',
            formatShortText: 'Texte court',
            formatLongText: 'Texte long',
            formatEnum: 'Énumération',
            formatNumber: 'Nombre',
            formatDate: 'Date',
            formatList: 'Liste',
            columnRequireFields: 'Le nom et le prompt sont requis.',
            addColumnError: "Échec de l'ajout de la colonne.",
            cancelButton: 'Annuler',
            createButton: 'Créer',
            creating: 'Création…',
            addButton: 'Ajouter',
            adding: 'Ajout…',
            runConfirmHeading: "Exécuter l'extraction",
            estimateBody: '~{units} unités facturables estimées (confiance {confidence}).',
            starting: 'Démarrage…',
            confirmRun: "Confirmer l'exécution",
            charts: {
                title: 'Graphiques de colonnes',
                tabsAria: 'Colonnes des graphiques',
                enumAria: 'Distribution des valeurs enum',
                numberAria: 'Histogramme de distribution numérique',
                dateAria: 'Chronologie des dates',
                enumEmpty: 'Aucune cellule complétée.',
                numberEmpty: 'Aucune valeur numérique.',
                numberExcluded: '{n} cellule(s) exclues (vides / non numériques / non complétées).',
                dateEmpty: 'Aucune valeur de date.',
                dateExcluded: '{n} cellule(s) exclues.',
                unsupportedFormat: 'Format de colonne non pris en charge.',
            },
            threads: {
                drawerTitle: 'Discussion',
                loading: 'Chargement…',
                empty: 'Aucun message pour le moment. Lancez la conversation.',
                composerLabel: 'Nouveau commentaire',
                composerPlaceholder: 'Ajouter un commentaire…',
                post: 'Publier',
                posting: 'Publication…',
                edit: 'Modifier',
                editLabel: 'Modifier le message',
                delete: 'Supprimer',
                save: 'Enregistrer',
                cancel: 'Annuler',
                close: 'Fermer',
                editedBadge: 'modifié',
                loadError: 'Échec du chargement du fil',
                postError: 'Échec de la publication',
                editError: 'Échec de la modification',
                deleteError: 'Échec de la suppression',
            },
            compare: {
                pageTitle: 'Comparer les revues',
                leftReviewLabel: 'Revue de gauche',
                rightReviewLabel: 'Revue de droite',
                pickReview: 'Choisissez une revue à comparer',
                noSharedDocuments: 'Ces revues ne partagent aucun document — la comparaison nécessite au moins une pièce jointe commune.',
                sharedDocumentsHeading: 'Documents partagés',
                leftOnlyHeading: 'Colonnes uniquement dans la revue de gauche',
                rightOnlyHeading: 'Colonnes uniquement dans la revue de droite',
                differs: 'Diffère',
                identical: 'Identique',
                emptyValue: '—',
                compareButton: 'Comparer avec…',
                documentHeading: 'Documenter',
                errorMissingIds: 'Les ids des revues de gauche et de droite sont requis.',
                errorComparisonFailed: 'La comparaison a échoué.',
                errorLoadFailed: 'Échec du chargement des revues.',
                pickerLoading: 'Chargement…',
                pickerNoOtherReviews: 'Aucune autre revue disponible pour la comparaison.',
                pickerCancel: 'Annuler',
            },
        },
        documentReview: {
            pageTitle: 'Examen des documents',
            pageSubtitle: 'Acceptez ou rejetez les modifications suggérées par AI pour ce document.',
            sidePanelLabel: 'AI lignes rouges',
            sidePanelHeading: 'AI lignes rouges',
            noPendingEdits: 'Aucune modification en attente.',
            pendingCount: '{count} en attente',
            acceptAllButton: 'Acceptez tout',
            rejectAllButton: 'Rejeter tout',
            acceptAllTitle: 'Accepter toutes les modifications en attente ?',
            acceptAllConfirm: 'Cela acceptera les {count} modifications en attente et créera une ou plusieurs nouvelles versions. Continuer?',
            editsHeading: 'Modifications',
            editsEmpty: 'Aucune modification à examiner.',
            versionsHeading: 'Historique des versions',
            versionsEmpty: "Aucune version pour l'instant.",
            editLabel: 'Modifier',
            editLabelWithId: 'Modifier #{id}',
            acceptButton: 'Accepter',
            rejectButton: 'Rejeter',
            accepting: 'Accepter…',
            rejecting: 'Rejeter…',
            diffDeletedAria: 'Supprimé',
            diffInsertedAria: 'Inséré',
            statusPending: 'En attente',
            statusAccepted: 'Accepté',
            statusRejected: 'Rejeté',
            versionLabel: 'Version',
            versionAriaLabel: 'Version {n}',
            versionAriaLabelCurrent: 'Version {n} ({current})',
            versionCurrentTag: 'courant',
            versionSourceLabel: 'Origine',
            versionCreatedLabel: 'Créé',
            versionModelLabel: 'Modèle',
            sourceUpload: 'Télécharger',
            sourceUserUpload: "Téléchargement par l'utilisateur",
            sourceAssistantEdit: 'Assistant de modification',
            sourceUserAccept: "Modification acceptée par l'utilisateur",
            sourceUserReject: "L'utilisateur a refusé la modification",
            sourceGenerated: 'Généré',
            extractionExtracted: 'Texte extrait',
            extractionFailed: 'Échec de l’extraction du texte',
            extractionUnsupported: 'Le texte n’a pas pu être extrait',
            extractionPages: '{n} pages',
            extractionWords: '{n} mots',
            extractionReasonScanned: 'Ce document semble numérisé ou composé uniquement d’images. Forseti ne peut pas encore le lire — téléversez un fichier texte ou collez le contenu directement.',
            extractionReasonEncrypted: 'Le fichier est protégé par mot de passe. Retirez la protection et téléversez-le à nouveau.',
            extractionReasonMalformed: 'Le fichier n’a pas pu être analysé et est peut-être corrompu.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Le fichier est trop volumineux pour être traité.',
            extractionReasonTimeout: 'Le traitement a pris trop de temps. Essayez un fichier plus petit ou plus simple.',
            extractionReasonUnknown: 'Aucun détail supplémentaire n’est disponible.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Texte du document',
            textPaneTitle: 'Texte du document',
            textPaneVersion: 'Version {n}',
            textPaneEmpty: 'Aucun texte n’est disponible pour cette version. Les PDF numérisés et certains types de documents n’ont pas de texte extractible.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Revue de documents',
            pageTitle: 'Revue de documents',
            pageSubtitle: 'Documents de votre organisation comportant des modifications suggérées par IA en attente de revue ou des versions issues de Forseti.',
            colDocument: 'Documenter',
            colLinkedRecord: 'Enregistrement lié',
            colPendingEdits: 'Modifications en attente',
            colLastActivity: 'Dernière activité',
            colOpen: 'Ouvrir',
            open: 'Ouvrir',
            empty: "Aucun document en revue Forseti pour le moment. Les documents entrent dans cette file lorsque Forseti suggère une modification dans un chat, lorsqu'un flux IA produit un redline, ou lorsque vous envoyez un document en revue via le bouton ci-dessus.",
            loading: 'Chargement…',
            loadError: 'Échec du chargement des documents en revue.',
            retryButton: 'Réessayer',
            noLinkedRecord: '—',
            pendingZero: 'Aucune',
            sendForReviewButton: 'Envoyer un document en revue',
            sendForReviewTitle: 'Envoyer un document en revue',
            sendForReviewSubtitle: 'Téléversez un document .txt, .md, .pdf ou .docx (jusqu’à 50 Mo). Il est ajouté à la file de revue et Forseti effectuera une passe redline dessus.',
            sendForReviewTabUpload: 'Téléverser nouveau',
            sendForReviewPickerLabel: 'Rechercher des pièces jointes',
            sendForReviewPickerPlaceholder: 'Tapez pour rechercher…',
            sendForReviewPickerEmpty: 'Aucune pièce jointe correspondante. Essayez une autre recherche.',
            sendForReviewSubmit: 'Envoyer en revue',
            sendForReviewCancel: 'Annuler',
            sendForReviewSubmitting: 'Envoi…',
            sendForReviewError: "Impossible d'envoyer le document en revue. Veuillez réessayer.",
            sendForReviewSuccess: 'Document envoyé en revue.',
            sendForReviewAcceptedExt: 'Acceptés : {ext}.',
            sendForReviewMaxSize: 'Taille max. {mb} Mo.',
        },
        chats: {
            exportError: "Échec de l'exportation.",
        },
    },
    pdplEvidencePacks: {
        title: 'Dossiers de preuves PDPL',
        subtitle: 'Soumission contrôlée par le client à MoPS A05',
        nav: { emailProviders: 'Fournisseurs de messagerie' },
        providers: {
            pageTitle: 'Fournisseurs de messagerie',
            pageSubtitle: 'Configurez le fournisseur de messagerie sortante de votre organisation pour soumettre les dossiers de preuves PDPL à MoPS A05.',
            architectureRuleHeading: "Règle d'architecture (verrouillée)",
            architectureRuleBody: "AesirX n'est pas l'expéditeur des courriels de preuve. Chaque envoi utilise un fournisseur que vous possédez et contrôlez. AesirX n'envoie jamais en votre nom.",
            empty: 'Aucun fournisseur configuré pour le moment.',
            createCta: 'Créer un fournisseur',
            statusPendingConsent: 'En attente de consentement',
            statusPendingConnectionCheck: 'Test de connexion en attente',
            statusActive: 'Actif',
            statusConnectionCheckFailed: 'Échec du test de connexion',
            statusDisabled: 'Désactivé',
            acceptConsentCta: 'Accepter le consentement',
            runConnectionCheckCta: 'Lancer le test de connexion',
            disableCta: 'Désactiver',
            disableConfirm: "Désactiver ce fournisseur interrompt les envois directs jusqu'à la configuration d'un nouveau fournisseur et la reconfirmation du consentement. Continuer ?",
            columnDisplayName: 'Nom',
            columnFromAddress: 'Adresse expéditrice',
            columnStatus: 'Statut',
            columnLastCheck: 'Dernier test',
            columnActions: 'Actions',
            createModalTitle: 'Nouveau fournisseur SMTP',
            createDisplayNameLabel: 'Nom affiché',
            createFromAddressLabel: 'Adresse expéditrice',
            createReplyToLabel: 'Adresse de réponse (facultatif)',
            createSmtpHostLabel: 'Serveur SMTP',
            createSmtpPortLabel: 'Port SMTP',
            createSmtpUsernameLabel: 'Identifiant SMTP',
            createSmtpPasswordLabel: 'Mot de passe SMTP',
            createSmtpEncryptionLabel: 'Chiffrement',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implicite)',
            createSmtpEncryptionNone: 'Aucun',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Bientôt disponible',
            createOauthDisabledHint: "Les fournisseurs OAuth ne sont pas encore activés. Utilisez SMTP pour l'instant.",
            createSubmitCta: 'Créer le fournisseur',
            createCancelCta: 'Annuler',
            createErrorInvalidFromAddress: "L'adresse expéditrice doit être une boîte e-mail valide.",
            createErrorDuplicateFromAddress: 'Un autre fournisseur est déjà configuré pour cette adresse expéditrice.',
            createErrorGeneric: 'Impossible de créer le fournisseur. Vérifiez le formulaire et réessayez.',
            consentModalTitle: "Confirmer la règle d'architecture",
            consentLocaleToggleEn: 'Anglais',
            consentLocaleToggleVi: 'Vietnamien',
            consentAcknowledgeCta: 'Je confirme — accepter le consentement',
            consentCancelCta: 'Annuler',
            consentErrorMismatch: 'Le texte de consentement a été modifié et ne correspond plus à la clause canonique. Rouvrez la boîte de dialogue et soumettez à nouveau.',
            testModalTitle: 'Test de connexion',
            testRunningCta: 'Test de connexion en cours…',
            testRunCta: 'Lancer le test de connexion',
            testRetryCta: 'Réessayer le test',
            testSuccessHeading: 'Connexion réussie',
            testSuccessBody: 'Authentifié auprès de {host}:{port} en {latencyMs} ms. Le fournisseur est prêt pour les envois directs.',
            testFailureHeading: 'Échec de la connexion',
            testFailureBody: "Le test n'a pas pu aboutir. Examinez la cause ci-dessous et réessayez après correction.",
            testFailureReasonLabel: "Cause de l'échec",
            testCloseCta: 'Fermer',
            detailDrawerTitle: 'Détails du fournisseur',
            detailDrawerHistoryHeading: 'Historique des tests de connexion',
            detailDrawerConsentHeading: 'Confirmation du consentement',
            detailDrawerNoHistory: 'Aucun test de connexion enregistré pour le moment.',
            detailDrawerNoConsent: "Le consentement n'a pas encore été confirmé.",
            outcomeSuccess: 'Succès',
            outcomeFailure: 'Échec',
        },
        builder: {
            pageTitle: 'Assembler le dossier de preuves',
            pageSubtitle: 'Téléversez les 6 fichiers, choisissez le type de paquet et assemblez le ZIP déterministe en 8 parties pour la soumission.',
            packTypeLabel: 'Type de paquet',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Partie 01 — Lettre de présentation (PDF)',
            part02AuthorityForm: "Partie 02 — Formulaire de l'autorité (PDF)",
            part03Supporting: 'Partie 03 — Preuves justificatives (≥1)',
            part04Safeguards: 'Partie 04 — Garanties',
            part04SafeguardsRequiredOtia: '(requis pour OTIA)',
            part05PriorCorrespondence: 'Partie 05 — Correspondance antérieure (facultatif)',
            part06Signature: 'Partie 06 — Preuves de signature et cachet (≥1)',
            uploadPickFile: 'Choisir un fichier',
            uploadPickFiles: 'Choisir des fichiers',
            uploadRemoveFile: 'Retirer',
            requiredMissing: 'Partie requise manquante',
            assembleCta: 'Assembler le paquet',
            assembling: 'Assemblage…',
            assembleSuccessHeading: 'Paquet assemblé',
            assembleSuccessSummary: '{fileCount} fichiers · {bytes} octets · SHA-256 {shaPrefix}…',
            composeNextCta: "Rédiger un brouillon d'e-mail",
            warningsHeading: 'Avertissements de validation',
            assembleError: "Impossible d'assembler le paquet. Vérifiez les données et réessayez.",
            forsetiAssistHeading: 'Assistance IA Forseti (optionnelle)',
            forsetiAssistHint: "Exécutez le PDPL Evidence Pack Assembly Advisor pour mettre en évidence les éléments manquants, rédiger le texte de la lettre d'accompagnement et signaler les lacunes en matière de protection. AesirX n'est pas l'expéditeur — l'IA ne fait que conseiller ; vous approuvez chaque artefact.",
            forsetiAssistCta: 'Ouvrir la bibliothèque de workflows Forseti',
            forsetiAiRunIdLabel: "ID d'exécution IA Forseti liée (coller pour enregistrer l'assistance IA)",
        },
        draft: {
            pageTitle: "Brouillon d'e-mail",
            pageSubtitle: "Rédigez et modifiez le brouillon d'e-mail. L'envoi direct arrive dans une version ultérieure.",
            composeCta: 'Rédiger le brouillon',
            composing: 'Rédaction…',
            noDraftYet: 'Aucun brouillon. Rédigez-en un pour commencer.',
            localeToggleLabel: 'Langue',
            localeEn: 'Anglais',
            localeVi: 'Vietnamien',
            subjectLabel: 'Sujet',
            bodyLabel: 'Corps',
            saveCta: 'Enregistrer les modifications',
            saving: 'Enregistrement…',
            saveError: "Impossible d'enregistrer le brouillon. Veuillez réessayer.",
            manifestSummaryLabel: 'Récapitulatif du manifeste',
            coverLetterLabel: 'Lettre de présentation (PDF)',
            coverLetterDownloadCta: 'Télécharger le PDF',
            editHistoryHeading: "Historique d'édition",
            editHistoryEmpty: 'Aucune modification pour le moment.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 avis de canal non officiel',
            uploadSentProofCta: "Téléverser une preuve d'envoi manuel",
            versionLabel: 'Version',
            statusDraft: 'Brouillon',
            statusEdited: 'Modifié',
            statusFinalized: 'Finalisé',
        },
        sentProof: {
            modalTitle: "Téléverser une preuve d'envoi manuel",
            architectureLockNote: "AesirX n'est pas l'expéditeur. Cette preuve capture l'envoi manuel que vous avez effectué via votre propre client de messagerie.",
            fileLabel: 'Fichier de preuve (.eml, .png, .jpg)',
            envelopeFromLabel: 'De',
            envelopeToLabel: 'À',
            envelopeSubjectLabel: "Sujet (à l'envoi)",
            envelopeSentAtLabel: 'Envoyé à',
            envelopeAttachmentShaLabel: "SHA-256 de la pièce jointe (à l'envoi)",
            uploadCta: 'Téléverser la preuve',
            uploading: 'Téléversement…',
            uploadSuccess: "Preuve d'envoi téléversée.",
            uploadErrorInvalidMime: 'Type de fichier non autorisé. Utilisez .eml, .png, .jpg ou .jpeg.',
            uploadErrorGeneric: 'Impossible de téléverser la preuve. Veuillez réessayer.',
            existingProofsHeading: "Preuves d'envoi enregistrées",
            existingProofsEmpty: "Aucune preuve d'envoi enregistrée pour le moment.",
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Dossier de preuves',
            notFoundMessage: 'Dossier de preuves introuvable : il a peut-être été supprimé ou le lien est incorrect.',
            manifestHeading: 'Manifeste',
            dossierLabel: 'Dossier',
            packShaLabel: 'SHA-256 du paquet',
            fileCountLabel: 'Fichiers',
            bytesLabel: 'Taille',
            composeDraftCta: 'Rédiger le brouillon',
            sendComingSoonCta: 'Envoi direct : bientôt disponible',
            sendComingSoonHint: "L'envoi direct est en cours de développement. Utilisez « Téléverser une preuve d'envoi manuel » sur la page du brouillon en attendant.",
            sendCta: 'Envoyer via le fournisseur configuré',
            sendDisabledNoProviderHint: "Configurez d'abord un fournisseur de messagerie maîtrisé par le client. AesirX n'envoie jamais en votre nom.",
            timelineHeading: 'Historique des envois',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Envoyer le dossier de preuves',
            providerPickerLabel: 'Fournisseur de messagerie configuré',
            providerPickerHint: "AesirX n'envoie jamais en votre nom. Sélectionnez ci-dessous l'un de vos fournisseurs configurés.",
            providerPickerEmpty: "Aucun fournisseur actif n'est configuré. Configurez-en un sur la page Fournisseurs de messagerie avant l'envoi.",
            providerPickerEmptyCta: 'Ouvrir les fournisseurs de messagerie',
            draftPickerLabel: 'Brouillon de courriel',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: "Aucun brouillon n'a encore été rédigé pour ce dossier. Rédigez-en un sur la page du brouillon au préalable.",
            recipientToLabel: 'Destinataire',
            recipientToHint: 'Une ou plusieurs adresses e-mail, séparées par des virgules.',
            recipientCcLabel: 'Cc (séparés par des virgules)',
            recipientCcHint: 'Facultatif. Liste d’adresses e-mail séparée par des virgules.',
            recipientBccLabel: 'Cci (séparés par des virgules)',
            recipientBccHint: 'Facultatif. Liste d’adresses e-mail séparée par des virgules.',
            consentReaffirmHeading: 'Réaffirmer le consentement du fournisseur',
            submitCta: 'Envoyer maintenant',
            sending: 'Envoi en cours…',
            cancelCta: 'Annuler',
            sendSuccess: 'Envoi enregistré. Le dossier a été transmis via votre fournisseur configuré.',
            sendErrorGeneric: "Impossible d'envoyer le dossier. Veuillez consulter l'erreur ci-dessous et réessayer.",
            sendErrorAlreadyFinalized: 'Ce brouillon a déjà été utilisé pour un envoi réussi. Rédigez un nouveau brouillon avant de renvoyer.',
            sendErrorInvalidRecipients: 'Une ou plusieurs adresses destinataires ne sont pas des boîtes de messagerie valides.',
            // Timeline
            timelineEmpty: "Aucun envoi enregistré pour l'instant. Utilisez « Envoyer via le fournisseur configuré » ci-dessus pour transmettre via votre fournisseur SMTP / Microsoft 365 / Google Workspace maîtrisé par le client.",
            timelineProviderLabel: 'Envoyé via votre fournisseur configuré',
            timelineSentAtLabel: 'Envoyé',
            timelineRecipientLabel: 'Destinataire',
            timelineOutcomeSent: 'Envoyé',
            timelineOutcomeFailed: 'Échec',
            timelineViewDetailsCta: 'Voir les détails',
            // Authority-response chips
            authorityChipNone: 'En attente de réponse',
            authorityChipAcknowledgement: 'Accusé de réception',
            authorityChipAcceptance: 'Acceptation',
            authorityChipRejection: 'Rejet',
            authorityChipSupplementRequest: 'Demande de complément',
            authorityChipNoResponse: 'Aucune réponse',
            // Hard-copy chips
            hardCopyChipNone: 'Aucun suivi papier',
            hardCopyChipPending: 'Posté — livraison en attente',
            hardCopyChipDelivered: 'Livré',
            // Drawer
            drawerTitle: "Enregistrement d'envoi",
            drawerEnvelopeHeading: 'Enveloppe',
            drawerEnvelopeFromLabel: 'De',
            drawerEnvelopeToLabel: 'À',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Cci',
            drawerEnvelopeSubjectLabel: 'Objet',
            drawerEnvelopeMessageIdLabel: 'Message-ID',
            drawerOutcomeHeading: 'Résultat',
            drawerSnapshotHeading: 'Instantané de la chaîne de conservation',
            drawerSnapshotPackShaLabel: "SHA-256 du paquet à l'envoi",
            drawerSnapshotDraftVersionLabel: "Version du brouillon à l'envoi",
            drawerSnapshotAttachmentLabel: 'ZIP transmis',
            drawerSnapshotAttachmentDownloadCta: 'Télécharger le ZIP',
            drawerAuthorityResponseHeading: "Réponse de l'autorité",
            drawerHardCopyHeading: 'Suivi papier',
            drawerSentByLabel: 'Envoyé par',
            drawerSendIdLabel: "Identifiant de l'enregistrement d'envoi",
            drawerSmtpResponseLabel: 'Réponse SMTP',
            drawerFailureReasonLabel: "Motif de l'échec",
            drawerLatencyLabel: 'Latence',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Accepté',
            drawerRejectedLabel: 'Rejeté',
            drawerCloseCta: 'Fermer',
            // Authority-response panel
            authorityRecordCta: 'Enregistrer la réponse',
            authorityReclassifyCta: 'Reclasser',
            authorityClassificationLabel: 'Classification',
            authorityReceivedAtLabel: 'Reçu le',
            authorityFreeTextLabel: 'Notes (facultatif)',
            authorityEvidenceLabel: 'Fichier de preuve (.eml / .pdf / .png / .jpg — facultatif)',
            authorityReasonLabel: 'Motif de la reclassification',
            authorityHistoryHeading: 'Historique des reclassifications',
            authorityHistoryEmpty: 'Aucune reclassification enregistrée.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Enregistrer la réponse',
            authoritySubmitting: 'Enregistrement…',
            authorityErrorInvalidMime: 'Type de fichier de preuve non autorisé. Utilisez .eml, .pdf, .png ou .jpg.',
            authorityErrorGeneric: "Impossible d'enregistrer la réponse. Veuillez réessayer.",
            authorityRecordedAtLabel: 'Enregistré le',
            authorityRecordedByLabel: 'Enregistré par',
            authorityEvidenceFilenameLabel: 'Preuve',
            authorityEvidenceDownloadCta: 'Télécharger la preuve',
            // Hard-copy panel
            hardCopyRecordCta: 'Consigner le suivi papier',
            hardCopyConfirmCta: 'Confirmer la livraison',
            hardCopyEditNotesCta: 'Modifier les notes de livraison',
            hardCopyPostedAtLabel: 'Posté le',
            hardCopyCourierLabel: 'Coursier',
            hardCopyTrackingLabel: 'Numéro de suivi',
            hardCopyRecipientLabel: "Destinataire sur l'étiquette",
            hardCopyReceiptLabel: 'Preuve de réception (.pdf / .png / .jpg — facultatif)',
            hardCopyReceiptDownloadCta: 'Télécharger le reçu',
            hardCopyDeliveredAtLabel: 'Livré le',
            hardCopyDeliveryNotesLabel: 'Notes de livraison (facultatif)',
            hardCopySubmitCta: 'Enregistrer le suivi',
            hardCopySubmitting: 'Enregistrement…',
            hardCopyErrorInvalidMime: 'Type de fichier de reçu non autorisé. Utilisez .pdf, .png ou .jpg.',
            hardCopyErrorGeneric: "Impossible d'enregistrer le suivi. Veuillez réessayer.",
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Rapports et notifications finalisés du dossier',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=fr.js.map